import React, { useState, useRef, useEffect } from 'react';
import { GraduationCap, Users, FileText, BarChart3, CreditCard, Headphones, BookOpen, Upload, Edit, Trash2, Plus, Download, Eye, X, Menu, LogOut, CheckCircle, AlertCircle, Video, Target, Award, Layers, Home, Settings, Save, QrCode, ArrowLeft, Printer, Building2, PieChart, Phone, Mail, MapPin, User, ToggleLeft, ToggleRight, MessageCircle, Lock, Minus, Globe, Image, Play, Check, ChevronDown, ChevronUp, Monitor, Star, ChevronLeft, ChevronRight, ExternalLink, List, Smartphone, AlertTriangle, Camera } from 'lucide-react';

// 🚀 VERSÃO: v703 - Fix cobrança duplicada por duplo-clique no botão Gerar PDF - 23/06/2026
console.log('🚀 FacilitaProf v693 - Fix planilha respostas professor vinculado');
// BASE: v689
// CORREÇÕES:
// - Professor vinculado agora carrega também simulados criados pela escola (userId da escola)
// - Filtro de série no select de simulados agora é case-insensitive com trim (evita falha por "9º Ano" vs "9º ano")
// - Fix race condition: escolaId adicionado como dependência do useEffect de simulados
//   → Professor vinculado carregava simulados ANTES do escolaId ser preenchido e nunca recarregava
// REGRAS FIRESTORE (atualizar manualmente no console Firebase):
// - simulados: allow update para professor vinculado salvar correção de simulados da escola
// Build 100% funcional ✅

// 🔥 FIREBASE CONFIG - As credenciais do seu projeto
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyBOpjLQZgyFJMbT0LZIsnpPBNFA9RH9dBI",
  authDomain: "facilitaprof.com.br",
  projectId: "facilitaprof-c2d1a",
  storageBucket: "facilitaprof-c2d1a.firebasestorage.app",
  messagingSenderId: "725193918724",
  appId: "1:725193918724:web:56db71f9af515eff74e851",
  measurementId: "G-ENTDRCJQ54"
};

// 🔥 Variáveis globais do Firebase (serão inicializadas quando o script carregar)
let firebaseApp = null;
let auth = null;
let db = null;
let storage = null;
let googleProvider = null;
let firebaseFunctions = null; // v545: Para Cloud Functions
let firebaseReady = false;

// 🔥 Função para inicializar Firebase (chamada após carregar scripts)
const initFirebase = () => {
  if (typeof window !== 'undefined' && window.firebase) {
    try {
      // Verificar se já foi inicializado
      if (!firebaseApp) {
        firebaseApp = window.firebase.initializeApp(FIREBASE_CONFIG);
      }
      auth = window.firebase.auth();
      db = window.firebase.firestore();
      storage = window.firebase.storage();
      googleProvider = new window.firebase.auth.GoogleAuthProvider();
      
      // v602: Configurar persistência do Firestore
      try {
        db.enablePersistence({ synchronizeTabs: true })
          .then(() => console.log('✅ Persistência Firestore habilitada'))
          .catch((err) => {
            if (err.code === 'failed-precondition') {
              console.warn('⚠️ Múltiplas abas abertas, persistência desabilitada');
            } else if (err.code === 'unimplemented') {
              console.warn('⚠️ Navegador não suporta persistência');
            }
          });
      } catch (e) {
        console.warn('⚠️ Erro ao habilitar persistência:', e);
      }
      
      // v545: Inicializar Functions com região correta
      if (window.firebase.functions) {
        firebaseFunctions = window.firebase.app().functions('us-central1');
        console.log('✅ Firebase Functions inicializado!');
      }
      firebaseReady = true;
      console.log('✅ Firebase inicializado com sucesso!');
      console.log('📊 Firestore configurado:', {
        projectId: window.firebase.app().options.projectId,
        apiKey: window.firebase.app().options.apiKey.substring(0, 10) + '...'
      });
      return true;
    } catch (error) {
      console.error('❌ Erro ao inicializar Firebase:', error);
      return false;
    }
  }
  return false;
};

// 🔥 FUNÇÃO PARA UPLOAD DE IMAGEM
const uploadImagem = async (arquivo, pasta = 'imagens') => {
  if (!firebaseReady || !storage) {
    console.error('Storage não disponível');
    return { success: false, error: 'Storage não disponível' };
  }
  
  try {
    const nomeArquivo = `${pasta}/${Date.now()}_${arquivo.name}`;
    const ref = storage.ref(nomeArquivo);
    
    await ref.put(arquivo);
    const url = await ref.getDownloadURL();
    
    console.log('✅ Imagem enviada:', url);
    return { success: true, url, path: nomeArquivo };
  } catch (error) {
    console.error('❌ Erro no upload:', error);
    return { success: false, error: error.message };
  }
};

// 🔥 FUNÇÃO PARA DELETAR IMAGEM
const deletarImagem = async (path) => {
  if (!firebaseReady || !storage) {
    return { success: false, error: 'Storage não disponível' };
  }
  
  try {
    const ref = storage.ref(path);
    await ref.delete();
    return { success: true };
  } catch (error) {
    console.error('❌ Erro ao deletar:', error);
    return { success: false, error: error.message };
  }
};

// 🔄 v654/v698: Converter URL de imagem para Base64 (para geração de PDF)
// Mantido como medida de segurança ao migrar de provedor de PDF (CloudLayer → html2pdfrocket),
// já que não há confirmação se o novo provedor acessa Firebase Storage sem problemas de CORS/permissão
const converterImagemParaBase64 = async (url) => {
  if (!url) return null;
  
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('❌ Erro ao converter imagem para base64:', error);
    return null;
  }
};

// 🖼️ COMPONENTE PARA IMAGENS OTIMIZADAS COM WEBP
// Usa WebP (30-50% menor) com fallback para PNG
const ImagemOtimizada = ({ webp, png, alt, className, loading = 'lazy' }) => (
  <picture>
    {webp && <source srcSet={webp} type="image/webp" />}
    <img src={png || webp} alt={alt} className={className} loading={loading} />
  </picture>
);

// 🖼️ v557: COMPONENTE PARA IMAGENS COM LOADING STATE
// Mostra skeleton enquanto carrega, melhora percepção de velocidade
// Nota: Usa React.useState para funcionar fora do componente principal
function ImagemComLoading({ src, alt, className, style, tamanho }) {
  const [status, setStatus] = React.useState('loading'); // 'loading', 'loaded', 'error'
  
  // Reset quando a src muda
  React.useEffect(() => {
    if (src) {
      setStatus('loading');
    }
  }, [src]);
  
  const alturasPorTamanho = {
    'pequena': '160px',
    'media': '220px', 
    'grande': '280px',
    'inteira': '350px',
    'texto-multiplas': '300px'
  };
  
  const alturaMax = style?.maxHeight || alturasPorTamanho[tamanho] || '220px';
  
  if (!src) return null;
  
  return (
    <div className="relative w-full" style={{ minHeight: status === 'loading' ? alturaMax : 'auto' }}>
      {/* Skeleton Loading */}
      {status === 'loading' && (
        <div 
          className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse rounded flex items-center justify-center z-10"
          style={{ minHeight: alturaMax }}
        >
          <div className="text-center text-gray-400">
            <div className="w-8 h-8 mx-auto mb-2 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin"></div>
            <span className="text-xs">Carregando...</span>
          </div>
        </div>
      )}
      
      {/* Imagem real */}
      <img 
        src={src} 
        alt={alt || 'Questão'} 
        className={`${className || ''} transition-opacity duration-200 ${status === 'loading' ? 'opacity-0 h-0' : 'opacity-100'}`}
        style={status === 'loaded' ? style : { ...style, position: 'absolute', opacity: 0 }}
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
      />
      
      {/* Erro */}
      {status === 'error' && (
        <div className="flex items-center justify-center bg-red-50 rounded text-red-400 p-4" style={{ minHeight: '100px' }}>
          <span className="text-xs">Erro ao carregar imagem</span>
        </div>
      )}
    </div>
  );
}

// 🔥 FUNÇÕES FIREBASE (com fallback para quando não estiver disponível)

// Login com email e senha
const loginComEmail = async (email, senha) => {
  if (!firebaseReady || !auth) {
    // Fallback para teste local
    console.log('⚠️ Firebase não disponível, usando modo de teste');
    const usuariosTeste = [
      { uid: '1', email: 'salmeronrosadesales@gmail.com', senha: 'admin123', tipo: 'admin', nome: 'Administrador', creditos: 9999, escola: '', seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'] },
      { uid: '2', email: 'professor@teste.com', senha: 'prof123', tipo: 'professor', nome: 'Ana Paula Silva', creditos: 450, escola: 'Escola Municipal João Silva', seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'] },
      { uid: '3', email: 'escola@teste.com', senha: 'escola123', tipo: 'escola', nome: 'Colégio Estadual ABC', creditos: 3000, escola: 'Colégio Estadual ABC', seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'] }
    ];
    const user = usuariosTeste.find(u => u.email === email && u.senha === senha);
    if (user) {
      return { success: true, user };
    }
    return { success: false, error: 'Email ou senha incorretos' };
  }
  
  try {
    const userCredential = await auth.signInWithEmailAndPassword(email, senha);
    const user = userCredential.user;
    const userDoc = await db.collection('users').doc(user.uid).get();
    
    if (userDoc.exists) {
      return { success: true, user: { uid: user.uid, email: user.email, ...userDoc.data() } };
    }
    return { success: false, error: "Dados do usuário não encontrados" };
  } catch (error) {
    let mensagem = "Erro ao fazer login";
    if (error.code === "auth/user-not-found") mensagem = "Email não cadastrado";
    else if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") mensagem = "Email ou senha incorretos";
    else if (error.code === "auth/invalid-email") mensagem = "Email inválido";
    else if (error.code === "auth/too-many-requests") mensagem = "Muitas tentativas. Aguarde alguns minutos.";
    return { success: false, error: mensagem };
  }
};

// Login com Google
const loginComGoogle = async () => {
  if (!firebaseReady || !auth) {
    return { success: false, error: 'Firebase não disponível. Use email e senha.' };
  }
  
  try {
    const result = await auth.signInWithPopup(googleProvider);
    const user = result.user;
    const userDoc = await db.collection('users').doc(user.uid).get();
    
    let isNewUser = !userDoc.exists;
    let needsCompletion = false;
    
    if (!userDoc.exists) {
      // Novo usuário - criar documento básico
      await db.collection('users').doc(user.uid).set({
        nome: user.displayName || "Usuário",
        email: user.email,
        tipo: "professor",
        creditos: 10,
        plano: "Gratuito",
        dataCadastro: new Date(),
        seriesLiberadas: ["1º ano", "2º ano", "3º ano", "4º ano", "5º ano", "6º ano", "7º ano", "8º ano", "9º ano"],
        escola: "",
        cadastroGoogleCompleto: false // v540: Flag para indicar que precisa completar
      });
      needsCompletion = true;
    } else {
      // Usuário existente - verificar se tem escola e nome preenchidos
      const data = userDoc.data();
      // v540: Se não tem escola OU nome do professor OU flag indica incompleto
      if (!data.escola || data.escola.trim() === '' || data.cadastroGoogleCompleto === false) {
        needsCompletion = true;
      }
    }
    
    const userData = await db.collection('users').doc(user.uid).get();
    return { 
      success: true, 
      user: { uid: user.uid, email: user.email, ...userData.data() }, 
      isNewUser,
      needsCompletion // v540: Indica se precisa completar cadastro
    };
  } catch (error) {
    console.error('Erro login Google:', error);
    return { success: false, error: "Erro ao fazer login com Google" };
  }
};

// Cadastro
const cadastrarUsuario = async (dadosCadastro) => {
  if (!firebaseReady || !auth) {
    // Fallback para teste local
    console.log('⚠️ Firebase não disponível, simulando cadastro');
    return { 
      success: true, 
      user: { 
        uid: 'local-' + Date.now(), 
        email: dadosCadastro.email, 
        nome: dadosCadastro.nome, 
        tipo: dadosCadastro.tipo === 'juridica' ? 'escola' : 'professor',
        creditos: 10, 
        plano: "Gratuito", 
        escola: dadosCadastro.tipo === 'juridica' ? dadosCadastro.nome : dadosCadastro.escola, 
        seriesLiberadas: ["1º ano", "2º ano", "3º ano", "4º ano", "5º ano", "6º ano", "7º ano", "8º ano", "9º ano"] 
      } 
    };
  }
  
  try {
    const { email, senha, nome, telefone, tipo, escola, telefoneVerificado } = dadosCadastro;
    const userCredential = await auth.createUserWithEmailAndPassword(email, senha);
    const user = userCredential.user;
    const tipoUsuario = tipo === "juridica" ? "escola" : "professor";
    
    // v544: Dados base do usuário
    const userData = {
      nome, email, telefone,
      tipo: tipoUsuario,
      tipoCliente: tipo,
      escola: tipo === "juridica" ? nome : escola,
      creditos: 10,
      plano: "Gratuito",
      dataCadastro: new Date(),
      seriesLiberadas: ["1º ano", "2º ano", "3º ano", "4º ano", "5º ano", "6º ano", "7º ano", "8º ano", "9º ano"]
    };
    
    // v548: Adicionar telefone verificado (para TODOS - pessoa física e jurídica)
    if (telefoneVerificado) {
      userData.telefoneVerificado = telefoneVerificado;
      userData.telefoneVerificadoEm = new Date();
    }
    
    await db.collection('users').doc(user.uid).set(userData);
    
    return { success: true, user: { uid: user.uid, email, nome, tipo: tipoUsuario, creditos: 10, plano: "Gratuito", escola: tipo === "juridica" ? nome : escola, seriesLiberadas: ["1º ano", "2º ano", "3º ano", "4º ano", "5º ano", "6º ano", "7º ano", "8º ano", "9º ano"], telefoneVerificado } };
  } catch (error) {
    let mensagem = "Erro ao cadastrar";
    if (error.code === "auth/email-already-in-use") mensagem = "Este email já está cadastrado";
    else if (error.code === "auth/weak-password") mensagem = "A senha deve ter pelo menos 6 caracteres";
    else if (error.code === "auth/invalid-email") mensagem = "Email inválido";
    return { success: false, error: mensagem };
  }
};

// Recuperar senha
const recuperarSenhaFirebase = async (email) => {
  if (!firebaseReady || !auth) {
    return { success: false, error: 'Firebase não disponível' };
  }
  
  try {
    await auth.sendPasswordResetEmail(email);
    return { success: true };
  } catch (error) {
    let mensagem = "Erro ao enviar email";
    if (error.code === "auth/user-not-found") mensagem = "Email não cadastrado";
    return { success: false, error: mensagem };
  }
};

// Logout
const logoutFirebase = async () => {
  // Marcar que o usuário fez logout explícito
  localStorage.setItem('facilitaprof_logout', 'true');
  
  if (firebaseReady && auth) {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  }
  return { success: true };
};

// Debitar créditos
const debitarCreditos = async (userId, quantidade, descricao) => {
  if (!firebaseReady || !db) {
    return { success: true, novoSaldo: 0 }; // Fallback
  }
  
  try {
    const userRef = db.collection('users').doc(userId);
    const userDoc = await userRef.get();
    if (!userDoc.exists) return { success: false, error: "Usuário não encontrado" };
    
    const creditosAtuais = userDoc.data().creditos || 0;
    if (creditosAtuais < quantidade) return { success: false, error: "Créditos insuficientes" };
    
    await userRef.update({ creditos: creditosAtuais - quantidade });
    await db.collection('transacoes').add({ userId, tipo: "uso", quantidade: -quantidade, data: new Date(), descricao });
    
    return { success: true, novoSaldo: creditosAtuais - quantidade };
  } catch (error) {
    return { success: false, error: "Erro ao debitar créditos" };
  }
};

// 🔥 FUNÇÕES PARA QUESTÕES NO FIRESTORE

// Salvar questão no Firestore
const salvarQuestao = async (questaoData, imagemFile = null) => {
  if (!firebaseReady || !db) {
    return { success: false, error: 'Firebase não disponível' };
  }
  
  try {
    let imagemUrl = questaoData.imagem;
    
    // Se tem arquivo de imagem, fazer upload
    if (imagemFile) {
      const uploadResult = await uploadImagem(imagemFile, 'questoes');
      if (uploadResult.success) {
        imagemUrl = uploadResult.url;
      } else {
        return { success: false, error: 'Erro ao fazer upload da imagem' };
      }
    }
    
    const questao = {
      ...questaoData,
      imagem: imagemUrl,
      dataCriacao: new Date(),
      ativo: true
    };
    
    const docRef = await db.collection('questoes').add(questao);
    _invalidarCache(); // v650: nova questão → cache desatualizado
    return { success: true, id: docRef.id, questao: { ...questao, id: docRef.id } };
  } catch (error) {
    console.error('Erro ao salvar questão:', error);
    return { success: false, error: error.message };
  }
};

// v650: Cache em memória para questões — TTL de 24h, invalida automaticamente
const _cacheQuestoes = {};
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 horas

const _cacheKey = (filtros) => `${filtros.serie || ''}|${filtros.componente || ''}|${filtros.habilidade || ''}`;

const _lerCache = (key) => {
  const entrada = _cacheQuestoes[key];
  if (!entrada) return null;
  if (Date.now() - entrada.timestamp > CACHE_TTL_MS) {
    delete _cacheQuestoes[key]; // expirado
    return null;
  }
  return entrada.questoes;
};

const _escreverCache = (key, questoes) => {
  _cacheQuestoes[key] = { questoes, timestamp: Date.now() };
};

// Limpa todo o cache — chamada após criar/atualizar/deletar questões
const _invalidarCache = () => {
  Object.keys(_cacheQuestoes).forEach(k => delete _cacheQuestoes[k]);
  console.log('🗑️ [v650-CACHE] Cache invalidado');
};

// v640: Buscar questões - agora com filtro de habilidade flexível
const buscarQuestoes = async (filtros = {}, habilidadesArray = []) => {
  if (!firebaseReady || !db) {
    console.log('Firebase não disponível, usando questões locais');
    return { success: false, error: 'Firebase não disponível' };
  }
  
  // v659: Verificar se usuário está autenticado antes de buscar
  if (auth && !auth.currentUser) {
    console.log('⏸️ [v659] Aguardando autenticação antes de carregar questões');
    return { success: false, error: 'Aguardando autenticação' };
  }
  
  try {
    // v650: Verificar cache antes de ir ao Firestore
    const key = _cacheKey(filtros);
    const cached = _lerCache(key);
    if (cached) {
      console.log(`✅ [v650-CACHE] ${cached.length} questões servidas do cache (key: ${key})`);
      return { success: true, questoes: cached };
    }

    console.log('🔄 [v640] Carregando questões com filtros:', filtros);
    const tempoInicio = Date.now();
    
    // Construir query
    let query = db.collection('questoes').where('ativo', '==', true);
    
    // Aplicar filtros se fornecidos
    if (filtros.serie) {
      query = query.where('serie', '==', filtros.serie);
      console.log('  📌 Filtro: série =', filtros.serie);
    }
    
    // v650: Filtrar componente no Firestore (campo exato, sem necessidade de lógica flexível aqui)
    if (filtros.componente && filtros.componente !== 'Lingua Portuguesa SAEB' && filtros.componente !== 'Língua Portuguesa SAEB') {
      query = query.where('componente', '==', filtros.componente);
      console.log('  📌 Filtro Firestore: componente =', filtros.componente);
    }
    // Nota: Língua Portuguesa tem duas grafias no banco, então vai para filtro JS (abaixo)
    
    // v638: SEM LIMIT - carrega TODAS as questões do filtro
    const snapshot = await query.get();
    
    // Filtrar componente e habilidade no JavaScript (evita precisar de índices compostos)
    let questoes = [];
    snapshot.forEach(doc => {
      questoes.push({ id: doc.id, ...doc.data() });
    });
    
    if (filtros.componente) {
      // v643: Filtro flexível para Língua Portuguesa (com/sem acento)
      questoes = questoes.filter(q => {
        if (filtros.componente === 'Lingua Portuguesa SAEB' || filtros.componente === 'Língua Portuguesa SAEB') {
          // Aceita AMBAS as grafias
          return q.componente === 'Lingua Portuguesa SAEB' || q.componente === 'Língua Portuguesa SAEB';
        }
        // Outros componentes: comparação exata
        return q.componente === filtros.componente;
      });
      console.log('  🔍 Filtro JS: componente =', filtros.componente, '→', questoes.length, 'questões');
    }
    
    if (filtros.habilidade) {
      // v640: Buscar habilidade no array para pegar código
      const habilidadeObj = habilidadesArray.find(h => 
        h.id === filtros.habilidade || 
        h.nome === filtros.habilidade ||
        h.codigo === filtros.habilidade
      );
      
      const habilidadeCodigo = habilidadeObj?.codigo;
      const habilidadeNome = habilidadeObj?.nome;
      const habilidadeId = habilidadeObj?.id || filtros.habilidade;
      
      console.log('  🔍 v640: Habilidade buscada:', {
        entrada: filtros.habilidade,
        codigo: habilidadeCodigo,
        nome: habilidadeNome,
        id: habilidadeId
      });
      
      // v640: Comparar por ID, código E nome
      questoes = questoes.filter(q => 
        q.habilidade === habilidadeId ||
        q.habilidade === habilidadeCodigo ||
        q.habilidade === habilidadeNome ||
        q.habilidade === filtros.habilidade ||
        q.habilidadeId === habilidadeId ||
        q.habilidadeId === filtros.habilidade
      );
      
      console.log('  🔍 Filtro JS: habilidade =', filtros.habilidade, '→', questoes.length, 'questões');
    }
    
    const tempoTotal = ((Date.now() - tempoInicio) / 1000).toFixed(2);
    console.log(`✅ [v640] ${questoes.length} questões carregadas em ${tempoTotal}s (${snapshot.size} leituras)`);
    
    // v650: Salvar no cache
    _escreverCache(key, questoes);
    
    return { success: true, questoes };
  } catch (error) {
    console.error('Erro ao buscar questões:', error);
    return { success: false, error: error.message };
  }
};

// Atualizar questão
const atualizarQuestao = async (questaoId, dados) => {
  if (!firebaseReady || !db) {
    return { success: false, error: 'Firebase não disponível' };
  }
  
  try {
    await db.collection('questoes').doc(questaoId).update(dados);
    _invalidarCache(); // v650: questão editada → cache desatualizado
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Deletar questão (soft delete)
const deletarQuestao = async (questaoId) => {
  if (!firebaseReady || !db) {
    return { success: false, error: 'Firebase não disponível' };
  }
  
  try {
    await db.collection('questoes').doc(questaoId).update({ ativo: false });
    _invalidarCache(); // v650: questão deletada → cache desatualizado
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 🔥 FUNÇÕES PARA HISTÓRICO DE ATIVIDADES

// Salvar atividade gerada
const salvarAtividadeHistorico = async (userId, atividadeData) => {
  if (!firebaseReady || !db) {
    return { success: false, error: 'Firebase não disponível' };
  }
  
  try {
    const atividade = {
      ...atividadeData,
      usuarioId: userId,
      dataGeracao: new Date()
    };
    
    const docRef = await db.collection('historico_atividades').add(atividade);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Buscar histórico do usuário
const buscarHistorico = async (userId) => {
  if (!firebaseReady || !db) {
    return { success: false, error: 'Firebase não disponível' };
  }
  
  try {
    const snapshot = await db.collection('historico_atividades')
      .where('usuarioId', '==', userId)
      .orderBy('dataGeracao', 'desc')
      .limit(50)
      .get();
    
    const historico = [];
    snapshot.forEach(doc => {
      historico.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, historico };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 🛡️ Componente de Proteção de Imagem (evita clique direito e download, mas permite clique normal)
const ProtectedImageArea = ({ children, className = '', onClick }) => (
  <div 
    className={`relative select-none ${className}`}
    onContextMenu={(e) => e.preventDefault()}
    onDragStart={(e) => e.preventDefault()}
    draggable={false}
    onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    {children}
    {/* Overlay invisível de proteção - pointer-events: none permite clique passar */}
    <div 
      className="absolute inset-0 z-10 pointer-events-none" 
      onContextMenu={(e) => e.preventDefault()}
    />
  </div>
);

// 📝 Componente para renderizar imagens das questões (gráficos, tabelas, figuras)
const ImagemQuestao = ({ tipo }) => {
  switch(tipo) {
    case 'grafico_barras':
      return (
        <svg viewBox="0 0 300 150" className="w-full h-32 mx-auto">
          <rect x="20" y="100" width="30" height="40" fill="#3b82f6" />
          <rect x="60" y="80" width="30" height="60" fill="#3b82f6" />
          <rect x="100" y="60" width="30" height="80" fill="#3b82f6" />
          <rect x="140" y="90" width="30" height="50" fill="#3b82f6" />
          <rect x="180" y="30" width="30" height="110" fill="#22c55e" />
          <rect x="220" y="50" width="30" height="90" fill="#3b82f6" />
          <line x1="10" y1="140" x2="260" y2="140" stroke="#333" strokeWidth="2" />
          <text x="28" y="155" fontSize="10" fill="#666">Seg</text>
          <text x="68" y="155" fontSize="10" fill="#666">Ter</text>
          <text x="108" y="155" fontSize="10" fill="#666">Qua</text>
          <text x="148" y="155" fontSize="10" fill="#666">Qui</text>
          <text x="188" y="155" fontSize="10" fill="#666">Sáb</text>
          <text x="228" y="155" fontSize="10" fill="#666">Dom</text>
          <text x="100" y="20" fontSize="12" fontWeight="bold" fill="#333">Vendas de Sorvete</text>
        </svg>
      );
    case 'fracao_pizza':
      return (
        <svg viewBox="0 0 150 150" className="w-28 h-28 mx-auto">
          <circle cx="75" cy="75" r="60" fill="#e5e7eb" stroke="#333" strokeWidth="2" />
          <path d="M75,75 L75,15 A60,60 0 1,1 15,75 Z" fill="#f97316" />
          <line x1="75" y1="75" x2="75" y2="15" stroke="#333" strokeWidth="2" />
          <line x1="75" y1="75" x2="15" y2="75" stroke="#333" strokeWidth="2" />
          <text x="50" y="85" fontSize="14" fontWeight="bold" fill="#333">3/4</text>
        </svg>
      );
    case 'tabela_precos':
      return (
        <div className="border-2 border-gray-400 rounded overflow-hidden mx-auto w-48">
          <table className="w-full text-sm">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="p-2 border-r border-blue-500">Produto</th>
                <th className="p-2">Preço</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-50">
                <td className="p-2 border-r border-gray-300">Caderno</td>
                <td className="p-2 text-center">R$ 8,00</td>
              </tr>
              <tr>
                <td className="p-2 border-r border-gray-300">Lápis</td>
                <td className="p-2 text-center">R$ 1,00</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="p-2 border-r border-gray-300">Borracha</td>
                <td className="p-2 text-center">R$ 2,00</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    case 'retangulo':
      return (
        <svg viewBox="0 0 200 120" className="w-40 h-24 mx-auto">
          <rect x="30" y="20" width="120" height="70" fill="none" stroke="#3b82f6" strokeWidth="3" />
          <text x="80" y="60" fontSize="14" fill="#333">5 cm</text>
          <text x="155" y="60" fontSize="12" fill="#333">3 cm</text>
          <line x1="30" y1="100" x2="150" y2="100" stroke="#666" strokeWidth="1" strokeDasharray="5,3" />
          <line x1="160" y1="20" x2="160" y2="90" stroke="#666" strokeWidth="1" strokeDasharray="5,3" />
        </svg>
      );
    case 'grafico_pizza':
      return (
        <svg viewBox="0 0 200 150" className="w-48 h-36 mx-auto">
          <circle cx="80" cy="75" r="55" fill="#fbbf24" />
          <path d="M80,75 L80,20 A55,55 0 0,1 130,100 Z" fill="#22c55e" />
          <path d="M80,75 L130,100 A55,55 0 0,1 50,120 Z" fill="#ef4444" />
          <path d="M80,75 L50,120 A55,55 0 0,1 30,60 Z" fill="#8b5cf6" />
          <circle cx="160" cy="35" r="8" fill="#22c55e" /><text x="172" y="40" fontSize="10">Laranja 40%</text>
          <circle cx="160" cy="55" r="8" fill="#fbbf24" /><text x="172" y="60" fontSize="10">Banana 25%</text>
          <circle cx="160" cy="75" r="8" fill="#ef4444" /><text x="172" y="80" fontSize="10">Maçã 20%</text>
          <circle cx="160" cy="95" r="8" fill="#8b5cf6" /><text x="172" y="100" fontSize="10">Uva 15%</text>
        </svg>
      );
    case 'tirinha':
      return (
        <div className="border-2 border-gray-800 rounded-lg overflow-hidden mx-auto w-72">
          <div className="flex">
            <div className="w-1/3 border-r-2 border-gray-800 p-2 bg-blue-50">
              <div className="w-8 h-8 bg-yellow-400 rounded-full mx-auto mb-1"></div>
              <p className="text-xs text-center">Que dia lindo!</p>
            </div>
            <div className="w-1/3 border-r-2 border-gray-800 p-2 bg-gray-100">
              <div className="w-8 h-8 bg-gray-400 rounded-full mx-auto mb-1"></div>
              <p className="text-xs text-center">☁️ Nuvens...</p>
            </div>
            <div className="w-1/3 p-2 bg-blue-200">
              <div className="w-8 h-8 bg-blue-600 rounded-full mx-auto mb-1"></div>
              <p className="text-xs text-center">🌧️ Chuva!</p>
            </div>
          </div>
        </div>
      );
    case 'grafico_funcao':
      return (
        <svg viewBox="0 0 200 150" className="w-48 h-36 mx-auto">
          <line x1="30" y1="120" x2="180" y2="120" stroke="#333" strokeWidth="2" />
          <line x1="30" y1="120" x2="30" y2="20" stroke="#333" strokeWidth="2" />
          <path d="M30,100 L60,85 L90,70 L120,55 L150,40 L180,25" fill="none" stroke="#3b82f6" strokeWidth="3" />
          <text x="100" y="135" fontSize="10" fill="#333">x</text>
          <text x="15" y="70" fontSize="10" fill="#333">y</text>
          <circle cx="30" cy="100" r="4" fill="#3b82f6" />
          <circle cx="90" cy="70" r="4" fill="#3b82f6" />
          <circle cx="150" cy="40" r="4" fill="#3b82f6" />
        </svg>
      );
    case 'animal_cachorro':
      return (
        <div className="w-24 h-24 mx-auto bg-amber-100 rounded-lg flex items-center justify-center text-5xl">
          🐕
        </div>
      );
    case 'contagem_macas':
      return (
        <div className="flex gap-1 justify-center flex-wrap w-40 mx-auto">
          {[1,2,3,4,5,6,7].map(i => (
            <span key={i} className="text-2xl">🍎</span>
          ))}
        </div>
      );
    default:
      return <div className="h-20 bg-gray-200 rounded flex items-center justify-center text-gray-500">Imagem</div>;
  }
};

// 📝 Componente para renderizar uma questão completa
const QuestaoCompleta = ({ questao, numero, mostrarGabarito = false }) => {
  if (!questao) return null;
  
  // 🔥 Se a questão tem uma imagem (do Firebase), renderiza a imagem
  if (questao.imagem && (questao.imagem.startsWith('http') || questao.imagem.startsWith('data:'))) {
    return (
      <div className="border border-gray-300 rounded-lg p-4 mb-3 bg-white">
        <div className="flex items-start gap-3">
          <span className="font-bold text-sm bg-gray-800 text-white px-3 py-1 rounded-full flex-shrink-0">
            {numero}
          </span>
          <div className="flex-1">
            {/* Imagem da questão */}
            <img 
              src={questao.imagem} 
              alt={`Questão ${numero}`} 
              className="max-w-full rounded-lg"
              style={{ maxHeight: '400px', objectFit: 'contain' }}
            />
            
            {/* Gabarito (se solicitado) */}
            {mostrarGabarito && (
              <div className="mt-2 text-sm">
                <span className="font-bold">Gabarito: </span>
                <span className={`px-2 py-1 rounded ${questao.gabarito && questao.gabarito !== '-' ? 'bg-green-100 text-green-700 font-bold' : 'bg-gray-100 text-gray-500'}`}>
                  {questao.gabarito || '-'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Renderização padrão para questões com texto
  return (
    <div className="border border-gray-300 rounded-lg p-4 mb-3 bg-white">
      <div className="flex items-start gap-3">
        <span className="font-bold text-sm bg-gray-800 text-white px-3 py-1 rounded-full flex-shrink-0">
          {numero}
        </span>
        <div className="flex-1">
          {/* Enunciado */}
          {questao.enunciado && (
            <p className="text-sm font-semibold text-gray-700 mb-2">{questao.enunciado}</p>
          )}
          
          {/* Texto da questão */}
          {questao.texto && (
            <div className="bg-gray-50 border-l-4 border-blue-500 p-3 mb-3 text-sm italic whitespace-pre-line">
              {questao.texto}
            </div>
          )}
          
          {/* Imagem/Gráfico/Tabela */}
          {questao.temImagem && (
            <div className="my-3">
              <ImagemQuestao tipo={questao.tipoImagem} />
            </div>
          )}
          
          {/* Pergunta */}
          {questao.pergunta && (
            <p className="text-sm font-medium text-gray-800 mb-3">{questao.pergunta}</p>
          )}
          
          {/* Alternativas */}
          {questao.alternativas && (
            <div className="space-y-2">
              {questao.alternativas.map((alt, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-center gap-2 p-2 rounded text-sm ${
                    mostrarGabarito && alt.startsWith(questao.gabarito) 
                      ? 'bg-green-100 border border-green-500' 
                      : 'bg-gray-50'
                  }`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    mostrarGabarito && alt.startsWith(questao.gabarito)
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-300 text-gray-700'
                  }`}>
                    {alt.charAt(0)}
                  </span>
                  <span>{alt.substring(3)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 📄 Componente de Página A4 para PDF
const PaginaA4 = React.forwardRef(({ cabecalho, logoBase64, questoes, questoesData, numeroPagina, totalPaginas, tipoDoc, mostrarGabarito = false }, ref) => (
  <div 
    ref={ref}
    className="bg-white shadow-lg mx-auto mb-4"
    style={{
      width: '210mm',
      minHeight: '297mm',
      padding: '15mm',
      boxSizing: 'border-box',
      fontFamily: 'Arial, sans-serif'
    }}
  >
    {/* Cabeçalho */}
    <div className="border-b-2 border-gray-800 pb-3 mb-4">
      <div className="text-center">
        {/* v654: Logo em base64 (evita CORS) */}
        <div className="flex items-center justify-center gap-3 mb-2">
          {logoBase64 && (
            <img 
              src={logoBase64} 
              alt="Logo" 
              style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
            />
          )}
          <h1 className="text-lg font-bold uppercase">{cabecalho.nomeEscola}</h1>
        </div>
        <p className="text-sm mt-1">Professor(a): {cabecalho.professor}</p>
        <div className="flex justify-center gap-8 mt-2 text-sm">
          <span><strong>Série:</strong> {cabecalho.serie}</span>
          <span><strong>Turma:</strong> {cabecalho.turma}</span>
          <span><strong>Disciplina:</strong> {cabecalho.disciplina}</span>
        </div>
        <p className="text-xs mt-2 font-semibold uppercase tracking-wide">{tipoDoc}</p>
      </div>
    </div>

    {/* Linha para nome do aluno */}
    <div className="mb-4 pb-2 border-b border-gray-300">
      <p className="text-sm"><strong>Nome:</strong> ______________________________________________ <strong>Data:</strong> ____/____/________</p>
    </div>

    {/* Área de Questões */}
    <div className="space-y-4">
      {questoes.map((questaoId, index) => {
        const questao = questoesData.find(q => q.id === questaoId);
        if (!questao) return null;
        
        const alturaQuestao = questao.tamanho === 'pequena' ? '5cm' : 
                             questao.tamanho === 'media' ? '7cm' : 
                             questao.tamanho === 'grande' ? '10.5cm' : '20.5cm';
        
        return (
          <div key={questaoId} className="border border-gray-300 rounded p-3">
            <div className="flex items-start gap-2">
              <span className="font-bold text-sm bg-gray-200 px-2 py-1 rounded">
                {index + 1 + (numeroPagina - 1) * 4}
              </span>
              <div className="flex-1">
                {/* 🔥 Se a questão tem imagem do Firebase, mostra a imagem */}
                {questao.imagem && (questao.imagem.startsWith('http') || questao.imagem.startsWith('data:')) ? (
                  <div 
                    className="flex items-center justify-center"
                    style={{ minHeight: alturaQuestao }}
                  >
                    <img 
                      src={questao.imagem} 
                      alt={`Questão ${index + 1}`} 
                      className="max-w-full object-contain"
                      style={{ maxHeight: alturaQuestao }}
                    />
                  </div>
                ) : (
                  /* Área placeholder para questões sem imagem */
                  <div 
                    className="bg-gray-50 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400"
                    style={{ minHeight: alturaQuestao }}
                  >
                    <div className="text-center p-4">
                      <p className="text-sm font-semibold">Questão {questao.id}</p>
                      <p className="text-xs">{questao.serie} • {questao.componente}</p>
                      <p className="text-xs">{questao.habilidade}</p>
                      <p className="text-xs mt-2">Tamanho: {alturaQuestao}</p>
                    </div>
                  </div>
                )}
                {mostrarGabarito && (
                  <p className="text-xs mt-1 text-green-600 font-bold">Gabarito: {questao.gabarito || '-'}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* Rodapé */}
    <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-500" style={{position: 'relative', marginTop: '20px'}}>
      <p>Página {numeroPagina} de {totalPaginas} • Gerado por FacilitaPROF</p>
    </div>
  </div>
));

// 📋 Componente de Gabarito
const PaginaGabarito = React.forwardRef(({ cabecalho, logoBase64, todasQuestoes, questoesData, tipoDoc }, ref) => (
  <div 
    ref={ref}
    className="bg-white shadow-lg mx-auto mb-4"
    style={{
      width: '210mm',
      minHeight: '297mm',
      padding: '15mm',
      boxSizing: 'border-box',
      fontFamily: 'Arial, sans-serif'
    }}
  >
    {/* Cabeçalho */}
    <div className="border-b-2 border-gray-800 pb-3 mb-4">
      <div className="text-center">
        {/* v654: Logo em base64 (evita CORS) */}
        <div className="flex items-center justify-center gap-3 mb-2">
          {logoBase64 && (
            <img 
              src={logoBase64} 
              alt="Logo" 
              style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
            />
          )}
          <h1 className="text-lg font-bold uppercase">{cabecalho.nomeEscola}</h1>
        </div>
        <p className="text-xl font-bold mt-2 text-blue-800">📋 GABARITO</p>
        <p className="text-sm mt-1">{tipoDoc}</p>
      </div>
    </div>

    {/* Grade de Gabarito */}
    <div className="grid grid-cols-5 gap-2 mt-6">
      {todasQuestoes.map((questaoId, index) => {
        const questao = questoesData.find(q => q.id === questaoId);
        return (
          <div key={questaoId} className="border border-gray-300 rounded p-2 text-center">
            <span className="text-xs text-gray-500">Questão</span>
            <p className="font-bold text-lg">{index + 1}</p>
            <p className="text-xl font-bold text-green-600">{questao?.gabarito || '-'}</p>
          </div>
        );
      })}
    </div>

    {/* Legenda */}
    <div className="mt-8 p-4 bg-gray-50 rounded-lg">
      <h3 className="font-bold text-sm mb-2">Informações:</h3>
      <p className="text-xs text-gray-600">• Total de questões: {todasQuestoes.length}</p>
      <p className="text-xs text-gray-600">• Professor(a): {cabecalho.professor}</p>
      <p className="text-xs text-gray-600">• Série: {cabecalho.serie} - Turma: {cabecalho.turma}</p>
    </div>
  </div>
));

export default function Facilitaprof() {
  // 🔄 VERSÃO: v200-FIREBASE - 2024-12-19
  // ✅ Integração com Firebase Auth e Firestore
  // ✅ Login com Email/Senha e Google
  // ✅ Cadastro de usuários com 10 créditos grátis
  // ✅ Recuperação de senha via Firebase
  // ✅ Fallback para modo de teste quando Firebase não disponível
  // 🎯 TIMESTAMP: 1734620000000
  
  // v617: IMPORTANTE - Limpar flag de logout no carregamento da página
  // Isso evita que o onAuthStateChanged faça signOut antes do login
  React.useEffect(() => {
    const fezLogout = localStorage.getItem('facilitaprof_logout');
    if (fezLogout === 'true') {
      console.log('🧹 Limpando flag de logout do localStorage');
      localStorage.removeItem('facilitaprof_logout');
    }
  }, []);
  
  const [currentUser, setCurrentUser] = useState(null);
  const [firebaseStatus, setFirebaseStatus] = useState('loading'); // 'loading', 'ready', 'fallback'
  const [authReady, setAuthReady] = useState(false); // v621: Sinaliza quando auth está 100% pronto
  
  // v624: Helper para aguardar Firebase Auth estar pronto - ATÉ 60 SEGUNDOS
  const aguardarAuth = async () => {
    let tentativas = 0;
    const maxTentativas = 120; // 120 tentativas = 60 segundos
    
    while (!auth?.currentUser && tentativas < maxTentativas) {
      if (tentativas % 10 === 0) { // Log a cada 5 segundos
        console.log(`⏳ Aguardando autenticação... ${tentativas + 1}/${maxTentativas} (${((tentativas * 500) / 1000).toFixed(1)}s)`);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      tentativas++;
    }
    
    const autenticado = !!auth?.currentUser;
    if (autenticado) {
      console.log(`✅ Autenticação confirmada após ${((tentativas * 500) / 1000).toFixed(1)}s!`);
    } else {
      console.error(`❌ Timeout aguardando autenticação após ${((tentativas * 500) / 1000).toFixed(1)}s`);
    }
    
    return autenticado;
  };
  
  // 🔥 Carregar Firebase via CDN
  useEffect(() => {
    const loadFirebase = async () => {
      // Verificar se já está carregado
      if (window.firebase) {
        if (initFirebase()) {
          setFirebaseStatus('ready');
        } else {
          setFirebaseStatus('fallback');
        }
        return;
      }
      
      // Carregar scripts do Firebase
      const loadScript = (src) => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      };
      
      try {
        // Carregar Firebase App primeiro
        await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-app-compat.js');
        await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-auth-compat.js');
        await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-firestore-compat.js');
        await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-storage-compat.js');
        await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-functions-compat.js');
        
        // 🆕 Carregar bibliotecas de QR Code
        await loadScript('https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js');
        await loadScript('https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js');
        
        // Aguardar um pouco para garantir que os scripts foram processados
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (initFirebase()) {
          setFirebaseStatus('ready');
          console.log('🔥 Firebase carregado com sucesso!');
          console.log('📷 jsQR disponível:', !!window.jsQR);
          console.log('🔲 qrcode disponível:', !!window.qrcode);
          console.log('⚡ Functions disponível:', !!window.firebase?.functions);
        } else {
          setFirebaseStatus('fallback');
          console.log('⚠️ Usando modo de teste (Firebase não inicializado)');
        }
      } catch (error) {
        console.error('❌ Erro ao carregar Firebase:', error);
        setFirebaseStatus('fallback');
      }
    };
    
    loadFirebase();
  }, []);
  
  // 🔥 PERSISTÊNCIA DE SESSÃO - Detectar usuário já logado ao atualizar página
  // Só restaura se o usuário NÃO fez logout explícito
  useEffect(() => {
    if (firebaseStatus === 'ready' && firebaseReady && auth) {
      console.log('👂 Configurando listener de autenticação...');
      
      // Verificar se o usuário fez logout explícito (usar localStorage que persiste)
      const fezLogout = localStorage.getItem('facilitaprof_logout') === 'true';
      
      if (fezLogout) {
        console.log('ℹ️ Usuário fez logout anteriormente, fazendo signOut do Firebase...');
        // Fazer signOut do Firebase para limpar a sessão persistida
        auth.signOut().then(() => {
          console.log('✅ SignOut do Firebase realizado');
        }).catch(err => console.error('Erro no signOut:', err));
        return; // Não configurar listener
      }
      
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        try {
          if (user) {
            console.log('✅ Usuário autenticado detectado:', user.email);
            
            // v625: CRÍTICO - Aguardar token estar completamente disponível
            console.log('⏳ Aguardando token de autenticação...');
            await user.getIdToken(true); // Force refresh do token
            console.log('✅ Token de autenticação confirmado');
            
            // v625: Delay MAIOR para garantir que Firestore Rules valide o token
            console.log('⏳ Aguardando propagação do token no Firestore (2s)...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            console.log('✅ Delay de propagação completado');
            
            // v625: SINALIZAR que autenticação está pronta APÓS o delay
            setAuthReady(true);
            console.log('🎯 Auth marcado como pronto - triggers serão disparados');
            
            try {
              // Buscar dados completos do usuário no Firestore
              const userDoc = await db.collection('users').doc(user.uid).get();
              if (userDoc.exists) {
                let userData = { uid: user.uid, email: user.email, ...userDoc.data() };
                
                // v550: CRÍTICO - Se o cadastro Google está incompleto, NÃO prosseguir com login automático
                // Isso evita que o onAuthStateChanged feche o modal de completar cadastro
                if (userData.cadastroGoogleCompleto === false) {
                  console.log('⏸️ Cadastro Google incompleto - aguardando usuário completar modal');
                  // Abrir o modal para completar cadastro (caso ainda não esteja aberto)
                  setUserGooglePendente(userData);
                  setDadosCadastroGoogle({
                    nomeEscola: userData.escola || '',
                    nomeProfessor: userData.nome || user.displayName || '',
                    telefone: userData.telefone || ''
                  });
                  setShowModalCompletarCadastroGoogle(true);
                  setAuthReady(true);
                  return; // NÃO continuar com setCurrentUser/redirecionamento
                }
                
                // v515: Log detalhado para debug
                console.log('📋 Dados do usuário carregados:', {
                  nome: userData.nome,
                  tipo: userData.tipo,
                  escolaId: userData.escolaId,
                  creditos: userData.creditos,
                  seriesLiberadas: userData.seriesLiberadas
                });
                
                // 🔗 v511: Verificar se professor tem vínculo não reconhecido
                // v689: SEMPRE validar o escolaId - pode estar desatualizado ou apontar para documento excluído
                if (userData.tipo === 'professor') {
                  // Primeiro: se tem escolaId, verificar se o documento da escola existe
                  let escolaIdValido = false;
                  if (userData.escolaId) {
                    try {
                      const escolaCheck = await db.collection('users').doc(userData.escolaId).get();
                      if (escolaCheck.exists && escolaCheck.data().tipo === 'escola') {
                        escolaIdValido = true;
                        console.log('✅ escolaId validado - escola existe:', userData.escolaId);
                      } else {
                        console.warn('⚠️ escolaId inválido ou escola excluída:', userData.escolaId);
                      }
                    } catch (e) {
                      console.warn('⚠️ Erro ao validar escolaId:', e.message);
                    }
                  }
                  
                  // Se não tem escolaId OU o escolaId é inválido, buscar vínculo correto
                  if (!escolaIdValido) {
                    console.log('🔍 Buscando vínculo correto em professoresEscola...');
                    try {
                      // Buscar por email
                      let vinculoSnapshot = await db.collection('professoresEscola')
                        .where('professorEmail', '==', user.email)
                        .limit(1)
                        .get();
                      
                      // Se não encontrou por professorEmail, tenta por email
                      if (vinculoSnapshot.empty) {
                        vinculoSnapshot = await db.collection('professoresEscola')
                          .where('email', '==', user.email)
                          .limit(1)
                          .get();
                      }
                      
                      // Se não encontrou por email, tenta por professorId (UID)
                      if (vinculoSnapshot.empty) {
                        vinculoSnapshot = await db.collection('professoresEscola')
                          .where('professorId', '==', user.uid)
                          .limit(1)
                          .get();
                      }
                    
                      if (!vinculoSnapshot.empty) {
                        const vinculoDoc = vinculoSnapshot.docs[0];
                        const vinculoData = vinculoDoc.data();
                        console.log('🔗 Vínculo encontrado! Escola:', vinculoData.escolaId);
                        
                        // Buscar nome da escola
                        let escolaNome = 'Escola';
                        try {
                          const escolaDoc = await db.collection('users').doc(vinculoData.escolaId).get();
                          if (escolaDoc.exists) {
                            escolaNome = escolaDoc.data().nome || escolaDoc.data().nomeEscola || escolaDoc.data().escola || 'Escola';
                          }
                        } catch (e) { console.log('⚠️ Erro ao buscar nome da escola'); }
                        
                        // Atualizar documento do usuário com o vínculo correto
                        const seriesProf = vinculoData.serie ? [vinculoData.serie] : (userData.seriesLiberadas || []);
                        await db.collection('users').doc(user.uid).update({
                          escolaId: vinculoData.escolaId,
                          escolaNome: escolaNome,
                          serie: vinculoData.serie || userData.serie || '',
                          turma: vinculoData.turma || userData.turma || '',
                          turno: vinculoData.turno || userData.turno || '',
                          componente: vinculoData.componente || userData.componente || '',
                          seriesLiberadas: seriesProf,
                          plano: 'Vinculado'
                        });
                        
                        // Atualizar professoresEscola com o professorId se não tiver
                        if (!vinculoData.professorId && !vinculoData.professorUserId) {
                          await db.collection('professoresEscola').doc(vinculoDoc.id).update({
                            professorId: user.uid
                          });
                        }
                        
                        // Atualizar userData local
                        userData = {
                          ...userData,
                          escolaId: vinculoData.escolaId,
                          escolaNome: escolaNome,
                          serie: vinculoData.serie || userData.serie || '',
                          turma: vinculoData.turma || userData.turma || '',
                          seriesLiberadas: seriesProf,
                          plano: 'Vinculado'
                        };
                        console.log('✅ Vínculo corrigido automaticamente!');
                      } else {
                        console.log('📝 Nenhum vínculo encontrado em professoresEscola');
                        // Se tinha escolaId inválido, limpar
                        if (userData.escolaId && !escolaIdValido) {
                          console.log('🧹 Limpando escolaId inválido');
                          await db.collection('users').doc(user.uid).update({ escolaId: null, escolaNome: '' });
                          userData = { ...userData, escolaId: null, escolaNome: '' };
                        }
                      }
                    } catch (vinculoError) {
                      console.log('⚠️ Erro ao verificar vínculo:', vinculoError.message);
                    }
                  }
                }
                
                setCurrentUser(userData);
                
                // 🔧 v421: Buscar histórico de recargas (pedidos pagos)
                try {
                  const pedidosSnapshot = await db.collection('pedidos')
                    .where('userId', '==', user.uid)
                    .where('status', '==', 'paid')
                    .orderBy('createdAt', 'desc')
                    .limit(20)
                    .get();
                  
                  const historico = pedidosSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  }));
                  setHistoricoRecargas(historico);
                  console.log('📋 Histórico de recargas carregado:', historico.length, 'registros');
                } catch (histError) {
                  console.log('⚠️ Erro ao buscar histórico:', histError.message);
                }
                
                // 🔧 v411: Atualizar cabeçalho com dados do usuário ao restaurar sessão
                // 🔧 v518: Usar escolaNome para professor vinculado
                // 🔧 v541: Não sobrescrever se cadastro Google foi recém completado
                if (!cadastroGoogleRecemCompleto) {
                  setCabecalhoConfig(prev => ({
                    ...prev,
                    professor: userData.tipo === 'professor' ? userData.nome : '',
                    nomeEscola: userData.escolaNome || userData.escola || ''
                  }));
                }
                
                // Redirecionar baseado no tipo de usuário
                // v696: só força a tela inicial UMA VEZ por sessão — evita sobrescrever
                // navegação manual do usuário caso esse callback demore ou seja refeito
                // (ex: refresh de token do Firebase logo após o login)
                if (!telaInicialJaDefinida.current) {
                  telaInicialJaDefinida.current = true;
                  if (userData.tipo === 'admin') {
                    setCurrentView('admin-dashboard');
                  } else if (userData.tipo === 'escola') {
                    setCurrentView('escola-estatisticas');
                  } else {
                    setCurrentView('professor-gerador');
                  }
                } else {
                  console.log('ℹ️ Tela inicial já definida anteriormente — não sobrescrevendo navegação atual');
                }
                console.log('✅ Sessão restaurada para:', userData.nome);
              }
            } catch (error) {
              console.error('Erro ao restaurar sessão:', error);
            }
          } else {
            console.log('ℹ️ Nenhum usuário autenticado');
             // v605: Não há usuário, finaliza loading
          }
        } catch (err) {
          // Ignorar erros de extensões do Chrome
          if (!err.message?.includes('message channel closed')) {
            console.error('Erro no listener de autenticação:', err);
          }
        }
      });
      
      return () => unsubscribe();
    }
  }, [firebaseStatus]);
  
  // ⏱️ TIMEOUT DE INATIVIDADE - Desconecta após 10 minutos sem interação
  useEffect(() => {
    if (!currentUser) return;
    
    let timeoutId = null;
    const TEMPO_INATIVIDADE = 10 * 60 * 1000; // 10 minutos em milissegundos
    
    const resetarTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('⏱️ Timeout de inatividade - desconectando usuário...');
        // Marcar como logout explícito (por timeout)
        localStorage.setItem('facilitaprof_logout', 'true');
        if (auth) {
          auth.signOut().then(() => {
            setCurrentUser(null);
            setCurrentView('landing');
            alert('Sua sessão expirou por inatividade. Faça login novamente.');
          });
        } else {
          setCurrentUser(null);
          setCurrentView('landing');
        }
      }, TEMPO_INATIVIDADE);
    };
    
    // Eventos que resetam o timeout
    const eventos = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    eventos.forEach(evento => window.addEventListener(evento, resetarTimeout));
    
    // Iniciar timeout
    resetarTimeout();
    
    // Cleanup
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      eventos.forEach(evento => window.removeEventListener(evento, resetarTimeout));
    };
  }, [currentUser]);
  
  // 🔥 Estado para questões carregadas do Firebase
  const [questoesFirebase, setQuestoesFirebase] = useState([]);
  const [questoesCarregadas, setQuestoesCarregadas] = useState(false);
  const [carregandoQuestoes, setCarregandoQuestoes] = useState(false); // v650: indicador de carregamento
  const [reloadTrigger, setReloadTrigger] = useState(0); // v660: trigger para forçar reload
  
  // 🔥 Carregar questões do Firebase quando o status mudar para 'ready'
  // v638: NÃO carregar questões automaticamente no login!
  // Questões serão carregadas APENAS quando usuário aplicar filtros
  useEffect(() => {
    // v638: Apenas log informativo - não carrega nada
    if (firebaseStatus === 'ready' && firebaseReady) {
      console.log('✅ [v638] Firebase pronto. Questões NÃO serão carregadas automaticamente.');
      console.log('   💡 Questões carregarão quando usuário aplicar filtros.');
    }
  }, [firebaseStatus, firebaseReady]);
  
  // 🔥 Estado para habilidades carregadas do Firebase
  const [habilidadesFirebase, setHabilidadesFirebase] = useState([]);
  const [habilidadesCarregadas, setHabilidadesCarregadas] = useState(false);
  
  // 🔥 Carregar habilidades do Firebase quando o status mudar para 'ready'
  useEffect(() => {
    const carregarHabilidades = async () => {
      // v621: Agora também reage a authReady
      if (firebaseStatus === 'ready' && firebaseReady && db && !habilidadesCarregadas && authReady) {
        try {
          console.log('🔄 [HABILIDADES] Carregando do Firebase...');
          const snapshot = await db.collection('habilidades').get();
          const habilidadesDoFirebase = [];
          snapshot.forEach(doc => {
            habilidadesDoFirebase.push({ id: doc.id, ...doc.data() });
          });
          // Ordenar por dataCriacao (ordem de inserção) ou por ordem se existir
          habilidadesDoFirebase.sort((a, b) => {
            // Se tiver campo ordem, usar ele
            if (a.ordem !== undefined && b.ordem !== undefined) {
              return a.ordem - b.ordem;
            }
            // Senão, ordenar por dataCriacao
            const dataA = a.dataCriacao?.toDate?.() || a.dataCriacao || new Date(0);
            const dataB = b.dataCriacao?.toDate?.() || b.dataCriacao || new Date(0);
            return dataA - dataB;
          });
          setHabilidadesFirebase(habilidadesDoFirebase);
          setHabilidadesCarregadas(true);
          console.log(`✅ [HABILIDADES] ${habilidadesDoFirebase.length} carregadas do Firebase!`);
        } catch (error) {
          console.error('❌ [HABILIDADES] Erro ao carregar:', error);
        }
      }
    };
    
    carregarHabilidades();
  }, [firebaseStatus, habilidadesCarregadas, authReady]);
  
  // 🔥 Estado para séries carregadas do Firebase
  const [seriesFirebase, setSeriesFirebase] = useState([]);
  const [seriesCarregadas, setSeriesCarregadas] = useState(false);
  
  // 🔥 Carregar séries do Firebase
  useEffect(() => {
    const carregarSeries = async () => {
      // v626: Retry com diagnóstico detalhado
      if (firebaseStatus === 'ready' && firebaseReady && db && !seriesCarregadas && authReady) {
        let tentativas = 0;
        const maxTentativas = 3;
        
        while (tentativas < maxTentativas) {
          try {
            console.log(`🔄 [SÉRIES] Tentativa ${tentativas + 1}/${maxTentativas} - Carregando do Firebase...`);
            
            // v626: Verificar se auth está realmente disponível
            if (!auth?.currentUser) {
              console.warn('⚠️ [SÉRIES] auth.currentUser não disponível, aguardando...');
              await new Promise(resolve => setTimeout(resolve, 1000));
              tentativas++;
              continue;
            }
            
            const snapshot = await db.collection('series').get();
            const seriesDoFirebase = [];
            snapshot.forEach(doc => {
              seriesDoFirebase.push({ id: doc.id, ...doc.data() });
            });
            
            console.log(`📊 [SÉRIES] Query retornou ${snapshot.size} documentos`);
            
            if (seriesDoFirebase.length === 0 && tentativas < maxTentativas - 1) {
              console.warn(`⚠️ [SÉRIES] Retornou 0 séries, tentando novamente em 2s...`);
              await new Promise(resolve => setTimeout(resolve, 2000));
              tentativas++;
              continue;
            }
            
            // v689d: Filtrar séries SAEB (distinção fica nos componentes)
            const seriesFiltradas = seriesDoFirebase.filter(s => {
              const nome = (s.nome || "").toLowerCase();
              return !nome.includes("saeb") && !nome.includes("simulado");
            });
            setSeriesFirebase(seriesFiltradas);
            setSeriesCarregadas(true);
            console.log(`✅ [SÉRIES] ${seriesDoFirebase.length} carregadas do Firebase!`);
            
            if (seriesDoFirebase.length === 0) {
              console.error('❌ [SÉRIES] ATENÇÃO: Coleção vazia ou sem permissão! Verifique:');
              console.error('   1. Firestore Console: Existem documentos em /series?');
              console.error('   2. Firebase Rules: Permissão de leitura configurada?');
              console.error('   3. Token válido:', auth.currentUser?.uid);
            }
            
            break; // Sucesso, sair do loop
            
          } catch (error) {
            console.error(`❌ [SÉRIES] Erro na tentativa ${tentativas + 1}:`, error);
            console.error('   Código:', error.code);
            console.error('   Mensagem:', error.message);
            
            if (error.code === 'permission-denied') {
              console.error('   🚨 ERRO DE PERMISSÃO! Verifique Firebase Rules');
              setSeriesCarregadas(true); // Marcar como carregada para não tentar mais
              break;
            }
            
            tentativas++;
            if (tentativas < maxTentativas) {
              console.log(`   ⏳ Aguardando 2s antes da próxima tentativa...`);
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
        }
        
        if (tentativas >= maxTentativas && !seriesCarregadas) {
          console.error('❌ [SÉRIES] Falhou após todas as tentativas');
          setSeriesCarregadas(true);
        }
      }
    };
    
    carregarSeries();
  }, [firebaseStatus, seriesCarregadas, authReady]);
  
  // 🔥 Estado para componentes curriculares do Firebase
  const [componentesFirebase, setComponentesFirebase] = useState([]);
  const [componentesCarregados, setComponentesCarregados] = useState(false);
  
  // 🔥 Carregar componentes curriculares do Firebase
  useEffect(() => {
    const carregarComponentes = async () => {
      // v626: Retry com diagnóstico detalhado
      if (firebaseStatus === 'ready' && firebaseReady && db && !componentesCarregados && authReady) {
        let tentativas = 0;
        const maxTentativas = 3;
        
        while (tentativas < maxTentativas) {
          try {
            console.log(`🔄 [COMPONENTES] Tentativa ${tentativas + 1}/${maxTentativas} - Carregando do Firebase...`);
            
            // v626: Verificar se auth está realmente disponível
            if (!auth?.currentUser) {
              console.warn('⚠️ [COMPONENTES] auth.currentUser não disponível, aguardando...');
              await new Promise(resolve => setTimeout(resolve, 1000));
              tentativas++;
              continue;
            }
            
            const snapshot = await db.collection('componentes').get();
            const componentesDoFirebase = [];
            snapshot.forEach(doc => {
              componentesDoFirebase.push({ id: doc.id, ...doc.data() });
            });
            
            console.log(`📊 [COMPONENTES] Query retornou ${snapshot.size} documentos`);
            
            if (componentesDoFirebase.length === 0 && tentativas < maxTentativas - 1) {
              console.warn(`⚠️ [COMPONENTES] Retornou 0 componentes, tentando novamente em 2s...`);
              await new Promise(resolve => setTimeout(resolve, 2000));
              tentativas++;
              continue;
            }
            
            setComponentesFirebase(componentesDoFirebase);
            setComponentesCarregados(true);
            console.log(`✅ [COMPONENTES] ${componentesDoFirebase.length} carregados do Firebase!`);
            
            if (componentesDoFirebase.length === 0) {
              console.error('❌ [COMPONENTES] ATENÇÃO: Coleção vazia ou sem permissão! Verifique:');
              console.error('   1. Firestore Console: Existem documentos em /componentes?');
              console.error('   2. Firebase Rules: Permissão de leitura configurada?');
              console.error('   3. Token válido:', auth.currentUser?.uid);
            }
            
            break; // Sucesso, sair do loop
            
          } catch (error) {
            console.error(`❌ [COMPONENTES] Erro na tentativa ${tentativas + 1}:`, error);
            console.error('   Código:', error.code);
            console.error('   Mensagem:', error.message);
            
            if (error.code === 'permission-denied') {
              console.error('   🚨 ERRO DE PERMISSÃO! Verifique Firebase Rules');
              setComponentesCarregados(true); // Marcar como carregada para não tentar mais
              break;
            }
            
            tentativas++;
            if (tentativas < maxTentativas) {
              console.log(`   ⏳ Aguardando 2s antes da próxima tentativa...`);
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
        }
        
        if (tentativas >= maxTentativas && !componentesCarregados) {
          console.error('❌ [COMPONENTES] Falhou após todas as tentativas');
          setComponentesCarregados(true);
        }
      }
    };
    
    carregarComponentes();
  }, [firebaseStatus, componentesCarregados, authReady]);
  
  // 🔥 Estado para recargas carregadas do Firebase
  const [recargasCarregadas, setRecargasCarregadas] = useState(false);
  
  // 🔥 Carregar recargas do Firebase
  useEffect(() => {
    const carregarRecargas = async () => {
      // v674: Remover authReady - recargas são públicas (Landing Page)
      if (firebaseStatus === 'ready' && firebaseReady && db && !recargasCarregadas) {
        try {
          console.log('🔄 [v674-RECARGAS] Carregando do Firebase...');
          const snapshot = await db.collection('recargas').get();
          console.log('📦 [v674] Snapshot recargas:', snapshot.size, 'documentos');
          
          if (!snapshot.empty) {
            const recargasDoFirebase = [];
            snapshot.forEach(doc => {
              console.log('📄 [v674] Recarga encontrada:', doc.id, doc.data());
              recargasDoFirebase.push({ id: doc.id, ...doc.data() });
            });
            // Ordenar por preço localmente
            recargasDoFirebase.sort((a, b) => a.preco - b.preco);
            setRecargas(recargasDoFirebase);
            console.log(`✅ [v674-RECARGAS] ${recargasDoFirebase.length} carregadas do Firebase!`);
          } else {
            console.log('ℹ️ [v674] Nenhuma recarga no Firebase');
          }
          setRecargasCarregadas(true);
        } catch (error) {
          console.error('❌ [v674-RECARGAS] Erro ao carregar:', error);
          setRecargasCarregadas(true);
        }
      }
    };
    
    carregarRecargas();
  }, [firebaseStatus, firebaseReady, recargasCarregadas]); // v674: Remover authReady das dependências
  
  // 🔥 Estado para turmas do Firebase
  const [turmasFirebase, setTurmasFirebase] = useState([]);
  const [turmasCarregadas, setTurmasCarregadas] = useState(false);
  const [ultimoUserIdTurmas, setUltimoUserIdTurmas] = useState(null);
  
  // 🔥 Carregar turmas do Firebase (por usuário)
  // v506: Resetar quando usuário mudar
  // v520: Escolas também carregam turmas dos professores vinculados
  useEffect(() => {
    const carregarTurmas = async () => {
      if (firebaseStatus === 'ready' && firebaseReady && db && currentUser?.uid) {
        // v506: Verificar se o usuário mudou
        if (ultimoUserIdTurmas !== currentUser.uid) {
          console.log('🔄 [TURMAS] Usuário mudou de', ultimoUserIdTurmas, 'para', currentUser.uid, '- resetando turmas');
          setTurmasFirebase([]);
          setTurmasCarregadas(false);
          setUltimoUserIdTurmas(currentUser.uid);
        }
        
        if (!turmasCarregadas || ultimoUserIdTurmas !== currentUser.uid) {
          try {
            console.log('🔄 [CARREGAR] Carregando turmas do Firebase para userId:', currentUser.uid);
            
            let turmasDoFirebase = [];
            
            // Carregar turmas do próprio usuário
            const snapshot = await db.collection('turmas').where('userId', '==', currentUser.uid).get();
            snapshot.forEach(doc => {
              const data = doc.data();
              console.log('📄 [CARREGAR] Turma própria encontrada:', doc.id, '- Nome:', data.nome);
              turmasDoFirebase.push({ id: doc.id, ...data, origem: 'proprio' });
            });
            
            // v689d: Se for professor vinculado, também carregar turmas da escola
            if (currentUser?.tipo === 'professor' && currentUser?.escolaId) {
              try {
                const turmasEscola = await db.collection('turmas').where('escolaId', '==', currentUser.escolaId).get();
                turmasEscola.forEach(doc => {
                  if (!turmasDoFirebase.find(t => t.id === doc.id)) {
                    const data = doc.data();
                    console.log('📄 [CARREGAR] Turma da escola vinculada:', doc.id, '- Nome:', data.nome);
                    turmasDoFirebase.push({ id: doc.id, ...data, origem: 'escola' });
                  }
                });
              } catch (e) { console.warn('⚠️ Erro turmas escola:', e); }
            }
            
            // 🔧 v520: Se for escola, também carregar turmas dos professores vinculados
            if (currentUser?.tipo === 'escola') {
              try {
                const escolaId = currentUser.uid;
                const profSnapshot = await db.collection('professoresEscola')
                  .where('escolaId', '==', escolaId)
                  .get();
                
                const professorIds = [];
                profSnapshot.forEach(doc => {
                  const profId = doc.data().professorId;
                  if (profId) professorIds.push(profId);
                });
                
                // Carregar turmas de cada professor vinculado
                for (const profId of professorIds) {
                  const turmasProf = await db.collection('turmas').where('userId', '==', profId).get();
                  turmasProf.forEach(doc => {
                    const data = doc.data();
                    // Evitar duplicatas
                    if (!turmasDoFirebase.find(t => t.id === doc.id)) {
                      console.log('📄 [CARREGAR] Turma de professor vinculado:', doc.id, '- Nome:', data.nome);
                      turmasDoFirebase.push({ id: doc.id, ...data, origem: 'professor', professorId: profId });
                    }
                  });
                }
                console.log(`✅ [v520] Turmas de professores vinculados carregadas`);
              } catch (e) {
                console.error('⚠️ Erro ao carregar turmas de professores:', e);
              }
            }
            
            setTurmasFirebase(turmasDoFirebase);
            setTurmasCarregadas(true);
            setUltimoUserIdTurmas(currentUser.uid);
            console.log(`✅ [CARREGAR] ${turmasDoFirebase.length} turmas carregadas do Firebase!`);
          } catch (error) {
            console.error('❌ [CARREGAR] Erro ao carregar turmas:', error);
          }
        }
      }
    };
    
    carregarTurmas();
  }, [firebaseStatus, firebaseReady, currentUser?.uid, currentUser?.tipo]);
  
  // 🔥 Estado para imagens da landing page
  // ✅ OTIMIZADO v400: Imagens WebP do Firebase Hosting
  // Servidas via CDN com cache de 1 ano (public/assets/)
  // Carregamento instantâneo (~100ms) vs 5s do Storage
  // 🆕 v400: Adicionado logo escuro para modais e atividades de exemplo
  const LANDING_IMAGES_DEFAULT = {
    logomarca: '/assets/logo.webp',
    logomarcaEscuro: '/assets/logo-escuro.webp', // 🆕 Para modais com fundo escuro
    professoraImg: '/assets/professora.webp',
    // 🆕 Atividades de exemplo (carrossel da landing)
    atividadeExemplo1: '/assets/atividade1.webp',
    atividadeExemplo2: '/assets/atividade2.webp',
    atividadeExemplo3: '/assets/atividade3.webp',
    atividadeExemplo4: '/assets/atividade4.webp',
    atividadeExemplo5: '/assets/atividade5.webp',
    atividadeExemplo6: '/assets/atividade6.webp',
    atividadeExemplo7: '/assets/atividade7.webp',
    atividadeExemplo8: '/assets/atividade8.webp'
  };

  const [landingImagesFirebase, setLandingImagesFirebase] = useState(LANDING_IMAGES_DEFAULT);
  const [landingImagesCarregadas, setLandingImagesCarregadas] = useState(true); // ✅ v399: Já marca como carregadas
  
  // 🚫 DESABILITADO v399: Não carregar mais do Firestore (BUG CORRIGIDO)
  // PROBLEMA: O Firestore sobrescrevia as URLs otimizadas do Hosting com URLs antigas do Storage
  // SOLUÇÃO: Desabilitado completamente. As imagens agora vêm exclusivamente de /assets/
  // Se precisar carregar do Firestore novamente, altere landingImagesCarregadas para false acima
  /*
  useEffect(() => {
    const carregarLandingImages = async () => {
      if (firebaseStatus === 'ready' && firebaseReady && db && !landingImagesCarregadas) {
        try {
          console.log('🔄 Carregando imagens da landing do Firebase...');
          const doc = await db.collection('configuracoes').doc('landing').get();
          if (doc.exists) {
            const data = doc.data();
            // Mescla com as URLs padrão (não sobrescreve se não existir)
            setLandingImagesFirebase(prev => ({
              ...LANDING_IMAGES_DEFAULT,
              ...prev,
              ...data
            }));
            console.log('✅ Imagens da landing carregadas do Firebase!');
          }
          setLandingImagesCarregadas(true);
        } catch (error) {
          console.error('❌ Erro ao carregar imagens da landing:', error);
        }
      }
    };
    
    carregarLandingImages();
  }, [firebaseStatus, landingImagesCarregadas]);
  */
  
  const [currentView, setCurrentView] = useState('landing'); // 🆕 Inicia na landing page
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // 📱 Menu mobile
  const [loginTipo, setLoginTipo] = useState('professor');
  const [showCadastro, setShowCadastro] = useState(false);
  const [aceitouTermos, setAceitouTermos] = useState(false);
  const [showModalTermosLogin, setShowModalTermosLogin] = useState(false);
  const [showModalPoliticaLogin, setShowModalPoliticaLogin] = useState(false);
  const [showModalRecuperarSenha, setShowModalRecuperarSenha] = useState(false);
  const [emailRecuperacao, setEmailRecuperacao] = useState('');
  const [modoRecuperarSenha, setModoRecuperarSenha] = useState(false); // 🔧 v428: Modo dentro do modal de login
  const [etapaRecuperacao, setEtapaRecuperacao] = useState(1);
  const [codigoRecuperacao, setCodigoRecuperacao] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');
  const [senhaAtual, setSenhaAtual] = useState(''); // 🔧 v424: Para alterar senha
  const [codigoGerado, setCodigoGerado] = useState('');
  const [dadosCadastro, setDadosCadastro] = useState({ nome: '', email: '', telefone: '', senha: '', confirmarSenha: '', tipo: 'fisica', escola: '' });
  
  // 🌐 Estados da Landing Page
  const [landingFaqAberto, setLandingFaqAberto] = useState(null);
  const [landingMenuMobileAberto, setLandingMenuMobileAberto] = useState(false);
  const [landingMostrarLogin, setLandingMostrarLogin] = useState(false);
  const [landingMostrarCadastro, setLandingMostrarCadastro] = useState(false);
  const [landingCarrosselIndex, setLandingCarrosselIndex] = useState(0);
  const [telefoneSuporte, setTelefoneSuporte] = useState('5579998923895'); // Telefone padrão para WhatsApp
  
  // v540: Estados para completar cadastro após login com Google
  const [showModalCompletarCadastroGoogle, setShowModalCompletarCadastroGoogle] = useState(false);
  const [dadosCadastroGoogle, setDadosCadastroGoogle] = useState({ nomeEscola: '', nomeProfessor: '', telefone: '' });
  const [userGooglePendente, setUserGooglePendente] = useState(null);
  const [cadastroGoogleRecemCompleto, setCadastroGoogleRecemCompleto] = useState(false); // v541: Flag para evitar sobrescrita
  
  // v542: Estados para verificação de telefone via WhatsApp
  const [telefoneVerificacao, setTelefoneVerificacao] = useState('');
  const [codigoVerificacao, setCodigoVerificacao] = useState('');
  const [etapaVerificacao, setEtapaVerificacao] = useState('telefone'); // 'telefone' | 'codigo' | 'verificado'
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);
  const [verificandoCodigo, setVerificandoCodigo] = useState(false);
  const [telefoneVerificadoTemp, setTelefoneVerificadoTemp] = useState(null); // Telefone verificado temporário
  const [tempoReenvio, setTempoReenvio] = useState(0); // Countdown para reenvio
  
  // v689d: Restaurar estado da verificação WhatsApp ao voltar do app (mobile)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('fp_whatsapp_verif');
      if (saved) {
        const data = JSON.parse(saved);
        // Só restaurar se ainda está na etapa de código (não verificado)
        if (data.etapa === 'codigo' && data.telefone && data.modalType) {
          console.log('🔄 Restaurando verificação WhatsApp...');
          setEtapaVerificacao('codigo');
          if (data.modalType === 'google') {
            setDadosCadastroGoogle(prev => ({...prev, telefone: data.telefone}));
            setShowModalCompletarCadastroGoogle(true);
          } else if (data.modalType === 'cadastro') {
            setDadosCadastro(prev => ({...prev, telefone: data.telefone}));
            setLandingMostrarCadastro(true);
          }
          setTempoReenvio(0); // Permitir reenvio imediato
        }
      }
    } catch (e) { console.warn('Erro ao restaurar verificação:', e); }
  }, []);
  
  // 🎬 Estado para vídeos tutoriais
  const [videosTutoriais, setVideosTutoriais] = useState([
    { id: 1, titulo: 'Como criar atividades', url: '', ativo: true },
    { id: 2, titulo: 'Como usar o Simulado SAEB', url: '', ativo: true },
    { id: 3, titulo: 'Como configurar o cabeçalho', url: '', ativo: true }
  ]);
  
  // 📧 Estados para convites pendentes
  const [convitesPendentes, setConvitesPendentes] = useState([]);
  
  // 🌐 Estados para Landing Page (uploads)
  const [landingAssets, setLandingAssets] = useState({
    logomarca: null,
    logomarcaPreview: null,
    professoraImg: null,
    professoraImgPreview: null,
    atividadeExemplo1: null, atividadeExemplo1Preview: null,
    atividadeExemplo2: null, atividadeExemplo2Preview: null,
    atividadeExemplo3: null, atividadeExemplo3Preview: null,
    atividadeExemplo4: null, atividadeExemplo4Preview: null,
    atividadeExemplo5: null, atividadeExemplo5Preview: null,
    atividadeExemplo6: null, atividadeExemplo6Preview: null,
    atividadeExemplo7: null, atividadeExemplo7Preview: null,
    atividadeExemplo8: null, atividadeExemplo8Preview: null
  });
  
  const [tipoGeracao, setTipoGeracao] = useState('questao');
  const [filtros, setFiltros] = useState({ serie: '', componente: '', habilidade: '', habilidadeId: '', tamanho: '' });
  
  // 🔥 v639: Carregar questões quando FILTROS DO PROFESSOR mudarem
  useEffect(() => {
    const carregarPorFiltrosProfessor = async () => {
      // Só carregar se tiver série E componente (filtros mínimos professor)
      if (!filtros.serie || !filtros.componente) {
        console.log('⏸️ [v639-PROF] Aguardando série + componente...');
        return;
      }
      
      if (!firebaseReady || !db) {
        console.log('⚠️ [v639-PROF] Firebase não pronto');
        return;
      }
      
      console.log('🔄 [v640-PROF] Carregando questões...');
      setCarregandoQuestoes(true); // v650: mostrar loading
      const result = await buscarQuestoes({
        serie: filtros.serie,
        componente: filtros.componente,
        habilidade: filtros.habilidadeId || filtros.habilidade
      }, habilidadesFirebase);  // v640: Passar array de habilidades
      
      if (result.success) {
        setQuestoesFirebase(prev => {
                  // v689d: Preservar questões já selecionadas nas páginas ao trocar filtro
                  const idsSel = new Set(paginas.flatMap(p => p.questoes).map(String));
                  const preservadas = prev.filter(q => idsSel.has(String(q.id)));
                  const idsPreservados = new Set(preservadas.map(q => String(q.id)));
                  const novas = result.questoes.filter(q => !idsPreservados.has(String(q.id)));
                  return [...preservadas, ...novas];
                });
        setQuestoesCarregadas(true);
        console.log(`✅ [v640-PROF] ${result.questoes.length} questões carregadas`);
      }
      setCarregandoQuestoes(false); // v650: esconder loading
    };
    
    carregarPorFiltrosProfessor();
  }, [filtros.serie, filtros.componente, filtros.habilidadeId, filtros.habilidade, firebaseReady, habilidadesFirebase]);
  
  const [tipoFiltroSimulado, setTipoFiltroSimulado] = useState('habilidade-unica'); // 'habilidade-unica' ou 'texto-multiplas'
  const [blocosSimulado, setBlocosSimulado] = useState([
    { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
    { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
  ]);
  const [blocoAtivo, setBlocoAtivo] = useState(1);
  const [componenteSimulado, setComponenteSimulado] = useState('Língua Portuguesa'); // Componente único do simulado
  const [showModalSegundoBloco, setShowModalSegundoBloco] = useState(false);
  const [showModalSegundoBlocoEscola, setShowModalSegundoBlocoEscola] = useState(false);
  const [paginaSimuladoAtiva, setPaginaSimuladoAtiva] = useState(3); // Começa mostrando questões
  
  // 🔧 v536: Função helper para obter nome do aluno (pode ser string ou objeto)
  // v695: Extrai timestamp (ms) de dataCadastro/createdAt em qualquer formato
  // (Timestamp do Firestore, {seconds}, string ISO ou Date) — usado para ordenação
  const getDataCadastroMs = (c) => {
    const campo = c?.dataCadastro || c?.createdAt;
    if (!campo) return 0;
    try {
      if (campo.toDate) return campo.toDate().getTime();
      if (campo.seconds) return campo.seconds * 1000;
      const data = new Date(campo);
      return isNaN(data.getTime()) ? 0 : data.getTime();
    } catch (e) {
      return 0;
    }
  };

  // v695: Formata dataCadastro/createdAt para exibição em pt-BR
  const formatarDataCadastro = (c) => {
    const ms = getDataCadastroMs(c);
    return ms ? new Date(ms).toLocaleDateString('pt-BR') : '-';
  };

  const getNomeAluno = (aluno) => {
    if (typeof aluno === 'string') return aluno;
    if (typeof aluno === 'object' && aluno !== null) return aluno.nome || aluno.name || String(aluno);
    return String(aluno);
  };
  
  // 🔢 Função helper para contar questões reais (incluindo questões vinculadas de textos)
  const contarQuestoesReais = (questoesSelecionadasIds, listaQuestoes) => {
    return questoesSelecionadasIds.reduce((total, id) => {
      const q = listaQuestoes.find(quest => String(quest.id) === String(id));
      if (q?.tamanho === 'texto-multiplas' && q?.questoesVinculadas) {
        return total + q.questoesVinculadas.length;
      }
      return total + 1;
    }, 0);
  };
  
  // 📏 v475: Função para calcular páginas baseado nos tamanhos reais das questões
  // TAMANHOS (título 0.5cm + questão + padding 1.0cm):
  // Pequena: 0.5 + 5.0 + 1.0 = 6.5cm
  // Média: 0.5 + 7.0 + 1.0 = 8.5cm
  // Grande: 0.5 + 10.5 + 1.0 = 12.0cm
  // Inteira: 0.5 + 20.5 + 0.8 = 21.8cm
  // 
  // Página A4 = 29.7cm - margens (1.0cm top + 0.8cm bottom) = ~27.9cm úteis
  // Primeira página com cabeçalho: 27.9 - 5 = 22.9cm disponíveis para questões
  // 🔧 v489: @page margin:0, body padding: 10mm/8mm, limite 27.9cm
  const calcularPaginasPorTamanho = (questoesSelecionadasIds, listaQuestoes, temCabecalho = false) => {
    // Configurações de tamanho em cm (título 0.5cm + conteúdo + padding 0.8cm)
    // v552: Novos tamanhos para texto-base e questao-vinculada
    const TAMANHOS = {
      'pequena': 6.3,       // 0.5 + 5.0 + 0.8
      'media': 8.3,         // 0.5 + 7.0 + 0.8
      'grande': 11.8,       // 0.5 + 10.5 + 0.8
      'inteira': 21.8,      // 0.5 + 20.5 + 0.8
      'texto-base': 14.0,   // v552: Texto base (14cm)
      'questao-vinculada': 6.3, // v552: Questão vinculada (igual pequena)
      'texto-multiplas': 26.6 // v552: Texto base (14cm) + 2 questões (6.3cm × 2) = 26.6cm
    };
    const PADDING_QUESTAO = 0.8; // cm de padding entre questões
    const ALTURA_PAGINA = 27.9; // cm úteis em uma página A4 (29.7 - 1.0 - 0.8 margens)
    const ALTURA_CABECALHO = 5.0; // cm ocupados pelo cabeçalho + título
    
    if (!questoesSelecionadasIds || questoesSelecionadasIds.length === 0) {
      return 0;
    }
    
    let paginas = 1;
    // Primeira página: se tem cabeçalho, começa com 5cm já usados
    let alturaUsada = temCabecalho ? ALTURA_CABECALHO : 0;
    
    questoesSelecionadasIds.forEach((qId, index) => {
      const q = listaQuestoes.find(quest => String(quest.id) === String(qId));
      const tamanho = q?.tamanho || 'media';
      let alturaQuestao = TAMANHOS[tamanho] || TAMANHOS['media'];
      
      // Questão inteira ou texto-multiplas: só força nova página se NÃO couber com o que já tem
      // v566: Removido o force de nova página - agora verifica se realmente cabe
      if ((tamanho === 'inteira' || tamanho === 'texto-multiplas') && alturaUsada > ALTURA_CABECALHO) {
        // Só força nova página se já tem mais que só o cabeçalho (ou seja, já tem outras questões)
        paginas++;
        alturaUsada = 0;
      }
      
      // Verificar se cabe na página atual
      // Considera que a última questão não tem padding (economia de 1cm)
      const alturaComPadding = alturaQuestao;
      const alturaSemPadding = alturaQuestao - PADDING_QUESTAO;
      
      // Tenta encaixar: primeiro com padding, se não couber, tenta sem padding (como última da página)
      if (alturaUsada + alturaComPadding <= ALTURA_PAGINA) {
        // Cabe com padding
        alturaUsada += alturaComPadding;
      } else if (alturaUsada + alturaSemPadding <= ALTURA_PAGINA) {
        // Cabe sem padding (será a última questão desta página)
        alturaUsada = ALTURA_PAGINA; // Marca como cheia
      } else {
        // Não cabe de jeito nenhum, criar nova página
        paginas++;
        alturaUsada = alturaComPadding;
      }
      
      // Se questão é inteira ou texto-multiplas, força nova página para próxima questão
      if (tamanho === 'inteira' || tamanho === 'texto-multiplas') {
        alturaUsada = ALTURA_PAGINA; // Marca página como cheia
      }
    });
    
    return paginas;
  };
  
  // 📊 v475: Calcular total de páginas do Simulado (inclui cartão + capas + questões)
  const calcularPaginasSimulado = (blocosAtivos, listaQuestoes) => {
    let totalPaginas = 1; // Cartão-resposta genérico
    
    // Bloco 1
    if (blocosAtivos[0]?.questoesSelecionadas?.length > 0) {
      totalPaginas += 1; // Capa do Bloco 1
      totalPaginas += calcularPaginasPorTamanho(blocosAtivos[0].questoesSelecionadas, listaQuestoes, false);
    }
    
    // Bloco 2
    if (blocosAtivos[1]?.questoesSelecionadas?.length > 0) {
      totalPaginas += 1; // Capa do Bloco 2
      totalPaginas += calcularPaginasPorTamanho(blocosAtivos[1].questoesSelecionadas, listaQuestoes, false);
    }
    
    return totalPaginas;
  };
  
  // 📋 Função helper para expandir gabaritos (texto-multiplas retorna array de gabaritos)
  const expandirGabaritos = (questoesSelecionadasIds, listaQuestoes) => {
    const gabaritos = [];
    questoesSelecionadasIds.forEach(id => {
      const q = listaQuestoes.find(quest => String(quest.id) === String(id));
      if (q?.tamanho === 'texto-multiplas' && q?.questoesVinculadas) {
        // Adiciona gabarito de cada questão vinculada
        q.questoesVinculadas.forEach(qv => {
          gabaritos.push({ gabarito: qv.gabarito, habilidade: qv.habilidade, questaoId: id, vinculadaId: qv.id });
        });
      } else if (q) {
        gabaritos.push({ gabarito: q.gabarito, habilidade: q.habilidade, questaoId: id });
      }
    });
    return gabaritos;
  };
  
  // 🔧 v404: Função helper para obter gabaritos da fonte correta (Firebase ou blocos locais)
  // Prioriza gabaritos salvos no Firebase para não perder após gerar PDF
  const obterGabaritosSimulado = (simuladoSelecionado, blocosSimuladoLocal, listaQuestoes, bloco = 1) => {
    // v713: 9º ano tem 13 questões por bloco (26 no total); 2º/5º ano têm 11 (22 no total)
    const totalPorBloco = questoesPorBlocoSAEB(simuladoSelecionado?.serie || configSimulado.serie || filtrosEscola.serie || '');
    // Se tem simulado selecionado com gabaritos salvos, usar esses
    if (simuladoSelecionado?.gabarito && Object.keys(simuladoSelecionado.gabarito).length > 0) {
      const gabSalvo = simuladoSelecionado.gabarito;
      const descSalvo = simuladoSelecionado.descritores || {}; // 🔧 v475: Usar descritores salvos
      const gabOverride = simuladoSelecionado.gabaritosOverride || {}; // v672: Gabaritos editados pelo professor
      const inicio = bloco === 1 ? 1 : totalPorBloco + 1;
      return Array.from({ length: totalPorBloco }, (_, i) => {
        const chave = String(inicio + i).padStart(2, '0');
        const chaveAlt = String(inicio + i); // Algumas chaves podem não ter zero à esquerda
        return {
          gabarito: gabOverride[chave] || gabOverride[chaveAlt] || gabSalvo[chave] || gabSalvo[chaveAlt] || '', // v672: Priorizar override
          gabaritoOriginal: gabSalvo[chave] || gabSalvo[chaveAlt] || '', // v672: Manter original para referência
          foiEditado: !!(gabOverride[chave] || gabOverride[chaveAlt]), // v672: Flag se foi editado
          habilidade: descSalvo[chave] || descSalvo[chaveAlt] || '' // 🔧 v475: Incluir descritor
        };
      });
    }
    
    // Fallback: Expandir gabaritos dos blocos locais (durante criação do simulado)
    const blocoIdx = bloco === 1 ? 0 : 1;
    return expandirGabaritos(blocosSimuladoLocal[blocoIdx]?.questoesSelecionadas || [], listaQuestoes).slice(0, totalPorBloco);
  };
  
  // 🔧 Função helper para verificar se ID está na lista (comparação flexível de tipos)
  const idNaLista = (lista, id) => lista.some(item => String(item) === String(id));
  
  // 🔧 Função helper para encontrar índice de ID na lista (comparação flexível de tipos)
  const indiceNaLista = (lista, id) => lista.findIndex(item => String(item) === String(id));
  
  // 🏫 ESTADOS SEPARADOS PARA ESCOLA - Simulado SAEB
  const [blocosSimuladoEscola, setBlocosSimuladoEscola] = useState([
    { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
    { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
  ]);
  const [blocoAtivoEscola, setBlocoAtivoEscola] = useState(1);
  const [componenteSimuladoEscola, setComponenteSimuladoEscola] = useState('Língua Portuguesa');
  const [filtrosEscola, setFiltrosEscola] = useState({ serie: '', habilidade: '', tamanho: '' });
  
  // 🔥 v642: Carregar questões para SIMULADO SAEB (Escola) - monitora componenteSimuladoEscola
  useEffect(() => {
    const carregarQuestoesSimuladoSAEBEscola = async () => {
      // Escola precisa de série (em filtrosEscola) e componente
      if (!filtrosEscola.serie || !componenteSimuladoEscola) {
        console.log('⏸️ [v642-SIMULADO-ESCOLA] Aguardando série + componente do simulado...');
        return;
      }
      
      if (!firebaseReady || !db) {
        console.log('⚠️ [v642-SIMULADO-ESCOLA] Firebase não pronto');
        return;
      }
      
      // Mapear componente para nome SAEB
      let componenteSAEB = componenteSimuladoEscola;
      if (componenteSimuladoEscola === 'Língua Portuguesa') {
        componenteSAEB = 'Lingua Portuguesa SAEB';
      } else if (componenteSimuladoEscola === 'Matemática') {
        componenteSAEB = 'Matemática SAEB';
      }
      
      console.log(`🔄 [v642-SIMULADO-ESCOLA] Carregando questões: ${filtrosEscola.serie} + ${componenteSAEB}`);
      setCarregandoQuestoes(true); // v650
      const result = await buscarQuestoes({
        serie: filtrosEscola.serie,
        componente: componenteSAEB,
        habilidade: filtrosEscola.habilidade
      }, habilidadesFirebase);
      
      if (result.success) {
        setQuestoesFirebase(prev => {
                  // v689d: Preservar questões já selecionadas nas páginas ao trocar filtro
                  const idsSel = new Set(paginas.flatMap(p => p.questoes).map(String));
                  const preservadas = prev.filter(q => idsSel.has(String(q.id)));
                  const idsPreservados = new Set(preservadas.map(q => String(q.id)));
                  const novas = result.questoes.filter(q => !idsPreservados.has(String(q.id)));
                  return [...preservadas, ...novas];
                });
        setQuestoesCarregadas(true);
        console.log(`✅ [v642-SIMULADO-ESCOLA] ${result.questoes.length} questões carregadas`);
      }
      setCarregandoQuestoes(false); // v650
    };
    
    carregarQuestoesSimuladoSAEBEscola();
  }, [filtrosEscola.serie, componenteSimuladoEscola, filtrosEscola.habilidade, firebaseReady, habilidadesFirebase]);
  
  // 🔥 v639: Carregar questões quando FILTROS DA ESCOLA mudarem
  useEffect(() => {
    const carregarPorFiltrosEscola = async () => {
      // Escola precisa apenas de série
      if (!filtrosEscola.serie) {
        console.log('⏸️ [v639-ESCOLA] Aguardando série...');
        return;
      }
      
      if (!firebaseReady || !db) {
        console.log('⚠️ [v639-ESCOLA] Firebase não pronto');
        return;
      }
      
      console.log('🔄 [v640-ESCOLA] Carregando questões...');
      setCarregandoQuestoes(true); // v650
      const result = await buscarQuestoes({
        serie: filtrosEscola.serie,
        habilidade: filtrosEscola.habilidade
      }, habilidadesFirebase);  // v640: Passar array de habilidades
      
      if (result.success) {
        setQuestoesFirebase(prev => {
                  // v689d: Preservar questões já selecionadas nas páginas ao trocar filtro
                  const idsSel = new Set(paginas.flatMap(p => p.questoes).map(String));
                  const preservadas = prev.filter(q => idsSel.has(String(q.id)));
                  const idsPreservados = new Set(preservadas.map(q => String(q.id)));
                  const novas = result.questoes.filter(q => !idsPreservados.has(String(q.id)));
                  return [...preservadas, ...novas];
                });
        setQuestoesCarregadas(true);
        console.log(`✅ [v640-ESCOLA] ${result.questoes.length} questões carregadas`);
      }
      setCarregandoQuestoes(false); // v650
    };
    
    carregarPorFiltrosEscola();
  }, [filtrosEscola.serie, filtrosEscola.habilidade, firebaseReady, habilidadesFirebase]);
  
  const [tipoFiltroSimuladoEscola, setTipoFiltroSimuladoEscola] = useState('habilidade-unica'); // 'habilidade-unica' ou 'texto-multiplas'
  const [paginaSimuladoAtivaEscola, setPaginaSimuladoAtivaEscola] = useState(3);
  const [indiceQuestaoVisivelEscola, setIndiceQuestaoVisivelEscola] = useState({
    pequena: 0,
    media: 0,
    grande: 0,
    inteira: 0
  });
  
  // 📄 Estado para Modal de Geração de PDF
  const [showModalGerarPDF, setShowModalGerarPDF] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  // v703: Ref (não state) para travar duplo-clique instantaneamente — o `disabled`
  // do botão só atualiza após o React re-renderizar, deixando uma janela onde 2
  // cliques rápidos passam e geram (e cobram) o PDF duas vezes
  const gerandoPDFRef = useRef(false);
  const [pdfGerado, setPdfGerado] = useState(null); // { url: string, nome: string }
  const [incluirGabarito, setIncluirGabarito] = useState(false);
  const pdfContainerRef = useRef(null);
  // v696: Evita que o redirecionamento de tela inicial (dentro do onAuthStateChanged,
  // que é assíncrono e pode demorar) sobrescreva uma navegação manual do usuário.
  // Só é aplicado UMA VEZ por sessão — em execuções seguintes (ex: refresh de token,
  // ou clique do usuário enquanto a cadeia assíncrona ainda rodava), não força a tela.
  const telaInicialJaDefinida = useRef(false);
  
  // v599: Função para obter próximo código sequencial de 4 dígitos
  const obterProximoCodigoSimulado = async () => {
    if (!db || !currentUser?.uid) {
      console.log('⚠️ obterProximoCodigoSimulado: aguardando db/uid...');
      return 1;
    }
    
    try {
      // Buscar todos os simulados do usuário
      const snapshot = await db.collection('simulados')
        .where('userId', '==', currentUser.uid)
        .get();
      
      if (snapshot.empty) return 1;
      
      // v610: Encontrar o maior código de 4 dígitos (ignorar códigos antigos de 6 dígitos)
      let maiorCodigo = 0;
      snapshot.forEach(doc => {
        const codigo = parseInt(doc.data().codigo) || 0;
        // Só considerar códigos de 4 dígitos (1 a 9999)
        if (codigo > 0 && codigo <= 9999 && codigo > maiorCodigo) {
          maiorCodigo = codigo;
        }
      });
      
      console.log(`📊 Maior código encontrado: ${maiorCodigo}, próximo será: ${maiorCodigo + 1}`);
      return maiorCodigo + 1;
    } catch (error) {
      console.error('❌ Erro ao obter próximo código:', error);
      return 1;
    }
  };
  
  // v599: Função para formatar código com 4 dígitos (ex: 1 → 0001)
  const formatarCodigoSimulado = (codigo) => {
    return String(codigo || 0).padStart(4, '0');
  };
  
  // 🆕 Número único do simulado gerado (sequencial de 4 dígitos)
  const [numeroSimulado, setNumeroSimulado] = useState(1);
  
  // 🆕 Configuração do simulado (série selecionada para o SAEB)
  const [configSimulado, setConfigSimulado] = useState({
    serie: '',
    turma: '',
    dataAplicacao: new Date().toISOString().split('T')[0]
  });
  
  // 🔥 v641: Carregar questões para SIMULADO SAEB (Professor)
  useEffect(() => {
    const carregarQuestoesSimuladoSAEB = async () => {
      // v668: NÃO carregar se estiver na view de admin-questoes
      if (currentView === 'admin-questoes') {
        console.log('⏸️ [v668-SIMULADO-PROF] Bloqueado - está na view de admin');
        return;
      }
      
      // Só carregar se tiver série e componente do simulado
      if (!configSimulado.serie || !componenteSimulado) {
        console.log('⏸️ [v641-SIMULADO-PROF] Aguardando série + componente do simulado...');
        return;
      }
      
      if (!firebaseReady || !db) {
        console.log('⚠️ [v641-SIMULADO-PROF] Firebase não pronto');
        return;
      }
      
      // Mapear componente para nome SAEB
      let componenteSAEB = componenteSimulado;
      if (componenteSimulado === 'Língua Portuguesa') {
        componenteSAEB = 'Lingua Portuguesa SAEB';
      } else if (componenteSimulado === 'Matemática') {
        componenteSAEB = 'Matemática SAEB';
      }
      
      console.log(`🔄 [v641-SIMULADO-PROF] Carregando questões: ${configSimulado.serie} + ${componenteSAEB}`);
      setCarregandoQuestoes(true); // v650
      const result = await buscarQuestoes({
        serie: configSimulado.serie,
        componente: componenteSAEB
      }, habilidadesFirebase);
      
      if (result.success) {
        setQuestoesFirebase(prev => {
                  // v689d: Preservar questões já selecionadas nas páginas ao trocar filtro
                  const idsSel = new Set(paginas.flatMap(p => p.questoes).map(String));
                  const preservadas = prev.filter(q => idsSel.has(String(q.id)));
                  const idsPreservados = new Set(preservadas.map(q => String(q.id)));
                  const novas = result.questoes.filter(q => !idsPreservados.has(String(q.id)));
                  return [...preservadas, ...novas];
                });
        setQuestoesCarregadas(true);
        console.log(`✅ [v641-SIMULADO-PROF] ${result.questoes.length} questões carregadas`);
      }
      setCarregandoQuestoes(false); // v650
    };
    
    carregarQuestoesSimuladoSAEB();
  }, [currentView, configSimulado.serie, componenteSimulado, firebaseReady, habilidadesFirebase]); // v668: adicionar currentView
  
  const [tipoCartaoResposta, setTipoCartaoResposta] = useState('generico'); // 'generico' ou 'personalizado'
  const [turmaSimuladoSelecionada, setTurmaSimuladoSelecionada] = useState(null);
  const [turmasSelecionadasSimulado, setTurmasSelecionadasSimulado] = useState([]); // v497: Múltiplas turmas
  const [pdfCartoesPersonalizados, setPdfCartoesPersonalizados] = useState(null); // PDF separado dos cartões
  const [gerandoCartoes, setGerandoCartoes] = useState(false);
  const [showModalCartaoPersonalizado, setShowModalCartaoPersonalizado] = useState(false);
  const [alunosSelecionadosCartao, setAlunosSelecionadosCartao] = useState([]);
  const [turmaParaCartao, setTurmaParaCartao] = useState(null);
  const [showModalTurma, setShowModalTurma] = useState(false);
  const [showModalAluno, setShowModalAluno] = useState(false);
  const [turmaEditando, setTurmaEditando] = useState(null);
  const [novoAlunoNome, setNovoAlunoNome] = useState('');
  const [novaTurma, setNovaTurma] = useState({ nome: '', serie: '', turma: '', turno: '' });
  const [filtroHistorico, setFiltroHistorico] = useState({ componente: '', serie: '', tipo: '' });
  const [buscaHistoricoRealizada, setBuscaHistoricoRealizada] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showModalLimitePagina, setShowModalLimitePagina] = useState(false);
  const [questaoParaAdicionar, setQuestaoParaAdicionar] = useState(null);
  const [paginas, setPaginas] = useState([{ id: 1, questoes: [] }]);
  const [paginaAtiva, setPaginaAtiva] = useState(1);
  
  // Estados para controlar o carrossel de questões por tamanho
  const [indiceQuestaoVisivel, setIndiceQuestaoVisivel] = useState({
    pequena: 0,
    media: 0,
    grande: 0,
    inteira: 0
  });
  
  // Estado para controlar o modal de visualização da questão em zoom
  const [questaoEmVisualizacao, setQuestaoEmVisualizacao] = useState(null);
  
  // Estado para modal de zoom no painel Admin (separado do modal do professor)
  const [questaoAdminVisualizacao, setQuestaoAdminVisualizacao] = useState(null);
  
  // Estado para controlar modal de componente diferente
  const [showModalComponenteDiferente, setShowModalComponenteDiferente] = useState(false);
  const [componenteBloqueado, setComponenteBloqueado] = useState(null);
  
  // 🆕 Estado para controlar habilidade bloqueada (Atividades Prontas e Atividade por Questão)
  const [habilidadeBloqueada, setHabilidadeBloqueada] = useState(null);
  const [showModalHabilidadeDiferente, setShowModalHabilidadeDiferente] = useState(false);
  
  // 🆕 Estado para modal de questões vinculadas excedentes
  const [showModalQuestoesExcedentes, setShowModalQuestoesExcedentes] = useState(false);
  const [questaoExcedenteInfo, setQuestaoExcedenteInfo] = useState(null);
  
  // Estado para controlar expansão das turmas (mostrar/ocultar alunos)
  const [turmasExpandidas, setTurmasExpandidas] = useState({});
  
  // Estado para seleção de questões em lote (Admin)
  const [questoesSelecionadasLote, setQuestoesSelecionadasLote] = useState([]);
  
  const [cabecalhoConfig, setCabecalhoConfig] = useState({
    nomeEscola: '',
    serie: '',
    turma: '',
    data: new Date().toISOString().split('T')[0],
    disciplina: '',
    professor: '',
    logoUrl: '' // v651: logomarca da escola
  });

  // v654: Logo em base64 para PDFs (evita CORS)
  const [logoBase64, setLogoBase64] = useState('');

  const [clientes, setClientes] = useState([
    { id: 1, nome: 'Escola Municipal João Silva', email: 'joaosilva@escola.com', senha: '123456', tipo: 'juridica', telefone: '(75) 99999-1234', dataCadastro: '2024-01-15', creditos: 1500, plano: 'Profissional', escola: 'Escola Municipal João Silva', seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'], cadastradoPor: 1, atendimentos: [{membroId: 3, data: '2024-01-20'}, {membroId: 4, data: '2024-02-15'}] },
    { id: 2, nome: 'Maria Santos', email: 'maria@email.com', senha: '123456', tipo: 'fisica', telefone: '(75) 98888-5678', dataCadastro: '2024-03-22', creditos: 450, plano: 'Básico', escola: 'Escola Municipal João Silva', seriesLiberadas: ['3º ano', '4º ano', '5º ano'], cadastradoPor: 1, atendimentos: [{membroId: 3, data: '2024-03-25'}] },
    { id: 3, nome: 'Prof. Carlos Almeida', email: 'carlos@email.com', senha: '123456', tipo: 'fisica', telefone: '(75) 97777-9012', dataCadastro: '2024-06-10', creditos: 200, plano: 'Básico', escola: 'Escola Estadual Central', seriesLiberadas: ['5º ano'], cadastradoPor: 2, atendimentos: [{membroId: 4, data: '2024-06-15'}, {membroId: 4, data: '2024-07-01'}] },
    { id: 4, nome: 'Colégio Estadual ABC', email: 'contato@colegioabc.com', senha: '123456', tipo: 'juridica', telefone: '(75) 96666-3456', dataCadastro: '2024-02-05', creditos: 3000, plano: 'Premium', escola: 'Colégio Estadual ABC', seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'], cadastradoPor: 2, atendimentos: [{membroId: 3, data: '2024-02-10'}, {membroId: 3, data: '2024-04-20'}, {membroId: 4, data: '2024-05-05'}] },
    { id: 5, nome: 'Prof. Ana Paula', email: 'anapaula@email.com', senha: '123456', tipo: 'fisica', telefone: '(75) 95555-7890', dataCadastro: '2024-07-15', creditos: 100, plano: 'Básico', escola: 'Instituto Educar', seriesLiberadas: ['2º ano', '3º ano'], cadastradoPor: 1, atendimentos: [] },
    { id: 6, nome: 'Instituto Educar', email: 'contato@institutoeducar.com', senha: '123456', tipo: 'juridica', telefone: '(75) 94444-1234', dataCadastro: '2024-08-01', creditos: 2500, plano: 'Profissional', escola: 'Instituto Educar', seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'], cadastradoPor: 2, atendimentos: [{membroId: 4, data: '2024-08-10'}] }
  ]);

  // Estados para modais de cliente
  const [showModalCliente, setShowModalCliente] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [buscaCliente, setBuscaCliente] = useState(''); // v695: busca/filtro da lista de clientes
  const [clienteEditandoSeries, setClienteEditandoSeries] = useState([]);
  const [showModalCreditos, setShowModalCreditos] = useState(false);
  const [clienteCreditos, setClienteCreditos] = useState(null);
  const [operacaoCreditos, setOperacaoCreditos] = useState({ tipo: 'adicionar', quantidade: 0, motivo: '' });

  // 🆕 Estados para Equipe (Admin)
  const [equipe, setEquipe] = useState([
    { id: 1, nome: 'João Pereira', email: 'joao@facilitaprof.com', telefone: '(75) 99111-2222', funcao: 'Comercial', status: 'ativo', dataCadastro: '2024-01-10' },
    { id: 2, nome: 'Ana Costa', email: 'ana@facilitaprof.com', telefone: '(75) 99333-4444', funcao: 'Comercial', status: 'ativo', dataCadastro: '2024-02-15' },
    { id: 3, nome: 'Pedro Souza', email: 'pedro@facilitaprof.com', telefone: '(75) 99555-6666', funcao: 'Suporte', status: 'ativo', dataCadastro: '2024-03-20' },
    { id: 4, nome: 'Maria Lima', email: 'maria@facilitaprof.com', telefone: '(75) 99777-8888', funcao: 'Suporte', status: 'ativo', dataCadastro: '2024-04-10' }
  ]);
  const [showModalEquipe, setShowModalEquipe] = useState(false);
  const [membroEditando, setMembroEditando] = useState(null);
  const [showModalDetalhesMembro, setShowModalDetalhesMembro] = useState(false);
  const [membroDetalhes, setMembroDetalhes] = useState(null);
  const [filtroMesMembro, setFiltroMesMembro] = useState('');

  // 🆕 Estados para Termos e Políticas (Admin)
  const [termosUso, setTermosUso] = useState(`TERMOS DE USO - FacilitaPROF

1. ACEITAÇÃO DOS TERMOS
Ao acessar e utilizar a plataforma FacilitaPROF, você concorda com estes Termos de Uso. Se não concordar com qualquer parte destes termos, não utilize nossos serviços.

2. DESCRIÇÃO DO SERVIÇO
O FacilitaPROF é uma plataforma educacional que oferece ferramentas para geração de atividades, avaliações e simulados para professores e instituições de ensino.

3. CADASTRO E CONTA
- O usuário é responsável por manter a confidencialidade de sua conta
- Todas as informações fornecidas devem ser verdadeiras e atualizadas
- É proibido compartilhar credenciais de acesso

4. USO DOS CRÉDITOS
- Os créditos adquiridos são pessoais e intransferíveis
- Os créditos não utilizados não são reembolsáveis
- A validade dos créditos está sujeita ao plano contratado

5. PROPRIEDADE INTELECTUAL
Todo o conteúdo disponibilizado na plataforma é de propriedade do FacilitaPROF ou de seus licenciadores.`);

  const [politicaPrivacidade, setPoliticaPrivacidade] = useState(`POLÍTICA DE PRIVACIDADE - FacilitaPROF

1. COLETA DE DADOS
Coletamos as seguintes informações:
- Dados de identificação (nome, e-mail, telefone)
- Dados de uso da plataforma
- Informações de pagamento

2. USO DOS DADOS
Seus dados são utilizados para:
- Fornecer e melhorar nossos serviços
- Processar pagamentos
- Enviar comunicações relevantes
- Personalizar sua experiência

3. COMPARTILHAMENTO
Não compartilhamos seus dados pessoais com terceiros, exceto:
- Quando necessário para processamento de pagamentos
- Por exigência legal
- Com seu consentimento expresso

4. SEGURANÇA
Implementamos medidas de segurança técnicas e organizacionais para proteger seus dados contra acesso não autorizado.

5. SEUS DIREITOS
Você tem direito a:
- Acessar seus dados pessoais
- Solicitar correção de dados incorretos
- Solicitar exclusão de seus dados
- Revogar consentimento

6. CONTATO
Para questões sobre privacidade, entre em contato pelo e-mail: privacidade@facilitaprof.com`);

  const [editandoTermos, setEditandoTermos] = useState(false);
  const [editandoPolitica, setEditandoPolitica] = useState(false);

  const professores = [
    { id: 1, nome: 'Ana Paula', email: 'ana@email.com', serie: '5º ano', componente: 'Matemática' },
    { id: 2, nome: 'Carlos Alberto', email: 'carlos@email.com', serie: '9º ano', componente: 'Português' }
  ];

  // 🆕 TURMAS (locais - para fallback)
  // 🔧 v411: Removido turmas de exemplo - usuário deve criar suas próprias turmas
  const turmasLocais = [];
  
  // 🔥 TURMAS COMBINADAS: Firebase + Locais (Firebase tem prioridade)
  const turmas = turmasFirebase.length > 0 ? turmasFirebase : turmasLocais;

  // 🆕 DADOS DE HABILIDADES E CONTEÚDOS (locais - para fallback)
  const habilidadesLocais = [
    { id: 1, codigo: 'EF05MA08', conteudo: 'Frações - Conceitos básicos', serie: '5º ano', componente: 'Matemática' },
    { id: 2, codigo: 'EF09LP02', conteudo: 'Interpretação de texto - Gêneros textuais', serie: '9º ano', componente: 'Português' },
    { id: 3, codigo: 'EF05LP15', conteudo: 'Ortografia - Uso de S e Z', serie: '5º ano', componente: 'Português' },
    { id: 4, codigo: 'EF02MA06', conteudo: 'Adição e Subtração', serie: '2º ano', componente: 'Matemática' },
    { id: 5, codigo: 'EF05MA19', conteudo: 'Geometria - Figuras planas', serie: '5º ano', componente: 'Matemática' },
    { id: 6, codigo: 'EF09MA15', conteudo: 'Equações do 2º grau', serie: '9º ano', componente: 'Matemática' },
    { id: 7, codigo: 'EF05LP26', conteudo: 'Produção de texto - Narrativa', serie: '5º ano', componente: 'Português' },
    { id: 8, codigo: 'EF09LP05', conteudo: 'Análise sintática - Período composto', serie: '9º ano', componente: 'Português' },
    { id: 9, codigo: 'EF02LP08', conteudo: 'Alfabetização - Sílabas', serie: '2º ano', componente: 'Português' },
    
    // 🆕 Habilidades correspondentes às questões locais de Português
    { id: 'lp01', codigo: 'EF05LP01', conteudo: 'Identificar personagens em textos narrativos', serie: '5º ano', componente: 'Português' },
    { id: 'lp02', codigo: 'EF05LP02', conteudo: 'Compreender a moral de fábulas e contos', serie: '5º ano', componente: 'Português' },
    { id: 'lp03', codigo: 'EF05LP03', conteudo: 'Concordância verbal em frases', serie: '5º ano', componente: 'Português' },
    { id: 'lp04', codigo: 'EF05LP04', conteudo: 'Interpretar poemas e identificar temas', serie: '5º ano', componente: 'Português' },
    { id: 'lp05', codigo: 'EF02LP01', conteudo: 'Identificar letras do alfabeto', serie: '2º ano', componente: 'Português' },
    { id: 'lp06', codigo: 'EF02LP02', conteudo: 'Segmentar palavras em sílabas', serie: '2º ano', componente: 'Português' },
    { id: 'lp07', codigo: 'EF02LP03', conteudo: 'Ler e escrever palavras com diferentes estruturas silábicas', serie: '2º ano', componente: 'Português' },
    { id: 'lp08', codigo: 'EF02LP04', conteudo: 'Ler e compreender textos curtos', serie: '2º ano', componente: 'Português' },
    
    // Habilidades SAEB - Língua Portuguesa 5º ano
    { id: 10, codigo: 'D01', conteudo: 'Localizar informações explícitas em um texto', serie: '5º ano', componente: 'Português' },
    { id: 11, codigo: 'D03', conteudo: 'Inferir o sentido de uma palavra ou expressão', serie: '5º ano', componente: 'Português' },
    { id: 12, codigo: 'D04', conteudo: 'Inferir uma informação implícita em um texto', serie: '5º ano', componente: 'Português' },
    { id: 13, codigo: 'D06', conteudo: 'Identificar o tema de um texto', serie: '5º ano', componente: 'Português' },
    { id: 14, codigo: 'D10', conteudo: 'Identificar o conflito gerador do enredo', serie: '5º ano', componente: 'Português' },
    { id: 15, codigo: 'D11', conteudo: 'Estabelecer relação causa/consequência', serie: '5º ano', componente: 'Português' },
    { id: 16, codigo: 'D12', conteudo: 'Estabelecer relações lógico-discursivas', serie: '5º ano', componente: 'Português' },
    // Habilidades SAEB - Língua Portuguesa 9º ano
    { id: 17, codigo: 'D01', conteudo: 'Localizar informações explícitas em um texto', serie: '9º ano', componente: 'Português' },
    { id: 18, codigo: 'D03', conteudo: 'Inferir o sentido de uma palavra ou expressão', serie: '9º ano', componente: 'Português' },
    { id: 19, codigo: 'D04', conteudo: 'Inferir uma informação implícita em um texto', serie: '9º ano', componente: 'Português' },
    { id: 20, codigo: 'D06', conteudo: 'Identificar o tema de um texto', serie: '9º ano', componente: 'Português' },
    { id: 21, codigo: 'D07', conteudo: 'Identificar a tese de um texto', serie: '9º ano', componente: 'Português' },
    { id: 22, codigo: 'D08', conteudo: 'Estabelecer relação entre a tese e os argumentos', serie: '9º ano', componente: 'Português' },
    { id: 23, codigo: 'D14', conteudo: 'Distinguir um fato da opinião relativa a esse fato', serie: '9º ano', componente: 'Português' },
    // Habilidades SAEB - Língua Portuguesa 2º ano
    { id: 24, codigo: 'D01', conteudo: 'Localizar informações explícitas em um texto', serie: '2º ano', componente: 'Português' },
    { id: 25, codigo: 'D04', conteudo: 'Inferir uma informação implícita em um texto', serie: '2º ano', componente: 'Português' },
    { id: 26, codigo: 'D09', conteudo: 'Identificar a finalidade de textos de diferentes gêneros', serie: '2º ano', componente: 'Português' },
    
    // 🆕 HABILIDADES SAEB - LÍNGUA PORTUGUESA SAEB
    { id: 'saeb-lp-5-d01', codigo: 'D01', conteudo: 'Localizar informações explícitas em um texto', serie: '5º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-5-d03', codigo: 'D03', conteudo: 'Inferir o sentido de uma palavra ou expressão', serie: '5º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-5-d04', codigo: 'D04', conteudo: 'Inferir uma informação implícita em um texto', serie: '5º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-5-d06', codigo: 'D06', conteudo: 'Identificar o tema de um texto', serie: '5º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-5-d10', codigo: 'D10', conteudo: 'Identificar o conflito gerador do enredo', serie: '5º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-5-d11', codigo: 'D11', conteudo: 'Estabelecer relação causa/consequência entre partes e elementos do texto', serie: '5º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-9-d01', codigo: 'D01', conteudo: 'Localizar informações explícitas em um texto', serie: '9º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-9-d03', codigo: 'D03', conteudo: 'Inferir o sentido de uma palavra ou expressão', serie: '9º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-9-d04', codigo: 'D04', conteudo: 'Inferir uma informação implícita em um texto', serie: '9º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-9-d14', codigo: 'D14', conteudo: 'Distinguir um fato da opinião relativa a esse fato', serie: '9º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-2-d01', codigo: 'D01', conteudo: 'Localizar informações explícitas em um texto', serie: '2º ano', componente: 'Língua Portuguesa SAEB' },
    { id: 'saeb-lp-2-d04', codigo: 'D04', conteudo: 'Inferir uma informação implícita em um texto', serie: '2º ano', componente: 'Língua Portuguesa SAEB' },
    
    // 🆕 HABILIDADES SAEB - MATEMÁTICA SAEB
    { id: 'saeb-mat-5-d19', codigo: '(5N1.1)', conteudo: 'Escrever números racionais (naturais de até 6 ordens, representação fracionária ou decimal finita)', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d20', codigo: '(EF05MA01)', conteudo: 'Sequência numérica; Ordens e classes', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d21', codigo: '(EF05MA01 - EF05MA10)', conteudo: 'Composição e decomposição; Ordenação e comparação', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d22', codigo: '(EF05MA01)', conteudo: 'O milhão; Números com até nove algarismos', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d23', codigo: '(EF05MA07)', conteudo: 'Números naturais: Adição e subtração', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d24', codigo: '(EF05MA08)', conteudo: 'Multiplicação e divisão', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d25', codigo: '(EF05MA11 - EF05MA12)', conteudo: 'Expressões numéricas; Problemas com mais de uma operação', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d26', codigo: '(EF05MA03 - EF05MA013)', conteudo: 'Frações de figuras; Frações de uma quantidade', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d27', codigo: '(EF05MA03 - EF05MA04)', conteudo: 'Frações maiores do que a unidade; Frações equivalentes', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d28', codigo: '((EF05MA05))', conteudo: 'Frações na Reta numérica; Comparação de frações', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-5-d29', codigo: '(EF05MA06)', conteudo: 'A porcentagem e sua relação com a fração centesimal', serie: '5º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-9-d01', codigo: 'D01', conteudo: 'Identificar a localização de números racionais na reta numérica', serie: '9º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-9-d13', codigo: 'D13', conteudo: 'Reconhecer diferentes representações de um mesmo número racional', serie: '9º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-9-d14', codigo: 'D14', conteudo: 'Identificar a localização de números racionais na reta numérica', serie: '9º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-2-d01', codigo: 'D01', conteudo: 'Associar a contagem de coleções de objetos à representação numérica', serie: '2º ano', componente: 'Matemática SAEB' },
    { id: 'saeb-mat-2-d08', codigo: 'D08', conteudo: 'Calcular o resultado de uma adição ou subtração', serie: '2º ano', componente: 'Matemática SAEB' }
  ];
  
  // 🔥 HABILIDADES COMBINADAS: Firebase + Locais (Firebase tem prioridade)
  const habilidades = habilidadesFirebase.length > 0 ? habilidadesFirebase : habilidadesLocais;

  const [showModalHabilidade, setShowModalHabilidade] = useState(false);
  const [habilidadeEditando, setHabilidadeEditando] = useState(null);
  const [novaHabilidade, setNovaHabilidade] = useState({ codigo: '', conteudo: '', serie: '', componente: '' });

  // 📝 QUESTÕES LOCAIS (mock para desenvolvimento)
  // v661: Array vazio - usar apenas questões do Firebase
  const questoesLocais = [];

  // 🔥 QUESTÕES COMBINADAS: locais + Firebase
  // As questões do Firebase têm prioridade (aparecem primeiro)
  // Ordenadas por tamanho: pequena → media → grande → inteira
  const ordemTamanho = { 'pequena': 1, 'media': 2, 'grande': 3, 'inteira': 4, 'texto-multiplas': 5 };
  const questoes = [...questoesFirebase, ...questoesLocais].sort((a, b) => {
    const ordemA = ordemTamanho[a.tamanho] || 99;
    const ordemB = ordemTamanho[b.tamanho] || 99;
    return ordemA - ordemB;
  });

  const atividades = [
    { id: 1, titulo: 'Atividade de Frações - Nível 1', tipo: 'atividade', serie: '5º ano', componente: 'Matemática', questoes: 8, data: '2024-12-10', turma: '5º ano A', status: 'gerado' },
    { id: 2, titulo: 'Avaliação Bimestral de Interpretação', tipo: 'avaliacao', serie: '9º ano', componente: 'Português', questoes: 12, data: '2024-12-08', turma: '9º ano B', status: 'gerado' },
    { id: 3, titulo: 'Simulado SAEB - 1º Bimestre', tipo: 'simulado', serie: '5º ano', componente: 'Matemática', questoes: 22, data: '2024-12-05', turma: '5º ano A', status: 'aplicado' },
    { id: 4, titulo: 'Atividade de Ortografia - S e Z', tipo: 'atividade', serie: '5º ano', componente: 'Português', questoes: 10, data: '2024-12-03', turma: '5º ano A', status: 'gerado' },
    { id: 5, titulo: 'Avaliação de Equações', tipo: 'avaliacao', serie: '9º ano', componente: 'Matemática', questoes: 15, data: '2024-12-01', turma: '9º ano B', status: 'gerado' },
    { id: 6, titulo: 'Atividade Adição e Subtração', tipo: 'atividade', serie: '2º ano', componente: 'Matemática', questoes: 12, data: '2024-11-28', turma: '2º ano A', status: 'gerado' },
    { id: 7, titulo: 'Simulado SAEB - Diagnóstico', tipo: 'simulado', serie: '9º ano', componente: 'Português', questoes: 22, data: '2024-11-25', turma: '9º ano B', status: 'corrigido' }
  ];

  // 🔥 Histórico de PDFs gerados (Firebase)
  const [historicoPDFs, setHistoricoPDFs] = useState([]);
  const [historicoRecargas, setHistoricoRecargas] = useState([]); // 🔧 v421: Histórico de recargas real
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  const atividadesProntas = [
    { id: 1, titulo: 'Atividade de Frações - Nível 1', serie: '5º ano', componente: 'Matemática', questoes: 10, descricao: 'Conceitos básicos de frações' },
    { id: 2, titulo: 'Interpretação de Texto - Fábulas', serie: '5º ano', componente: 'Português', questoes: 8, descricao: 'Textos curtos com questões interpretativas' },
    { id: 3, titulo: 'Adição e Subtração', serie: '2º ano', componente: 'Matemática', questoes: 12, descricao: 'Operações básicas até 100' },
    { id: 4, titulo: 'Ortografia - S e Z', serie: '5º ano', componente: 'Português', questoes: 15, descricao: 'Uso correto de S e Z' }
  ];

  // 🆕 ESTADO DE RECARGAS (editável)
  // v671: Recargas agora vêm 100% do Firebase (não usar fallback hardcoded)
  const [recargas, setRecargas] = useState([]);
  
  // 🆕 ESTADO DE SÉRIES (editável pelo admin)
  // 🆕 SÉRIES DISPONÍVEIS (locais - para fallback)
  const seriesLocais = [
    { id: 1, nome: '1º ano', ativo: true },
    { id: 2, nome: '2º ano', ativo: true },
    { id: 3, nome: '3º ano', ativo: true },
    { id: 4, nome: '4º ano', ativo: true },
    { id: 5, nome: '5º ano', ativo: true },
    { id: 6, nome: '6º ano', ativo: true },
    { id: 7, nome: '7º ano', ativo: true },
    { id: 8, nome: '8º ano', ativo: true },
    { id: 9, nome: '9º ano', ativo: true }
  ];
  
  // 🔥 SÉRIES COMBINADAS: Firebase + Locais (Firebase tem prioridade)
  const seriesDisponiveis = seriesFirebase.length > 0 ? seriesFirebase : seriesLocais;
  
  // 🆕 COMPONENTES CURRICULARES (locais - para fallback)
  const componentesLocais = [
    { id: 1, nome: 'Matemática', ativo: true, apenasAdmin: false },
    { id: 2, nome: 'Língua Portuguesa', ativo: true, apenasAdmin: false },
    { id: 3, nome: 'Ciências', ativo: true, apenasAdmin: false },
    { id: 4, nome: 'Geografia', ativo: true, apenasAdmin: false },
    { id: 5, nome: 'História', ativo: true, apenasAdmin: false },
    { id: 6, nome: 'Arte', ativo: true, apenasAdmin: false },
    { id: 7, nome: 'Educação Física', ativo: true, apenasAdmin: false },
    { id: 8, nome: 'Ens. Religioso', ativo: true, apenasAdmin: false },
    { id: 9, nome: 'Datas Comemorativas', ativo: true, apenasAdmin: false },
    { id: 10, nome: 'Língua Portuguesa SAEB', ativo: true, apenasAdmin: true, saeb: true },
    { id: 11, nome: 'Matemática SAEB', ativo: true, apenasAdmin: true, saeb: true }
  ];
  
  // 🔥 COMPONENTES COMBINADOS: Firebase + Locais (Firebase tem prioridade)
  const componentesCurriculares = componentesFirebase.length > 0 ? componentesFirebase : componentesLocais;
  
  // Filtrar componentes: Admin vê todos, outros usuários não veem os SAEB
  const componentesVisiveis = componentesCurriculares.filter(c => {
    if (currentUser?.tipo === 'admin') return true;
    return !c.apenasAdmin;
  });
  
  // Componentes SAEB (para uso no Simulado)
  const componentesSAEB = componentesCurriculares.filter(c => c.saeb);
  
  // Estados para modais de séries e componentes
  const [showModalSerie, setShowModalSerie] = useState(false);
  const [serieEditando, setSerieEditando] = useState(null);
  const [showModalComponente, setShowModalComponente] = useState(false);
  const [componenteEditando, setComponenteEditando] = useState(null);
  
  // Estados para modal de edição de recarga
  const [showModalRecarga, setShowModalRecarga] = useState(false);
  const [recargaEditando, setRecargaEditando] = useState(null);
  
  // Estados para fluxo de compra de recarga
  const [showModalCompraRecarga, setShowModalCompraRecarga] = useState(false);
  const [recargaSelecionada, setRecargaSelecionada] = useState(null);
  const [etapaCompra, setEtapaCompra] = useState(1); // 1=séries, 2=pagamento, 3=dados, 4=pix, 5=confirmação
  const [formaPagamento, setFormaPagamento] = useState(''); // 'cartao' ou 'pix'
  const [dadosCartao, setDadosCartao] = useState({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
  const [dadosPix, setDadosPix] = useState({ cpf: '' });
  const [seriesSelecionadasCompra, setSeriesSelecionadasCompra] = useState([]);
  
  // 🆕 Estados para integração Pagar.me
  const [processandoPagamento, setProcessandoPagamento] = useState(false);
  const [erroPagamento, setErroPagamento] = useState('');
  const [pedidoAtual, setPedidoAtual] = useState(null);
  const [pixData, setPixData] = useState({ qrCode: '', qrCodeUrl: '', copiaCola: '' });
  const [verificandoPix, setVerificandoPix] = useState(false);
  const [pollingAtivo, setPollingAtivo] = useState(false);
  const [mostrarBotaoConcluirManual, setMostrarBotaoConcluirManual] = useState(false); // 🔧 v416: Botão para concluir manualmente em caso de erro
  const [pagamentoProcessado, setPagamentoProcessado] = useState(false); // 🔧 v421: Flag para evitar processamento duplicado
  
  // 🔄 Polling automático para verificar pagamento PIX
  useEffect(() => {
    let intervalId = null;
    
    // Iniciar polling quando tiver pedido e estiver na tela do QR Code
    const etapaQRCode = (etapaCompra === 3 || etapaCompra === 4) && formaPagamento === 'pix' && pedidoAtual && pixData.qrCodeUrl;
    
    if (etapaQRCode && !pollingAtivo) {
      setPollingAtivo(true);
      console.log('🔄 Iniciando polling automático...');
      
      intervalId = setInterval(async () => {
        try {
          console.log('🔍 Verificando pagamento automaticamente...');
          
          // 🔧 v418: Verificar diretamente no Firestore (mais confiável que Cloud Function)
          if (firebaseReady && db && pedidoAtual) {
            try {
              const pedidoDoc = await db.collection('pedidos').doc(pedidoAtual).get();
              if (pedidoDoc.exists) {
                const pedidoData = pedidoDoc.data();
                console.log('📋 Polling - Status do pedido:', pedidoData.status, pedidoData.chargeStatus);
                
                if (pedidoData.status === 'paid' || pedidoData.chargeStatus === 'paid' || pedidoData.pago === true) {
                  console.log('✅ Pagamento confirmado via Firestore (polling)!');
                  clearInterval(intervalId);
                  setPollingAtivo(false);
                  
                  // Usar a função auxiliar para processar
                  await processarPagamentoConfirmado();
                  return;
                }
              }
            } catch (firestoreError) {
              console.log('⚠️ Polling Firestore erro:', firestoreError.message);
            }
          }
        } catch (error) {
          console.log('Erro no polling:', error.message);
        }
      }, 5000); // Verificar a cada 5 segundos
    }
    
    // 🔧 v413: Parar polling quando sair da tela do QR Code ou modal fechar
    if (!etapaQRCode && pollingAtivo) {
      console.log('🛑 Parando polling - saiu da tela QR Code');
      setPollingAtivo(false);
    }
    
    // Limpar polling quando sair da tela
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        console.log('🛑 Polling parado (cleanup)');
      }
    };
  }, [etapaCompra, formaPagamento, pedidoAtual, pixData.qrCodeUrl]);
  
  // 🔥 Criar pedido PIX via Firebase Functions (usando fetch direto)
  const criarPedidoPix = async () => {
    if (!firebaseReady) {
      setErroPagamento('Firebase não disponível.');
      return;
    }
    const cpfLimpo = dadosPix.cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setErroPagamento('CPF inválido.');
      return;
    }
    
    if (!recargaSelecionada || !recargaSelecionada.preco || !recargaSelecionada.paginas) {
      setErroPagamento('Dados do pacote incompletos. Selecione novamente.');
      return;
    }
    
    setProcessandoPagamento(true);
    setErroPagamento('');
    try {
      // Extrair apenas os dados primitivos
      const seriesAtivas = seriesDisponiveis.filter(s => s.ativo).map(s => String(s.nome));
      const seriesParaCompra = recargaSelecionada.series >= seriesAtivas.length
        ? seriesAtivas
        : seriesSelecionadasCompra.map(s => String(s));
      
      // Criar objeto simples com dados primitivos apenas
      const dadosEnvio = {
        data: {
          pacoteId: String(recargaSelecionada.id || ''),
          pacoteNome: String(recargaSelecionada.nome || ''),
          valor: Number(recargaSelecionada.preco) || 0,
          paginas: Number(recargaSelecionada.paginas) || 0,
          series: Number(recargaSelecionada.series) || 1,
          seriesSelecionadas: seriesParaCompra,
          userId: String(currentUser?.uid || currentUser?.id || 'anonimo'),
          userEmail: String(currentUser?.email || 'sem@email.com'),
          userName: String(currentUser?.nome || 'Usuario'),
          pagadorCpf: cpfLimpo
        }
      };
      
      console.log('📤 Enviando para Firebase:', JSON.stringify(dadosEnvio));
      
      // Chamar via fetch direto
      const response = await fetch('https://us-central1-facilitaprof-c2d1a.cloudfunctions.net/criarPedidoPix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dadosEnvio)
      });
      
      const resultado = await response.json();
      console.log('📥 Resposta:', resultado);
      
      if (resultado.result && resultado.result.success) {
        setPedidoAtual(resultado.result.pedidoId);
        setPixData({
          qrCode: resultado.result.pixQrCode || '',
          qrCodeUrl: resultado.result.pixQrCodeUrl || '',
          copiaCola: resultado.result.pixQrCode || ''
        });
        setEtapaCompra(recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length ? 4 : 3);
      } else if (resultado.error) {
        setErroPagamento(resultado.error.message || 'Erro ao gerar PIX.');
      } else {
        setErroPagamento('Erro ao gerar PIX.');
      }
    } catch (error) {
      console.error('Erro PIX:', error);
      setErroPagamento(error.message || 'Erro ao processar.');
    } finally {
      setProcessandoPagamento(false);
    }
  };
  
  // 🔥 Criar pedido Cartão
  const criarPedidoCartao = async () => {
    if (!firebaseReady) {
      setErroPagamento('Firebase não disponível.');
      return;
    }
    if (!dadosCartao.numero || !dadosCartao.nome || !dadosCartao.validade || !dadosCartao.cvv) {
      setErroPagamento('Preencha todos os dados.');
      return;
    }
    setProcessandoPagamento(true);
    setErroPagamento('');
    try {
      const functions = window.firebase.functions();
      const criarPedido = functions.httpsCallable('criarPedidoCartao');
      const seriesParaCompra = recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length
        ? seriesDisponiveis.filter(s => s.ativo).map(s => s.nome)
        : seriesSelecionadasCompra;
      const resultado = await criarPedido({
        pacoteId: recargaSelecionada.id,
        pacoteNome: recargaSelecionada.nome,
        valor: recargaSelecionada.preco,
        paginas: recargaSelecionada.paginas,
        series: recargaSelecionada.series,
        seriesSelecionadas: seriesParaCompra,
        userId: currentUser.uid || currentUser.id,
        userEmail: currentUser.email,
        userName: currentUser.nome,
        cardNumber: dadosCartao.numero,
        cardHolder: dadosCartao.nome,
        cardExpiry: dadosCartao.validade,
        cardCvv: dadosCartao.cvv,
        cpf: dadosCartao.cpf,
        installments: 1
      });
      if (resultado.data.success && resultado.data.aprovado) {
        setPedidoAtual(resultado.data.pedidoId);
        const novasSeries = recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length
          ? seriesDisponiveis.filter(s => s.ativo).map(s => s.nome)
          : seriesSelecionadasCompra;
        setCurrentUser({
          ...currentUser,
          creditos: currentUser.creditos + recargaSelecionada.paginas,
          seriesLiberadas: novasSeries
        });
        setEtapaCompra(recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length ? 5 : 4);
      } else {
        setErroPagamento(resultado.data.aprovado === false ? 'Pagamento recusado.' : 'Em análise.');
      }
    } catch (error) {
      console.error('Erro Cartão:', error);
      setErroPagamento(error.message || 'Erro ao processar.');
    } finally {
      setProcessandoPagamento(false);
    }
  };
  
  // 🔥 Verificar status PIX
  const verificarStatusPix = async () => {
    if (!pedidoAtual) {
      setErroPagamento('ID do pedido não encontrado.');
      return;
    }
    setVerificandoPix(true);
    setErroPagamento('');
    
    try {
      console.log('🔍 Verificando status do pedido:', pedidoAtual);
      
      // 🔧 v418: MÉTODO 1 - Verificar diretamente no Firestore
      if (firebaseReady && db) {
        try {
          const pedidoDoc = await db.collection('pedidos').doc(pedidoAtual).get();
          if (pedidoDoc.exists) {
            const pedidoData = pedidoDoc.data();
            console.log('📋 Dados do pedido no Firestore:', pedidoData);
            
            if (pedidoData.status === 'paid' || pedidoData.chargeStatus === 'paid' || pedidoData.pago === true) {
              console.log('✅ PAGAMENTO CONFIRMADO via Firestore!');
              await processarPagamentoConfirmado();
              return;
            }
          }
        } catch (firestoreError) {
          console.log('⚠️ Não foi possível verificar no Firestore:', firestoreError.message);
        }
      }
      
      // 🔧 v418: MÉTODO 2 - Usar httpsCallable (Cloud Function onCall)
      if (window.firebase && window.firebase.functions) {
        try {
          const functions = window.firebase.functions();
          const verificarStatus = functions.httpsCallable('verificarStatusPedido');
          const resultado = await verificarStatus({ pedidoId: pedidoAtual });
          
          console.log('📥 Resultado Cloud Function:', resultado.data);
          
          if (resultado.data && resultado.data.pago) {
            console.log('✅ PAGAMENTO CONFIRMADO via Cloud Function!');
            await processarPagamentoConfirmado();
            return;
          } else {
            setErroPagamento('⏳ Pagamento ainda não confirmado. Se você já pagou, aguarde alguns segundos e clique novamente.');
            setMostrarBotaoConcluirManual(true);
          }
        } catch (cloudError) {
          console.error('❌ Erro Cloud Function:', cloudError);
          setErroPagamento('⚠️ Não foi possível verificar automaticamente. Se seus créditos já foram adicionados, clique em "Concluir" abaixo.');
          setMostrarBotaoConcluirManual(true);
        }
      } else {
        setErroPagamento('⚠️ Firebase não disponível. Clique em "Concluir" se seus créditos já foram adicionados.');
        setMostrarBotaoConcluirManual(true);
      }
      
    } catch (error) {
      console.error('❌ Erro ao verificar:', error);
      setErroPagamento('⚠️ Erro ao verificar. Se seus créditos já foram adicionados, clique em "Concluir" abaixo.');
      setMostrarBotaoConcluirManual(true);
    } finally {
      setVerificandoPix(false);
    }
  };
  
  // 🔧 v421: Função auxiliar para processar pagamento confirmado
  // NÃO credita aqui - o webhook do Pagar.me já faz isso!
  // Apenas atualiza o estado local com os dados do Firebase
  const processarPagamentoConfirmado = async () => {
    // 🔧 v421: Evitar processamento duplicado
    if (pagamentoProcessado) {
      console.log('⚠️ Pagamento já foi processado, ignorando...');
      return;
    }
    setPagamentoProcessado(true);
    
    setPollingAtivo(false);
    
    // 🔧 v419: Buscar dados ATUALIZADOS do usuário no Firebase (já creditado pelo webhook)
    if (firebaseReady && db && currentUser) {
      try {
        const userDoc = await db.collection('users').doc(currentUser.uid || currentUser.id).get();
        if (userDoc.exists) {
          const userData = userDoc.data();
          console.log('📋 Dados atualizados do usuário:', userData.creditos, 'créditos');
          
          // Atualizar estado local com dados do Firebase (sem duplicar)
          setCurrentUser(prev => ({
            ...prev,
            creditos: userData.creditos || prev.creditos,
            seriesLiberadas: userData.seriesLiberadas || prev.seriesLiberadas,
            plano: userData.plano || recargaSelecionada.nome
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar dados atualizados:', err);
        // Se falhar, apenas atualiza o plano localmente
        setCurrentUser(prev => ({
          ...prev,
          plano: recargaSelecionada.nome
        }));
      }
    }
    
    setErroPagamento('');
    setMostrarBotaoConcluirManual(false);
    
    // Avançar para tela de confirmação
    const seriesAtivas = seriesDisponiveis.filter(s => s.ativo);
    const proximaEtapa = recargaSelecionada.series < seriesAtivas.length ? 5 : 4;
    console.log('📍 Avançando para etapa:', proximaEtapa);
    
    setTimeout(() => {
      setEtapaCompra(proximaEtapa);
    }, 100);
  };
  
  // Copiar código PIX
  const copiarCodigoPix = () => {
    if (pixData.copiaCola) {
      navigator.clipboard.writeText(pixData.copiaCola);
      alert('Código PIX copiado!');
    }
  };

  // 🆕 Estados para Upload de Questões (Admin)
  const [showModalUploadQuestao, setShowModalUploadQuestao] = useState(false);
  const [uploadQuestao, setUploadQuestao] = useState({
    modulo: '', // 'prontas', 'questao_avaliacao', 'simulado'
    serie: '',
    componente: '',
    habilidade: '',
    habilidadeId: '', // 🆕 ID único da habilidade
    conteudo: '', // 🆕 Conteúdo da habilidade
    tamanho: 'media',
    gabarito: '',
    imagem: null,
    imagemFile: null
  });
  
  // 🆕 Estado para UPLOAD EM LOTE (até 20 questões)
  const [showModalUploadLote, setShowModalUploadLote] = useState(false);
  const [uploadLote, setUploadLote] = useState({
    modulo: '',
    serie: '',
    componente: '',
    habilidade: '',
    habilidadeId: '', // 🆕 ID único da habilidade
    conteudo: '', // 🆕 Conteúdo da habilidade
    tamanho: 'media',
    questoes: [] // Array de { file, preview, gabarito }
  });
  const [uploadLoteProgresso, setUploadLoteProgresso] = useState({ atual: 0, total: 0, salvando: false });
  
  // 🆕 Estado para upload de texto com múltiplas questões
  const [uploadTextoMultiplo, setUploadTextoMultiplo] = useState({
    serie: '',
    textoImagem: null,
    questoesVinculadas: [
      { id: 1, habilidade: '', habilidadeId: '', gabarito: '', imagem: null },
      { id: 2, habilidade: '', habilidadeId: '', gabarito: '', imagem: null }
    ]
  });
  const [showModalTextoMultiplo, setShowModalTextoMultiplo] = useState(false);
  
  const [filtroQuestoesAdmin, setFiltroQuestoesAdmin] = useState({
    modulo: '',
    serie: '',
    componente: '',
    habilidadeId: ''
  });
  
  const [permitirCarregamentoQuestoes, setPermitirCarregamentoQuestoes] = useState(false); // v667: Flag de controle
  
  // v664: Limpar filtros ao entrar na view de questões
  useEffect(() => {
    if (currentView === 'admin-questoes') {
      console.log('🔄 [v667-ADMIN] Entrando na view - limpando filtros e cache');
      setFiltroQuestoesAdmin({
        modulo: '',
        serie: '',
        componente: '',
        habilidadeId: ''
      });
      setQuestoesFirebase([]);
      setQuestoesCarregadas(false);
      _invalidarCache(); // v666: Invalidar cache também
      setPermitirCarregamentoQuestoes(false); // v667: Bloquear carregamento automático
    }
  }, [currentView]); // Só executa quando currentView muda
  
  // 🔥 v639: Carregar questões quando ADMIN entrar na view de questões OU filtros mudarem
  useEffect(() => {
    const carregarPorFiltrosAdmin = async () => {
      // Só carregar se estiver na view de questões do admin
      if (currentView !== 'admin-questoes') {
        console.log('⏸️ [v639-ADMIN] Não está na view de questões');
        return;
      }
      
      // v667: NÃO carregar se não tiver permissão (até clicar em "Buscar")
      if (!permitirCarregamentoQuestoes) {
        console.log('⏸️ [v667-ADMIN] Carregamento bloqueado - aguardando clique em Buscar');
        return;
      }
      
      // v663: NÃO carregar se não houver nenhum filtro aplicado
      if (!filtroQuestoesAdmin.modulo && !filtroQuestoesAdmin.serie && !filtroQuestoesAdmin.componente && !filtroQuestoesAdmin.habilidadeId) {
        console.log('⏸️ [v663-ADMIN] Aguardando filtros - não carregar sem filtros');
        setQuestoesFirebase([]);
        setQuestoesCarregadas(false);
        return;
      }
      
      // Admin pode carregar sem filtros (todas) ou com filtros
      if (!firebaseReady || !db) {
        console.log('⚠️ [v639-ADMIN] Firebase não pronto');
        return;
      }
      
      console.log('🔄 [v640-ADMIN] Carregando questões...');
      setCarregandoQuestoes(true); // v650
      const result = await buscarQuestoes({
        serie: filtroQuestoesAdmin.serie,
        componente: filtroQuestoesAdmin.componente,
        habilidade: filtroQuestoesAdmin.habilidadeId
      }, habilidadesFirebase);  // v640: Passar array de habilidades
      
      if (result.success) {
        setQuestoesFirebase(prev => {
                  // v689d: Preservar questões já selecionadas nas páginas ao trocar filtro
                  const idsSel = new Set(paginas.flatMap(p => p.questoes).map(String));
                  const preservadas = prev.filter(q => idsSel.has(String(q.id)));
                  const idsPreservados = new Set(preservadas.map(q => String(q.id)));
                  const novas = result.questoes.filter(q => !idsPreservados.has(String(q.id)));
                  return [...preservadas, ...novas];
                });
        setQuestoesCarregadas(true);
        console.log(`✅ [v640-ADMIN] ${result.questoes.length} questões carregadas`);
      }
      setCarregandoQuestoes(false); // v650
    };
    
    carregarPorFiltrosAdmin();
  }, [
    currentView,
    // v665: REMOVER filtros das dependências - só carregar ao clicar em "Buscar"
    // filtroQuestoesAdmin.modulo,
    // filtroQuestoesAdmin.serie,
    // filtroQuestoesAdmin.componente,
    // filtroQuestoesAdmin.habilidadeId,
    firebaseReady,
    habilidadesFirebase,
    permitirCarregamentoQuestoes,  // v667: Controla se pode carregar
    reloadTrigger  // v665: Só carrega quando clicar em "Buscar Questões"
  ]);
  
  const [paginaQuestoesAdmin, setPaginaQuestoesAdmin] = useState(1);
  const QUESTOES_POR_PAGINA = 50;

  // 🆕 Estados para módulo Correção/Desempenho Simulados
  const [viewDesempenho, setViewDesempenho] = useState('lista'); // 'lista', 'planilha', 'grafico', 'correcao'
  const [turmaDesempenhoSelecionada, setTurmaDesempenhoSelecionada] = useState(null);
  
  // 🆕 v475: Estados para filtros do card Correção de Simulados
  const [filtroCorrecaoSerie, setFiltroCorrecaoSerie] = useState('');
  const [filtroCorrecaoSimulado, setFiltroCorrecaoSimulado] = useState('');
  const [filtroCorrecaoTurma, setFiltroCorrecaoTurma] = useState(''); // ID da turma selecionada
  
  // 🆕 Estado para respostas editáveis dos alunos na planilha
  const [respostasEditaveis, setRespostasEditaveis] = useState({});
  
  // 🆕 Estados para OCR/Leitura de Cartão-Resposta
  const [showModalOCR, setShowModalOCR] = useState(false);
  const [showModalResultadoOCR, setShowModalResultadoOCR] = useState(false); // v593: Modal com resultados e destaque
  const [alunoOCR, setAlunoOCR] = useState(null); // {index, nome, painel: 'prof'|'escola'}
  const [imagemOCR, setImagemOCR] = useState(null);
  const [processandoOCR, setProcessandoOCR] = useState(false);
  const [resultadoOCR, setResultadoOCR] = useState(null);
  const [configOCR, setConfigOCR] = useState({
    claudeApiKey: '',
    mostrarConfigAPI: false
  });
  const inputFileOCR = useRef(null);
  const inputCameraOCR = useRef(null);
  // 🆕 v709: Câmera guiada (com moldura de enquadramento) para captura do cartão-resposta
  const [showCameraGuiada, setShowCameraGuiada] = useState(false);
  const videoRefOCR = useRef(null);
  const canvasRefOCR = useRef(null);
  const streamRefOCR = useRef(null);
  const guiaFrameRefOCR = useRef(null); // v711: ref da moldura, usada pra recortar a foto exatamente na área dela
  // v709: Garante que a câmera é liberada se o componente desmontar com o stream aberto
  useEffect(() => {
    return () => {
      if (streamRefOCR.current) {
        streamRefOCR.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);
  
  // 🆕 v698: Estado para configuração de geração de PDF (html2pdfrocket.com, antes CloudLayer.io)
  // Nome da variável mantido como configCloudLayer para minimizar alterações no código
  const [configCloudLayer, setConfigCloudLayer] = useState({
    apiKey: ''
  });
  
  // 🆕 Estados para QR Code e Leitura Automática
  const [modoLeituraQR, setModoLeituraQR] = useState(false);
  const [showScannerQR, setShowScannerQR] = useState(false);
  const [dadosQRLido, setDadosQRLido] = useState(null);
  const videoQRRef = useRef(null);
  const canvasQRRef = useRef(null);
  const streamQRRef = useRef(null);
  const scanningQRRef = useRef(false);
  
  // 🆕 Estados para Cartões com QR Code
  const [cartoesPersonalizadosGerados, setCartoesPersonalizadosGerados] = useState([]);
  const [simuladoAtualId, setSimuladoAtualId] = useState(null);
  const [showPreviewCartoesQR, setShowPreviewCartoesQR] = useState(false);
  
  // 🔥 Estados para Simulados no Firebase
  const [simuladosFirebase, setSimuladosFirebase] = useState([]);
  const [simuladosCarregados, setSimuladosCarregados] = useState(false);
  const [simuladoSelecionado, setSimuladoSelecionado] = useState(null);
  const [showDropdownSimulado, setShowDropdownSimulado] = useState(false);
  const [showDropdownTurma, setShowDropdownTurma] = useState(false);
  
  // 🔗 v507: Estado para dados da escola vinculada (para professores vinculados)
  const [escolaVinculada, setEscolaVinculada] = useState(null);
  
  // 🔗 v508: Variáveis derivadas para créditos e séries efetivos
  // Se professor vinculado a escola, usa dados da escola; senão, usa dados próprios
  // Nota: escolaVinculada pode ser null enquanto carrega, então verificamos se existe
  const isProfessorVinculado = currentUser?.tipo === 'professor' && !!currentUser?.escolaId;
  const escolaCarregada = isProfessorVinculado && escolaVinculada;
  const creditosEfetivos = escolaCarregada ? (escolaVinculada?.creditos || 0) : (currentUser?.creditos || 0);
  
  // v532: Verificar se usuário é plano Gold (não tem acesso ao gabarito)
  const isPlanoGold = (currentUser?.plano || '').toLowerCase().includes('gold');
  
  // v512: Séries efetivas - professor vinculado pode usar suas séries OU as da escola
  const seriesEfetivas = isProfessorVinculado 
    ? (currentUser?.seriesLiberadas?.length > 0 
        ? currentUser.seriesLiberadas 
        : (escolaVinculada?.seriesLiberadas || [])) // Fallback para séries da escola
    : (currentUser?.seriesLiberadas || []);
  
  // v512: Professor vinculado com créditos da escola tem acesso liberado
  // Mesmo se não tiver série específica atribuída, usa as séries da escola
  const temAcessoLiberado = currentUser?.tipo === 'admin' || 
    (creditosEfetivos > 0 && seriesEfetivas.length > 0) ||
    (isProfessorVinculado && escolaCarregada && escolaVinculada?.creditos > 0); // Professor vinculado sempre tem acesso se escola tem créditos
  
  // Se professor vinculado mas escola ainda não carregou, mostrar loading
  const aguardandoEscola = isProfessorVinculado && !escolaVinculada;
  
  // 🏫 Estados para módulos da ESCOLA
  const [perfilEscola, setPerfilEscola] = useState({
    nome: 'Escola Municipal João Silva',
    endereco: 'Rua das Flores, 123 - Centro',
    cidade: 'São Paulo',
    estado: 'SP',
    cep: '01234-567',
    telefone: '(11) 1234-5678',
    email: 'contato@escolajoaosilva.edu.br',
    diretor: 'Maria Aparecida Santos',
    cnpj: '12.345.678/0001-90',
    logoUrl: '' // v651: logomarca da escola
  });
  
  const [professoresEscola, setProfessoresEscola] = useState([]);
  
  const [showModalProfessor, setShowModalProfessor] = useState(false);
  const [professorEditando, setProfessorEditando] = useState(null);
  const [novoProfessor, setNovoProfessor] = useState({
    nome: '', email: '', serie: '', turma: '', turno: '', componente: ''
  });
  
  const [estatisticasEscola, setEstatisticasEscola] = useState({
    totalPaginasGeradas: 0,
    totalProfessores: 0,
    professoresAtivos: 0,
    creditosUsadosMes: 0,
    mediaPorProfessor: 0
  });

  // Dados de exemplo de simulados aplicados
  const [simuladosAplicados, setSimuladosAplicados] = useState([]);

  // Níveis de proficiência SAEB
  const niveisProficiencia = [
    { nivel: 1, min: 125, max: 150, padrao: 'Abaixo do Básico', cor: '#ef4444' },
    { nivel: 2, min: 150, max: 175, padrao: 'Abaixo do Básico', cor: '#f97316' },
    { nivel: 3, min: 175, max: 200, padrao: 'Básico', cor: '#eab308' },
    { nivel: 4, min: 200, max: 225, padrao: 'Básico', cor: '#84cc16' },
    { nivel: 5, min: 225, max: 250, padrao: 'Adequado', cor: '#22c55e' },
    { nivel: 6, min: 250, max: 275, padrao: 'Adequado', cor: '#14b8a6' },
    { nivel: 7, min: 275, max: 300, padrao: 'Adequado', cor: '#06b6d4' },
    { nivel: 8, min: 300, max: 325, padrao: 'Avançado', cor: '#3b82f6' },
    { nivel: 9, min: 325, max: 350, padrao: 'Avançado', cor: '#8b5cf6' },
    { nivel: 10, min: 350, max: 375, padrao: 'Avançado', cor: '#a855f7' }
  ];

  // 🆕 v712: SAEB do 9º ano tem 26 questões no simulado (13 por bloco);
  // 2º e 5º ano têm 22 (11 por bloco) — segue a quantidade da prova oficial SAEB por etapa
  const questoesPorBlocoSAEB = (serie) => (/9\s*[º°]?\s*ano/i.test(String(serie || '')) ? 13 : 11);

  // 📷 FUNÇÕES DE OCR/LEITURA DE CARTÃO-RESPOSTA

  // v692: Converter respostas do Firebase {"01":"B","02":"A",...} para o formato da planilha
  // escola_IDX_BLOCO_QUESTAO e prof_IDX_BLOCO_QUESTAO
  // Chamada tanto ao abrir a planilha quanto pelo listener em tempo real
  const converterRespostasParaPlanilha = (respostasFirebase, turmaAlunos, serie = '') => {
    const novasRespostas = {};
    if (!respostasFirebase || !turmaAlunos) return novasRespostas;
    const corte = questoesPorBlocoSAEB(serie);

    Object.entries(respostasFirebase).forEach(([nomeAluno, respostasAluno]) => {
      if (typeof respostasAluno !== 'object') return;
      const indexAluno = turmaAlunos.findIndex(a => {
        const nomeA = (a?.nome || a || '').toString().toLowerCase().trim();
        const nomeB = (nomeAluno || '').toLowerCase().trim();
        return nomeA === nomeB;
      });
      if (indexAluno < 0) return;

      Object.entries(respostasAluno).forEach(([numQuestao, resposta]) => {
        const questaoNum = parseInt(numQuestao);
        if (isNaN(questaoNum) || !resposta || resposta === '-') return;
        const bloco = questaoNum <= corte ? 0 : 1;
        const questaoIdx = questaoNum <= corte ? questaoNum - 1 : questaoNum - corte - 1;
        novasRespostas[`escola_${indexAluno}_${bloco}_${questaoIdx}`] = resposta;
        novasRespostas[`prof_${indexAluno}_${bloco}_${questaoIdx}`] = resposta;
      });
    });
    return novasRespostas;
  };

  // Abrir modal de OCR para um aluno específico
  const abrirModalOCR = (alunoIndex, alunoNome, painel, serie = '') => {
    setAlunoOCR({ index: alunoIndex, nome: alunoNome, painel, serie });
    setImagemOCR(null);
    setResultadoOCR(null);
    setProcessandoOCR(false);
    setShowModalOCR(true);
  };

  // Converter arquivo para base64
  const converterParaBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // v475: Pré-processar imagem para melhor OCR (aumentar contraste, redimensionar)
  const preprocessarImagemOCR = (imagemBase64, maxWidth = 1200) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Criar canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calcular nova dimensão mantendo proporção
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Desenhar imagem
        ctx.drawImage(img, 0, 0, width, height);
        
        // Aumentar contraste
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        const contrast = 1.3; // Aumentar contraste em 30%
        const factor = (259 * (contrast * 100 + 255)) / (255 * (259 - contrast * 100));
        
        for (let i = 0; i < data.length; i += 4) {
          data[i] = factor * (data[i] - 128) + 128;     // R
          data[i + 1] = factor * (data[i + 1] - 128) + 128; // G
          data[i + 2] = factor * (data[i + 2] - 128) + 128; // B
        }
        
        ctx.putImageData(imageData, 0, 0);
        
        // Retornar como base64 com qualidade 0.9
        const processedBase64 = canvas.toDataURL('image/jpeg', 0.9);
        console.log(`📷 Imagem pré-processada: ${img.width}x${img.height} → ${width}x${height}`);
        resolve(processedBase64);
      };
      img.src = imagemBase64;
    });
  };

  // Handler para upload de imagem
  const handleImagemOCR = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log(`📷 Imagem selecionada: ${file.name}, ${(file.size/1024).toFixed(1)}KB`);
        const base64Original = await converterParaBase64(file);
        // Pré-processar para melhor OCR
        const base64Processada = await preprocessarImagemOCR(base64Original);
        setImagemOCR(base64Processada);
        setResultadoOCR(null);
        console.log('✅ Imagem pronta para OCR');
      } catch (error) {
        console.error('Erro ao processar imagem:', error);
        // Fallback: usar imagem original
        const base64 = await converterParaBase64(file);
        setImagemOCR(base64);
        setResultadoOCR(null);
      }
    }
  };

  // 🆕 v709: Parar a câmera guiada (libera a webcam/câmera do dispositivo)
  const pararCameraGuiada = () => {
    if (streamRefOCR.current) {
      streamRefOCR.current.getTracks().forEach(track => track.stop());
      streamRefOCR.current = null;
    }
    if (videoRefOCR.current) {
      videoRefOCR.current.srcObject = null;
    }
  };

  // 🆕 v709: Abrir câmera guiada com moldura de enquadramento.
  // Só é possível desenhar a moldura por cima de uma câmera controlada pela própria página
  // (getUserMedia) — o <input capture="environment"> abre o app de câmera nativo do celular,
  // que fica fora do nosso controle e não permite overlay nenhum.
  // Se getUserMedia falhar (permissão negada, navegador sem suporte, etc.), cai automaticamente
  // para a captura nativa antiga, sem travar o fluxo do usuário.
  const abrirCameraGuiada = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Câmera não suportada neste navegador');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });
      streamRefOCR.current = stream;
      setShowCameraGuiada(true);
      // Aguarda o <video> ser renderizado antes de atribuir o stream
      setTimeout(() => {
        if (videoRefOCR.current) {
          videoRefOCR.current.srcObject = stream;
          videoRefOCR.current.play().catch(() => {});
        }
      }, 50);
    } catch (error) {
      console.warn('⚠️ Câmera guiada indisponível, usando captura nativa:', error.message);
      // Fallback: abre o seletor de câmera nativo do celular (comportamento antigo)
      if (inputCameraOCR.current) {
        inputCameraOCR.current.value = '';
        inputCameraOCR.current.click();
      }
    }
  };

  // 🆕 v709: Fechar a câmera guiada sem capturar (botão cancelar/X)
  const fecharCameraGuiada = () => {
    pararCameraGuiada();
    setShowCameraGuiada(false);
  };

  // 🆕 v709: Capturar o frame atual do vídeo (dentro da moldura) e seguir o mesmo
  // pipeline de pré-processamento já usado para fotos vindas da galeria/câmera nativa
  // v711: Capturar o frame atual do vídeo, mas RECORTADO exatamente na área da
  // moldura mostrada na tela — sem isso a função capturava o quadro inteiro da
  // câmera (por isso a foto saía com a página inteira, mesmo com a moldura sendo
  // só visual). O cálculo abaixo converte a posição da moldura na tela (CSS) para
  // pixels reais do vídeo, considerando que o <video> usa object-fit:cover (que
  // amplia/corta o vídeo pra preencher a tela).
  const capturarFotoGuiada = async () => {
    const video = videoRefOCR.current;
    const canvas = canvasRefOCR.current;
    const guia = guiaFrameRefOCR.current;
    if (!video || !canvas || !video.videoWidth) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    let sx = 0, sy = 0, sw = vw, sh = vh; // fallback: quadro inteiro, caso a ref da moldura falhe

    if (guia) {
      const videoRect = video.getBoundingClientRect();
      const guiaRect = guia.getBoundingClientRect();

      // Fator de escala do object-fit:cover (o vídeo é ampliado até cobrir a tela)
      const scale = Math.max(videoRect.width / vw, videoRect.height / vh);
      const videoRenderW = vw * scale;
      const videoRenderH = vh * scale;
      const offsetX = (videoRenderW - videoRect.width) / 2;
      const offsetY = (videoRenderH - videoRect.height) / 2;

      // Posição da moldura relativa ao vídeo, já convertida pra pixels nativos do vídeo
      const relLeft = guiaRect.left - videoRect.left;
      const relTop = guiaRect.top - videoRect.top;

      sx = Math.max(0, (relLeft + offsetX) / scale);
      sy = Math.max(0, (relTop + offsetY) / scale);
      sw = Math.min(vw - sx, guiaRect.width / scale);
      sh = Math.min(vh - sy, guiaRect.height / scale);
    }

    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
    const base64Original = canvas.toDataURL('image/jpeg', 0.92);

    pararCameraGuiada();
    setShowCameraGuiada(false);

    try {
      const base64Processada = await preprocessarImagemOCR(base64Original);
      setImagemOCR(base64Processada);
      setResultadoOCR(null);
    } catch (error) {
      console.error('Erro ao processar imagem capturada:', error);
      setImagemOCR(base64Original);
      setResultadoOCR(null);
    }
  };

  // Prompt para extração de respostas do cartão
  const promptOCR = `Analise esta imagem de um cartão-resposta de simulado escolar.

TAREFA: Extraia as respostas marcadas pelo aluno.

INSTRUÇÕES:
1. Identifique cada questão numerada (01, 02, 03, etc.)
2. Para cada questão, identifique qual alternativa está marcada (A, B, C ou D)
3. Se uma questão não tiver marcação clara ou tiver mais de uma marcação, use "X"
4. Retorne APENAS um JSON no formato abaixo, sem explicações adicionais

FORMATO DE RESPOSTA (JSON puro, sem markdown):
{
  "aluno_detectado": "nome do aluno se visível ou null",
  "serie_detectada": "série se visível ou null",
  "total_questoes": numero,
  "respostas": {
    "bloco1": ["A", "B", "C", "D", "A", "B", "C", "D", "A", "B", "C"],
    "bloco2": ["A", "B", "C", "D", "A", "B", "C", "D", "A", "B", "C"]
  },
  "confianca": "alta|media|baixa",
  "observacoes": "qualquer observação relevante"
}

IMPORTANTE: Retorne APENAS o JSON, sem texto antes ou depois.`;

  // Processar imagem com Claude API (Haiku 4.5)
  const processarComClaude = async (imagemBase64) => {
    const apiKey = configOCR.claudeApiKey;
    if (!apiKey) {
      throw new Error('API Key do Claude não configurada. Clique em ⚙️ para configurar.');
    }

    // Extrair apenas o base64 sem o prefixo data:image/...
    const base64Data = imagemBase64.split(',')[1];
    const mediaType = imagemBase64.split(';')[0].split(':')[1];

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 1024,
        messages: [{
          role: 'user',
          content: [
            {
              type: 'image',
              source: {
                type: 'base64',
                media_type: mediaType,
                data: base64Data
              }
            },
            {
              type: 'text',
              text: promptOCR
            }
          ]
        }]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Erro na API do Claude');
    }

    const data = await response.json();
    const textoResposta = data.content[0].text;
    
    // Tentar parsear o JSON da resposta
    try {
      // Remover possíveis backticks de markdown
      const jsonLimpo = textoResposta.replace(/```json\n?|\n?```/g, '').trim();
      return JSON.parse(jsonLimpo);
    } catch (e) {
      console.error('Erro ao parsear resposta:', textoResposta);
      throw new Error('Não foi possível interpretar a resposta do OCR');
    }
  };

  // 🔥 Processar OCR via Cloud Function (método seguro)
  const processarComCloudFunction = async (imagemBase64) => {
    if (!firebaseReady || !window.firebase) {
      throw new Error('Firebase não disponível');
    }
    
    const functions = window.firebase.functions();
    const processarCartao = functions.httpsCallable('processarCartaoResposta');
    
    // v713: informa quantas questões tem cada bloco (11 no 2º/5º ano, 13 no 9º ano) —
    // sem isso a function não tem como saber se deve procurar até a questão 11 ou 13 em cada bloco
    const questoesPorBloco = questoesPorBlocoSAEB(alunoOCR?.serie);
    const resultado = await processarCartao({ imagemBase64, questoesPorBloco });
    
    if (resultado.data.success) {
      return resultado.data;
    } else {
      throw new Error(resultado.data.error || 'Erro ao processar cartão');
    }
  };

  // Função principal para processar OCR
  const processarOCR = async () => {
    if (!imagemOCR) {
      alert('Por favor, selecione uma imagem do cartão-resposta.');
      return;
    }

    setProcessandoOCR(true);
    setResultadoOCR(null);

    try {
      let resultado;
      
      // Tentar Cloud Function primeiro (método seguro)
      if (firebaseReady && window.firebase?.functions) {
        try {
          // v709: 1 nova tentativa automática antes de desistir — a Cloud Function
          // às vezes falha de forma transitória (timeout/instabilidade momentânea),
          // e isso antes aparecia direto pro professor como "Configure a Cloud
          // Function..." mesmo estando tudo certo, obrigando ele a clicar de novo
          try {
            resultado = await processarComCloudFunction(imagemOCR);
          } catch (primeiraTentativaErro) {
            console.warn('⚠️ 1ª tentativa da Cloud Function falhou, tentando novamente:', primeiraTentativaErro.message);
            await new Promise(r => setTimeout(r, 1200));
            resultado = await processarComCloudFunction(imagemOCR);
          }
          console.log('✅ OCR processado via Cloud Function');
          console.log('📊 Resultado OCR:', JSON.stringify(resultado, null, 2));
        } catch (cfError) {
          console.warn('⚠️ Cloud Function não disponível após 2 tentativas:', cfError.message);
          // Fallback para API direta (apenas desenvolvimento)
          if (configOCR.claudeApiKey) {
            resultado = await processarComClaude(imagemOCR);
            console.log('⚠️ OCR processado via API direta (desenvolvimento)');
          } else {
            throw new Error('Não foi possível ler o cartão agora (falha de conexão com o servidor). Tente novamente em alguns segundos.');
          }
        }
      } else if (configOCR.claudeApiKey) {
        // Fallback direto se não tem Firebase Functions
        resultado = await processarComClaude(imagemOCR);
        console.log('⚠️ OCR processado via API direta (desenvolvimento)');
      } else {
        throw new Error('Cloud Function não configurada. Deploy as functions do Firebase.');
      }
      
      setResultadoOCR(resultado);
    } catch (error) {
      console.error('Erro no OCR:', error);
      alert(`Erro ao processar imagem: ${error.message}`);
    } finally {
      setProcessandoOCR(false);
    }
  };

  // v594: Função para calcular acertos comparando com gabarito
  const calcularAcertos = () => {
    if (!resultadoOCR) return { total: 0, detalhes: [] };
    
    // Buscar o simulado correto - pode ser pelo filtro ou selecionado
    let simulado = simuladoSelecionado;
    
    if (!simulado && filtroCorrecaoSimulado) {
      // Buscar pelo código do filtro de correção
      simulado = simuladosFirebase.find(s => 
        String(s.codigo) === String(filtroCorrecaoSimulado)
      );
    }
    
    if (!simulado || !simulado.gabarito) {
      console.warn('⚠️ Gabarito não encontrado:', { simuladoSelecionado, filtroCorrecaoSimulado });
      return { total: 0, detalhes: [] };
    }
    
    const gabarito = simulado.gabarito;
    let acertos = 0;
    const detalhes = [];
    
    console.log('📊 Calculando acertos. Gabarito:', gabarito);
    
    // Verificar Bloco 1
    if (resultadoOCR.respostas.bloco1) {
      resultadoOCR.respostas.bloco1.forEach((resposta, i) => {
        const numQuestao = String(i + 1).padStart(2, '0');
        const respostaCorreta = gabarito[numQuestao];
        const acertou = resposta === respostaCorreta && resposta !== '-' && resposta !== 'X';
        if (acertou) acertos++;
        detalhes.push({ questao: numQuestao, resposta, gabarito: respostaCorreta, acertou });
      });
    }
    
    // Verificar Bloco 2
    if (resultadoOCR.respostas.bloco2) {
      // v713: offset dinâmico — soma a quantidade real de questões do Bloco 1 (11 ou 13, 2º/5º vs 9º ano)
      const offsetBloco2 = resultadoOCR.respostas.bloco1?.length || 11;
      resultadoOCR.respostas.bloco2.forEach((resposta, i) => {
        const numQuestao = String(i + offsetBloco2 + 1).padStart(2, '0');
        const respostaCorreta = gabarito[numQuestao];
        const acertou = resposta === respostaCorreta && resposta !== '-' && resposta !== 'X';
        if (acertou) acertos++;
        detalhes.push({ questao: numQuestao, resposta, gabarito: respostaCorreta, acertou });
      });
    }
    
    console.log('✅ Acertos calculados:', acertos, 'de', detalhes.length);
    
    return { total: acertos, detalhes, simulado };
  };

  // 🔥 Carregar simulados do Firebase EM TEMPO REAL
  // v482: Usar onSnapshot para sincronização automática entre dispositivos
  // v505: Escolas veem simulados dos professores vinculados
  // v506: Limpar dados ao mudar de usuário
  // 🔧 v529: Limpar TODOS os estados de filtros e questões ao trocar de usuário
  useEffect(() => {
    // v529: Limpar estados de filtros, questões selecionadas e pré-visualização
    console.log('🧹 v529: Limpando estados ao mudar de usuário:', currentUser?.uid);
    
    // Limpar filtros do professor
    setFiltros({ serie: '', componente: '', habilidade: '', habilidadeId: '', tamanho: '' });
    
    // Limpar filtros da escola
    setFiltrosEscola({ serie: '', habilidade: '', tamanho: '' });
    
    // Limpar blocos de questões do professor
    setBlocosSimulado([
      { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
      { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
    ]);
    setBlocoAtivo(1);
    setComponenteSimulado('Língua Portuguesa');
    setPaginaSimuladoAtiva(3);
    
    // Limpar blocos de questões da escola
    setBlocosSimuladoEscola([
      { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
      { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
    ]);
    setBlocoAtivoEscola(1);
    setComponenteSimuladoEscola('Língua Portuguesa');
    setPaginaSimuladoAtivaEscola(3);
    
    // Limpar índices de questões visíveis
    setIndiceQuestaoVisivel({ pequena: 0, media: 0, grande: 0, inteira: 0 });
    setIndiceQuestaoVisivelEscola({ pequena: 0, media: 0, grande: 0, inteira: 0 });
    
    // Limpar tipo de geração
    setTipoGeracao('questao');
    
    // Limpar configuração do simulado
    setConfigSimulado({
      serie: '',
      turma: '',
      dataAplicacao: new Date().toISOString().split('T')[0]
    });
    
    // Limpar turmas selecionadas
    setTurmaSimuladoSelecionada(null);
    setTurmasSelecionadasSimulado([]);
    
    // Limpar PDF gerado
    setPdfGerado(null);
    
    // v599: Gerar novo número de simulado sequencial
    obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
    
  }, [currentUser?.uid]);

  useEffect(() => {
    let unsubscribeSimulados = null;
    let unsubscribeSimuladosProfessores = [];
    
    // v506: Limpar dados anteriores imediatamente ao mudar de usuário
    setSimuladosFirebase([]);
    setSimuladosCarregados(false);
    setSimuladoSelecionado(null);
    
    const carregarSimulados = async () => {
      if (firebaseStatus === 'ready' && firebaseReady && db && currentUser?.uid) {
        console.log('🔄 Iniciando listener de simulados em tempo real para userId:', currentUser.uid);
        
        // Carregar simulados do próprio usuário
        unsubscribeSimulados = db.collection('simulados')
          .where('userId', '==', currentUser.uid)
          .onSnapshot(
            async (snapshot) => {
              let simuladosDoFirebase = [];
              snapshot.forEach(doc => {
                simuladosDoFirebase.push({ id: doc.id, ...doc.data(), origem: 'proprio' });
              });
              
              // v505: Se for escola, também carregar simulados dos professores vinculados
              if (currentUser?.tipo === 'escola') {
                try {
                  const escolaId = currentUser.uid || currentUser.id;
                  const profSnapshot = await db.collection('professoresEscola')
                    .where('escolaId', '==', escolaId)
                    .get();
                  
                  const professoresIds = [];
                  profSnapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.professorUserId || data.professorId) {
                      professoresIds.push(data.professorUserId || data.professorId);
                    }
                  });
                  
                  console.log(`📚 Escola: carregando simulados de ${professoresIds.length} professores vinculados`);
                  
                  // Carregar simulados de cada professor vinculado
                  for (const profId of professoresIds) {
                    const simProf = await db.collection('simulados')
                      .where('userId', '==', profId)
                      .get();
                    
                    simProf.forEach(doc => {
                      simuladosDoFirebase.push({ 
                        id: doc.id, 
                        ...doc.data(), 
                        origem: 'professor',
                        professorId: profId 
                      });
                    });
                  }
                } catch (error) {
                  console.error('❌ Erro ao carregar simulados de professores:', error);
                }
              }
              
              // v690: Professor vinculado à escola também carrega simulados criados pela escola
              // Os simulados gerados pela escola têm userId da escola, não do professor
              if (currentUser?.tipo === 'professor' && currentUser?.escolaId) {
                try {
                  console.log(`📚 Professor vinculado: buscando simulados da escola ${currentUser.escolaId}`);
                  const simEscola = await db.collection('simulados')
                    .where('userId', '==', currentUser.escolaId)
                    .get();
                  
                  simEscola.forEach(doc => {
                    // Evitar duplicatas caso o professor seja também o autor
                    const jaExiste = simuladosDoFirebase.some(s => s.id === doc.id);
                    if (!jaExiste) {
                      simuladosDoFirebase.push({ 
                        id: doc.id, 
                        ...doc.data(), 
                        origem: 'escola'
                      });
                    }
                  });
                  
                  console.log(`✅ Professor vinculado: ${simEscola.size} simulados da escola carregados`);
                } catch (error) {
                  console.error('❌ Erro ao carregar simulados da escola (professor vinculado):', error);
                }
              }
              
              setSimuladosFirebase(simuladosDoFirebase);
              setSimuladosCarregados(true);
              console.log(`✅ Simulados atualizados em tempo real: ${simuladosDoFirebase.length}`);
            },
            (error) => {
              console.error('❌ Erro no listener de simulados:', error);
            }
          );
      }
    };
    
    carregarSimulados();
    
    // Cleanup: remover listener quando componente desmontar ou usuário mudar
    return () => {
      if (unsubscribeSimulados) {
        console.log('🔌 Desconectando listener de simulados');
        unsubscribeSimulados();
      }
      unsubscribeSimuladosProfessores.forEach(unsub => unsub && unsub());
    };
  // v690: escolaId como dependência garante re-execução quando vínculo escola é estabelecido após login
  // Sem isso, professor vinculado pode carregar simulados antes do escolaId ser preenchido e nunca recarregar
  }, [firebaseStatus, firebaseReady, currentUser?.uid, currentUser?.tipo, currentUser?.escolaId]);
  
  // 🔄 v482: Listener específico para o simulado selecionado (atualização em tempo real da planilha)
  useEffect(() => {
    let unsubscribeSimuladoSelecionado = null;
    
    if (firebaseStatus === 'ready' && firebaseReady && db && simuladoSelecionado?.id) {
      console.log('🔄 Iniciando listener para simulado selecionado:', simuladoSelecionado.id);
      
      unsubscribeSimuladoSelecionado = db.collection('simulados')
        .doc(simuladoSelecionado.id)
        .onSnapshot(
          (doc) => {
            if (doc.exists) {
              const dadosAtualizados = { id: doc.id, ...doc.data() };
              
              // Atualizar simulado selecionado
              setSimuladoSelecionado(dadosAtualizados);
              
              // Converter respostas do Firebase para formato da planilha
              if (dadosAtualizados.respostas) {
                console.log('📊 Atualizando planilha com novas respostas');
                console.log('📋 Respostas no Firebase:', JSON.stringify(dadosAtualizados.respostas));
                
                setRespostasEditaveis(prev => {
                  const novasRespostas = { ...prev };
                  
                  // v692: Encontrar turma — prioridade: turmaId do simulado > turmaId do configSimulado > serie+turma > filtroCorrecao
                  let turmaAtual = null;

                  // Método 0 (v692): turmaId gravado no próprio documento do simulado (mais confiável)
                  if (dadosAtualizados.turmaId) {
                    turmaAtual = turmas.find(t => String(t.id) === String(dadosAtualizados.turmaId));
                    console.log('🔍 [M0] Buscando turma por simulado.turmaId:', dadosAtualizados.turmaId, '->', turmaAtual?.serie);
                  }

                  // Método 1: turmaId do configSimulado
                  if (!turmaAtual && configSimulado?.turmaId) {
                    turmaAtual = turmas.find(t => String(t.id) === String(configSimulado.turmaId));
                    console.log('🔍 [M1] Buscando turma por configSimulado.turmaId:', configSimulado.turmaId, '->', turmaAtual?.serie);
                  }

                  // Método 2: série + turma do configSimulado
                  if (!turmaAtual && configSimulado?.serie) {
                    turmaAtual = turmas.find(t =>
                      t.serie === configSimulado.serie &&
                      (configSimulado.turma === '' || !configSimulado.turma || t.turma === configSimulado.turma)
                    );
                    console.log('🔍 [M2] Buscando turma por configSimulado.serie+turma:', configSimulado.serie, configSimulado.turma, '->', turmaAtual?.serie);
                  }

                  // Método 3: filtroCorrecaoTurma (vem da tela Correção mobile)
                  if (!turmaAtual && filtroCorrecaoTurma) {
                    turmaAtual = turmas.find(t => String(t.id) === String(filtroCorrecaoTurma));
                    console.log('🔍 [M3] Buscando turma por filtroCorrecaoTurma:', filtroCorrecaoTurma, '->', turmaAtual?.serie);
                  }

                  // Método 4: série + turma do próprio simulado
                  if (!turmaAtual && dadosAtualizados.serie && dadosAtualizados.turma) {
                    turmaAtual = turmas.find(t =>
                      (t.serie || '').trim().toLowerCase() === (dadosAtualizados.serie || '').trim().toLowerCase() &&
                      (t.turma || '').trim().toLowerCase() === (dadosAtualizados.turma || '').trim().toLowerCase()
                    );
                    console.log('🔍 [M4] Buscando turma por simulado.serie+turma:', dadosAtualizados.serie, dadosAtualizados.turma, '->', turmaAtual?.serie);
                  }
                  // Método 5 (v693): só série — simulados antigos sem turma ou turmaId
                  if (!turmaAtual && dadosAtualizados.serie) {
                    turmaAtual = turmas.find(t =>
                      (t.serie || '').trim().toLowerCase() === (dadosAtualizados.serie || '').trim().toLowerCase()
                    );
                    console.log('🔍 [M5] Buscando turma só por serie:', dadosAtualizados.serie, '->', turmaAtual?.serie);
                  }
                  
                  console.log('👥 Turma encontrada:', turmaAtual ? `${turmaAtual.serie} com ${turmaAtual.alunos?.length} alunos` : 'NÃO ENCONTRADA');
                  console.log('👥 Turmas disponíveis:', turmas.map(t => `${t.serie} (${t.alunos?.length} alunos)`).join(', '));
                  
                  if (turmaAtual?.alunos) {
                    // v692: usar função centralizada de conversão
                    const convertidas = converterRespostasParaPlanilha(dadosAtualizados.respostas, turmaAtual.alunos, dadosAtualizados.serie);
                    Object.assign(novasRespostas, convertidas);
                    console.log(`✅ Listener: ${Object.keys(convertidas).length} chaves convertidas para turma ${turmaAtual.serie}`);
                  } else {
                    console.warn('⚠️ Turma não encontrada para converter respostas do listener');
                    console.warn('simulado.turmaId:', dadosAtualizados.turmaId, '| configSimulado.turmaId:', configSimulado?.turmaId, '| serie:', dadosAtualizados.serie);
                  }
                  
                  return novasRespostas;
                });
              }
            }
          },
          (error) => {
            console.error('❌ Erro no listener do simulado selecionado:', error);
          }
        );
    }
    
    return () => {
      if (unsubscribeSimuladoSelecionado) {
        console.log('🔌 Desconectando listener do simulado selecionado');
        unsubscribeSimuladoSelecionado();
      }
    };
  }, [firebaseStatus, firebaseReady, simuladoSelecionado?.id]); // v603: Remover turmas/configSimulado para evitar loops

  // 🔥 Carregar clientes do Firebase para admin
  useEffect(() => {
    const carregarClientes = async () => {
      if (firebaseStatus === 'ready' && firebaseReady && db && currentUser?.tipo === 'admin') {
        try {
          console.log('🔄 Carregando clientes do Firebase...');
          const snapshot = await db.collection('users').get();
          const clientesDoFirebase = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            clientesDoFirebase.push({ 
              id: doc.id, 
              nome: data.nome || data.email,
              email: data.email,
              tipo: data.tipo === 'escola' ? 'juridica' : 'fisica',
              telefone: data.telefone || '',
              dataCadastro: data.dataCadastro || new Date().toISOString().split('T')[0],
              creditos: data.creditos || 0,
              plano: data.plano || 'Gratuito',
              escola: data.escola || '',
              seriesLiberadas: data.seriesLiberadas || [],
              ...data
            });
          });
          setClientes(clientesDoFirebase);
          console.log(`✅ ${clientesDoFirebase.length} clientes carregados do Firebase!`);
        } catch (error) {
          console.error('❌ Erro ao carregar clientes:', error);
        }
      }
    };
    carregarClientes();
  }, [firebaseStatus, firebaseReady, currentUser]);

  // 🔧 v424: Carregar professores da escola do Firebase
  useEffect(() => {
    const carregarProfessoresEscola = async () => {
      if (firebaseStatus === 'ready' && firebaseReady && db && currentUser?.tipo === 'escola') {
        try {
          console.log('🔄 Carregando professores da escola...');
          const escolaId = currentUser.uid || currentUser.id;
          
          const snapshot = await db.collection('professoresEscola')
            .where('escolaId', '==', escolaId)
            .get();
          
          const professores = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            professores.push({
              id: doc.id,
              firestoreId: doc.id,
              professorId: data.professorId || data.professorUserId || null, // v510: UID do professor
              nome: data.nome || '',
              email: data.email || '',
              serie: data.serie || '',
              turma: data.turma || '',
              turno: data.turno || '',
              componente: data.componente || '',
              ativo: data.ativo !== false,
              status: data.status || 'ativo',
              paginasGeradas: data.paginasGeradas || 0,
              conviteId: data.conviteId || null
            });
          });
          
          setProfessoresEscola(professores);
          console.log(`✅ ${professores.length} professores carregados!`, professores.map(p => ({ nome: p.nome, professorId: p.professorId })));
        } catch (error) {
          console.error('❌ Erro ao carregar professores:', error);
        }
      }
    };
    carregarProfessoresEscola();
  }, [firebaseStatus, firebaseReady, currentUser]);

  // 🔗 v507: Carregar dados da escola vinculada (para professores vinculados)
  // v689d: Garantir que SEMPRE desbloqueia - nunca fica preso em loading
  const [tentativasEscola, setTentativasEscola] = useState(0);
  const MAX_TENTATIVAS_ESCOLA = 2;
  
  useEffect(() => {
    let timeoutRetry = null;
    let cancelado = false;
    
    const carregarEscolaVinculada = async () => {
      if (!(firebaseStatus === 'ready' && firebaseReady && db && currentUser?.uid && currentUser?.escolaId && currentUser?.tipo === 'professor')) {
        if (currentUser?.tipo === 'professor' && !currentUser?.escolaId) {
          setEscolaVinculada(null);
        } else {
          setEscolaVinculada(null);
        }
        return;
      }
      
      console.log('🔗 v689d: Carregando escola (tentativa', tentativasEscola + 1, ')');
      
      // ========= MÉTODO 1: Ler documento da escola diretamente =========
      try {
        const escolaDoc = await db.collection('users').doc(currentUser.escolaId).get();
        if (cancelado) return;
        if (escolaDoc.exists) {
          const d = escolaDoc.data();
          setEscolaVinculada({
            id: escolaDoc.id,
            nome: d.nomeEscola || d.escola || d.nome || 'Escola',
            creditos: d.creditos || 0,
            seriesLiberadas: d.seriesLiberadas || [],
            plano: d.plano || 'basico',
            logoUrl: d.logoUrl || ''
          });
          console.log('✅ Escola carregada (método 1 - direto)');
          return;
        }
      } catch (e) {
        console.warn('⚠️ Método 1 falhou:', e.message);
      }
      if (cancelado) return;
      
      // ========= MÉTODO 2: Buscar via professoresEscola =========
      try {
        let snap = await db.collection('professoresEscola').where('email', '==', currentUser.email).limit(1).get();
        if (snap.empty) snap = await db.collection('professoresEscola').where('professorId', '==', currentUser.uid).limit(1).get();
        if (cancelado) return;
        
        if (!snap.empty) {
          const vinculo = snap.docs[0].data();
          if (vinculo.escolaId) {
            try {
              const escolaDoc2 = await db.collection('users').doc(vinculo.escolaId).get();
              if (cancelado) return;
              if (escolaDoc2.exists) {
                const d2 = escolaDoc2.data();
                setEscolaVinculada({
                  id: escolaDoc2.id,
                  nome: d2.nomeEscola || d2.escola || d2.nome || 'Escola',
                  creditos: d2.creditos || 0,
                  seriesLiberadas: d2.seriesLiberadas || [],
                  plano: d2.plano || 'basico',
                  logoUrl: d2.logoUrl || ''
                });
                // Corrigir escolaId se estava errado
                if (vinculo.escolaId !== currentUser.escolaId) {
                  db.collection('users').doc(currentUser.uid).update({ escolaId: vinculo.escolaId, escolaNome: d2.nomeEscola || d2.escola || d2.nome }).catch(() => {});
                }
                console.log('✅ Escola carregada (método 2 - via vínculo)');
                return;
              }
            } catch (e2) {
              console.warn('⚠️ Método 2b falhou:', e2.message);
            }
            
            // Método 2c: Montar dados do vínculo sem ler escola
            const nomeFallback = vinculo.escolaNome || currentUser.escolaNome || 'Escola';
            setEscolaVinculada({
              id: vinculo.escolaId,
              nome: nomeFallback,
              creditos: 999,
              seriesLiberadas: currentUser.seriesLiberadas || (vinculo.serie ? [vinculo.serie] : ['1º ano','2º ano','3º ano','4º ano','5º ano','6º ano','7º ano','8º ano','9º ano']),
              plano: 'Vinculado',
              logoUrl: ''
            });
            console.log('✅ Escola carregada (método 2c - dados do vínculo)');
            return;
          }
        }
      } catch (e) {
        console.warn('⚠️ Método 2 falhou:', e.message);
      }
      if (cancelado) return;
      
      // ========= MÉTODO 3: FALLBACK TOTAL - usar dados do professor =========
      // Chega aqui se: escola não existe, não tem vínculo, ou sem permissão
      // SEMPRE desbloqueia - o professor não pode ficar preso
      console.log('🔄 v689d: Fallback total - desbloqueando com dados do professor');
      
      // Todas as séries liberadas se não tiver nenhuma
      const seriesFallback = (currentUser.seriesLiberadas && currentUser.seriesLiberadas.length > 0)
        ? currentUser.seriesLiberadas
        : ['1º ano','2º ano','3º ano','4º ano','5º ano','6º ano','7º ano','8º ano','9º ano'];
      
      setEscolaVinculada({
        id: currentUser.escolaId,
        nome: currentUser.escolaNome || 'Escola',
        creditos: 999,
        seriesLiberadas: seriesFallback,
        plano: currentUser.plano || 'Vinculado',
        logoUrl: '',
        _fallback: true
      });
      console.log('✅ v689d: Desbloqueado via fallback total');
    };
    
    carregarEscolaVinculada();
    
    return () => {
      cancelado = true;
      if (timeoutRetry) clearTimeout(timeoutRetry);
    };
  }, [firebaseStatus, firebaseReady, currentUser?.uid, currentUser?.escolaId, currentUser?.tipo, tentativasEscola]);

  // 🔧 v518: Atualizar cabecalhoConfig quando escolaVinculada for carregada
  // Isso garante que o nome da escola apareça no PDF para professor vinculado
  useEffect(() => {
    if (escolaVinculada && escolaVinculada.nome && currentUser?.tipo === 'professor') {
      console.log('🔧 v518: Atualizando cabecalho com escola vinculada:', escolaVinculada.nome);
      setCabecalhoConfig(prev => ({
        ...prev,
        nomeEscola: escolaVinculada.nome,
        logoUrl: escolaVinculada.logoUrl || '' // v651
      }));
    }
  }, [escolaVinculada, currentUser?.tipo]);

  // 🔄 v654: Converter logo para base64 quando logoUrl mudar (para PDFs)
  useEffect(() => {
    const converterLogo = async () => {
      if (cabecalhoConfig.logoUrl) {
        console.log('🔄 Convertendo logo para base64...');
        const base64 = await converterImagemParaBase64(cabecalhoConfig.logoUrl);
        if (base64) {
          setLogoBase64(base64);
          console.log('✅ Logo convertida para base64');
        }
      } else {
        setLogoBase64('');
      }
    };
    
    converterLogo();
  }, [cabecalhoConfig.logoUrl]);

  // 🔧 v519: Carregar perfilEscola do Firebase quando escola logar
  // 🔧 v521: Também atualizar cabecalhoConfig com nome da escola
  useEffect(() => {
    const carregarPerfilEscola = async () => {
      if (firebaseStatus === 'ready' && firebaseReady && db && currentUser?.uid && currentUser?.tipo === 'escola') {
        try {
          console.log('🔄 Carregando perfil da escola:', currentUser.uid);
          const escolaDoc = await db.collection('users').doc(currentUser.uid).get();
          
          if (escolaDoc.exists) {
            const dados = escolaDoc.data();
            const nomeEscolaReal = dados.nomeEscola || dados.escola || dados.nome || 'Escola';
            
            setPerfilEscola({
              nome: nomeEscolaReal,
              endereco: dados.endereco || '',
              cidade: dados.cidade || '',
              estado: dados.estado || '',
              cep: dados.cep || '',
              telefone: dados.telefone || '',
              email: dados.email || currentUser.email || '',
              diretor: dados.diretor || '',
              cnpj: dados.cnpj || '',
              logoUrl: dados.logoUrl || '' // v651
            });
            
            // 🔧 v521: Atualizar cabecalhoConfig com nome da escola
            setCabecalhoConfig(prev => ({
              ...prev,
              nomeEscola: nomeEscolaReal,
              logoUrl: dados.logoUrl || '' // v651
            }));
            
            // 🔧 v524: Atualizar currentUser com nomeEscola
            setCurrentUser(prev => ({
              ...prev,
              nomeEscola: nomeEscolaReal,
              escola: nomeEscolaReal
            }));
            
            console.log('✅ Perfil da escola carregado:', nomeEscolaReal);
          }
        } catch (error) {
          console.error('❌ Erro ao carregar perfil da escola:', error);
        }
      }
    };
    
    carregarPerfilEscola();
  }, [firebaseStatus, firebaseReady, currentUser?.uid, currentUser?.tipo]);

  // 🔥 Carregar histórico de PDFs do Firebase
  // v505: Escolas veem histórico dos professores vinculados
  // v506: Limpar dados ao mudar de usuário
  useEffect(() => {
    // v506: Limpar dados anteriores imediatamente ao mudar de usuário
    setHistoricoPDFs([]);
    
    const carregarHistoricoPDFs = async () => {
      if (firebaseStatus === 'ready' && firebaseReady && db && currentUser?.uid) {
        setCarregandoHistorico(true);
        try {
          console.log('🔄 Carregando histórico de PDFs para usuário:', currentUser.uid);
          
          let pdfs = [];
          
          // Carregar histórico do próprio usuário
          let snapshot;
          try {
            snapshot = await db.collection('historico_pdfs')
              .where('userId', '==', currentUser.uid)
              .orderBy('dataGeracao', 'desc')
              .limit(50)
              .get();
          } catch (indexError) {
            console.warn('⚠️ Índice não encontrado, carregando sem ordenação:', indexError.message);
            snapshot = await db.collection('historico_pdfs')
              .where('userId', '==', currentUser.uid)
              .limit(50)
              .get();
          }
          
          snapshot.forEach(doc => {
            pdfs.push({ id: doc.id, ...doc.data(), origem: 'proprio' });
          });
          
          // v505: Se for escola, também carregar histórico dos professores vinculados
          if (currentUser?.tipo === 'escola') {
            try {
              const escolaId = currentUser.uid || currentUser.id;
              const profSnapshot = await db.collection('professoresEscola')
                .where('escolaId', '==', escolaId)
                .get();
              
              const professoresIds = [];
              profSnapshot.forEach(doc => {
                const data = doc.data();
                if (data.professorUserId || data.professorId) {
                  professoresIds.push({ id: data.professorUserId || data.professorId, nome: data.nome });
                }
              });
              
              console.log(`📚 Escola: carregando histórico de ${professoresIds.length} professores vinculados`);
              
              // Carregar histórico de cada professor vinculado
              for (const prof of professoresIds) {
                try {
                  const histProf = await db.collection('historico_pdfs')
                    .where('userId', '==', prof.id)
                    .limit(30)
                    .get();
                  
                  histProf.forEach(doc => {
                    pdfs.push({ 
                      id: doc.id, 
                      ...doc.data(), 
                      origem: 'professor',
                      professorId: prof.id,
                      professorNome: prof.nome
                    });
                  });
                } catch (err) {
                  console.warn(`⚠️ Erro ao carregar histórico do professor ${prof.id}:`, err);
                }
              }
            } catch (error) {
              console.error('❌ Erro ao carregar histórico de professores:', error);
            }
          }
          
          // Ordenar manualmente
          pdfs.sort((a, b) => {
            const dataA = a.dataGeracao ? new Date(a.dataGeracao) : new Date(0);
            const dataB = b.dataGeracao ? new Date(b.dataGeracao) : new Date(0);
            return dataB - dataA; // Mais recente primeiro
          });
          
          setHistoricoPDFs(pdfs);
          console.log(`✅ ${pdfs.length} PDFs carregados no histórico!`);
        } catch (error) {
          console.error('❌ Erro ao carregar histórico:', error);
          // Não limpar o histórico em caso de erro
        } finally {
          setCarregandoHistorico(false);
        }
      }
    };
    carregarHistoricoPDFs();
  }, [firebaseStatus, firebaseReady, currentUser?.uid]);

  // 🔥 Listener em tempo real para atualizar dados do usuário (créditos, etc)
  useEffect(() => {
    if (!firebaseReady || !db || !currentUser?.uid) return;
    
    console.log('👂 Iniciando listener de dados do usuário:', currentUser.uid);
    
    // Criar listener em tempo real
    const unsubscribe = db.collection('users').doc(currentUser.uid).onSnapshot(
      (doc) => {
        if (doc.exists) {
          const dadosAtualizados = doc.data();
          console.log('🔄 Dados do usuário atualizados do Firebase:', dadosAtualizados);
          
          // Atualizar o currentUser com os dados mais recentes do Firebase
          setCurrentUser(prev => {
            if (!prev) return prev;
            
            // v516: Verificar se escolaId mudou também
            const escolaIdMudou = prev.escolaId !== dadosAtualizados.escolaId;
            // v523: Verificar se nome da escola mudou
            const nomeEscolaMudou = prev.nomeEscola !== dadosAtualizados.nomeEscola || prev.escola !== dadosAtualizados.escola;
            
            // Só atualiza se algo mudou
            if (prev.creditos !== dadosAtualizados.creditos ||
                JSON.stringify(prev.seriesLiberadas) !== JSON.stringify(dadosAtualizados.seriesLiberadas) ||
                escolaIdMudou || nomeEscolaMudou ||
                prev.logoUrl !== dadosAtualizados.logoUrl) { // v653: sincronizar logoUrl
              console.log('💰 Atualizando currentUser - Créditos:', prev.creditos, '→', dadosAtualizados.creditos, '| nomeEscola:', dadosAtualizados.nomeEscola);
              
              // v653: Atualizar cabecalhoConfig com logoUrl
              setCabecalhoConfig(prevCab => ({
                ...prevCab,
                logoUrl: dadosAtualizados.logoUrl || prevCab.logoUrl || ''
              }));
              
              return {
                ...prev,
                creditos: dadosAtualizados.creditos,
                seriesLiberadas: dadosAtualizados.seriesLiberadas || prev.seriesLiberadas,
                plano: dadosAtualizados.plano || prev.plano,
                nome: dadosAtualizados.nome || prev.nome,
                // v516: Incluir escolaId e escolaNome
                escolaId: dadosAtualizados.escolaId || prev.escolaId,
                escolaNome: dadosAtualizados.escolaNome || prev.escolaNome,
                // v523: Incluir nomeEscola e escola para escolas
                nomeEscola: dadosAtualizados.nomeEscola || prev.nomeEscola,
                escola: dadosAtualizados.escola || prev.escola,
                logoUrl: dadosAtualizados.logoUrl || prev.logoUrl || '' // v653
              };
            }
            return prev;
          });
        }
      },
      (error) => {
        console.error('❌ Erro no listener do usuário:', error);
      }
    );
    
    // Cleanup: remover listener quando componente desmontar ou usuário mudar
    return () => {
      console.log('🔇 Removendo listener de dados do usuário');
      unsubscribe();
    };
  }, [firebaseReady, currentUser?.uid]);

  // 🔥 Criar simulado no Firebase
  const criarSimuladoFirebase = async (dadosSimulado) => {
    // Funciona mesmo sem Firebase (modo local)
    const novoSimulado = {
      id: 'local_' + Date.now(),
      ...dadosSimulado,
      userId: currentUser?.id || 'local',
      dataCriacao: new Date().toISOString(),
      resultados: {}
    };
    
    if (firebaseReady && db && currentUser?.uid) {
      try {
        const docRef = await db.collection('simulados').add({
          ...dadosSimulado,
          userId: currentUser.uid,
          dataCriacao: new Date().toISOString(),
          resultados: {}
        });
        novoSimulado.id = docRef.id;
        console.log('✅ Simulado criado no Firebase:', docRef.id);
      } catch (error) {
        console.error('❌ Erro ao criar no Firebase:', error);
      }
    }
    
    setSimuladosFirebase(prev => [...prev, novoSimulado]);
    setSimuladoSelecionado(novoSimulado);
    return novoSimulado;
  };

  // 🔥 Salvar respostas do simulado
  const salvarRespostasSimulado = async (simuladoId, respostas) => {
    if (!simuladoId) return;
    
    // Atualizar estado local
    setSimuladosFirebase(prev => prev.map(s => 
      s.id === simuladoId ? { ...s, resultados: respostas } : s
    ));
    
    if (simuladoSelecionado?.id === simuladoId) {
      setSimuladoSelecionado(prev => prev ? { ...prev, resultados: respostas } : null);
    }
    
    // Salvar no Firebase se disponível
    if (firebaseReady && db && !simuladoId.startsWith('local_')) {
      try {
        await db.collection('simulados').doc(simuladoId).update({
          resultados: respostas,
          dataAtualizacao: new Date().toISOString()
        });
      } catch (error) {
        console.error('❌ Erro ao salvar:', error);
      }
    }
  };

  // 🔥 Selecionar simulado do histórico
  const selecionarSimulado = (simulado) => {
    setSimuladoSelecionado(simulado);
    setShowDropdownSimulado(false);
    
    // v693: Converter respostas do Firebase para formato da planilha usando função centralizada
    // Busca robusta: turmaId (preciso) > serie+turma > só serie (simulados antigos sem turmaId/turma)
    const encontrarTurmaDoSimulado = (sim) => {
      if (!sim) return null;
      // 1. Por turmaId (simulados novos com v693+)
      if (sim.turmaId) {
        const t = turmas.find(t => String(t.id) === String(sim.turmaId));
        if (t) return t;
      }
      // 2. Por serie + turma exata
      if (sim.serie && sim.turma) {
        const t = turmas.find(t =>
          (t.serie || '').trim().toLowerCase() === (sim.serie || '').trim().toLowerCase() &&
          (t.turma || '').trim().toLowerCase() === (sim.turma || '').trim().toLowerCase()
        );
        if (t) return t;
      }
      // 3. Só por serie (quando turma está em branco ou indefinida — simulados antigos)
      if (sim.serie) {
        const t = turmas.find(t =>
          (t.serie || '').trim().toLowerCase() === (sim.serie || '').trim().toLowerCase()
        );
        if (t) return t;
      }
      return null;
    };

    if (simulado?.respostas && typeof simulado.respostas === 'object') {
      const turmaDoSim = encontrarTurmaDoSimulado(simulado);
      if (turmaDoSim?.alunos) {
        setRespostasEditaveis(converterRespostasParaPlanilha(simulado.respostas, turmaDoSim.alunos, simulado.serie));
      } else if (simulado?.resultados && typeof simulado.resultados === 'object') {
        setRespostasEditaveis(simulado.resultados);
      } else {
        setRespostasEditaveis({});
      }
    } else if (simulado?.resultados && typeof simulado.resultados === 'object') {
      setRespostasEditaveis(simulado.resultados);
    } else {
      setRespostasEditaveis({});
    }
    
    if (simulado) {
      // v693: incluir turmaId para o listener de tempo real encontrar a turma corretamente
      const turmaDoSimConf = encontrarTurmaDoSimulado(simulado);
      setConfigSimulado({
        serie: simulado.serie || '',
        turma: simulado.turma || '',
        turmaId: turmaDoSimConf?.id || simulado.turmaId || '',
        dataAplicacao: simulado.dataAplicacao || ''
      });
      // Usar campo 'codigo' que é o que está sendo salvo
      setNumeroSimulado(simulado.codigo || simulado.numero || Math.floor(Math.random() * 900000) + 100000);
      
      // 🔧 v475: Atualizar componente curricular baseado no simulado selecionado
      if (simulado.componente) {
        // Normalizar o nome do componente
        const comp = simulado.componente.toLowerCase();
        if (comp.includes('matemática') || comp.includes('matematica')) {
          setComponenteSimulado('Matemática');
          setComponenteSimuladoEscola('Matemática');
        } else {
          setComponenteSimulado('Língua Portuguesa');
          setComponenteSimuladoEscola('Língua Portuguesa');
        }
      }
    }
  };

  // 📷 Scanner de QR Code
  const iniciarScannerQR = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (videoQRRef.current) {
        videoQRRef.current.srcObject = stream;
        streamQRRef.current = stream;
        scanningQRRef.current = true;
        setShowScannerQR(true);
        requestAnimationFrame(scanearQRCode);
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      alert('❌ Não foi possível acessar a câmera.');
    }
  };

  const pararScannerQR = () => {
    scanningQRRef.current = false;
    if (streamQRRef.current) {
      streamQRRef.current.getTracks().forEach(track => track.stop());
      streamQRRef.current = null;
    }
    setShowScannerQR(false);
  };

  const scanearQRCode = () => {
    if (!scanningQRRef.current || !videoQRRef.current || !canvasQRRef.current) return;
    const video = videoQRRef.current;
    const canvas = canvasQRRef.current;
    const ctx = canvas.getContext('2d');
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      if (window.jsQR) {
        const code = window.jsQR(imageData.data, imageData.width, imageData.height);
        if (code) {
          try {
            const dados = JSON.parse(code.data);
            if (dados.tipo === 'facilitaprof_cartao' && dados.alunoId) {
              console.log('✅ QR Code detectado:', dados);
              setDadosQRLido(dados);
              pararScannerQR();
              setAlunoOCR({
                index: dados.alunoIndex || 0,
                nome: dados.alunoNome,
                alunoId: dados.alunoId,
                turmaId: dados.turmaId,
                simuladoId: dados.simuladoId,
                painel: 'auto'
              });
              setImagemOCR(null);
              setResultadoOCR(null);
              setShowModalOCR(true);
              return;
            }
          } catch (e) { /* QR inválido */ }
        }
      }
    }
    if (scanningQRRef.current) {
      requestAnimationFrame(scanearQRCode);
    }
  };

  // 🔲 Gerar QR Code como Data URL
  const gerarQRCodeDataURL = (dados) => {
    const jsonString = JSON.stringify(dados);
    try {
      if (window.qrcode) {
        const qr = window.qrcode(0, 'M');
        qr.addData(jsonString);
        qr.make();
        return qr.createDataURL(4, 4);
      }
    } catch (e) {
      console.error('Erro ao gerar QR Code:', e);
    }
    // Fallback placeholder
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, 100, 100);
    ctx.fillStyle = '#000';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('QR CODE', 50, 50);
    return canvas.toDataURL('image/png');
  };

  // Aplicar respostas do OCR na planilha
  const aplicarRespostasOCR = async () => {
    if (!resultadoOCR || !alunoOCR) {
      console.error('❌ Dados faltando:', { resultadoOCR, alunoOCR });
      return;
    }

    // 🔥 Verificar e descontar crédito (1 crédito por leitura OCR)
    // v690: Usa creditosEfetivos — considera escola se professor vinculado
    if (currentUser?.tipo !== 'admin') {
      if (creditosEfetivos < 1) {
        alert('⚠️ Saldo insuficiente!\n\nVocê precisa de 1 crédito para confirmar a leitura OCR.\n\nRecarregue seus créditos para continuar.');
        return;
      }
      
      // v690: Descontar da escola se professor vinculado, do próprio usuário caso contrário
      if (db) {
        try {
          if (isProfessorVinculado && escolaVinculada?.id) {
            const novoSaldo = (escolaVinculada.creditos || 0) - 1;
            await db.collection('users').doc(escolaVinculada.id).update({ creditos: novoSaldo });
            setEscolaVinculada(prev => ({ ...prev, creditos: novoSaldo }));
            console.log('💳 Crédito descontado da escola (aplicarRespostasOCR). Novo saldo escola:', novoSaldo);
          } else if (currentUser?.uid) {
            const novoSaldo = (currentUser.creditos || 0) - 1;
            await db.collection('users').doc(currentUser.uid).update({ creditos: novoSaldo });
            setCurrentUser(prev => ({ ...prev, creditos: novoSaldo }));
            console.log('💳 Crédito descontado! Novo saldo:', novoSaldo);
          }
        } catch (error) {
          console.error('Erro ao atualizar créditos:', error);
        }
      }
    }

    const { index, painel } = alunoOCR;
    // Tratar painel 'auto' (vindo do QR Code) - usa 'prof' como padrão
    const prefixo = (painel === 'prof' || painel === 'auto') ? 'prof' : 'escola';
    
    console.log('📝 Aplicando respostas OCR:', {
      aluno: alunoOCR.nome,
      index,
      painel,
      prefixo,
      respostas: resultadoOCR.respostas
    });
    
    const novasRespostas = { ...respostasEditaveis };

    // Aplicar respostas do bloco 1 (questões 1-11)
    resultadoOCR.respostas.bloco1.forEach((resp, i) => {
      const chave = `${prefixo}_${index}_0_${i}`;
      novasRespostas[chave] = resp && resp !== 'X' ? resp.toUpperCase() : '';
      console.log(`  Q${i+1}: ${chave} = ${novasRespostas[chave]}`);
    });

    // Aplicar respostas do bloco 2 (questões 12-22)
    resultadoOCR.respostas.bloco2.forEach((resp, i) => {
      const chave = `${prefixo}_${index}_1_${i}`;
      novasRespostas[chave] = resp && resp !== 'X' ? resp.toUpperCase() : '';
      console.log(`  Q${i+12}: ${chave} = ${novasRespostas[chave]}`);
    });

    console.log('📊 Novas respostas:', novasRespostas);
    
    // Atualizar estado - isso deve forçar re-render
    setRespostasEditaveis({...novasRespostas});
    
    // 🔥 Salvar automaticamente se houver simulado selecionado
    if (simuladoSelecionado?.id) {
      salvarRespostasSimulado(simuladoSelecionado.id, novasRespostas);
    }
    
    // Fechar modal
    setShowModalOCR(false);
    setResultadoOCR(null);
    setImagemOCR(null);
    setAlunoOCR(null);
    
    // Feedback visual
    alert(`✅ Respostas aplicadas para ${alunoOCR.nome}!${simuladoSelecionado?.id ? '\n\n💾 Salvo automaticamente!' : ''}\n\n💳 1 crédito descontado.`);
  };

  // 🧹 Limpar todas as respostas da planilha
  const limparPlanilha = () => {
    if (!window.confirm('⚠️ Tem certeza que deseja limpar TODAS as respostas da planilha?\n\nEsta ação não pode ser desfeita.')) {
      return;
    }
    setRespostasEditaveis({});
    
    // Limpar também no Firebase se houver simulado
    if (simuladoSelecionado?.id) {
      salvarRespostasSimulado(simuladoSelecionado.id, {});
    }
    
    alert('✅ Planilha limpa com sucesso!');
  };

  // 🗑️ Excluir simulado e planilha
  const excluirSimulado = async () => {
    if (!window.confirm('⚠️ ATENÇÃO!\n\nAo excluir a planilha, você também excluirá o SIMULADO do histórico.\n\nTodas as respostas dos alunos serão perdidas permanentemente.\n\nDeseja continuar?')) {
      return;
    }
    
    const simuladoId = simuladoSelecionado?.id;
    
    // Limpar respostas locais
    setRespostasEditaveis({});
    
    // Remover do Firebase se existir
    if (simuladoId && firebaseReady && db && !simuladoId.startsWith('local_')) {
      try {
        await db.collection('simulados').doc(simuladoId).delete();
        console.log('✅ Simulado excluído do Firebase');
      } catch (error) {
        console.error('❌ Erro ao excluir simulado:', error);
      }
    }
    
    // Remover do estado local
    setSimuladosFirebase(prev => prev.filter(s => s.id !== simuladoId));
    setSimuladoSelecionado(null);
    
    // Gerar novo número de simulado
    obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
    
    alert('✅ Simulado e planilha excluídos com sucesso!');
  };

  // 📄 v500: Função para gerar PDF da Planilha de Resultados
  // 🔧 v525: Mostrar nome do professor corretamente
  const gerarPDFPlanilha = async (tipoUsuario = 'escola') => {
    const turmaAtual = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
    if (!turmaAtual || !simuladoSelecionado) {
      alert('⚠️ Selecione um simulado e uma turma para gerar o PDF!');
      return;
    }

    const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, tipoUsuario === 'escola' ? blocosSimuladoEscola : blocosSimulado, questoes, 1);
    const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, tipoUsuario === 'escola' ? blocosSimuladoEscola : blocosSimulado, questoes, 2);
    
    const alunosLimitados = turmaAtual.alunos || []; // v689d: Sem limite - paginação automática
    const prefixo = tipoUsuario === 'escola' ? 'escola' : 'prof';
    const componente = simuladoSelecionado.componente || componenteSimuladoEscola;
    
    // Calcular alunos com respostas (presentes)
    const alunosComRespostas = alunosLimitados.filter((_, idx) => {
      for (let b = 0; b < 2; b++) {
        const totalNoBloco = b === 0 ? gabaritosB1.length : gabaritosB2.length;
        for (let q = 0; q < totalNoBloco; q++) {
          if (respostasEditaveis[`${prefixo}_${idx}_${b}_${q}`]) return true;
        }
      }
      return false;
    }).length;
    
    // Nome completo da turma
    const turmaNomeCompleto = turmaAtual.nome || `${turmaAtual.serie} ${turmaAtual.turma || ''}`.trim();
    
    // Número do simulado
    const numeroSimulado = simuladoSelecionado.codigo || simuladoSelecionado.numero || '---';
    
    // 🔧 v525: Determinar nome do professor
    let nomeProfessorPDF = cabecalhoConfig.professor;
    if (tipoUsuario === 'escola' && simuladoSelecionado?.userId) {
      // Buscar nome do professor que criou o simulado
      const profDoSimulado = professoresEscola.find(p => 
        p.professorId === simuladoSelecionado.userId || 
        p.professorUserId === simuladoSelecionado.userId ||
        p.id === simuladoSelecionado.userId
      );
      if (profDoSimulado?.nome) {
        nomeProfessorPDF = profDoSimulado.nome;
      }
    }
    if (!nomeProfessorPDF) {
      nomeProfessorPDF = currentUser?.nome || '-';
    }

    // Construir HTML da planilha
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Planilha de Resultados - Simulado SAEB</title>
        <style>
          @page { 
            size: A4 landscape; 
            margin: 10mm; 
          }
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          body { font-family: Arial, sans-serif; font-size: 9px; margin: 0; padding: 0; }
          .header { background: #1e3a5f !important; color: white; padding: 10px 15px; margin-bottom: 10px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .header-title { display: flex; align-items: center; gap: 15px; margin-bottom: 8px; }
          .header-logo { height: 40px; width: auto; }
          .header h1 { margin: 0; font-size: 16px; }
          .header-info { display: flex; flex-wrap: wrap; gap: 15px; font-size: 10px; }
          .header-info span { background: rgba(255,255,255,0.15); padding: 3px 8px; border-radius: 4px; }
          table { width: 100%; border-collapse: collapse; font-size: 8px; }
          th, td { border: 1px solid #ddd; padding: 3px 4px; text-align: center; }
          th { background: #f0f0f0 !important; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .bloco-header { background: #e8d4f0 !important; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .bloco2-header { background: #d4e8f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .habilidade { background: #fef3c7 !important; font-size: 7px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .gabarito { background: #e0e7ff !important; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .alunos-header { background: #f8fafc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .aluno-nome { text-align: left; background: #f8f8f8 !important; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .acerto { background: #d1fae5 !important; color: #065f46; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .erro { background: #fee2e2 !important; color: #991b1b; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .vazio { background: #f3f4f6 !important; color: #9ca3af; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .total-acertos { background: #10b981 !important; color: white; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .total-header { background: #10b981 !important; color: white; font-weight: bold; font-size: 7px; vertical-align: middle; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pct-row td { font-weight: bold; font-size: 7px; }
          .pct-verde { background: #d1fae5 !important; color: #065f46; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pct-amarelo { background: #fef3c7 !important; color: #92400e; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pct-vermelho { background: #fee2e2 !important; color: #991b1b; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .footer { margin-top: 10px; font-size: 8px; color: #666; text-align: center; }
          .page-break { page-break-before: always; }
          thead { display: table-header-group; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-title">
            ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="header-logo" />` : ''}
            <h1>${cabecalhoConfig.nomeEscola}</h1>
          </div>
          <div class="header-info">
            <span>👤 Professor: <strong>${nomeProfessorPDF}</strong></span>
            <span>📚 Turma: <strong>${turmaNomeCompleto}</strong></span>
            <span>📋 Simulado: <strong>#${formatarCodigoSimulado(numeroSimulado)}</strong></span>
            <span>📖 Componente: <strong>${componente}</strong></span>
            <span>👥 Matriculados: <strong>${turmaAtual.alunos?.length || 0}</strong></span>
            <span>✅ Presentes: <strong>${alunosComRespostas}</strong></span>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th rowspan="2" style="width: 120px;">QUESTÕES</th>
              <th colspan="${questoesPorBlocoSAEB(configSimulado.serie)}" class="bloco-header">BLOCO 1 - ${componente?.toUpperCase()}</th>
              <th colspan="${questoesPorBlocoSAEB(configSimulado.serie)}" class="bloco2-header">BLOCO 2 - ${componente?.toUpperCase()}</th>
              <th rowspan="5" style="width: 55px; vertical-align: middle; text-align: center;" class="total-header"><span style="font-size: 7px; writing-mode: vertical-lr; transform: rotate(180deg);">TOTAL DE ACERTOS</span></th>
            </tr>
            <tr>
              ${[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => `<th>Q${String(i+1).padStart(2,'0')}</th>`).join('')}
              ${[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => `<th>Q${String(i+questoesPorBlocoSAEB(configSimulado.serie)+1).padStart(2,'0')}</th>`).join('')}
            </tr>
            <tr class="habilidade">
              <th><strong>HABILIDADES/<br/>DESCRITORES</strong></th>
              ${gabaritosB1.map(g => `<th class="habilidade">${g.habilidade || '-'}</th>`).join('')}
              ${gabaritosB2.map(g => `<th class="habilidade">${g.habilidade || '-'}</th>`).join('')}
            </tr>
            <tr class="gabarito">
              <th><strong>GABARITO</strong></th>
              ${gabaritosB1.map(g => `<th class="gabarito">${g.gabarito || '-'}</th>`).join('')}
              ${gabaritosB2.map(g => `<th class="gabarito">${g.gabarito || '-'}</th>`).join('')}
            </tr>
            <tr class="alunos-header">
              <th><strong>ALUNOS</strong></th>
              ${[...Array(questoesPorBlocoSAEB(configSimulado.serie) * 2)].map(() => `<th></th>`).join('')}
            </tr>
          </thead>
          <tbody>
    `;

    // v689d: Cabeçalho da tabela reutilizável para paginação
    const theadHTML = `
          <thead>
            <tr>
              <th rowspan="2" style="width: 120px;">QUESTÕES</th>
              <th colspan="${questoesPorBlocoSAEB(configSimulado.serie)}" class="bloco-header">BLOCO 1 - ${componente?.toUpperCase()}</th>
              <th colspan="${questoesPorBlocoSAEB(configSimulado.serie)}" class="bloco2-header">BLOCO 2 - ${componente?.toUpperCase()}</th>
              <th rowspan="5" style="width: 55px; vertical-align: middle; text-align: center;" class="total-header"><span style="font-size: 7px; writing-mode: vertical-lr; transform: rotate(180deg);">TOTAL DE ACERTOS</span></th>
            </tr>
            <tr>
              ${[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => `<th>Q${String(i+1).padStart(2,'0')}</th>`).join('')}
              ${[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => `<th>Q${String(i+questoesPorBlocoSAEB(configSimulado.serie)+1).padStart(2,'0')}</th>`).join('')}
            </tr>
            <tr class="habilidade">
              <th><strong>HABILIDADES/<br/>DESCRITORES</strong></th>
              ${gabaritosB1.map(g => `<th class="habilidade">${g.habilidade || '-'}</th>`).join('')}
              ${gabaritosB2.map(g => `<th class="habilidade">${g.habilidade || '-'}</th>`).join('')}
            </tr>
            <tr class="gabarito">
              <th><strong>GABARITO</strong></th>
              ${gabaritosB1.map(g => `<th class="gabarito">${g.gabarito || '-'}</th>`).join('')}
              ${gabaritosB2.map(g => `<th class="gabarito">${g.gabarito || '-'}</th>`).join('')}
            </tr>
            <tr class="alunos-header">
              <th><strong>ALUNOS</strong></th>
              ${[...Array(questoesPorBlocoSAEB(configSimulado.serie) * 2)].map(() => `<th></th>`).join('')}
            </tr>
          </thead>`;

    const ALUNOS_POR_PAGINA = 20;

    // Linhas dos alunos com paginação
    alunosLimitados.forEach((aluno, idx) => {
      // v689d: Quebra de página a cada 20 alunos (exceto primeiro grupo)
      if (idx > 0 && idx % ALUNOS_POR_PAGINA === 0) {
        html += `</tbody></table><div class="page-break"></div><table>${theadHTML}<tbody>`;
      }
      let acertosTotal = 0;
      let cells = '';
      
      // Bloco 1
      gabaritosB1.forEach((g, qIdx) => {
        const chave = `${prefixo}_${idx}_0_${qIdx}`;
        const resp = respostasEditaveis[chave] || '';
        const acertou = resp && resp === g.gabarito;
        if (acertou) acertosTotal++;
        const classe = !resp ? 'vazio' : acertou ? 'acerto' : 'erro';
        cells += `<td class="${classe}">${resp || '-'}</td>`;
      });
      
      // Bloco 2
      gabaritosB2.forEach((g, qIdx) => {
        const chave = `${prefixo}_${idx}_1_${qIdx}`;
        const resp = respostasEditaveis[chave] || '';
        const acertou = resp && resp === g.gabarito;
        if (acertou) acertosTotal++;
        const classe = !resp ? 'vazio' : acertou ? 'acerto' : 'erro';
        cells += `<td class="${classe}">${resp || '-'}</td>`;
      });

      html += `
        <tr>
          <td class="aluno-nome">${idx + 1}. ${aluno.nome || aluno}</td>
          ${cells}
          <td class="total-acertos">${acertosTotal}</td>
        </tr>
      `;
    });

    // Linha de % ACERTOS
    html += `<tr class="pct-row"><td><strong>% ACERTOS</strong></td>`;
    
    // Calcular % por questão - Bloco 1
    gabaritosB1.forEach((g, qIdx) => {
      let acertos = 0, total = 0;
      alunosLimitados.forEach((_, aIdx) => {
        const resp = respostasEditaveis[`${prefixo}_${aIdx}_0_${qIdx}`];
        if (resp) { total++; if (resp === g.gabarito) acertos++; }
      });
      const pct = total > 0 ? Math.round((acertos / total) * 100) : 0;
      const classe = pct >= 70 ? 'pct-verde' : pct >= 50 ? 'pct-amarelo' : 'pct-vermelho';
      html += `<td class="${classe}">${pct}%</td>`;
    });
    
    // Calcular % por questão - Bloco 2
    gabaritosB2.forEach((g, qIdx) => {
      let acertos = 0, total = 0;
      alunosLimitados.forEach((_, aIdx) => {
        const resp = respostasEditaveis[`${prefixo}_${aIdx}_1_${qIdx}`];
        if (resp) { total++; if (resp === g.gabarito) acertos++; }
      });
      const pct = total > 0 ? Math.round((acertos / total) * 100) : 0;
      const classe = pct >= 70 ? 'pct-verde' : pct >= 50 ? 'pct-amarelo' : 'pct-vermelho';
      html += `<td class="${classe}">${pct}%</td>`;
    });
    
    html += `<td></td></tr>`;

    html += `
          </tbody>
        </table>
        <div class="footer">
          Planilha de Resultados - Simulado SAEB #${formatarCodigoSimulado(numeroSimulado)} | Gerado por FacilitaProf em ${new Date().toLocaleDateString('pt-BR')} | ${alunosLimitados.length} alunos
        </div>
      </body>
      </html>
    `;

    // Abrir em nova janela para impressão
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // 📊 v502: Função para gerar PDF do Gráfico de Desempenho
  const gerarPDFGrafico = async (tipoUsuario = 'escola') => {
    const turmaAtual = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
    if (!turmaAtual || !simuladoSelecionado) {
      alert('⚠️ Selecione um simulado e uma turma para gerar o PDF!');
      return;
    }

    const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, tipoUsuario === 'escola' ? blocosSimuladoEscola : blocosSimulado, questoes, 1);
    const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, tipoUsuario === 'escola' ? blocosSimuladoEscola : blocosSimulado, questoes, 2);
    const prefixo = tipoUsuario === 'escola' ? 'escola' : 'prof';
    const componente = simuladoSelecionado.componente || componenteSimuladoEscola;
    
    // Nome completo da turma
    const turmaNomeCompleto = turmaAtual.nome || `${turmaAtual.serie} ${turmaAtual.turma || ''}`.trim();
    
    // Número do simulado
    const numeroSimulado = simuladoSelecionado.codigo || simuladoSelecionado.numero || '---';
    
    // Total de questões válidas
    const totalQuestoes = gabaritosB1.filter(g => g.gabarito).length + gabaritosB2.filter(g => g.gabarito).length;

    // Calcular dados do gráfico
    let totalAcertos = 0, totalRespostas = 0;
    const acertosPorHabilidade = {};

    // Inicializar habilidades
    [...gabaritosB1, ...gabaritosB2].forEach(g => {
      if (g.habilidade && !acertosPorHabilidade[g.habilidade]) {
        acertosPorHabilidade[g.habilidade] = { acertos: 0, total: 0 };
      }
    });

    // Calcular alunos com respostas (presentes)
    let alunosComRespostas = 0;
    turmaAtual.alunos?.forEach((_, aIdx) => {
      let temResposta = false;
      gabaritosB1.forEach((g, qIdx) => {
        if (g.gabarito) {
          const resp = respostasEditaveis[`${prefixo}_${aIdx}_0_${qIdx}`];
          if (resp) {
            temResposta = true;
            totalRespostas++;
            if (resp === g.gabarito) {
              totalAcertos++;
              if (g.habilidade) acertosPorHabilidade[g.habilidade].acertos++;
            }
            if (g.habilidade) acertosPorHabilidade[g.habilidade].total++;
          }
        }
      });
      gabaritosB2.forEach((g, qIdx) => {
        if (g.gabarito) {
          const resp = respostasEditaveis[`${prefixo}_${aIdx}_1_${qIdx}`];
          if (resp) {
            temResposta = true;
            totalRespostas++;
            if (resp === g.gabarito) {
              totalAcertos++;
              if (g.habilidade) acertosPorHabilidade[g.habilidade].acertos++;
            }
            if (g.habilidade) acertosPorHabilidade[g.habilidade].total++;
          }
        }
      });
      if (temResposta) alunosComRespostas++;
    });

    const percentualGeral = totalRespostas > 0 ? (totalAcertos / totalRespostas) * 100 : 0;
    
    // Determinar padrão de desempenho (igual ao da página)
    let padrao, padraoColor, padraoDesc;
    if (percentualGeral >= 70) {
      padrao = 'AVANÇADO'; padraoColor = '#0ea5e9';
      padraoDesc = 'Este padrão agrupa estudantes com desenvolvimento além do esperado para a sua etapa de escolaridade, os quais precisam de estímulos para continuar avançando no processo de aprendizagem.';
    } else if (percentualGeral >= 40) {
      padrao = 'ADEQUADO'; padraoColor = '#64748b';
      padraoDesc = 'Este padrão reúne estudantes que consolidaram o desenvolvimento das habilidades e competências previstas para a etapa de escolaridade. Entretanto, ainda requerem ações para aprofundar a aprendizagem.';
    } else if (percentualGeral >= 20) {
      padrao = 'BÁSICO'; padraoColor = '#f97316';
      padraoDesc = 'Este padrão agrupa estudantes que ainda não demonstram ter desenvolvido adequadamente as habilidades e competências essenciais para a sua etapa de escolaridade. Demandam atividades de reforço na aprendizagem.';
    } else {
      padrao = 'ABAIXO DO BÁSICO'; padraoColor = '#ef4444';
      padraoDesc = 'Este padrão reúne estudantes com carência de aprendizagem para o desenvolvimento das habilidades e competências mínimas requeridas para a conclusão da etapa de escolaridade em que se encontram. São estudantes que necessitam de ações pedagógicas de recuperação.';
    }

    // Calcular nível de proficiência (igual ao da página)
    const nivel = Math.min(10, Math.max(1, Math.ceil(percentualGeral / 10)));
    const faixaPontos = componente?.toLowerCase().includes('portugu') 
      ? ['Até 125 pontos', '125 a 150 pontos', '150 a 175 pontos', '175 a 200 pontos', '200 a 225 pontos', '225 a 250 pontos', '250 a 275 pontos', '275 a 300 pontos', '300 a 325 pontos', 'Acima de 325 pontos']
      : ['Até 150 pontos', '150 a 175 pontos', '175 a 200 pontos', '200 a 225 pontos', '225 a 250 pontos', '250 a 275 pontos', '275 a 300 pontos', '300 a 325 pontos', '325 a 350 pontos', 'Acima de 350 pontos'];

    // Gerar barras verticais do gráfico
    const habilidades = Object.entries(acertosPorHabilidade)
      .filter(([_, v]) => v.total > 0)
      .map(([hab, v]) => ({
        nome: hab,
        pct: Math.round((v.acertos / v.total) * 100)
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome));

    // Função para determinar cor da barra igual ao sistema
    const getBarColor = (pct) => {
      if (pct >= 70) return '#22c55e'; // Verde - Avançado
      if (pct >= 40) return '#3b82f6'; // Azul - Adequado
      if (pct >= 20) return '#eab308'; // Amarelo - Básico
      return '#ef4444'; // Vermelho - Abaixo do Básico
    };

    const barWidth = Math.min(50, Math.floor(500 / Math.max(habilidades.length, 1)));
    
    let barrasHTML = habilidades.length > 0 ? `
      <div style="display: flex; align-items: flex-end; justify-content: center; gap: 8px; height: 180px; padding: 15px 0;">
        ${habilidades.map(h => {
          const barColor = getBarColor(h.pct);
          const barHeight = Math.max(5, h.pct * 1.4);
          return `
          <div style="display: flex; flex-direction: column; align-items: center; width: ${barWidth}px;">
            <div style="font-size: 9px; font-weight: bold; margin-bottom: 3px; color: ${barColor};">${h.pct}%</div>
            <div style="width: 100%; height: 130px; display: flex; flex-direction: column; justify-content: flex-end;">
              <div style="width: 100%; background-color: ${barColor} !important; height: ${barHeight}px; border-radius: 3px 3px 0 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;"></div>
            </div>
            <div style="font-size: 9px; font-weight: bold; margin-top: 4px; color: #ffffff; text-align: center; text-shadow: 0 1px 2px rgba(0,0,0,0.5);">${h.nome}</div>
          </div>
        `;}).join('')}
      </div>
      <div style="display: flex; justify-content: center; gap: 15px; margin-top: 10px; font-size: 9px;">
        <span style="display: flex; align-items: center; gap: 4px;"><span style="width: 12px; height: 12px; background: #22c55e; border-radius: 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact;"></span> Avançado</span>
        <span style="display: flex; align-items: center; gap: 4px;"><span style="width: 12px; height: 12px; background: #3b82f6; border-radius: 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact;"></span> Adequado</span>
        <span style="display: flex; align-items: center; gap: 4px;"><span style="width: 12px; height: 12px; background: #eab308; border-radius: 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact;"></span> Básico</span>
        <span style="display: flex; align-items: center; gap: 4px;"><span style="width: 12px; height: 12px; background: #ef4444; border-radius: 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact;"></span> Abaixo do Básico</span>
      </div>
    ` : '<p style="text-align: center; color: #999; padding: 40px;">Selecione questões no simulado para ver o gráfico de desempenho</p>';

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Gráfico de Desempenho - Simulado SAEB</title>
        <style>
          @page { size: A4 landscape; margin: 12mm; }
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .container { background: #1e3a5f !important; padding: 15px 20px; border-radius: 10px; min-height: 175mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .title-section { display: flex; align-items: center; justify-content: center; gap: 15px; margin-bottom: 5px; }
          .title-logo { height: 50px; width: auto; }
          .title { color: white; text-align: center; margin: 0; font-size: 18px; }
          .subtitle { color: #93c5fd; text-align: center; font-size: 14px; margin-bottom: 10px; font-weight: bold; }
          .info-row { display: flex; justify-content: center; gap: 20px; margin-bottom: 10px; }
          .info-item { color: #93c5fd; font-size: 11px; }
          .info-item strong { color: white; }
          .cards { display: flex; gap: 12px; margin-bottom: 12px; }
          .card { flex: 1; background: white !important; border-radius: 8px; padding: 10px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .card-padrao { border-left: 5px solid ${padraoColor} !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .card-nivel { border-left: 5px solid #8b5cf6 !important; text-align: center; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .card-participantes { border-left: 5px solid #3b82f6 !important; text-align: center; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .card-title { font-size: 9px; color: #666; margin-bottom: 3px; text-transform: uppercase; }
          .card-value { font-size: 18px; font-weight: bold; margin: 3px 0; }
          .card-desc { font-size: 7px; color: #666; line-height: 1.3; text-align: justify; }
          .chart-section { background: white !important; border-radius: 8px; padding: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .chart-title { text-align: center; font-weight: bold; margin-bottom: 8px; color: #1e3a5f; font-size: 12px; }
          .footer { text-align: center; color: #93c5fd; font-size: 9px; margin-top: 8px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="title-section">
            ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="title-logo" />` : ''}
            <h2 class="title">NÍVEIS E ESCALAS DE PROFICIÊNCIA NO SIMULADO SAEB</h2>
          </div>
          <p class="subtitle">Simulado #${formatarCodigoSimulado(numeroSimulado)}</p>
          
          <div class="info-row">
            <span class="info-item">📚 Turma: <strong>${turmaNomeCompleto}</strong></span>
            <span class="info-item">📖 Componente: <strong>${componente?.toUpperCase()}</strong></span>
          </div>
          
          <p style="color: #93c5fd; text-align: center; font-size: 11px; margin-bottom: 10px;">
            De acordo com os resultados neste simulado, a turma ${turmaNomeCompleto} está com:
          </p>
          
          <div class="cards">
            <div class="card card-padrao">
              <div class="card-title">Padrão de Desempenho</div>
              <div class="card-value" style="color: ${padraoColor};">${padrao}</div>
              <div class="card-desc">${padraoDesc}</div>
            </div>
            <div class="card card-nivel">
              <div class="card-title">Nível de Proficiência</div>
              <div class="card-value" style="color: #8b5cf6;">Nível ${nivel}</div>
              <div class="card-desc" style="text-align: center;">${faixaPontos[nivel - 1] || ''}<br/>${turmaNomeCompleto} - ${componente}</div>
            </div>
            <div class="card card-participantes">
              <div class="card-title">Participantes</div>
              <div class="card-value" style="color: #3b82f6;">${alunosComRespostas}</div>
              <div class="card-desc" style="text-align: center;">alunos • ${totalQuestoes} questões</div>
            </div>
          </div>

          <div class="chart-section">
            <div class="chart-title">PORCENTAGEM DE ACERTO POR HABILIDADES/DESCRITORES</div>
            ${barrasHTML}
          </div>

          <div class="footer">
            Gráfico de Desempenho - Simulado SAEB #${formatarCodigoSimulado(numeroSimulado)} | ${cabecalhoConfig.nomeEscola} | Gerado por FacilitaProf em ${new Date().toLocaleDateString('pt-BR')}
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const salvarCabecalho = async () => {
    // Validações
    if (!cabecalhoConfig.nomeEscola.trim()) {
      alert('⚠️ O nome da escola é obrigatório!');
      return;
    }
    if (!cabecalhoConfig.professor.trim()) {
      alert('⚠️ O nome do professor é obrigatório!');
      return;
    }

    // v653: Salvar no Firebase (incluindo logoUrl)
    if (!firebaseReady || !db) {
      alert('⚠️ Firebase não está pronto. Aguarde e tente novamente.');
      return;
    }
    
    if (!currentUser?.uid) {
      alert('⚠️ Usuário não identificado. Faça login novamente.');
      return;
    }
    
    try {
      await db.collection('users').doc(currentUser.uid).update({
        professor: cabecalhoConfig.professor,
        logoUrl: cabecalhoConfig.logoUrl || '',
        atualizadoEm: new Date().toISOString()
      });
      
      console.log('✅ Configurações do cabeçalho salvas no Firebase!');
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('❌ Erro ao salvar cabeçalho:', error);
      alert('❌ Erro ao salvar: ' + error.message);
    }
  };

  // 🗑️ Função para excluir questões em lote
  const excluirQuestoesEmLote = async () => {
    if (questoesSelecionadasLote.length === 0) {
      alert('⚠️ Nenhuma questão selecionada!');
      return;
    }
    
    if (!window.confirm(`⚠️ ATENÇÃO!\n\nVocê está prestes a excluir ${questoesSelecionadasLote.length} questão(ões).\n\nEsta ação não pode ser desfeita.\n\nDeseja continuar?`)) {
      return;
    }
    
    try {
      if (firebaseReady && db) {
        // Excluir cada questão do Firebase (soft delete)
        for (const qId of questoesSelecionadasLote) {
          await db.collection('questoes').doc(qId).update({ ativo: false });
        }
        // v657: Invalidar cache após exclusão
        _invalidarCache();
        // v660: Forçar reload da lista
        setReloadTrigger(prev => prev + 1);
        setQuestoesSelecionadasLote([]);
        alert(`✅ ${questoesSelecionadasLote.length} questão(ões) excluída(s) com sucesso!`);
      } else {
        // Remover apenas localmente
        setQuestoesFirebase(prev => prev.filter(q => !questoesSelecionadasLote.includes(q.id)));
        setQuestoesSelecionadasLote([]);
        alert(`✅ ${questoesSelecionadasLote.length} questão(ões) excluída(s) localmente!`);
      }
    } catch (error) {
      console.error('Erro ao excluir questões:', error);
      alert('❌ Erro ao excluir questões: ' + error.message);
    }
  };

  // 📝 Função para atualizar gabarito da questão
  const atualizarGabaritoQuestao = async (questaoId, novoGabarito) => {
    console.log(`🔄 [v670] Atualizando gabarito: questão ${questaoId} → "${novoGabarito}"`);
    try {
      if (firebaseReady && db) {
        await db.collection('questoes').doc(questaoId).update({ gabarito: novoGabarito });
        console.log(`✅ [v670] Gabarito atualizado no Firestore!`);
        // v670: Invalidar cache para garantir que próxima busca pegue do Firestore
        _invalidarCache();
        console.log(`✅ [v670] Cache invalidado - próxima busca verá o gabarito atualizado`);
      } else {
        console.warn('⚠️ [v670] Firebase não pronto - gabarito NÃO salvo no Firestore');
      }
      // Atualizar localmente
      setQuestoesFirebase(prev => prev.map(q => 
        q.id === questaoId ? { ...q, gabarito: novoGabarito } : q
      ));
      console.log(`✅ [v670] Gabarito atualizado localmente`);
    } catch (error) {
      console.error('❌ [v670] Erro ao atualizar gabarito:', error);
      alert('❌ Erro ao atualizar gabarito: ' + error.message);
    }
  };

  // 📝 v672: Função para atualizar gabarito SOMENTE neste simulado (override)
  const atualizarGabaritoSimulado = async (simuladoId, numeroQuestao, novoGabarito) => {
    console.log(`🔄 [v672] Atualizando gabarito do simulado ${simuladoId}, questão ${numeroQuestao} → "${novoGabarito}"`);
    try {
      if (firebaseReady && db) {
        // Carregar simulado atual
        const simuladoRef = db.collection('simulados').doc(simuladoId);
        const simuladoDoc = await simuladoRef.get();
        
        if (!simuladoDoc.exists) {
          console.error('❌ Simulado não encontrado');
          return;
        }
        
        const simuladoData = simuladoDoc.data();
        const gabaritosOverride = simuladoData.gabaritosOverride || {};
        
        // Atualizar gabarito override
        gabaritosOverride[numeroQuestao] = novoGabarito;
        
        await simuladoRef.update({ gabaritosOverride });
        console.log(`✅ [v672] Gabarito override salvo no Firestore!`);
        
        // Atualizar localmente o simulado selecionado
        if (simuladoSelecionado && simuladoSelecionado.id === simuladoId) {
          setSimuladoSelecionado({
            ...simuladoSelecionado,
            gabaritosOverride
          });
        }
      } else {
        console.warn('⚠️ [v672] Firebase não pronto');
      }
    } catch (error) {
      console.error('❌ [v672] Erro ao atualizar gabarito do simulado:', error);
      alert('❌ Erro ao atualizar gabarito: ' + error.message);
    }
  };

  // 📄 Função para gerar PDF usando html2canvas + jsPDF
  const gerarPDF = async () => {
    setGerandoPDF(true);
    
    try {
      // 🔥 Se for Simulado SAEB, salvar no histórico
      if (tipoGeracao === 'simulado') {
        await criarSimuladoFirebase({
          codigo: numeroSimulado, // v610: Usar 'codigo' em vez de 'numero'
          numero: numeroSimulado,  // Manter compatibilidade
          serie: configSimulado.serie,
          turma: configSimulado.turma,
          componente: componenteSimulado,
          dataAplicacao: configSimulado.dataAplicacao || new Date().toISOString().split('T')[0],
          questoesBloco1: blocosSimulado[0]?.questoesSelecionadas || [],
          questoesBloco2: blocosSimulado[1]?.questoesSelecionadas || [],
          alunos: turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)))?.alunos || []
        });
      }

      // Importar bibliotecas dinamicamente
      const html2canvas = (await import('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.esm.js')).default;
      const { jsPDF } = await import('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const elementos = pdfContainerRef.current?.querySelectorAll('.pagina-pdf');
      
      if (!elementos || elementos.length === 0) {
        alert('Nenhuma página para gerar!');
        setGerandoPDF(false);
        return;
      }
      
      for (let i = 0; i < elementos.length; i++) {
        const elemento = elementos[i];
        const canvas = await html2canvas(elemento, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210; // A4 width in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      }
      
      // Nome do arquivo
      const tipoDoc = tipoGeracao === 'pronta' ? 'Atividade_Pronta' : 
                     tipoGeracao === 'questao' ? 'Atividade' : 
                     tipoGeracao === 'avaliacao' ? 'Avaliacao' : 'Simulado_SAEB';
      const dataAtual = new Date().toISOString().split('T')[0];
      
      pdf.save(`${tipoDoc}_${cabecalhoConfig.serie.replace(/\s/g, '')}_${dataAtual}.pdf`);
      
      setShowModalGerarPDF(false);
      alert('✅ PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      // Fallback: usar window.print()
      gerarPDFComPrint();
    }
    
    setGerandoPDF(false);
  };
  
  // 📄 Fallback: Gerar PDF usando window.print()
  const gerarPDFComPrint = () => {
    const conteudo = pdfContainerRef.current;
    if (!conteudo) return;
    
    const janelaImpressao = window.open('', '_blank');
    janelaImpressao.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>FacilitaPROF - ${tipoGeracao === 'pronta' ? 'Atividade Pronta' : tipoGeracao === 'questao' ? 'Atividade' : tipoGeracao === 'avaliacao' ? 'Avaliação' : 'Simulado SAEB'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; }
          @page { size: A4; margin: 10mm; }
          @media print {
            .pagina-pdf { page-break-after: always; }
            .pagina-pdf:last-child { page-break-after: avoid; }
          }
          .pagina-pdf {
            width: 210mm;
            min-height: 287mm;
            padding: 15mm;
            background: white;
            margin: 0 auto 10px;
          }
          .cabecalho { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; text-align: center; }
          .cabecalho h1 { font-size: 16px; text-transform: uppercase; }
          .cabecalho p { font-size: 12px; margin-top: 5px; }
          .info-aluno { border-bottom: 1px solid #ccc; padding-bottom: 10px; margin-bottom: 15px; font-size: 12px; }
          .questao { border: 1px solid #ccc; border-radius: 5px; padding: 10px; margin-bottom: 10px; }
          .questao-numero { background: #eee; padding: 3px 8px; border-radius: 3px; font-weight: bold; font-size: 12px; }
          .questao-area { min-height: 100px; background: #f9f9f9; border: 1px dashed #ccc; margin-top: 10px; display: flex; align-items: center; justify-content: center; color: #999; }
          .rodape { text-align: center; font-size: 10px; color: #666; margin-top: 20px; }
          .gabarito-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-top: 20px; }
          .gabarito-item { border: 1px solid #ccc; padding: 10px; text-align: center; border-radius: 5px; }
          .gabarito-item .numero { font-size: 10px; color: #666; }
          .gabarito-item .questao-num { font-size: 18px; font-weight: bold; }
          .gabarito-item .resposta { font-size: 24px; font-weight: bold; color: green; }
        </style>
      </head>
      <body>
        ${conteudo.innerHTML}
      </body>
      </html>
    `);
    janelaImpressao.document.close();
    setTimeout(() => {
      janelaImpressao.print();
      janelaImpressao.close();
    }, 500);
  };

  // Função para calcular espaço usado por questão (em cm)
  // v475: Tamanhos consistentes com o cálculo de páginas
  // Cada questão = título (0.5cm) + conteúdo + padding (1.0cm)
  const getTamanhoQuestao = (tamanho) => {
    // Normalizar o tamanho (lowercase, sem acentos)
    const t = (tamanho || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Tamanhos TOTAIS das questões (título 0.5cm + conteúdo + padding 1.0cm)
    const tamanhos = {
      'pequena': 6.5,    // 0.5 + 5.0 + 1.0
      'media': 8.5,      // 0.5 + 7.0 + 1.0
      'grande': 12.0,    // 0.5 + 10.5 + 1.0
      'inteira': 22.0    // 0.5 + 20.5 + 1.0
    };
    return tamanhos[t] || 8.5; // Default média
  };
  
  // Padding entre questões já está incluído no tamanho da questão
  // Então não precisamos adicionar margin extra
  const MARGIN_ENTRE_QUESTOES = 0; // Já incluído no tamanho
  
  // Altura máxima disponível por página (em cm)
  // v475: Valores consistentes com calcularPaginasPorTamanho
  // Página A4 = ~26cm útil (conservador)
  const ALTURA_CABECALHO = 5.0;
  const ALTURA_UTIL_PAGINA = 26.0;
  const ALTURA_MAXIMA_PAGINA_COM_CABECALHO = ALTURA_UTIL_PAGINA - ALTURA_CABECALHO; // 21cm para página 1
  const ALTURA_MAXIMA_PAGINA_SEM_CABECALHO = ALTURA_UTIL_PAGINA; // 26cm para páginas 2+

  // Função para adicionar nova página
  const adicionarNovaPagina = () => {
    // Limite de 6 páginas (não se aplica ao Simulado SAEB)
    if (tipoGeracao !== 'simulado' && paginas.length >= 6) {
      alert('⚠️ Limite de páginas atingido!\n\nO máximo permitido é 6 páginas por atividade/avaliação.\n\n💡 Dica: Gere o PDF atual e crie uma nova atividade para mais questões.');
      return null;
    }
    const novaId = paginas.length + 1;
    setPaginas([...paginas, { id: novaId, questoes: [] }]);
    setPaginaAtiva(novaId);
    return novaId;
  };

  // Função para adicionar questão considerando páginas
  const adicionarQuestao = (questaoId) => {
    const questao = questoes.find(q => q.id === questaoId);
    if (!questao) return;

    // Limpar PDF gerado anterior quando modifica questões
    setPdfGerado(null);

    // Verifica se já está selecionada
    const jaAdicionada = paginas.some(p => p.questoes.includes(questaoId));
    
    if (jaAdicionada) {
      // Remove de todas as páginas
      const novasPaginas = paginas.map(p => ({
        ...p,
        questoes: p.questoes.filter(id => id !== questaoId)
      }));
      
      // v567: Limpar páginas vazias (exceto a página 1)
      const paginasLimpas = novasPaginas.filter((p, index) => index === 0 || p.questoes.length > 0);
      
      // Se sobrou só página 1 vazia, garantir que está no formato correto
      if (paginasLimpas.length === 1 && paginasLimpas[0].questoes.length === 0) {
        setPaginas([{ id: 1, questoes: [] }]);
        setPaginaAtiva(1);
      } else {
        setPaginas(paginasLimpas);
        // Se a página ativa foi removida, voltar para a última existente
        if (!paginasLimpas.find(p => p.id === paginaAtiva)) {
          setPaginaAtiva(paginasLimpas[paginasLimpas.length - 1].id);
        }
      }
      
      // Se removeu todas as questões, limpa os bloqueios
      const totalRestante = novasPaginas.reduce((total, p) => total + p.questoes.length, 0);
      if (totalRestante === 0) {
        setComponenteBloqueado(null);
        setHabilidadeBloqueada(null);
      }
      return;
    }

    // 🆕 VALIDAÇÃO: Verifica habilidade para Atividades Prontas e Atividade por Questão
    // v566: Normalizar comparação (remover parênteses e espaços)
    const normalizarHabilidade = (hab) => (hab || '').replace(/[()]/g, '').trim().toUpperCase();
    const habQuestao = normalizarHabilidade(questao.habilidade);
    const habBloqueada = normalizarHabilidade(habilidadeBloqueada);
    
    console.log('🔍 Validação habilidade:', { habQuestao, habBloqueada, igual: habQuestao === habBloqueada });
    
    if ((tipoGeracao === 'pronta' || tipoGeracao === 'questao') && habilidadeBloqueada && habQuestao !== habBloqueada) {
      setShowModalHabilidadeDiferente(true);
      return;
    }

    // VALIDAÇÃO: Verifica se já existe componente bloqueado (para Avaliação)
    if (tipoGeracao === 'avaliacao' && componenteBloqueado && questao.componente !== componenteBloqueado) {
      setShowModalComponenteDiferente(true);
      return;
    }

    // 🆕 Se é a primeira questão em Atividades Prontas ou Atividade por Questão, bloqueia habilidade E componente
    if ((tipoGeracao === 'pronta' || tipoGeracao === 'questao') && totalQuestoesSelecionadas === 0) {
      setHabilidadeBloqueada(questao.habilidade);
      setComponenteBloqueado(questao.componente);
    }

    // Se é a primeira questão em Avaliação, bloqueia apenas o componente
    if (tipoGeracao === 'avaliacao' && totalQuestoesSelecionadas === 0) {
      setComponenteBloqueado(questao.componente);
    }

    // Calcula espaço da página atual
    const paginaAtual = paginas.find(p => p.id === paginaAtiva);
    if (!paginaAtual) return;

    // Calcula espaço usado pelas questões existentes
    // v475: O padding (1cm) está incluído em cada questão, mas a última não precisa
    let espacoQuestoes = 0;
    const numQuestoesAtuais = paginaAtual.questoes.length;
    
    paginaAtual.questoes.forEach((qId, idx) => {
      const q = questoes.find(questao => String(questao.id) === String(qId));
      if (q) {
        let tam = getTamanhoQuestao(q.tamanho);
        // Se é a última questão atual E vamos adicionar mais uma, o padding dela será usado
        // Se não vamos adicionar mais, a última não precisa de padding
        console.log(`   📦 Questão ${qId}: tamanho="${q.tamanho}" = ${tam}cm`);
        espacoQuestoes += tam;
      }
    });
    
    // Se já tem questões, a última não precisa de padding (já que estava como última)
    // Mas ao adicionar nova, ela passa a precisar do padding
    // Então não precisamos subtrair nada - o cálculo está correto
    
    const espacoNecessario = getTamanhoQuestao(questao.tamanho);
    // A nova questão será a última, então ela não precisa de padding
    const espacoNovaQuestaoSemPadding = espacoNecessario - 1.0; // Remove padding da nova (será a última)
    
    const espacoTotal = espacoQuestoes + espacoNovaQuestaoSemPadding;
    
    // Página 1 tem cabeçalho, então usa limite menor
    const limitePagina = paginaAtiva === 1 ? ALTURA_MAXIMA_PAGINA_COM_CABECALHO : ALTURA_MAXIMA_PAGINA_SEM_CABECALHO;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📄 Página ativa:', paginaAtiva);
    console.log('📦 Questão a adicionar:', questaoId, '- tamanho:', questao.tamanho);
    console.log('✅ Espaço questões existentes:', espacoQuestoes, 'cm');
    console.log('➕ Nova questão (sem padding):', espacoNovaQuestaoSemPadding, 'cm');
    console.log('📊 Total:', espacoTotal.toFixed(1), 'cm');
    console.log('📏 Limite:', limitePagina, 'cm', paginaAtiva === 1 ? '(com cabeçalho)' : '(sem cabeçalho)');
    console.log('🔍 Cabe?', espacoTotal <= limitePagina);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    if (espacoTotal <= limitePagina) {
      // Cabe na página atual
      console.log('✅ CABE! Adicionando...');
      setPaginas(paginas.map(p => 
        p.id === paginaAtiva 
          ? { ...p, questoes: [...p.questoes, questaoId] }
          : p
      ));
    } else {
      // NÃO CABE! Abre popup
      console.log('🚨 NÃO CABE! Abrindo popup...');
      setQuestaoParaAdicionar(questaoId);
      setShowModalLimitePagina(true);
    }
  };

  // Função para confirmar criação de nova página e adicionar questão
  const confirmarNovaPagina = () => {
    if (!questaoParaAdicionar) {
      setShowModalLimitePagina(false);
      return;
    }
    
    // Criar nova página E adicionar questão em uma única operação
    const novaId = paginas.length + 1;
    
    // Verificar limite de páginas
    if (tipoGeracao !== 'simulado' && paginas.length >= 6) {
      alert('⚠️ Limite de páginas atingido!\n\nO máximo permitido é 6 páginas por atividade/avaliação.');
      setShowModalLimitePagina(false);
      setQuestaoParaAdicionar(null);
      return;
    }
    
    // Criar nova página com a questão já incluída
    setPaginas(prev => [...prev, { id: novaId, questoes: [questaoParaAdicionar] }]);
    setPaginaAtiva(novaId);
    
    setShowModalLimitePagina(false);
    setQuestaoParaAdicionar(null);
  };

  // Calcular total de questões selecionadas
  const totalQuestoesSelecionadas = paginas.reduce((total, p) => total + p.questoes.length, 0);

  // Verificar se questão está selecionada em alguma página
  const isQuestaoSelecionada = (questaoId) => {
    return paginas.some(p => p.questoes.includes(questaoId));
  };

  // Obter número da questão globalmente
  const getNumeroQuestao = (questaoId) => {
    let numero = 0;
    for (const pagina of paginas) {
      for (const qId of pagina.questoes) {
        numero++;
        if (qId === questaoId) return numero;
      }
    }
    return 0;
  };

  // Funções para navegar no carrossel de questões
  const navegarQuestao = (tamanho, direcao) => {
    console.log('🎨 NAVEGANDO CARROSSEL:', tamanho, direcao);
    
    // Verificar se está no modo simulado
    const isSimulado = tipoGeracao === 'simulado';
    
    let questoesTamanho;
    
    if (isSimulado) {
      // Para Simulado SAEB, usa componenteSimulado e filtros específicos
      const componentesFiltro = componenteSimulado === 'Língua Portuguesa' 
        ? ['Língua Portuguesa', 'Língua Portuguesa SAEB']
        : ['Matemática', 'Matemática SAEB'];
      
      questoesTamanho = questoes.filter(q => q.tamanho === tamanho &&
        componentesFiltro.includes(q.componente) &&
        (!filtros.serie || q.serie === filtros.serie) &&
        (!filtros.habilidadeId || q.habilidadeId === filtros.habilidadeId));
    } else {
      // Para outros modos, usa filtros normais
      questoesTamanho = questoes.filter(q => q.tamanho === tamanho &&
        (!filtros.serie || q.serie === filtros.serie) && 
        (!filtros.componente || q.componente === filtros.componente) &&
        (!filtros.habilidadeId || q.habilidadeId === filtros.habilidadeId));
    }
    
    const totalQuestoes = questoesTamanho.length;
    console.log('📊 Total de questões deste tamanho:', totalQuestoes);
    
    if (totalQuestoes === 0) return;

    setIndiceQuestaoVisivel(prev => {
      const indiceAtual = prev[tamanho] || 0;
      let novoIndice;
      
      if (direcao === 'anterior') {
        novoIndice = indiceAtual > 0 ? indiceAtual - 1 : totalQuestoes - 1;
      } else {
        novoIndice = indiceAtual < totalQuestoes - 1 ? indiceAtual + 1 : 0;
      }
      
      console.log('➡️ Mudando de índice', indiceAtual, 'para', novoIndice);
      
      // v556: Preload das imagens adjacentes (anterior e próxima)
      const preloadIndices = [
        novoIndice > 0 ? novoIndice - 1 : totalQuestoes - 1,
        novoIndice < totalQuestoes - 1 ? novoIndice + 1 : 0
      ];
      
      // v558: Preload usando fetch em vez de new Image() para evitar erros
      preloadIndices.forEach(idx => {
        const q = questoesTamanho[idx];
        if (q?.imagem && q.imagem.startsWith('http')) {
          // Usa fetch para pré-carregar a imagem no cache do navegador
          fetch(q.imagem, { mode: 'no-cors' }).catch(() => {});
        }
      });
      
      return { ...prev, [tamanho]: novoIndice };
    });
  };

  // 🆕 FUNÇÃO NOVA: Determinar o componente curricular (agora sempre único)
  const getComponenteCurricular = () => {
    // Se já tem componente bloqueado, usa ele
    if (componenteBloqueado) {
      return componenteBloqueado;
    }

    // Se não há questões, usa o filtro ou default
    if (totalQuestoesSelecionadas === 0) {
      return filtros.componente || 'Selecione questões';
    }

    return 'Selecione questões';
  };

  // 🆕 v583: FUNÇÃO AUXILIAR - Buscar código atualizado da habilidade pelo ID
  // Isso garante que sempre usamos o código mais recente, mesmo após edições no Admin
  // v588: Função para buscar código atualizado da habilidade
  const getCodigoHabilidadeAtualizado = (questao) => {
    try {
      if (!questao) return '';
      
      // Verificar se habilidades está disponível
      if (!habilidades || habilidades.length === 0) {
        return questao.habilidade || '';
      }
      
      // 1. Primeiro tenta buscar pelo habilidadeId (mais confiável)
      if (questao.habilidadeId) {
        const habPorId = habilidades.find(h => h.id === questao.habilidadeId);
        if (habPorId) return habPorId.codigo;
      }
      
      // 2. Tenta buscar pelo código atual salvo na questão
      if (questao.habilidade) {
        const habPorCodigo = habilidades.find(h => h.codigo === questao.habilidade);
        if (habPorCodigo) return habPorCodigo.codigo;
        
        // Tenta sem parênteses
        const codigoLimpo = questao.habilidade.replace(/[()]/g, '').trim();
        const habPorCodigoLimpo = habilidades.find(h => 
          h.codigo === codigoLimpo || 
          h.codigo.replace(/[()]/g, '').trim() === codigoLimpo
        );
        if (habPorCodigoLimpo) return habPorCodigoLimpo.codigo;
      }
      
      // 3. Busca por série + componente (para questões antigas cujo código foi alterado)
      if (questao.serie && questao.componente && questao.habilidade) {
        const codigoAntigoLimpo = questao.habilidade.replace(/[()]/g, '').trim();
        
        // Busca habilidades que tenham o mesmo código BASE
        const habPorSerieComponente = habilidades.filter(h => 
          h.serie === questao.serie && 
          h.componente === questao.componente
        );
        
        // Tenta encontrar a habilidade que melhor corresponde ao código antigo
        for (const hab of habPorSerieComponente) {
          const codigoHabLimpo = hab.codigo.replace(/[()]/g, '').trim();
          
          // Verifica se é o mesmo código base (ex: EF01LP01 == EF01LP01-NOVO)
          if (codigoHabLimpo.startsWith(codigoAntigoLimpo) || 
              codigoAntigoLimpo.startsWith(codigoHabLimpo)) {
            return hab.codigo;
          }
        }
        
        // Se só tem uma habilidade para essa série/componente, retorna ela
        if (habPorSerieComponente.length === 1) {
          return habPorSerieComponente[0].codigo;
        }
      }
      
      // 4. Último fallback: retorna o código salvo na questão (mesmo que antigo)
      return questao.habilidade || '';
      
    } catch (error) {
      // Em caso de erro, retorna o código original da questão
      console.error('Erro em getCodigoHabilidadeAtualizado:', error);
      return questao?.habilidade || '';
    }
  };

  // 🆕 FUNÇÃO NOVA: Obter habilidades com conteúdos das questões selecionadas  
  // 🆕 v588: Função limpa com proteção contra erros
  const getHabilidadesConteudos = () => {
    try {
      if (totalQuestoesSelecionadas === 0) return '';
      
      // Verificar se habilidades está disponível
      if (!habilidades || habilidades.length === 0) return '';
      
      // Pega todas as questões selecionadas
      const questoesSelecionadas = [];
      paginas.forEach(pagina => {
        pagina.questoes.forEach(qId => {
          const questao = questoes.find(q => String(q.id) === String(qId));
          if (questao) questoesSelecionadas.push(questao);
        });
      });

      // Busca habilidades usando a mesma lógica inteligente
      const habilidadesEncontradas = new Map();
      
      questoesSelecionadas.forEach(q => {
        // Usa a função getCodigoHabilidadeAtualizado para pegar o código correto
        const codigoAtualizado = getCodigoHabilidadeAtualizado(q);
        
        if (codigoAtualizado) {
          // Busca a habilidade completa pelo código atualizado
          const codigoLimpo = codigoAtualizado.replace(/[()]/g, '').trim();
          const habInfo = habilidades.find(h => {
            const hCodigo = h.codigo.replace(/[()]/g, '').trim();
            return hCodigo === codigoLimpo;
          });
          
          if (habInfo && !habilidadesEncontradas.has(habInfo.id)) {
            habilidadesEncontradas.set(habInfo.id, habInfo);
          }
        }
      });
      
      // Mapeia para habilidade + conteúdo
      return Array.from(habilidadesEncontradas.values()).map(habilidadeInfo => {
        // Formato: (CÓDIGO) - Conteúdo
        const codigoFormatado = habilidadeInfo.codigo.startsWith('(') 
          ? habilidadeInfo.codigo 
          : `(${habilidadeInfo.codigo})`;
        return `${codigoFormatado} ${habilidadeInfo.conteudo}`;
      }).join(' • ');
      
    } catch (error) {
      console.error('Erro em getHabilidadesConteudos:', error);
      return '';
    }
  };

  // 🆕 v590: FUNÇÃO para formatar nome da turma (extrai letra do nome se campo turma não existir)
  const formatarNomeTurma = (turma) => {
    let turmaNome = turma.turma;
    
    // Se não tem campo turma, extrair do nome (ex: "5º Ano B" -> "B")
    if (!turmaNome && turma.nome) {
      const match = turma.nome.match(/\b([A-Z])\s*$/i); // Pega última letra maiúscula
      turmaNome = match ? match[1].toUpperCase() : '';
    }
    
    return `${turma.serie}${turmaNome ? ` ${turmaNome}` : ""} (${turma.alunos?.length || 0} alunos)`;
  };

  // 🆕 FUNÇÃO NOVA: Gerar o título do documento baseado no tipo e componente
  const getTituloDocumento = () => {
    const componente = getComponenteCurricular();
    
    if (tipoGeracao === 'pronta') {
      return componente === 'Selecione questões' ? 'Atividade' : `Atividade de ${componente}`;
    } else if (tipoGeracao === 'questao') {
      return componente === 'Selecione questões' ? 'Atividade' : `Atividade de ${componente}`;
    } else if (tipoGeracao === 'avaliacao') {
      return componente === 'Selecione questões' ? 'Avaliação' : `Avaliação de ${componente}`;
    } else if (tipoGeracao === 'simulado') {
      return 'Simulado SAEB';
    }
    
    return 'Documento';
  };

  // 🌐 LANDING PAGE - Efeito do carrossel automático
  useEffect(() => {
    if (currentView === 'landing') {
      const timer = setInterval(() => {
        setLandingCarrosselIndex((prev) => (prev + 1) % 4);
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [currentView]);

  // Função de scroll para seções da landing
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setLandingMenuMobileAberto(false);
  };

  // Dados da landing page
  const landingVantagens = [
    { icon: <Monitor className="w-8 h-8" />, titulo: 'Facilidade de uso', desc: 'Nossa ferramenta é intuitiva, completa e muito prática, mesmo para quem não tem experiência com tecnologia.' },
    { icon: <CheckCircle className="w-8 h-8" />, titulo: 'Resultados comprovados', desc: 'Usuários relatam aumento da eficiência em até 10X nas tarefas do dia a dia escolar.' },
    { icon: <Settings className="w-8 h-8" />, titulo: 'Personalização completa', desc: 'Escolha as questões de acordo com sua necessidade em apenas alguns cliques.' },
    { icon: <Star className="w-8 h-8" />, titulo: 'Questões originais', desc: 'Nossa plataforma oferece questões originais, criadas pela nossa equipe de professores e coordenadores pedagógicos.' },
    { icon: <Headphones className="w-8 h-8" />, titulo: 'Suporte especializado', desc: 'Nossa equipe está disponível para ajudar você sempre que precisar.' },
    { icon: <Award className="w-8 h-8" />, titulo: 'Garantia de satisfação', desc: 'Testado e aprovado por centenas de usuários, nossa solução foi criada para superar suas expectativas.' }
  ];

  const landingDisciplinas = [
    { icon: '📐', titulo: 'Matemática', desc: 'Simplifique o planejamento de exercícios e problemas matemáticos com questões prontas para engajar os seus alunos.' },
    { icon: '🔬', titulo: 'Ciências', desc: 'Economize tempo com questões que exploram os principais temas da natureza, prontas para aplicar em sala.' },
    { icon: '📚', titulo: 'Língua portuguesa', desc: 'Questões de leitura, interpretação, escrita e gramática já estruturadas para facilitar o aprendizado de seus alunos.' },
    { icon: '🌍', titulo: 'Geografia', desc: 'Explore os conteúdos do espaço geográfico com atividades rápidas e práticas, prontas para uso.' },
    { icon: '🏛️', titulo: 'História', desc: 'Leve conteúdos históricos relevantes para a sala de aula, com atividades que conectam passado e presente.' },
    { icon: '🎨', titulo: 'Artes', desc: 'Questões criativas e inspiradoras, prontas para incentivar a expressão artística dos seus alunos.' },
    { icon: '⚽', titulo: 'Educação física', desc: 'Planeje aulas práticas em poucos minutos com questões prontas para promover a saúde e o bem-estar.' },
    { icon: '🙏', titulo: 'Ensino religioso', desc: 'Trabalhe valores e reflexões de forma objetiva, com atividades que facilitam o ensino.' },
    { icon: '📊', titulo: 'Simulados SAEB', desc: 'Crie Simulados SAEB para os 2º, 5º e 9º anos do E.F., com correção automatizada dos cartões-resposta e geração automática de planilhas e gráficos de desempenho.' }
  ];

  const landingFaqs = [
    { pergunta: 'O que é a Facilitaprof?', resposta: 'A FacilitaProf é uma plataforma online que ajuda professores e coordenadores pedagógicos do 1º ao 5º ano a criar atividades e avaliações de alta qualidade, com ilustrações, alinhadas à BNCC, em poucos minutos.' },
    { pergunta: 'As questões da plataforma são cópias de questões já existentes na internet?', resposta: 'Não! Todas as questões são originais, criadas pela nossa equipe de professores e coordenadores pedagógicos especialmente para a plataforma.' },
    { pergunta: 'Preciso baixar ou instalar algo para usar a FacilitaProf?', resposta: 'Não! A FacilitaProf funciona 100% online. Basta acessar pelo navegador do seu computador, tablet ou celular.' },
    { pergunta: 'A plataforma é alinhada à BNCC?', resposta: 'Sim! Todas as questões e atividades são desenvolvidas seguindo as diretrizes da Base Nacional Comum Curricular (BNCC).' },
    { pergunta: 'Os créditos que eu comprar têm validade?', resposta: 'Não! Seus créditos não expiram. Você pode usar quando quiser, no seu próprio ritmo.' },
    { pergunta: 'Há suporte caso eu tenha dúvidas?', resposta: 'Sim! Nossa equipe de suporte está disponível para ajudar você pelo WhatsApp ou e-mail sempre que precisar.' }
  ];

  const landingImagensCarrossel = [
    { id: 1, titulo: 'Atividade de Matemática', tipo: 'Matemática - 2º Ano' },
    { id: 2, titulo: 'Avaliação de Português', tipo: 'Português - 3º Ano' },
    { id: 3, titulo: 'Simulado SAEB', tipo: 'SAEB - 5º Ano' },
    { id: 4, titulo: 'Atividade de Ciências', tipo: 'Ciências - 4º Ano' }
  ];

  // Usa recargas do estado (sincronizado com Admin)
  const landingRecargas = recargas.filter(r => r.ativo);

  // Função de login da landing - AGORA COM FIREBASE! 🔥
  const [loginLoading, setLoginLoading] = useState(false);
  
  const handleLandingLogin = async () => {
    const email = document.getElementById('landingLoginEmail')?.value;
    const senha = document.getElementById('landingLoginSenha')?.value;
    
    if (!email || !senha) {
      alert('⚠️ Preencha email e senha!');
      return;
    }
    
    setLoginLoading(true);
    
    try {
      const resultado = await loginComEmail(email, senha);
      
      if (resultado.success) {
        const user = resultado.user;
        
        // Limpar flag de logout ao fazer login
        localStorage.removeItem('facilitaprof_logout');
        
        // v516: Log para debug
        console.log('🔐 Login bem sucedido, dados do usuário:', user);
        
        // Configura o usuário com todas as propriedades necessárias
        // v516: Incluir escolaId e escolaNome para professores vinculados
        setCurrentUser({
          uid: user.uid,
          nome: user.nome,
          email: user.email,
          tipo: user.tipo,
          creditos: user.creditos || 0,
          escola: user.escola || '',
          // v524: Incluir nomeEscola para escolas
          nomeEscola: user.nomeEscola || user.escola || '',
          seriesLiberadas: user.seriesLiberadas || ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'],
          plano: user.plano || 'Gratuito',
          escolaId: user.escolaId || null,
          escolaNome: user.escolaNome || '',
          logoUrl: user.logoUrl || '' // v653
        });
        
        // Atualiza o cabecalho
        // 🔧 v518: Usar escolaNome para professor vinculado
        // 🔧 v524: Para escola, usar nomeEscola
        setCabecalhoConfig(prev => ({
          ...prev,
          professor: user.tipo === 'professor' ? user.nome : '',
          nomeEscola: user.tipo === 'escola' 
            ? (user.nomeEscola || user.escola || user.nome || '')
            : (user.escolaNome || user.escola || ''),
          logoUrl: user.logoUrl || '' // v653
        }));
        
        setLandingMostrarLogin(false);
        
        // Redireciona para a tela apropriada
        if (user.tipo === 'admin') {
          setCurrentView('admin-dashboard');
        } else if (user.tipo === 'escola') {
          setCurrentView('escola-estatisticas');
        } else {
          setCurrentView('professor-gerador');
        }
      } else {
        alert('❌ ' + resultado.error);
      }
    } catch (error) {
      console.error('Erro no login:', error);
      alert('❌ Erro ao fazer login. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  };
  
  // v542: Funções para verificação de telefone via WhatsApp (Z-API)
  
  // Formatar telefone para exibição
  const formatarTelefone = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 7) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    if (numeros.length <= 11) return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
  };
  
  // Enviar código de verificação via WhatsApp
  const enviarCodigoWhatsApp = async (telefone) => {
    if (!telefone || telefone.replace(/\D/g, '').length < 10) {
      alert('⚠️ Digite um número de telefone válido com DDD.');
      return false;
    }
    
    setEnviandoCodigo(true);
    
    try {
      // v545: Usar variável global firebaseFunctions
      if (!firebaseFunctions) {
        throw new Error('Firebase Functions não inicializado');
      }
      
      // Chamar Cloud Function
      const enviarCodigo = firebaseFunctions.httpsCallable('enviarCodigoWhatsApp');
      const result = await enviarCodigo({ telefone: telefone.replace(/\D/g, '') });
      
      if (result.data.success) {
        setEtapaVerificacao('codigo');
        
        // v689d: Salvar estado no sessionStorage (para não perder ao voltar do WhatsApp no mobile)
        try {
          const modalType = showModalCompletarCadastroGoogle ? 'google' : landingMostrarCadastro ? 'cadastro' : '';
          sessionStorage.setItem('fp_whatsapp_verif', JSON.stringify({
            etapa: 'codigo', telefone, modalType
          }));
        } catch (e) {}
        
        // Iniciar countdown de 60 segundos para reenvio
        setTempoReenvio(60);
        const interval = setInterval(() => {
          setTempoReenvio(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        alert('✅ Código enviado para seu WhatsApp!\n\nVerifique suas mensagens.');
        return true;
      } else {
        alert('❌ ' + (result.data.message || 'Erro ao enviar código.'));
        return false;
      }
    } catch (error) {
      console.error('Erro ao enviar código:', error);
      const mensagem = error.message || 'Erro ao enviar código. Tente novamente.';
      // Tratar erros específicos
      if (mensagem.includes('já está vinculado')) {
        alert('❌ Este telefone já está vinculado a outra conta.\n\nSe você já tem uma conta, faça login.');
      } else if (mensagem.includes('Limite de envios')) {
        alert('❌ Limite de envios atingido.\n\nTente novamente amanhã.');
      } else {
        alert('❌ ' + mensagem);
      }
      return false;
    } finally {
      setEnviandoCodigo(false);
    }
  };
  
  // Verificar código digitado
  const verificarCodigoWhatsApp = async (telefone, codigo) => {
    if (!codigo || codigo.length !== 6) {
      alert('⚠️ Digite o código de 6 dígitos.');
      return false;
    }
    
    setVerificandoCodigo(true);
    
    try {
      // v545: Usar variável global firebaseFunctions
      if (!firebaseFunctions) {
        throw new Error('Firebase Functions não inicializado');
      }
      
      // Chamar Cloud Function
      const verificarCodigo = firebaseFunctions.httpsCallable('verificarCodigoWhatsApp');
      const result = await verificarCodigo({ 
        telefone: telefone.replace(/\D/g, ''), 
        codigo 
      });
      
      if (result.data.success) {
        setEtapaVerificacao('verificado');
        setTelefoneVerificadoTemp(result.data.telefoneVerificado);
        // v689d: Limpar estado salvo
        try { sessionStorage.removeItem('fp_whatsapp_verif'); } catch (e) {}
        return true;
      } else {
        alert('❌ ' + (result.data.message || 'Código inválido.'));
        return false;
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error);
      const mensagem = error.message || 'Código inválido ou expirado.';
      alert('❌ ' + mensagem);
      
      // Se código expirou ou muitas tentativas, voltar para etapa de telefone
      if (mensagem.includes('expirado') || mensagem.includes('Solicite um novo')) {
        setEtapaVerificacao('telefone');
        setCodigoVerificacao('');
      }
      return false;
    } finally {
      setVerificandoCodigo(false);
    }
  };
  
  // Resetar verificação
  const resetarVerificacao = () => {
    setTelefoneVerificacao('');
    setCodigoVerificacao('');
    setEtapaVerificacao('telefone');
    setTelefoneVerificadoTemp(null);
    setTempoReenvio(0);
    // v689d: Limpar estado salvo
    try { sessionStorage.removeItem('fp_whatsapp_verif'); } catch (e) {}
  };
  
  // Login com Google 🔥
  // v540: Verificar se precisa completar cadastro antes de acessar
  const handleLoginGoogle = async () => {
    setLoginLoading(true);
    try {
      const resultado = await loginComGoogle();
      
      if (resultado.success) {
        const user = resultado.user;
        
        // v540: Se precisa completar cadastro, mostrar modal
        if (resultado.needsCompletion) {
          setUserGooglePendente(user);
          setDadosCadastroGoogle({
            nomeEscola: user.escola || '',
            nomeProfessor: user.nome || user.displayName || ''
          });
          setShowModalCompletarCadastroGoogle(true);
          setLandingMostrarLogin(false);
          setLoginLoading(false);
          return;
        }
        
        // Limpar flag de logout ao fazer login
        localStorage.removeItem('facilitaprof_logout');
        
        // Limpar estados do gerador antes do login
        setPdfGerado(null);
        setPaginas([{ id: 1, questoes: [] }]);
        setPaginaAtiva(1);
        setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
        setComponenteBloqueado(null);
        setHabilidadeBloqueada(null);
        setTipoGeracao('questao');
        setBlocosSimulado([
          { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
          { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
        ]);
        setBlocoAtivo(1);
        setTurmaSimuladoSelecionada(null); setTurmasSelecionadasSimulado([]);
        obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
        
        // v516: Incluir escolaId e escolaNome para professores vinculados
        setCurrentUser({
          uid: user.uid,
          nome: user.nome,
          email: user.email,
          tipo: user.tipo,
          creditos: user.creditos || 0,
          escola: user.escola || '',
          seriesLiberadas: user.seriesLiberadas || ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'],
          plano: user.plano || 'Gratuito',
          escolaId: user.escolaId || null,
          escolaNome: user.escolaNome || ''
        });
        
        if (resultado.isNewUser) {
          alert('🎉 Bem-vindo! Você ganhou 10 créditos grátis para começar!');
        }
        
        setLandingMostrarLogin(false);
        setCurrentView('professor-gerador');
      } else {
        alert('❌ ' + resultado.error);
      }
    } catch (error) {
      alert('❌ Erro ao fazer login com Google.');
    } finally {
      setLoginLoading(false);
    }
  };
  
  // v540/v542: Função para completar cadastro após login com Google
  const handleCompletarCadastroGoogle = async () => {
    if (!dadosCadastroGoogle.nomeEscola.trim() || !dadosCadastroGoogle.nomeProfessor.trim()) {
      alert('⚠️ Por favor, preencha o nome da escola e o nome do professor.');
      return;
    }
    
    // v542: Verificar se telefone foi verificado
    if (etapaVerificacao !== 'verificado' || !telefoneVerificadoTemp) {
      alert('⚠️ Por favor, verifique seu telefone primeiro.');
      return;
    }
    
    try {
      setLoginLoading(true);
      
      // Atualizar dados no Firebase - v542: incluir telefone verificado
      await db.collection('users').doc(userGooglePendente.uid).update({
        escola: dadosCadastroGoogle.nomeEscola.trim(),
        nome: dadosCadastroGoogle.nomeProfessor.trim(),
        telefone: dadosCadastroGoogle.telefone,
        telefoneVerificado: telefoneVerificadoTemp,
        telefoneVerificadoEm: window.firebase.firestore.FieldValue.serverTimestamp(),
        cadastroGoogleCompleto: true
      });
      
      // Limpar flag de logout ao fazer login
      localStorage.removeItem('facilitaprof_logout');
      
      // Limpar estados do gerador
      setPdfGerado(null);
      setPaginas([{ id: 1, questoes: [] }]);
      setPaginaAtiva(1);
      setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
      setComponenteBloqueado(null);
      setHabilidadeBloqueada(null);
      setTipoGeracao('questao');
      setBlocosSimulado([
        { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
        { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
      ]);
      setBlocoAtivo(1);
      setTurmaSimuladoSelecionada(null); 
      setTurmasSelecionadasSimulado([]);
      obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
      
      // Definir usuário atual
      setCurrentUser({
        uid: userGooglePendente.uid,
        nome: dadosCadastroGoogle.nomeProfessor.trim(),
        email: userGooglePendente.email,
        tipo: 'professor',
        creditos: userGooglePendente.creditos || 10,
        escola: dadosCadastroGoogle.nomeEscola.trim(),
        seriesLiberadas: userGooglePendente.seriesLiberadas || ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'],
        plano: userGooglePendente.plano || 'Gratuito',
        escolaId: null,
        escolaNome: '',
        telefoneVerificado: telefoneVerificadoTemp
      });
      
      // Atualizar cabeçalho - v541: usar campo 'professor' e não 'nomeProfessor'
      setCabecalhoConfig(prev => ({
        ...prev,
        nomeEscola: dadosCadastroGoogle.nomeEscola.trim(),
        professor: dadosCadastroGoogle.nomeProfessor.trim()
      }));
      
      // v541: Marcar que o cadastro Google foi recém completado para evitar sobrescrita pelo listener
      setCadastroGoogleRecemCompleto(true);
      setTimeout(() => setCadastroGoogleRecemCompleto(false), 3000); // Reset após 3 segundos
      
      // Fechar modal e ir para gerador
      setShowModalCompletarCadastroGoogle(false);
      setUserGooglePendente(null);
      resetarVerificacao(); // v542: Resetar estados de verificação
      setCurrentView('professor-gerador');
      
      alert('🎉 Cadastro completo! Bem-vindo ao FacilitaProf!');
      
    } catch (error) {
      console.error('Erro ao completar cadastro:', error);
      alert('❌ Erro ao salvar dados. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  };

  if (currentView === 'landing') {
    return (
      <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#0a1929', fontFamily: "'Segoe UI', sans-serif" }}>
        {/* Header/Navbar - Responsivo com menu mobile */}
        <header className="w-full z-50 sticky top-0" style={{ backgroundColor: '#0a1929', borderBottom: '1px solid #1e3a5f' }}>
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-2 flex items-center justify-between">
            {/* Logo - Esquerda */}
            <div className="flex items-center flex-shrink-0">
              {(landingImagesFirebase.logomarca || landingAssets.logomarcaPreview) ? (
                <img 
                  src={landingImagesFirebase.logomarca || landingAssets.logomarcaPreview} 
                  alt="Logo FacilitaProf" 
                  className="h-8 sm:h-10 md:h-12 w-auto object-contain"
                />
              ) : (
                <svg viewBox="0 0 200 80" className="h-8 sm:h-10 md:h-12 w-auto">
                  <ellipse cx="35" cy="45" rx="22" ry="28" fill="#f7941d" opacity="0.3"/>
                  <ellipse cx="35" cy="45" rx="18" ry="24" fill="#f7941d" opacity="0.5"/>
                  <ellipse cx="35" cy="45" rx="14" ry="20" fill="#f7941d" opacity="0.7"/>
                  <polygon points="20,25 35,15 50,25 35,30" fill="white"/>
                  <rect x="32" y="12" width="6" height="4" fill="white"/>
                  <line x1="50" y1="25" x2="55" y2="35" stroke="white" strokeWidth="2"/>
                  <circle cx="55" cy="37" r="3" fill="white"/>
                  <text x="60" y="32" fill="white" fontSize="18" fontWeight="bold" fontStyle="italic" fontFamily="Arial, sans-serif">Facilita</text>
                  <text x="60" y="55" fill="#f7941d" fontSize="24" fontWeight="bold" fontFamily="Arial, sans-serif">PROF</text>
                  <text x="60" y="68" fill="#8899aa" fontSize="6" fontFamily="Arial, sans-serif">FEITO POR PROFESSORES, PARA PROFESSORES!</text>
                </svg>
              )}
            </div>

            {/* Menu Desktop - Oculto no mobile */}
            <nav className="hidden md:flex items-center justify-center flex-1 gap-4 lg:gap-8 mx-4 lg:mx-8">
              <button onClick={() => scrollToSection('vantagens')} className="text-white hover:text-orange-400 transition font-semibold text-sm lg:text-base whitespace-nowrap">Vantagens</button>
              <button onClick={() => scrollToSection('disciplinas')} className="text-white hover:text-orange-400 transition font-semibold text-sm lg:text-base whitespace-nowrap">Disciplinas</button>
              <button onClick={() => scrollToSection('recargas')} className="text-white hover:text-orange-400 transition font-semibold text-sm lg:text-base whitespace-nowrap">Recargas</button>
              <button onClick={() => scrollToSection('duvidas')} className="text-white hover:text-orange-400 transition font-semibold text-sm lg:text-base whitespace-nowrap">Dúvidas</button>
            </nav>

            {/* Botões + Menu Mobile - Visíveis sempre */}
            <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-shrink-0">
              {/* Botões Cadastre-se e Entrar - Sempre visíveis */}
              <button 
                onClick={() => setLandingMostrarCadastro(true)}
                className="px-2 sm:px-3 lg:px-6 py-1 sm:py-1.5 lg:py-2 rounded-lg font-semibold text-white transition hover:opacity-90 text-[10px] sm:text-xs lg:text-base"
                style={{ backgroundColor: '#f7941d' }}
              >
                Cadastre-se
              </button>
              <button 
                onClick={() => setLandingMostrarLogin(true)}
                className="px-2 sm:px-3 lg:px-6 py-1 sm:py-1.5 lg:py-2 rounded-lg font-semibold border sm:border-2 border-white text-white hover:bg-white hover:text-gray-900 transition text-[10px] sm:text-xs lg:text-base"
              >
                Entrar
              </button>
              
              {/* Botão Menu Hamburguer - Visível apenas no mobile */}
              <button 
                onClick={() => setLandingMenuMobileAberto(!landingMenuMobileAberto)}
                className="md:hidden p-1.5 sm:p-2 text-white hover:text-orange-400 transition"
              >
                {landingMenuMobileAberto ? (
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Menu Mobile - Dropdown (apenas navegação) */}
          {landingMenuMobileAberto && (
            <div className="md:hidden border-t border-gray-700" style={{ backgroundColor: '#0a1929' }}>
              <nav className="flex flex-col px-4 py-3 space-y-2">
                <button onClick={() => scrollToSection('vantagens')} className="text-white hover:text-orange-400 transition font-semibold text-base py-2 text-left">Vantagens</button>
                <button onClick={() => scrollToSection('disciplinas')} className="text-white hover:text-orange-400 transition font-semibold text-base py-2 text-left">Disciplinas</button>
                <button onClick={() => scrollToSection('recargas')} className="text-white hover:text-orange-400 transition font-semibold text-base py-2 text-left">Recargas</button>
                <button onClick={() => scrollToSection('duvidas')} className="text-white hover:text-orange-400 transition font-semibold text-base py-2 text-left">Dúvidas</button>
              </nav>
            </div>
          )}
        </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-3 md:pt-6">
          
          {/* Badge CTA centralizado */}
          <div className="flex justify-center mb-4 sm:mb-6 md:mb-10">
            <div className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 rounded-xl text-white text-[11px] sm:text-xs md:text-base font-bold shadow-lg text-center leading-tight" style={{ backgroundColor: '#22c55e', maxWidth: '600px' }}>
              CADASTRE-SE E GANHE CRÉDITOS PARA CRIAR SUAS PRIMEIRAS ATIVIDADES, AVALIAÇÕES E SIMULADOS SAEB GRATUITAMENTE!
            </div>
          </div>

          {/* Conteúdo principal - Layout especial para mobile */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-8">
            {/* No mobile: Título + primeiro parágrafo + imagem lado a lado */}
            <div className="flex flex-row items-start justify-between gap-3 md:gap-0 md:flex-1">
              {/* Texto - sempre alinhado à esquerda */}
              <div className="flex-1 text-left pt-2 md:pt-0">
                <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 md:mb-5 leading-tight">
                  Simplifique sua rotina escolar!!
                </h1>

                {/* Descrição sobre Atividades */}
                <p className="text-gray-200 text-sm sm:text-base md:text-xl mb-3 sm:mb-4 md:mb-5 leading-relaxed">
                  <span style={{ color: '#f7941d' }} className="font-semibold">Crie ATIVIDADES ou AVALIAÇÕES</span> do 1º ao 5º Ano do E.F, alinhadas à BNCC, escolhendo as questões desejadas ou Atividades já prontas.
                </p>

                {/* Descrição sobre Simulados SAEB - Oculto no mobile, visível md+ */}
                <p className="hidden md:block text-gray-200 text-sm sm:text-base md:text-xl mb-3 sm:mb-4 md:mb-5 leading-relaxed">
                  Crie rapidamente <span style={{ color: '#f7941d' }} className="font-semibold">SIMULADOS SAEB</span> para os 2º, 5º e 9º anos do E.F, de acordo com as Matrizes de Referência SAEB, faça a <span style={{ color: '#f7941d' }} className="font-semibold">leitura automatizada dos cartões-respostas</span> dos alunos e tenha <span style={{ color: '#f7941d' }} className="font-semibold">planilhas e gráficos detalhados do desempenho</span> da turma.
                </p>
              </div>

              {/* Imagem da professora - Altura reduzida apenas no desktop */}
              <div className="flex flex-shrink-0 w-32 h-40 sm:w-40 sm:h-48 md:w-48 md:h-52 lg:w-56 lg:h-60 items-center justify-center">
                {(landingImagesFirebase.professoraImg || landingAssets.professoraImgPreview) && (
                  <img 
                    src={landingImagesFirebase.professoraImg || landingAssets.professoraImgPreview} 
                    alt="Professora FacilitaProf" 
                    className="max-w-full max-h-full object-contain"
                    style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' }}
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Descrição sobre Simulados SAEB - Apenas mobile, largura total abaixo da imagem */}
          <p className="md:hidden text-gray-200 text-sm leading-relaxed mt-3">
            Crie rapidamente <span style={{ color: '#f7941d' }} className="font-semibold">SIMULADOS SAEB</span> para os 2º, 5º e 9º anos do E.F, de acordo com as Matrizes de Referência SAEB, faça a <span style={{ color: '#f7941d' }} className="font-semibold">leitura automatizada dos cartões-respostas</span> dos alunos e tenha <span style={{ color: '#f7941d' }} className="font-semibold">planilhas e gráficos detalhados do desempenho</span> da turma.
          </p>
        </div>

        {/* Card "O que deseja fazer hoje?" - Mais próximo da hero no desktop */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-4 md:-mt-2 lg:-mt-3">
          <div className="rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-xl" style={{ backgroundColor: '#1e3a5f', border: '2px solid #f7941d' }}>
            <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-3 sm:mb-4 md:mb-5 text-center">O que deseja fazer hoje?</h3>
            {/* Grid responsivo: 2 colunas no mobile, 4 no desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              <button 
                onClick={() => setLandingMostrarLogin(true)}
                className="px-2 sm:px-3 py-2.5 sm:py-3 bg-white border-2 border-transparent rounded-lg sm:rounded-xl text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-500 transition shadow-md text-[11px] sm:text-sm md:text-sm leading-tight"
              >
                Atividade Pronta
              </button>
              <button 
                onClick={() => setLandingMostrarLogin(true)}
                className="px-2 sm:px-3 py-2.5 sm:py-3 bg-white border-2 border-transparent rounded-lg sm:rounded-xl text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-500 transition shadow-md text-[11px] sm:text-sm md:text-sm leading-tight"
              >
                Atividade (questões)
              </button>
              <button 
                onClick={() => setLandingMostrarLogin(true)}
                className="px-2 sm:px-3 py-2.5 sm:py-3 bg-white border-2 border-transparent rounded-lg sm:rounded-xl text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-500 transition shadow-md text-[11px] sm:text-sm md:text-sm leading-tight"
              >
                Avaliação
              </button>
              <button 
                onClick={() => setLandingMostrarLogin(true)}
                className="px-2 sm:px-3 py-2.5 sm:py-3 bg-white border-2 border-transparent rounded-lg sm:rounded-xl text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-500 transition shadow-md text-[11px] sm:text-sm md:text-sm leading-tight"
              >
                Simulado SAEB
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Seção Preview do PDF com Carrossel */}
      <section className="py-8 sm:py-12 px-3 sm:px-4" style={{ backgroundColor: '#0a1929' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6 md:mb-8" style={{ color: '#f7941d' }}>Veja como ficarão as suas Atividades, Avaliações e Simulados SAEB</h2>
          
          {/* Layout responsivo: stack no mobile, lado a lado no desktop */}
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6 lg:gap-12">
            {/* Carrossel de exemplos */}
            <div className="w-full max-w-xs sm:max-w-sm lg:w-80 flex-shrink-0 mx-auto lg:mx-0">
              <div className="relative bg-white rounded-lg shadow-2xl overflow-hidden">
                {/* Slides do carrossel - altura responsiva (maior no mobile) */}
                <div className="relative h-96 sm:h-[420px] lg:h-[450px]">
                  {landingImagensCarrossel.map((img, index) => (
                    <div 
                      key={img.id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: landingCarrosselIndex === index ? 1 : 0,
                        transition: 'opacity 0.5s ease-in-out'
                      }}
                    >
                      <div className="p-3 h-full">
                        {/* Se tem imagem do Firebase para este índice, mostra a imagem */}
                        {(landingImagesFirebase[`atividadeExemplo${index + 1}`] || landingAssets[`atividadeExemplo${index + 1}Preview`]) ? (
                          <img 
                            src={landingImagesFirebase[`atividadeExemplo${index + 1}`] || landingAssets[`atividadeExemplo${index + 1}Preview`]} 
                            alt={img.titulo}
                            className="w-full h-full object-contain rounded"
                          />
                        ) : (
                          <div className="border-2 border-gray-200 rounded p-3 h-full flex flex-col">
                            <div className="text-center text-xs text-gray-600 mb-2">
                              <p className="font-bold">Escola: Escola Municipal João XXIII</p>
                              <p>Professor(a): Maria Silva | {img.tipo}</p>
                            </div>
                            <div className="border-t pt-2 flex-1">
                              <p className="text-center font-bold text-sm mb-3" style={{ color: '#1e3a5f' }}>{img.titulo.toUpperCase()}</p>
                              <div className="text-xs text-gray-700">
                                <p className="font-semibold mb-1">QUESTÃO 1</p>
                                <p className="mb-2">Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod.</p>
                                <div className="bg-gray-100 h-20 rounded flex items-center justify-center mb-3">
                                  <span className="text-gray-400 text-xs">📊 Ilustração da questão</span>
                                </div>
                                <p className="font-semibold mb-1">QUESTÃO 2</p>
                                <p className="mb-2">Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
                                <div className="bg-gray-100 h-16 rounded flex items-center justify-center mb-3">
                                  <span className="text-gray-400 text-xs">📐 Ilustração</span>
                                </div>
                                <p className="font-semibold mb-1">QUESTÃO 3</p>
                                <p>Ut enim ad minim veniam, quis nostrud exercitation.</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Apenas indicadores (bolinhas) */}
                <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px' }}>
                  {landingImagensCarrossel.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setLandingCarrosselIndex(index)}
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: landingCarrosselIndex === index ? '#f7941d' : '#ccc',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Texto explicativo - à direita do carrossel no desktop, abaixo no mobile */}
            <div className="flex-1 pt-0 lg:pt-2 text-left">
              {/* Descrição movida da Hero */}
              <p className="text-gray-200 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed mb-6 lg:mb-10">
                A Facilitaprof é uma <span style={{ color: '#f7941d' }} className="font-semibold">Plataforma Digital</span> na qual você tem acesso a <span style={{ color: '#f7941d' }} className="font-semibold">mais de 12 mil questões originais</span>, com ilustrações e ferramentas práticas que otimizam o seu trabalho. Economize horas preciosas no planejamento e entregue materiais de alta qualidade <span style={{ color: '#f7941d' }} className="font-semibold">com apenas alguns cliques</span>.
              </p>
              
              <div className="flex flex-row items-start gap-3 sm:gap-4">
                <div className="text-3xl sm:text-4xl lg:text-5xl">📄</div>
                <div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 lg:mb-3" style={{ color: '#f7941d' }}>PDFs em Alta Qualidade</h3>
                  <p className="text-gray-300 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed mb-4 lg:mb-5">
                    Em pouco tempo, depois de escolher as questões ou atividades que você deseja abordar na aula, você terá um PDF em alta qualidade para salvar ou imprimir e assim facilitar e otimizar o seu tempo de forma prática.
                  </p>
                  <button 
                    onClick={() => setLandingMostrarLogin(true)}
                    className="px-5 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-lg font-semibold text-white transition hover:opacity-90 text-xs sm:text-sm lg:text-base"
                    style={{ backgroundColor: '#22c55e' }}
                  >
                    Começar agora
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção Vantagens - cards lado a lado */}
      <section id="vantagens" className="py-8 sm:py-10 md:py-16 px-3 sm:px-4" style={{ backgroundColor: '#0a1929' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-1" style={{ color: '#f7941d' }}>Vantagens</h2>
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 sm:mb-6 md:mb-8">Por que escolher a Facilitaprof?</h3>
          
          {/* Grid responsivo: 1 coluna mobile, 2 tablet, 3 desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
            {landingVantagens.map((v, i) => (
              <div key={i} className="rounded-xl p-3 sm:p-4 md:p-6 border-l-4 transition hover:scale-105" style={{ backgroundColor: '#1e3a5f', borderColor: '#f7941d' }}>
                <div className="mb-2 sm:mb-3 md:mb-4" style={{ color: '#f7941d' }}>{v.icon}</div>
                <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-1 sm:mb-2">{v.titulo}</h3>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seção Disciplinas */}
      <section id="disciplinas" className="py-8 sm:py-10 md:py-16 px-3 sm:px-4" style={{ backgroundColor: '#0a1929' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-1" style={{ color: '#f7941d' }}>Disciplinas</h2>
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 sm:mb-6 md:mb-8">Todos os Componentes Curriculares da BNCC + Simulados SAEB</h3>
          
          {/* Grid responsivo: 1 coluna mobile, 2 tablet, 3 desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
            {landingDisciplinas.map((d, i) => (
              <div key={i} className="rounded-xl p-3 sm:p-4 md:p-6 transition hover:scale-105" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3 md:mb-4">{d.icon}</div>
                <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-1 sm:mb-2">{d.titulo}</h3>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seção Recargas - Sincronizada com Admin */}
      <section id="recargas" className="py-8 sm:py-10 md:py-16 px-3 sm:px-4" style={{ backgroundColor: '#0a1929' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-1" style={{ color: '#f7941d' }}>Recargas</h2>
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 sm:mb-6 md:mb-8">Comprar créditos</h3>
          
          {/* Grid responsivo: 1 coluna mobile, 2 tablet, até 4 desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
            {landingRecargas.length === 0 ? (
              // v673: Mostrar loading se não há recargas carregadas
              <div className="col-span-full text-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Carregando planos...</p>
              </div>
            ) : (
              landingRecargas.map((r, i) => (
              <div 
                key={r.id || i} 
                className="rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 transition hover:scale-105 border-2 border-gray-600"
                style={{ backgroundColor: '#1e3a5f' }}
              >
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-white text-center mb-1 sm:mb-2">{r.nome}</h3>
                <div className="text-center mb-2 sm:mb-3 md:mb-4">
                  <span className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: '#f7941d' }}>R$ {typeof r.preco === 'number' ? r.preco.toFixed(2).replace('.', ',') : r.preco}</span>
                </div>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg mb-2 sm:mb-3 md:mb-4">{r.descricao || r.desc}</p>
                <div className="space-y-1 sm:space-y-2 text-gray-300 text-sm sm:text-base md:text-lg">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 text-green-400 flex-shrink-0" />
                    <span>{r.paginas} Créditos <span className="text-xs text-gray-400">(válidos por 1 ano)</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 text-green-400 flex-shrink-0" />
                    <span>{r.series === 1 ? '1 série' : r.series >= 5 ? 'Todas as séries' : `${r.series} séries`}</span>
                  </div>
                  {r.vantagens && r.vantagens.map((v, vi) => (
                    <div key={vi} className="flex items-center gap-2">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 text-green-400 flex-shrink-0" />
                      <span>{v}</span>
                    </div>
                  ))}
                </div>
                <button 
                  onClick={() => setLandingMostrarLogin(true)}
                  className="w-full mt-3 sm:mt-4 md:mt-6 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold text-white transition hover:opacity-90 text-sm sm:text-base md:text-lg bg-green-500 hover:bg-green-600"
                >
                  Escolher plano
                </button>
              </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Seção Dúvidas/FAQ - alinhada à esquerda como as outras */}
      <section id="duvidas" className="py-8 sm:py-10 md:py-16 px-3 sm:px-4" style={{ backgroundColor: '#0a1929' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-1" style={{ color: '#f7941d' }}>Dúvidas</h2>
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 sm:mb-6 md:mb-8">Perguntas frequentes</h3>
          
          <div className="space-y-2 sm:space-y-3">
            {landingFaqs.map((faq, i) => (
              <div key={i} className="rounded-lg sm:rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
                <button 
                  onClick={() => setLandingFaqAberto(landingFaqAberto === i ? null : i)}
                  className="w-full flex items-center justify-between p-3 sm:p-4 text-left text-white font-medium hover:bg-opacity-80 transition text-xs sm:text-sm md:text-base"
                >
                  <span className="pr-2 leading-tight">{faq.pergunta}</span>
                  {landingFaqAberto === i ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />}
                </button>
                {landingFaqAberto === i && (
                  <div className="px-3 sm:px-4 pb-3 sm:pb-4 text-gray-300 text-xs sm:text-sm md:text-base leading-relaxed">
                    {faq.resposta}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Botão CTA */}
          <div className="text-center mt-5 sm:mt-6 md:mt-8">
            <a 
              href={`https://wa.me/${telefoneSuporte}`} 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold text-white transition hover:opacity-90 text-xs sm:text-sm md:text-base"
              style={{ backgroundColor: '#22c55e' }}
            >
              Ainda tem dúvida? Fale com a gente!
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 md:py-12 px-4 border-t" style={{ backgroundColor: '#0a1929', borderColor: '#1e3a5f' }}>
        <div className="max-w-7xl mx-auto">
          {/* Layout responsivo: stack no mobile, lado a lado no desktop */}
          <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-8 md:gap-10">
            {/* Logo - usa imagem do Firebase ou SVG padrão */}
            <div className="text-center md:text-left">
              {(landingImagesFirebase.logomarca || landingAssets.logomarcaPreview) ? (
                <img 
                  src={landingImagesFirebase.logomarca || landingAssets.logomarcaPreview} 
                  alt="Logo FacilitaProf" 
                  className="h-12 md:h-16 w-auto object-contain mb-4 mx-auto md:mx-0"
                />
              ) : (
                <svg viewBox="0 0 200 80" className="h-12 md:h-16 w-auto mb-4 mx-auto md:mx-0">
                  <ellipse cx="35" cy="45" rx="22" ry="28" fill="#f7941d" opacity="0.3"/>
                  <ellipse cx="35" cy="45" rx="18" ry="24" fill="#f7941d" opacity="0.5"/>
                  <ellipse cx="35" cy="45" rx="14" ry="20" fill="#f7941d" opacity="0.7"/>
                  <polygon points="20,25 35,15 50,25 35,30" fill="white"/>
                  <rect x="32" y="12" width="6" height="4" fill="white"/>
                  <line x1="50" y1="25" x2="55" y2="35" stroke="white" strokeWidth="2"/>
                  <circle cx="55" cy="37" r="3" fill="white"/>
                  <text x="60" y="32" fill="white" fontSize="18" fontWeight="bold" fontStyle="italic" fontFamily="Arial, sans-serif">Facilita</text>
                  <text x="60" y="55" fill="#f7941d" fontSize="24" fontWeight="bold" fontFamily="Arial, sans-serif">PROF</text>
                  <text x="60" y="68" fill="#8899aa" fontSize="6" fontFamily="Arial, sans-serif">FEITO POR PROFESSORES, PARA PROFESSORES!</text>
                </svg>
              )}
            </div>

            {/* Contato e Sobre - stack no mobile, lado a lado no desktop */}
            <div className="flex flex-col sm:flex-row gap-8 sm:gap-12 md:gap-16 text-center sm:text-left">
              {/* Contato */}
              <div>
                <h4 className="text-white font-bold mb-3 md:mb-4">Fale conosco</h4>
                <div className="space-y-2 text-gray-300 text-sm md:text-base">
                  <a 
                    href={`https://wa.me/${telefoneSuporte}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center sm:justify-start gap-2 hover:text-green-400 transition"
                  >
                    <MessageCircle className="w-4 h-4" style={{ color: '#25d366' }} />
                    <span><strong>WhatsApp:</strong> {telefoneSuporte.replace(/^55(\d{2})(\d{5})(\d{4})$/, '$1 $2-$3')}</span>
                  </a>
                  <p className="flex items-center justify-center sm:justify-start gap-2">
                    <Mail className="w-4 h-4" style={{ color: '#f7941d' }} />
                    <span><strong>E-mail:</strong> contato@facilitaprof.com.br</span>
                  </p>
                </div>
              </div>

              {/* Sobre */}
              <div>
                <h4 className="text-white font-bold mb-3 md:mb-4">Sobre nós</h4>
                <div className="space-y-1 text-gray-400 text-xs md:text-sm">
                  <p>CNPJ: 57.992.957/0001-89</p>
                  <p>S. R. SALES PUBLICIDADE LTDA.</p>
                  <p>Rua Manoel Zacarias, 227, Poço Verde, SE.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Copyright centralizado */}
          <div className="text-center mt-6 md:mt-8 pt-4 md:pt-6 border-t" style={{ borderColor: '#1e3a5f' }}>
            <p className="text-gray-400 text-xs md:text-sm">© 2025 Facilitaprof, todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* v540/v542: Modal de Completar Cadastro após Login com Google - Com verificação de telefone */}
      {showModalCompletarCadastroGoogle && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-start md:items-center justify-center z-50 p-3 md:p-4 overflow-y-auto pt-8 md:pt-4">
          <div className="rounded-2xl shadow-2xl p-4 md:p-6 w-full max-w-md relative my-2 md:my-4" style={{ backgroundColor: '#1e3a5f' }}>
            {/* Header */}
            <div className="text-center mb-4 md:mb-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                {etapaVerificacao === 'verificado' ? (
                  <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-white" />
                ) : (
                  <User className="w-6 h-6 md:w-8 md:h-8 text-white" />
                )}
              </div>
              <h2 className="text-lg md:text-xl font-bold text-white">Complete seu Cadastro</h2>
              <p className="text-gray-300 text-xs md:text-sm mt-1 md:mt-2">
                {etapaVerificacao === 'verificado' 
                  ? 'Telefone verificado! Agora preencha os dados do cabeçalho.'
                  : 'Para evitar abusos, precisamos verificar seu telefone via WhatsApp.'}
              </p>
            </div>

            {/* Etapa 1: Verificação de Telefone */}
            {etapaVerificacao !== 'verificado' && (
              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                {/* Campo Telefone */}
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Telefone com WhatsApp *
                  </label>
                  <div className="flex flex-col md:flex-row gap-2">
                    <input 
                      type="tel" 
                      placeholder="(79) 99999-9999"
                      className="flex-1 p-2.5 md:p-3 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white min-w-0" 
                      value={dadosCadastroGoogle.telefone}
                      onChange={(e) => setDadosCadastroGoogle({...dadosCadastroGoogle, telefone: formatarTelefone(e.target.value)})}
                      disabled={etapaVerificacao === 'codigo'}
                      maxLength={15}
                    />
                    {etapaVerificacao === 'telefone' && (
                      <button
                        onClick={() => enviarCodigoWhatsApp(dadosCadastroGoogle.telefone)}
                        disabled={enviandoCodigo || (dadosCadastroGoogle.telefone || '').replace(/\D/g, '').length < 10}
                        className="w-full md:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-semibold"
                      >
                        {enviandoCodigo ? 'Enviando...' : '📩 Enviar Código'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Campo Código (aparece após enviar) */}
                {etapaVerificacao === 'codigo' && (
                  <>
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1">
                        🔐 Código de Verificação
                      </label>
                      <div className="flex flex-col md:flex-row gap-2">
                        <input 
                          type="text"
                          inputMode="numeric"
                          autoFocus
                          placeholder="000000"
                          className="flex-1 p-2.5 md:p-3 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white text-center font-mono text-lg tracking-widest min-w-0" 
                          value={codigoVerificacao}
                          onChange={(e) => setCodigoVerificacao(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                        />
                        <button
                          onClick={() => verificarCodigoWhatsApp(dadosCadastroGoogle.telefone, codigoVerificacao)}
                          disabled={verificandoCodigo || codigoVerificacao.length !== 6}
                          className="w-full md:w-auto px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-semibold"
                        >
                          {verificandoCodigo ? 'Verificando...' : '🔐 Verificar Código'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Reenviar código */}
                    <div className="text-center">
                      {tempoReenvio > 0 ? (
                        <span className="text-gray-400 text-xs">
                          Reenviar código em {tempoReenvio}s
                        </span>
                      ) : (
                        <button
                          onClick={() => enviarCodigoWhatsApp(dadosCadastroGoogle.telefone)}
                          disabled={enviandoCodigo}
                          className="text-orange-400 text-xs hover:underline"
                        >
                          Reenviar código
                        </button>
                      )}
                      <span className="mx-2 text-gray-500">|</span>
                      <button
                        onClick={() => {
                          setEtapaVerificacao('telefone');
                          setCodigoVerificacao('');
                        }}
                        className="text-gray-400 text-xs hover:underline"
                      >
                        Alterar número
                      </button>
                    </div>
                  </>
                )}

                {/* Info WhatsApp */}
                <div className="bg-green-900 bg-opacity-30 p-3 rounded-lg text-xs text-green-300 border border-green-600">
                  <p>📱 Você receberá um código de 6 dígitos no WhatsApp do número informado.</p>
                </div>
              </div>
            )}

            {/* Etapa 2: Dados do Cabeçalho (após verificação) */}
            {etapaVerificacao === 'verificado' && (
              <div className="space-y-4 mb-6">
                <div className="bg-green-900 bg-opacity-30 p-3 rounded-lg text-xs text-green-300 border border-green-600 flex items-center gap-2 mb-4">
                  <CheckCircle className="w-4 h-4" />
                  <span>Telefone {formatarTelefone(dadosCadastroGoogle.telefone)} verificado!</span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Nome da Escola *
                  </label>
                  <input 
                    type="text" 
                    placeholder="Ex: Escola Municipal João Paulo II"
                    className="w-full p-3 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                    value={dadosCadastroGoogle.nomeEscola}
                    onChange={(e) => setDadosCadastroGoogle({...dadosCadastroGoogle, nomeEscola: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Nome do Professor *
                  </label>
                  <input 
                    type="text" 
                    placeholder="Ex: Prof. Maria Silva"
                    className="w-full p-3 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                    value={dadosCadastroGoogle.nomeProfessor}
                    onChange={(e) => setDadosCadastroGoogle({...dadosCadastroGoogle, nomeProfessor: e.target.value})}
                  />
                </div>

                {/* Info */}
                <div className="bg-blue-900 bg-opacity-50 p-3 rounded-lg text-xs text-gray-300 border border-blue-400">
                  <p>📝 Essas informações aparecerão no cabeçalho das atividades e simulados que você gerar.</p>
                </div>
              </div>
            )}

            {/* Botão Principal */}
            {etapaVerificacao === 'verificado' && (
              <button 
                onClick={handleCompletarCadastroGoogle}
                disabled={loginLoading || !dadosCadastroGoogle.nomeEscola.trim() || !dadosCadastroGoogle.nomeProfessor.trim()}
                className="w-full py-3 rounded-lg font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" 
                style={{ backgroundColor: '#f7941d' }}
              >
                {loginLoading ? 'Salvando...' : 'Continuar para o FacilitaProf'}
              </button>
            )}

            {/* Cancelar */}
            <button 
              onClick={async () => {
                // Fazer logout se cancelar
                if (auth.currentUser) {
                  await auth.signOut();
                }
                setShowModalCompletarCadastroGoogle(false);
                setUserGooglePendente(null);
                resetarVerificacao();
              }}
              className="w-full mt-3 py-2 text-gray-400 hover:text-white text-sm transition"
            >
              Cancelar e sair
            </button>
          </div>
        </div>
      )}

      {/* Modal de Login - Vertical */}
      {landingMostrarLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-start justify-center z-50 p-4 pt-16">
          <div className="rounded-2xl shadow-2xl p-6 w-full max-w-sm relative" style={{ backgroundColor: '#1e3a5f' }}>
            <button onClick={() => { setLandingMostrarLogin(false); setModoRecuperarSenha(false); }} className="absolute top-3 right-3 text-gray-300 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            
            {/* 🆕 v400: Header com imagem do logo */}
            <div className="text-center mb-5">
              <img 
                src={landingImagesFirebase.logomarcaEscuro || '/assets/logo-escuro.webp'} 
                alt="FacilitaProf" 
                className="h-16 mx-auto mb-3" 
              />
              <p className="text-xs text-gray-300">
                {modoRecuperarSenha ? 'Recuperar sua senha' : 'Feito por professores, para professores!'}
              </p>
            </div>

            {/* 🔧 v428: Conteúdo condicional - Login ou Recuperar Senha */}
            {!modoRecuperarSenha ? (
              <>
                {/* Campos de Login */}
                <div className="space-y-3 mb-4">
                  <input 
                    type="email" 
                    id="landingLoginEmail" 
                    placeholder="Email" 
                    className="w-full p-3 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500 text-sm bg-white"
                    onKeyDown={(e) => e.key === 'Enter' && document.getElementById('landingLoginSenha')?.focus()}
                  />
                  <input 
                    type="password" 
                    id="landingLoginSenha" 
                    placeholder="Senha" 
                    className="w-full p-3 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500 text-sm bg-white"
                    onKeyDown={(e) => e.key === 'Enter' && handleLandingLogin()}
                  />
                  <button 
                    onClick={handleLandingLogin}
                    disabled={loginLoading}
                    className="w-full py-3 rounded-lg font-semibold text-white transition hover:opacity-90 disabled:opacity-50" 
                    style={{backgroundColor: '#f7941d'}}
                  >
                    {loginLoading ? 'Entrando...' : 'Entrar'}
                  </button>
                  
                  {/* Divisor */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-600"></div>
                    <span className="text-gray-400 text-xs">ou</span>
                    <div className="flex-1 h-px bg-gray-600"></div>
                  </div>
                  
                  {/* Botão Google */}
                  <button 
                    onClick={handleLoginGoogle}
                    disabled={loginLoading}
                    className="w-full py-3 bg-white border-2 border-gray-300 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Entrar com Google
                  </button>
                </div>

                {/* Links */}
                <div className="text-center text-sm text-gray-300 mb-3">
                  <button 
                    onClick={() => {
                      const emailDigitado = document.getElementById('landingLoginEmail')?.value || '';
                      // Limpar campo de senha para evitar confusão
                      const senhaInput = document.getElementById('landingLoginSenha');
                      if (senhaInput) senhaInput.value = '';
                      setEmailRecuperacao(emailDigitado);
                      setModoRecuperarSenha(true);
                    }}
                    className="hover:text-orange-400 hover:underline"
                  >
                    Esqueceu a senha?
                  </button>
                </div>

                {/* Cadastro */}
                <div className="text-center pt-3 border-t border-gray-600 text-sm text-gray-300">
                  Não tem conta?{' '}
                  <button onClick={() => { setLandingMostrarLogin(false); setLandingMostrarCadastro(true); }} className="text-orange-400 font-semibold hover:underline">
                    Cadastre-se
                  </button>
                </div>

                {/* Termos e Política */}
                <div className="text-center mt-3 text-xs text-gray-400">
                  Ao entrar, você concorda com os{' '}
                  <button onClick={() => setShowModalTermosLogin(true)} className="text-orange-400 hover:underline">Termos de Uso</button>
                  {' '}e a{' '}
                  <button onClick={() => setShowModalPoliticaLogin(true)} className="text-orange-400 hover:underline">Política de Privacidade</button>
                </div>
              </>
            ) : (
              <>
                {/* Tela de Recuperar Senha */}
                <div className="space-y-4">
                  <p className="text-sm text-gray-300 text-center">
                    Digite seu email cadastrado. Você receberá um link para redefinir sua senha.
                  </p>
                  <input 
                    type="email"
                    id="emailRecuperacaoSenha"
                    autoComplete="off"
                    placeholder="Seu email" 
                    className="w-full p-3 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500 text-sm bg-white" 
                    value={emailRecuperacao} 
                    onChange={(e) => setEmailRecuperacao(e.target.value)} 
                  />
                  <button 
                    onClick={async () => {
                      if (!emailRecuperacao) { 
                        alert('⚠️ Digite seu email!'); 
                        return; 
                      }
                      
                      setLoginLoading(true);
                      try {
                        const resultado = await recuperarSenhaFirebase(emailRecuperacao);
                        
                        if (resultado.success) {
                          alert('✅ Email de recuperação enviado!\n\nVerifique sua caixa de entrada (e spam) e clique no link para redefinir sua senha.');
                          setModoRecuperarSenha(false);
                          setEmailRecuperacao('');
                        } else {
                          alert('❌ ' + resultado.error);
                        }
                      } catch (error) {
                        alert('❌ Erro ao enviar email. Tente novamente.');
                      } finally {
                        setLoginLoading(false);
                      }
                    }} 
                    disabled={loginLoading}
                    className="w-full py-3 rounded-lg font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                    style={{backgroundColor: '#f7941d'}}
                  >
                    {loginLoading ? 'Enviando...' : 'Enviar Email de Recuperação'}
                  </button>
                  <button 
                    onClick={() => { setModoRecuperarSenha(false); setEmailRecuperacao(''); }} 
                    className="w-full py-2 text-gray-400 hover:text-orange-400 text-sm"
                  >
                    ← Voltar para Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Cadastro - Compacto e Largo */}
      {landingMostrarCadastro && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
          <div className="rounded-2xl w-full max-w-xl p-5 relative my-2" style={{ backgroundColor: '#1e3a5f' }}>
            <button onClick={() => { setLandingMostrarCadastro(false); resetarVerificacao(); }} className="absolute top-3 right-3 text-gray-300 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            
            {/* Header compacto */}
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-white">Criar Conta</h2>
              <p className="text-gray-300 text-sm">
                {etapaVerificacao === 'verificado' 
                  ? '✅ Telefone verificado! Complete o cadastro.'
                  : 'Cadastre-se e ganhe 10 créditos grátis!'}
              </p>
            </div>

            {/* v548: ETAPA 1 - Verificação de Telefone (para TODOS - pessoa física e jurídica) */}
            {etapaVerificacao !== 'verificado' && (
              <div className="mb-4 p-4 rounded-lg border-2 border-green-600 bg-green-900 bg-opacity-20">
                <label className="block text-sm font-medium text-white mb-2">
                  📱 Verificação de Telefone via WhatsApp
                </label>
                
                {/* Campo Telefone */}
                <div className="flex flex-col md:flex-row gap-2 mb-2">
                  <input 
                    type="tel" 
                    placeholder="(79) 99999-9999"
                    className="flex-1 p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white min-w-0" 
                    value={dadosCadastro.telefone}
                    onChange={(e) => setDadosCadastro({...dadosCadastro, telefone: formatarTelefone(e.target.value)})}
                    disabled={etapaVerificacao === 'codigo'}
                    maxLength={15}
                  />
                  {etapaVerificacao === 'telefone' && (
                    <button
                      onClick={() => enviarCodigoWhatsApp(dadosCadastro.telefone)}
                      disabled={enviandoCodigo || (dadosCadastro.telefone || '').replace(/\D/g, '').length < 10}
                      className="w-full md:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-semibold"
                    >
                      {enviandoCodigo ? 'Enviando...' : '📩 Enviar Código'}
                    </button>
                  )}
                </div>

                {/* Campo Código */}
                {etapaVerificacao === 'codigo' && (
                  <>
                    <div className="flex flex-col md:flex-row gap-2 mb-2">
                      <input 
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        placeholder="Código de 6 dígitos"
                        className="flex-1 p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white text-center font-mono tracking-widest min-w-0" 
                        value={codigoVerificacao}
                        onChange={(e) => setCodigoVerificacao(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                      />
                      <button
                        onClick={async () => {
                          const sucesso = await verificarCodigoWhatsApp(dadosCadastro.telefone, codigoVerificacao);
                          if (sucesso) {
                            setEtapaVerificacao('verificado');
                          }
                        }}
                        disabled={verificandoCodigo || codigoVerificacao.length !== 6}
                        className="w-full md:w-auto px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-semibold"
                      >
                        {verificandoCodigo ? 'Verificando...' : '🔐 Verificar Código'}
                      </button>
                    </div>
                    
                    {/* Reenviar / Alterar */}
                    <div className="text-center text-xs">
                      {tempoReenvio > 0 ? (
                        <span className="text-gray-400">Reenviar em {tempoReenvio}s</span>
                      ) : (
                        <button onClick={() => enviarCodigoWhatsApp(dadosCadastro.telefone)} disabled={enviandoCodigo} className="text-orange-400 hover:underline">
                          Reenviar código
                        </button>
                      )}
                      <span className="mx-2 text-gray-500">|</span>
                      <button onClick={() => { setEtapaVerificacao('telefone'); setCodigoVerificacao(''); }} className="text-gray-400 hover:underline">
                        Alterar número
                      </button>
                    </div>
                  </>
                )}

                {etapaVerificacao === 'telefone' && (
                  <p className="text-xs text-green-300 mt-2">
                    Você receberá um código de 6 dígitos no WhatsApp.
                  </p>
                )}
              </div>
            )}

            {/* v551: Botão "Entrar com Google" visível na etapa de verificação de telefone */}
            {etapaVerificacao !== 'verificado' && (
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 h-px bg-gray-600"></div>
                  <span className="text-gray-400 text-xs">ou</span>
                  <div className="flex-1 h-px bg-gray-600"></div>
                </div>
                <button 
                  onClick={async () => {
                    setLoginLoading(true);
                    try {
                      const resultado = await loginComGoogle();
                      
                      if (resultado.success) {
                        const user = resultado.user;
                        
                        // Salvar dados do Google pendente para completar após verificação WhatsApp
                        setUserGooglePendente(user);
                        setDadosCadastroGoogle({
                          nomeEscola: user.escola || '',
                          nomeProfessor: user.nome || user.displayName || '',
                          telefone: dadosCadastro.telefone || ''
                        });
                        
                        // Fechar modal de cadastro e abrir modal de completar cadastro Google
                        // (que já exige verificação WhatsApp)
                        setLandingMostrarCadastro(false);
                        setShowModalCompletarCadastroGoogle(true);
                      } else {
                        alert('❌ ' + resultado.error);
                      }
                    } catch (error) {
                      console.error('Erro no login Google:', error);
                      alert('❌ Erro ao conectar com Google.');
                    } finally {
                      setLoginLoading(false);
                    }
                  }}
                  disabled={loginLoading}
                  className="w-full py-2.5 bg-white border-2 border-gray-300 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-50 transition disabled:opacity-50 text-sm"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {loginLoading ? 'Conectando...' : 'Entrar com Google'}
                </button>
              </div>
            )}

            {/* v548: Indicador de telefone verificado (para todos) */}
            {etapaVerificacao === 'verificado' && (
              <div className="mb-4 p-3 rounded-lg border-2 border-green-500 bg-green-900 bg-opacity-30 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-300 text-sm">Telefone {dadosCadastro.telefone} verificado!</span>
                <button 
                  onClick={() => { resetarVerificacao(); setDadosCadastro({...dadosCadastro, telefone: ''}); }}
                  className="ml-auto text-xs text-gray-400 hover:text-white"
                >
                  Alterar
                </button>
              </div>
            )}

            {/* Campos do cadastro - mostrar apenas após verificação */}
            {etapaVerificacao === 'verificado' && (
              <div className="space-y-3 mb-4">
                {/* Tipo de conta */}
                <select 
                  className="w-full p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-gray-600 text-sm bg-white"
                  value={dadosCadastro.tipo}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, tipo: e.target.value})}
                >
                  <option value="fisica">👤 Pessoa Física (Professor)</option>
                  <option value="juridica">🏫 Pessoa Jurídica (Escola)</option>
                </select>
              
                {/* Nome completo */}
                <input 
                  type="text" 
                  placeholder="Nome completo *" 
                  className="w-full p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                  value={dadosCadastro.nome}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, nome: e.target.value})}
                />
                
                {/* Nome da escola */}
                <input 
                  type="text" 
                  placeholder={dadosCadastro.tipo === 'juridica' ? "Nome da Escola *" : "Nome da Escola onde trabalha *"}
                  className="w-full p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                  value={dadosCadastro.escola}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, escola: e.target.value})}
                />
                
                {/* Email */}
                <input 
                  type="email" 
                  placeholder="Email *" 
                  className="w-full p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                  value={dadosCadastro.email}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, email: e.target.value})}
                />
                
                {/* Telefone para escola (sem verificação) */}
                {dadosCadastro.tipo === 'juridica' && (
                  <input 
                    type="tel" 
                    placeholder="Telefone *" 
                    className="w-full p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                    value={dadosCadastro.telefone}
                    onChange={(e) => setDadosCadastro({...dadosCadastro, telefone: formatarTelefone(e.target.value)})}
                    maxLength={15}
                  />
                )}
                
                {/* Senha e Confirmar */}
                <div className="grid grid-cols-2 gap-3">
                  <input 
                    type="password" 
                    placeholder="Senha (mín. 6 caract.) *" 
                    className="p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                    value={dadosCadastro.senha}
                    onChange={(e) => setDadosCadastro({...dadosCadastro, senha: e.target.value})}
                  />
                  <input 
                    type="password" 
                    placeholder="Confirmar senha *" 
                    className="p-2.5 border-2 border-gray-600 rounded-lg focus:border-orange-400 focus:outline-none text-sm bg-white" 
                    value={dadosCadastro.confirmarSenha}
                    onChange={(e) => setDadosCadastro({...dadosCadastro, confirmarSenha: e.target.value})}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        document.getElementById('btnCriarConta')?.click();
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Termos - mostrar apenas quando pode cadastrar */}
            {etapaVerificacao === 'verificado' && (
              <>
                <label className="flex items-start gap-3 text-sm text-gray-300 mb-4">
                  <input 
                    type="checkbox" 
                    className="w-5 h-5 rounded border-2 border-gray-400 mt-0.5 flex-shrink-0" 
                    checked={aceitouTermos}
                    onChange={(e) => setAceitouTermos(e.target.checked)}
                  />
                  <span>Li e aceito os <button onClick={() => setShowModalTermosLogin(true)} className="font-semibold text-orange-400 hover:underline">Termos de Uso</button> e <button onClick={() => setShowModalPoliticaLogin(true)} className="font-semibold text-orange-400 hover:underline">Política de Privacidade</button></span>
                </label>
                
                {/* Botão Criar Conta */}
                <button 
                  id="btnCriarConta"
                  onClick={async () => {
                    // Validações
                    if (!dadosCadastro.nome || !dadosCadastro.email || !dadosCadastro.telefone || !dadosCadastro.escola || !dadosCadastro.senha) {
                      alert('⚠️ Preencha todos os campos obrigatórios!');
                      return;
                    }
                    if (dadosCadastro.senha.length < 6) {
                      alert('⚠️ A senha deve ter pelo menos 6 caracteres!');
                      return;
                    }
                    if (dadosCadastro.senha !== dadosCadastro.confirmarSenha) {
                      alert('⚠️ As senhas não conferem!');
                      return;
                    }
                    if (!aceitouTermos) {
                      alert('⚠️ Você precisa aceitar os Termos de Uso!');
                      return;
                    }
                    
                    // v548: Verificar se telefone foi verificado (para TODOS)
                    if (!telefoneVerificadoTemp) {
                      alert('⚠️ Verifique seu telefone via WhatsApp primeiro.');
                      return;
                    }
                    
                    // 🔥 CADASTRO COM FIREBASE
                    setLoginLoading(true);
                    try {
                      const resultado = await cadastrarUsuario({
                        ...dadosCadastro,
                        telefoneVerificado: telefoneVerificadoTemp
                      });
                      
                      if (resultado.success) {
                        // Limpar estados do gerador antes do login
                        setPdfGerado(null);
                        setPaginas([{ id: 1, questoes: [] }]);
                        setPaginaAtiva(1);
                        setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                        setComponenteBloqueado(null);
                        setHabilidadeBloqueada(null);
                        setTipoGeracao('questao');
                        setBlocosSimulado([
                          { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                          { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                        ]);
                        setBlocoAtivo(1);
                        setTurmaSimuladoSelecionada(null); setTurmasSelecionadasSimulado([]);
                        obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
                        
                        alert('✅ Cadastro realizado com sucesso!\n\n🎉 Você ganhou 10 créditos grátis!');
                        
                        setCurrentUser({
                          uid: resultado.user.uid,
                          nome: resultado.user.nome,
                          email: resultado.user.email,
                          tipo: resultado.user.tipo,
                          creditos: resultado.user.creditos,
                          escola: resultado.user.escola,
                          nomeEscola: resultado.user.nomeEscola || resultado.user.escola || '',
                          seriesLiberadas: resultado.user.seriesLiberadas,
                          plano: resultado.user.plano,
                          telefoneVerificado: telefoneVerificadoTemp
                        });
                        
                        setCabecalhoConfig(prev => ({
                          ...prev,
                          professor: resultado.user.tipo === 'professor' ? resultado.user.nome : '',
                          nomeEscola: resultado.user.tipo === 'escola'
                            ? (resultado.user.nomeEscola || resultado.user.escola || resultado.user.nome || '')
                            : (resultado.user.escolaNome || resultado.user.escola || '')
                        }));
                        
                        setLandingMostrarCadastro(false);
                        resetarVerificacao();
                        setCurrentView('professor-gerador');
                        
                        // Limpar formulário
                        setDadosCadastro({ nome: '', email: '', telefone: '', senha: '', confirmarSenha: '', tipo: 'fisica', escola: '' });
                        setAceitouTermos(false);
                      } else {
                        alert('❌ ' + resultado.error);
                      }
                    } catch (error) {
                      console.error('Erro no cadastro:', error);
                      alert('❌ Erro ao cadastrar. Tente novamente.');
                    } finally {
                      setLoginLoading(false);
                    }
                  }}
                  disabled={loginLoading}
                  className="w-full py-3 rounded-lg font-semibold text-white transition hover:opacity-90 disabled:opacity-50" 
                  style={{ backgroundColor: '#f7941d' }}
                >
                  {loginLoading ? 'Cadastrando...' : 'Criar Conta'}
                </button>
              </>
            )}

            {/* Link para login */}
            <p className="text-center mt-3 pt-3 border-t border-gray-600 text-sm text-gray-300">
              Já tem conta?{' '}
              <button onClick={() => { setLandingMostrarCadastro(false); setLandingMostrarLogin(true); resetarVerificacao(); }} className="font-semibold text-orange-400 hover:underline">
                Entrar
              </button>
            </p>
          </div>
        </div>
      )}

      {/* v689d: Modais de Termos e Política acessíveis da landing page (login + cadastro) */}
      {showModalTermosLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="bg-blue-600 px-5 py-3 flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Termos de Uso
              </h3>
              <button onClick={() => setShowModalTermosLogin(false)} className="text-white hover:bg-blue-700 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]" style={{fontFamily: 'Georgia, serif', lineHeight: '1.8'}}>
              <pre className="whitespace-pre-wrap text-sm text-gray-800">{termosUso}</pre>
            </div>
            <div className="border-t px-5 py-3">
              <button onClick={() => setShowModalTermosLogin(false)} className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalPoliticaLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="bg-purple-600 px-5 py-3 flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Política de Privacidade
              </h3>
              <button onClick={() => setShowModalPoliticaLogin(false)} className="text-white hover:bg-purple-700 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]" style={{fontFamily: 'Georgia, serif', lineHeight: '1.8'}}>
              <pre className="whitespace-pre-wrap text-sm text-gray-800">{politicaPrivacidade}</pre>
            </div>
            <div className="border-t px-5 py-3">
              <button onClick={() => setShowModalPoliticaLogin(false)} className="w-full py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS para animação de estrelas */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.8; }
        }
      `}</style>
      </div>
    );
  }

  if (currentView === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{backgroundColor: '#0a1929'}}>
        <div className="rounded-3xl shadow-2xl p-8 w-full max-w-md" style={{ backgroundColor: '#1e3a5f' }}>
          <div className="text-center mb-6">
            {/* 🆕 v400: Logo com imagem */}
            <div className="mb-4">
              <img 
                src={landingImagesFirebase.logomarcaEscuro || '/assets/logo-escuro.webp'} 
                alt="FacilitaProf" 
                className="h-20 mx-auto" 
              />
            </div>
            <p className="text-xs text-gray-300 mt-1">Feito por professores, para professores!</p>
          </div>

          {!showCadastro ? (
            <>
              {/* TELA DE LOGIN */}
              <input type="email" placeholder="Email" className="w-full p-3 border border-gray-600 rounded-lg mb-3 focus:outline-none focus:border-orange-500 text-sm bg-white" id="loginEmail" />
              <input type="password" placeholder="Senha" className="w-full p-3 border border-gray-600 rounded-lg mb-4 focus:outline-none focus:border-orange-500 text-sm bg-white" id="loginSenha" />
              
              {/* Aviso de aceite aos termos no login */}
              <div className="bg-blue-900 bg-opacity-50 p-3 rounded-lg mb-4 text-xs text-gray-300 border border-blue-400">
                <p>Ao entrar na plataforma, você concorda com os nossos{' '}
                  <button onClick={() => setShowModalTermosLogin(true)} className="text-orange-400 hover:underline font-semibold">
                    Termos de Uso
                  </button>
                  {' '}e{' '}
                  <button onClick={() => setShowModalPoliticaLogin(true)} className="text-orange-400 hover:underline font-semibold">
                    Política de Privacidade
                  </button>.
                </p>
              </div>

              <button onClick={async () => {
                const email = document.getElementById('loginEmail').value;
                const senha = document.getElementById('loginSenha').value;
                
                if (!email || !senha) {
                  alert('⚠️ Preencha email e senha!');
                  return;
                }
                
                // Aguardar Firebase estar pronto (máximo 10s)
                let tentativas = 0;
                while ((!firebaseReady || !auth || !db) && tentativas < 20) {
                  console.log('⏳ Aguardando Firebase... tentativa', tentativas + 1);
                  await new Promise(resolve => setTimeout(resolve, 500));
                  tentativas++;
                }
                
                if (!firebaseReady || !auth || !db) {
                  alert('❌ Firebase não disponível. Recarregue a página.');
                  return;
                }
                
                try {
                  console.log('🔐 Autenticando no Firebase...');
                  
                  // v616: Limpar flag de logout
                  localStorage.removeItem('facilitaprof_logout');
                  
                  const userCredential = await auth.signInWithEmailAndPassword(email, senha);
                  const user = userCredential.user;
                  
                  console.log('✅ Firebase Auth OK! UID:', user.uid);
                  
                  // Buscar dados do usuário no Firestore
                  const userDoc = await db.collection('users').doc(user.uid).get();
                  
                  if (!userDoc.exists) {
                    alert('❌ Usuário não encontrado no banco de dados.');
                    return;
                  }
                  
                  const userData = { uid: user.uid, email: user.email, ...userDoc.data() };
                  
                  // v616: Resetar estados de carregamento para forçar reload
                  setQuestoesCarregadas(false);
                  setHabilidadesCarregadas(false);
                  setSeriesCarregadas(false);
                  setComponentesCarregados(false);
                  setRecargasCarregadas(false);
                  
                  // Limpar estados antes do login
                  setPdfGerado(null);
                  setPaginas([{ id: 1, questoes: [] }]);
                  setPaginaAtiva(1);
                  setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                  setComponenteBloqueado(null);
                  setHabilidadeBloqueada(null);
                  setTipoGeracao('questao');
                  setBlocosSimulado([
                    { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                    { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                  ]);
                  setBlocoAtivo(1);
                  setTurmaSimuladoSelecionada(null);
                  setTurmasSelecionadasSimulado([]);
                  obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
                  
                  // Setar usuário
                  setCurrentUser(userData);
                  setCabecalhoConfig({
                    ...cabecalhoConfig,
                    professor: userData.tipo === 'professor' ? userData.nome : '',
                    nomeEscola: userData.escolaNome || userData.escola || ''
                  });
                  
                  // Redirecionar
                  if (userData.tipo === 'admin') {
                    setCurrentView('admin-dashboard');
                  } else if (userData.tipo === 'escola') {
                    setCurrentView('escola-estatisticas');
                  } else {
                    setCurrentView('professor-gerador');
                  }
                  
                  console.log('✅ Login completo!');
                } catch (error) {
                  console.error('❌ Erro no login Firebase:', error);
                  let mensagem = 'Erro ao fazer login';
                  if (error.code === 'auth/user-not-found') mensagem = 'Email não cadastrado';
                  else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') mensagem = 'Email ou senha incorretos';
                  else if (error.code === 'auth/invalid-email') mensagem = 'Email inválido';
                  else if (error.code === 'auth/too-many-requests') mensagem = 'Muitas tentativas. Aguarde alguns minutos.';
                  alert('❌ ' + mensagem);
                }
              }} className="w-full text-white py-3 rounded-lg font-semibold transition hover:opacity-90" style={{backgroundColor: '#f7941d'}}>
                Entrar
              </button>
              <div className="text-center mt-3">
                <button 
                  onClick={() => {
                    setEmailRecuperacao(document.getElementById('loginEmail').value || '');
                    setEtapaRecuperacao(1);
                    setCodigoRecuperacao('');
                    setNovaSenha('');
                    setConfirmarNovaSenha('');
                    setShowModalRecuperarSenha(true);
                  }}
                  className="text-sm text-gray-500 hover:text-orange-500 hover:underline"
                >
                  Esqueceu a senha?
                </button>
              </div>

              {/* Credenciais de teste */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs">
                <p className="font-semibold text-blue-700 mb-2">🔑 Credenciais de Teste:</p>
                <div className="space-y-1 text-blue-600">
                  <p><span className="font-semibold">👤 Professor:</span> professor@teste.com / prof123</p>
                  <p><span className="font-semibold">🏫 Escola:</span> escola@teste.com / escola123</p>
                </div>
              </div>

              <div className="text-center mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Não tem conta?{' '}
                  <button onClick={() => { setShowCadastro(true); setAceitouTermos(false); setDadosCadastro({ nome: '', email: '', telefone: '', senha: '', confirmarSenha: '', tipo: 'fisica', escola: '' }); }} className="text-orange-500 font-semibold hover:underline">
                    Cadastre-se
                  </button>
                </p>
              </div>
            </>
          ) : (
            <>
              {/* TELA DE CADASTRO */}
              <h2 className="text-lg font-bold text-center mb-4" style={{color: '#1e3a5f'}}>Criar Conta</h2>
              
              <div className="space-y-3">
                <input 
                  type="text" 
                  placeholder="Nome completo *" 
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                  value={dadosCadastro.nome}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, nome: e.target.value})}
                />
                <input 
                  type="email" 
                  placeholder="Email *" 
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                  value={dadosCadastro.email}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, email: e.target.value})}
                />
                <input 
                  type="tel" 
                  placeholder="Telefone *" 
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                  value={dadosCadastro.telefone}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, telefone: e.target.value})}
                />
                <select 
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                  value={dadosCadastro.tipo}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, tipo: e.target.value})}
                >
                  <option value="fisica">👤 Pessoa Física (Professor)</option>
                  <option value="juridica">🏫 Pessoa Jurídica (Escola)</option>
                </select>
                <input 
                  type="text" 
                  placeholder={dadosCadastro.tipo === 'juridica' ? "Nome da Escola *" : "Nome da Escola onde trabalha *"}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                  value={dadosCadastro.escola}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, escola: e.target.value})}
                />
                <input 
                  type="password" 
                  placeholder="Senha *" 
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                  value={dadosCadastro.senha}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, senha: e.target.value})}
                />
                <input 
                  type="password" 
                  placeholder="Confirmar senha *" 
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                  value={dadosCadastro.confirmarSenha}
                  onChange={(e) => setDadosCadastro({...dadosCadastro, confirmarSenha: e.target.value})}
                />
              </div>

              {/* Checkbox de aceite aos termos */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="w-5 h-5 mt-0.5 border-2 border-gray-400 rounded" 
                    checked={aceitouTermos}
                    onChange={(e) => setAceitouTermos(e.target.checked)}
                  />
                  <span className="text-sm text-gray-600">
                    Li e aceito os{' '}
                    <button onClick={() => setShowModalTermosLogin(true)} className="text-orange-500 hover:underline font-semibold">
                      Termos de Uso
                    </button>
                    {' '}e a{' '}
                    <button onClick={() => setShowModalPoliticaLogin(true)} className="text-orange-500 hover:underline font-semibold">
                      Política de Privacidade
                    </button>
                    {' '}da plataforma FacilitaPROF. *
                  </span>
                </label>
              </div>

              <button 
                onClick={async () => {
                  if (!dadosCadastro.nome || !dadosCadastro.email || !dadosCadastro.telefone || !dadosCadastro.escola || !dadosCadastro.senha) {
                    alert('⚠️ Preencha todos os campos obrigatórios!');
                    return;
                  }
                  if (dadosCadastro.senha !== dadosCadastro.confirmarSenha) {
                    alert('⚠️ As senhas não conferem!');
                    return;
                  }
                  if (dadosCadastro.senha.length < 6) {
                    alert('⚠️ A senha deve ter pelo menos 6 caracteres!');
                    return;
                  }
                  if (!aceitouTermos) {
                    alert('⚠️ Você precisa aceitar os Termos de Uso e Política de Privacidade para continuar.');
                    return;
                  }
                  
                  // v549: Verificar se telefone foi verificado (pessoa física também)
                  if (!telefoneVerificadoTemp) {
                    alert('⚠️ Verifique seu telefone via WhatsApp primeiro.');
                    return;
                  }
                  
                  // 🔥 CADASTRO COM FIREBASE
                  setLoginLoading(true);
                  try {
                    const resultado = await cadastrarUsuario({
                      ...dadosCadastro,
                      telefoneVerificado: telefoneVerificadoTemp
                    });
                    
                    if (resultado.success) {
                      alert(`✅ Cadastro realizado com sucesso!\n\n🎉 Você ganhou 10 créditos grátis!\n\n📧 Email: ${dadosCadastro.email}\n\nVocê já está logado!`);
                      
                      setCurrentUser({
                        uid: resultado.user.uid,
                        nome: resultado.user.nome,
                        email: resultado.user.email,
                        tipo: resultado.user.tipo,
                        creditos: resultado.user.creditos,
                        escola: resultado.user.escola,
                        // v524: Incluir nomeEscola
                        nomeEscola: resultado.user.nomeEscola || resultado.user.escola || '',
                        seriesLiberadas: resultado.user.seriesLiberadas,
                        plano: resultado.user.plano,
                        telefoneVerificado: telefoneVerificadoTemp
                      });
                      
                      // 🔧 v412: Atualizar cabeçalho com dados do novo usuário
                      // 🔧 v518: Usar escolaNome para professor vinculado
                      // 🔧 v524: Para escola, usar nomeEscola
                      setCabecalhoConfig(prev => ({
                        ...prev,
                        professor: resultado.user.tipo === 'professor' ? resultado.user.nome : '',
                        nomeEscola: resultado.user.tipo === 'escola'
                          ? (resultado.user.nomeEscola || resultado.user.escola || resultado.user.nome || '')
                          : (resultado.user.escolaNome || resultado.user.escola || '')
                      }));
                      
                      setShowCadastro(false);
                      setLandingMostrarCadastro(false);
                      resetarVerificacao();
                      setCurrentView('professor-gerador');
                    } else {
                      alert('❌ ' + resultado.error);
                    }
                  } catch (error) {
                    console.error('Erro no cadastro:', error);
                    alert('❌ Erro ao cadastrar. Tente novamente.');
                  } finally {
                    setLoginLoading(false);
                  }
                }}
                disabled={!aceitouTermos || loginLoading}
                className={`w-full py-3 rounded-lg font-semibold transition mt-4 ${
                  aceitouTermos && !loginLoading ? 'text-white hover:opacity-90' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                style={aceitouTermos && !loginLoading ? {backgroundColor: '#f7941d'} : {}}
              >
                {loginLoading ? 'Cadastrando...' : 'Criar Conta'}
              </button>

              <div className="text-center mt-4">
                <p className="text-sm text-gray-500">
                  Já tem conta?{' '}
                  <button onClick={() => setShowCadastro(false)} className="text-orange-500 font-semibold hover:underline">
                    Faça login
                  </button>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Modal de Termos de Uso */}
        {showModalTermosLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="bg-blue-600 px-5 py-3 flex items-center justify-between">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Termos de Uso
                </h3>
                <button onClick={() => setShowModalTermosLogin(false)} className="text-white hover:bg-blue-700 p-1 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]" style={{fontFamily: 'Georgia, serif', lineHeight: '1.8'}}>
                <pre className="whitespace-pre-wrap text-sm text-gray-800">{termosUso}</pre>
              </div>
              <div className="border-t px-5 py-3">
                <button onClick={() => setShowModalTermosLogin(false)} className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Política de Privacidade */}
        {showModalPoliticaLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="bg-purple-600 px-5 py-3 flex items-center justify-between">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Política de Privacidade
                </h3>
                <button onClick={() => setShowModalPoliticaLogin(false)} className="text-white hover:bg-purple-700 p-1 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]" style={{fontFamily: 'Georgia, serif', lineHeight: '1.8'}}>
                <pre className="whitespace-pre-wrap text-sm text-gray-800">{politicaPrivacidade}</pre>
              </div>
              <div className="border-t px-5 py-3">
                <button onClick={() => setShowModalPoliticaLogin(false)} className="w-full py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Recuperação de Senha */}
        {showModalRecuperarSenha && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-md overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-4 flex items-center justify-between">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Recuperar Senha
                </h3>
                <button onClick={() => { setShowModalRecuperarSenha(false); setEmailRecuperacao(''); }} className="text-white hover:bg-orange-700 p-1 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 text-center">
                    Digite seu email cadastrado. Você receberá um link para redefinir sua senha.
                  </p>
                  <input 
                    type="email" 
                    placeholder="Seu email" 
                    className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500" 
                    value={emailRecuperacao} 
                    onChange={(e) => setEmailRecuperacao(e.target.value)} 
                  />
                  <button 
                    onClick={async () => {
                      if (!emailRecuperacao) { 
                        alert('⚠️ Digite seu email!'); 
                        return; 
                      }
                      
                      setLoginLoading(true);
                      try {
                        const resultado = await recuperarSenhaFirebase(emailRecuperacao);
                        
                        if (resultado.success) {
                          alert('✅ Email de recuperação enviado!\n\nVerifique sua caixa de entrada (e spam) e clique no link para redefinir sua senha.');
                          setShowModalRecuperarSenha(false);
                          setEmailRecuperacao('');
                        } else {
                          alert('❌ ' + resultado.error);
                        }
                      } catch (error) {
                        alert('❌ Erro ao enviar email. Tente novamente.');
                      } finally {
                        setLoginLoading(false);
                      }
                    }} 
                    disabled={loginLoading}
                    className="w-full py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50"
                  >
                    {loginLoading ? 'Enviando...' : 'Enviar Email de Recuperação'}
                  </button>
                  <button 
                    onClick={() => { setShowModalRecuperarSenha(false); setLandingMostrarLogin(true); }} 
                    className="w-full py-2 text-gray-500 hover:text-orange-500 text-sm"
                  >
                    ← Voltar para Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const menuItems = currentUser?.tipo === 'admin' ? [
    { view: 'admin-dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { view: 'admin-clientes', label: 'Clientes', icon: <Users className="w-5 h-5" /> },
    { view: 'admin-equipe', label: 'Equipe', icon: <Award className="w-5 h-5" /> },
    { view: 'admin-questoes', label: 'Questões', icon: <FileText className="w-5 h-5" /> },
    { view: 'admin-habilidades', label: 'Habilidades/Conteúdos', icon: <BookOpen className="w-5 h-5" /> },
    { view: 'admin-series', label: 'Séries', icon: <Layers className="w-5 h-5" /> },
    { view: 'admin-componentes', label: 'Componentes Curriculares', icon: <Target className="w-5 h-5" /> },
    { view: 'admin-recargas', label: 'Recargas', icon: <CreditCard className="w-5 h-5" /> },
    { view: 'admin-landing', label: 'Imagens e Links', icon: <Globe className="w-5 h-5" /> },
    { view: 'admin-termos-uso', label: 'Termos de Uso', icon: <FileText className="w-5 h-5" /> },
    { view: 'admin-politica-privacidade', label: 'Política de Privacidade', icon: <Lock className="w-5 h-5" /> }
  ] : currentUser?.tipo === 'escola' ? [
    { view: 'escola-estatisticas', label: 'Estatísticas', icon: <PieChart className="w-5 h-5" /> },
    { view: 'escola-simulado', label: 'Simulado SAEB', icon: <Target className="w-5 h-5" /> },
    { view: 'escola-professores', label: 'Professores', icon: <Users className="w-5 h-5" /> },
    { view: 'escola-turmas', label: 'Turmas', icon: <GraduationCap className="w-5 h-5" /> },
    { view: 'escola-desempenho', label: 'Correção/Desempenho Simulados', icon: <BarChart3 className="w-5 h-5" /> },
    { view: 'escola-perfil', label: 'Perfil da Escola', icon: <Building2 className="w-5 h-5" /> },
    { view: 'escola-recargas', label: 'Recargas', icon: <CreditCard className="w-5 h-5" /> },
    { view: 'escola-historico', label: 'Histórico', icon: <BookOpen className="w-5 h-5" /> },
    { view: 'escola-suporte', label: 'Suporte', icon: <Headphones className="w-5 h-5" /> }
  ] : [
    { view: 'professor-gerador', label: 'Gerador', icon: <FileText className="w-5 h-5" /> },
    { view: 'professor-turmas', label: 'Correção/Desempenho Simulados', icon: <BarChart3 className="w-5 h-5" /> },
    { view: 'professor-cabecalho', label: 'Meu Perfil', icon: <User className="w-5 h-5" /> }, // 🔧 v426: Renomeado de Cabeçalho para Meu Perfil
    { view: 'professor-historico', label: 'Histórico', icon: <BookOpen className="w-5 h-5" /> },
    // Recargas só aparece se professor NÃO estiver vinculado a uma escola
    ...(currentUser?.escolaId ? [] : [{ view: 'professor-recargas', label: 'Recargas', icon: <CreditCard className="w-5 h-5" /> }]),
    { view: 'professor-suporte', label: 'Suporte', icon: <Headphones className="w-5 h-5" /> }
  ];

  // 📱 Itens da barra de navegação inferior (mobile)
  const mobileBottomNavItems = currentUser?.tipo === 'escola' ? [
    { view: 'escola-estatisticas', label: 'Estatísticas', icon: <PieChart className="w-5 h-5" /> },
    { view: 'escola-simulado', label: 'Simulado', icon: <Target className="w-5 h-5" /> },
    { view: 'escola-desempenho', label: 'Correção', icon: <BarChart3 className="w-5 h-5" /> },
    { view: 'escola-professores', label: 'Professores', icon: <Users className="w-5 h-5" /> }
  ] : currentUser?.tipo === 'admin' ? [] : [
    { view: 'professor-gerador', label: 'Gerador', icon: <FileText className="w-5 h-5" /> },
    { view: 'professor-turmas', label: 'Correção', icon: <BarChart3 className="w-5 h-5" /> },
    { view: 'professor-historico', label: 'Histórico', icon: <BookOpen className="w-5 h-5" /> },
    ...(currentUser?.escolaId ? [] : [{ view: 'professor-recargas', label: 'Recargas', icon: <CreditCard className="w-5 h-5" /> }])
  ];

  // 📱 Itens do menu hamburguer mobile (os que não estão na barra inferior)
  const mobileMenuItems = currentUser?.tipo === 'escola' ? [
    { view: 'escola-perfil', label: 'Perfil da Escola', icon: <Building2 className="w-5 h-5" /> },
    { view: 'escola-recargas', label: 'Recargas', icon: <CreditCard className="w-5 h-5" /> },
    { view: 'escola-historico', label: 'Histórico', icon: <BookOpen className="w-5 h-5" /> },
    { view: 'escola-suporte', label: 'Suporte', icon: <Headphones className="w-5 h-5" /> }
  ] : currentUser?.tipo === 'admin' ? menuItems : [
    { view: 'professor-cabecalho', label: 'Meu Perfil', icon: <User className="w-5 h-5" /> }, // 🔧 v426: Renomeado
    { view: 'professor-suporte', label: 'Suporte', icon: <Headphones className="w-5 h-5" /> }
  ];

  // v605: Tela de loading enquanto autentica
  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#0a1929' }}>
      {/* Notificação de Sucesso */}
      {showSaveSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <div className="font-bold">Configurações salvas!</div>
              <div className="text-sm text-green-100">O cabeçalho será usado em todas as atividades</div>
            </div>
          </div>
        </div>
      )}

      {/* 📱 Header Mobile - Visível apenas em telas pequenas */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 px-3 py-2 flex items-center justify-between" style={{ backgroundColor: '#1e3a5f' }}>
        <div className="flex items-center gap-2">
          {/* 🆕 v400: Sempre usa a imagem do logo */}
          <img 
            src={landingImagesFirebase.logomarcaEscuro || '/assets/logo-escuro.webp'} 
            alt="FacilitaProf" 
            className="h-8" 
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Indicador de Créditos - Clicável para ir para Recargas */}
          {/* v507: Não mostrar para professores vinculados */}
          {/* v510: Não mostrar quando aguardando dados da escola */}
          {currentUser?.tipo !== 'admin' && !isProfessorVinculado && !aguardandoEscola && (
            <button 
              onClick={() => {
                if (currentUser?.tipo === 'escola') {
                  setCurrentView('escola-recargas');
                } else if (!currentUser?.escolaId) {
                  setCurrentView('professor-recargas');
                }
              }}
              className="flex flex-col items-center px-3 py-1 rounded-lg transition hover:bg-white hover:bg-opacity-10"
              style={{ backgroundColor: 'rgba(10, 25, 41, 0.5)' }}
            >
              <span className="text-[10px] text-gray-400">Créditos</span>
              <span className="text-sm font-bold" style={{ color: '#f7941d' }}>{creditosEfetivos}</span>
            </button>
          )}
          {/* v507: Mostrar indicador de escola vinculada */}
          {isProfessorVinculado && escolaVinculada && (
            <div 
              className="flex flex-col items-center px-3 py-1 rounded-lg"
              style={{ backgroundColor: 'rgba(10, 25, 41, 0.5)' }}
              title={`Vinculado à ${escolaVinculada?.nome} - ${creditosEfetivos} créditos`}
            >
              <span className="text-[10px] text-gray-400">Escola</span>
              <span className="text-xs font-bold text-blue-400 truncate max-w-[80px]">{escolaVinculada?.nome?.split(' ')[0] || 'Vinculado'}</span>
            </div>
          )}
          {/* v510: Loading enquanto carrega escola */}
          {aguardandoEscola && (
            <div 
              className="flex flex-col items-center px-3 py-1 rounded-lg"
              style={{ backgroundColor: 'rgba(10, 25, 41, 0.5)' }}
            >
              <span className="text-[10px] text-gray-400">Carregando...</span>
              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="p-2 text-white hover:bg-white hover:bg-opacity-10 rounded"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* 📱 Menu Mobile Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-14 left-0 right-0 z-40 border-t border-gray-700 shadow-lg" style={{ backgroundColor: '#1e3a5f' }}>
          <nav className="p-3 space-y-1">
            {mobileMenuItems.map((item, i) => (
              <button 
                key={i} 
                onClick={() => { setCurrentView(item.view); setMobileMenuOpen(false); }} 
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left text-white ${currentView === item.view ? 'bg-orange-500' : 'hover:bg-white hover:bg-opacity-10'}`}
              >
                {item.icon}
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
            <div className="border-t border-gray-600 pt-2 mt-2">
              <button 
                onClick={() => { 
                  localStorage.setItem('facilitaprof_logout', 'true');
                  telaInicialJaDefinida.current = false; // v696: permite redirecionar corretamente em próximo login
                  if (auth) auth.signOut().catch(err => console.error('Erro no signOut:', err));
                  setCurrentUser(null); 
                  setCurrentView('landing');
                  setMobileMenuOpen(false);
                }} 
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-600 hover:bg-opacity-30"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm">Sair</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Sidebar Desktop - Oculta no mobile */}
      <aside className={`hidden md:block fixed left-0 top-0 h-full text-white transition-all z-40 ${sidebarOpen ? 'w-64' : 'w-20'}`} style={{backgroundColor: '#1e3a5f'}}>
        <div className="p-4 flex items-center justify-between border-b border-opacity-20 border-white">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              {/* 🆕 v400: Sempre usa a imagem do logo */}
              <img 
                src={landingImagesFirebase.logomarcaEscuro || '/assets/logo-escuro.webp'} 
                alt="FacilitaProf" 
                className="h-10" 
              />
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white hover:bg-opacity-10 rounded"><Menu className="w-5 h-5" /></button>
        </div>
        <nav className={`p-2 ${currentUser?.tipo === 'admin' ? 'space-y-0' : 'space-y-1'}`}>
          {menuItems.map((item, i) => (
            <button key={i} onClick={() => setCurrentView(item.view)} className={`w-full flex items-center ${currentUser?.tipo === 'admin' ? 'gap-2 px-3 py-1.5' : 'gap-3 px-3 py-2.5'} rounded-lg transition text-left ${currentView === item.view ? '' : 'hover:bg-white hover:bg-opacity-10'}`} style={currentView === item.view ? {backgroundColor: '#f7941d'} : {}}>
              <span className="flex-shrink-0">{item.icon}</span>
              {sidebarOpen && <span className={`${currentUser?.tipo === 'admin' ? 'text-sm' : 'text-base'} leading-tight`}>{item.label}</span>}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-4 left-4 right-4 border-t border-white border-opacity-20 pt-4">
          <button onClick={() => { 
            // Limpar estados do gerador
            setPdfGerado(null);
            setPaginas([{ id: 1, questoes: [] }]);
            setPaginaAtiva(1);
            setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
            setComponenteBloqueado(null);
            setHabilidadeBloqueada(null);
            setTipoGeracao('questao');
            setBlocosSimulado([
              { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
              { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
            ]);
            setBlocoAtivo(1);
            setTurmaSimuladoSelecionada(null); setTurmasSelecionadasSimulado([]);
            obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
            
            // 🔥 Marcar logout explícito e fazer signOut do Firebase
            localStorage.setItem('facilitaprof_logout', 'true');
            telaInicialJaDefinida.current = false; // v696: permite redirecionar corretamente em próximo login
            if (auth) {
              auth.signOut().catch(err => console.error('Erro no signOut:', err));
            }
            
            // Limpar usuário e voltar para landing
            setCurrentUser(null); 
            setCurrentView('landing'); 
          }} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-red-600 hover:bg-opacity-30 text-red-400">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* 📱 Barra de Navegação Inferior - Mobile (Professor e Escola) */}
      {currentUser?.tipo !== 'admin' && mobileBottomNavItems.length > 0 && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-gray-700" style={{ backgroundColor: '#1e3a5f' }}>
          <div className="flex justify-around items-center py-2">
            {mobileBottomNavItems.map((item, i) => (
              <button 
                key={i} 
                onClick={() => setCurrentView(item.view)} 
                className={`flex flex-col items-center justify-center py-1 px-2 rounded-lg transition ${currentView === item.view ? 'text-orange-400' : 'text-gray-400'}`}
              >
                <span className={currentView === item.view ? 'text-orange-400' : ''}>{item.icon}</span>
                <span className="text-[10px] mt-0.5">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      )}

      {/* Main Content - Ajustado para mobile */}
      <main className={`flex-1 transition-all pt-16 pb-20 md:pt-0 md:pb-0 px-3 md:px-6 overflow-x-hidden ${sidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
        {/* Espaçador mobile para separar do header */}
        <div className="h-2 md:h-0"></div>
        <header className="hidden md:block mb-6 p-6 rounded-xl" style={{ backgroundColor: '#1e3a5f' }}>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">
              {currentView === 'admin-dashboard' && 'Painel Administrativo'}
              {currentView === 'admin-clientes' && 'Gestão de Clientes'}
              {currentView === 'admin-equipe' && 'Gestão de Equipe'}
              {currentView === 'admin-questoes' && 'Banco de Questões'}
              {currentView === 'admin-habilidades' && 'Habilidades e Conteúdos'}
              {currentView === 'admin-series' && 'Gestão de Séries'}
              {currentView === 'admin-componentes' && 'Componentes Curriculares'}
              {currentView === 'admin-recargas' && 'Gestão de Recargas'}
              {currentView === 'admin-landing' && 'Imagens e Links'}
              {currentView === 'admin-termos-uso' && 'Termos de Uso'}
              {currentView === 'admin-politica-privacidade' && 'Política de Privacidade'}
              {currentView === 'escola-perfil' && 'Perfil da Escola'}
              {currentView === 'escola-professores' && 'Gestão de Professores'}
              {currentView === 'escola-turmas' && 'Gestão de Turmas'}
              {currentView === 'escola-desempenho' && 'Correção/Desempenho Simulados'}
              {currentView === 'escola-estatisticas' && 'Estatísticas de Uso'}
              {currentView === 'escola-simulado' && 'Simulado SAEB'}
              {currentView === 'escola-recargas' && 'Recargas de Créditos'}
              {currentView === 'escola-suporte' && 'Suporte'}
              {currentView === 'escola-historico' && 'Histórico'}
              {currentView === 'professor-cabecalho' && 'Meu Perfil'}
              {currentView === 'professor-gerador' && 'Gerador de Atividades'}
              {currentView === 'professor-turmas' && 'Correção/Desempenho Simulados'}
              {currentView === 'professor-historico' && 'Histórico de Atividades'}
              {currentView === 'professor-recargas' && 'Recargas'}
              {currentView === 'professor-suporte' && 'Suporte'}
            </h1>
            <div className="flex items-center gap-4">
              {/* v517: Ocultar créditos para professor vinculado, mostrar badge da escola */}
              {currentUser?.tipo !== 'admin' && !isProfessorVinculado && currentUser?.creditos !== undefined && (
                <div 
                  onClick={() => {
                    // Navegar para página de recargas baseado no tipo de usuário
                    if (currentUser?.tipo === 'escola') {
                      setCurrentView('escola-recargas');
                    } else {
                      setCurrentView('professor-recargas');
                    }
                  }}
                  className="bg-blue-900 bg-opacity-50 px-6 py-3 rounded-xl border border-blue-400 cursor-pointer hover:bg-opacity-70 hover:border-blue-300 transition-all group"
                  title="Clique para ver recargas"
                >
                  <div className="text-sm text-gray-300 flex items-center gap-1">
                    Créditos 
                    <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity">💳</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-300">{creditosEfetivos}</div>
                </div>
              )}
              {/* v517: Badge da escola para professor vinculado */}
              {isProfessorVinculado && escolaVinculada && (
                <div 
                  className="bg-blue-900 bg-opacity-50 px-6 py-3 rounded-xl border border-green-400"
                  title={`Vinculado à ${escolaVinculada?.nome} - ${creditosEfetivos} créditos disponíveis`}
                >
                  <div className="text-sm text-gray-300">Escola</div>
                  <div className="text-lg font-bold text-green-300 truncate max-w-[150px]">{escolaVinculada?.nome}</div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                  {/* v523: Para escola, usar inicial do nomeEscola */}
                  {currentUser?.tipo === 'escola' 
                    ? (currentUser?.nomeEscola || currentUser?.escola || currentUser?.nome || 'E').charAt(0).toUpperCase()
                    : (currentUser?.nome || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  {/* v523: Para escola, mostrar nomeEscola em vez de nome */}
                  <div className="font-semibold text-white">
                    {currentUser?.tipo === 'escola' 
                      ? (currentUser?.nomeEscola || currentUser?.escola || currentUser?.nome)
                      : currentUser?.nome}
                  </div>
                  <div className="text-sm text-gray-300 capitalize">{currentUser?.tipo}</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {currentView === 'admin-dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="rounded-xl p-6" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-4">
                  <Users className="w-7 h-7" />
                </div>
                <div className="text-3xl font-bold mb-1 text-white">{clientes.length}</div>
                <div className="text-gray-300">Clientes</div>
              </div>
              <div className="rounded-xl p-6" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-4">
                  <GraduationCap className="w-7 h-7" />
                </div>
                <div className="text-3xl font-bold mb-1 text-white">{professores.length}</div>
                <div className="text-gray-300">Professores</div>
              </div>
              {/* v638: Card de Questões removido - questões só carregam quando necessário */}
            </div>
          </div>
        )}

        {currentView === 'admin-clientes' && (
          <div className="rounded-xl p-3 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
            <div className="flex flex-col sm:flex-row gap-3 justify-between mb-4 md:mb-6">
              <input
                type="text"
                value={buscaCliente}
                onChange={(e) => setBuscaCliente(e.target.value)}
                placeholder="Buscar por nome, telefone ou email..."
                className="px-4 py-2 border rounded-lg w-full sm:w-64 bg-white"
              />
              <button 
                onClick={() => {
                  setClienteEditando(null);
                  setShowModalCliente(true);
                }}
                className="text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:opacity-90 whitespace-nowrap" style={{ backgroundColor: '#f7941d' }}
              >
                <Plus className="w-5 h-5" />Novo Cliente
              </button>
            </div>

            {(() => {
              // v695: Filtrar admin, aplicar busca e ORDENAR por data de cadastro (mais recente primeiro)
              const termo = buscaCliente.trim().toLowerCase();
              const listaClientes = clientes
                .filter(c => c.tipo !== 'admin' && c.email !== 'admin@facilitaprof.com')
                .filter(c => {
                  if (!termo) return true;
                  return (c.nome || '').toLowerCase().includes(termo) ||
                         (c.telefone || c.telefoneVerificado || '').toLowerCase().includes(termo) ||
                         (c.email || '').toLowerCase().includes(termo);
                })
                .sort((a, b) => getDataCadastroMs(b) - getDataCadastroMs(a));

              const excluirCliente = async (c) => {
                if (window.confirm(`⚠️ Deseja excluir o cliente "${c.nome}"?\n\n📧 Email: ${c.email}\n\nEsta ação não pode ser desfeita.`)) {
                  try {
                    if (db && c.id) {
                      await db.collection('users').doc(c.id).delete();
                    }
                    setClientes(clientes.filter(cl => cl.id !== c.id));
                    alert(`✅ Cliente "${c.nome}" excluído do banco de dados!\n\n⚠️ IMPORTANTE: Para liberar o email "${c.email}" para novo cadastro, acesse o Firebase Console:\n\n1. Vá em Authentication > Users\n2. Busque pelo email: ${c.email}\n3. Clique nos 3 pontos e "Excluir conta"\n\n💡 Sem isso, o email continuará bloqueado para novos cadastros.`);
                  } catch (error) {
                    console.error('Erro ao excluir cliente:', error);
                    alert('❌ Erro ao excluir cliente. Tente novamente.');
                  }
                }
              };

              if (listaClientes.length === 0) {
                return (
                  <div className="text-center py-12 text-gray-300 bg-white bg-opacity-5 rounded-xl">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                    <p>{termo ? 'Nenhum cliente encontrado para essa busca' : 'Nenhum cliente cadastrado'}</p>
                  </div>
                );
              }

              return (
                <>
                  {/* 🖥️ TABELA — apenas desktop */}
                  <div className="hidden md:block bg-white rounded-xl overflow-hidden overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Nome</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Telefone</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Cadastro</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Plano</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Créditos</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listaClientes.map(c => (
                          <tr key={c.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div className="font-semibold text-gray-800">{c.nome}</div>
                              <div className="text-xs text-gray-500">{(c.tipo === 'juridica' || c.tipo === 'escola') ? '🏫 Pessoa Jurídica' : '👤 Pessoa Física'}</div>
                            </td>
                            <td className="py-3 px-4 text-sm">{c.telefone || c.telefoneVerificado || ''}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{formatarDataCadastro(c)}</td>
                            <td className="py-3 px-4">
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                c.plano === 'Premium' ? 'bg-yellow-100 text-yellow-700' :
                                c.plano === 'Profissional' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {c.plano}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-bold text-blue-600">{c.creditos.toLocaleString()}</span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-center gap-1">
                                <button 
                                  onClick={() => {
                                    setClienteCreditos(c);
                                    setOperacaoCreditos({ tipo: 'adicionar', quantidade: 0, motivo: '' });
                                    setShowModalCreditos(true);
                                  }}
                                  className="p-1.5 hover:bg-green-50 rounded text-green-600" 
                                  title="Gerenciar Créditos"
                                >
                                  <CreditCard className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => {
                                    setClienteEditando(c);
                                    setClienteEditandoSeries(c.seriesLiberadas || []);
                                    setShowModalCliente(true);
                                  }}
                                  className="p-1.5 hover:bg-blue-50 rounded text-blue-600" 
                                  title="Editar Cliente"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => excluirCliente(c)}
                                  className="p-1.5 hover:bg-red-50 rounded text-red-600" 
                                  title="Excluir Cliente"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* 📱 CARDS — apenas mobile */}
                  <div className="md:hidden space-y-2.5">
                    {listaClientes.map(c => (
                      <div key={c.id} className="bg-white rounded-xl p-3.5 shadow-sm">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-800 text-sm truncate">{c.nome}</div>
                            <div className="text-[11px] text-gray-500">
                              {(c.tipo === 'juridica' || c.tipo === 'escola') ? '🏫 Pessoa Jurídica' : '👤 Pessoa Física'}
                            </div>
                          </div>
                          <span className={`shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                            c.plano === 'Premium' ? 'bg-yellow-100 text-yellow-700' :
                            c.plano === 'Profissional' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {c.plano}
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mb-3 text-[11px]">
                          <div>
                            <p className="text-gray-400">Telefone</p>
                            <p className="text-gray-700 font-medium truncate">{c.telefone || c.telefoneVerificado || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Cadastro</p>
                            <p className="text-gray-700 font-medium">{formatarDataCadastro(c)}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Créditos</p>
                            <p className="text-blue-600 font-bold">{c.creditos.toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button 
                            onClick={() => {
                              setClienteCreditos(c);
                              setOperacaoCreditos({ tipo: 'adicionar', quantidade: 0, motivo: '' });
                              setShowModalCreditos(true);
                            }}
                            className="flex-1 py-2 rounded-lg bg-green-50 text-green-600 flex items-center justify-center gap-1 text-[11px] font-medium"
                          >
                            <CreditCard className="w-3.5 h-3.5" /> Créditos
                          </button>
                          <button 
                            onClick={() => {
                              setClienteEditando(c);
                              setClienteEditandoSeries(c.seriesLiberadas || []);
                              setShowModalCliente(true);
                            }}
                            className="flex-1 py-2 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center gap-1 text-[11px] font-medium"
                          >
                            <Edit className="w-3.5 h-3.5" /> Editar
                          </button>
                          <button 
                            onClick={() => excluirCliente(c)}
                            className="px-3 py-2 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"
                            title="Excluir Cliente"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* 🆕 MÓDULO EQUIPE (Admin) */}
        {currentView === 'admin-equipe' && (
          <div className="rounded-xl p-6" style={{ backgroundColor: '#1e3a5f' }}>
            <div className="flex justify-between mb-6">
              <div>
                <p className="text-sm text-gray-300">Gerencie parceiros e colaboradores</p>
                <p className="text-xs text-gray-400 mt-1">
                  <span className="text-blue-400 font-semibold">Comercial:</span> cadastra clientes | 
                  <span className="text-purple-400 font-semibold ml-2">Suporte:</span> edita clientes
                </p>
              </div>
              <button 
                onClick={() => {
                  setMembroEditando(null);
                  setShowModalEquipe(true);
                }}
                className="text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90"
                style={{backgroundColor: '#f7941d'}}
              >
                <Plus className="w-5 h-5" />Novo Membro
              </button>
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              <div className="bg-blue-900 bg-opacity-50 p-4 rounded-xl border border-blue-500">
                <div className="text-2xl font-bold text-blue-300">{equipe.length}</div>
                <div className="text-sm text-gray-300">Total</div>
              </div>
              <div className="bg-green-900 bg-opacity-50 p-4 rounded-xl border border-green-500">
                <div className="text-2xl font-bold text-green-300">{equipe.filter(m => m.funcao === 'Comercial').length}</div>
                <div className="text-sm text-gray-300">Comercial</div>
              </div>
              <div className="bg-purple-900 bg-opacity-50 p-4 rounded-xl border border-purple-500">
                <div className="text-2xl font-bold text-purple-300">{equipe.filter(m => m.funcao === 'Suporte').length}</div>
                <div className="text-sm text-gray-300">Suporte</div>
              </div>
              <div className="bg-orange-900 bg-opacity-50 p-4 rounded-xl border border-orange-500">
                <div className="text-2xl font-bold text-orange-300">
                  {clientes.filter(c => c.cadastradoPor).length}
                </div>
                <div className="text-sm text-gray-300">Clientes Cadastrados</div>
              </div>
              <div className="bg-cyan-900 bg-opacity-50 p-4 rounded-xl border border-cyan-500">
                <div className="text-2xl font-bold text-cyan-300">
                  {clientes.reduce((acc, c) => acc + (c.atendimentos?.length || 0), 0)}
                </div>
                <div className="text-sm text-gray-300">Atendimentos</div>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Membro</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Contato</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Função</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Atividade</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {equipe.map(m => {
                  const clientesCadastrados = clientes.filter(c => c.cadastradoPor === m.id).length;
                  const atendimentosRealizados = clientes.reduce((acc, c) => 
                    acc + (c.atendimentos?.filter(a => a.membroId === m.id).length || 0), 0);
                  
                  return (
                    <tr key={m.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-gray-800">{m.nome}</div>
                        <div className="text-xs text-gray-500">Desde {new Date(m.dataCadastro).toLocaleDateString('pt-BR')}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">{m.email}</div>
                        <div className="text-xs text-gray-500">{m.telefone}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                          m.funcao === 'Comercial' ? 'bg-blue-100 text-blue-700' :
                          m.funcao === 'Suporte' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {m.funcao}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                          m.status === 'ativo' ? 'bg-green-100 text-green-700' : 
                          m.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {m.status === 'ativo' ? '● Ativo' : m.status === 'pendente' ? '⏳ Pendente' : '● Inativo'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {m.funcao === 'Comercial' ? (
                          <div className="text-sm">
                            <span className="font-bold text-blue-600">{clientesCadastrados}</span>
                            <span className="text-gray-500 text-xs ml-1">cadastros</span>
                          </div>
                        ) : m.funcao === 'Suporte' ? (
                          <div className="text-sm">
                            <span className="font-bold text-purple-600">{atendimentosRealizados}</span>
                            <span className="text-gray-500 text-xs ml-1">atendimentos</span>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400">-</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          {/* Ver Detalhes */}
                          <button 
                            onClick={() => {
                              setMembroDetalhes(m);
                              setFiltroMesMembro('');
                              setShowModalDetalhesMembro(true);
                            }}
                            className="p-1.5 hover:bg-cyan-50 rounded text-cyan-600" 
                            title="Ver Detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {/* Editar */}
                          <button 
                            onClick={() => {
                              setMembroEditando(m);
                              setShowModalEquipe(true);
                            }}
                            className="p-1.5 hover:bg-blue-50 rounded text-blue-600" 
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {/* Ativar/Desativar */}
                          <button 
                            onClick={() => {
                              setEquipe(equipe.map(eq => eq.id === m.id ? {...eq, status: eq.status === 'ativo' ? 'inativo' : 'ativo'} : eq));
                            }}
                            className={`p-1.5 rounded ${m.status === 'ativo' ? 'hover:bg-red-50 text-red-600' : 'hover:bg-green-50 text-green-600'}`}
                            title={m.status === 'ativo' ? 'Desativar' : 'Ativar'}
                          >
                            {m.status === 'ativo' ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          {/* Excluir */}
                          <button 
                            onClick={() => {
                              if (window.confirm(`⚠️ Deseja excluir "${m.nome}" da equipe?`)) {
                                setEquipe(equipe.filter(eq => eq.id !== m.id));
                              }
                            }}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600" 
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            
            {equipe.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Award className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                <p>Nenhum membro cadastrado na equipe</p>
              </div>
            )}
          </div>
        )}

        {/* 🆕 MÓDULO TERMOS DE USO (Admin) */}
        {currentView === 'admin-termos-uso' && (
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-white" />
                <div>
                  <h3 className="font-bold text-white">Termos de Uso</h3>
                  <p className="text-xs text-blue-100">Documento legal de uso da plataforma - exibido na tela de login/cadastro</p>
                </div>
              </div>
              <button 
                onClick={() => setEditandoTermos(!editandoTermos)}
                className={`px-4 py-1.5 rounded-lg flex items-center gap-2 text-sm font-semibold transition ${
                  editandoTermos ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white text-blue-600 hover:bg-blue-50'
                }`}
              >
                {editandoTermos ? <><X className="w-4 h-4" /> Cancelar</> : <><Edit className="w-4 h-4" /> Editar</>}
              </button>
            </div>
            {/* Área do documento estilo Word */}
            <div className="bg-gray-200 p-6 flex justify-center">
              <div className="bg-white shadow-xl border border-gray-300 w-full max-w-4xl" style={{minHeight: '600px'}}>
                {/* Barra de ferramentas simulada */}
                {editandoTermos && (
                  <div className="border-b border-gray-200 px-4 py-2 bg-gray-50 flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-semibold">📝 Modo Edição</span>
                    <span className="text-gray-300">|</span>
                    <span>Fonte: Georgia</span>
                    <span className="text-gray-300">|</span>
                    <span>Tamanho: 12pt</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-blue-500">Este texto será exibido aos usuários na tela de login e cadastro</span>
                  </div>
                )}
                {/* Conteúdo do documento */}
                <div className="overflow-y-auto" style={{height: editandoTermos ? '440px' : '500px'}}>
                  {editandoTermos ? (
                    <textarea 
                      className="w-full h-full p-8 text-sm resize-none focus:outline-none"
                      style={{fontFamily: 'Georgia, serif', lineHeight: '1.8', minHeight: '100%'}}
                      value={termosUso}
                      onChange={(e) => setTermosUso(e.target.value)}
                      placeholder="Digite os termos de uso aqui..."
                    />
                  ) : (
                    <div className="p-8" style={{fontFamily: 'Georgia, serif', lineHeight: '1.8'}}>
                      <pre className="whitespace-pre-wrap text-sm text-gray-800 font-serif">{termosUso}</pre>
                    </div>
                  )}
                </div>
                {/* Rodapé do documento */}
                <div className="border-t border-gray-200 px-4 py-2 text-xs text-gray-400 text-center bg-gray-50 flex items-center justify-between">
                  <span>FacilitaPROF - Termos de Uso - Documento Legal</span>
                  {editandoTermos && (
                    <button 
                      onClick={() => {
                        alert('✅ Termos de Uso salvos com sucesso!');
                        setEditandoTermos(false);
                      }}
                      className="px-6 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold bg-green-500 text-white hover:bg-green-600"
                    >
                      <Save className="w-4 h-4" /> Salvar Alterações
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🆕 MÓDULO POLÍTICA DE PRIVACIDADE (Admin) */}
        {currentView === 'admin-politica-privacidade' && (
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-white" />
                <div>
                  <h3 className="font-bold text-white">Política de Privacidade</h3>
                  <p className="text-xs text-purple-100">Documento de privacidade e LGPD - exibido na tela de login/cadastro</p>
                </div>
              </div>
              <button 
                onClick={() => setEditandoPolitica(!editandoPolitica)}
                className={`px-4 py-1.5 rounded-lg flex items-center gap-2 text-sm font-semibold transition ${
                  editandoPolitica ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white text-purple-600 hover:bg-purple-50'
                }`}
              >
                {editandoPolitica ? <><X className="w-4 h-4" /> Cancelar</> : <><Edit className="w-4 h-4" /> Editar</>}
              </button>
            </div>
            {/* Área do documento estilo Word */}
            <div className="bg-gray-200 p-6 flex justify-center">
              <div className="bg-white shadow-xl border border-gray-300 w-full max-w-4xl" style={{minHeight: '600px'}}>
                {/* Barra de ferramentas simulada */}
                {editandoPolitica && (
                  <div className="border-b border-gray-200 px-4 py-2 bg-gray-50 flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-semibold">📝 Modo Edição</span>
                    <span className="text-gray-300">|</span>
                    <span>Fonte: Georgia</span>
                    <span className="text-gray-300">|</span>
                    <span>Tamanho: 12pt</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-purple-500">Este texto será exibido aos usuários na tela de login e cadastro</span>
                  </div>
                )}
                {/* Conteúdo do documento */}
                <div className="overflow-y-auto" style={{height: editandoPolitica ? '440px' : '500px'}}>
                  {editandoPolitica ? (
                    <textarea 
                      className="w-full h-full p-8 text-sm resize-none focus:outline-none"
                      style={{fontFamily: 'Georgia, serif', lineHeight: '1.8', minHeight: '100%'}}
                      value={politicaPrivacidade}
                      onChange={(e) => setPoliticaPrivacidade(e.target.value)}
                      placeholder="Digite a política de privacidade aqui..."
                    />
                  ) : (
                    <div className="p-8" style={{fontFamily: 'Georgia, serif', lineHeight: '1.8'}}>
                      <pre className="whitespace-pre-wrap text-sm text-gray-800 font-serif">{politicaPrivacidade}</pre>
                    </div>
                  )}
                </div>
                {/* Rodapé do documento */}
                <div className="border-t border-gray-200 px-4 py-2 text-xs text-gray-400 text-center bg-gray-50 flex items-center justify-between">
                  <span>FacilitaPROF - Política de Privacidade - Documento Legal</span>
                  {editandoPolitica && (
                    <button 
                      onClick={() => {
                        alert('✅ Política de Privacidade salva com sucesso!');
                        setEditandoPolitica(false);
                      }}
                      className="px-6 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold bg-green-500 text-white hover:bg-green-600"
                    >
                      <Save className="w-4 h-4" /> Salvar Alterações
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🌐 MÓDULO IMAGENS E LINKS */}
        {currentView === 'admin-landing' && (
          <div className="space-y-6">
            {/* Cabeçalho */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <Globe className="w-8 h-8" />
                <div>
                  <h2 className="text-2xl font-bold">Gerenciar Imagens e Links</h2>
                  <p className="text-teal-100">Configure imagens, telefone de suporte e vídeos tutoriais do sistema</p>
                </div>
              </div>
            </div>

            {/* Cards de Upload */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Logomarca */}
              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="bg-gray-800 px-5 py-3">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Logomarca Principal
                  </h3>
                  <p className="text-xs text-gray-300">Exibida em toda a aplicação: Landing Page, Login, Cadastro e Headers internos</p>
                </div>
                <div className="p-5">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-teal-400 transition">
                    {landingAssets.logomarcaPreview ? (
                      <div className="relative">
                        <img src={landingAssets.logomarcaPreview} alt="Logomarca" className="max-h-32 mx-auto rounded-lg" />
                        <button 
                          onClick={() => setLandingAssets({...landingAssets, logomarca: null, logomarcaPreview: null})}
                          className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500 text-sm">Clique para fazer upload</p>
                        <p className="text-gray-400 text-xs mt-1">PNG, JPG ou SVG (max 2MB)</p>
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                setLandingAssets({...landingAssets, logomarca: file, logomarcaPreview: ev.target.result});
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                  {landingAssets.logomarcaPreview && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Logomarca será exibida em toda a aplicação!
                    </p>
                  )}
                </div>
              </div>

              {/* Imagem da Professora */}
              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="bg-gray-800 px-5 py-3">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Imagem da Professora
                  </h3>
                  <p className="text-xs text-gray-300">Ilustração exibida no hero da landing page</p>
                </div>
                <div className="p-5">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-teal-400 transition">
                    {landingAssets.professoraImgPreview ? (
                      <div className="relative">
                        <img src={landingAssets.professoraImgPreview} alt="Professora" className="max-h-32 mx-auto rounded-lg" />
                        <button 
                          onClick={() => setLandingAssets({...landingAssets, professoraImg: null, professoraImgPreview: null})}
                          className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500 text-sm">Clique para fazer upload</p>
                        <p className="text-gray-400 text-xs mt-1">PNG ou JPG (max 2MB)</p>
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                setLandingAssets({...landingAssets, professoraImg: file, professoraImgPreview: ev.target.result});
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Exemplos de Atividades - 8 slots */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="bg-orange-500 px-5 py-3">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Exemplos de Atividades (até 8 imagens)
                </h3>
                <p className="text-xs text-orange-100">Imagens de atividades para exibir na seção "Veja como ficarão suas Atividades"</p>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => {
                    const key = `atividadeExemplo${num}`;
                    const previewKey = `${key}Preview`;
                    return (
                      <div key={num} className="border-2 border-dashed border-gray-300 rounded-xl p-3 text-center hover:border-orange-400 transition">
                        {landingAssets[previewKey] ? (
                          <div className="relative">
                            <img src={landingAssets[previewKey]} alt={`Exemplo ${num}`} className="max-h-28 mx-auto rounded-lg" />
                            <button 
                              onClick={() => setLandingAssets({...landingAssets, [key]: null, [previewKey]: null})}
                              className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <p className="text-xs text-gray-500 mt-1">Exemplo {num}</p>
                          </div>
                        ) : (
                          <label className="cursor-pointer block py-2">
                            <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                            <p className="text-gray-500 text-xs">Exemplo {num}</p>
                            <input 
                              type="file" 
                              className="hidden" 
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (ev) => {
                                    setLandingAssets({...landingAssets, [key]: file, [previewKey]: ev.target.result});
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Card de Telefone de Suporte */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="bg-green-600 px-5 py-3">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Telefone de Suporte (WhatsApp)
                </h3>
                <p className="text-xs text-green-100">Este número aparece no botão "Fale com a gente", footer da Landing Page e nos módulos de Suporte</p>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Número com DDD (apenas números)</label>
                    <div className="flex gap-3">
                      <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                        <span className="text-gray-500 font-semibold">+55</span>
                      </div>
                      <input
                        type="text"
                        value={telefoneSuporte.replace(/^55/, '')}
                        onChange={(e) => {
                          const numeros = e.target.value.replace(/\D/g, '').slice(0, 11);
                          setTelefoneSuporte('55' + numeros);
                        }}
                        placeholder="79998923895"
                        className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none text-lg font-mono"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Formato: DDD + Número (ex: 79998923895)
                    </p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <p className="text-xs text-gray-600 mb-1">Preview:</p>
                    <p className="text-lg font-semibold text-green-700">
                      {telefoneSuporte.replace(/^55(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3') || '(XX) XXXXX-XXXX'}
                    </p>
                    <a 
                      href={`https://wa.me/${telefoneSuporte}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-2 text-xs text-green-600 hover:text-green-700"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Testar link
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* 🎬 Vídeos Tutoriais */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-5 py-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Vídeos Tutoriais
                </h3>
                <p className="text-xs text-red-100">Adicione links de vídeos do YouTube para ajudar os usuários. Eles aparecerão nos módulos de Suporte.</p>
              </div>
              <div className="p-5">
                <div className="space-y-4">
                  {videosTutoriais.map((video, index) => (
                    <div key={video.id} className="bg-white rounded-lg p-4 border-2 border-gray-200">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={video.ativo}
                            onChange={(e) => {
                              const novosVideos = [...videosTutoriais];
                              novosVideos[index].ativo = e.target.checked;
                              setVideosTutoriais(novosVideos);
                            }}
                            className="w-5 h-5 rounded"
                          />
                          <Video className={`w-8 h-8 ${video.ativo ? 'text-red-500' : 'text-gray-300'}`} />
                        </div>
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Título do Vídeo</label>
                            <input
                              type="text"
                              value={video.titulo}
                              onChange={(e) => {
                                const novosVideos = [...videosTutoriais];
                                novosVideos[index].titulo = e.target.value;
                                setVideosTutoriais(novosVideos);
                              }}
                              placeholder="Ex: Como criar atividades"
                              className="w-full px-3 py-2 border rounded-lg focus:border-red-500 focus:outline-none text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Link do YouTube</label>
                            <input
                              type="text"
                              value={video.url}
                              onChange={(e) => {
                                const novosVideos = [...videosTutoriais];
                                novosVideos[index].url = e.target.value;
                                setVideosTutoriais(novosVideos);
                              }}
                              placeholder="https://www.youtube.com/watch?v=..."
                              className="w-full px-3 py-2 border rounded-lg focus:border-red-500 focus:outline-none text-sm"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (videosTutoriais.length > 1) {
                              setVideosTutoriais(videosTutoriais.filter((_, i) => i !== index));
                            }
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Remover vídeo"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      {video.url && (
                        <div className="mt-2 flex items-center gap-2">
                          <a 
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Testar link
                          </a>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${video.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {video.ativo ? '✓ Ativo' : 'Inativo'}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setVideosTutoriais([...videosTutoriais, { 
                      id: Date.now(), 
                      titulo: '', 
                      url: '', 
                      ativo: true 
                    }]);
                  }}
                  className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg hover:border-red-400 hover:text-red-500 transition flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Adicionar Novo Vídeo
                </button>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => {
                  setLandingAssets({
                    logomarca: null, logomarcaPreview: null,
                    professoraImg: null, professoraImgPreview: null,
                    atividadeExemplo1: null, atividadeExemplo1Preview: null,
                    atividadeExemplo2: null, atividadeExemplo2Preview: null,
                    atividadeExemplo3: null, atividadeExemplo3Preview: null,
                    atividadeExemplo4: null, atividadeExemplo4Preview: null,
                    atividadeExemplo5: null, atividadeExemplo5Preview: null,
                    atividadeExemplo6: null, atividadeExemplo6Preview: null,
                    atividadeExemplo7: null, atividadeExemplo7Preview: null,
                    atividadeExemplo8: null, atividadeExemplo8Preview: null
                  });
                  setTelefoneSuporte('5579998923895');
                  setVideosTutoriais([
                    { id: 1, titulo: 'Como criar atividades', url: '', ativo: true },
                    { id: 2, titulo: 'Como usar o Simulado SAEB', url: '', ativo: true },
                    { id: 3, titulo: 'Como configurar o cabeçalho', url: '', ativo: true }
                  ]);
                  alert('🗑️ Todas as configurações foram resetadas!');
                }}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition flex items-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Resetar Tudo
              </button>
              <button 
                onClick={async () => {
                  if (!firebaseReady || !db || !storage) {
                    alert('⚠️ Firebase não disponível');
                    return;
                  }
                  
                  try {
                    const imagensParaSalvar = {};
                    
                    // Upload da logomarca
                    if (landingAssets.logomarca) {
                      const ref = storage.ref().child(`landing/logomarca_${Date.now()}`);
                      await ref.put(landingAssets.logomarca);
                      imagensParaSalvar.logomarca = await ref.getDownloadURL();
                    } else if (landingAssets.logomarcaPreview) {
                      imagensParaSalvar.logomarca = landingAssets.logomarcaPreview;
                    }
                    
                    // Upload da imagem da professora
                    if (landingAssets.professoraImg) {
                      const ref = storage.ref().child(`landing/professora_${Date.now()}`);
                      await ref.put(landingAssets.professoraImg);
                      imagensParaSalvar.professoraImg = await ref.getDownloadURL();
                    } else if (landingAssets.professoraImgPreview) {
                      imagensParaSalvar.professoraImg = landingAssets.professoraImgPreview;
                    }
                    
                    // Upload das atividades de exemplo (1-8)
                    for (let i = 1; i <= 8; i++) {
                      const key = `atividadeExemplo${i}`;
                      const previewKey = `${key}Preview`;
                      if (landingAssets[key]) {
                        const ref = storage.ref().child(`landing/${key}_${Date.now()}`);
                        await ref.put(landingAssets[key]);
                        imagensParaSalvar[key] = await ref.getDownloadURL();
                      } else if (landingAssets[previewKey]) {
                        imagensParaSalvar[key] = landingAssets[previewKey];
                      }
                    }
                    
                    // Salvar telefone e vídeos também
                    imagensParaSalvar.telefoneSuporte = telefoneSuporte;
                    imagensParaSalvar.videosTutoriais = videosTutoriais;
                    imagensParaSalvar.dataAtualizacao = new Date();
                    
                    // Salvar no Firestore
                    await db.collection('configuracoes').doc('landing').set(imagensParaSalvar, { merge: true });
                    
                    // Atualizar estado local
                    setLandingImagesFirebase(imagensParaSalvar);
                    
                    alert('✅ Configurações salvas com sucesso no Firebase!\n\nAs imagens, telefone de suporte e vídeos tutoriais serão exibidos em toda a aplicação.');
                  } catch (error) {
                    console.error('Erro ao salvar:', error);
                    alert('❌ Erro ao salvar: ' + error.message);
                  }
                }}
                className="px-6 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                Salvar Alterações
              </button>
            </div>

            {/* Preview */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="bg-gray-100 px-5 py-3 border-b">
                <h3 className="font-bold text-gray-700 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Pré-visualização do Header
                </h3>
              </div>
              <div className="p-4" style={{backgroundColor: '#0f2744'}}>
                <div className="flex items-center justify-between max-w-4xl mx-auto">
                  {/* Logo Preview */}
                  <div className="flex items-center gap-2">
                    {/* 🆕 v400: Sempre usa a imagem do logo */}
                    <img 
                      src={landingAssets.logomarcaPreview || landingImagesFirebase.logomarca || '/assets/logo.webp'} 
                      alt="Logo" 
                      className="h-12" 
                    />
                  </div>
                  {/* Menu Preview */}
                  <div className="flex gap-6 text-white text-sm">
                    <span>Vantagens</span>
                    <span>Disciplinas</span>
                    <span>Recargas</span>
                    <span>Dúvidas</span>
                  </div>
                  {/* Botões Preview */}
                  <div className="flex gap-2">
                    <span className="px-4 py-2 rounded-lg text-white text-sm" style={{backgroundColor: '#f7941d'}}>Cadastre-se</span>
                    <span className="px-4 py-2 rounded-lg text-white text-sm border border-white">Entrar</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 🔑 Card de Configuração de APIs Externas */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="bg-purple-600 px-5 py-3">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configuração de APIs Externas
                </h3>
                <p className="text-xs text-purple-100">Configure as chaves de API para geração de PDF</p>
              </div>
              <div className="p-5 space-y-4">
                
                {/* v706: pdfEndpoint.com - Geração de PDF (substitui html2pdfrocket.com) */}
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white">pdfEndpoint.com</h4>
                        <p className="text-xs text-gray-300">Geração de PDF a partir de HTML (navegador real)</p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${configCloudLayer.apiKey ? 'bg-green-500 text-white' : 'bg-yellow-500 text-yellow-900'}`}>
                      {configCloudLayer.apiKey ? '✓ Configurado' : '⚠ Não configurado'}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-300 mb-1">API Key</label>
                      <input
                        type="password"
                        value={configCloudLayer.apiKey}
                        onChange={(e) => setConfigCloudLayer({ ...configCloudLayer, apiKey: e.target.value })}
                        placeholder="pdfe_live_xxxxxxxxxxxxxxxxxxxxxxxxxx"
                        className="w-full px-3 py-2 rounded-lg bg-white/90 text-gray-800 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-end">
                      <a
                        href="https://pdfendpoint.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition flex items-center gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Obter Key
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    🔒 A chave é salva no Firestore e usada apenas pela Cloud Function (servidor) — nunca fica exposta no navegador.
                  </p>
                </div>

                {/* Google Cloud Vision - OCR */}
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white">Google Cloud Vision</h4>
                        <p className="text-xs text-gray-300">Leitura OCR de cartões-resposta</p>
                      </div>
                    </div>
                    <div className="px-3 py-1 rounded-full text-xs font-bold bg-green-500 text-white">
                      ✓ Via Cloud Function
                    </div>
                  </div>
                  <div className="bg-green-900/30 rounded-lg p-3">
                    <p className="text-xs text-green-300">
                      ✅ O Google Cloud Vision está configurado através da <strong>Firebase Cloud Function</strong>.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      A API Key está protegida no servidor. Nenhuma configuração adicional necessária.
                    </p>
                  </div>
                </div>

                {/* v706: Botão Salvar pdfEndpoint API */}
                <button
                  onClick={async () => {
                    if (!firebaseReady || !db) {
                      alert('⚠️ Firebase não disponível');
                      return;
                    }
                    
                    try {
                      await db.collection('configuracoes').doc('apis').set({
                        pdfendpointApiKey: configCloudLayer.apiKey || '',
                        dataAtualizacao: new Date()
                      }, { merge: true });
                      
                      alert('✅ API Key do pdfEndpoint.com salva com sucesso!');
                    } catch (error) {
                      console.error('Erro ao salvar APIs:', error);
                      alert('❌ Erro ao salvar: ' + error.message);
                    }
                  }}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl font-bold transition flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Salvar Configuração do pdfEndpoint.com
                </button>
              </div>
            </div>
          </div>
        )}

        {currentView === 'admin-questoes' && (
          <div className="space-y-6">
            {/* Filtros e Botão Upload */}
            <div className="rounded-xl p-6" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-white">Banco de Questões</h3>
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      setUploadLote({ modulo: '', serie: '', componente: '', habilidade: '', tamanho: 'media', questoes: [] });
                      setShowModalUploadLote(true);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
                  >
                    <Layers className="w-5 h-5" />
                    Upload em Lote (até 20)
                  </button>
                  <button 
                    onClick={() => {
                      setUploadQuestao({ modulo: '', serie: '', componente: '', habilidade: '', tamanho: 'media', gabarito: '', imagem: null, imagemFile: null });
                      setShowModalUploadQuestao(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                  >
                    <Upload className="w-5 h-5" />
                    Upload Individual
                  </button>
                </div>
              </div>
              
              {/* Filtros */}
              <div className="grid grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Módulo</label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    value={filtroQuestoesAdmin.modulo}
                    onChange={(e) => {
                      setFiltroQuestoesAdmin({...filtroQuestoesAdmin, modulo: e.target.value, componente: '', habilidadeId: ''});
                      setPaginaQuestoesAdmin(1);
                    }}
                  >
                    <option value="">Todos os módulos</option>
                    <option value="prontas">📚 Atividades Prontas</option>
                    <option value="questao_avaliacao">📝 Atividade/Avaliação</option>
                    <option value="simulado">🎯 Simulado SAEB</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Série</label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    value={filtroQuestoesAdmin.serie}
                    onChange={(e) => {
                      setFiltroQuestoesAdmin({...filtroQuestoesAdmin, serie: e.target.value, habilidadeId: ''});
                      setPaginaQuestoesAdmin(1);
                    }}
                  >
                    <option value="">Todas as séries</option>
                    {seriesDisponiveis.filter(s => s.ativo).map(s => (
                      <option key={s.id} value={s.nome}>{s.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Componente</label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    value={filtroQuestoesAdmin.componente}
                    onChange={(e) => {
                      setFiltroQuestoesAdmin({...filtroQuestoesAdmin, componente: e.target.value, habilidadeId: ''});
                      setPaginaQuestoesAdmin(1);
                    }}
                  >
                    <option value="">Todos</option>
                    {filtroQuestoesAdmin.modulo === 'simulado' ? (
                      // Mostrar componentes SAEB quando módulo for simulado
                      <>
                        <option value="Matemática SAEB">Matemática SAEB</option>
                        <option value="Língua Portuguesa SAEB">Língua Portuguesa SAEB</option>
                      </>
                    ) : (
                      // Mostrar componentes normais para outros módulos
                      componentesCurriculares.filter(c => c.ativo).map(c => (
                        <option key={c.id} value={c.nome}>{c.nome}</option>
                      ))
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Habilidade</label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    value={filtroQuestoesAdmin.habilidadeId || ''}
                    onChange={(e) => {
                      setFiltroQuestoesAdmin({...filtroQuestoesAdmin, habilidadeId: e.target.value});
                      setPaginaQuestoesAdmin(1);
                    }}
                  >
                    <option value="">Todas</option>
                    {habilidades
                      .filter(h => {
                        // Se módulo é 'simulado', mostrar apenas habilidades com componente SAEB
                        if (filtroQuestoesAdmin.modulo === 'simulado') {
                          return h.componente?.includes('SAEB');
                        }
                        // Se módulo é outro ou vazio, mostrar habilidades sem SAEB no componente
                        if (filtroQuestoesAdmin.modulo && filtroQuestoesAdmin.modulo !== 'simulado') {
                          return !h.componente?.includes('SAEB');
                        }
                        // Se não tem módulo selecionado, mostrar todas
                        return true;
                      })
                      .filter(h => !filtroQuestoesAdmin.serie || h.serie === filtroQuestoesAdmin.serie)
                      .filter(h => !filtroQuestoesAdmin.componente || h.componente === filtroQuestoesAdmin.componente)
                      .map(h => (
                        <option key={h.id} value={h.id}>({h.codigo}) - {h.conteudo}</option>
                      ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button 
                    onClick={() => {
                      // v667: Dar permissão e buscar questões
                      setPermitirCarregamentoQuestoes(true);
                      setReloadTrigger(prev => prev + 1);
                    }}
                    className="w-full px-3 py-2 rounded-lg text-sm text-white font-semibold hover:opacity-90 transition"
                    style={{backgroundColor: '#f7941d'}}
                    disabled={!filtroQuestoesAdmin.modulo && !filtroQuestoesAdmin.serie && !filtroQuestoesAdmin.componente && !filtroQuestoesAdmin.habilidadeId}
                  >
                    🔍 Buscar Questões
                  </button>
                </div>
              </div>
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-blue-600">{questoes.length}</div>
                <div className="text-sm text-blue-700">Total de Questões</div>
              </div>
              <div className="bg-green-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-green-600">
                  {questoes.filter(q => q.modulo === 'prontas').length}
                </div>
                <div className="text-sm text-green-700">Atividades Prontas</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-purple-600">
                  {questoes.filter(q => q.modulo === 'questao_avaliacao').length}
                </div>
                <div className="text-sm text-purple-700">Atividade/Avaliação</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-orange-600">
                  {questoes.filter(q => q.modulo === 'simulado').length}
                </div>
                <div className="text-sm text-orange-700">Simulado SAEB</div>
              </div>
            </div>

            {/* Lista de Questões */}
            <div className="rounded-xl p-6" style={{ backgroundColor: '#1e3a5f' }}>
              {/* Barra de ações em lote */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-400">
                    Exibindo {questoes.filter(q => 
                      (!filtroQuestoesAdmin.modulo || q.modulo === filtroQuestoesAdmin.modulo) &&
                      (!filtroQuestoesAdmin.serie || q.serie === filtroQuestoesAdmin.serie) &&
                      (!filtroQuestoesAdmin.componente || q.componente === filtroQuestoesAdmin.componente) &&
                      (!filtroQuestoesAdmin.habilidadeId || q.habilidadeId === filtroQuestoesAdmin.habilidadeId)
                    ).length} questões
                  </div>
                  {questoesSelecionadasLote.length > 0 && (
                    <span className="text-sm text-orange-400 font-semibold">
                      {questoesSelecionadasLote.length} selecionada(s)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {questoesSelecionadasLote.length > 0 && (
                    <>
                      <button
                        onClick={() => setQuestoesSelecionadasLote([])}
                        className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
                      >
                        Limpar seleção
                      </button>
                      <button
                        onClick={excluirQuestoesEmLote}
                        className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-1 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        Excluir {questoesSelecionadasLote.length} questão(ões)
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      const questoesFiltradas = questoes.filter(q => 
                        (!filtroQuestoesAdmin.modulo || q.modulo === filtroQuestoesAdmin.modulo) &&
                        (!filtroQuestoesAdmin.serie || q.serie === filtroQuestoesAdmin.serie) &&
                        (!filtroQuestoesAdmin.componente || q.componente === filtroQuestoesAdmin.componente) &&
                        (!filtroQuestoesAdmin.habilidadeId || q.habilidadeId === filtroQuestoesAdmin.habilidadeId)
                      );
                      if (questoesSelecionadasLote.length === questoesFiltradas.length) {
                        setQuestoesSelecionadasLote([]);
                      } else {
                        setQuestoesSelecionadasLote(questoesFiltradas.map(q => q.id));
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                  >
                    {questoesSelecionadasLote.length === questoes.filter(q => 
                      (!filtroQuestoesAdmin.modulo || q.modulo === filtroQuestoesAdmin.modulo) &&
                      (!filtroQuestoesAdmin.serie || q.serie === filtroQuestoesAdmin.serie) &&
                      (!filtroQuestoesAdmin.componente || q.componente === filtroQuestoesAdmin.componente) &&
                      (!filtroQuestoesAdmin.habilidadeId || q.habilidadeId === filtroQuestoesAdmin.habilidadeId)
                    ).length ? 'Desmarcar todas' : 'Selecionar todas'}
                  </button>
                </div>
              </div>
              
              {/* v554: Mostrar mensagem se nenhum filtro selecionado */}
              {!filtroQuestoesAdmin.modulo && !filtroQuestoesAdmin.serie && !filtroQuestoesAdmin.componente && !filtroQuestoesAdmin.habilidadeId ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
                  <FileText className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-blue-700 mb-2">Selecione um filtro para ver as questões</p>
                  <p className="text-sm text-blue-500">Use os filtros acima para buscar questões por módulo, série, componente ou habilidade</p>
                </div>
              ) : (
              <>
              <div className="grid grid-cols-4 gap-4">
                {(() => {
                  const questoesFiltradas = questoes.filter(q => 
                    (!filtroQuestoesAdmin.modulo || q.modulo === filtroQuestoesAdmin.modulo) &&
                    (!filtroQuestoesAdmin.serie || q.serie === filtroQuestoesAdmin.serie) &&
                    (!filtroQuestoesAdmin.componente || q.componente === filtroQuestoesAdmin.componente) &&
                    (!filtroQuestoesAdmin.habilidadeId || q.habilidadeId === filtroQuestoesAdmin.habilidadeId)
                  );
                  const inicio = (paginaQuestoesAdmin - 1) * QUESTOES_POR_PAGINA;
                  const questoesPaginadas = questoesFiltradas.slice(inicio, inicio + QUESTOES_POR_PAGINA);
                  
                  return questoesPaginadas.map(q => (
                  <div key={q.id} className={`border rounded-lg p-4 hover:shadow-md transition relative ${
                    questoesSelecionadasLote.includes(q.id) 
                      ? 'border-2 border-orange-500 bg-orange-50 ring-2 ring-orange-300' 
                      : q.tamanho === 'texto-multiplas' 
                        ? 'border-2 border-orange-400 bg-orange-50' 
                        : 'bg-white'
                  }`}>
                    {/* Checkbox de seleção */}
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        checked={questoesSelecionadasLote.includes(q.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setQuestoesSelecionadasLote(prev => [...prev, q.id]);
                          } else {
                            setQuestoesSelecionadasLote(prev => prev.filter(id => String(id) !== String(q.id)));
                          }
                        }}
                        className="w-5 h-5 rounded border-2 border-gray-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
                      />
                    </div>
                    
                    <div 
                      onClick={() => setQuestaoAdminVisualizacao(q)}
                      className={`rounded h-32 flex items-center justify-center mb-3 relative overflow-hidden cursor-pointer hover:opacity-90 transition ${q.tamanho === 'texto-multiplas' ? 'bg-orange-100' : 'bg-gray-100'}`}
                    >
                      {/* v554: Placeholder em vez de carregar imagem - só carrega ao clicar */}
                      {q.tamanho === 'texto-multiplas' ? (
                        <div className="text-center">
                          <Layers className="w-8 h-8 text-orange-500 mx-auto mb-1" />
                          <span className="text-xs font-bold text-orange-600">TEXTO + {q.questoesVinculadas?.length || 0} QUESTÕES</span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Eye className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                          <span className="text-xs text-gray-500">Clique para ver</span>
                        </div>
                      )}
                      <span className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full font-semibold ${
                        q.tamanho === 'texto-multiplas' ? 'bg-orange-500 text-white' :
                        q.tamanho === 'pequena' ? 'bg-green-200 text-green-800' :
                        q.tamanho === 'media' ? 'bg-blue-200 text-blue-800' :
                        q.tamanho === 'grande' ? 'bg-purple-200 text-purple-800' :
                        q.tamanho === 'inteira' ? 'bg-red-200 text-red-800' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {q.tamanho === 'texto-multiplas' ? 'MÚLT' :
                         q.tamanho === 'pequena' ? 'P' :
                         q.tamanho === 'media' ? 'M' :
                         q.tamanho === 'grande' ? 'G' :
                         q.tamanho === 'inteira' ? 'I' : q.tamanho}
                      </span>
                    </div>
                    <div className="flex gap-1 mb-2 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-1 rounded ${
                        q.modulo === 'prontas' ? 'bg-green-100 text-green-700' :
                        q.modulo === 'simulado' ? 'bg-orange-100 text-orange-700' :
                        q.tamanho === 'texto-multiplas' ? 'bg-orange-100 text-orange-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {q.modulo === 'prontas' ? '📚 Prontas' : 
                         q.modulo === 'simulado' ? '🎯 Simulado' : 
                         q.tamanho === 'texto-multiplas' ? '🎯 Simulado' : '📝 Ativ/Aval'}
                      </span>
                    </div>
                    <div className="text-xs font-semibold text-blue-600 mb-1">{q.serie}</div>
                    <div className="text-sm font-semibold text-gray-800">{q.componente}</div>
                    {q.tamanho === 'texto-multiplas' ? (
                      <div className="text-xs text-gray-500">
                        Habilidades: {q.questoesVinculadas?.map(qv => qv.habilidade).join(', ')}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 truncate">{getCodigoHabilidadeAtualizado(q)}</div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      {q.tamanho === 'texto-multiplas' ? (
                        <div className="text-xs font-bold text-orange-600">
                          Gab: {q.questoesVinculadas?.map(qv => qv.gabarito).join(', ')}
                        </div>
                      ) : q.modulo === 'simulado' ? (
                        /* Simulado SAEB - apenas alternativas A, B, C, D */
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-bold text-gray-600">Gab:</span>
                          <select
                            value={q.gabarito || '-'}
                            onChange={(e) => atualizarGabaritoQuestao(q.id, e.target.value)}
                            className="text-sm font-bold text-green-600 bg-green-50 border border-green-300 rounded px-2 py-0.5 cursor-pointer hover:bg-green-100 focus:ring-2 focus:ring-green-400"
                          >
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                            <option value="D">D</option>
                            <option value="-">-</option>
                          </select>
                        </div>
                      ) : (
                        /* Outros módulos - aceita respostas subjetivas */
                        <div className="flex items-center gap-1 flex-1 mr-2">
                          <span className="text-sm font-bold text-gray-600 flex-shrink-0">Gab:</span>
                          <input
                            type="text"
                            value={q.gabarito || ''}
                            onChange={(e) => atualizarGabaritoQuestao(q.id, e.target.value)}
                            placeholder="A, B, C, D ou texto..."
                            className="text-sm font-bold text-green-600 bg-green-50 border border-green-300 rounded px-2 py-0.5 w-full max-w-[120px] focus:ring-2 focus:ring-green-400 focus:outline-none hover:bg-green-100"
                            title="Digite A, B, C, D ou uma resposta subjetiva"
                          />
                        </div>
                      )}
                      <div className="flex gap-1 flex-shrink-0">
                        <button 
                          onClick={() => setQuestaoAdminVisualizacao(q)}
                          className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                          title="Visualizar questão"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={async () => {
                            if (window.confirm('Tem certeza que deseja excluir esta questão?')) {
                              try {
                                if (firebaseReady && db) {
                                  // Excluir do Firebase (soft delete)
                                  await db.collection('questoes').doc(q.id).update({ ativo: false });
                                  // Remover da lista local
                                  setQuestoesFirebase(prev => prev.filter(questao => questao.id !== q.id));
                                  alert('✅ Questão excluída com sucesso!');
                                } else {
                                  alert('⚠️ Firebase não disponível');
                                }
                              } catch (error) {
                                console.error('Erro ao excluir:', error);
                                alert('❌ Erro ao excluir: ' + error.message);
                              }
                            }
                          }}
                          className="p-1.5 hover:bg-red-50 rounded text-red-600"
                          title="Excluir questão"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
                })()}
              </div>
              
              {/* v554: Paginação */}
              {(filtroQuestoesAdmin.modulo || filtroQuestoesAdmin.serie || filtroQuestoesAdmin.componente || filtroQuestoesAdmin.habilidadeId) && (() => {
                const questoesFiltradas = questoes.filter(q => 
                  (!filtroQuestoesAdmin.modulo || q.modulo === filtroQuestoesAdmin.modulo) &&
                  (!filtroQuestoesAdmin.serie || q.serie === filtroQuestoesAdmin.serie) &&
                  (!filtroQuestoesAdmin.componente || q.componente === filtroQuestoesAdmin.componente) &&
                  (!filtroQuestoesAdmin.habilidadeId || q.habilidadeId === filtroQuestoesAdmin.habilidadeId)
                );
                const totalPaginas = Math.ceil(questoesFiltradas.length / QUESTOES_POR_PAGINA);
                
                if (totalPaginas <= 1) return null;
                
                return (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                      onClick={() => setPaginaQuestoesAdmin(p => Math.max(1, p - 1))}
                      disabled={paginaQuestoesAdmin === 1}
                      className={`px-4 py-2 rounded-lg font-semibold ${paginaQuestoesAdmin === 1 ? 'bg-gray-200 text-gray-400' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                    >
                      ← Anterior
                    </button>
                    <span className="px-4 py-2 text-white font-semibold">
                      Página {paginaQuestoesAdmin} de {totalPaginas}
                    </span>
                    <button
                      onClick={() => setPaginaQuestoesAdmin(p => Math.min(totalPaginas, p + 1))}
                      disabled={paginaQuestoesAdmin === totalPaginas}
                      className={`px-4 py-2 rounded-lg font-semibold ${paginaQuestoesAdmin === totalPaginas ? 'bg-gray-200 text-gray-400' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                    >
                      Próxima →
                    </button>
                  </div>
                );
              })()}
              </>
              )}
              
              {(filtroQuestoesAdmin.modulo || filtroQuestoesAdmin.serie || filtroQuestoesAdmin.componente || filtroQuestoesAdmin.habilidadeId) && questoes.filter(q => 
                (!filtroQuestoesAdmin.modulo || q.modulo === filtroQuestoesAdmin.modulo) &&
                (!filtroQuestoesAdmin.serie || q.serie === filtroQuestoesAdmin.serie) &&
                (!filtroQuestoesAdmin.componente || q.componente === filtroQuestoesAdmin.componente) &&
                (!filtroQuestoesAdmin.habilidadeId || q.habilidadeId === filtroQuestoesAdmin.habilidadeId)
              ).length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Nenhuma questão encontrada com os filtros selecionados</p>
                </div>
              )}
            </div>
          </div>
        )}

        {currentView === 'admin-habilidades' && (
          <div className="rounded-xl p-6" style={{ backgroundColor: '#1e3a5f' }}>
            <div className="flex justify-between mb-6">
              <div className="flex gap-3">
                <select 
                  className="px-4 py-2 border rounded-lg bg-white"
                  onChange={(e) => setFiltros({...filtros, serie: e.target.value, componente: ''})}
                  value={filtros.serie}
                >
                  <option value="">Selecione uma Série</option>
                  <option>1º ano</option>
                  <option>2º ano</option>
                  <option>3º ano</option>
                  <option>4º ano</option>
                  <option>5º ano</option>
                  <option>6º ano</option>
                  <option>7º ano</option>
                  <option>8º ano</option>
                  <option>9º ano</option>
                </select>
                <select 
                  className={`px-4 py-2 border rounded-lg ${!filtros.serie ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'}`}
                  onChange={(e) => setFiltros({...filtros, componente: e.target.value})}
                  disabled={!filtros.serie}
                  value={filtros.componente}
                >
                  <option value="">Selecione um Componente</option>
                  {componentesCurriculares.filter(c => c.ativo).map(c => (
                    <option key={c.id} value={c.nome}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <button 
                onClick={() => {
                  setNovaHabilidade({ codigo: '', conteudo: '', serie: '', componente: '' });
                  setHabilidadeEditando(null);
                  setShowModalHabilidade(true);
                }}
                className="text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90" style={{backgroundColor: '#f7941d'}}
              >
                <Plus className="w-5 h-5" />
                Nova Habilidade
              </button>
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4">Código</th>
                  <th className="text-left py-3 px-4">Conteúdo</th>
                  <th className="text-left py-3 px-4">Série</th>
                  <th className="text-left py-3 px-4">Componente</th>
                  <th className="text-right py-3 px-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {habilidades
                  .filter(h => 
                    (!filtros.serie || h.serie === filtros.serie) &&
                    (!filtros.componente || h.componente === filtros.componente)
                  )
                  .map(h => (
                  <tr key={h.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="font-semibold text-blue-600">{h.codigo}</span>
                    </td>
                    <td className="py-3 px-4">{h.conteudo}</td>
                    <td className="py-3 px-4">{h.serie}</td>
                    <td className="py-3 px-4">{h.componente}</td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => {
                            setNovaHabilidade({ codigo: h.codigo, conteudo: h.conteudo, serie: h.serie, componente: h.componente });
                            setHabilidadeEditando(h);
                            setShowModalHabilidade(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={async () => {
                            if (window.confirm(`Deseja excluir a habilidade ${h.codigo}?`)) {
                              try {
                                if (firebaseReady && db) {
                                  await db.collection('habilidades').doc(h.id).delete();
                                  setHabilidadesFirebase(prev => prev.filter(hab => hab.id !== h.id));
                                  alert('✅ Habilidade excluída com sucesso!');
                                } else {
                                  alert('⚠️ Firebase não disponível');
                                }
                              } catch (error) {
                                console.error('Erro ao excluir:', error);
                                alert('❌ Erro ao excluir: ' + error.message);
                              }
                            }
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Modal de Habilidade */}
            {showModalHabilidade && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl p-6 w-full max-w-lg">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold">
                      {habilidadeEditando ? 'Editar Habilidade' : 'Nova Habilidade'}
                    </h3>
                    <button onClick={() => setShowModalHabilidade(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Código da Habilidade <span className="text-red-600">*</span>
                      </label>
                      <input 
                        type="text" 
                        placeholder="Ex: EF05MA08"
                        value={novaHabilidade.codigo}
                        onChange={(e) => setNovaHabilidade({...novaHabilidade, codigo: e.target.value})}
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conteúdo <span className="text-red-600">*</span>
                      </label>
                      <input 
                        type="text" 
                        placeholder="Ex: Frações - Conceitos básicos"
                        value={novaHabilidade.conteudo}
                        onChange={(e) => setNovaHabilidade({...novaHabilidade, conteudo: e.target.value})}
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Série <span className="text-red-600">*</span>
                        </label>
                        <select 
                          value={novaHabilidade.serie}
                          onChange={(e) => setNovaHabilidade({...novaHabilidade, serie: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Selecione</option>
                          {seriesDisponiveis.map(s => (
                            <option key={s.id} value={s.nome}>{s.nome}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Componente <span className="text-red-600">*</span>
                        </label>
                        <select 
                          value={novaHabilidade.componente}
                          onChange={(e) => setNovaHabilidade({...novaHabilidade, componente: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Selecione</option>
                          {componentesCurriculares.map(c => (
                            <option key={c.id} value={c.nome}>{c.nome}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button 
                      onClick={() => setShowModalHabilidade(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button 
                      onClick={async () => {
                        if (!novaHabilidade.codigo || !novaHabilidade.conteudo || !novaHabilidade.serie || !novaHabilidade.componente) {
                          alert('Preencha todos os campos obrigatórios!');
                          return;
                        }
                        
                        try {
                          if (habilidadeEditando) {
                            // EDITAR habilidade existente
                            if (firebaseReady && db) {
                              await db.collection('habilidades').doc(habilidadeEditando.id).update({
                                codigo: novaHabilidade.codigo,
                                conteudo: novaHabilidade.conteudo,
                                serie: novaHabilidade.serie,
                                componente: novaHabilidade.componente
                              });
                              // Atualizar lista local
                              setHabilidadesFirebase(prev => prev.map(h => 
                                h.id === habilidadeEditando.id 
                                  ? {...h, codigo: novaHabilidade.codigo, conteudo: novaHabilidade.conteudo, serie: novaHabilidade.serie, componente: novaHabilidade.componente}
                                  : h
                              ));
                              alert('✅ Habilidade atualizada com sucesso!');
                            }
                          } else {
                            // CRIAR nova habilidade
                            if (firebaseReady && db) {
                              const novaHab = {
                                codigo: novaHabilidade.codigo,
                                conteudo: novaHabilidade.conteudo,
                                serie: novaHabilidade.serie,
                                componente: novaHabilidade.componente,
                                dataCriacao: new Date()
                              };
                              const docRef = await db.collection('habilidades').add(novaHab);
                              // Adicionar à lista local
                              setHabilidadesFirebase(prev => [...prev, { id: docRef.id, ...novaHab }]);
                              alert('✅ Habilidade criada com sucesso!');
                            } else {
                              alert('⚠️ Firebase não disponível');
                            }
                          }
                          
                          setShowModalHabilidade(false);
                          setHabilidadeEditando(null);
                          setNovaHabilidade({ codigo: '', conteudo: '', serie: '', componente: '' });
                        } catch (error) {
                          console.error('Erro ao salvar habilidade:', error);
                          alert('❌ Erro ao salvar: ' + error.message);
                        }
                      }}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {habilidadeEditando ? 'Salvar Alterações' : 'Adicionar Habilidade'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🆕 PÁGINA ADMIN - GESTÃO DE SÉRIES */}
        {currentView === 'admin-series' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Séries Disponíveis</h2>
                <p className="text-sm text-gray-300">Gerencie as séries disponíveis para os módulos de atividades</p>
              </div>
              <button 
                onClick={() => {
                  setSerieEditando(null);
                  setShowModalSerie(true);
                }}
                className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2 hover:opacity-90"
                style={{backgroundColor: '#f7941d'}}
              >
                <Plus className="w-5 h-5" />
                Nova Série
              </button>
            </div>
            
            <div className="rounded-xl overflow-hidden p-6" style={{ backgroundColor: '#1e3a5f' }}>
              <table className="w-full">
                <thead style={{ backgroundColor: '#0a1929' }}>
                  <tr>
                    <th className="text-left p-4 font-semibold text-blue-300">ID</th>
                    <th className="text-left p-4 font-semibold text-blue-300">Nome da Série</th>
                    <th className="text-left p-4 font-semibold text-blue-300">Status</th>
                    <th className="text-right p-4 font-semibold text-blue-300">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {seriesDisponiveis.map(serie => (
                    <tr key={serie.id} className="hover:bg-gray-800">
                      <td className="p-4 text-gray-400">{serie.id}</td>
                      <td className="p-4 font-semibold text-white">{serie.nome}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          serie.ativo ? 'bg-green-900 text-green-300 border border-green-500' : 'bg-red-900 text-red-300 border border-red-500'
                        }`}>
                          {serie.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <button 
                          onClick={() => {
                            setSerieEditando(serie);
                            setShowModalSerie(true);
                          }}
                          className="px-4 py-2 text-sm font-semibold rounded-lg text-blue-400 hover:bg-blue-900"
                        >
                          <Edit className="w-4 h-4 inline mr-1" />
                          Editar
                        </button>
                        <button 
                          onClick={async () => {
                            try {
                              if (firebaseReady && db) {
                                const novoStatus = !serie.ativo;
                                await db.collection('series').doc(serie.id).update({ ativo: novoStatus });
                                setSeriesFirebase(prev => prev.map(s => 
                                  s.id === serie.id ? {...s, ativo: novoStatus} : s
                                ));
                              }
                            } catch (error) {
                              console.error('Erro ao atualizar série:', error);
                              alert('❌ Erro ao atualizar: ' + error.message);
                            }
                          }}
                          className={`px-4 py-2 text-sm font-semibold rounded-lg ml-2 ${
                            serie.ativo ? 'text-red-400 hover:bg-red-900' : 'text-green-400 hover:bg-green-900'
                          }`}
                        >
                          {serie.ativo ? 'Desativar' : 'Ativar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 🆕 PÁGINA ADMIN - GESTÃO DE COMPONENTES CURRICULARES */}
        {currentView === 'admin-componentes' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Componentes Curriculares</h2>
                <p className="text-sm text-gray-300">Gerencie os componentes curriculares disponíveis para os módulos de atividades</p>
              </div>
              <button 
                onClick={() => {
                  setComponenteEditando(null);
                  setShowModalComponente(true);
                }}
                className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2 hover:opacity-90"
                style={{backgroundColor: '#f7941d'}}
              >
                <Plus className="w-5 h-5" />
                Novo Componente
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              {componentesCurriculares.map(comp => (
                <div key={comp.id} className={`p-4 rounded-xl border-2 ${comp.ativo ? 'border-gray-600' : 'border-red-500 opacity-60'}`} style={{ backgroundColor: '#1e3a5f' }}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-lg text-white">{comp.nome}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      comp.ativo ? 'bg-green-900 text-green-300 border border-green-500' : 'bg-red-900 text-red-300 border border-red-500'
                    }`}>
                      {comp.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        setComponenteEditando(comp);
                        setShowModalComponente(true);
                      }}
                      className="flex-1 py-2 text-sm font-semibold rounded-lg border border-blue-400 text-blue-300 hover:bg-blue-900"
                    >
                      <Edit className="w-4 h-4 inline mr-1" />
                      Editar
                    </button>
                    <button 
                      onClick={async () => {
                        try {
                          if (firebaseReady && db) {
                            const novoStatus = !comp.ativo;
                            await db.collection('componentes').doc(comp.id).update({ ativo: novoStatus });
                            setComponentesFirebase(prev => prev.map(c => 
                              c.id === comp.id ? {...c, ativo: novoStatus} : c
                            ));
                          }
                        } catch (error) {
                          console.error('Erro ao atualizar componente:', error);
                          alert('❌ Erro ao atualizar: ' + error.message);
                        }
                      }}
                      className={`flex-1 py-2 text-sm font-semibold rounded-lg ${
                        comp.ativo ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-green-900 text-green-300 hover:bg-green-800'
                      }`}
                    >
                      {comp.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentView === 'admin-recargas' && (
          <div>
            {/* v694: Ferramenta de reprocessamento manual de crédito travado */}
            <div className="mb-6 p-4 rounded-xl border-2 border-yellow-500" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="text-sm font-bold text-yellow-400 mb-2">🛠️ Reprocessar Crédito de Pedido</h3>
              <p className="text-xs text-gray-300 mb-3">
                Use quando um pedido aparece como "Pago" no Pagar.me mas o cliente não recebeu os créditos.
                Cole o ID do pedido (ex: or_xxxxxxxx) abaixo. Seguro chamar mais de uma vez — não credita em duplicidade.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="reprocessar-pedido-id"
                  placeholder="ID do pedido (or_...)"
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-500 bg-gray-800 text-white text-sm"
                />
                <button
                  onClick={async () => {
                    const pedidoId = document.getElementById('reprocessar-pedido-id').value.trim();
                    if (!pedidoId) {
                      alert('Informe o ID do pedido.');
                      return;
                    }
                    try {
                      const functionsRef = window.firebase.functions();
                      const reprocessar = functionsRef.httpsCallable('reprocessarCreditoPedido');
                      const resp = await reprocessar({ pedidoId });
                      const r = resp.data.resultado;
                      if (r.sucesso) {
                        alert(`✅ Crédito aplicado!\nUsuário: ${r.userId}\n+${r.paginas} créditos\nPlano: ${r.pacoteNome || 'não alterado'}`);
                      } else if (r.motivo === 'ja_creditado') {
                        alert('ℹ️ Esse pedido já havia sido creditado anteriormente. Nada foi alterado.');
                      } else {
                        alert(`⚠️ Não foi possível creditar.\nMotivo: ${r.motivo}`);
                      }
                    } catch (err) {
                      console.error('Erro ao reprocessar crédito:', err);
                      alert('❌ Erro ao reprocessar: ' + (err.message || 'erro desconhecido'));
                    }
                  }}
                  className="px-4 py-2 rounded-lg font-semibold text-white hover:opacity-90"
                  style={{ backgroundColor: '#f7941d' }}
                >
                  Reprocessar
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Pacotes de Recarga</h2>
                <p className="text-sm text-gray-300">Gerencie os pacotes disponíveis para professores e escolas</p>
              </div>
              <button 
                onClick={() => {
                  setRecargaEditando(null);
                  setShowModalRecarga(true);
                }}
                className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2 hover:opacity-90"
                style={{backgroundColor: '#f7941d'}}
              >
                <Plus className="w-5 h-5" />
                Novo Pacote
              </button>
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              {recargas.map(r => (
                <div key={r.id} className={`p-5 rounded-xl border-2 relative ${!r.ativo ? 'border-red-500 opacity-60' : r.popular ? 'border-orange-400' : 'border-gray-600'}`} style={{ backgroundColor: '#1e3a5f' }}>
                  {r.popular && r.ativo && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white" style={{backgroundColor: '#f7941d'}}>
                      MAIS POPULAR
                    </div>
                  )}
                  {!r.ativo && (
                    <span className="inline-block bg-red-900 text-red-300 border border-red-500 text-xs px-2 py-1 rounded-full mb-2">Inativo</span>
                  )}
                  <h3 className="text-xl font-bold mb-2 text-white">{r.nome}</h3>
                  <p className="text-xs text-gray-400 mb-3 min-h-[40px]">{r.descricao}</p>
                  <div className="text-3xl font-bold mb-3" style={{color: '#f7941d'}}>
                    R$ {r.preco.toFixed(2).replace('.', ',')}
                  </div>
                  <div className="space-y-1 text-sm text-gray-300 mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>{r.paginas.toLocaleString()} Créditos <span className="text-xs text-gray-400">(válidos por 1 ano)</span></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>{r.series} {r.series === 1 ? 'série' : 'séries'}</span>
                    </div>
                    {r.vantagens && r.vantagens.map((vantagem, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span>{vantagem}</span>
                      </div>
                    ))}
                  </div>
                  <button 
                    onClick={() => {
                      setRecargaEditando(r);
                      setShowModalRecarga(true);
                    }}
                    className="w-full py-2 rounded-lg font-semibold border border-blue-400 text-blue-300 hover:bg-blue-900 transition text-sm"
                  >
                    <Edit className="w-4 h-4 inline mr-2" />
                    Editar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 🆕 PÁGINA ESCOLA - CORREÇÃO/DESEMPENHO SIMULADOS */}
        {currentView === 'escola-desempenho' && (
          <div>
            {viewDesempenho === 'lista' ? (
              <>
                {/* ⚠️ Aviso importante */}
                <div className="mb-4 md:mb-6 p-3 md:p-4 rounded-xl flex items-start gap-2 md:gap-3" style={{ backgroundColor: '#1e3a5f', border: '2px solid #f7941d' }}>
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-4 h-4 md:w-6 md:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-orange-300 mb-1 text-xs md:text-base">⚠️ Importante</h4>
                    <p className="text-[10px] md:text-sm text-gray-300">
                      Para correção, crie a turma e cadastre os <strong className="text-white">alunos</strong> usando a seção <strong className="text-orange-300">"Gestão de Turmas"</strong> abaixo.
                    </p>
                  </div>
                </div>

                {/* Cards de Acesso - v496: Ordem diferente desktop/mobile */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-6">
                  {/* Card 1 - Correção de Simulados - Aparece primeiro no mobile, terceiro no desktop */}
                  <div 
                    onClick={() => {
                      // v496: No desktop, não abre a correção (só funciona no mobile)
                      if (window.innerWidth >= 768) {
                        return; // Não faz nada no desktop
                      }
                      setViewDesempenho('correcao');
                    }}
                    className="rounded-xl p-4 md:p-6 hover:shadow-lg transition cursor-pointer border-2 border-orange-500 bg-white md:order-3"
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Camera className="w-6 h-6 md:w-8 md:h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2 text-gray-800">Correção de Simulados</h3>
                        {/* v496: Texto diferente no mobile e desktop */}
                        <p className="text-xs text-gray-600 mb-2 md:hidden">Clique aqui para corrigir os Simulados SAEB!</p>
                        <p className="text-xs text-gray-500 mb-4 hidden md:block">Essa funcionalidade só está ativa pelo Smartphone! Acesse facilitaprof.com.br pelo seu Smartphone para corrigir Simulados SAEB.</p>
                        <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-orange-500 text-white rounded-full font-semibold">📷 Correção</span>
                      </div>
                      <Eye className="w-5 h-5 md:w-6 md:h-6 text-orange-500 hidden md:block" />
                    </div>
                  </div>

                  {/* Card 2 - Planilha de Resultados (renomeado) - Aparece segundo no mobile, primeiro no desktop */}
                  <div 
                    onClick={() => {
                      // v689d: Pré-definir série da primeira turma real
                      if (!configSimulado.serie && turmas.length > 0) {
                        const primeiraTurma = turmas[0];
                        setConfigSimulado(prev => ({...prev, serie: primeiraTurma.serie, turma: primeiraTurma.turma || '', turmaId: primeiraTurma.id, turmaNome: primeiraTurma.nome || `${primeiraTurma.serie} ${primeiraTurma.turma || ''}`}));
                      }
                      setViewDesempenho('planilha');
                    }}
                    className="rounded-xl p-4 md:p-6 hover:shadow-lg transition cursor-pointer border-2 border-green-500 bg-white md:order-1"
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                        <FileText className="w-6 h-6 md:w-8 md:h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2 text-gray-800">Planilha de Resultados</h3>
                        <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden md:block">Clique aqui para visualizar os resultados em tabela(s) detalhada(s)!</p>
                        <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-green-600 text-white rounded-full font-semibold">📊 Análise</span>
                      </div>
                      <Eye className="w-5 h-5 md:w-6 md:h-6 text-green-600 hidden md:block" />
                    </div>
                  </div>

                  {/* Card 3 - Gráficos e Padrões de Desempenho (renomeado) - Aparece terceiro no mobile, segundo no desktop */}
                  <div 
                    onClick={() => {
                      // v689d: Pré-definir série da primeira turma real
                      if (!configSimulado.serie && turmas.length > 0) {
                        const primeiraTurma = turmas[0];
                        setConfigSimulado(prev => ({...prev, serie: primeiraTurma.serie, turma: primeiraTurma.turma || '', turmaId: primeiraTurma.id, turmaNome: primeiraTurma.nome || `${primeiraTurma.serie} ${primeiraTurma.turma || ''}`}));
                      }
                      setViewDesempenho('grafico');
                    }}
                    className="rounded-xl p-4 md:p-6 hover:shadow-lg transition cursor-pointer border-2 border-blue-500 bg-white md:order-2"
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base md:text-lg font-bold mb-1 md:mb-2 text-gray-800">Gráficos e Padrões de Desempenho</h3>
                        <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden md:block">Clique aqui para ver gráfico(s) de desempenho por Habilidades e Descritores!</p>
                        <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-blue-600 text-white rounded-full font-semibold">📈 Visualização</span>
                      </div>
                      <Eye className="w-5 h-5 md:w-6 md:h-6 text-blue-600 hidden md:block" />
                    </div>
                  </div>
                </div>

                {/* Resumo Geral - v520: Usar dados reais do Firebase */}
                <div className="p-3 md:p-6 rounded-xl mb-4 md:mb-6" style={{ backgroundColor: '#1e3a5f' }}>
                  <h3 className="font-bold text-sm md:text-lg mb-3 md:mb-4 text-white">Resumo dos Simulados</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                    <div className="bg-blue-900 bg-opacity-50 p-2 md:p-4 rounded-xl text-center border border-blue-500">
                      <div className="text-xl md:text-3xl font-bold text-blue-300">{turmas.length}</div>
                      <div className="text-[10px] md:text-sm text-gray-300">Turmas</div>
                    </div>
                    <div className="bg-green-900 bg-opacity-50 p-2 md:p-4 rounded-xl text-center border border-green-500">
                      <div className="text-xl md:text-3xl font-bold text-green-300">{simuladosFirebase.length}</div>
                      <div className="text-[10px] md:text-sm text-gray-300">Simulados</div>
                    </div>
                    <div className="bg-purple-900 bg-opacity-50 p-2 md:p-4 rounded-xl text-center border border-purple-500">
                      <div className="text-xl md:text-3xl font-bold text-purple-300">{turmas.reduce((acc, t) => acc + (t.alunos?.length || 0), 0)}</div>
                      <div className="text-[10px] md:text-sm text-gray-300">Alunos</div>
                    </div>
                    <div className="bg-orange-900 bg-opacity-50 p-2 md:p-4 rounded-xl text-center border border-orange-500">
                      <div className="text-xl md:text-3xl font-bold text-orange-300">{(() => {
                        // Calcular média real de acertos dos simulados
                        let totalAcertos = 0;
                        let totalRespostas = 0;
                        simuladosFirebase.forEach(sim => {
                          if (sim.respostas && sim.gabarito) {
                            Object.values(sim.respostas).forEach(resp => {
                              if (Array.isArray(resp)) {
                                resp.forEach((r, idx) => {
                                  if (r && sim.gabarito[idx]) {
                                    totalRespostas++;
                                    if (r === sim.gabarito[idx]) totalAcertos++;
                                  }
                                });
                              }
                            });
                          }
                        });
                        return totalRespostas > 0 ? Math.round((totalAcertos / totalRespostas) * 100) + '%' : '-';
                      })()}</div>
                      <div className="text-[10px] md:text-sm text-gray-300">Média</div>
                    </div>
                  </div>
                </div>

                {/* Lista de Turmas - Desktop: tabela, Mobile: cards - v520: Dados reais */}
                <div className="rounded-xl p-3 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
                  <h3 className="font-bold text-sm md:text-lg mb-3 md:mb-4 text-white">Turmas com Simulados</h3>
                  
                  {/* Desktop: Tabela */}
                  <div className="hidden md:block overflow-x-auto bg-white rounded-xl">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-semibold">Turma</th>
                          <th className="text-left p-3 font-semibold">Professor</th>
                          <th className="text-left p-3 font-semibold">Data</th>
                          <th className="text-center p-3 font-semibold">Alunos</th>
                          <th className="text-center p-3 font-semibold">Média</th>
                          <th className="text-center p-3 font-semibold">Nível</th>
                          <th className="text-right p-3 font-semibold">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {simuladosFirebase.length > 0 ? simuladosFirebase.map((sim, idx) => {
                          // Buscar turma e professor do simulado
                          const turmaDoSimulado = turmas.find(t => 
                            t.serie === sim.serie || 
                            (t.serie && sim.serie && t.serie.toLowerCase().includes(sim.serie.split(' ')[0].toLowerCase()))
                          );
                          // v521: Buscar professor por professorId OU pelo userId do simulado
                          const profDoSimulado = professoresEscola.find(p => 
                            p.professorId === sim.userId || 
                            p.professorUserId === sim.userId ||
                            p.id === sim.userId
                          );
                          // v521: Se não encontrar nos professores vinculados, buscar nome nas turmas
                          let nomeProfessor = profDoSimulado?.nome;
                          if (!nomeProfessor && turmaDoSimulado?.professorId) {
                            const profDaTurma = professoresEscola.find(p => p.professorId === turmaDoSimulado.professorId);
                            nomeProfessor = profDaTurma?.nome;
                          }
                          
                          // Calcular média do simulado
                          let acertos = 0, total = 0;
                          if (sim.respostas && sim.gabarito) {
                            Object.values(sim.respostas).forEach(resp => {
                              if (Array.isArray(resp)) {
                                resp.forEach((r, i) => {
                                  if (r && sim.gabarito[i]) {
                                    total++;
                                    if (r === sim.gabarito[i]) acertos++;
                                  }
                                });
                              }
                            });
                          }
                          const media = total > 0 ? Math.round((acertos / total) * 100) : 0;
                          const nivel = media >= 70 ? 'Nível 4' : media >= 50 ? 'Nível 3' : media >= 30 ? 'Nível 2' : 'Nível 1';
                          const corNivel = media >= 70 ? 'green' : media >= 50 ? 'yellow' : media >= 30 ? 'orange' : 'red';
                          
                          return (
                            <tr key={sim.id || idx} className="hover:bg-gray-50">
                              <td className="p-3 font-medium">{sim.serie} {sim.turma || ''}</td>
                              <td className="p-3">{nomeProfessor || '-'}</td>
                              <td className="p-3">{sim.dataGeracao ? new Date(sim.dataGeracao).toLocaleDateString('pt-BR') : '-'}</td>
                              <td className="p-3 text-center">{turmaDoSimulado?.alunos?.length || Object.keys(sim.respostas || {}).length || 0}</td>
                              <td className="p-3 text-center"><span className={`px-2 py-1 bg-${corNivel}-100 text-${corNivel}-700 rounded font-semibold`}>{media}%</span></td>
                              <td className="p-3 text-center"><span className={`px-2 py-1 bg-blue-100 text-blue-700 rounded font-semibold`}>{nivel}</span></td>
                              <td className="p-3 text-right">
                                <button onClick={() => { setSimuladoSelecionado(sim); const turmaSimBtn = sim.turmaId ? turmas.find(t => String(t.id) === String(sim.turmaId)) : (turmas.find(t => (t.serie||'').trim().toLowerCase()===(sim.serie||'').trim().toLowerCase() && (t.turma||'').trim().toLowerCase()===(sim.turma||'').trim().toLowerCase()) || turmas.find(t => (t.serie||'').trim().toLowerCase()===(sim.serie||'').trim().toLowerCase())); setConfigSimulado(prev => ({...prev, serie: sim.serie, turma: sim.turma || '', turmaId: turmaSimBtn?.id || sim.turmaId || ''})); if (sim.respostas && turmaSimBtn?.alunos) { setRespostasEditaveis(converterRespostasParaPlanilha(sim.respostas, turmaSimBtn.alunos, sim.serie)); } setViewDesempenho('planilha'); }} className="text-blue-600 hover:underline text-sm mr-3">Planilha</button>
                                <button onClick={() => { setSimuladoSelecionado(sim); setConfigSimulado(prev => ({...prev, serie: sim.serie, turma: sim.turma || ''})); setViewDesempenho('grafico'); }} className="text-green-600 hover:underline text-sm">Gráfico</button>
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr>
                            <td colSpan={7} className="p-6 text-center text-gray-500">
                              Nenhum simulado encontrado. Gere simulados na aba "Simulado SAEB".
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: Cards - v520: Dados reais */}
                  <div className="md:hidden space-y-2">
                    {simuladosFirebase.length > 0 ? simuladosFirebase.map((sim, idx) => {
                      // v521: Mesma lógica de busca de professor
                      const profDoSimulado = professoresEscola.find(p => 
                        p.professorId === sim.userId || 
                        p.professorUserId === sim.userId ||
                        p.id === sim.userId
                      );
                      const turmaDoSim = turmas.find(t => t.serie === sim.serie);
                      let nomeProfMobile = profDoSimulado?.nome;
                      if (!nomeProfMobile && turmaDoSim?.professorId) {
                        const profDaTurma = professoresEscola.find(p => p.professorId === turmaDoSim.professorId);
                        nomeProfMobile = profDaTurma?.nome;
                      }
                      
                      let acertos = 0, total = 0;
                      if (sim.respostas && sim.gabarito) {
                        Object.values(sim.respostas).forEach(resp => {
                          if (Array.isArray(resp)) {
                            resp.forEach((r, i) => {
                              if (r && sim.gabarito[i]) { total++; if (r === sim.gabarito[i]) acertos++; }
                            });
                          }
                        });
                      }
                      const media = total > 0 ? Math.round((acertos / total) * 100) : 0;
                      
                      return (
                        <div key={sim.id || idx} className="bg-gray-800 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-bold text-white text-sm">{sim.serie} {sim.turma || ''}</p>
                              <p className="text-[10px] text-gray-400">{nomeProfMobile || '-'}</p>
                            </div>
                            <div className="text-right">
                              <span className="px-2 py-0.5 bg-yellow-500 text-white rounded text-[10px] font-bold">{media}%</span>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => { setSimuladoSelecionado(sim); const turmaSimBtnM = sim.turmaId ? turmas.find(t => String(t.id) === String(sim.turmaId)) : (turmas.find(t => (t.serie||'').trim().toLowerCase()===(sim.serie||'').trim().toLowerCase() && (t.turma||'').trim().toLowerCase()===(sim.turma||'').trim().toLowerCase()) || turmas.find(t => (t.serie||'').trim().toLowerCase()===(sim.serie||'').trim().toLowerCase())); setConfigSimulado(prev => ({...prev, serie: sim.serie, turma: sim.turma || '', turmaId: turmaSimBtnM?.id || sim.turmaId || ''})); if (sim.respostas && turmaSimBtnM?.alunos) { setRespostasEditaveis(converterRespostasParaPlanilha(sim.respostas, turmaSimBtnM.alunos, sim.serie)); } setViewDesempenho('planilha'); }} className="flex-1 py-1.5 bg-green-600 text-white rounded text-[10px] font-medium">📊 Planilha</button>
                            <button onClick={() => { setSimuladoSelecionado(sim); setConfigSimulado(prev => ({...prev, serie: sim.serie, turma: sim.turma || ''})); setViewDesempenho('grafico'); }} className="flex-1 py-1.5 bg-blue-600 text-white rounded text-[10px] font-medium">📈 Gráfico</button>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="bg-gray-800 rounded-lg p-4 text-center text-gray-400 text-sm">
                        Nenhum simulado encontrado
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : viewDesempenho === 'correcao' ? (
              /* 🆕 v475: TELA DE CORREÇÃO DE SIMULADOS */
              <div>
                {/* Header com botão voltar */}
                <div className="flex items-center gap-3 mb-4">
                  <button 
                    onClick={() => setViewDesempenho('lista')}
                    className="p-2 rounded-lg bg-gray-600 hover:bg-gray-500 transition"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-white">Correção de Simulados</h2>
                    <p className="text-xs text-gray-300 font-semibold">Selecione a turma e o simulado para corrigir</p>
                  </div>
                </div>

                {/* Filtros - v475: Turma + Simulado */}
                <div className="bg-white rounded-xl p-4 mb-4 border-2 border-orange-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Filtro Turma */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">👥 Turma</label>
                      <select
                        value={filtroCorrecaoTurma}
                        onChange={(e) => {
                          setFiltroCorrecaoTurma(e.target.value);
                          setFiltroCorrecaoSimulado('');
                          const turmaSel = turmas.find(t => String(t.id) === e.target.value);
                          setFiltroCorrecaoSerie(turmaSel?.serie || '');
                        }}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                      >
                        <option value="">Selecione a turma...</option>
                        {turmas.map(t => (
                          <option key={t.id} value={t.id}>
                            {formatarNomeTurma(t)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Filtro Simulado */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">📋 Simulado</label>
                      <select
                        value={filtroCorrecaoSimulado}
                        onChange={(e) => setFiltroCorrecaoSimulado(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                        disabled={!filtroCorrecaoTurma}
                      >
                        <option value="">Selecione o simulado...</option>
                        {simuladosFirebase
                          .filter(s => !filtroCorrecaoSerie || (s.serie || '').trim().toLowerCase() === (filtroCorrecaoSerie || '').trim().toLowerCase()).sort((a, b) => { const dataA = new Date(a.dataGeracao || a.dataCriacao || a.dataAplicacao || 0).getTime(); const dataB = new Date(b.dataGeracao || b.dataCriacao || b.dataAplicacao || 0).getTime(); return dataB - dataA; })
                          .map(s => (
                            <option key={s.id} value={s.codigo}>
                              #{formatarCodigoSimulado(s.codigo)} - {s.componente} • {s.serie}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Lista de Alunos para Correção */}
                {filtroCorrecaoSimulado && filtroCorrecaoTurma ? (
                  <div className="bg-white rounded-xl border-2 border-orange-200 overflow-hidden">
                    <div className="bg-orange-500 text-white px-4 py-3">
                      <h3 className="font-bold">Alunos - Simulado #{formatarCodigoSimulado(filtroCorrecaoSimulado)}</h3>
                      <p className="text-xs text-orange-100">Clique no nome do aluno para corrigir o cartão-resposta</p>
                    </div>
                    
                    <div className="divide-y">
                      {(() => {
                        const simuladoSelecionado = simuladosFirebase.find(s => String(s.codigo) === String(filtroCorrecaoSimulado));
                        const turmaDoSimulado = turmas.find(t => String(t.id) === String(filtroCorrecaoTurma));
                        const alunosDaTurma = turmaDoSimulado?.alunos || [];
                        
                        if (alunosDaTurma.length === 0) {
                          return (
                            <div className="p-8 text-center text-gray-500">
                              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>Nenhum aluno cadastrado nesta turma</p>
                              <p className="text-xs mt-1">Cadastre alunos em "Gestão de Turmas"</p>
                            </div>
                          );
                        }
                        
                        return alunosDaTurma.map((aluno, idx) => {
                          const respostasAluno = simuladoSelecionado?.respostas?.[aluno] || simuladoSelecionado?.respostas?.[idx];
                          const temRespostas = respostasAluno && Object.keys(respostasAluno).length > 0;
                          
                          return (
                            <div 
                              key={idx}
                              onClick={() => abrirModalOCR(idx, getNomeAluno(aluno), 'escola', simuladoSelecionado?.serie || turmaDoSimulado?.serie)}
                              className={`flex items-center justify-between px-4 py-3 hover:bg-orange-50 cursor-pointer transition ${temRespostas ? 'bg-green-50' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${temRespostas ? 'bg-green-500' : 'bg-gray-400'}`}>
                                  {idx + 1}
                                </div>
                                <span className="font-medium text-gray-800">{getNomeAluno(aluno)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {temRespostas ? (
                                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                                    ✓ Corrigido
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">
                                    Pendente
                                  </span>
                                )}
                                <Camera className="w-5 h-5 text-orange-500" />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-8 text-center border-2 border-dashed border-gray-300">
                    <Camera className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500 font-medium">Selecione a série e o simulado</p>
                    <p className="text-xs text-gray-400 mt-1">para visualizar os alunos e iniciar a correção</p>
                  </div>
                )}
              </div>
            ) : viewDesempenho === 'planilha' ? (
              <div>
                {/* Aviso para rotacionar celular - só aparece em portrait mobile */}
                <div className="md:hidden fixed inset-0 bg-gray-900 bg-opacity-95 z-50 flex items-center justify-center portrait:flex landscape:hidden">
                  <div className="text-center p-6">
                    <div className="w-20 h-20 mx-auto mb-4 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
                      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Gire o Celular</h3>
                    <p className="text-gray-300 text-sm mb-4">Para melhor visualização da planilha, coloque o celular na horizontal</p>
                    <button 
                      onClick={() => setViewDesempenho('lista')}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm"
                    >
                      ← Voltar
                    </button>
                  </div>
                </div>

                <button onClick={() => setViewDesempenho('lista')} className="mb-1 md:mb-4 flex items-center gap-1 md:gap-2 text-orange-400 hover:text-orange-300 font-semibold text-xs md:text-base">
                  <ArrowLeft className="w-3 h-3 md:w-5 md:h-5" /> Voltar
                </button>
                <div className="rounded-lg md:rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
                  <div className="hidden md:block p-6 border-b" style={{backgroundColor: '#1e3a5f'}}>
                    <div className="text-white">
                      <div className="flex justify-between items-start mb-2">
                        <h2 className="text-2xl font-bold">{cabecalhoConfig.nomeEscola || perfilEscola.nome}</h2>
                        <button onClick={() => setShowDropdownSimulado(true)} className="px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 hover:opacity-90 transition cursor-pointer" style={{backgroundColor: '#f7941d'}}>Simulado #{formatarCodigoSimulado(simuladoSelecionado?.codigo || simuladoSelecionado?.numero || numeroSimulado)} <ChevronDown className="w-3 h-3" /></button>
                      </div>
                      <div className="grid grid-cols-5 gap-4 text-sm">
                        {/* v520: Mostrar professor do simulado selecionado */}
                        <div><span className="text-blue-200">Professor:</span> <span className="font-semibold ml-1">{(() => {
                          if (simuladoSelecionado?.userId) {
                            const prof = professoresEscola.find(p => p.professorId === simuladoSelecionado.userId);
                            return prof?.nome || cabecalhoConfig.professor || '-';
                          }
                          return cabecalhoConfig.professor || '-';
                        })()}</span></div>
                        <div className="relative">
                          <span className="text-blue-200">Série:</span>
                          <button 
                            onClick={() => setShowDropdownTurma && setShowDropdownTurma(!showDropdownTurma)}
                            className="font-semibold ml-1 hover:text-orange-300 cursor-pointer inline-flex items-center gap-1"
                          >
                            {configSimulado.turmaNome || `${configSimulado.serie} ${configSimulado.turma}`}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          {showDropdownTurma && (
                            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl z-50 min-w-[180px]">
                              {/* v498: Filtrar turmas compatíveis com a série do simulado */}
                              {turmas
                                .filter(t => {
                                  // Extrair série base do simulado (ex: "5º ano" de "5º ano A")
                                  const serieSimulado = simuladoSelecionado?.serie || configSimulado.serie || '';
                                  // Extrair apenas o número e "ano" (ex: "5º ano" de "5º ano A" ou "5º Ano B")
                                  const serieBaseSimulado = serieSimulado.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || serieSimulado.toLowerCase();
                                  const serieBaseTurma = t.serie?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || t.serie?.toLowerCase() || '';
                                  // Comparar séries base
                                  return serieBaseTurma === serieBaseSimulado || t.serie?.toLowerCase().includes(serieBaseSimulado);
                                })
                                .map(t => (
                                <button
                                  key={t.id}
                                  onClick={() => {
                                    // v498: Salvar ID da turma para identificação única
                                    setConfigSimulado(prev => ({...prev, serie: t.serie, turma: t.turma || '', turmaId: t.id, turmaNome: t.nome || `${t.serie} ${t.turma || ''}`}));
                                    setShowDropdownTurma(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700 ${configSimulado.turmaId === t.id ? 'bg-blue-100 font-bold' : ''}`}
                                >
                                  {t.nome || `${t.serie} ${t.turma || ''}`}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-blue-200">Data da Aplicação:</span>
                          <input 
                            type="date" 
                            value={configSimulado.dataAplicacao} 
                            onChange={(e) => setConfigSimulado(prev => ({...prev, dataAplicacao: e.target.value}))}
                            className="bg-transparent border-b border-blue-300 text-white font-semibold text-sm px-1 focus:outline-none focus:border-orange-400"
                          />
                        </div>
                        <div><span className="text-blue-200">Matriculados:</span> <span className="font-semibold ml-1">{turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)))?.alunos?.length || 0}</span></div>
                        <div><span className="text-blue-200">Presentes:</span> <span className="font-semibold ml-1">{(() => {
                          const turmaAtual = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
                          if (!turmaAtual?.alunos) return 0;
                          return turmaAtual.alunos.filter((_, idx) => {
                            // Verificar se o aluno tem pelo menos uma resposta
                            for (let b = 0; b < 2; b++) {
                              const totalNoBloco = questoesPorBlocoSAEB(configSimulado.serie);
                              for (let q = 0; q < totalNoBloco; q++) {
                                if (respostasEditaveis[`escola_${idx}_${b}_${q}`]) return true;
                              }
                            }
                            return false;
                          }).length;
                        })()}</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse" style={{tableLayout: "fixed"}}>
                      <thead>
                        {/* Linha 1: Cabeçalho dos Blocos */}
                        <tr>
                          <th rowSpan={2} className="p-2 text-center font-bold border-2 border-gray-400 sticky left-0 bg-gray-200 z-10 text-gray-700" style={{width: '195px', minWidth: '195px', maxWidth: '195px', verticalAlign: 'middle'}}>
                            <span style={{fontSize: '11px'}}>QUESTÕES</span>
                          </th>
                          <th colSpan={questoesPorBlocoSAEB(configSimulado.serie)} className={`p-1 text-center font-bold border-2 text-xs ${componenteSimulado === "Língua Portuguesa" ? "border-purple-500 bg-purple-300 text-purple-900" : "border-blue-500 bg-blue-300 text-blue-900"}`}>BLOCO 1 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</th>
                          <th colSpan={questoesPorBlocoSAEB(configSimulado.serie)} className={`p-1 text-center font-bold border-2 text-xs ${componenteSimulado === "Língua Portuguesa" ? "border-purple-500 bg-purple-300 text-purple-900" : "border-blue-500 bg-blue-300 text-blue-900"}`}>BLOCO 2 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</th>
                          <th rowSpan={5} className="p-1 text-center font-bold border-2 border-green-500 bg-green-300 text-green-900 align-middle" style={{minWidth: '45px', maxWidth: '45px'}}>
                            <div className="text-xs font-bold flex items-center justify-center" style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', height: '100px', margin: '0 auto'}}>TOTAL DE ACERTOS</div>
                          </th>
                        </tr>
                        {/* Linha 2: Números das Questões (vertical, de cima para baixo) */}
                        <tr>
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`q1-${i}`} className="text-center font-bold text-xs bg-purple-200 text-purple-900 border border-purple-300" style={{minWidth: '20px', maxWidth: '20px', height: '90px', fontSize: '9px', verticalAlign: 'middle'}}>
                              <div style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', margin: '0 auto'}}>QUESTÃO {String(i + 1).padStart(2, '0')}</div>
                            </td>
                          ))}
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`q2-${i}`} className="text-center font-bold text-xs bg-blue-200 text-blue-900 border border-blue-300" style={{minWidth: '20px', maxWidth: '20px', height: '90px', fontSize: '9px', verticalAlign: 'middle'}}>
                              <div style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', margin: '0 auto'}}>QUESTÃO {String(i + questoesPorBlocoSAEB(configSimulado.serie) + 1).padStart(2, '0')}</div>
                            </td>
                          ))}
                        </tr>
                        {/* Linha 3: HABILIDADES/DESCRITORES (dados reais das questões selecionadas) */}
                        <tr className="bg-cyan-100">
                          <td className="p-2 font-bold border-2 border-cyan-400 sticky left-0 bg-cyan-200 z-10 text-cyan-900 text-center text-xs">HABILIDADES/<br/>DESCRITORES</td>
                          {(() => {
                            // 🔧 v475: Usar obterGabaritosSimulado para pegar descritores salvos
                            const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 1);
                            return (
                              <>
                                {gabaritosB1.map((g, i) => (
                                  <td key={`h1-${i}`} className="p-0 text-center font-mono bg-cyan-100 text-cyan-800 border border-cyan-300" style={{maxWidth: "22px", fontSize: "8px"}}>{g.habilidade || '-'}</td>
                                ))}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB1.length))].map((_, i) => (
                                  <td key={`h1e-${i}`} className="p-0 text-center bg-cyan-50 border border-cyan-200 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                          {(() => {
                            // 🔧 v475: Usar obterGabaritosSimulado para pegar descritores salvos
                            const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 2);
                            return (
                              <>
                                {gabaritosB2.map((g, i) => (
                                  <td key={`h2-${i}`} className="p-0 text-center font-mono bg-cyan-100 text-cyan-800 border border-cyan-300" style={{maxWidth: "22px", fontSize: "8px"}}>{g.habilidade || '-'}</td>
                                ))}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB2.length))].map((_, i) => (
                                  <td key={`h2e-${i}`} className="p-0 text-center bg-cyan-50 border border-cyan-200 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                        </tr>
                        {/* Linha 4: GABARITO (dados reais das questões selecionadas ou do simulado salvo) */}
                        <tr className="bg-yellow-200">
                          <td className="p-2 font-bold border-2 border-yellow-400 sticky left-0 bg-yellow-300 z-10 text-yellow-900 text-center text-sm">GABARITO</td>
                          {(() => {
                            // 🔧 v404: Usar função helper que prioriza gabaritos salvos no Firebase
                            const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 1);
                            return (
                              <>
                                {gabaritosB1.map((g, i) => {
                                  const numeroQuestao = String(i + 1).padStart(2, '0');
                                  return (
                                    <td key={`g1-${i}`} className={`p-0 text-center border border-yellow-300 relative cursor-pointer hover:brightness-95 ${g.foiEditado ? 'bg-orange-100' : 'bg-yellow-200'}`} style={{maxWidth: "22px", height: "26px"}}>
                                      <div className={`absolute inset-0 flex items-center justify-center font-bold ${g.foiEditado ? 'text-orange-900' : 'text-yellow-900'}`} style={{fontSize: "15px", pointerEvents: "none"}}>
                                        {g.gabarito || '-'}
                                      </div>
                                      <div className="absolute top-0.5 right-0.5 text-gray-500 pointer-events-none" style={{fontSize: "9px", lineHeight: "9px", opacity: 0.5}}>▼</div>
                                      <select
                                        value={g.gabarito || '-'}
                                        onChange={(e) => atualizarGabaritoSimulado(simuladoSelecionado?.id, numeroQuestao, e.target.value)}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        style={{appearance: "none", textAlign: "center"}}
                                        title={g.foiEditado ? `Editado (original: ${g.gabaritoOriginal})` : 'Clique para editar'}
                                      >
                                        <option value="-">-</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                      </select>
                                    </td>
                                  );
                                })}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB1.length))].map((_, i) => (
                                  <td key={`g1e-${i}`} className="p-0 text-center bg-yellow-100 border border-yellow-300 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                          {(() => {
                            // 🔧 v404: Usar função helper que prioriza gabaritos salvos no Firebase
                            const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 2);
                            return (
                              <>
                                {gabaritosB2.map((g, i) => {
                                  const numeroQuestao = String(i + questoesPorBlocoSAEB(configSimulado.serie) + 1).padStart(2, '0');
                                  return (
                                    <td key={`g2-${i}`} className={`p-0 text-center border border-yellow-300 relative cursor-pointer hover:brightness-95 ${g.foiEditado ? 'bg-orange-100' : 'bg-yellow-200'}`} style={{maxWidth: "22px", height: "26px"}}>
                                      <div className={`absolute inset-0 flex items-center justify-center font-bold ${g.foiEditado ? 'text-orange-900' : 'text-yellow-900'}`} style={{fontSize: "15px", pointerEvents: "none"}}>
                                        {g.gabarito || '-'}
                                      </div>
                                      <div className="absolute top-0.5 right-0.5 text-gray-500 pointer-events-none" style={{fontSize: "9px", lineHeight: "9px", opacity: 0.5}}>▼</div>
                                      <select
                                        value={g.gabarito || '-'}
                                        onChange={(e) => atualizarGabaritoSimulado(simuladoSelecionado?.id, numeroQuestao, e.target.value)}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        style={{appearance: "none", textAlign: "center"}}
                                        title={g.foiEditado ? `✏️ Editado (original: ${g.gabaritoOriginal})` : ''}
                                      >
                                        <option value="-">-</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                      </select>
                                    </td>
                                  );
                                })}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB2.length))].map((_, i) => (
                                  <td key={`g2e-${i}`} className="p-0 text-center bg-yellow-100 border border-yellow-300 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                        </tr>
                        {/* Linha 5: ALUNOS - ESCOLA */}
                        <tr className="bg-gray-100">
                          <td className="p-2 font-bold border-2 border-gray-400 sticky left-0 bg-gray-300 z-10 text-gray-800 text-center text-sm">ALUNOS</td>
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`a1e-${i}`} className="p-1 bg-purple-50 border border-purple-100"></td>
                          ))}
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`a2e-${i}`} className="p-1 bg-blue-50 border border-blue-100"></td>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Alunos da turma selecionada - ESCOLA - EDITÁVEL */}
                        {turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)))?.alunos.map((aluno, idx) => {
                          const opcoes = ['A', 'B', 'C', 'D', ''];
                          
                          // 🔧 v404: Usar função helper que prioriza gabaritos salvos no Firebase
                          const gabaritosExpandidosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 1);
                          const gabaritosExpandidosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 2);
                          
                          // Função para obter resposta editável (sem gerar aleatório)
                          const getRespostaEditavel = (alunoIdx, bloco, questaoIdx) => {
                            const chave = `escola_${alunoIdx}_${bloco}_${questaoIdx}`;
                            if (respostasEditaveis[chave] !== undefined) {
                              return respostasEditaveis[chave];
                            }
                            // Retorna vazio se não houver resposta cadastrada
                            return '';
                          };
                          
                          // Função para atualizar resposta
                          const atualizarResposta = (alunoIdx, bloco, questaoIdx, valor) => {
                            const chave = `escola_${alunoIdx}_${bloco}_${questaoIdx}`;
                            const valorUpper = valor.toUpperCase();
                            if (valorUpper === '' || ['A', 'B', 'C', 'D'].includes(valorUpper)) {
                              setRespostasEditaveis(prev => ({...prev, [chave]: valorUpper}));
                            }
                          };
                          
                          // Calcular acertos usando gabaritos expandidos
                          // 🔧 v406: Só conta acerto se o gabarito E a resposta NÃO estiverem vazios
                          let acertosTotal = 0;
                          const respostasB1 = gabaritosExpandidosB1.map((g, i) => {
                            const r = getRespostaEditavel(idx, 0, i);
                            if (g.gabarito && r === g.gabarito) acertosTotal++;
                            return r;
                          });
                          const respostasB2 = gabaritosExpandidosB2.map((g, i) => {
                            const r = getRespostaEditavel(idx, 1, i);
                            if (g.gabarito && r === g.gabarito) acertosTotal++;
                            return r;
                          });
                          
                          return (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td style={{width: "195px", maxWidth: "195px"}} className={`p-1 border-r-2 border-gray-400 sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <span className="text-xs font-medium truncate block">{idx + 1}. {getNomeAluno(aluno)}</span>
                              </td>
                              {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                const temQ = i < gabaritosExpandidosB1.length;
                                const r = respostasB1[i] || '';
                                const gab = gabaritosExpandidosB1[i]?.gabarito;
                                const ok = temQ && r === gab;
                                return (
                                  <td key={`r1e-${i}`} style={{width: "22px", maxWidth: "22px"}} className={`p-0 text-center border-r border-purple-100 ${!temQ ? 'bg-gray-100' : ''}`}>
                                    {temQ ? (
                                      <input
                                        type="text"
                                        value={r}
                                        onChange={(e) => atualizarResposta(idx, 0, i, e.target.value)}
                                        maxLength={1}
                                        className={`w-full h-full p-1 text-center font-mono text-xs border-0 focus:ring-2 focus:ring-blue-400 focus:outline-none ${ok ? 'bg-green-200 text-green-800 font-bold' : r === '' ? 'bg-gray-50 text-gray-400' : 'bg-red-100 text-red-600'}`}
                                        style={{width: '20px', maxWidth: '20px', padding: '2px'}}
                                      />
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </td>
                                );
                              })}
                              {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                const temQ = i < gabaritosExpandidosB2.length;
                                const r = respostasB2[i] || '';
                                const gab = gabaritosExpandidosB2[i]?.gabarito;
                                const ok = temQ && r === gab;
                                return (
                                  <td key={`r2e-${i}`} style={{width: "22px", maxWidth: "22px"}} className={`p-0 text-center border-r border-blue-100 ${!temQ ? 'bg-gray-100' : ''}`}>
                                    {temQ ? (
                                      <input
                                        type="text"
                                        value={r}
                                        onChange={(e) => atualizarResposta(idx, 1, i, e.target.value)}
                                        maxLength={1}
                                        className={`w-full h-full p-1 text-center font-mono text-xs border-0 focus:ring-2 focus:ring-blue-400 focus:outline-none ${ok ? 'bg-green-200 text-green-800 font-bold' : r === '' ? 'bg-gray-50 text-gray-400' : 'bg-red-100 text-red-600'}`}
                                        style={{width: '20px', maxWidth: '20px', padding: '2px'}}
                                      />
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="p-2 text-center font-bold bg-green-200 text-green-800 border-l-2 border-green-400">{acertosTotal}</td>
                            </tr>
                          );
                        })}
                        {/* Linha % ACERTOS - v494: Calcular porcentagem real por questão */}
                        <tr className="border-t-2 border-orange-500">
                          <td className="p-2 border-r-2 border-gray-400 sticky left-0 bg-orange-300 z-10 text-orange-900 text-center font-bold text-sm">% ACERTOS</td>
                          {(() => {
                            // Obter gabaritos do simulado selecionado
                            const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 1);
                            const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 2);
                            const turmaAtualPct = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
                            const totalAlunos = turmaAtualPct?.alunos?.length || 0;
                            
                            // Calcular % acertos para Bloco 1
                            // v591: Contar respostas em branco e múltiplas marcações como ERRO
                            const pctB1 = gabaritosB1.map((g, qIdx) => {
                              if (!g.gabarito) return { temQ: false, pct: 0 };
                              let acertos = 0;
                              const totalAlunos = turmaAtualPct?.alunos?.length || 0;
                              
                              turmaAtualPct?.alunos?.forEach((_, alunoIdx) => {
                                const chave = `escola_${alunoIdx}_0_${qIdx}`;
                                const resp = respostasEditaveis[chave];
                                
                                // Só conta como acerto se marcou corretamente
                                // Em branco ou múltiplas marcações = ERRO
                                if (resp === g.gabarito) {
                                  acertos++;
                                }
                              });
                              
                              return { temQ: true, pct: totalAlunos > 0 ? Math.round((acertos / totalAlunos) * 100) : 0 };
                            });
                            
                            // Calcular % acertos para Bloco 2
                            // v591: Contar respostas em branco e múltiplas marcações como ERRO
                            const pctB2 = gabaritosB2.map((g, qIdx) => {
                              if (!g.gabarito) return { temQ: false, pct: 0 };
                              let acertos = 0;
                              const totalAlunos = turmaAtualPct?.alunos?.length || 0;
                              
                              turmaAtualPct?.alunos?.forEach((_, alunoIdx) => {
                                const chave = `escola_${alunoIdx}_1_${qIdx}`;
                                const resp = respostasEditaveis[chave];
                                
                                // Só conta como acerto se marcou corretamente
                                // Em branco ou múltiplas marcações = ERRO
                                if (resp === g.gabarito) {
                                  acertos++;
                                }
                              });
                              
                              return { temQ: true, pct: totalAlunos > 0 ? Math.round((acertos / totalAlunos) * 100) : 0 };
                            });
                            
                            return (
                              <>
                                {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                  const dados = pctB1[i] || { temQ: false, pct: 0 };
                                  return <td key={`pct1-${i}`} className={`p-1 text-center font-bold text-xs ${!dados.temQ ? 'bg-gray-100 text-gray-300' : dados.pct >= 70 ? 'text-green-800 bg-green-200' : dados.pct >= 50 ? 'text-yellow-800 bg-yellow-200' : 'text-red-800 bg-red-200'}`}>{dados.temQ ? `${dados.pct}%` : '-'}</td>;
                                })}
                                {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                  const dados = pctB2[i] || { temQ: false, pct: 0 };
                                  return <td key={`pct2-${i}`} className={`p-1 text-center font-bold text-xs ${!dados.temQ ? 'bg-gray-100 text-gray-300' : dados.pct >= 70 ? 'text-green-800 bg-green-200' : dados.pct >= 50 ? 'text-yellow-800 bg-yellow-200' : 'text-red-800 bg-red-200'}`}>{dados.temQ ? `${dados.pct}%` : '-'}</td>;
                                })}
                              </>
                            );
                          })()}
                          <td className="p-2 bg-orange-400 border-l-2 border-green-400"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="p-4 border-t flex gap-3 justify-between">
                    <div className="flex gap-3">
                      <button onClick={limparPlanilha} className="px-6 py-2 border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50 rounded-lg font-semibold flex items-center gap-2 transition"><Trash2 className="w-4 h-4" /> Limpar Planilha</button>
                      <button onClick={excluirSimulado} className="px-6 py-2 border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-lg font-semibold flex items-center gap-2 transition"><Trash2 className="w-4 h-4" /> Excluir Simulado</button>
                    </div>
                    <div className="flex gap-3">
                      <button className="px-6 py-2 border-2 rounded-lg font-semibold flex items-center gap-2" style={{borderColor: '#1e3a5f', color: '#1e3a5f'}}><Download className="w-4 h-4" /> Excel</button>
                      <button onClick={() => gerarPDFPlanilha('escola')} className="px-6 py-2 rounded-lg font-semibold flex items-center gap-2 text-white" style={{backgroundColor: '#f7941d'}}><Download className="w-4 h-4" /> PDF</button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <button onClick={() => setViewDesempenho('lista')} className="mb-4 flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold">
                  <ArrowLeft className="w-5 h-5" /> Voltar
                </button>
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
                  {(() => {
                    // 🔧 v493: Calcular dados reais do simulado selecionado
                    const turmaAtual = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
                    
                    // 🔧 v493: Usar gabaritos do simulado selecionado (não dos blocos locais)
                    const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 1);
                    const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimuladoEscola, questoes, 2);
                    
                    // Calcular total de questões baseado nos gabaritos válidos
                    const questoesB1 = gabaritosB1.filter(g => g.gabarito && g.gabarito !== '').length;
                    const questoesB2 = gabaritosB2.filter(g => g.gabarito && g.gabarito !== '').length;
                    const totalQuestoesReal = questoesB1 + questoesB2;
                    
                    // Calcular acertos usando dados reais da planilha
                    let totalAcertosGeral = 0;
                    let totalRespostasGeral = 0;
                    
                    const acertosPorHabilidade = {};
                    
                    // Inicializar contadores por habilidade usando gabaritos do simulado
                    gabaritosB1.forEach((g, i) => {
                      if (g.gabarito && g.habilidade) {
                        if (!acertosPorHabilidade[g.habilidade]) {
                          acertosPorHabilidade[g.habilidade] = { acertos: 0, total: 0 };
                        }
                      }
                    });
                    gabaritosB2.forEach((g, i) => {
                      if (g.gabarito && g.habilidade) {
                        if (!acertosPorHabilidade[g.habilidade]) {
                          acertosPorHabilidade[g.habilidade] = { acertos: 0, total: 0 };
                        }
                      }
                    });
                    
                    // Calcular acertos usando dados da planilha
                    turmaAtual?.alunos.forEach((aluno, alunoIdx) => {
                      // Bloco 1
                      gabaritosB1.forEach((g, qIdx) => {
                        if (g.gabarito) {
                          const chavePlanilha = `escola_${alunoIdx}_0_${qIdx}`;
                          const resposta = respostasEditaveis[chavePlanilha];
                          if (resposta && resposta !== '') {
                            const acertou = resposta === g.gabarito;
                            if (acertou) {
                              totalAcertosGeral++;
                              if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                                acertosPorHabilidade[g.habilidade].acertos++;
                              }
                            }
                            totalRespostasGeral++;
                            if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                              acertosPorHabilidade[g.habilidade].total++;
                            }
                          }
                        }
                      });
                      // Bloco 2
                      gabaritosB2.forEach((g, qIdx) => {
                        if (g.gabarito) {
                          const chavePlanilha = `escola_${alunoIdx}_1_${qIdx}`;
                          const resposta = respostasEditaveis[chavePlanilha];
                          if (resposta && resposta !== '') {
                            const acertou = resposta === g.gabarito;
                            if (acertou) {
                              totalAcertosGeral++;
                              if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                                acertosPorHabilidade[g.habilidade].acertos++;
                              }
                            }
                            totalRespostasGeral++;
                            if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                              acertosPorHabilidade[g.habilidade].total++;
                            }
                          }
                        }
                      });
                    });
                    
                    const percentualGeral = totalRespostasGeral > 0 ? (totalAcertosGeral / totalRespostasGeral) * 100 : 0;
                    
                    // Determinar padrão de desempenho (porcentagens corretas)
                    let padrao, padraoColor, padraoBg, padraoBorder, padraoDescricao;
                    if (percentualGeral >= 70) {
                      padrao = 'AVANÇADO';
                      padraoColor = 'text-sky-700';
                      padraoBg = 'bg-sky-100';
                      padraoBorder = 'border-sky-400';
                      padraoDescricao = 'Este padrão agrupa estudantes com desenvolvimento além do esperado para a sua etapa de escolaridade, os quais precisam de estímulos para continuar avançando no processo de aprendizagem.';
                    } else if (percentualGeral >= 40) {
                      padrao = 'ADEQUADO';
                      padraoColor = 'text-slate-600';
                      padraoBg = 'bg-slate-100';
                      padraoBorder = 'border-slate-400';
                      padraoDescricao = 'Este padrão reúne estudantes que consolidaram o desenvolvimento das habilidades e competências previstas para a etapa de escolaridade. Entretanto, ainda requerem ações para aprofundar a aprendizagem.';
                    } else if (percentualGeral >= 20) {
                      padrao = 'BÁSICO';
                      padraoColor = 'text-orange-600';
                      padraoBg = 'bg-orange-100';
                      padraoBorder = 'border-orange-300';
                      padraoDescricao = 'Este padrão agrupa estudantes que ainda não demonstram ter desenvolvido adequadamente as habilidades e competências essenciais para a sua etapa de escolaridade. Demandam atividades de reforço na aprendizagem.';
                    } else {
                      padrao = 'ABAIXO DO BÁSICO';
                      padraoColor = 'text-red-700';
                      padraoBg = 'bg-red-100';
                      padraoBorder = 'border-red-500';
                      padraoDescricao = 'Este padrão reúne estudantes com carência de aprendizagem para o desenvolvimento das habilidades e competências mínimas requeridas para a conclusão da etapa de escolaridade em que se encontram. São estudantes que necessitam de ações pedagógicas de recuperação.';
                    }
                    
                    // Calcular níveis separados para Português e Matemática
                    const calcularNivelPortugues5ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 125 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '125 a 150 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '150 a 175 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '175 a 200 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '200 a 225 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '225 a 250 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '250 a 275 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '275 a 300 pontos' };
                      if (pct <= 95) return { nivel: 9, faixa: '300 a 325 pontos' };
                      return { nivel: 10, faixa: 'Acima de 325 pontos' };
                    };
                    
                    const calcularNivelMatematica5ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 150 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '150 a 175 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '175 a 200 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '200 a 225 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '225 a 250 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '250 a 275 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '275 a 300 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '300 a 325 pontos' };
                      return { nivel: 9, faixa: 'Acima de 325 pontos' };
                    };
                    
                    const calcularNivelPortugues9ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 175 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '175 a 200 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '200 a 225 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '225 a 250 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '250 a 275 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '275 a 300 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '300 a 325 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '325 a 350 pontos' };
                      if (pct <= 95) return { nivel: 9, faixa: '350 a 375 pontos' };
                      return { nivel: 10, faixa: 'Acima de 375 pontos' };
                    };
                    
                    const calcularNivelMatematica9ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 225 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '225 a 250 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '250 a 275 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '275 a 300 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '300 a 325 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '325 a 350 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '350 a 375 pontos' };
                      return { nivel: 8, faixa: 'Acima de 375 pontos' };
                    };
                    
                    const calcularNivelPortugues2ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: '650 a 675 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '675 a 700 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '700 a 725 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '725 a 750 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '750 a 775 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '775 a 800 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '800 a 825 pontos' };
                      return { nivel: 8, faixa: 'Acima de 825 pontos' };
                    };
                    
                    const calcularNivelMatematica2ano = (pct) => {
                      if (pct <= 10) return { nivel: 'Abaixo de 1', faixa: 'Até 650 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '675 a 700 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '700 a 725 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '725 a 750 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '750 a 775 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '775 a 800 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '800 a 825 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '825 a 850 pontos' };
                      return { nivel: 9, faixa: 'Acima de 850 pontos' };
                    };
                    
                    // Calcular percentual por bloco (Português = Bloco 1, Matemática = Bloco 2)
                    let acertosPort = 0, totalPort = 0, acertosMat = 0, totalMat = 0;
                    turmaAtual?.alunos.forEach((aluno, alunoIdx) => {
                      blocosSimuladoEscola[0].questoesSelecionadas.forEach((qId, qIdx) => {
                        const q = questoes.find(x => String(x.id) === String(qId));
                        if (q) {
                          const acertou = ((alunoIdx * 7 + qIdx * 3) % 10) > 4;
                          if (acertou) acertosPort++;
                          totalPort++;
                        }
                      });
                      blocosSimuladoEscola[1].questoesSelecionadas.forEach((qId, qIdx) => {
                        const q = questoes.find(x => String(x.id) === String(qId));
                        if (q) {
                          const acertou = ((alunoIdx * 5 + qIdx * 11) % 10) > 3;
                          if (acertou) acertosMat++;
                          totalMat++;
                        }
                      });
                    });
                    
                    const pctPort = totalPort > 0 ? (acertosPort / totalPort) * 100 : 0;
                    const pctMat = totalMat > 0 ? (acertosMat / totalMat) * 100 : 0;
                    
                    // Selecionar função de nível baseada na série
                    const serieAtual = configSimulado.serie;
                    let nivelPort, nivelMat;
                    if (serieAtual.includes('2')) {
                      nivelPort = calcularNivelPortugues2ano(pctPort);
                      nivelMat = calcularNivelMatematica2ano(pctMat);
                    } else if (serieAtual.includes('9')) {
                      nivelPort = calcularNivelPortugues9ano(pctPort);
                      nivelMat = calcularNivelMatematica9ano(pctMat);
                    } else {
                      nivelPort = calcularNivelPortugues5ano(pctPort);
                      nivelMat = calcularNivelMatematica5ano(pctMat);
                    }
                    
                    // Lista de habilidades para o gráfico
                    const habilidadesGrafico = Object.entries(acertosPorHabilidade).map(([hab, dados]) => ({
                      habilidade: hab,
                      percentual: dados.total > 0 ? Math.round((dados.acertos / dados.total) * 100) : 0
                    }));
                    
                    return (
                      <>
                        <div className="p-6 border-b text-center" style={{backgroundColor: '#1e3a5f'}}>
                          <div className="flex justify-center items-center gap-4 mb-2">
                            <h2 className="text-xl font-bold text-white">NÍVEIS E ESCALAS DE PROFICIÊNCIA NO SIMULADO SAEB</h2>
                            <button onClick={() => setShowDropdownSimulado(true)} className="px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 hover:opacity-90 transition cursor-pointer" style={{backgroundColor: '#f7941d'}}>Simulado #{formatarCodigoSimulado(simuladoSelecionado?.codigo || simuladoSelecionado?.numero || numeroSimulado)} <ChevronDown className="w-3 h-3" /></button>
                          </div>
                          <p className="text-blue-200">{configSimulado.turmaNome || `${configSimulado.serie} ${configSimulado.turma}`} - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</p>
                        </div>
                        <div className="p-6 border-b">
                          <p className="text-center text-gray-300 mb-4">De acordo com os resultados neste simulado, a turma {configSimulado.turmaNome || `${configSimulado.serie} ${configSimulado.turma}`} está com:</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                            <div className={`${padraoBg} border-2 ${padraoBorder} rounded-xl p-5`}>
                              <div className={`text-xs ${padraoColor} mb-1 uppercase text-center`}>Padrão de Desempenho</div>
                              <div className={`text-2xl font-bold ${padraoColor} text-center mb-3`}>{padrao}</div>
                              <div className={`text-xs ${padraoColor} opacity-90 text-justify leading-relaxed`}>{padraoDescricao}</div>
                            </div>
                            <div className={`${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-50 border-purple-300' : 'bg-blue-50 border-blue-300'} border-2 rounded-xl p-5 text-center flex flex-col justify-center`}>
                              <div className={`text-xs ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'} mb-2 uppercase`}>Nível de Proficiência</div>
                              <div className={`text-4xl font-bold ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-700' : 'text-blue-700'}`}>
                                Nível {componenteSimulado === 'Língua Portuguesa' ? nivelPort.nivel : nivelMat.nivel}
                              </div>
                              <div className={`text-sm ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'} font-semibold mt-2`}>
                                {componenteSimulado === 'Língua Portuguesa' ? nivelPort.faixa : nivelMat.faixa}
                              </div>
                              <div className={`text-xs ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-400' : 'text-blue-400'} mt-2`}>
                                {configSimulado.serie} - {componenteSimulado === 'Língua Portuguesa' ? 'Língua Portuguesa' : 'Matemática'}
                              </div>
                            </div>
                            <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-5 text-center flex flex-col justify-center">
                              <div className="text-xs text-gray-600 mb-1 uppercase">Participantes</div>
                              <div className="text-2xl font-bold text-gray-700">{turmaAtual?.alunos.length || 0}</div>
                              <div className="text-xs text-gray-500 mt-1">alunos • {totalQuestoesReal} questões</div>
                            </div>
                          </div>
                        </div>
                        <div className="p-6">
                          <h3 className="text-center font-bold text-lg mb-6">PORCENTAGEM DE ACERTO POR HABILIDADES/DESCRITORES</h3>
                          {habilidadesGrafico.length === 0 ? (
                            <div className="text-center py-8 text-gray-400">
                              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                              <p>Selecione questões no simulado para ver o gráfico de desempenho</p>
                            </div>
                          ) : (
                            <>
                              <div className="overflow-x-auto">
                                <div className="flex items-end justify-center gap-2" style={{minWidth: `${habilidadesGrafico.length * 50}px`, height: '280px'}}>
                                  {habilidadesGrafico.map((item, i) => {
                                    const altura = (item.percentual / 100) * 200;
                                    const cor = item.percentual >= 70 ? '#22c55e' : item.percentual >= 40 ? '#3b82f6' : item.percentual >= 20 ? '#eab308' : '#ef4444';
                                    return (
                                      <div key={i} className="flex flex-col items-center" style={{width: '45px'}}>
                                        <div className="text-xs font-bold mb-1" style={{color: cor}}>{item.percentual}%</div>
                                        <div className="w-8 rounded-t transition-all" style={{height: `${Math.max(altura, 5)}px`, backgroundColor: cor}} />
                                        <div className="mt-2 text-xs font-mono text-gray-200 -rotate-45 origin-top-left whitespace-nowrap" style={{width: '60px'}}>{item.habilidade}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="flex justify-center gap-4 mt-10 pt-4 border-t flex-wrap">
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-green-500"></div><span className="text-xs">Avançado</span></div>
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-500"></div><span className="text-xs">Adequado</span></div>
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-yellow-500"></div><span className="text-xs">Básico</span></div>
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500"></div><span className="text-xs">Abaixo do Básico</span></div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="p-4 border-t flex gap-3 justify-end">
                          <button className="px-6 py-2 border-2 rounded-lg font-semibold flex items-center gap-2" style={{borderColor: '#1e3a5f', color: '#1e3a5f'}}><Download className="w-4 h-4" /> Imagem</button>
                          <button onClick={() => gerarPDFGrafico('escola')} className="px-6 py-2 rounded-lg font-semibold flex items-center gap-2 text-white" style={{backgroundColor: '#f7941d'}}><Download className="w-4 h-4" /> PDF</button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {currentView === 'professor-cabecalho' && (
          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
              <div className="flex items-start gap-2 md:gap-3">
                <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-blue-600 flex-shrink-0 mt-0.5 md:mt-1" />
                <div>
                  <h4 className="font-bold text-blue-900 mb-1 md:mb-2 text-sm md:text-base">Configurações do Cabeçalho</h4>
                  <p className="text-xs md:text-sm text-blue-800 mb-2">
                    As informações aqui aparecerão no cabeçalho de todas as atividades geradas.
                  </p>
                  {currentUser?.escolaId && (
                    <div className="bg-blue-100 rounded-lg p-2 md:p-3 mt-2 md:mt-3">
                      <p className="text-xs md:text-sm font-semibold text-blue-900">
                        🏫 Vinculado à: <span className="font-bold">{currentUser.escolaNome}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              <div className="rounded-xl p-4 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
                <h3 className="font-bold text-base md:text-lg mb-4 md:mb-6 flex items-center gap-2 text-white">
                  <Settings className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
                  Configurações
                </h3>
                
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1 md:mb-2">
                      Nome da Escola
                    </label>
                    <input 
                      type="text" 
                      value={cabecalhoConfig.nomeEscola}
                      readOnly
                      disabled
                      className="w-full p-2.5 md:p-3 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed text-sm md:text-base"
                      placeholder="Ex: Escola Municipal João Silva"
                    />
                    <p className="text-[10px] md:text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Vinculado ao cadastro. Caso queira alterar o nome da escola, entre em contato com o nosso suporte!
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1 md:mb-2">
                      Professor(a) <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="text" 
                      value={cabecalhoConfig.professor}
                      onChange={(e) => setCabecalhoConfig({...cabecalhoConfig, professor: e.target.value})}
                      className="w-full p-2.5 md:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm md:text-base"
                      placeholder="Nome do professor"
                    />
                  </div>

                  {/* Nota sobre Série Automática */}
                  <div className="bg-blue-900 bg-opacity-50 border border-blue-400 rounded-lg p-3 md:p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs md:text-sm font-semibold text-blue-100 mb-1">Série automática</p>
                        <p className="text-[10px] md:text-xs text-blue-200">
                          Definida ao selecionar no filtro do Gerador.
                          {cabecalhoConfig.serie && (
                            <span className="block mt-1">
                              Atual: <span className="font-bold text-white">{cabecalhoConfig.serie}</span>
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1 md:mb-2">Turma</label>
                    <input 
                      type="text" 
                      value={cabecalhoConfig.turma}
                      onChange={(e) => setCabecalhoConfig({...cabecalhoConfig, turma: e.target.value})}
                      className="w-full p-2.5 md:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm md:text-base"
                      placeholder="Ex: A, B, C"
                      maxLength={2}
                    />
                  </div>
                </div>

                {/* v651: Upload de Logomarca */}
                <div className="border-t border-gray-600 pt-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-300 mb-2">
                    Logomarca da Escola
                  </label>
                  <div className="space-y-3">
                    {cabecalhoConfig.logoUrl ? (
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <img 
                          src={cabecalhoConfig.logoUrl} 
                          alt="Logo" 
                          className="h-16 w-16 object-contain border border-gray-200 rounded"
                        />
                        <button
                          onClick={() => setCabecalhoConfig({...cabecalhoConfig, logoUrl: ''})}
                          className="text-xs text-red-500 hover:text-red-700 hover:underline"
                        >
                          Remover Logo
                        </button>
                      </div>
                    ) : (
                      <label className="border-2 border-dashed border-gray-500 rounded-lg p-4 cursor-pointer hover:border-blue-400 hover:bg-gray-700 transition block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            if (!firebaseReady || !storage) {
                              alert('⚠️ Aguarde o Firebase carregar completamente.');
                              return;
                            }
                            
                            try {
                              const result = await uploadImagem(file, 'logos');
                              if (result.success) {
                                setCabecalhoConfig({...cabecalhoConfig, logoUrl: result.url});
                                alert('✅ Logo enviada com sucesso!');
                              } else {
                                alert('❌ Erro ao enviar logo: ' + result.error);
                              }
                            } catch (error) {
                              alert('❌ Erro: ' + error.message);
                            }
                          }}
                        />
                        <div className="text-center">
                          <Image className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-xs text-gray-300">Clique para fazer upload</p>
                          <p className="text-[10px] text-gray-400 mt-1">PNG, JPG (max 2MB)</p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t flex flex-col md:flex-row gap-2 md:gap-3">
                  <button 
                    onClick={() => {
                      // 🔧 v518: Usar escolaVinculada se disponível, senão escolaNome ou fallback
                      const nomeEscolaOriginal = escolaVinculada?.nome || currentUser?.escolaNome || currentUser?.escola || 'Escola Municipal João Silva';
                      setCabecalhoConfig({
                        nomeEscola: nomeEscolaOriginal,
                        serie: '',
                        turma: '',
                        data: new Date().toISOString().split('T')[0],
                        disciplina: '',
                        professor: currentUser?.nome || ''
                      });
                      alert('✅ Configurações restauradas!');
                    }}
                    className="flex-1 px-4 md:px-6 py-2.5 md:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm md:text-base"
                  >
                    Restaurar
                  </button>
                  <button 
                    onClick={salvarCabecalho}
                    className="flex-1 px-4 md:px-6 py-2.5 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 text-sm md:text-base"
                  >
                    <Save className="w-4 h-4 md:w-5 md:h-5" />
                    Salvar
                  </button>
                </div>
              </div>

              <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border-2 border-gray-200">
                <h3 className="font-bold text-base md:text-lg mb-3 md:mb-4 flex items-center gap-2">
                  <Eye className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                  Pré-visualização
                </h3>
                <p className="text-xs md:text-sm text-gray-600 mb-4 md:mb-6">Como aparecerá nas atividades:</p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 md:p-6 bg-gray-50">
                  <div className="space-y-2 md:space-y-3 text-xs md:text-sm">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-2">
                      {/* v651: Logo à esquerda */}
                      {cabecalhoConfig.logoUrl && (
                        <div className="flex-shrink-0">
                          <img 
                            src={cabecalhoConfig.logoUrl} 
                            alt="Logo" 
                            className="h-12 md:h-16 w-auto object-contain"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-bold text-sm md:text-lg text-gray-900 mb-1">
                          {cabecalhoConfig.nomeEscola || 'Nome da Escola'}
                        </div>
                        <div className="text-gray-700 text-xs md:text-sm">
                          Professor(a): <span className="font-semibold">{cabecalhoConfig.professor || 'Nome'}</span>
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <div className="text-gray-700 text-xs md:text-sm">
                          Data: <span className="font-semibold">
                            {cabecalhoConfig.data ? new Date(cabecalhoConfig.data + 'T00:00:00').toLocaleDateString('pt-BR') : '__/__/____'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-2 md:pt-3 border-t border-gray-300">
                      <div className="grid grid-cols-3 gap-2 md:gap-4">
                        <div>
                          <span className="text-gray-600 text-[10px] md:text-xs">Série:</span>
                          <div className="font-semibold text-gray-900 text-xs md:text-sm">{cabecalhoConfig.serie || '_____'}</div>
                        </div>
                        <div>
                          <span className="text-gray-600 text-[10px] md:text-xs">Turma:</span>
                          <div className="font-semibold text-gray-900 text-xs md:text-sm">{cabecalhoConfig.turma || '_____'}</div>
                        </div>
                        <div>
                          <span className="text-gray-600 text-[10px] md:text-xs">Disciplina:</span>
                          <div className="font-semibold text-gray-900 text-xs md:text-sm">{cabecalhoConfig.disciplina || '_____'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-300">
                      <div className="text-gray-600">Aluno(a):</div>
                      <div className="h-8 border-b-2 border-gray-400 mt-2"></div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <div className="font-semibold mb-1">Este cabeçalho será usado em:</div>
                      <ul className="space-y-1 ml-4">
                        <li>• Todas as atividades</li>
                        <li>• Todas as avaliações</li>
                        <li>• Todos os simulados SAEB</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 🔧 v426: Seção de Alterar Senha */}
            <div className="mt-6 rounded-xl p-4 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="font-bold text-base md:text-lg mb-4 md:mb-6 flex items-center gap-2 text-white">
                <Lock className="w-4 h-4 md:w-5 md:h-5 text-red-400" />
                Alterar Senha
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1 md:mb-2">
                    Senha Atual
                  </label>
                  <input 
                    type="password" 
                    value={senhaAtual}
                    onChange={(e) => setSenhaAtual(e.target.value)}
                    className="w-full p-2.5 md:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm md:text-base"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1 md:mb-2">
                    Nova Senha
                  </label>
                  <input 
                    type="password" 
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    className="w-full p-2.5 md:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm md:text-base"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1 md:mb-2">
                    Confirmar Nova Senha
                  </label>
                  <input 
                    type="password" 
                    value={confirmarNovaSenha}
                    onChange={(e) => setConfirmarNovaSenha(e.target.value)}
                    className="w-full p-2.5 md:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm md:text-base"
                    placeholder="Repita a nova senha"
                  />
                </div>
              </div>
              
              <div className="flex justify-end mt-4">
                <button
                  onClick={async () => {
                    if (!senhaAtual || !novaSenha || !confirmarNovaSenha) {
                      alert('⚠️ Preencha todos os campos!');
                      return;
                    }
                    if (novaSenha.length < 6) {
                      alert('⚠️ A nova senha deve ter pelo menos 6 caracteres!');
                      return;
                    }
                    if (novaSenha !== confirmarNovaSenha) {
                      alert('⚠️ As senhas não coincidem!');
                      return;
                    }
                    
                    try {
                      const user = auth.currentUser;
                      if (!user) {
                        alert('❌ Usuário não autenticado!');
                        return;
                      }
                      
                      // Reautenticar o usuário
                      const credential = window.firebase.auth.EmailAuthProvider.credential(user.email, senhaAtual);
                      await user.reauthenticateWithCredential(credential);
                      
                      // Alterar a senha
                      await user.updatePassword(novaSenha);
                      
                      alert('✅ Senha alterada com sucesso!');
                      setSenhaAtual('');
                      setNovaSenha('');
                      setConfirmarNovaSenha('');
                    } catch (error) {
                      console.error('Erro ao alterar senha:', error);
                      if (error.code === 'auth/wrong-password') {
                        alert('❌ Senha atual incorreta!');
                      } else if (error.code === 'auth/weak-password') {
                        alert('❌ A nova senha é muito fraca. Use pelo menos 6 caracteres.');
                      } else {
                        alert('❌ Erro ao alterar senha. Tente novamente.');
                      }
                    }
                  }}
                  className="px-6 py-2.5 md:py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2 text-sm md:text-base"
                >
                  <Lock className="w-4 h-4 md:w-5 md:h-5" />
                  Alterar Senha
                </button>
              </div>
            </div>
          </div>
        )}

        {currentView === 'professor-gerador' && (
          <div>
            {/* v508: Loading enquanto carrega dados da escola vinculada */}
            {aguardandoEscola && (
              <div className="bg-white p-8 rounded-xl shadow-sm text-center mb-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Carregando...</h3>
                <p className="text-gray-600 mb-2">Buscando dados da escola vinculada...</p>
                {tentativasEscola >= MAX_TENTATIVAS_ESCOLA - 1 && (
                  <p className="text-sm text-red-500 mb-3">Não foi possível conectar à escola.</p>
                )}
                <div className="flex gap-3 justify-center mt-4">
                  <button 
                    onClick={() => setTentativasEscola(prev => prev + 1)} 
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-semibold"
                  >
                    🔄 Tentar novamente
                  </button>
                  <button 
                    onClick={() => window.location.reload()} 
                    className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-semibold"
                  >
                    Recarregar página
                  </button>
                </div>
              </div>
            )}
            {/* Aviso de bloqueio se sem créditos ou séries - só para professor NÃO vinculado */}
            {currentUser?.tipo !== 'admin' && !temAcessoLiberado && !isProfessorVinculado && !aguardandoEscola && (
              <div className="bg-white p-8 rounded-xl shadow-sm text-center mb-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Acesso Restrito</h3>
                <p className="text-gray-600 mb-4">
                  {creditosEfetivos <= 0 ? 'Seus créditos acabaram.' : 'Você não possui séries liberadas.'} Adquira uma recarga para continuar.
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4 inline-block">
                  <span className="text-gray-600">Créditos: </span>
                  <span className={`font-bold ${creditosEfetivos > 0 ? 'text-green-600' : 'text-red-600'}`}>{creditosEfetivos}</span>
                  <span className="mx-2">|</span>
                  <span className="text-gray-600">Séries: </span>
                  <span className={`font-bold ${seriesEfetivas.length > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {seriesEfetivas.length}
                  </span>
                </div>
                <div>
                  <button 
                    onClick={() => setCurrentView('professor-recargas')}
                    className="px-6 py-2 rounded-lg font-semibold text-white"
                    style={{backgroundColor: '#f7941d'}}
                  >
                    Comprar Recarga
                  </button>
                </div>
              </div>
            )}
            {/* Aviso para professor vinculado sem acesso (escola sem créditos ou sem séries) */}
            {isProfessorVinculado && escolaVinculada && !temAcessoLiberado && (
              <div className="bg-white p-8 rounded-xl shadow-sm text-center mb-6">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-10 h-10 text-yellow-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Aguardando Liberação</h3>
                <p className="text-gray-600 mb-4">
                  Você está vinculado à escola <strong>{escolaVinculada?.nome}</strong>. 
                  {creditosEfetivos <= 0 ? ' A escola está sem créditos disponíveis.' : ' Aguarde a liberação de séries pela escola.'}
                </p>
                <div className="bg-blue-50 rounded-lg p-3 inline-block">
                  <span className="text-gray-600">Escola: </span>
                  <span className="font-bold text-blue-600">{escolaVinculada?.nome}</span>
                  <span className="mx-2">|</span>
                  <span className="text-gray-600">Créditos: </span>
                  <span className={`font-bold ${creditosEfetivos > 0 ? 'text-green-600' : 'text-red-600'}`}>{creditosEfetivos}</span>
                </div>
              </div>
            )}
            {/* Conteúdo normal do gerador - só aparece se tiver acesso liberado */}
            {temAcessoLiberado && !aguardandoEscola && (
            <div className="rounded-xl p-4 md:p-6 mb-4 md:mb-6" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="font-bold text-base md:text-lg mb-3 md:mb-4 text-white">Escolha o tipo de geração</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                <button 
                  onClick={() => {
                    setTipoGeracao('pronta');
                    // Limpar filtros e PDF ao mudar de módulo
                    setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                    setComponenteBloqueado(null);
                    setHabilidadeBloqueada(null);
                    setPaginas([{ id: 1, questoes: [] }]);
                    setPaginaAtiva(1);
                    setPdfGerado(null);
                  }} 
                  className={`p-3 md:p-6 rounded-xl border-2 transition ${tipoGeracao === 'pronta' ? 'border-blue-400 bg-blue-900 bg-opacity-50' : 'border-gray-600 hover:border-blue-400 bg-opacity-20 bg-white'}`}
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                    <Layers className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="font-semibold text-center text-white text-xs md:text-base">Atividades Prontas</div>
                  <div className="text-[10px] md:text-xs text-gray-300 text-center mt-1 hidden md:block">Pré-configuradas</div>
                </button>
                <button 
                  onClick={() => {
                    setTipoGeracao('questao');
                    // Limpar filtros e PDF ao mudar de módulo
                    setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                    setComponenteBloqueado(null);
                    setHabilidadeBloqueada(null);
                    setPaginas([{ id: 1, questoes: [] }]);
                    setPaginaAtiva(1);
                    setPdfGerado(null);
                  }} 
                  className={`p-3 md:p-6 rounded-xl border-2 transition ${tipoGeracao === 'questao' ? 'border-blue-400 bg-blue-900 bg-opacity-50' : 'border-gray-600 hover:border-blue-400 bg-opacity-20 bg-white'}`}
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 rounded-lg bg-green-100 flex items-center justify-center text-green-600">
                    <FileText className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="font-semibold text-center text-white text-xs md:text-base">Atividade</div>
                  <div className="text-[10px] md:text-xs text-gray-300 text-center mt-1 hidden md:block">Selecione questões</div>
                </button>
                <button 
                  onClick={() => {
                    setTipoGeracao('avaliacao');
                    // Limpar filtros e PDF ao mudar de módulo
                    setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                    setComponenteBloqueado(null);
                    setHabilidadeBloqueada(null);
                    setPaginas([{ id: 1, questoes: [] }]);
                    setPaginaAtiva(1);
                    setPdfGerado(null);
                  }} 
                  className={`p-3 md:p-6 rounded-xl border-2 transition ${tipoGeracao === 'avaliacao' ? 'border-blue-400 bg-blue-900 bg-opacity-50' : 'border-gray-600 hover:border-blue-400 bg-opacity-20 bg-white'}`}
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600">
                    <Award className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="font-semibold text-center text-white text-xs md:text-base">Avaliação</div>
                  <div className="text-[10px] md:text-xs text-gray-300 text-center mt-1 hidden md:block">Prova formal</div>
                </button>
                <button 
                  onClick={() => {
                    setTipoGeracao('simulado');
                    // Limpar filtros e PDF ao mudar de módulo
                    setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                    setComponenteBloqueado(null);
                    setHabilidadeBloqueada(null);
                    setPaginas([{ id: 1, questoes: [] }]);
                    setPaginaAtiva(1);
                    setPdfGerado(null);
                  }} 
                  className={`p-3 md:p-6 rounded-xl border-2 transition ${tipoGeracao === 'simulado' ? 'border-blue-400 bg-blue-900 bg-opacity-50' : 'border-gray-600 hover:border-blue-400 bg-opacity-20 bg-white'}`}
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                    <Target className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="font-semibold text-center text-white text-xs md:text-base">Simulado SAEB</div>
                  <div className="text-[10px] md:text-xs text-gray-300 text-center mt-1 hidden md:block">2º, 5º e 9º ano</div>
                </button>
              </div>
            </div>
            )}

            {temAcessoLiberado && (tipoGeracao === 'pronta' || tipoGeracao === 'questao' || tipoGeracao === 'avaliacao') && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
                {/* Coluna Esquerda - Seleção de Questões */}
                <div className="lg:col-span-6 rounded-xl p-4 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
                  <div className="mb-4 md:mb-6">
                    <h3 className="font-bold text-sm md:text-lg mb-3 md:mb-4 text-white">
                      {tipoGeracao === 'pronta' ? 'Selecione Questões para Atividade Pronta' : 
                       tipoGeracao === 'questao' ? 'Selecione Questões para Atividade' : 
                       'Selecione Questões para Avaliação'}
                    </h3>
                    <div className={`grid ${tipoGeracao === 'pronta' ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'} gap-2`}>
                      <select 
                        className="px-3 py-2 border rounded-lg text-sm bg-white" 
                        onChange={(e) => {
                          setFiltros({...filtros, serie: e.target.value, componente: '', habilidade: ''});
                          // 🆕 Atualiza automaticamente a série no cabeçalho e configSimulado
                          if (e.target.value) {
                            setCabecalhoConfig({...cabecalhoConfig, serie: e.target.value});
                            setConfigSimulado({...configSimulado, serie: e.target.value});
                          }
                          // v529: Reiniciar contador de questões ao mudar série
                          setIndiceQuestaoVisivel({ pequena: 0, media: 0, grande: 0, inteira: 0, 'texto-multiplas': 0 });
                        }}
                        value={filtros.serie}
                      >
                        <option value="">Selecione uma Série</option>
                        {/* 🆕 Filtrar séries baseado em seriesLiberadas do usuário (se não for admin) */}
                        {/* v689: Usar seriesEfetivas para professor vinculado (inclui séries da escola) */}
                        {seriesDisponiveis
                          .filter(s => s.ativo)
                          .filter(s => {
                            // Admin vê todas as séries
                            if (currentUser?.tipo === 'admin') return true;
                            // v689: Usar seriesEfetivas que já faz fallback para séries da escola
                            if (!seriesEfetivas || seriesEfetivas.length === 0) return true;
                            // v689d: Comparação case-insensitive (ex: "9º Ano" vs "9º ano")
                            return seriesEfetivas.some(se => se.toLowerCase() === s.nome.toLowerCase());
                          })
                          .map(s => (
                            <option key={s.id} value={s.nome}>{s.nome}</option>
                          ))}
                      </select>
                      <select 
                        className={`px-3 py-2 border rounded-lg text-sm ${!filtros.serie ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                        onChange={(e) => {
                          setFiltros({...filtros, componente: e.target.value, habilidade: ''});
                          // v529: Reiniciar contador de questões ao mudar componente
                          setIndiceQuestaoVisivel({ pequena: 0, media: 0, grande: 0, inteira: 0, 'texto-multiplas': 0 });
                        }}
                        disabled={!filtros.serie}
                        value={filtros.componente}
                      >
                        <option value="">Selecione um Componente</option>
                        {tipoGeracao === 'simulado' ? (
                          // Mostrar apenas componentes SAEB para Simulado
                          <>
                            <option value="Língua Portuguesa SAEB">Língua Portuguesa SAEB</option>
                            <option value="Matemática SAEB">Matemática SAEB</option>
                          </>
                        ) : (
                          // Ocultar componentes SAEB para outros módulos
                          componentesVisiveis.filter(c => c.ativo && !c.nome.includes('SAEB')).map(c => (
                            <option key={c.id} value={c.nome}>{c.nome}</option>
                          ))
                        )}
                      </select>
                      <select 
                        className={`px-3 py-2 border rounded-lg text-sm ${!filtros.componente ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        onChange={(e) => {
                          const habSelecionada = habilidades.find(h => h.id === e.target.value);
                          setFiltros({
                            ...filtros, 
                            habilidadeId: e.target.value,
                            habilidade: habSelecionada?.codigo || ''
                          });
                          // v529: Reiniciar contador de questões ao mudar habilidade
                          setIndiceQuestaoVisivel({ pequena: 0, media: 0, grande: 0, inteira: 0, 'texto-multiplas': 0 });
                        }}
                        disabled={!filtros.componente}
                        value={filtros.habilidadeId || ''}
                      >
                        <option value="">Selecione uma Habilidade</option>
                        {habilidades
                          .filter(h => h.serie === filtros.serie && h.componente === filtros.componente)
                          .map(h => (
                          <option key={h.id} value={h.id}>
                            ({h.codigo}) - {h.conteudo}
                          </option>
                        ))}
                      </select>
                      {tipoGeracao !== 'pronta' && (
                        <select className="px-3 py-2 border rounded-lg text-sm bg-white" onChange={(e) => setFiltros({...filtros, tamanho: e.target.value})}>
                          <option value="">Todos tamanhos</option>
                          <option value="pequena">Pequena (~6.5cm)</option>
                          <option value="media">Média (~8.5cm)</option>
                          <option value="grande">Grande (~12cm)</option>
                          <option value="inteira">Inteira (~22cm)</option>
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Lista de Questões Ordenada por Tamanho - SEM SCROLL */}
                  <div className="space-y-4">
                    {/* Verificar se todos os filtros foram selecionados */}
                    {(!filtros.serie || !filtros.componente || !filtros.habilidadeId) ? (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-8 text-center">
                        <div className="text-5xl mb-4">🔍</div>
                        <h3 className="text-xl font-bold text-amber-800 mb-3">Selecione os filtros para ver as questões</h3>
                        <div className="space-y-2 text-amber-700">
                          <p className={`flex items-center justify-center gap-2 ${filtros.serie ? 'text-green-600' : ''}`}>
                            {filtros.serie ? '✅' : '⏳'} 1. Selecione uma Série {filtros.serie && <span className="font-bold">({filtros.serie})</span>}
                          </p>
                          <p className={`flex items-center justify-center gap-2 ${filtros.componente ? 'text-green-600' : ''}`}>
                            {filtros.componente ? '✅' : '⏳'} 2. Selecione um Componente Curricular {filtros.componente && <span className="font-bold">({filtros.componente})</span>}
                          </p>
                          <p className={`flex items-center justify-center gap-2 ${filtros.habilidadeId ? 'text-green-600' : ''}`}>
                            {filtros.habilidadeId ? '✅' : '⏳'} 3. Selecione uma Habilidade {filtros.habilidade && <span className="font-bold">({filtros.habilidade})</span>}
                          </p>
                        </div>
                      </div>
                    ) : carregandoQuestoes ? (
                      /* v650: Indicador de carregamento enquanto busca no Firestore */
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-8 text-center">
                        <div className="flex justify-center mb-4">
                          <svg className="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-blue-800 mb-1">Carregando questões...</h3>
                        <p className="text-sm text-blue-600">
                          Buscando questões de <span className="font-semibold">{filtros.serie}</span> · <span className="font-semibold">{filtros.componente}</span>
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Banner indicador */}
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 rounded-lg text-center text-sm font-semibold shadow-lg">
                          🔍 CLIQUE NAS QUESTÕES PARA DAR ZOOM!
                        </div>
                    
                    {(tipoGeracao === 'pronta' ? ['inteira'] : ['pequena', 'media', 'grande', 'inteira']).map(tamanhoAtual => {
                      const questoesFiltradas = questoes
                        .filter(q => q.tamanho === tamanhoAtual &&
                                    (!filtros.serie || q.serie === filtros.serie) && 
                                    (!filtros.componente || q.componente === filtros.componente) &&
                                    (!filtros.habilidadeId || q.habilidadeId === filtros.habilidadeId) &&
                                    (!filtros.tamanho || q.tamanho === filtros.tamanho) &&
                                    // v571: Filtrar por módulo - bancos de questões separados
                                    (tipoGeracao === 'pronta' 
                                      ? q.modulo === 'prontas' 
                                      : (tipoGeracao === 'questao' || tipoGeracao === 'avaliacao') 
                                        ? q.modulo === 'questao_avaliacao' 
                                        : true));
                      
                      if (questoesFiltradas.length === 0) return null;

                      return (
                        <div key={tamanhoAtual} className="mb-8 md:mb-4">
                          {tipoGeracao !== 'pronta' && (
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-3 h-3 rounded-full ${
                                tamanhoAtual === 'pequena' ? 'bg-green-500' :
                                tamanhoAtual === 'media' ? 'bg-blue-500' :
                                tamanhoAtual === 'grande' ? 'bg-orange-500' :
                                'bg-purple-500'
                              }`}></div>
                              <span className="text-xs md:text-sm font-bold text-white uppercase">
                                {tamanhoAtual === 'pequena' ? 'Pequenas (~6.5cm)' :
                                 tamanhoAtual === 'media' ? 'Médias (~8.5cm)' :
                                 tamanhoAtual === 'grande' ? 'Grandes (~12cm)' :
                                 'Página Inteira (~22cm)'}
                              </span>
                            </div>
                          )}
                          
                          {/* Carrossel de Questões */}
                          <div className="relative">
                            {questoesFiltradas.length > 0 && (() => {
                              const indiceAtual = indiceQuestaoVisivel[tamanhoAtual];
                              const q = questoesFiltradas[indiceAtual];
                              const selected = isQuestaoSelecionada(q.id);
                              const orderNumber = selected ? getNumeroQuestao(q.id) : 0;
                              
                              return (
                                <div className="relative">
                                  {/* Container com setas alinhadas ao card da questão */}
                                  <div className="flex items-center gap-1 md:gap-2">
                                    {/* Botão Seta Esquerda */}
                                    <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                      {questoesFiltradas.length > 1 && (
                                        <button
                                          onClick={() => navegarQuestao(tamanhoAtual, 'anterior')}
                                          className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                          title="Questão anterior"
                                        >
                                          <ChevronLeft className="w-4 h-4 md:w-6 md:h-6" />
                                        </button>
                                      )}
                                    </div>

                                    {/* Card da Questão - apenas o preview */}
                                    <div className="flex-1">
                                      <ProtectedImageArea 
                                        className="border-2 border-gray-300 rounded-lg bg-white shadow-inner hover:border-blue-400 transition overflow-hidden"
                                        onClick={() => setQuestaoEmVisualizacao(q)}
                                      >
                                        <div className="p-2 md:p-3">
                                          {/* Conteúdo Real da Questão */}
                                          {q.enunciado && (
                                            <p className="text-xs font-semibold text-gray-700 mb-1">{q.enunciado}</p>
                                          )}
                                          {q.texto && (
                                            <div className="bg-gray-50 border-l-2 border-blue-400 p-2 mb-2 text-xs italic line-clamp-4">
                                              {q.texto.substring(0, 200)}{q.texto.length > 200 ? '...' : ''}
                                            </div>
                                          )}
                                          {q.temImagem && (
                                            <div className="my-2 scale-75 origin-top">
                                              <ImagemQuestao tipo={q.tipoImagem} />
                                            </div>
                                          )}
                                          {q.pergunta && (
                                            <p className="text-xs font-medium text-gray-800 mb-2">{q.pergunta}</p>
                                          )}
                                          {q.alternativas && (
                                            <div className="space-y-1">
                                              {q.alternativas.slice(0, 4).map((alt, idx) => (
                                                <div key={idx} className="flex items-center gap-1 text-xs bg-gray-50 p-1 rounded">
                                                  <span className="w-4 h-4 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center text-xs font-bold">
                                                    {alt.charAt(0)}
                                                  </span>
                                                  <span className="truncate">{alt.substring(3)}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          
                                          {/* 🔥 Questões com imagem do Firebase */}
                                          {q.imagem && (q.imagem.startsWith('http') || q.imagem.startsWith('data:')) && (
                                            <div className="flex items-center justify-center h-full">
                                              <ImagemComLoading 
                                                src={q.imagem} 
                                                alt={`Questão ${q.id}`} 
                                                className={`object-contain rounded ${tamanhoAtual === 'inteira' ? 'w-full' : 'max-w-full'}`}
                                                tamanho={tamanhoAtual}
                                                style={{ maxHeight: tamanhoAtual === 'pequena' ? '160px' : tamanhoAtual === 'media' ? '220px' : tamanhoAtual === 'grande' ? '280px' : '550px' }}
                                              />
                                            </div>
                                          )}
                                          
                                          {/* Fallback para questões sem conteúdo detalhado */}
                                          {!q.enunciado && !q.texto && !q.temImagem && !q.imagem && (
                                            <div className="flex items-center justify-center h-full text-gray-400">
                                              <div className="text-center">
                                                <Eye className="w-12 h-12 mx-auto mb-2 opacity-40" />
                                                <p className="text-sm">Questão {q.id}</p>
                                                <p className="text-xs">{getCodigoHabilidadeAtualizado(q)}</p>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </ProtectedImageArea>
                                    </div>

                                    {/* Botão Seta Direita */}
                                    <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                      {questoesFiltradas.length > 1 && (
                                        <button
                                          onClick={() => navegarQuestao(tamanhoAtual, 'proximo')}
                                          className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                          title="Próxima questão"
                                        >
                                          <ChevronRight className="w-4 h-4 md:w-6 md:h-6" />
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {/* Contador + Gabarito + Botão - abaixo do carrossel */}
                                  <div className="mt-1 md:mt-2 space-y-1 md:space-y-2">
                                    <div className="flex items-center justify-center gap-4 md:gap-8 px-2">
                                      <span className="text-[10px] md:text-sm font-semibold text-white bg-gray-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                                        {indiceAtual + 1} de {questoesFiltradas.length}
                                      </span>
                                      {/* v532: Omitir gabarito para plano Gold */}
                                      {!isPlanoGold && (
                                        <span className="text-[10px] md:text-sm px-2 md:px-3 py-0.5 md:py-1 bg-green-100 text-green-700 rounded-full font-bold">
                                          Gabarito: {q.gabarito}
                                        </span>
                                      )}
                                    </div>

                                    {/* Botão QUERO ESSA QUESTÃO/ATIVIDADE */}
                                    <div className="flex justify-center">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          adicionarQuestao(q.id);
                                        }}
                                        className={`px-4 md:px-6 py-1.5 md:py-2.5 rounded-lg font-bold text-[10px] md:text-sm shadow-md transition-all hover:scale-[1.02] flex items-center justify-center whitespace-nowrap ${
                                          selected 
                                            ? 'bg-gray-400 text-white cursor-not-allowed' 
                                            : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-green-700 hover:to-emerald-700'
                                        }`}
                                        disabled={selected}
                                      >
                                        {selected 
                                          ? 'JÁ ADICIONADA'
                                          : (tipoGeracao === 'pronta' ? 'QUERO ESSA ATIVIDADE' : 'QUERO ESSA QUESTÃO')}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                      </>
                    )}
                  </div>

                  {totalQuestoesSelecionadas > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      {/* 🆕 Indicador do Componente/Habilidade Bloqueada */}
                      <div className="mb-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-blue-900 mb-1">
                              {(tipoGeracao === 'pronta' || tipoGeracao === 'questao') ? 'Habilidade e Componente Ativos' : 'Componente Curricular Ativo'}
                            </h4>
                            
                            {/* Para Atividades Prontas e Atividade por Questão */}
                            {(tipoGeracao === 'pronta' || tipoGeracao === 'questao') && habilidadeBloqueada && (
                              <>
                                <p className="text-sm text-blue-800 font-semibold mb-1">
                                  📚 {(filtros.habilidadeId && habilidades.find(h => h.id === filtros.habilidadeId) || habilidades.find(h => h.codigo === habilidadeBloqueada))?.codigo} - {(filtros.habilidadeId && habilidades.find(h => h.id === filtros.habilidadeId) || habilidades.find(h => h.codigo === habilidadeBloqueada))?.conteudo}
                                </p>
                                <p className="text-sm text-blue-800 font-semibold mb-2">
                                  📖 {componenteBloqueado}
                                </p>
                                <p className="text-xs text-blue-700">
                                  Todas as questões desta atividade serão da mesma habilidade (<span className="font-bold">{habilidadeBloqueada}</span>) e 
                                  do componente <span className="font-bold">{componenteBloqueado}</span>. 
                                  Para mudar, remova todas as questões.
                                </p>
                              </>
                            )}

                            {/* Para Avaliação */}
                            {tipoGeracao === 'avaliacao' && componenteBloqueado && (
                              <>
                                <p className="text-sm text-blue-800 font-semibold mb-2">
                                  📚 {componenteBloqueado}
                                </p>
                                <p className="text-xs text-blue-700">
                                  Todas as questões desta avaliação serão de <span className="font-bold">{componenteBloqueado}</span>. 
                                  Para mudar o componente, remova todas as questões.
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Coluna Direita - Pré-visualização */}
                <div className="lg:col-span-6 bg-white p-4 md:p-6 rounded-xl shadow-sm">
                  {/* v475: Calcular número real de páginas baseado nos tamanhos */}
                  {(() => {
                    const todasQuestoes = paginas.flatMap(p => p.questoes);
                    const paginasCalculadas = totalQuestoesSelecionadas > 0 
                      ? calcularPaginasPorTamanho(todasQuestoes, questoes, true)
                      : 0;
                    
                    return (
                      <>
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                          <h3 className="font-bold text-sm md:text-lg flex items-center gap-2">
                            <Eye className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                            Pré-visualização
                          </h3>
                          {totalQuestoesSelecionadas > 0 && (
                            <span className="text-xs md:text-sm bg-blue-100 text-blue-700 px-2 md:px-3 py-1 rounded-full font-semibold">
                              {totalQuestoesSelecionadas} {totalQuestoesSelecionadas === 1 ? 'questão' : 'questões'} • {paginasCalculadas} {paginasCalculadas === 1 ? 'página' : 'páginas'}
                            </span>
                          )}
                        </div>

                        {/* Navegação entre Páginas - v567: Ocultar páginas vazias */}
                        {paginas.length > 1 && (
                          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                            {paginas.map(p => (
                              <button
                                key={p.id}
                                onClick={() => setPaginaAtiva(p.id)}
                                className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition ${
                                  paginaAtiva === p.id
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                Página {p.id} ({p.questoes.length})
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Área de Pré-visualização - ALTURA FIXA no desktop, auto no mobile */}
                  <div className="border-2 border-gray-300 rounded-lg bg-white shadow-inner overflow-y-auto" style={{ minHeight: '400px', maxHeight: '650px' }}>
                    {paginas.map(pagina => {
                      if (paginas.length > 1 && pagina.id !== paginaAtiva) return null;
                      
                      return (
                        <div key={pagina.id} className="p-3 md:p-6">
                          {/* Indicador de Página (quando múltiplas) - v567: Ocultar em Atividades Prontas */}
                          {paginas.length > 1 && tipoGeracao !== 'pronta' && (
                            <div className="text-center mb-3 md:mb-4">
                              <span className="inline-block bg-gray-200 text-gray-700 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-semibold">
                                Página {pagina.id} de {paginas.length}
                              </span>
                            </div>
                          )}

                          {/* Cabeçalho do Documento - APENAS NA PRIMEIRA PÁGINA - Responsivo */}
                          {pagina.id === 1 && (
                            <div className="pb-2 md:pb-3 mb-3 md:mb-6">
                              {/* Layout igual ao desktop, apenas com tamanhos menores no mobile */}
                              <div className="flex gap-2 md:gap-3">
                                {/* v654: Logo real na pré-visualização */}
                                {cabecalhoConfig.logoUrl && (
                                  <div className="flex-shrink-0">
                                    <img 
                                      src={cabecalhoConfig.logoUrl} 
                                      alt="Logo" 
                                      className="w-10 h-10 md:w-16 md:h-16 object-contain"
                                    />
                                  </div>
                                )}
                                {!cabecalhoConfig.logoUrl && (
                                  <div className="flex-shrink-0">
                                    <div className="w-10 h-12 md:w-16 md:h-16 border border-dashed md:border-2 border-gray-400 rounded md:rounded-lg flex items-center justify-center bg-gray-50">
                                      <span className="text-[8px] md:text-xs text-gray-400">Logo</span>
                                    </div>
                                  </div>
                                )}
                                
                                <div className="flex-1 min-w-0">
                                  {/* Linha 1: Nome da Escola */}
                                  <div className="font-bold text-[11px] md:text-base text-gray-900 mb-0.5 md:mb-1">
                                    {cabecalhoConfig.nomeEscola || 'Nome da Escola'}
                                  </div>
                                  
                                  {/* Linha 2: Professor | Série | Turma - layout tabular */}
                                  <div className="flex text-[9px] md:text-sm mb-0.5 md:mb-1 text-gray-700">
                                    <div className="md:w-[55%]">
                                      <span className="hidden md:inline">Professor(a): </span>
                                      <span className="md:hidden">Prof: </span>
                                      <span className="font-semibold">{cabecalhoConfig.professor || 'Nome'}</span>
                                    </div>
                                    <div className="ml-2 md:ml-0 md:w-[25%]">
                                      Série: <span className="font-semibold">{cabecalhoConfig.serie || '___'}</span>
                                    </div>
                                    <div className="ml-2 md:ml-0 md:w-[20%]">
                                      Turma: <span className="font-semibold">{cabecalhoConfig.turma || '___'}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Linha 3: Aluno | Data - layout tabular */}
                                  <div className="flex text-[9px] md:text-sm text-gray-600">
                                    <div className="flex items-end flex-1 md:flex-none md:w-[80%]">
                                      <span className="whitespace-nowrap">Aluno(a):</span>
                                      <div className="flex-1 border-b border-gray-400 mx-1"></div>
                                    </div>
                                    <div className="flex items-end shrink-0">
                                      <span className="whitespace-nowrap">Data:</span>
                                      <div className="border-b border-gray-400 w-12 md:w-16 ml-1"></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Título da Atividade/Avaliação - Responsivo */}
                          {pagina.id === 1 && (
                            <div className="text-center mb-2 md:mb-4">
                              <h2 className="text-[10px] md:text-xl font-bold text-gray-900 uppercase">
                                {getTituloDocumento()}
                              </h2>
                              {(tipoGeracao === 'pronta' || tipoGeracao === 'questao') && totalQuestoesSelecionadas > 0 && (
                                <p className="text-[8px] md:text-xs text-gray-600 mt-0.5 md:mt-1 leading-tight">
                                  {getHabilidadesConteudos()}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Questões da Página */}
                          {pagina.questoes.length === 0 ? (
                            <div className="text-center py-8 md:py-16 text-gray-400">
                              <FileText className="w-12 h-12 md:w-20 md:h-20 mx-auto mb-2 md:mb-4 opacity-30" />
                              <p className="text-sm md:text-lg font-semibold mb-1 md:mb-2">
                                {paginas.length === 1 ? 'Nenhuma questão selecionada' : `Página ${pagina.id} vazia`}
                              </p>
                              <p className="text-xs md:text-sm">Selecione questões na coluna à esquerda</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {pagina.questoes.map((qId) => {
                                const questao = questoes.find(q => String(q.id) === String(qId));
                                if (!questao) return null;
                                
                                const numeroGlobal = getNumeroQuestao(qId);
                                
                                return (
                                  <div key={qId} className="relative">
                                    {/* BOTÃO X PARA REMOVER QUESTÃO */}
                                    <button
                                      onClick={() => {
                                        setPaginas(paginas.map(p => ({
                                          ...p,
                                          questoes: p.questoes.filter(id => String(id) !== String(qId))
                                        })));
                                      }}
                                      className="absolute right-0 top-0 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                      title="Remover questão"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>

                                    {/* TÍTULO DA QUESTÃO - retângulo cinza igual ao PDF */}
                                    {/* v567: Ocultar em Atividades Prontas (já tem identificação na imagem) */}
                                    {tipoGeracao !== 'pronta' && (
                                      <div className="mb-1">
                                        <span className="inline-block bg-gray-200 text-gray-700 text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1">
                                          QUESTÃO {String(numeroGlobal).padStart(2, '0')}
                                        </span>
                                      </div>
                                    )}

                                    {/* Área da questão - IMAGEM ou PLACEHOLDER - alinhada com logo */}
                                    {questao.imagem && (questao.imagem.startsWith('http') || questao.imagem.startsWith('data:')) ? (
                                      <div className="bg-white overflow-hidden">
                                        <img 
                                          src={questao.imagem} 
                                          alt={`Questão ${numeroGlobal}`} 
                                          className="max-w-full object-contain"
                                        />
                                      </div>
                                    ) : (
                                      <div 
                                        className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400"
                                        style={{ minHeight: '150px' }}
                                      >
                                        <div className="text-center">
                                          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                          <p className="text-sm font-semibold">Questão {numeroGlobal}</p>
                                          <p className="text-xs">{questao.serie} - {questao.componente}</p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Botões de Ação - ABAIXO DO LAYOUT - Responsivo */}
                  <div className="mt-3 md:mt-4 flex gap-2 md:gap-3">
                    <button
                      onClick={() => {
                        adicionarNovaPagina();
                      }}
                      className="w-auto md:flex-1 bg-white border-2 border-blue-600 text-blue-600 px-3 md:px-6 py-2 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-1 md:gap-2 hover:bg-blue-50 transition text-xs md:text-base whitespace-nowrap"
                    >
                      <Plus className="w-4 h-4 md:w-5 md:h-5" />
                      <span className="hidden md:inline">Nova Página</span>
                      <span className="md:hidden">Nova Pág.</span>
                    </button>
                    
                    {totalQuestoesSelecionadas > 0 && !pdfGerado && (
                      <div className="flex-1 bg-green-50 border-2 border-green-200 rounded-lg p-2 md:p-3">
                        <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0" />
                          <div className="text-xs md:text-sm text-green-800 flex-1 min-w-0">
                            <p className="font-semibold">Pronto para gerar!</p>
                            <p className="text-[10px] md:text-xs">{totalQuestoesSelecionadas} questões em {calcularPaginasPorTamanho(paginas.flatMap(p => p.questoes), questoes, true)} página(s)</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => {
                            setShowModalGerarPDF(true);
                          }}
                          className="w-full py-1.5 md:py-2 rounded-lg font-bold text-xs md:text-sm flex items-center justify-center gap-1 md:gap-2 text-white hover:opacity-90 transition whitespace-nowrap"
                          style={{backgroundColor: '#f7941d'}}
                        >
                          <Download className="w-3 h-3 md:w-4 md:h-4" />
                          Gerar PDF
                        </button>
                      </div>
                    )}
                    
                    {/* Botão Baixar PDF - aparece após gerar */}
                    {pdfGerado && (
                      <div className="flex-1 bg-green-50 border-2 border-green-500 rounded-lg p-2 md:p-3">
                        <div className="flex items-center gap-2 mb-1 md:mb-2">
                          <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-600 flex-shrink-0" />
                          <p className="text-xs md:text-sm font-semibold text-green-800">PDF Gerado!</p>
                        </div>
                        <div className="flex gap-1 md:gap-2">
                          <button
                            onClick={async () => {
                              try {
                                const response = await fetch(pdfGerado.url);
                                const blob = await response.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = pdfGerado.nome;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                // v475: Limpar após download
                                setPdfGerado(null);
                              } catch (error) {
                                console.error('Erro ao baixar:', error);
                                window.open(pdfGerado.url, '_blank');
                                // v475: Limpar mesmo com erro
                                setPdfGerado(null);
                              }
                            }}
                            className="flex-1 py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 text-white bg-green-600 hover:bg-green-700 transition"
                          >
                            <Download className="w-4 h-4" />
                            Baixar PDF
                          </button>
                          <button
                            onClick={() => setPdfGerado(null)}
                            className="px-3 py-2 rounded-lg font-bold text-xs text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                            title="Criar nova atividade"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {temAcessoLiberado && tipoGeracao === 'simulado' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
                {/* Coluna Esquerda - Seleção de Questões do Simulado */}
                <div className="lg:col-span-6 rounded-xl p-4 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
                  <div className="mb-3 md:mb-4">
                    <div className="bg-blue-900 bg-opacity-50 border border-blue-400 rounded-lg p-3 md:p-4 mb-3 md:mb-4">
                      <div className="flex items-start gap-2 md:gap-3">
                        <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-blue-100 text-xs md:text-sm mb-1">Simulado SAEB</h4>
                          <p className="text-[10px] md:text-xs text-blue-200">22 questões (11 Bloco 1 + 11 Bloco 2) do mesmo componente • 2º e 5º ano</p>
                          <p className="text-[10px] md:text-xs text-blue-200">26 questões (13 Bloco 1 + 13 Bloco 2) do mesmo componente • 9º ano</p>
                        </div>
                      </div>
                    </div>

                    <h3 className="font-bold text-sm md:text-lg mb-3 md:mb-4 text-white">Selecione Questões para Simulado</h3>
                    
                    {/* Seletor de Componente Curricular do Simulado - PRIMEIRO */}
                    <div className="mb-3 md:mb-4 p-2 md:p-3 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl">
                      <div className="text-[10px] md:text-xs text-gray-600 mb-2 font-semibold uppercase">Componente Curricular:</div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setComponenteSimulado('Língua Portuguesa');
                            setBlocosSimulado([
                              { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                              { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                            ]);
                            setBlocoAtivo(1);
                          }}
                          className={`flex-1 py-1.5 md:py-2 rounded-lg font-semibold text-[10px] md:text-sm transition ${
                            componenteSimulado === 'Língua Portuguesa'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          📚 <span className="hidden md:inline">Língua </span>Portuguesa
                        </button>
                        <button
                          onClick={() => {
                            setComponenteSimulado('Matemática');
                            setBlocosSimulado([
                              { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                              { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                            ]);
                            setBlocoAtivo(1);
                          }}
                          className={`flex-1 py-1.5 md:py-2 rounded-lg font-semibold text-[10px] md:text-sm transition ${
                            componenteSimulado === 'Matemática'
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          🔢 Matemática
                        </button>
                      </div>
                    </div>

                    {/* Seletor de Tipo de Filtro - apenas para Língua Portuguesa */}
                    {componenteSimulado === 'Língua Portuguesa' && (
                      <div className="mb-3 md:mb-4 p-2 md:p-3 bg-gradient-to-r from-purple-50 to-orange-50 border-2 border-purple-200 rounded-xl">
                        <div className="text-[10px] md:text-xs text-gray-600 mb-2 font-semibold uppercase">Tipo de Questão:</div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setTipoFiltroSimulado('habilidade-unica');
                              setFiltros({...filtros, habilidade: ''});
                            }}
                            className={`flex-1 py-1.5 md:py-2 px-2 md:px-3 rounded-lg font-semibold text-[10px] md:text-sm transition flex items-center justify-center gap-1 md:gap-2 ${
                              tipoFiltroSimulado === 'habilidade-unica'
                                ? 'bg-purple-600 text-white shadow-md'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <Target className="w-3 h-3 md:w-4 md:h-4" />
                            <span className="hidden md:inline">Habilidade Única</span>
                            <span className="md:hidden">Hab. Única</span>
                          </button>
                          <button
                            onClick={() => {
                              setTipoFiltroSimulado('texto-multiplas');
                              setFiltros({...filtros, habilidade: ''});
                            }}
                            className={`flex-1 py-1.5 md:py-2 px-2 md:px-3 rounded-lg font-semibold text-[10px] md:text-sm transition flex items-center justify-center gap-1 md:gap-2 ${
                              tipoFiltroSimulado === 'texto-multiplas'
                                ? 'bg-orange-500 text-white shadow-md'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <Layers className="w-3 h-3 md:w-4 md:h-4" />
                            <span className="hidden md:inline">Texto c/ Hab. Variadas</span>
                            <span className="md:hidden">Texto Hab. Variadas</span>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 md:mb-4">
                      <select 
                        className="px-2 md:px-3 py-2 border rounded-lg text-xs md:text-sm bg-white" 
                        onChange={(e) => {
                          setFiltros({...filtros, serie: e.target.value, habilidade: ''});
                          // 🆕 Atualiza automaticamente a série no cabeçalho e configSimulado
                          if (e.target.value) {
                            setCabecalhoConfig({...cabecalhoConfig, serie: e.target.value});
                            setConfigSimulado({...configSimulado, serie: e.target.value});
                          }
                          // v529: Reiniciar contador de questões ao mudar série
                          setIndiceQuestaoVisivel({ pequena: 0, media: 0, grande: 0, inteira: 0, 'texto-multiplas': 0 });
                        }}
                        value={filtros.serie}
                      >
                        <option value="">Selecione uma Série</option>
                        {/* 🆕 Filtrar séries SAEB baseado em seriesLiberadas do usuário */}
                        {/* v689: Usar seriesEfetivas para professor vinculado */}
                        {['2º ano', '5º ano', '9º ano']
                          .filter(serie => {
                            // Admin vê todas as séries
                            if (currentUser?.tipo === 'admin') return true;
                            // v689: Usar seriesEfetivas
                            if (!seriesEfetivas || seriesEfetivas.length === 0) return true;
                            // v689d: Comparação case-insensitive
                            return seriesEfetivas.some(se => se.toLowerCase() === serie.toLowerCase());
                          })
                          .map(serie => (
                            <option key={serie} value={serie}>{serie}</option>
                          ))}
                      </select>
                      
                      {/* Dropdown de Habilidade - só aparece se for "Habilidade Única" */}
                      {(componenteSimulado !== 'Língua Portuguesa' || tipoFiltroSimulado === 'habilidade-unica') && (
                        <select 
                          className={`px-2 md:px-3 py-2 border rounded-lg text-xs md:text-sm ${!filtros.serie ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'}`}
                          onChange={(e) => {
                            const habSelecionada = habilidades.find(h => h.id === e.target.value);
                            setFiltros({
                              ...filtros, 
                              habilidadeId: e.target.value,
                              habilidade: habSelecionada?.codigo || ''
                            });
                            // v529: Reiniciar contador de questões ao mudar habilidade
                            setIndiceQuestaoVisivel({ pequena: 0, media: 0, grande: 0, inteira: 0, 'texto-multiplas': 0 });
                          }}
                          disabled={!filtros.serie}
                          value={filtros.habilidadeId || ''}
                        >
                          <option value="">Selecione uma Habilidade</option>
                          {habilidades
                            .filter(h => h.serie === filtros.serie && 
                              h.componente === `${componenteSimulado} SAEB`
                            )
                            .map(h => (
                            <option key={h.id} value={h.id}>
                              ({h.codigo}) - {h.conteudo}
                            </option>
                          ))}
                        </select>
                      )}
                      
                      {/* Info quando for Texto com Habilidades Variadas */}
                      {componenteSimulado === 'Língua Portuguesa' && tipoFiltroSimulado === 'texto-multiplas' && (
                        <div className="px-2 md:px-3 py-2 bg-orange-100 border border-orange-300 rounded-lg text-xs md:text-sm text-orange-700 flex items-center gap-2">
                          <Layers className="w-3 h-3 md:w-4 md:h-4" />
                          <span className="text-[10px] md:text-sm">Textos c/ múltiplas hab.</span>
                        </div>
                      )}
                      
                      {/* Filtro de Tamanho - só aparece se for Habilidade Única */}
                      {(componenteSimulado !== 'Língua Portuguesa' || tipoFiltroSimulado === 'habilidade-unica') && (
                        <select className="px-2 md:px-3 py-2 border rounded-lg text-xs md:text-sm bg-white" onChange={(e) => setFiltros({...filtros, tamanho: e.target.value})}>
                          <option value="">Todos tamanhos</option>
                          <option value="pequena">Pequena (~6.5cm)</option>
                          <option value="media">Média (~8.5cm)</option>
                          <option value="grande">Grande (~12cm)</option>
                          <option value="inteira">Inteira (~22cm)</option>
                        </select>
                      )}
                    </div>

                    {/* Tabs de Blocos - Bloco 2 só clicável quando Bloco 1 completo */}
                    <div className="flex gap-2 mb-3 md:mb-4">
                      {blocosSimulado.map(bloco => {
                        const bloco1Completo = contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) >= questoesPorBlocoSAEB(configSimulado.serie);
                        const desabilitado = bloco.id === 2 && !bloco1Completo;
                        
                        return (
                          <button
                            key={bloco.id}
                            onClick={() => {
                              if (!desabilitado) {
                                setBlocoAtivo(bloco.id);
                                // Mostrar página de questões do bloco na pré-visualização
                                setPaginaSimuladoAtiva(bloco.id === 1 ? 3 : 5);
                              }
                            }}
                            disabled={desabilitado}
                            className={`flex-1 py-2 md:py-3 rounded-lg font-semibold text-xs md:text-sm transition ${
                              desabilitado
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                : blocoAtivo === bloco.id 
                                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title={desabilitado ? 'Complete o Bloco 1 primeiro' : ''}
                          >
                            <div>{bloco.nome}</div>
                            <div className="text-xs opacity-80">
                              {contarQuestoesReais(bloco.questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(configSimulado.serie)} questões
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Lista de Questões Ordenada por Tamanho - CARROSSEL - SEM SCROLL */}
                  <div className="space-y-4">
                    {/* Verificar se série foi selecionada (e habilidade se for o modo habilidade-unica) */}
                    {(!filtros.serie || (componenteSimulado === 'Língua Portuguesa' && tipoFiltroSimulado === 'habilidade-unica' && !filtros.habilidadeId) || (componenteSimulado !== 'Língua Portuguesa' && !filtros.habilidadeId)) ? (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-8 text-center">
                        <div className="text-5xl mb-4">🔍</div>
                        <h3 className="text-xl font-bold text-amber-800 mb-3">Selecione os filtros para ver as questões</h3>
                        <div className="space-y-2 text-amber-700">
                          <p className={`flex items-center justify-center gap-2 ${filtros.serie ? 'text-green-600' : ''}`}>
                            {filtros.serie ? '✅' : '⏳'} 1. Selecione uma Série {filtros.serie && <span className="font-bold">({filtros.serie})</span>}
                          </p>
                          <p className="flex items-center justify-center gap-2 text-green-600">
                            ✅ 2. Componente Curricular: <span className="font-bold">{componenteSimulado}</span>
                          </p>
                          {(componenteSimulado !== 'Língua Portuguesa' || tipoFiltroSimulado === 'habilidade-unica') && (
                            <p className={`flex items-center justify-center gap-2 ${filtros.habilidadeId ? 'text-green-600' : ''}`}>
                              {filtros.habilidadeId ? '✅' : '⏳'} 3. Selecione uma Habilidade {filtros.habilidade && <span className="font-bold">({filtros.habilidade})</span>}
                            </p>
                          )}
                          {componenteSimulado === 'Língua Portuguesa' && tipoFiltroSimulado === 'texto-multiplas' && (
                            <p className="flex items-center justify-center gap-2 text-green-600">
                              ✅ 3. Tipo: <span className="font-bold">Texto com Habilidades Variadas</span>
                            </p>
                          )}
                        </div>
                      </div>
                    ) : carregandoQuestoes ? (
                      /* v650: Spinner Simulado SAEB - Professor */
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-8 text-center">
                        <div className="flex justify-center mb-4">
                          <svg className="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-blue-800 mb-1">Carregando questões...</h3>
                        <p className="text-sm text-blue-600">
                          Buscando questões de <span className="font-semibold">{filtros.serie}</span> · <span className="font-semibold">{componenteSimulado}</span>
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Banner indicador */}
                        <div className={`text-white p-3 rounded-lg text-center text-sm font-semibold shadow-lg mb-4 ${
                          tipoFiltroSimulado === 'texto-multiplas' 
                            ? 'bg-gradient-to-r from-orange-500 to-pink-500' 
                            : 'bg-gradient-to-r from-purple-500 to-pink-500'
                        }`}>
                          🔍 CLIQUE NAS QUESTÕES PARA DAR ZOOM!
                        </div>
                    {/* Renderização condicional baseada no tipo de filtro */}
                    {componenteSimulado === 'Língua Portuguesa' && tipoFiltroSimulado === 'texto-multiplas' ? (
                      // MODO: Texto com Habilidades Variadas - só mostra questões texto-multiplas
                      (() => {
                        const questoesFiltradas = questoes.filter(q => 
                          q.tamanho === 'texto-multiplas' &&
                          (q.componente === 'Língua Portuguesa SAEB' || q.componente === 'Língua Portuguesa' || q.componente === componenteSimulado) &&
                          q.serie === filtros.serie &&
                          q.modulo === 'simulado' // v572: Filtrar apenas questões do módulo simulado
                        );
                        
                        if (questoesFiltradas.length === 0) {
                          return (
                            <div className="bg-gray-100 rounded-xl p-8 text-center">
                              <div className="text-4xl mb-3">📄</div>
                              <p className="text-gray-600">Nenhum texto com múltiplas questões encontrado para {filtros.serie}</p>
                            </div>
                          );
                        }

                        return (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                              <span className="text-sm font-bold text-white uppercase">
                                📄 Textos com Habilidades Variadas ({questoesFiltradas.length} disponíveis)
                              </span>
                            </div>
                            
                            <div className="relative">
                              {(() => {
                                const indiceAtual = indiceQuestaoVisivel['texto-multiplas'] || 0;
                                const q = questoesFiltradas[indiceAtual % questoesFiltradas.length];
                                const blocoAtual = blocosSimulado.find(b => b.id === blocoAtivo);
                                // 🔧 v475: Verificar se questão está em QUALQUER bloco (impede duplicação)
                                const selectedNoBloco1 = blocosSimulado[0]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                                const selectedNoBloco2 = blocosSimulado[1]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                                const selected = selectedNoBloco1 || selectedNoBloco2;
                                const questoesReaisNoBloco = contarQuestoesReais(blocoAtual?.questoesSelecionadas || [], questoes);
                                const questoesDoTexto = q?.questoesVinculadas?.length || 1;
                                const blocoCompleto = questoesReaisNoBloco >= questoesPorBlocoSAEB(configSimulado.serie);
                                const blocoFicariaCheio = questoesReaisNoBloco + questoesDoTexto > questoesPorBlocoSAEB(configSimulado.serie);
                                const orderNumber = selected ? blocoAtual.questoesSelecionadas.findIndex(item => String(item) === String(q.id)) + 1 : 0;
                                
                                return (
                                  <div className="relative">
                                    <div className="flex items-start gap-2">
                                      {/* Seta Esquerda */}
                                      <div className="flex-shrink-0 w-10 pt-16">
                                        {questoesFiltradas.length > 1 && (
                                          <button
                                            onClick={() => navegarQuestao('texto-multiplas', 'anterior')}
                                            className="w-10 h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                          >
                                            <ChevronLeft className="w-6 h-6" />
                                          </button>
                                        )}
                                      </div>

                                      {/* Card da Questão */}
                                      <div className="flex-1 space-y-3">
                                        <ProtectedImageArea 
                                          className="border-2 border-orange-300 rounded-lg bg-white shadow-inner hover:border-orange-400 transition overflow-hidden"
                                          onClick={() => setQuestaoEmVisualizacao(q)}
                                        >
                                          <div className="p-4 max-h-[500px] overflow-y-auto">
                                            {/* Texto Base */}
                                            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3 mb-3">
                                              <div className="flex items-center gap-2 mb-2">
                                                <Layers className="w-4 h-4 text-orange-500" />
                                                <span className="text-xs font-bold text-orange-700">TEXTO BASE</span>
                                              </div>
                                              {q.textoBase ? (
                                                <p className="text-xs text-gray-700 whitespace-pre-line line-clamp-6">{q.textoBase}</p>
                                              ) : (
                                                <div className="bg-gray-100 h-20 rounded flex items-center justify-center text-gray-400 text-xs">
                                                  [Imagem do texto]
                                                </div>
                                              )}
                                            </div>
                                            {/* Questões Vinculadas */}
                                            <div className="space-y-2">
                                              <span className="text-xs font-bold text-pink-600">📝 {q.questoesVinculadas?.length} QUESTÕES VINCULADAS:</span>
                                              {q.questoesVinculadas?.map((qv, idx) => (
                                                <div key={idx} className="bg-pink-50 border border-pink-200 rounded p-2">
                                                  <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold text-pink-700">Q{idx + 1} - {qv.habilidade}</span>
                                                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold">Gab: {qv.gabarito}</span>
                                                  </div>
                                                  {qv.enunciado && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{qv.enunciado}</p>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        </ProtectedImageArea>

                                        {/* Info */}
                                        <div className="flex items-center justify-center gap-8 px-2">
                                          <span className="text-sm font-semibold text-white bg-gray-700 px-3 py-1 rounded-full">
                                            {(indiceAtual % questoesFiltradas.length) + 1} de {questoesFiltradas.length}
                                          </span>
                                          <span className="text-sm px-3 py-1 bg-orange-100 text-orange-700 rounded-full font-bold">
                                            {q.questoesVinculadas?.length} questões | Gab: {q.questoesVinculadas?.map(qv => qv.gabarito).join(', ')}
                                          </span>
                                        </div>

                                        {/* Botão */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (selected) {
                                              setBlocosSimulado(blocosSimulado.map(b => 
                                                b.id === blocoAtivo 
                                                  ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(q.id))}
                                                  : b
                                              ));
                                            } else if (!blocoCompleto && blocoFicariaCheio) {
                                              // Mostrar modal de questões excedentes
                                              setQuestaoExcedenteInfo({
                                                questao: q,
                                                blocoId: blocoAtivo,
                                                questoesNoBloco: questoesReaisNoBloco,
                                                questoesDoTexto: questoesDoTexto,
                                                espacoDisponivel: questoesPorBlocoSAEB(configSimulado.serie) - questoesReaisNoBloco,
                                                painel: 'prof'
                                              });
                                              setShowModalQuestoesExcedentes(true);
                                            } else if (!blocoCompleto) {
                                              const novaLista = [...blocoAtual.questoesSelecionadas, q.id];
                                              setBlocosSimulado(blocosSimulado.map(b => 
                                                b.id === blocoAtivo 
                                                  ? {...b, questoesSelecionadas: novaLista}
                                                  : b
                                              ));
                                              // Mostrar página de questões do bloco na pré-visualização
                                              setPaginaSimuladoAtiva(blocoAtivo === 1 ? 3 : 5);
                                              const novoTotal = contarQuestoesReais(novaLista, questoes);
                                              if (blocoAtivo === 1 && novoTotal >= questoesPorBlocoSAEB(configSimulado.serie)) {
                                                setShowModalSegundoBloco(true);
                                              }
                                            }
                                          }}
                                          className={`w-full py-3 rounded-lg font-bold text-sm shadow-md transition-all hover:scale-[1.02] flex items-center justify-center ${
                                            selected 
                                              ? 'bg-gray-400 text-white cursor-not-allowed' 
                                              : blocoCompleto
                                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                              : blocoFicariaCheio
                                              ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                                              : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-green-700 hover:to-emerald-700'
                                          }`}
                                          disabled={selected || blocoCompleto}
                                        >
                                          {selected ? 'TEXTO JÁ ADICIONADO' : blocoCompleto ? `BLOCO COMPLETO (${questoesReaisNoBloco}/${questoesPorBlocoSAEB(configSimulado.serie)})` : blocoFicariaCheio ? `⚠️ EXCEDE LIMITE (${questoesReaisNoBloco}+${questoesDoTexto}>${questoesPorBlocoSAEB(configSimulado.serie)})` : `QUERO ESTE TEXTO (+${questoesDoTexto} questões)`}
                                        </button>
                                      </div>

                                      {/* Seta Direita */}
                                      <div className="flex-shrink-0 w-10 pt-16">
                                        {questoesFiltradas.length > 1 && (
                                          <button
                                            onClick={() => navegarQuestao('texto-multiplas', 'proximo')}
                                            className="w-10 h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                          >
                                            <ChevronRight className="w-6 h-6" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      // MODO: Habilidade Única - filtra por habilidade específica (exclui texto-multiplas)
                    ['pequena', 'media', 'grande', 'inteira'].map(tamanhoAtual => {
                      const componentesFiltro = componenteSimulado === 'Língua Portuguesa' 
                        ? ['Língua Portuguesa', 'Língua Portuguesa SAEB']
                        : ['Matemática', 'Matemática SAEB'];
                      const questoesFiltradas = questoes
                        .filter(q => q.tamanho === tamanhoAtual &&
                                    componentesFiltro.includes(q.componente) &&
                                    (!filtros.serie || q.serie === filtros.serie) &&
                                    (!filtros.habilidadeId || q.habilidadeId === filtros.habilidadeId) &&
                                    (!filtros.tamanho || q.tamanho === filtros.tamanho) &&
                                    q.modulo === 'simulado'); // v572: Filtrar apenas questões do módulo simulado
                      
                      if (questoesFiltradas.length === 0) return null;

                      return (
                        <div key={tamanhoAtual} className="mb-6 md:mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-3 h-3 rounded-full ${
                              tamanhoAtual === 'pequena' ? 'bg-green-500' :
                              tamanhoAtual === 'media' ? 'bg-blue-500' :
                              tamanhoAtual === 'grande' ? 'bg-orange-500' :
                              'bg-purple-500'
                            }`}></div>
                            <span className="text-xs md:text-sm font-bold text-white uppercase">
                              {tamanhoAtual === 'pequena' ? 'Pequenas (~6.5cm)' :
                               tamanhoAtual === 'media' ? 'Médias (~8.5cm)' :
                               tamanhoAtual === 'grande' ? 'Grandes (~12cm)' :
                               'Página Inteira (~22cm)'}
                            </span>
                          </div>
                          
                          {/* Carrossel de Questões */}
                          <div className="relative">
                            {questoesFiltradas.length > 0 && (() => {
                              const indiceAtual = indiceQuestaoVisivel[tamanhoAtual] || 0;
                              // Garantir que o índice está dentro dos limites
                              const indiceSafe = Math.min(indiceAtual, questoesFiltradas.length - 1);
                              const q = questoesFiltradas[indiceSafe];
                              if (!q) return null; // Segurança extra
                              const blocoAtual = blocosSimulado.find(b => b.id === blocoAtivo);
                              // 🔧 v475: Verificar se questão está em QUALQUER bloco (impede duplicação)
                              const selectedNoBloco1 = blocosSimulado[0]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                              const selectedNoBloco2 = blocosSimulado[1]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                              const selected = selectedNoBloco1 || selectedNoBloco2;
                              const questoesReaisNoBloco = contarQuestoesReais(blocoAtual?.questoesSelecionadas || [], questoes);
                              const blocoCompleto = questoesReaisNoBloco >= questoesPorBlocoSAEB(configSimulado.serie);
                              const orderNumber = selected ? blocoAtual.questoesSelecionadas.findIndex(item => String(item) === String(q.id)) + 1 : 0;
                              
                              return (
                                <div className="relative">
                                  {/* Container com setas e card */}
                                  <div className="flex items-center gap-1 md:gap-2">
                                    {/* Botão Seta Esquerda */}
                                    <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                      {questoesFiltradas.length > 1 && (
                                        <button
                                          onClick={() => navegarQuestao(tamanhoAtual, 'anterior')}
                                          className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                          title="Questão anterior"
                                        >
                                          <ChevronLeft className="w-4 h-4 md:w-6 md:h-6" />
                                        </button>
                                      )}
                                    </div>

                                    {/* Card da Questão */}
                                    <div className="flex-1">
                                      {/* Área de Pré-Visualização CLICÁVEL com PROTEÇÃO */}
                                      <ProtectedImageArea 
                                        className="border-2 border-gray-300 rounded-lg bg-white shadow-inner hover:border-blue-400 transition overflow-hidden"
                                        onClick={() => setQuestaoEmVisualizacao(q)}
                                      >
                                        <div className={`p-2 md:p-3 ${
                                        tamanhoAtual === 'inteira' ? '' :
                                        tamanhoAtual === 'pequena' ? 'max-h-48 overflow-hidden' :
                                        tamanhoAtual === 'media' ? 'max-h-64 overflow-hidden' :
                                        tamanhoAtual === 'grande' ? 'max-h-80 overflow-hidden' :
                                        tamanhoAtual === 'texto-multiplas' ? 'max-h-[500px] overflow-hidden' :
                                        'max-h-96 overflow-hidden'
                                      }`}>
                                        {/* Conteúdo para tipo texto-multiplas */}
                                        {q.tamanho === 'texto-multiplas' ? (
                                          <div className="space-y-3">
                                            {/* Texto Base */}
                                            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3">
                                              <div className="flex items-center gap-2 mb-2">
                                                <Layers className="w-4 h-4 text-orange-500" />
                                                <span className="text-xs font-bold text-orange-700">TEXTO BASE</span>
                                              </div>
                                              {q.textoBase ? (
                                                <p className="text-xs text-gray-700 whitespace-pre-line line-clamp-6">{q.textoBase}</p>
                                              ) : q.textoImagem ? (
                                                <img src={q.textoImagem} alt="Texto" className="max-h-32 rounded" />
                                              ) : (
                                                <div className="bg-gray-100 h-20 rounded flex items-center justify-center text-gray-400 text-xs">
                                                  [Imagem do texto]
                                                </div>
                                              )}
                                            </div>
                                            {/* Questões Vinculadas */}
                                            <div className="space-y-2">
                                              <span className="text-xs font-bold text-pink-600">📝 {q.questoesVinculadas?.length} QUESTÕES VINCULADAS:</span>
                                              {q.questoesVinculadas?.map((qv, idx) => (
                                                <div key={idx} className="bg-pink-50 border border-pink-200 rounded p-2">
                                                  <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold text-pink-700">Q{idx + 1} - {qv.habilidade}</span>
                                                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold">Gab: {qv.gabarito}</span>
                                                  </div>
                                                  {qv.enunciado && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{qv.enunciado}</p>}
                                                  {qv.alternativas && (
                                                    <div className="grid grid-cols-2 gap-1 mt-1">
                                                      {qv.alternativas.map((alt, i) => (
                                                        <span key={i} className={`text-xs px-1 py-0.5 rounded truncate ${alt.startsWith(qv.gabarito) ? 'bg-green-200 text-green-800 font-bold' : 'bg-gray-100'}`}>
                                                          {alt}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ) : (
                                          <>
                                        {/* Conteúdo Real da Questão */}
                                        {q.enunciado && (
                                          <p className="text-xs font-semibold text-gray-700 mb-1">{q.enunciado}</p>
                                        )}
                                        {q.texto && (
                                          <div className="bg-gray-50 border-l-2 border-blue-400 p-2 mb-2 text-xs italic line-clamp-4">
                                            {q.texto.substring(0, 200)}{q.texto.length > 200 ? '...' : ''}
                                          </div>
                                        )}
                                        {q.temImagem && (
                                          <div className="my-2 scale-75 origin-top">
                                            <ImagemQuestao tipo={q.tipoImagem} />
                                          </div>
                                        )}
                                        {q.pergunta && (
                                          <p className="text-xs font-medium text-gray-800 mb-2">{q.pergunta}</p>
                                        )}
                                        {q.alternativas && (
                                          <div className="space-y-1">
                                            {q.alternativas.slice(0, 4).map((alt, idx) => (
                                              <div key={idx} className="flex items-center gap-1 text-xs bg-gray-50 p-1 rounded">
                                                <span className="w-4 h-4 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center text-xs font-bold">
                                                  {alt.charAt(0)}
                                                </span>
                                                <span className="truncate">{alt.substring(3)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        
                                        {/* 🔥 Questões com imagem do Firebase */}
                                        {q.imagem && (q.imagem.startsWith('http') || q.imagem.startsWith('data:')) && (
                                          <div className="flex items-center justify-center h-full">
                                            <ImagemComLoading 
                                              src={q.imagem} 
                                              alt={`Questão ${q.id}`} 
                                              className="max-w-full object-contain rounded"
                                              tamanho={tamanhoAtual}
                                              style={{ maxHeight: tamanhoAtual === 'pequena' ? '160px' : tamanhoAtual === 'media' ? '220px' : tamanhoAtual === 'grande' ? '280px' : tamanhoAtual === 'inteira' ? '550px' : '350px' }}
                                            />
                                          </div>
                                        )}
                                        
                                        {/* Fallback para questões sem conteúdo detalhado */}
                                        {!q.enunciado && !q.texto && !q.temImagem && !q.imagem && (
                                          <div className="flex items-center justify-center h-full text-gray-400">
                                            <div className="text-center">
                                              <Eye className="w-12 h-12 mx-auto mb-2 opacity-40" />
                                              <p className="text-sm">Questão {q.id}</p>
                                              <p className="text-xs">{getCodigoHabilidadeAtualizado(q)}</p>
                                            </div>
                                          </div>
                                        )}
                                          </>
                                        )}
                                      </div>
                                    </ProtectedImageArea>
                                  </div>

                                  {/* Botão Seta Direita */}
                                  <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                    {questoesFiltradas.length > 1 && (
                                      <button
                                        onClick={() => navegarQuestao(tamanhoAtual, 'proximo')}
                                        className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                        title="Próxima questão"
                                      >
                                        <ChevronRight className="w-4 h-4 md:w-6 md:h-6" />
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Contador + Gabarito + Botão - abaixo do carrossel */}
                                <div className="mt-1 md:mt-2 space-y-1 md:space-y-2">
                                  {/* Informações abaixo da visualização: Contador + Gabarito */}
                                  <div className="flex items-center justify-center gap-4 md:gap-8 px-2">
                                    <span className="text-[10px] md:text-sm font-semibold text-white bg-gray-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                                      {indiceSafe + 1} de {questoesFiltradas.length}
                                    </span>
                                    {/* v532: Omitir gabarito para plano Gold */}
                                    {!isPlanoGold && (
                                      q.tamanho === 'texto-multiplas' ? (
                                        <span className="text-[10px] md:text-sm px-2 md:px-3 py-0.5 md:py-1 bg-orange-100 text-orange-700 rounded-full font-bold">
                                          {q.questoesVinculadas?.length} questões | Gab: {q.questoesVinculadas?.map(qv => qv.gabarito).join(', ')}
                                        </span>
                                      ) : (
                                        <span className="text-[10px] md:text-sm px-2 md:px-3 py-0.5 md:py-1 bg-green-100 text-green-700 rounded-full font-bold">
                                          Gabarito: {q.gabarito}
                                        </span>
                                      )
                                    )}
                                  </div>

                                  {/* Botão QUERO ESSA QUESTÃO */}
                                  <div className="flex justify-center">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (selected) {
                                          setBlocosSimulado(blocosSimulado.map(b => 
                                            b.id === blocoAtivo 
                                              ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(q.id))}
                                              : b
                                          ));
                                        } else if (!blocoCompleto) {
                                          const novaLista = [...blocoAtual.questoesSelecionadas, q.id];
                                          setBlocosSimulado(blocosSimulado.map(b => 
                                            b.id === blocoAtivo 
                                              ? {...b, questoesSelecionadas: novaLista}
                                              : b
                                          ));
                                          // Mostrar página de questões do bloco na pré-visualização
                                          setPaginaSimuladoAtiva(blocoAtivo === 1 ? 3 : 5);
                                          // Se completou 11 questões do bloco 1, pergunta se quer criar bloco 2
                                          if (blocoAtivo === 1 && novaLista.length === questoesPorBlocoSAEB(configSimulado.serie)) {
                                            setShowModalSegundoBloco(true);
                                          }
                                        }
                                      }}
                                      className={`px-4 md:px-6 py-1.5 md:py-2.5 rounded-lg font-bold text-[10px] md:text-sm shadow-md transition-all hover:scale-[1.02] flex items-center justify-center whitespace-nowrap ${
                                        selected 
                                          ? 'bg-gray-400 text-white cursor-not-allowed' 
                                          : blocoCompleto
                                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                          : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-green-700 hover:to-emerald-700'
                                      }`}
                                      disabled={selected || blocoCompleto}
                                    >
                                      {selected ? 'JÁ ADICIONADA' : blocoCompleto ? `BLOCO COMPLETO (${questoesPorBlocoSAEB(configSimulado.serie)}/${questoesPorBlocoSAEB(configSimulado.serie)})` : 'QUERO ESSA QUESTÃO'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })
                    )}
                      </>
                    )}
                  </div>

                  {/* Status de conclusão */}
                  {blocosSimulado.every(b => contarQuestoesReais(b.questoesSelecionadas, questoes) >= questoesPorBlocoSAEB(configSimulado.serie)) && (
                    <div className="mt-3 md:mt-4 bg-gradient-to-r from-green-600 to-blue-600 p-3 md:p-4 rounded-xl text-white">
                      <div className="flex items-center gap-2 md:gap-3">
                        <CheckCircle className="w-6 h-6 md:w-8 md:h-8" />
                        <div>
                          <h4 className="text-sm md:text-lg font-bold">Simulado Completo!</h4>
                          <p className="text-[10px] md:text-xs text-green-100">{questoesPorBlocoSAEB(configSimulado.serie) * 2} questões ({questoesPorBlocoSAEB(configSimulado.serie)} + {questoesPorBlocoSAEB(configSimulado.serie)}) • Pronto para gerar</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Coluna Direita - Pré-visualização */}
                <div className="lg:col-span-6 bg-white p-4 md:p-6 rounded-xl shadow-sm">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 md:mb-4 gap-2">
                    <h3 className="font-bold text-sm md:text-lg flex items-center gap-2">
                      <Eye className="w-4 h-4 md:w-5 md:h-5 text-orange-500" />
                      Pré-visualização do Simulado
                    </h3>
                    <span className="text-xs md:text-sm bg-orange-100 text-orange-700 px-2 md:px-3 py-1 rounded-full font-semibold">
                      {contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) + contarQuestoesReais(blocosSimulado[1].questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(configSimulado.serie) * 2} questões
                    </span>
                  </div>

                  {/* Navegação entre Páginas do Simulado */}
                  {(blocosSimulado[0].questoesSelecionadas.length > 0 || blocosSimulado[1].questoesSelecionadas.length > 0) && (
                    <div className="flex gap-1 md:gap-2 mb-3 md:mb-4 overflow-x-auto pb-2">
                      {blocosSimulado[0].questoesSelecionadas.length > 0 && (
                        <>
                          <button
                            onClick={() => setPaginaSimuladoAtiva(2)}
                            className={`px-2 py-1 md:py-1.5 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-xs ${
                              paginaSimuladoAtiva === 2 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            style={paginaSimuladoAtiva === 2 ? {backgroundColor: '#f7941d'} : {}}
                          >
                            Capa B1
                          </button>
                          <button
                            onClick={() => setPaginaSimuladoAtiva(3)}
                            className={`px-2 py-1 md:py-1.5 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-xs ${
                              paginaSimuladoAtiva === 3 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            style={paginaSimuladoAtiva === 3 ? {backgroundColor: '#f7941d'} : {}}
                          >
                            <span className="hidden md:inline">Questões B1 ({blocosSimulado[0].questoesSelecionadas.length})</span>
                            <span className="md:hidden">Q. B1 ({blocosSimulado[0].questoesSelecionadas.length})</span>
                          </button>
                        </>
                      )}
                      {blocosSimulado[1].questoesSelecionadas.length > 0 && (
                        <>
                          <button
                            onClick={() => setPaginaSimuladoAtiva(4)}
                            className={`px-2 py-1 md:py-1.5 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-xs ${
                              paginaSimuladoAtiva === 4 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            style={paginaSimuladoAtiva === 4 ? {backgroundColor: '#f7941d'} : {}}
                          >
                            Capa B2
                          </button>
                          <button
                            onClick={() => setPaginaSimuladoAtiva(5)}
                            className={`px-2 py-1 md:py-1.5 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-xs ${
                              paginaSimuladoAtiva === 5 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            style={paginaSimuladoAtiva === 5 ? {backgroundColor: '#f7941d'} : {}}
                          >
                            Questões B2 ({blocosSimulado[1].questoesSelecionadas.length})
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setPaginaSimuladoAtiva(1)}
                        className={`px-2 py-1.5 rounded-lg font-semibold whitespace-nowrap transition text-xs ${
                          paginaSimuladoAtiva === 1 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={paginaSimuladoAtiva === 1 ? {backgroundColor: '#f7941d'} : {}}
                      >
                        Cartão
                      </button>
                    </div>
                  )}

                  {/* Área de Pré-visualização com Questões */}
                  <div className="border-2 border-gray-300 rounded-xl p-6 bg-white shadow-inner max-h-[calc(100vh-350px)] overflow-y-auto">
                    {/* Se não há questões selecionadas */}
                    {blocosSimulado[0].questoesSelecionadas.length === 0 && blocosSimulado[1].questoesSelecionadas.length === 0 ? (
                      <div className="text-center py-16 text-gray-400">
                        <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-sm mb-2">Pré-visualização do Simulado SAEB</p>
                        <p className="text-xs">
                          {blocosSimulado[0].questoesSelecionadas.length}/{questoesPorBlocoSAEB(configSimulado.serie)} Português • 
                          {blocosSimulado[1].questoesSelecionadas.length}/{questoesPorBlocoSAEB(configSimulado.serie)} Matemática
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* PÁGINA 1: CARTÃO-RESPOSTA */}
                        {paginaSimuladoAtiva === 1 && (
                          <div className="text-center">
                            <div className="border-b-2 border-gray-300 pb-3 md:pb-4 mb-4 md:mb-6">
                              <div className="flex justify-between items-start mb-2">
                                <div></div>
                                <h2 className="text-base md:text-xl font-bold text-gray-900">SIMULADO SAEB</h2>
                                <div className="bg-orange-500 text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-bold">
                                  #{formatarCodigoSimulado(simuladoSelecionado?.codigo || simuladoSelecionado?.numero || numeroSimulado)}
                                </div>
                              </div>
                              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">CARTÃO-RESPOSTA</p>
                              
                              {/* Linha 1: Escola, Série, Turma (mobile e desktop) */}
                              <div className="flex justify-center gap-3 md:gap-8 text-[9px] md:text-xs text-gray-600 mb-1">
                                <span>Escola: <span className="font-semibold">{cabecalhoConfig.nomeEscola}</span></span>
                                <span>Série: <span className="font-semibold">{cabecalhoConfig.serie}</span></span>
                                <span>Turma: <span className="font-semibold">{cabecalhoConfig.turma}</span></span>
                              </div>
                              {/* Linha 2: Componente Curricular (mobile e desktop) */}
                              <div className="text-[9px] md:text-xs text-gray-600">
                                <span>Componente Curricular: <span className="font-semibold">{componenteSimulado === "Língua Portuguesa" ? "Língua Portuguesa" : componenteSimulado}</span></span>
                              </div>
                            </div>
                            
                            <div className="mb-4 md:mb-6 px-2 md:px-0">
                              <div className="flex items-baseline gap-1 md:gap-2 mb-3 md:mb-4 max-w-md mx-auto">
                                <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">Aluno(a):</span>
                                <div className="flex-1 border-b-2 border-gray-400"></div>
                              </div>
                              <div className="flex items-baseline gap-1 md:gap-2 max-w-xs mx-auto">
                                <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">Data:</span>
                                <div className="w-24 md:w-32 border-b-2 border-gray-400"></div>
                              </div>
                            </div>

                            {/* Grade do Cartão-Resposta - DINÂMICO baseado nas questões selecionadas */}
                            <div className="flex justify-center gap-8">
                              {/* Bloco 1 - Mostrar apenas se tiver questões */}
                              {contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) > 0 && (
                                <div className="text-center">
                                  <div className={`${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-800' : 'bg-blue-900'} text-white px-4 py-2 text-xs font-bold mb-4 rounded`}>
                                    BLOCO 1 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}
                                  </div>
                                  <div className="space-y-2">
                                    {Array.from({ length: contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) }, (_, i) => i + 1).map(num => (
                                      <div key={num} className="flex items-center gap-3 justify-center">
                                        <span className="text-xs font-bold text-gray-800 w-6">{String(num).padStart(2, '0')}</span>
                                        <div className="flex gap-3">
                                          {['A', 'B', 'C', 'D'].map(alt => (
                                            <div key={alt} className="w-7 h-7 border-2 border-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                              {alt}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Bloco 2 - Mostrar apenas se tiver questões */}
                              {contarQuestoesReais(blocosSimulado[1].questoesSelecionadas, questoes) > 0 && (
                                <div className="text-center">
                                  <div className={`${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-800' : 'bg-blue-900'} text-white px-4 py-2 text-xs font-bold mb-4 rounded`}>
                                    BLOCO 2 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}
                                  </div>
                                  <div className="space-y-2">
                                    {Array.from({ length: contarQuestoesReais(blocosSimulado[1].questoesSelecionadas, questoes) }, (_, i) => i + contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) + 1).map(num => (
                                      <div key={num} className="flex items-center gap-3 justify-center">
                                        <span className="text-xs font-bold text-gray-800 w-6">{num}</span>
                                        <div className="flex gap-3">
                                          {['A', 'B', 'C', 'D'].map(alt => (
                                            <div key={alt} className="w-7 h-7 border-2 border-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                              {alt}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Mensagem se não houver questões */}
                              {contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) === 0 && 
                               contarQuestoesReais(blocosSimulado[1].questoesSelecionadas, questoes) === 0 && (
                                <div className="text-center text-gray-400 py-8">
                                  <p className="text-sm">Adicione questões para visualizar o cartão-resposta</p>
                                </div>
                              )}
                            </div>

                            <div className="mt-6 flex justify-center">
                              <QrCode className="w-16 h-16 text-gray-400" />
                            </div>
                            <p className="text-xs text-gray-400 mt-2">QR Code para correção automática</p>
                          </div>
                        )}

                        {/* PÁGINA 2: CAPA BLOCO 1 */}
                        {paginaSimuladoAtiva === 2 && blocosSimulado[0].questoesSelecionadas.length > 0 && (
                          <div className="text-center py-16">
                            <div className="mb-8">
                              <div className={`w-24 h-24 ${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                                <BookOpen className={`w-12 h-12 ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`} />
                              </div>
                              <h1 className="text-4xl font-bold text-gray-900 mb-4">BLOCO 1</h1>
                              <div className={`w-32 h-1 ${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-600' : 'bg-blue-600'} mx-auto mb-6`}></div>
                              <h2 className={`text-2xl font-semibold ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-700' : 'text-blue-700'}`}>
                                {componenteSimulado === 'Língua Portuguesa' ? 'LÍNGUA PORTUGUESA' : 'MATEMÁTICA'}
                              </h2>
                            </div>
                            <div className={`${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-50' : 'bg-blue-50'} rounded-lg p-6 max-w-md mx-auto`}>
                              <p className={`text-sm ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-800' : 'text-blue-800'} mb-2`}>
                                <span className="font-bold">{contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes)}</span> questões
                              </p>
                              <p className={`text-xs ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`}>Questões 01 a {contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes)}</p>
                            </div>
                          </div>
                        )}

                        {/* PÁGINA 3: QUESTÕES BLOCO 1 */}
                        {paginaSimuladoAtiva === 3 && blocosSimulado[0].questoesSelecionadas.length > 0 && (
                          <div>
                            <div className="text-center border-b-2 border-purple-200 pb-3 mb-6">
                              <h3 className={`text-lg font-bold ${componenteSimulado === "Língua Portuguesa" ? "text-purple-700" : "text-blue-700"}`}>BLOCO 1 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</h3>
                              <p className="text-xs text-gray-600">{contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes)} questões</p>
                            </div>
                            <div className="space-y-4">
                              {(() => {
                                let numeroQuestaoGlobal = 0;
                                return blocosSimulado[0].questoesSelecionadas.map((qId, indexQ) => {
                                  // Busca com comparação flexível de tipos (string vs number)
                                  const questao = questoes.find(q => String(q.id) === String(qId));
                                  
                                  // Se não encontrou a questão, mostrar placeholder
                                  if (!questao) {
                                    numeroQuestaoGlobal++;
                                    return (
                                      <div key={qId || indexQ} className="border-l-4 border-gray-300 pl-4 relative bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-base font-bold text-gray-500 mb-2">
                                          QUESTÃO {String(numeroQuestaoGlobal).padStart(2, '0')} - <span className="text-gray-400">Carregando...</span>
                                        </h4>
                                        <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg h-20 flex items-center justify-center text-gray-400">
                                          <FileText className="w-8 h-8 opacity-50" />
                                        </div>
                                      </div>
                                    );
                                  }
                                  
                                  // Se for texto com múltiplas questões
                                  if (questao.tamanho === 'texto-multiplas' && questao.questoesVinculadas) {
                                    return (
                                      <div key={qId} className="border-2 border-orange-300 rounded-xl p-4 bg-orange-50 relative">
                                        <button
                                          onClick={() => {
                                            setBlocosSimulado(blocosSimulado.map(b => 
                                              b.id === 1 ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(qId))} : b
                                            ));
                                          }}
                                          className="absolute -right-2 -top-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                        >
                                          <X className="w-5 h-5" />
                                        </button>
                                        <div className="flex items-center gap-2 mb-3">
                                          <Layers className="w-5 h-5 text-orange-600" />
                                          <span className="font-bold text-orange-700">TEXTO COM {questao.questoesVinculadas.length} QUESTÕES</span>
                                        </div>
                                        <div className="bg-white border border-orange-200 rounded-lg p-3 mb-3">
                                          <p className="text-xs text-gray-500 text-center">[Texto Base]</p>
                                        </div>
                                        <div className="space-y-2">
                                          {questao.questoesVinculadas.map((qv, idx) => {
                                            numeroQuestaoGlobal++;
                                            return (
                                              <div key={idx} className="bg-white border border-pink-200 rounded-lg p-3">
                                                <h4 className="text-sm font-bold text-gray-900 mb-1">
                                                  QUESTÃO {String(numeroQuestaoGlobal).padStart(2, '0')} - <span className="text-pink-600">{qv.habilidade}</span>
                                                </h4>
                                                <div className="flex items-center gap-2 text-xs">
                                                  {['A', 'B', 'C', 'D'].map(alt => (
                                                    <div key={alt} className={`w-5 h-5 rounded border flex items-center justify-center font-semibold ${alt === qv.gabarito ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-400'}`}>{alt}</div>
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  }
                                  
                                  // Questão normal
                                  numeroQuestaoGlobal++;
                                  const numeroQuestao = numeroQuestaoGlobal;
                                
                                return (
                                  <div key={qId} className="relative mb-4">
                                    <button
                                      onClick={() => {
                                        setBlocosSimulado(blocosSimulado.map(b => 
                                          b.id === 1 ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(qId))} : b
                                        ));
                                      }}
                                      className="absolute right-0 top-0 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                    {/* TÍTULO DA QUESTÃO - retângulo cinza igual ao PDF */}
                                    <div className="mb-1">
                                      <span className="inline-block bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1">
                                        QUESTÃO {String(numeroQuestao).padStart(2, '0')} {getCodigoHabilidadeAtualizado(questao) ? `(${getCodigoHabilidadeAtualizado(questao)})` : ''}
                                      </span>
                                    </div>
                                    {/* Área da questão - IMAGEM COMPLETA (já inclui alternativas) */}
                                    {questao.imagem && (questao.imagem.startsWith('http') || questao.imagem.startsWith('data:')) ? (
                                      <div className="bg-white">
                                        <img 
                                          src={questao.imagem} 
                                          alt={`Questão ${numeroQuestao}`} 
                                          className="w-full object-contain"
                                        />
                                      </div>
                                    ) : (
                                      <div className={`bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 ${
                                        questao.tamanho === 'pequena' ? 'h-20' : questao.tamanho === 'media' ? 'h-28' : questao.tamanho === 'grande' ? 'h-40' : 'h-64'
                                      }`}>
                                        <div className="text-center">
                                          <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                          <p className="text-sm font-semibold">Questão {numeroQuestao}</p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                              })()}
                            </div>
                          </div>
                        )}

                        {/* PÁGINA 4: CAPA BLOCO 2 */}
                        {paginaSimuladoAtiva === 4 && blocosSimulado[1].questoesSelecionadas.length > 0 && (
                          <div className="text-center py-16">
                            <div className="mb-8">
                              <div className={`w-24 h-24 ${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                                <Target className={`w-12 h-12 ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`} />
                              </div>
                              <h1 className="text-4xl font-bold text-gray-900 mb-4">BLOCO 2</h1>
                              <div className={`w-32 h-1 ${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-600' : 'bg-blue-600'} mx-auto mb-6`}></div>
                              <h2 className={`text-2xl font-semibold ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-700' : 'text-blue-700'}`}>
                                {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}
                              </h2>
                            </div>
                            <div className={`${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-50' : 'bg-blue-50'} rounded-lg p-6 max-w-md mx-auto`}>
                              <p className={`text-sm ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-800' : 'text-blue-800'} mb-2`}>
                                <span className="font-bold">{contarQuestoesReais(blocosSimulado[1].questoesSelecionadas, questoes)}</span> questões
                              </p>
                              <p className={`text-xs ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`}>
                                Questões {contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) + 1} a {contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes) + contarQuestoesReais(blocosSimulado[1].questoesSelecionadas, questoes)}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* PÁGINA 5: QUESTÕES BLOCO 2 */}
                        {paginaSimuladoAtiva === 5 && blocosSimulado[1].questoesSelecionadas.length > 0 && (
                          <div>
                            <div className="text-center border-b-2 border-blue-200 pb-3 mb-6">
                              <h3 className={`text-lg font-bold ${componenteSimulado === "Língua Portuguesa" ? "text-purple-700" : "text-blue-700"}`}>BLOCO 2 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</h3>
                              <p className="text-xs text-gray-600">{blocosSimulado[1].questoesSelecionadas.length} questões</p>
                            </div>
                            <div className="space-y-4">
                              {blocosSimulado[1].questoesSelecionadas.map((qId, index) => {
                                // Busca com comparação flexível de tipos (string vs number)
                                const questao = questoes.find(q => String(q.id) === String(qId));
                                const numeroQuestao = blocosSimulado[0].questoesSelecionadas.length + index + 1;
                                
                                // Se não encontrou a questão, mostrar placeholder
                                if (!questao) {
                                  return (
                                    <div key={qId || index} className="border-l-4 border-gray-300 pl-4 relative bg-gray-50 rounded-lg p-4">
                                      <h4 className="text-base font-bold text-gray-500 mb-2">
                                        QUESTÃO {numeroQuestao} - <span className="text-gray-400">Carregando...</span>
                                      </h4>
                                      <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg h-20 flex items-center justify-center text-gray-400">
                                        <FileText className="w-8 h-8 opacity-50" />
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return (
                                  <div key={qId} className="relative mb-4">
                                    <button
                                      onClick={() => {
                                        setBlocosSimulado(blocosSimulado.map(b => 
                                          b.id === 2 ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(qId))} : b
                                        ));
                                      }}
                                      className="absolute right-0 top-0 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                    {/* TÍTULO DA QUESTÃO - retângulo cinza igual ao PDF */}
                                    <div className="mb-1">
                                      <span className="inline-block bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1">
                                        QUESTÃO {String(numeroQuestao).padStart(2, '0')} {getCodigoHabilidadeAtualizado(questao) ? `(${getCodigoHabilidadeAtualizado(questao)})` : ''}
                                      </span>
                                    </div>
                                    {/* Área da questão - IMAGEM COMPLETA (já inclui alternativas) */}
                                    {questao.imagem && (questao.imagem.startsWith('http') || questao.imagem.startsWith('data:')) ? (
                                      <div className="bg-white">
                                        <img 
                                          src={questao.imagem} 
                                          alt={`Questão ${numeroQuestao}`} 
                                          className="w-full object-contain"
                                        />
                                      </div>
                                    ) : (
                                      <div className={`bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 ${
                                        questao.tamanho === 'pequena' ? 'h-20' : questao.tamanho === 'media' ? 'h-28' : questao.tamanho === 'grande' ? 'h-40' : 'h-64'
                                      }`}>
                                        <div className="text-center">
                                          <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                          <p className="text-sm font-semibold">Questão {numeroQuestao}</p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* 🆕 BOTÃO GERAR SIMULADO - SEMPRE VISÍVEL */}
                  {(blocosSimulado[0].questoesSelecionadas.length > 0 || blocosSimulado[1].questoesSelecionadas.length > 0 || pdfGerado) && (
                    <div className="mt-4">
                      {(() => {
                        const bloco1QuestoesReais = contarQuestoesReais(blocosSimulado[0].questoesSelecionadas, questoes);
                        const bloco2QuestoesReais = contarQuestoesReais(blocosSimulado[1].questoesSelecionadas, questoes);
                        const bloco1Completo = bloco1QuestoesReais >= questoesPorBlocoSAEB(configSimulado.serie);
                        const bloco2Iniciado = blocosSimulado[1].questoesSelecionadas.length > 0;
                        const bloco2Completo = bloco2QuestoesReais >= questoesPorBlocoSAEB(configSimulado.serie);
                        
                        // Pode gerar se: Bloco 1 completo E (Bloco 2 não iniciado OU Bloco 2 completo)
                        const podeGerar = bloco1Completo && (!bloco2Iniciado || bloco2Completo);
                        
                        // Se tem PDF gerado, mostrar apenas o botão de download
                        if (pdfGerado) {
                          return (
                            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 space-y-3">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <p className="text-sm font-bold text-green-800">Simulado Gerado com Sucesso!</p>
                              </div>
                              <button
                                onClick={async () => {
                                  // 🔧 v475: Guardar dados antes de limpar
                                  const urlParaAbrir = pdfGerado.url;
                                  const nomeArquivo = pdfGerado.nome;
                                  
                                  // 🔧 v475: Limpar TUDO PRIMEIRO (antes de tentar baixar)
                                  setPdfGerado(null);
                                  setBlocosSimulado([
                                    { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                                    { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                                  ]);
                                  setBlocoAtivo(1);
                                  setComponenteSimulado('');
                                  obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
                                  
                                  try {
                                    const response = await fetch(urlParaAbrir);
                                    const blob = await response.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = nomeArquivo;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                    console.log('✅ Simulado baixado com sucesso');
                                  } catch (error) {
                                    console.error('Erro ao baixar, abrindo em nova aba:', error);
                                    window.open(urlParaAbrir, '_blank');
                                  }
                                  
                                  console.log('✅ Layout limpo e novo número gerado');
                                }}
                                className="w-full py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 text-white bg-green-600 hover:bg-green-700 transition"
                              >
                                <Download className="w-5 h-5" />
                                BAIXAR SIMULADO
                              </button>
                              <p className="text-xs text-green-700 text-center">📚 O PDF também está disponível no Histórico</p>
                            </div>
                          );
                        }
                        
                        // Mensagem de status
                        let mensagemStatus = '';
                        if (!bloco1Completo) {
                          mensagemStatus = `Bloco 1: ${bloco1QuestoesReais}/${questoesPorBlocoSAEB(configSimulado.serie)} questões`;
                        } else if (bloco2Iniciado && !bloco2Completo) {
                          mensagemStatus = `Bloco 2: ${bloco2QuestoesReais}/${questoesPorBlocoSAEB(configSimulado.serie)} questões`;
                        } else if (bloco1Completo && !bloco2Iniciado) {
                          mensagemStatus = 'Bloco 1 completo! Pronto para gerar.';
                        } else if (bloco1Completo && bloco2Completo) {
                          mensagemStatus = `Simulado completo (${questoesPorBlocoSAEB(configSimulado.serie) * 2} questões)!`;
                        }
                        
                        return (
                          <div className={`p-4 rounded-lg ${podeGerar ? 'bg-green-50 border-2 border-green-200' : 'bg-orange-50 border-2 border-orange-200'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                {podeGerar ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <AlertCircle className="w-5 h-5 text-orange-600" />
                                )}
                                <span className={`text-sm font-semibold ${podeGerar ? 'text-green-800' : 'text-orange-800'}`}>
                                  {mensagemStatus}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600">
                                {bloco1QuestoesReais + bloco2QuestoesReais} questões selecionadas
                              </div>
                            </div>
                            
                            {/* Botão Gerar Simulado - só mostra se NÃO tem PDF gerado */}
                            {!pdfGerado && (
                              <button 
                                onClick={() => {
                                  if (podeGerar) {
                                    // Abrir modal de geração de PDF para Simulado
                                    setShowModalGerarPDF(true);
                                  } else {
                                    alert(`⚠️ Blocos Incompletos!\n\nSó é possível gerar o simulado com blocos completos.\n\n• Bloco 1: ${questoesPorBlocoSAEB(configSimulado.serie)} questões\n• Bloco 2: ${questoesPorBlocoSAEB(configSimulado.serie)} questões (opcional)\n\nComplete o bloco atual antes de gerar o arquivo.`);
                                  }
                                }}
                                className={`w-full py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition ${
                                  podeGerar 
                                    ? 'text-white hover:opacity-90 shadow-lg' 
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                                style={podeGerar ? {backgroundColor: '#f7941d'} : {}}
                              >
                                <Download className="w-5 h-5" />
                                {podeGerar ? 'Gerar Simulado PDF' : 'Complete o bloco para gerar'}
                              </button>
                            )}
                            
                            {/* Botão Baixar PDF do Simulado - aparece após gerar */}
                            {pdfGerado && (
                              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3 space-y-2">
                                <div className="flex items-center gap-2 mb-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  <p className="text-sm font-semibold text-green-800">Simulado Gerado!</p>
                                </div>
                                <button
                                  onClick={async () => {
                                    // 🔧 v475: Guardar dados antes de limpar
                                    const urlParaAbrir = pdfGerado.url;
                                    const nomeArquivo = pdfGerado.nome;
                                    
                                    // 🔧 v475: Limpar TUDO PRIMEIRO
                                    setPdfGerado(null);
                                    setBlocosSimulado([
                                      { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                                      { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                                    ]);
                                    setComponenteSimulado('');
                                    obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
                                    
                                    try {
                                      const response = await fetch(urlParaAbrir);
                                      const blob = await response.blob();
                                      const blobUrl = URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.download = nomeArquivo;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                      console.log('✅ Simulado baixado com sucesso');
                                    } catch (error) {
                                      console.error('Erro ao baixar, abrindo em nova aba:', error);
                                      window.open(urlParaAbrir, '_blank');
                                    }
                                    
                                    console.log('✅ Layout limpo e novo número gerado');
                                  }}
                                  className="w-full py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 text-white bg-green-600 hover:bg-green-700 transition"
                                >
                                  <Download className="w-4 h-4" />
                                  Baixar Simulado PDF
                                </button>
                                
                                {/* Botão Gerar Cartões Personalizados */}
                                {tipoCartaoResposta === 'personalizado' && turmaSimuladoSelecionada?.alunos?.length > 0 && !pdfCartoesPersonalizados && (
                                  <button
                                    onClick={async () => {
                                      // Verificar créditos - v514: professor vinculado usa créditos da escola
                                      const creditosCartoes = turmaSimuladoSelecionada.alunos.length;
                                      const saldoAtual = creditosEfetivos;
                                      
                                      if (currentUser?.tipo !== 'admin' && !isProfessorVinculado && saldoAtual < creditosCartoes) {
                                        alert(`⚠️ Saldo insuficiente!\n\nVocê precisa de ${creditosCartoes} crédito(s) para gerar os cartões personalizados.\n\nSeu saldo atual: ${saldoAtual} créditos.`);
                                        return;
                                      }
                                      
                                      const custoExibido = (currentUser?.tipo === 'admin' || isProfessorVinculado) ? '0 (vinculado)' : creditosCartoes + ' crédito(s)';
                                      const confirmar = window.confirm(
                                        `📋 Gerar Cartões Personalizados?\n\n` +
                                        `• Turma: ${turmaSimuladoSelecionada.serie} ${turmaSimuladoSelecionada.turma}\n` +
                                        `• Alunos: ${turmaSimuladoSelecionada.alunos.length}\n` +
                                        `• Custo: ${custoExibido}\n\n` +
                                        `Deseja continuar?`
                                      );
                                      
                                      if (!confirmar) return;
                                      
                                      setGerandoCartoes(true);
                                      
                                      try {
                                        // Gerar HTML dos cartões personalizados
                                        const totalQuestoesB1 = blocosSimulado[0].questoesSelecionadas.length;
                                        const totalQuestoesB2 = blocosSimulado[1].questoesSelecionadas.length;
                                        const numeroRef = pdfGerado.nome.match(/\d{6}/)?.[0] || new Date().getTime().toString().slice(-6);
                                        
                                        let htmlCartoes = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Cartões Personalizados</title>
                                          <style>
                                            * { margin: 0; padding: 0; box-sizing: border-box; }
                                            body { font-family: Arial, sans-serif; padding: 15mm 20mm; }
                                            @page { size: A4; margin: 0; }
                                            .page-break { page-break-before: always; }
                                          </style>
                                        </head><body>`;
                                        
                                        turmaSimuladoSelecionada.alunos.forEach((aluno, idx) => {
                                          if (idx > 0) htmlCartoes += '<div class="page-break"></div>';
                                          
                                          htmlCartoes += '<div style="text-align:center;">';
                                          htmlCartoes += '<h1 style="font-size:20px;font-weight:bold;margin-bottom:5px;">SIMULADO SAEB</h1>';
                                          htmlCartoes += '<p style="font-size:14px;color:#666;margin-bottom:15px;">CARTÃO-RESPOSTA • #' + numeroRef + '</p>';
                                          // v699: sem flex/gap (incompatível com html2pdfrocket) — centralização via text-align + margin
                                          htmlCartoes += '<div style="text-align:center;font-size:12px;margin-bottom:15px;">';
                                          htmlCartoes += '<span style="display:inline-block;margin:0 15px;">Escola: ' + cabecalhoConfig.nomeEscola + '</span>';
                                          htmlCartoes += '<span style="display:inline-block;margin:0 15px;">Turma: ' + turmaSimuladoSelecionada.serie + ' ' + turmaSimuladoSelecionada.turma + '</span>';
                                          htmlCartoes += '</div>';
                                          htmlCartoes += '<div style="margin-bottom:20px;padding:10px;background:#f0f0f0;border-radius:5px;">';
                                          htmlCartoes += '<p style="font-size:16px;font-weight:bold;">' + (aluno.nome || aluno) + '</p>';
                                          htmlCartoes += '</div>';
                                          
                                          htmlCartoes += '<div style="text-align:center;">';
                                          
                                          // Bloco 1 - Layout otimizado para PDF
                                          if (totalQuestoesB1 > 0) {
                                            htmlCartoes += '<div style="display:inline-block;vertical-align:top;margin:0 20px;">';
                                            htmlCartoes += '<div style="background:#1e3a5f;color:white;padding:8px 20px;font-size:11px;font-weight:bold;text-align:center;margin-bottom:30px;white-space:nowrap;">BLOCO 1</div>';
                                            for (let i = 0; i < totalQuestoesB1; i++) {
                                              htmlCartoes += '<div style="margin-bottom:10px;white-space:nowrap;">';
                                              htmlCartoes += '<span style="display:inline-block;font-size:11px;font-weight:bold;color:#333;width:20px;text-align:right;margin-right:10px;vertical-align:middle;">' + String(i + 1).padStart(2, '0') + '</span>';
                                              ['A', 'B', 'C', 'D'].forEach(letra => {
                                                htmlCartoes += '<span style="display:inline-block;width:26px;height:26px;border:2px solid #555;border-radius:50%;text-align:center;line-height:22px;font-size:11px;font-weight:bold;color:#555;margin:0 4px;vertical-align:middle;">' + letra + '</span>';
                                              });
                                              htmlCartoes += '</div>';
                                            }
                                            htmlCartoes += '</div>';
                                          }
                                          
                                          // Bloco 2 - Layout otimizado para PDF
                                          if (totalQuestoesB2 > 0) {
                                            htmlCartoes += '<div style="display:inline-block;vertical-align:top;margin:0 20px;">';
                                            htmlCartoes += '<div style="background:#1e3a5f;color:white;padding:8px 20px;font-size:11px;font-weight:bold;text-align:center;margin-bottom:30px;white-space:nowrap;">BLOCO 2</div>';
                                            for (let i = 0; i < totalQuestoesB2; i++) {
                                              htmlCartoes += '<div style="margin-bottom:10px;white-space:nowrap;">';
                                              htmlCartoes += '<span style="display:inline-block;font-size:11px;font-weight:bold;color:#333;width:20px;text-align:right;margin-right:10px;vertical-align:middle;">' + String(i + totalQuestoesB1 + 1) + '</span>';
                                              ['A', 'B', 'C', 'D'].forEach(letra => {
                                                htmlCartoes += '<span style="display:inline-block;width:26px;height:26px;border:2px solid #555;border-radius:50%;text-align:center;line-height:22px;font-size:11px;font-weight:bold;color:#555;margin:0 4px;vertical-align:middle;">' + letra + '</span>';
                                              });
                                              htmlCartoes += '</div>';
                                            }
                                            htmlCartoes += '</div>';
                                          }
                                          htmlCartoes += '</div>';
                                          
                                          htmlCartoes += '</div></div>';
                                        });
                                        
                                        htmlCartoes += '</body></html>';
                                        
                                        // v698: Chamar Cloud Function segura (API Key fica no servidor)
                                        const response = await fetch('https://us-central1-facilitaprof-c2d1a.cloudfunctions.net/gerarPDFViaPdfEndpoint', {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({
                                            html: htmlCartoes,
                                            filename: 'Cartoes_' + pdfGerado.nome,
                                            options: {
                                              pageSize: 'A4',
                                              marginTop: 0,
                                              marginBottom: 0,
                                              marginLeft: 0,
                                              marginRight: 0
                                            }
                                          })
                                        });
                                        
                                        const resultado = await response.json();
                                        const pdfUrlCartoes = resultado.url || null;
                                        
                                        if (!response.ok || !pdfUrlCartoes) {
                                          console.error('❌ Erro ao gerar PDF dos cartões:', resultado);
                                          throw new Error(resultado.error || 'Erro ao gerar PDF dos cartões-resposta.');
                                        }
                                        
                                        {
                                            const nomeArquivoCartoes = 'Cartoes_Resposta_' + pdfGerado.nome;
                                            
                                            setPdfCartoesPersonalizados({
                                              url: pdfUrlCartoes,
                                              nome: nomeArquivoCartoes
                                            });
                                            
                                            // Salvar no histórico
                                            if (db && currentUser?.uid) {
                                              const historicoCartoes = {
                                                userId: currentUser.uid,
                                                titulo: nomeArquivoCartoes,
                                                nomeArquivo: nomeArquivoCartoes,
                                                urlPdf: pdfUrlCartoes,
                                                tipo: 'cartoes',
                                                componente: componenteSimulado,
                                                serie: turmaSimuladoSelecionada.serie,
                                                turma: turmaSimuladoSelecionada.turma,
                                                totalQuestoes: turmaSimuladoSelecionada.alunos.length,
                                                totalPaginas: turmaSimuladoSelecionada.alunos.length,
                                                dataGeracao: new Date().toISOString(),
                                                creditosUsados: currentUser?.tipo === 'admin' ? 0 : creditosCartoes
                                              };
                                              
                                              await db.collection('historico_pdfs').add(historicoCartoes);
                                              setHistoricoPDFs(prev => [historicoCartoes, ...prev]);
                                              
                                              // 🔧 v519: Incrementar paginasGeradas para professor vinculado
                                              if (currentUser?.tipo === 'professor' && currentUser?.escolaId) {
                                                try {
                                                  const profSnapshot = await db.collection('professoresEscola')
                                                    .where('professorId', '==', currentUser.uid)
                                                    .where('escolaId', '==', currentUser.escolaId)
                                                    .limit(1)
                                                    .get();
                                                  
                                                  if (!profSnapshot.empty) {
                                                    const profDoc = profSnapshot.docs[0];
                                                    const paginasAtuais = profDoc.data().paginasGeradas || 0;
                                                    await db.collection('professoresEscola').doc(profDoc.id).update({
                                                      paginasGeradas: paginasAtuais + creditosCartoes
                                                    });
                                                    console.log('📊 v519: Páginas (cartões) atualizadas:', paginasAtuais + creditosCartoes);
                                                  }
                                                } catch (e) { console.error('⚠️ Erro ao atualizar páginas:', e); }
                                              }
                                              
                                              // v689d: Descontar créditos - da escola se vinculado
                                              if (currentUser?.tipo !== 'admin') {
                                                const idDesconto = (currentUser?.tipo === 'professor' && currentUser?.escolaId) 
                                                  ? currentUser.escolaId 
                                                  : currentUser.uid;
                                                const docDesconto = await db.collection('users').doc(idDesconto).get();
                                                const creditosAtuais = docDesconto.exists ? (docDesconto.data().creditos || 0) : 0;
                                                await db.collection('users').doc(idDesconto).update({
                                                  creditos: Math.max(0, creditosAtuais - creditosCartoes)
                                                });
                                              }
                                            }
                                            
                                            alert('✅ Cartões personalizados gerados!\n\nClique em "Baixar Cartões" para salvar o arquivo.');
                                        }
                                      } catch (error) {
                                        console.error('Erro ao gerar cartões:', error);
                                        alert('❌ Erro ao gerar cartões personalizados.\n\n' + error.message);
                                      } finally {
                                        setGerandoCartoes(false);
                                      }
                                    }}
                                    disabled={gerandoCartoes}
                                    className="w-full py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 text-white bg-purple-600 hover:bg-purple-700 transition disabled:opacity-50"
                                  >
                                    {gerandoCartoes ? (
                                      <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Gerando Cartões...
                                      </>
                                    ) : (
                                      <>
                                        <Users className="w-4 h-4" />
                                        Gerar Cartões Personalizados ({turmaSimuladoSelecionada.alunos.length})
                                      </>
                                    )}
                                  </button>
                                )}
                                
                                {/* Botão Baixar Cartões Personalizados */}
                                {pdfCartoesPersonalizados && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const response = await fetch(pdfCartoesPersonalizados.url);
                                        const blob = await response.blob();
                                        const blobUrl = URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = blobUrl;
                                        link.download = pdfCartoesPersonalizados.nome;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                      } catch (error) {
                                        console.error('Erro ao baixar cartões:', error);
                                        window.open(pdfCartoesPersonalizados.url, '_blank');
                                      }
                                    }}
                                    className="w-full py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 text-white bg-purple-600 hover:bg-purple-700 transition"
                                  >
                                    <Download className="w-4 h-4" />
                                    Baixar Cartões Personalizados
                                  </button>
                                )}
                                
                                {/* Botão Limpar / Nova Atividade */}
                                <button
                                  onClick={() => {
                                    setPdfGerado(null);
                                    setPdfCartoesPersonalizados(null);
                                    setTipoCartaoResposta('generico');
                                    setTurmaSimuladoSelecionada(null); setTurmasSelecionadasSimulado([]);
                                    // Limpar blocos e gerar novo número de simulado
                                    setBlocosSimulado([
                                      { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                                      { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                                    ]);
                                    setBlocoAtivo(1);
                                    setPaginaSimuladoAtiva(1);
                                    obterProximoCodigoSimulado().then(codigo => setNumeroSimulado(codigo));
                                  }}
                                  className="w-full py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-2 text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
                                >
                                  Limpar e criar novo simulado
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - PERFIL */}
        {currentView === 'escola-perfil' && (
          <div className="max-w-4xl mx-auto">
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="p-6 border-b border-gray-600" style={{backgroundColor: '#1e3a5f'}}>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-white">
                    <h2 className="text-2xl font-bold">Dados da Escola</h2>
                    <p className="text-blue-200">Edite as informações da sua instituição</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Nome da Escola <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={perfilEscola.nome}
                      onChange={(e) => setPerfilEscola({...perfilEscola, nome: e.target.value})}
                      className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">CNPJ</label>
                    <input
                      type="text"
                      value={perfilEscola.cnpj}
                      onChange={(e) => setPerfilEscola({...perfilEscola, cnpj: e.target.value})}
                      className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Diretor(a)</label>
                  <input
                    type="text"
                    value={perfilEscola.diretor}
                    onChange={(e) => setPerfilEscola({...perfilEscola, diretor: e.target.value})}
                    className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                  />
                </div>
                
                {/* v651: Upload de Logomarca */}
                <div className="border-t border-gray-600 pt-6">
                  <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Image className="w-5 h-5 text-purple-400" />
                    Logomarca da Escola
                  </h3>
                  <div className="space-y-3">
                    {perfilEscola.logoUrl ? (
                      <div className="flex items-center gap-4 bg-white p-4 rounded-xl">
                        <img 
                          src={perfilEscola.logoUrl} 
                          alt="Logo" 
                          className="h-20 w-20 object-contain border border-gray-200 rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Logo atual</p>
                          <button
                            onClick={() => setPerfilEscola({...perfilEscola, logoUrl: ''})}
                            className="text-sm text-red-500 hover:text-red-700 hover:underline"
                          >
                            Remover Logo
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="border-2 border-dashed border-gray-500 rounded-xl p-6 cursor-pointer hover:border-blue-400 hover:bg-gray-700 transition block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            if (!firebaseReady || !storage) {
                              alert('⚠️ Aguarde o Firebase carregar completamente.');
                              return;
                            }
                            
                            try {
                              const result = await uploadImagem(file, 'logos');
                              if (result.success) {
                                setPerfilEscola({...perfilEscola, logoUrl: result.url});
                                alert('✅ Logo enviada com sucesso!');
                              } else {
                                alert('❌ Erro ao enviar logo: ' + result.error);
                              }
                            } catch (error) {
                              alert('❌ Erro: ' + error.message);
                            }
                          }}
                        />
                        <div className="text-center">
                          <Image className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                          <p className="text-sm text-gray-300 font-semibold">Clique para fazer upload da logomarca</p>
                          <p className="text-xs text-gray-400 mt-2">PNG, JPG ou SVG (recomendado: 200x200px, max 2MB)</p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
                
                <div className="border-t border-gray-600 pt-6">
                  <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-400" />
                    Endereço
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Endereço</label>
                      <input
                        type="text"
                        value={perfilEscola.endereco}
                        onChange={(e) => setPerfilEscola({...perfilEscola, endereco: e.target.value})}
                        className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Cidade</label>
                      <input
                        type="text"
                        value={perfilEscola.cidade}
                        onChange={(e) => setPerfilEscola({...perfilEscola, cidade: e.target.value})}
                        className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">Estado</label>
                        <input
                          type="text"
                          value={perfilEscola.estado}
                          onChange={(e) => setPerfilEscola({...perfilEscola, estado: e.target.value})}
                          className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">CEP</label>
                        <input
                          type="text"
                          value={perfilEscola.cep}
                          onChange={(e) => setPerfilEscola({...perfilEscola, cep: e.target.value})}
                          className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-600 pt-6">
                  <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-green-400" />
                    Contato
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Telefone</label>
                      <input
                        type="text"
                        value={perfilEscola.telefone}
                        onChange={(e) => setPerfilEscola({...perfilEscola, telefone: e.target.value})}
                        className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">E-mail</label>
                      <input
                        type="email"
                        value={perfilEscola.email}
                        onChange={(e) => setPerfilEscola({...perfilEscola, email: e.target.value})}
                        className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                  <button
                    onClick={async () => { 
                      // 🔧 v522: Salvar perfil da escola no Firebase com melhor feedback
                      console.log('🔄 Tentando salvar perfil da escola...');
                      console.log('📋 Dados a salvar:', perfilEscola);
                      console.log('🔥 Firebase ready:', firebaseReady, 'db:', !!db, 'uid:', currentUser?.uid);
                      
                      if (!firebaseReady || !db) {
                        alert('⚠️ Firebase não está pronto. Aguarde alguns segundos e tente novamente.');
                        return;
                      }
                      
                      if (!currentUser?.uid) {
                        alert('⚠️ Usuário não identificado. Faça login novamente.');
                        return;
                      }
                      
                      try {
                        // Salvar no Firebase
                        await db.collection('users').doc(currentUser.uid).update({
                          nomeEscola: perfilEscola.nome,
                          escola: perfilEscola.nome,
                          nome: perfilEscola.nome, // Também atualizar o campo 'nome'
                          endereco: perfilEscola.endereco || '',
                          cidade: perfilEscola.cidade || '',
                          estado: perfilEscola.estado || '',
                          cep: perfilEscola.cep || '',
                          telefone: perfilEscola.telefone || '',
                          diretor: perfilEscola.diretor || '',
                          cnpj: perfilEscola.cnpj || '',
                          logoUrl: perfilEscola.logoUrl || '', // v651
                          atualizadoEm: new Date().toISOString()
                        });
                        console.log('✅ Perfil da escola salvo no Firebase!');
                        
                        // Atualizar currentUser local
                        setCurrentUser(prev => ({
                          ...prev,
                          nomeEscola: perfilEscola.nome,
                          escola: perfilEscola.nome,
                          nome: perfilEscola.nome
                        }));
                        
                        // Atualizar cabecalhoConfig
                        setCabecalhoConfig(prev => ({
                          ...prev,
                          nomeEscola: perfilEscola.nome,
                          logoUrl: perfilEscola.logoUrl // v651
                        }));
                        
                        // 🔧 v522: Também atualizar o escolaNome nos professores vinculados
                        try {
                          const profSnapshot = await db.collection('professoresEscola')
                            .where('escolaId', '==', currentUser.uid)
                            .get();
                          
                          const batch = db.batch();
                          profSnapshot.forEach(doc => {
                            // Atualizar o documento do professor vinculado em professoresEscola
                            batch.update(doc.ref, { escolaNome: perfilEscola.nome });
                            
                            // Se o professor tiver um professorId, também atualizar em users
                            const profData = doc.data();
                            if (profData.professorId) {
                              const userRef = db.collection('users').doc(profData.professorId);
                              batch.update(userRef, { escolaNome: perfilEscola.nome });
                            }
                          });
                          
                          await batch.commit();
                          console.log('✅ Nome da escola atualizado em', profSnapshot.size, 'professores vinculados');
                        } catch (profError) {
                          console.warn('⚠️ Erro ao atualizar professores:', profError.message);
                        }
                        
                        alert('✅ Perfil salvo com sucesso!');
                        setShowSaveSuccess(true); 
                        setTimeout(() => setShowSaveSuccess(false), 3000);
                      } catch (error) {
                        console.error('❌ Erro ao salvar perfil:', error);
                        alert('❌ Erro ao salvar: ' + error.message);
                      }
                    }}
                    className="px-8 py-3 text-white rounded-xl font-semibold flex items-center gap-2 hover:opacity-90 transition"
                    style={{backgroundColor: '#f7941d'}}
                  >
                    <Save className="w-5 h-5" />
                    Salvar Alterações
                  </button>
                </div>
                
                {/* 🔧 v424: Seção Alterar Senha */}
                <div className="border-t border-gray-600 pt-6 mt-6">
                  <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-red-400" />
                    Alterar Senha
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Senha Atual</label>
                      <input
                        type="password"
                        value={senhaAtual}
                        onChange={(e) => setSenhaAtual(e.target.value)}
                        placeholder="••••••••"
                        className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Nova Senha</label>
                      <input
                        type="password"
                        value={novaSenha}
                        onChange={(e) => setNovaSenha(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Confirmar Nova Senha</label>
                      <input
                        type="password"
                        value={confirmarNovaSenha}
                        onChange={(e) => setConfirmarNovaSenha(e.target.value)}
                        placeholder="Repita a nova senha"
                        className="w-full p-3 border-2 border-gray-600 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={async () => {
                        if (!senhaAtual || !novaSenha || !confirmarNovaSenha) {
                          alert('⚠️ Preencha todos os campos!');
                          return;
                        }
                        if (novaSenha.length < 6) {
                          alert('⚠️ A nova senha deve ter pelo menos 6 caracteres!');
                          return;
                        }
                        if (novaSenha !== confirmarNovaSenha) {
                          alert('⚠️ As senhas não coincidem!');
                          return;
                        }
                        
                        try {
                          const user = auth.currentUser;
                          if (!user) {
                            alert('❌ Usuário não autenticado!');
                            return;
                          }
                          
                          // Reautenticar o usuário
                          const credential = window.firebase.auth.EmailAuthProvider.credential(user.email, senhaAtual);
                          await user.reauthenticateWithCredential(credential);
                          
                          // Alterar a senha
                          await user.updatePassword(novaSenha);
                          
                          alert('✅ Senha alterada com sucesso!');
                          setSenhaAtual('');
                          setNovaSenha('');
                          setConfirmarNovaSenha('');
                        } catch (error) {
                          console.error('Erro ao alterar senha:', error);
                          if (error.code === 'auth/wrong-password') {
                            alert('❌ Senha atual incorreta!');
                          } else if (error.code === 'auth/weak-password') {
                            alert('❌ A nova senha é muito fraca. Use pelo menos 6 caracteres.');
                          } else {
                            alert('❌ Erro ao alterar senha. Tente novamente.');
                          }
                        }
                      }}
                      className="px-6 py-3 bg-red-500 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-red-600 transition"
                    >
                      <Lock className="w-5 h-5" />
                      Alterar Senha
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - PROFESSORES */}
        {currentView === 'escola-professores' && (
          <div className="w-full overflow-x-hidden">
            {/* Header com botão adicionar - responsivo */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4 md:mb-6">
              <div>
                <p className="text-gray-300 text-xs md:text-base">Gerencie os professores vinculados à sua escola</p>
              </div>
              <button
                onClick={() => { setNovoProfessor({nome: '', email: '', serie: '', turma: '', turno: '', componente: ''}); setProfessorEditando(null); setShowModalProfessor(true); }}
                className="px-4 md:px-6 py-2 md:py-3 text-white rounded-xl font-semibold flex items-center gap-2 hover:opacity-90 transition text-sm md:text-base w-full md:w-auto justify-center"
                style={{backgroundColor: '#f7941d'}}
              >
                <Plus className="w-4 h-4 md:w-5 md:h-5" />
                Novo Professor
              </button>
            </div>

            {/* Cards de estatísticas - responsivos */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
              <div className="p-3 md:p-4 rounded-xl border-l-4 border-blue-400" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="text-xl md:text-3xl font-bold text-blue-300">{professoresEscola.length}</div>
                <div className="text-[10px] md:text-sm text-gray-300">Total</div>
              </div>
              <div className="p-3 md:p-4 rounded-xl border-l-4 border-green-400" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="text-xl md:text-3xl font-bold text-green-300">{professoresEscola.filter(p => p.ativo).length}</div>
                <div className="text-[10px] md:text-sm text-gray-300">Ativos</div>
              </div>
              <div className="p-3 md:p-4 rounded-xl border-l-4 border-gray-400" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="text-xl md:text-3xl font-bold text-gray-300">{professoresEscola.filter(p => !p.ativo).length}</div>
                <div className="text-[10px] md:text-sm text-gray-300">Inativos</div>
              </div>
              <div className="p-3 md:p-4 rounded-xl border-l-4 border-purple-400" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="text-xl md:text-3xl font-bold text-purple-300">{historicoPDFs.filter(pdf => pdf.origem === 'professor').reduce((acc, pdf) => acc + (pdf.creditosUsados || pdf.totalPaginas || 0), 0)}</div>
                <div className="text-[10px] md:text-sm text-gray-300">Créditos Gerados</div>
              </div>
            </div>

            {/* Lista de Professores */}
            <div className="rounded-xl overflow-hidden p-3 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="font-bold text-sm md:text-lg mb-3 md:mb-4 text-white">Lista de Professores</h3>
              
              {/* Desktop - Tabela */}
              <div className="hidden md:block bg-white rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-4 font-semibold">Professor</th>
                      <th className="text-left p-4 font-semibold">Série/Turma</th>
                      <th className="text-left p-4 font-semibold">Turno</th>
                      <th className="text-left p-4 font-semibold">Componente</th>
                      <th className="text-center p-4 font-semibold">Créditos</th>
                      <th className="text-center p-4 font-semibold">Status</th>
                      <th className="text-right p-4 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {professoresEscola.map(prof => (
                      <tr key={prof.id} className={`hover:bg-gray-50 ${!prof.ativo ? 'opacity-60' : ''}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium">{prof.nome}</div>
                              <div className="text-sm text-gray-500">{prof.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">{prof.serie} {prof.turma}</span>
                        </td>
                        <td className="p-4 text-gray-600">{prof.turno}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-medium">{prof.componente}</span>
                        </td>
                        <td className="p-4 text-center font-bold text-gray-700">{historicoPDFs.filter(pdf => pdf.userId === prof.professorId).reduce((acc, pdf) => acc + (pdf.creditosUsados || pdf.totalPaginas || 0), 0)}</td>
                        <td className="p-4 text-center">
                          {prof.status === 'pendente' ? (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 flex items-center gap-1 mx-auto w-fit">
                              ⏳ Pendente
                            </span>
                          ) : (
                            <button
                              onClick={() => {
                                setProfessoresEscola(professoresEscola.map(p => p.id === prof.id ? {...p, ativo: !p.ativo} : p));
                              }}
                              className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 mx-auto ${prof.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                            >
                              {prof.ativo ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                              {prof.ativo ? 'Ativo' : 'Inativo'}
                            </button>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => { setNovoProfessor(prof); setProfessorEditando(prof); setShowModalProfessor(true); }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition mr-1"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => { 
                              if(window.confirm(`Excluir ${prof.nome}?\n\nIsso removerá o professor da lista da escola.`)) {
                                // v689: Excluir do Firestore (coleção professoresEscola)
                                try {
                                  if (firebaseReady && db && prof.firestoreId) {
                                    await db.collection('professoresEscola').doc(prof.firestoreId).delete();
                                    console.log('✅ Professor excluído do Firestore:', prof.nome);
                                  }
                                } catch (err) {
                                  console.error('❌ Erro ao excluir professor do Firestore:', err);
                                }
                                setProfessoresEscola(professoresEscola.filter(p => p.id !== prof.id)); 
                              }
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile - Cards */}
              <div className="md:hidden space-y-2">
                {professoresEscola.map(prof => (
                  <div key={prof.id} className={`bg-white rounded-lg p-3 ${!prof.ativo ? 'opacity-60' : ''}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{prof.nome}</div>
                          <div className="text-[10px] text-gray-500 truncate">{prof.email}</div>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => { setNovoProfessor(prof); setProfessorEditando(prof); setShowModalProfessor(true); }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={async () => { 
                            if(window.confirm(`Excluir ${prof.nome}?\n\nIsso removerá o professor da lista da escola.`)) {
                              try {
                                if (firebaseReady && db && prof.firestoreId) {
                                  await db.collection('professoresEscola').doc(prof.firestoreId).delete();
                                  console.log('✅ Professor excluído do Firestore:', prof.nome);
                                }
                              } catch (err) {
                                console.error('❌ Erro ao excluir professor do Firestore:', err);
                              }
                              setProfessoresEscola(professoresEscola.filter(p => p.id !== prof.id)); 
                            }
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 items-center">
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-medium">{prof.serie} {prof.turma}</span>
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-medium">{prof.componente}</span>
                      <span className="text-[9px] text-gray-500">{prof.turno}</span>
                      <span className="text-[9px] font-bold text-gray-700 ml-auto">{historicoPDFs.filter(pdf => pdf.userId === prof.professorId).reduce((acc, pdf) => acc + (pdf.creditosUsados || pdf.totalPaginas || 0), 0)} pág</span>
                      {prof.status === 'pendente' ? (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-yellow-100 text-yellow-700">⏳</span>
                      ) : (
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${prof.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {prof.ativo ? '✓' : '✗'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Adicionar/Editar Professor */}
            {showModalProfessor && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                  <div className="p-6 border-b flex items-center justify-between" style={{backgroundColor: '#1e3a5f'}}>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      <User className="w-6 h-6" />
                      {professorEditando ? 'Editar Professor' : 'Novo Professor'}
                    </h2>
                    <button onClick={() => setShowModalProfessor(false)} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Completo *</label>
                      <input
                        type="text"
                        value={novoProfessor.nome}
                        onChange={(e) => setNovoProfessor({...novoProfessor, nome: e.target.value})}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        placeholder="Nome do professor"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                      <input
                        type="email"
                        value={novoProfessor.email}
                        onChange={(e) => setNovoProfessor({...novoProfessor, email: e.target.value})}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        placeholder="email@escola.com"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Série</label>
                        <select
                          value={novoProfessor.serie}
                          onChange={(e) => setNovoProfessor({...novoProfessor, serie: e.target.value})}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Selecione</option>
                          {seriesDisponiveis.filter(s => s.ativo).map(s => (
                            <option key={s.id} value={s.nome}>{s.nome}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Turma</label>
                        <select
                          value={novoProfessor.turma}
                          onChange={(e) => setNovoProfessor({...novoProfessor, turma: e.target.value})}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Selecione</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Turno</label>
                        <select
                          value={novoProfessor.turno}
                          onChange={(e) => setNovoProfessor({...novoProfessor, turno: e.target.value})}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Selecione</option>
                          <option value="Matutino">Matutino</option>
                          <option value="Vespertino">Vespertino</option>
                          <option value="Noturno">Noturno</option>
                          <option value="Integral">Integral</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Componente</label>
                        <select
                          value={novoProfessor.componente}
                          onChange={(e) => setNovoProfessor({...novoProfessor, componente: e.target.value})}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Selecione</option>
                          <option value="Matemática">Matemática</option>
                          <option value="Português">Português</option>
                          <option value="Língua Portuguesa SAEB">Língua Portuguesa SAEB</option>
                          <option value="Matemática SAEB">Matemática SAEB</option>
                          <option value="Ciências">Ciências</option>
                          <option value="História">História</option>
                          <option value="Geografia">Geografia</option>
                          <option value="Polivalente">Polivalente</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                    <button
                      onClick={() => setShowModalProfessor(false)}
                      className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={async () => {
                        if (!novoProfessor.nome || !novoProfessor.email) {
                          alert('Preencha nome e email!');
                          return;
                        }
                        
                        // Validar formato do email
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(novoProfessor.email)) {
                          alert('⚠️ Email inválido!');
                          return;
                        }
                        
                        if (professorEditando) {
                          // Editando professor existente
                          try {
                            const escolaId = currentUser?.uid || currentUser?.id;
                            
                            // Atualizar na coleção professoresEscola
                            if (db && professorEditando.firestoreId) {
                              await db.collection('professoresEscola').doc(professorEditando.firestoreId).update({
                                nome: novoProfessor.nome,
                                email: novoProfessor.email,
                                serie: novoProfessor.serie || '',
                                turma: novoProfessor.turma || '',
                                turno: novoProfessor.turno || '',
                                componente: novoProfessor.componente || '',
                                atualizadoEm: window.firebase?.firestore?.FieldValue?.serverTimestamp() || new Date()
                              });
                            }
                            
                            // v510: TAMBÉM atualizar o documento do usuário (users/{professorId})
                            // Isso garante que o professor terá o escolaId e seriesLiberadas corretos
                            // 🔧 v519: Usar perfilEscola.nome para o nome correto da escola
                            if (db && professorEditando.professorId) {
                              const seriesProf = novoProfessor.serie ? [novoProfessor.serie] : [];
                              await db.collection('users').doc(professorEditando.professorId).set({
                                nome: novoProfessor.nome,
                                email: novoProfessor.email,
                                tipo: 'professor',
                                escolaId: escolaId,
                                escolaNome: perfilEscola.nome || currentUser?.escola || 'Escola',
                                serie: novoProfessor.serie || '',
                                turma: novoProfessor.turma || '',
                                turno: novoProfessor.turno || '',
                                componente: novoProfessor.componente || '',
                                seriesLiberadas: seriesProf,
                                plano: 'Vinculado',
                                atualizadoEm: window.firebase?.firestore?.FieldValue?.serverTimestamp() || new Date()
                              }, { merge: true });
                              console.log('✅ Documento do professor atualizado com escolaId:', escolaId);
                            }
                            
                            setProfessoresEscola(professoresEscola.map(p => p.id === professorEditando.id ? {...novoProfessor, id: p.id, paginasGeradas: p.paginasGeradas, ativo: p.ativo, firestoreId: p.firestoreId, professorId: p.professorId} : p));
                            alert('✅ Professor atualizado!');
                          } catch (error) {
                            console.error('Erro ao atualizar professor:', error);
                            alert('❌ Erro ao atualizar professor. Tente novamente.');
                          }
                        } else {
                          // 🔧 v427: Novo professor - CRIAR CONTA AUTOMATICAMENTE
                          // 🔧 v552: Usar Firebase Auth REST API para criar conta sem afetar sessão da escola
                          try {
                            setLoginLoading(true);
                            const escolaId = currentUser?.uid || currentUser?.id;
                            const senhaInicial = '123456';
                            
                            // Criar conta do professor via REST API (não afeta a sessão atual)
                            let professorUid = null;
                            let contaCriada = false;
                            
                            try {
                              // v552: Usar Firebase Auth REST API - não troca a sessão da escola
                              const apiKey = FIREBASE_CONFIG.apiKey;
                              const response = await fetch(
                                `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`,
                                {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    email: novoProfessor.email,
                                    password: senhaInicial,
                                    returnSecureToken: false // Não precisamos do token
                                  })
                                }
                              );
                              
                              const data = await response.json();
                              
                              if (data.error) {
                                // Tratar erros da API
                                if (data.error.message === 'EMAIL_EXISTS') {
                                  throw { code: 'auth/email-already-in-use' };
                                } else {
                                  throw new Error(data.error.message || 'Erro ao criar conta');
                                }
                              }
                              
                              professorUid = data.localId;
                              contaCriada = true;
                              console.log('✅ Conta do professor criada (via REST API):', professorUid);
                              
                            } catch (authError) {
                              if (authError.code === 'auth/email-already-in-use') {
                                // Email já existe - buscar o usuário existente
                                alert('⚠️ Este email já está cadastrado na plataforma.\n\nO professor será vinculado à sua escola.');
                                
                                // Buscar o usuário existente no Firestore
                                const usersSnapshot = await db.collection('users')
                                  .where('email', '==', novoProfessor.email)
                                  .limit(1)
                                  .get();
                                
                                if (!usersSnapshot.empty) {
                                  professorUid = usersSnapshot.docs[0].id;
                                }
                              } else {
                                throw authError;
                              }
                            }
                            
                            // Salvar dados do professor no Firestore ANTES de fazer signOut
                            // v533: Fazer todas as operações de escrita enquanto ainda está autenticado
                            if (professorUid && db) {
                              // v509: Séries liberadas do professor = série atribuída a ele pela escola
                              const seriesProf = novoProfessor.serie ? [novoProfessor.serie] : [];
                              
                              // 🔧 v519: Usar perfilEscola.nome para o nome correto da escola
                              await db.collection('users').doc(professorUid).set({
                                nome: novoProfessor.nome,
                                email: novoProfessor.email,
                                tipo: 'professor',
                                escolaId: escolaId,
                                escolaNome: perfilEscola.nome || currentUser?.escola || 'Escola',
                                serie: novoProfessor.serie || '',
                                turma: novoProfessor.turma || '',
                                turno: novoProfessor.turno || '',
                                componente: novoProfessor.componente || '',
                                creditos: 0, // Professor vinculado usa créditos da escola
                                seriesLiberadas: seriesProf, // Apenas as séries atribuídas a este professor
                                plano: 'Vinculado',
                                dataCadastro: window.firebase?.firestore?.FieldValue?.serverTimestamp() || new Date(),
                                cadastradoPor: escolaId,
                                senhaInicial: true // Flag para indicar que precisa trocar a senha
                              }, { merge: true });
                              
                              // Adicionar à lista de professores da escola
                              const profRef = await db.collection('professoresEscola').add({
                                nome: novoProfessor.nome,
                                email: novoProfessor.email,
                                professorId: professorUid,
                                escolaId: escolaId,
                                serie: novoProfessor.serie || '',
                                turma: novoProfessor.turma || '',
                                turno: novoProfessor.turno || '',
                                componente: novoProfessor.componente || '',
                                status: 'ativo',
                                ativo: true,
                                paginasGeradas: 0,
                                criadoEm: window.firebase?.firestore?.FieldValue?.serverTimestamp() || new Date()
                              });
                              
                              // 🔧 v530: Criar turma automaticamente para o professor vinculado
                              if (novoProfessor.serie && novoProfessor.turma) {
                                try {
                                  const turmaNome = `${novoProfessor.serie} ${novoProfessor.turma}`.trim();
                                  const turmaRef = await db.collection('turmas').add({
                                    userId: professorUid,
                                    nome: turmaNome,
                                    serie: novoProfessor.serie,
                                    turma: novoProfessor.turma,
                                    turno: novoProfessor.turno || '',
                                    alunos: [], // Professor irá adicionar os alunos
                                    criadoPorEscola: true,
                                    escolaId: escolaId,
                                    criadoEm: window.firebase?.firestore?.FieldValue?.serverTimestamp() || new Date()
                                  });
                                  console.log('✅ Turma criada automaticamente para o professor:', turmaNome);
                                  
                                  // v689: Adicionar turma ao estado local para aparecer imediatamente
                                  setTurmasFirebase(prev => [...prev, {
                                    id: turmaRef.id,
                                    userId: professorUid,
                                    nome: turmaNome,
                                    serie: novoProfessor.serie,
                                    turma: novoProfessor.turma,
                                    turno: novoProfessor.turno || '',
                                    alunos: [],
                                    criadoPorEscola: true,
                                    escolaId: escolaId,
                                    origem: 'professor',
                                    professorId: professorUid
                                  }]);
                                } catch (turmaError) {
                                  console.warn('⚠️ Não foi possível criar turma automaticamente:', turmaError);
                                }
                              }
                              
                              // Adicionar à lista local
                              setProfessoresEscola([...professoresEscola, {
                                ...novoProfessor, 
                                id: profRef.id, 
                                firestoreId: profRef.id,
                                professorId: professorUid,
                                paginasGeradas: 0, 
                                ativo: true,
                                status: 'ativo'
                              }]);
                              
                              alert(`✅ Professor cadastrado com sucesso!\n\n📧 Email: ${novoProfessor.email}\n🔐 Senha inicial: ${senhaInicial}\n📚 Turma: ${novoProfessor.serie} ${novoProfessor.turma}\n\n⚠️ IMPORTANTE: O professor deve trocar a senha no primeiro acesso!\n\n✓ A turma foi criada automaticamente no painel do professor.\n✓ O professor só precisa adicionar os alunos.`);
                            }
                            
                          } catch (error) {
                            console.error('Erro ao cadastrar professor:', error);
                            let mensagemErro = error.message || 'Tente novamente.';
                            if (error.code === 'auth/invalid-email') {
                              mensagemErro = 'Email inválido.';
                            } else if (error.code === 'auth/weak-password') {
                              mensagemErro = 'Senha muito fraca.';
                            }
                            alert(`❌ Erro ao cadastrar professor: ${mensagemErro}`);
                          } finally {
                            setLoginLoading(false);
                          }
                        }
                        setShowModalProfessor(false);
                      }}
                      disabled={loginLoading}
                      className="px-6 py-2 text-white rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50"
                      style={{backgroundColor: '#f7941d'}}
                    >
                      {loginLoading ? 'Cadastrando...' : (professorEditando ? 'Salvar Alterações' : 'Cadastrar Professor')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - GESTÃO DE TURMAS */}
        {currentView === 'escola-turmas' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Turmas dos Professores</h2>
                    <p className="text-sm text-gray-500">Gerencie turmas e adicione alunos</p>
                  </div>
                </div>
                <div className="bg-purple-100 px-4 py-2 rounded-lg">
                  <span className="text-purple-700 font-bold">{turmas.filter(t => t.origem === 'professor' || t.criadoPorEscola).length}</span>
                  <span className="text-purple-600 text-sm ml-1">turmas</span>
                </div>
              </div>
            </div>

            {/* Lista de Turmas */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {turmas.filter(t => t.origem === 'professor' || t.criadoPorEscola || professoresEscola.some(p => p.professorId === t.userId)).length === 0 ? (
                <div className="p-12 text-center">
                  <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhuma turma encontrada</h3>
                  <p className="text-gray-500 text-sm">As turmas serão criadas automaticamente ao cadastrar professores com série e turma definidas.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {turmas
                    .filter(t => t.origem === 'professor' || t.criadoPorEscola || professoresEscola.some(p => p.professorId === t.userId))
                    .map(turma => {
                      // Buscar nome do professor
                      const professor = professoresEscola.find(p => p.professorId === turma.userId);
                      const isExpanded = turmasExpandidas[turma.id];
                      
                      return (
                        <div key={turma.id} className="p-4 hover:bg-gray-50 transition">
                          {/* Cabeçalho da Turma */}
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => setTurmasExpandidas(prev => ({ ...prev, [turma.id]: !prev[turma.id] }))}
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                                {turma.serie?.charAt(0) || 'T'}
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-800">{turma.nome || `${turma.serie} ${turma.turma}`}</h3>
                                <p className="text-sm text-gray-500">
                                  {professor?.nome || 'Professor não identificado'} • {turma.turno || 'Turno não definido'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                                {turma.alunos?.length || 0} alunos
                              </span>
                              <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </div>
                          </div>

                          {/* Conteúdo Expandido */}
                          {isExpanded && (
                            <div className="mt-4 pl-14">
                              {/* Lista de Alunos */}
                              <div className="bg-gray-50 rounded-lg p-4 mb-3">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-semibold text-gray-700 text-sm">Alunos da Turma</h4>
                                  <span className="text-xs text-gray-500">{turma.alunos?.length || 0} cadastrados</span>
                                </div>
                                
                                {turma.alunos?.length > 0 ? (
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {turma.alunos.map((aluno, idx) => (
                                      <div key={idx} className="flex items-center justify-between bg-white p-2 rounded-lg border">
                                        <div className="flex items-center gap-2">
                                          <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600">
                                            {idx + 1}
                                          </span>
                                          <span className="text-sm text-gray-700">{getNomeAluno(aluno)}</span>
                                        </div>
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Deseja remover o aluno "${getNomeAluno(aluno)}" da turma?`)) {
                                              try {
                                                const novosAlunos = turma.alunos.filter((_, i) => i !== idx);
                                                await db.collection('turmas').doc(turma.id).update({ alunos: novosAlunos });
                                                setTurmasFirebase(prev => prev.map(t => 
                                                  t.id === turma.id ? { ...t, alunos: novosAlunos } : t
                                                ));
                                                alert('✅ Aluno removido com sucesso!');
                                              } catch (error) {
                                                console.error('Erro ao remover aluno:', error);
                                                alert('❌ Erro ao remover aluno.');
                                              }
                                            }
                                          }}
                                          className="p-1 hover:bg-red-100 rounded text-red-500 transition"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 text-center py-4">Nenhum aluno cadastrado ainda.</p>
                                )}
                              </div>

                              {/* Formulário para Adicionar Aluno */}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Nome do aluno..."
                                  id={`novo-aluno-${turma.id}`}
                                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      const input = document.getElementById(`novo-aluno-${turma.id}`);
                                      const nomeAluno = input.value.trim();
                                      if (nomeAluno) {
                                        // v539: Salvar sempre como string para compatibilidade
                                        const novosAlunos = [...(turma.alunos || []), nomeAluno];
                                        db.collection('turmas').doc(turma.id).update({ alunos: novosAlunos })
                                          .then(() => {
                                            setTurmasFirebase(prev => prev.map(t => 
                                              t.id === turma.id ? { ...t, alunos: novosAlunos } : t
                                            ));
                                            input.value = '';
                                          })
                                          .catch(err => {
                                            console.error('Erro ao adicionar aluno:', err);
                                            alert('❌ Erro ao adicionar aluno.');
                                          });
                                      }
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => {
                                    const input = document.getElementById(`novo-aluno-${turma.id}`);
                                    const nomeAluno = input.value.trim();
                                    if (nomeAluno) {
                                      // v539: Salvar sempre como string para compatibilidade
                                      const novosAlunos = [...(turma.alunos || []), nomeAluno];
                                      db.collection('turmas').doc(turma.id).update({ alunos: novosAlunos })
                                        .then(() => {
                                          setTurmasFirebase(prev => prev.map(t => 
                                            t.id === turma.id ? { ...t, alunos: novosAlunos } : t
                                          ));
                                          input.value = '';
                                        })
                                        .catch(err => {
                                          console.error('Erro ao adicionar aluno:', err);
                                          alert('❌ Erro ao adicionar aluno.');
                                        });
                                    } else {
                                      alert('⚠️ Digite o nome do aluno!');
                                    }
                                  }}
                                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold text-sm flex items-center gap-2"
                                >
                                  <Plus className="w-4 h-4" />
                                  Adicionar
                                </button>
                              </div>

                              {/* Adicionar Múltiplos Alunos */}
                              <div className="mt-3">
                                <button
                                  onClick={() => {
                                    const nomes = window.prompt('Cole a lista de nomes dos alunos (um por linha, separados por vírgula, ou numerados):');
                                    if (nomes) {
                                      // v689: Parser melhorado - detecta múltiplos formatos
                                      let lista = nomes
                                        .replace(/^\d+[\.\)\-\s]+/gm, '') // Remove numeração (1. 2) 3- etc)
                                        .split(/\n/)
                                        .map(n => n.trim())
                                        .filter(n => n.length > 0);
                                      
                                      // Se veio tudo em uma linha, tenta separar por vírgula ou ponto-e-vírgula
                                      if (lista.length === 1 && (lista[0].includes(',') || lista[0].includes(';'))) {
                                        const tentativaVirgula = lista[0].split(/[;,]/).map(n => n.trim()).filter(n => n.length > 0);
                                        if (tentativaVirgula.length > 1) {
                                          lista = tentativaVirgula;
                                        }
                                      }
                                      
                                      // Se ainda é uma linha só muito longa, tenta separar por numeração inline (1 NOME 2 OUTRO)
                                      if (lista.length === 1 && lista[0].length > 50) {
                                        const tentativaNumeros = lista[0].split(/\s+\d+[\.\)\s]+/).map(n => n.trim()).filter(n => n.length > 1);
                                        if (tentativaNumeros.length > 1) {
                                          // Limpar primeiro item que pode ter número no início
                                          tentativaNumeros[0] = tentativaNumeros[0].replace(/^\d+[\.\)\-\s]+/, '').trim();
                                          lista = tentativaNumeros.filter(n => n.length > 0);
                                        }
                                      }
                                      
                                      if (lista.length > 0) {
                                        const alunosExistentes = turma.alunos || [];
                                        // v539: Salvar sempre como strings para compatibilidade
                                        // Converter alunos existentes que podem ser objetos para strings
                                        const alunosExistentesNormalizados = alunosExistentes.map(a => 
                                          typeof a === 'object' && a !== null ? (a.nome || String(a)) : String(a)
                                        );
                                        const novosAlunos = [
                                          ...alunosExistentesNormalizados,
                                          ...lista // lista já é array de strings
                                        ];
                                        db.collection('turmas').doc(turma.id).update({ alunos: novosAlunos })
                                          .then(() => {
                                            setTurmasFirebase(prev => prev.map(t => 
                                              t.id === turma.id ? { ...t, alunos: novosAlunos } : t
                                            ));
                                            alert(`✅ ${lista.length} aluno(s) adicionado(s) com sucesso!`);
                                          })
                                          .catch(err => {
                                            console.error('Erro ao adicionar alunos:', err);
                                            alert('❌ Erro ao adicionar alunos.');
                                          });
                                      }
                                    }
                                  }}
                                  className="text-sm text-purple-600 hover:text-purple-800 hover:underline flex items-center gap-1"
                                >
                                  <Upload className="w-4 h-4" />
                                  Adicionar lista de alunos (colar)
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - SIMULADO SAEB */}
        {currentView === 'escola-simulado' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
            {/* Coluna Esquerda - Seleção de Questões do Simulado */}
            <div className="lg:col-span-6 rounded-xl p-4 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="mb-3 md:mb-4">
                <div className="bg-blue-900 bg-opacity-50 border border-blue-400 rounded-lg p-3 md:p-4 mb-3 md:mb-4">
                  <div className="flex items-start gap-2 md:gap-3">
                    <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-blue-100 text-xs md:text-sm mb-1">Simulado SAEB</h4>
                      <p className="text-[10px] md:text-xs text-blue-200">22 questões (11 Bloco 1 + 11 Bloco 2) do mesmo componente • 2º e 5º ano</p>
                      <p className="text-[10px] md:text-xs text-blue-200">26 questões (13 Bloco 1 + 13 Bloco 2) do mesmo componente • 9º ano</p>
                    </div>
                  </div>
                </div>

                <h3 className="font-bold text-sm md:text-lg mb-3 md:mb-4 text-white">Selecione Questões para Simulado</h3>
                
                {/* Seletor de Componente Curricular do Simulado - PRIMEIRO */}
                <div className="mb-3 md:mb-4 p-2 md:p-3 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl">
                  <div className="text-[10px] md:text-xs text-gray-600 mb-2 font-semibold uppercase">Componente Curricular:</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setComponenteSimuladoEscola('Língua Portuguesa');
                        setBlocosSimuladoEscola([
                          { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                          { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                        ]);
                        setBlocoAtivoEscola(1);
                      }}
                      className={`flex-1 py-1.5 md:py-2 rounded-lg font-semibold text-[10px] md:text-sm transition ${
                        componenteSimuladoEscola === 'Língua Portuguesa'
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      📚 <span className="hidden md:inline">Língua </span>Portuguesa
                    </button>
                    <button
                      onClick={() => {
                        setComponenteSimuladoEscola('Matemática');
                        setBlocosSimuladoEscola([
                          { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                          { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                        ]);
                        setBlocoAtivoEscola(1);
                      }}
                      className={`flex-1 py-1.5 md:py-2 rounded-lg font-semibold text-[10px] md:text-sm transition ${
                        componenteSimuladoEscola === 'Matemática'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      🔢 Matemática
                    </button>
                  </div>
                </div>

                {/* Seletor de Tipo de Filtro - apenas para Língua Portuguesa */}
                {componenteSimuladoEscola === 'Língua Portuguesa' && (
                  <div className="mb-3 md:mb-4 p-2 md:p-3 bg-gradient-to-r from-purple-50 to-orange-50 border-2 border-purple-200 rounded-xl">
                    <div className="text-[10px] md:text-xs text-gray-600 mb-2 font-semibold uppercase">Tipo de Questão:</div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setTipoFiltroSimuladoEscola('habilidade-unica');
                          setFiltrosEscola({...filtrosEscola, habilidade: ''});
                        }}
                        className={`flex-1 py-1.5 md:py-2 px-2 md:px-3 rounded-lg font-semibold text-[10px] md:text-sm transition flex items-center justify-center gap-1 md:gap-2 ${
                          tipoFiltroSimuladoEscola === 'habilidade-unica'
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Target className="w-3 h-3 md:w-4 md:h-4" />
                        <span className="hidden md:inline">Habilidade Única</span>
                        <span className="md:hidden">Hab. Única</span>
                      </button>
                      <button
                        onClick={() => {
                          setTipoFiltroSimuladoEscola('texto-multiplas');
                          setFiltrosEscola({...filtrosEscola, habilidade: ''});
                        }}
                        className={`flex-1 py-1.5 md:py-2 px-2 md:px-3 rounded-lg font-semibold text-[10px] md:text-sm transition flex items-center justify-center gap-1 md:gap-2 ${
                          tipoFiltroSimuladoEscola === 'texto-multiplas'
                            ? 'bg-orange-500 text-white shadow-md'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Layers className="w-3 h-3 md:w-4 md:h-4" />
                        <span className="hidden md:inline">Texto c/ Hab. Variadas</span>
                        <span className="md:hidden">Texto Hab. Variadas</span>
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 md:mb-4">
                  <select 
                    className="px-2 md:px-3 py-2 border rounded-lg text-xs md:text-sm bg-white" 
                    onChange={(e) => {
                      setFiltrosEscola({...filtrosEscola, serie: e.target.value, habilidade: ''});
                      // v529: Reiniciar contador de questões ao mudar série
                      setIndiceQuestaoVisivelEscola({ pequena: 0, media: 0, grande: 0, inteira: 0, 'texto-multiplas': 0 });
                    }}
                    value={filtrosEscola.serie}
                  >
                    <option value="">Selecione uma Série</option>
                    {/* 🆕 Filtrar séries SAEB baseado em seriesLiberadas do usuário */}
                    {['2º ano', '5º ano', '9º ano']
                      .filter(serie => {
                        if (currentUser?.tipo === 'admin') return true;
                        if (!currentUser?.seriesLiberadas || currentUser.seriesLiberadas.length === 0) return true;
                        return currentUser.seriesLiberadas.some(se => se.toLowerCase() === serie.toLowerCase());
                      })
                      .map(serie => (
                        <option key={serie} value={serie}>{serie}</option>
                      ))}
                  </select>
                  
                  {/* Dropdown de Habilidade - só aparece se for "Habilidade Única" */}
                  {(componenteSimuladoEscola !== 'Língua Portuguesa' || tipoFiltroSimuladoEscola === 'habilidade-unica') && (
                    <select 
                      className={`px-2 md:px-3 py-2 border rounded-lg text-xs md:text-sm ${!filtrosEscola.serie ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'}`}
                      onChange={(e) => {
                        setFiltrosEscola({...filtrosEscola, habilidade: e.target.value});
                        // v529: Reiniciar contador de questões ao mudar habilidade
                        setIndiceQuestaoVisivelEscola({ pequena: 0, media: 0, grande: 0, inteira: 0, 'texto-multiplas': 0 });
                      }}
                      disabled={!filtrosEscola.serie}
                      value={filtrosEscola.habilidade}
                    >
                      <option value="">Selecione uma Habilidade</option>
                      {habilidades
                        .filter(h => h.serie === filtrosEscola.serie && 
                          h.componente === `${componenteSimuladoEscola} SAEB`
                        )
                        .map(h => (
                        <option key={h.id} value={h.codigo}>
                          {h.codigo} - {h.conteudo}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {/* Info quando for Texto com Habilidades Variadas */}
                  {componenteSimuladoEscola === 'Língua Portuguesa' && tipoFiltroSimuladoEscola === 'texto-multiplas' && (
                    <div className="px-2 md:px-3 py-2 bg-orange-100 border border-orange-300 rounded-lg text-xs md:text-sm text-orange-700 flex items-center gap-2">
                      <Layers className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="text-[10px] md:text-sm">Textos c/ múltiplas hab.</span>
                    </div>
                  )}
                  
                  {/* Filtro de Tamanho - só aparece se for Habilidade Única */}
                  {(componenteSimuladoEscola !== 'Língua Portuguesa' || tipoFiltroSimuladoEscola === 'habilidade-unica') && (
                    <select className="px-2 md:px-3 py-2 border rounded-lg text-xs md:text-sm bg-white" onChange={(e) => setFiltrosEscola({...filtrosEscola, tamanho: e.target.value})}>
                      <option value="">Todos tamanhos</option>
                      <option value="pequena">Pequena (~6.5cm)</option>
                      <option value="media">Média (~8.5cm)</option>
                      <option value="grande">Grande (~12cm)</option>
                      <option value="inteira">Inteira (~22cm)</option>
                    </select>
                  )}
                </div>

                {/* Tabs de Blocos - Bloco 2 só clicável quando Bloco 1 completo */}
                <div className="flex gap-2 mb-3 md:mb-4">
                  {blocosSimuladoEscola.map(bloco => {
                    const bloco1CompletoEscola = contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) >= questoesPorBlocoSAEB(filtrosEscola.serie);
                    const desabilitadoEscola = bloco.id === 2 && !bloco1CompletoEscola;
                    
                    return (
                      <button
                        key={bloco.id}
                        onClick={() => !desabilitadoEscola && setBlocoAtivoEscola(bloco.id)}
                        disabled={desabilitadoEscola}
                        className={`flex-1 py-2 md:py-3 rounded-lg font-semibold text-xs md:text-sm transition ${
                          desabilitadoEscola
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                            : blocoAtivoEscola === bloco.id 
                              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={desabilitadoEscola ? 'Complete o Bloco 1 primeiro' : ''}
                      >
                        <div>{bloco.nome}</div>
                        <div className="text-xs opacity-80">
                          {contarQuestoesReais(bloco.questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(filtrosEscola.serie)} questões
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Lista de Questões Ordenada por Tamanho - CARROSSEL - SEM SCROLL */}
              <div className="space-y-3 md:space-y-4">
                {/* Verificar se série foi selecionada (e habilidade se for o modo habilidade-unica) */}
                {(!filtrosEscola.serie || (componenteSimuladoEscola === 'Língua Portuguesa' && tipoFiltroSimuladoEscola === 'habilidade-unica' && !filtrosEscola.habilidade) || (componenteSimuladoEscola !== 'Língua Portuguesa' && !filtrosEscola.habilidade)) ? (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4 md:p-8 text-center">
                    <div className="text-3xl md:text-5xl mb-3 md:mb-4">🔍</div>
                    <h3 className="text-sm md:text-xl font-bold text-amber-800 mb-2 md:mb-3">Selecione os filtros para ver as questões</h3>
                    <div className="space-y-1 md:space-y-2 text-amber-700 text-xs md:text-base">
                      <p className={`flex items-center justify-center gap-2 ${filtrosEscola.serie ? 'text-green-600' : ''}`}>
                        {filtrosEscola.serie ? '✅' : '⏳'} 1. Selecione uma Série {filtrosEscola.serie && <span className="font-bold">({filtrosEscola.serie})</span>}
                      </p>
                      <p className="flex items-center justify-center gap-2 text-green-600">
                        ✅ 2. Componente Curricular: <span className="font-bold">{componenteSimuladoEscola}</span>
                      </p>
                      {(componenteSimuladoEscola !== 'Língua Portuguesa' || tipoFiltroSimuladoEscola === 'habilidade-unica') && (
                        <p className={`flex items-center justify-center gap-2 ${filtrosEscola.habilidade ? 'text-green-600' : ''}`}>
                          {filtrosEscola.habilidade ? '✅' : '⏳'} 3. Selecione uma Habilidade {filtrosEscola.habilidade && <span className="font-bold">({filtrosEscola.habilidade})</span>}
                        </p>
                      )}
                      {componenteSimuladoEscola === 'Língua Portuguesa' && tipoFiltroSimuladoEscola === 'texto-multiplas' && (
                        <p className="flex items-center justify-center gap-2 text-green-600">
                          ✅ 3. Tipo: <span className="font-bold">Texto com Habilidades Variadas</span>
                        </p>
                      )}
                    </div>
                  </div>
                ) : carregandoQuestoes ? (
                  /* v650: Spinner Simulado SAEB - Escola */
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 md:p-8 text-center">
                    <div className="flex justify-center mb-3 md:mb-4">
                      <svg className="animate-spin h-8 w-8 md:h-10 md:w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <h3 className="text-sm md:text-lg font-bold text-blue-800 mb-1">Carregando questões...</h3>
                    <p className="text-xs md:text-sm text-blue-600">
                      Buscando questões de <span className="font-semibold">{filtrosEscola.serie}</span> · <span className="font-semibold">{componenteSimuladoEscola}</span>
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Banner indicador */}
                    <div className={`text-white p-2 md:p-3 rounded-lg text-center text-[10px] md:text-sm font-semibold shadow-lg mb-3 md:mb-4 ${
                      tipoFiltroSimuladoEscola === 'texto-multiplas' 
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500' 
                        : 'bg-gradient-to-r from-purple-500 to-pink-500'
                    }`}>
                      🔍 CLIQUE NAS QUESTÕES PARA DAR ZOOM!
                    </div>
                    
                {/* Renderização condicional baseada no tipo de filtro */}
                {componenteSimuladoEscola === 'Língua Portuguesa' && tipoFiltroSimuladoEscola === 'texto-multiplas' ? (
                  // MODO: Texto com Habilidades Variadas - só mostra questões texto-multiplas
                  (() => {
                    const questoesFiltradas = questoes.filter(q => 
                      q.tamanho === 'texto-multiplas' &&
                      (q.componente === 'Língua Portuguesa SAEB' || q.componente === 'Língua Portuguesa' || q.componente === componenteSimuladoEscola) &&
                      q.serie === filtrosEscola.serie &&
                      q.modulo === 'simulado' // v572: Filtrar apenas questões do módulo simulado
                    );
                    
                    if (questoesFiltradas.length === 0) {
                      return (
                        <div className="bg-gray-100 rounded-xl p-4 md:p-8 text-center">
                          <div className="text-2xl md:text-4xl mb-2 md:mb-3">📄</div>
                          <p className="text-gray-600 text-xs md:text-base">Nenhum texto com múltiplas questões encontrado para {filtrosEscola.serie}</p>
                        </div>
                      );
                    }

                    return (
                      <div>
                        <div className="flex items-center gap-2 mb-2 md:mb-3">
                          <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-orange-500"></div>
                          <span className="text-xs md:text-sm font-bold text-white uppercase">
                            📄 Textos com Habilidades Variadas ({questoesFiltradas.length} disponíveis)
                          </span>
                        </div>
                        
                        <div className="relative">
                          {(() => {
                            const indiceAtual = indiceQuestaoVisivelEscola['texto-multiplas'] || 0;
                            const q = questoesFiltradas[indiceAtual % questoesFiltradas.length];
                            const blocoAtualEsc = blocosSimuladoEscola.find(b => b.id === blocoAtivoEscola);
                            // 🔧 v475: Verificar se questão está em QUALQUER bloco (impede duplicação)
                            const selectedNoBloco1 = blocosSimuladoEscola[0]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                            const selectedNoBloco2 = blocosSimuladoEscola[1]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                            const selected = selectedNoBloco1 || selectedNoBloco2;
                            const questoesReaisNoBloco = contarQuestoesReais(blocoAtualEsc?.questoesSelecionadas || [], questoes);
                            const questoesDoTexto = q?.questoesVinculadas?.length || 1;
                            const blocoCompleto = questoesReaisNoBloco >= questoesPorBlocoSAEB(filtrosEscola.serie);
                            const blocoFicariaCheio = questoesReaisNoBloco + questoesDoTexto > questoesPorBlocoSAEB(filtrosEscola.serie);
                            const orderNumber = selected ? blocoAtualEsc.questoesSelecionadas.findIndex(item => String(item) === String(q.id)) + 1 : 0;
                            const totalQuestoesEscola = questoesFiltradas.length;
                            
                            return (
                              <div className="relative">
                                <div className="flex items-center gap-1 md:gap-2">
                                  {/* Seta Esquerda */}
                                  <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                    {totalQuestoesEscola > 1 && (
                                      <button
                                        onClick={() => {
                                          setIndiceQuestaoVisivelEscola(prev => ({
                                            ...prev,
                                            ['texto-multiplas']: ((prev['texto-multiplas'] || 0) - 1 + totalQuestoesEscola) % totalQuestoesEscola
                                          }));
                                        }}
                                        className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                      >
                                        <ChevronLeft className="w-4 h-4 md:w-6 md:h-6" />
                                      </button>
                                    )}
                                  </div>

                                  {/* Card Central */}
                                  <div className="flex-1">
                                    <ProtectedImageArea 
                                      className="border-2 border-orange-300 rounded-lg bg-white shadow-inner hover:border-orange-400 transition overflow-hidden"
                                      onClick={() => setQuestaoEmVisualizacao(q)}
                                    >
                                      <div className="p-2 md:p-4 max-h-64 md:max-h-[400px] overflow-y-auto">
                                        <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-2 md:p-3 mb-2 md:mb-3">
                                        <div className="flex items-center gap-2 mb-1 md:mb-2">
                                          <Layers className="w-3 h-3 md:w-4 md:h-4 text-orange-500" />
                                          <span className="text-[10px] md:text-xs font-bold text-orange-700">TEXTO BASE</span>
                                        </div>
                                        {q.textoBase ? (
                                          <p className="text-[10px] md:text-xs text-gray-700 whitespace-pre-line line-clamp-4 md:line-clamp-6">{q.textoBase}</p>
                                        ) : (
                                          <div className="bg-gray-100 h-16 md:h-20 rounded flex items-center justify-center text-gray-400 text-[10px] md:text-xs">
                                            [Imagem do texto]
                                          </div>
                                        )}
                                      </div>
                                      <div className="space-y-1 md:space-y-2">
                                        <span className="text-[10px] md:text-xs font-bold text-pink-600">📝 {q.questoesVinculadas?.length} QUESTÕES VINCULADAS:</span>
                                        {q.questoesVinculadas?.map((qv, idx) => (
                                          <div key={idx} className="bg-pink-50 border border-pink-200 rounded p-1.5 md:p-2">
                                            <div className="flex items-center justify-between">
                                              <span className="text-[10px] md:text-xs font-bold text-pink-700">Q{idx + 1} - {qv.habilidade}</span>
                                              <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold">Gab: {qv.gabarito}</span>
                                            </div>
                                            {qv.enunciado && <p className="text-[10px] md:text-xs text-gray-600 mt-1 line-clamp-2">{qv.enunciado}</p>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </ProtectedImageArea>
                                </div>

                                {/* Seta Direita */}
                                <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                  {totalQuestoesEscola > 1 && (
                                    <button
                                      onClick={() => {
                                        setIndiceQuestaoVisivelEscola(prev => ({
                                          ...prev,
                                          ['texto-multiplas']: ((prev['texto-multiplas'] || 0) + 1) % totalQuestoesEscola
                                        }));
                                      }}
                                      className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                    >
                                      <ChevronRight className="w-4 h-4 md:w-6 md:h-6" />
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Contador + Gabarito + Botão - FORA do flex das setas */}
                              <div className="mt-1 md:mt-2 flex items-center justify-center gap-2 md:gap-8 px-2">
                                <span className="text-[10px] md:text-sm font-semibold text-white bg-gray-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                                  {(indiceAtual % questoesFiltradas.length) + 1} de {questoesFiltradas.length}
                                </span>
                                <span className="text-[10px] md:text-sm px-2 md:px-3 py-0.5 md:py-1 bg-orange-100 text-orange-700 rounded-full font-bold">
                                  {q.questoesVinculadas?.length} questões | Gab: {q.questoesVinculadas?.map(qv => qv.gabarito).join(', ')}
                                </span>
                              </div>

                              {/* Botão */}
                              <div className="mt-1 md:mt-2 flex justify-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!selected && !blocoCompleto && !blocoFicariaCheio) {
                                      const blocoIdx = blocosSimuladoEscola.findIndex(b => b.id === blocoAtivoEscola);
                                      const novaLista = [...blocosSimuladoEscola[blocoIdx].questoesSelecionadas, q.id];
                                      setBlocosSimuladoEscola(prev => prev.map((b, i) => 
                                        i === blocoIdx 
                                          ? { ...b, questoesSelecionadas: novaLista }
                                          : b
                                      ));
                                      // Mostrar modal quando bloco 1 fica completo
                                      const novoTotal = contarQuestoesReais(novaLista, questoes);
                                      if (blocoAtivoEscola === 1 && novoTotal >= questoesPorBlocoSAEB(filtrosEscola.serie)) {
                                        setShowModalSegundoBlocoEscola(true);
                                      }
                                    }
                                  }}
                                  className={`px-4 md:px-6 py-1.5 md:py-2.5 rounded-lg font-bold text-[10px] md:text-sm shadow-md transition-all hover:scale-[1.02] flex items-center justify-center whitespace-nowrap ${
                                    selected || blocoCompleto || blocoFicariaCheio
                                      ? 'bg-gray-400 text-white cursor-not-allowed' 
                                      : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-green-700 hover:to-emerald-700'
                                  }`}
                                  disabled={selected || blocoCompleto || blocoFicariaCheio}
                                >
                                  {selected ? 'TEXTO JÁ ADICIONADO' : blocoCompleto ? `BLOCO COMPLETO (${questoesReaisNoBloco}/${questoesPorBlocoSAEB(filtrosEscola.serie)})` : blocoFicariaCheio ? `⚠️ EXCEDE LIMITE` : `QUERO ESTE TEXTO (+${questoesDoTexto})`}
                                </button>
                              </div>
                            </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  // MODO: Habilidade Única - filtra por habilidade específica (exclui texto-multiplas)
                ['pequena', 'media', 'grande', 'inteira'].map(tamanhoAtual => {
                  const componentesFiltroEscola = componenteSimuladoEscola === 'Língua Portuguesa' 
                    ? ['Língua Portuguesa', 'Língua Portuguesa SAEB']
                    : ['Matemática', 'Matemática SAEB'];
                  const questoesFiltradas = questoes
                    .filter(q => q.tamanho === tamanhoAtual &&
                                componentesFiltroEscola.includes(q.componente) &&
                                (!filtrosEscola.serie || q.serie === filtrosEscola.serie) &&
                                (!filtrosEscola.habilidade || q.habilidade === filtrosEscola.habilidade) &&
                                (!filtrosEscola.tamanho || q.tamanho === filtrosEscola.tamanho) &&
                                q.modulo === 'simulado'); // v572: Filtrar apenas questões do módulo simulado
                  
                  if (questoesFiltradas.length === 0) return null;

                  return (
                    <div key={tamanhoAtual} className="mb-6 md:mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${
                          tamanhoAtual === 'pequena' ? 'bg-green-500' :
                          tamanhoAtual === 'media' ? 'bg-blue-500' :
                          tamanhoAtual === 'grande' ? 'bg-orange-500' :
                          'bg-purple-500'
                        }`}></div>
                        <span className="text-xs md:text-sm font-bold text-white uppercase">
                          {tamanhoAtual === 'pequena' ? 'Pequenas (~6.5cm)' :
                           tamanhoAtual === 'media' ? 'Médias (~8.5cm)' :
                           tamanhoAtual === 'grande' ? 'Grandes (~12cm)' :
                           'Página Inteira (~22cm)'}
                        </span>
                      </div>
                      
                      {/* Carrossel de Questões */}
                      <div className="relative">
                        {questoesFiltradas.length > 0 && (() => {
                          const indiceAtual = indiceQuestaoVisivelEscola[tamanhoAtual] || 0;
                          const q = questoesFiltradas[indiceAtual % questoesFiltradas.length];
                          const blocoAtualEsc = blocosSimuladoEscola.find(b => b.id === blocoAtivoEscola);
                          // 🔧 v475: Verificar se questão está em QUALQUER bloco (impede duplicação)
                          const selectedNoBloco1 = blocosSimuladoEscola[0]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                          const selectedNoBloco2 = blocosSimuladoEscola[1]?.questoesSelecionadas.some(item => String(item) === String(q.id));
                          const selected = selectedNoBloco1 || selectedNoBloco2;
                          const questoesReaisNoBloco = contarQuestoesReais(blocoAtualEsc?.questoesSelecionadas || [], questoes);
                          const blocoCompleto = questoesReaisNoBloco >= questoesPorBlocoSAEB(filtrosEscola.serie);
                          const orderNumber = selected ? blocoAtualEsc.questoesSelecionadas.findIndex(item => String(item) === String(q.id)) + 1 : 0;
                          const totalQuestoesEscola = questoesFiltradas.length;
                          
                          return (
                            <div className="relative">
                              <div className="flex items-center gap-1 md:gap-2">
                                {/* Seta Esquerda */}
                                <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                  {totalQuestoesEscola > 1 && (
                                    <button
                                      onClick={() => {
                                        setIndiceQuestaoVisivelEscola(prev => ({
                                          ...prev,
                                          [tamanhoAtual]: ((prev[tamanhoAtual] || 0) - 1 + totalQuestoesEscola) % totalQuestoesEscola
                                        }));
                                      }}
                                      className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                    >
                                      <ChevronLeft className="w-4 h-4 md:w-6 md:h-6" />
                                    </button>
                                  )}
                                </div>
                                
                                {/* Card Central */}
                                <div className="flex-1">
                                  <ProtectedImageArea 
                                    className="border-2 border-gray-300 rounded-lg bg-white shadow-inner hover:border-blue-400 transition overflow-hidden"
                                    onClick={() => setQuestaoEmVisualizacao(q)}
                                  >
                                    <div className={`p-2 md:p-3 ${
                                      tamanhoAtual === 'inteira' ? '' :
                                      tamanhoAtual === 'pequena' ? 'max-h-36 md:max-h-48 overflow-hidden' :
                                      tamanhoAtual === 'media' ? 'max-h-48 md:max-h-64 overflow-hidden' :
                                      tamanhoAtual === 'grande' ? 'max-h-64 md:max-h-80 overflow-hidden' :
                                      'max-h-80 md:max-h-96 overflow-hidden'
                                    }`}>
                                      {q.enunciado && (
                                        <p className="text-[10px] md:text-xs font-semibold text-gray-700 mb-1">{q.enunciado}</p>
                                      )}
                                      {q.texto && (
                                        <div className="bg-gray-50 border-l-2 border-blue-400 p-1.5 md:p-2 mb-1.5 md:mb-2 text-[10px] md:text-xs italic line-clamp-3 md:line-clamp-4">
                                          {q.texto.substring(0, 200)}{q.texto.length > 200 ? '...' : ''}
                                        </div>
                                      )}
                                      {q.temImagem && (
                                        <div className="my-1 md:my-2 scale-75 origin-top">
                                          <ImagemQuestao tipo={q.tipoImagem} />
                                        </div>
                                      )}
                                      {q.pergunta && (
                                        <p className="text-[10px] md:text-xs font-medium text-gray-800 mb-1.5 md:mb-2">{q.pergunta}</p>
                                      )}
                                      {q.alternativas && (
                                        <div className="space-y-0.5 md:space-y-1">
                                          {q.alternativas.slice(0, 4).map((alt, idx) => (
                                            <div key={idx} className="flex items-center gap-1 text-[10px] md:text-xs bg-gray-50 p-0.5 md:p-1 rounded">
                                              <span className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center text-[10px] md:text-xs font-bold">
                                                {alt.charAt(0)}
                                              </span>
                                              <span className="truncate">{alt.substring(3)}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {/* 🔥 Questões com imagem do Firebase */}
                                      {q.imagem && (q.imagem.startsWith('http') || q.imagem.startsWith('data:')) && (
                                        <div className="flex items-center justify-center h-full">
                                          <ImagemComLoading 
                                            src={q.imagem} 
                                            alt={`Questão ${q.id}`} 
                                            className="max-w-full object-contain rounded"
                                            tamanho={tamanhoAtual}
                                            style={{ maxHeight: tamanhoAtual === 'pequena' ? '160px' : tamanhoAtual === 'media' ? '220px' : tamanhoAtual === 'grande' ? '280px' : tamanhoAtual === 'inteira' ? '550px' : '350px' }}
                                          />
                                        </div>
                                      )}
                                      
                                      {/* Fallback para questões sem conteúdo detalhado */}
                                      {!q.enunciado && !q.texto && !q.temImagem && !q.imagem && (
                                        <div className="flex items-center justify-center h-full text-gray-400 py-4">
                                          <div className="text-center">
                                            <Eye className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-1 md:mb-2 opacity-40" />
                                            <p className="text-[10px] md:text-sm">Questão {q.id}</p>
                                            <p className="text-[10px] md:text-xs">{getCodigoHabilidadeAtualizado(q)}</p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </ProtectedImageArea>
                                </div>
                                
                                {/* Seta Direita */}
                                <div className="flex-shrink-0 w-6 md:w-10 flex items-center justify-center">
                                  {totalQuestoesEscola > 1 && (
                                    <button
                                      onClick={() => {
                                        setIndiceQuestaoVisivelEscola(prev => ({
                                          ...prev,
                                          [tamanhoAtual]: ((prev[tamanhoAtual] || 0) + 1) % totalQuestoesEscola
                                        }));
                                      }}
                                      className="w-6 h-6 md:w-10 md:h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                    >
                                      <ChevronRight className="w-4 h-4 md:w-6 md:h-6" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              
                              {/* Contador + Gabarito + Botão - FORA do flex das setas */}
                              <div className="mt-1 md:mt-2 space-y-1 md:space-y-2">
                                <div className="flex items-center justify-center gap-4 md:gap-8 px-2">
                                  <span className="text-[10px] md:text-sm font-semibold text-white bg-gray-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                                    {(indiceAtual % questoesFiltradas.length) + 1} de {questoesFiltradas.length}
                                  </span>
                                  {/* v532: Omitir gabarito para plano Gold */}
                                  {!isPlanoGold && (
                                    <span className="text-[10px] md:text-sm px-2 md:px-3 py-0.5 md:py-1 bg-green-100 text-green-700 rounded-full font-bold">
                                      Gabarito: {q.gabarito}
                                    </span>
                                  )}
                                </div>
                                
                                <div className="flex justify-center">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!selected && !blocoCompleto) {
                                        const blocoIdx = blocosSimuladoEscola.findIndex(b => b.id === blocoAtivoEscola);
                                        const novaLista = [...blocosSimuladoEscola[blocoIdx].questoesSelecionadas, q.id];
                                        setBlocosSimuladoEscola(prev => prev.map((b, i) => 
                                          i === blocoIdx 
                                            ? { ...b, questoesSelecionadas: novaLista }
                                            : b
                                        ));
                                        // Mostrar modal quando bloco 1 fica completo
                                        const novoTotal = contarQuestoesReais(novaLista, questoes);
                                        if (blocoAtivoEscola === 1 && novoTotal >= questoesPorBlocoSAEB(filtrosEscola.serie)) {
                                          setShowModalSegundoBlocoEscola(true);
                                        }
                                      }
                                    }}
                                    className={`px-4 md:px-6 py-1.5 md:py-2.5 rounded-lg font-bold text-[10px] md:text-sm shadow-md transition-all hover:scale-[1.02] flex items-center justify-center whitespace-nowrap ${
                                      selected 
                                        ? 'bg-gray-400 text-white cursor-not-allowed' 
                                        : blocoCompleto
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-green-700 hover:to-emerald-700'
                                    }`}
                                    disabled={selected || blocoCompleto}
                                  >
                                    {selected ? 'JÁ ADICIONADA' : blocoCompleto ? `BLOCO COMPLETO (${questoesPorBlocoSAEB(filtrosEscola.serie)}/${questoesPorBlocoSAEB(filtrosEscola.serie)})` : 'QUERO ESSA QUESTÃO'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })
                )}
                  </>
                )}
              </div>
            </div>

            {/* Coluna Direita - Preview do Simulado (IGUAL AO PROFESSOR) */}
            <div className="lg:col-span-6 bg-white rounded-xl shadow-sm p-4 md:p-4 sticky top-4 self-start">
              {/* Indicador de progresso */}
              <div className="mb-3 md:mb-4 p-3 md:p-4 rounded-xl" style={{ backgroundColor: '#1e3a5f' }}>
                <div className="flex items-center justify-between text-white mb-2 md:mb-3">
                  <span className="font-semibold text-xs md:text-base">Progresso do Simulado</span>
                  <span className="text-base md:text-xl font-bold">
                    {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) + contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(filtrosEscola.serie) * 2}
                  </span>
                </div>
                <div className="w-full bg-white bg-opacity-20 rounded-full h-2 md:h-3">
                  <div
                    className="h-2 md:h-3 rounded-full transition-all bg-gradient-to-r from-green-400 to-green-500"
                    style={{ width: `${((contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) + contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)) / (questoesPorBlocoSAEB(filtrosEscola.serie) * 2)) * 100}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1.5 md:mt-2 text-[10px] md:text-xs text-blue-200">
                  <span>Bloco 1: {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(filtrosEscola.serie)}</span>
                  <span>Bloco 2: {contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(filtrosEscola.serie)}</span>
                </div>
              </div>

              {/* Tabs de navegação */}
              {(blocosSimuladoEscola[0].questoesSelecionadas.length > 0 || blocosSimuladoEscola[1].questoesSelecionadas.length > 0) && (
                <div className="flex gap-1 md:gap-2 mb-3 md:mb-4 flex-wrap">
                  {blocosSimuladoEscola[0].questoesSelecionadas.length > 0 && (
                    <>
                      <button
                        onClick={() => setPaginaSimuladoAtivaEscola(2)}
                        className={`px-2 md:px-4 py-1 md:py-2 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-sm ${
                          paginaSimuladoAtivaEscola === 2 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={paginaSimuladoAtivaEscola === 2 ? {backgroundColor: '#f7941d'} : {}}
                      >
                        Capa B1
                      </button>
                      <button
                        onClick={() => setPaginaSimuladoAtivaEscola(3)}
                        className={`px-2 md:px-4 py-1 md:py-2 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-sm ${
                          paginaSimuladoAtivaEscola === 3 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={paginaSimuladoAtivaEscola === 3 ? {backgroundColor: '#f7941d'} : {}}
                      >
                        <span className="hidden md:inline">Questões B1 ({contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes)})</span>
                        <span className="md:hidden">Q. B1 ({contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes)})</span>
                      </button>
                    </>
                  )}
                  {blocosSimuladoEscola[1].questoesSelecionadas.length > 0 && (
                    <>
                      <button
                        onClick={() => setPaginaSimuladoAtivaEscola(4)}
                        className={`px-2 md:px-4 py-1 md:py-2 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-sm ${
                          paginaSimuladoAtivaEscola === 4 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={paginaSimuladoAtivaEscola === 4 ? {backgroundColor: '#f7941d'} : {}}
                      >
                        Capa B2
                      </button>
                      <button
                        onClick={() => setPaginaSimuladoAtivaEscola(5)}
                        className={`px-2 md:px-4 py-1 md:py-2 rounded-lg font-semibold whitespace-nowrap transition text-[10px] md:text-sm ${
                          paginaSimuladoAtivaEscola === 5 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={paginaSimuladoAtivaEscola === 5 ? {backgroundColor: '#f7941d'} : {}}
                      >
                        <span className="hidden md:inline">Questões B2 ({contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)})</span>
                        <span className="md:hidden">Q. B2 ({contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)})</span>
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setPaginaSimuladoAtivaEscola(1)}
                    className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition text-sm ${
                      paginaSimuladoAtivaEscola === 1 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={paginaSimuladoAtivaEscola === 1 ? {backgroundColor: '#f7941d'} : {}}
                  >
                    📋 Cartão-Resposta
                  </button>
                </div>
              )}

              {/* Área de Pré-visualização */}
              <div className="border-2 border-gray-300 rounded-xl p-6 bg-white shadow-inner max-h-[calc(100vh-350px)] overflow-y-auto">
                {/* Se não há questões selecionadas */}
                {blocosSimuladoEscola[0].questoesSelecionadas.length === 0 && blocosSimuladoEscola[1].questoesSelecionadas.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-sm mb-2">Pré-visualização do Simulado SAEB</p>
                    <p className="text-xs">
                      Bloco 1: {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(filtrosEscola.serie)} • 
                      Bloco 2: {contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)}/{questoesPorBlocoSAEB(filtrosEscola.serie)}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* PÁGINA 1: CARTÃO-RESPOSTA */}
                    {paginaSimuladoAtivaEscola === 1 && (
                      <div className="text-center">
                        <div className="border-b-2 border-gray-300 pb-3 md:pb-4 mb-4 md:mb-6">
                          <div className="flex justify-between items-start mb-2">
                            <div></div>
                            <h2 className="text-base md:text-xl font-bold text-gray-900">SIMULADO SAEB</h2>
                            <div className="bg-orange-500 text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-bold">
                              #{formatarCodigoSimulado(simuladoSelecionado?.codigo || simuladoSelecionado?.numero || numeroSimulado)}
                            </div>
                          </div>
                          <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">CARTÃO-RESPOSTA</p>
                          
                          {/* Linha 1: Escola, Série, Turma (mobile e desktop) */}
                          <div className="flex justify-center gap-3 md:gap-8 text-[9px] md:text-xs text-gray-600 mb-1">
                            <span>Escola: <span className="font-semibold">{cabecalhoConfig.nomeEscola || escolaVinculada?.nome || currentUser?.escolaNome || currentUser?.escola}</span></span>
                            <span>Série: <span className="font-semibold">{filtrosEscola.serie || cabecalhoConfig.serie}</span></span>
                            <span>Turma: <span className="font-semibold">{cabecalhoConfig.turma}</span></span>
                          </div>
                          {/* Linha 2: Componente Curricular (mobile e desktop) */}
                          <div className="text-[9px] md:text-xs text-gray-600">
                            <span>Componente Curricular: <span className="font-semibold">{componenteSimuladoEscola === "Língua Portuguesa" ? "Língua Portuguesa" : componenteSimuladoEscola}</span></span>
                          </div>
                        </div>
                        
                        <div className="mb-4 md:mb-6 px-2 md:px-0">
                          <div className="flex items-baseline gap-1 md:gap-2 mb-3 md:mb-4 max-w-md mx-auto">
                            <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">Aluno(a):</span>
                            <div className="flex-1 border-b-2 border-gray-400"></div>
                          </div>
                          <div className="flex items-baseline gap-1 md:gap-2 max-w-xs mx-auto">
                            <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">Data:</span>
                            <div className="w-24 md:w-32 border-b-2 border-gray-400"></div>
                          </div>
                        </div>

                        {/* Grade do Cartão-Resposta - DINÂMICO baseado nas questões selecionadas */}
                        <div className="flex justify-center gap-8">
                          {/* Bloco 1 - Mostrar apenas se tiver questões */}
                          {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) > 0 && (
                            <div className="text-center">
                              <div className={`${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-800' : 'bg-blue-900'} text-white px-4 py-2 text-xs font-bold mb-4 rounded`}>
                                BLOCO 1 - {componenteSimuladoEscola === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}
                              </div>
                              <div className="space-y-2">
                                {Array.from({ length: contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) }, (_, i) => i + 1).map(num => (
                                  <div key={num} className="flex items-center gap-3 justify-center">
                                    <span className="text-xs font-bold text-gray-800 w-6">{String(num).padStart(2, '0')}</span>
                                    <div className="flex gap-3">
                                      {['A', 'B', 'C', 'D'].map(alt => (
                                        <div key={alt} className="w-7 h-7 border-2 border-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                          {alt}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Bloco 2 - Mostrar apenas se tiver questões */}
                          {contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes) > 0 && (
                            <div className="text-center">
                              <div className={`${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-800' : 'bg-blue-900'} text-white px-4 py-2 text-xs font-bold mb-4 rounded`}>
                                BLOCO 2 - {componenteSimuladoEscola === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}
                              </div>
                              <div className="space-y-2">
                                {Array.from({ length: contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes) }, (_, i) => i + contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) + 1).map(num => (
                                  <div key={num} className="flex items-center gap-3 justify-center">
                                    <span className="text-xs font-bold text-gray-800 w-6">{num}</span>
                                    <div className="flex gap-3">
                                      {['A', 'B', 'C', 'D'].map(alt => (
                                        <div key={alt} className="w-7 h-7 border-2 border-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                          {alt}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Mensagem se não houver questões */}
                          {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) === 0 && 
                           contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes) === 0 && (
                            <div className="text-center text-gray-400 py-8">
                              <p className="text-sm">Adicione questões para visualizar o cartão-resposta</p>
                            </div>
                          )}
                        </div>

                        <div className="mt-6 flex justify-center">
                          <QrCode className="w-16 h-16 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-400 mt-2">QR Code para correção automática</p>
                      </div>
                    )}

                    {/* PÁGINA 2: CAPA BLOCO 1 */}
                    {paginaSimuladoAtivaEscola === 2 && blocosSimuladoEscola[0].questoesSelecionadas.length > 0 && (
                      <div className="text-center py-16">
                        <div className="mb-8">
                          <div className={`w-24 h-24 ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                            <BookOpen className={`w-12 h-12 ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`} />
                          </div>
                          <h1 className="text-4xl font-bold text-gray-900 mb-4">BLOCO 1</h1>
                          <div className={`w-32 h-1 ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-600' : 'bg-blue-600'} mx-auto mb-6`}></div>
                          <h2 className={`text-2xl font-semibold ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-700' : 'text-blue-700'}`}>
                            {componenteSimuladoEscola === 'Língua Portuguesa' ? 'LÍNGUA PORTUGUESA' : 'MATEMÁTICA'}
                          </h2>
                        </div>
                        <div className={`${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-50' : 'bg-blue-50'} rounded-lg p-6 max-w-md mx-auto`}>
                          <p className={`text-sm ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-800' : 'text-blue-800'} mb-2`}>
                            <span className="font-bold">{contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes)}</span> questões
                          </p>
                          <p className={`text-xs ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`}>
                            Questões 01 a {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* PÁGINA 3: QUESTÕES BLOCO 1 */}
                    {paginaSimuladoAtivaEscola === 3 && blocosSimuladoEscola[0].questoesSelecionadas.length > 0 && (
                      <div>
                        <div className="text-center border-b-2 border-purple-200 pb-3 mb-6">
                          <h3 className="font-bold text-purple-800">BLOCO 1 - {componenteSimuladoEscola === 'Língua Portuguesa' ? 'LÍNGUA PORTUGUESA' : 'MATEMÁTICA'}</h3>
                          <p className="text-xs text-gray-600">{contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes)} questões</p>
                        </div>
                        <div className="space-y-4">
                          {(() => {
                            let numeroQuestaoGlobal = 0;
                            return blocosSimuladoEscola[0].questoesSelecionadas.map((qId) => {
                              // Busca com comparação flexível de tipos (string vs number)
                              const q = questoes.find(quest => String(quest.id) === String(qId));
                              if (!q) return null;
                              
                              // Se for texto com múltiplas questões
                              if (q.tamanho === 'texto-multiplas' && q.questoesVinculadas) {
                                return (
                                  <div key={qId} className="border-2 border-orange-300 rounded-xl p-3 bg-orange-50 relative">
                                    <button
                                      onClick={() => {
                                        setBlocosSimuladoEscola(blocosSimuladoEscola.map(b => 
                                          b.id === 1 ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(qId))} : b
                                        ));
                                      }}
                                      className="absolute -right-2 -top-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Layers className="w-4 h-4 text-orange-600" />
                                      <span className="font-bold text-orange-700 text-sm">TEXTO COM {q.questoesVinculadas.length} QUESTÕES</span>
                                    </div>
                                    <div className="bg-white border border-orange-200 rounded p-2 mb-2">
                                      <p className="text-xs text-gray-500 text-center">[Texto Base]</p>
                                    </div>
                                    <div className="space-y-2">
                                      {q.questoesVinculadas.map((qv, idx) => {
                                        numeroQuestaoGlobal++;
                                        return (
                                          <div key={idx} className="bg-white border border-pink-200 rounded p-2">
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs font-bold text-pink-700">
                                                QUESTÃO {String(numeroQuestaoGlobal).padStart(2, '0')} - {qv.habilidade}
                                              </span>
                                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold">Gab: {qv.gabarito}</span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Questão normal
                              numeroQuestaoGlobal++;
                              return (
                                <div key={qId} className="mb-4 relative">
                                  {/* Botão X para remover questão */}
                                  <button
                                    onClick={() => {
                                      setBlocosSimuladoEscola(blocosSimuladoEscola.map(b => 
                                        b.id === 1 ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(qId))} : b
                                      ));
                                    }}
                                    className="absolute right-0 top-0 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                  {/* TÍTULO DA QUESTÃO - retângulo cinza igual ao PDF */}
                                  <div className="mb-1">
                                    <span className="inline-block bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1">
                                      QUESTÃO {String(numeroQuestaoGlobal).padStart(2, '0')} {getCodigoHabilidadeAtualizado(q) ? `(${getCodigoHabilidadeAtualizado(q)})` : ''}
                                    </span>
                                  </div>
                                  {/* Mostrar imagem se disponível */}
                                  {q.imagem && (q.imagem.startsWith('http') || q.imagem.startsWith('data:')) ? (
                                    <div className="bg-white">
                                      <img 
                                        src={q.imagem} 
                                        alt={`Questão ${numeroQuestaoGlobal}`} 
                                        className="w-full object-contain"
                                      />
                                    </div>
                                  ) : (
                                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-400">
                                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                      <p className="text-sm font-semibold">Questão {numeroQuestaoGlobal}</p>
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}

                    {/* PÁGINA 4: CAPA BLOCO 2 */}
                    {paginaSimuladoAtivaEscola === 4 && blocosSimuladoEscola[1].questoesSelecionadas.length > 0 && (
                      <div className="text-center py-16">
                        <div className="mb-8">
                          <div className={`w-24 h-24 ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                            <Target className={`w-12 h-12 ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`} />
                          </div>
                          <h1 className="text-4xl font-bold text-gray-900 mb-4">BLOCO 2</h1>
                          <div className={`w-32 h-1 ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-600' : 'bg-blue-600'} mx-auto mb-6`}></div>
                          <h2 className={`text-2xl font-semibold ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-700' : 'text-blue-700'}`}>
                            {componenteSimuladoEscola === 'Língua Portuguesa' ? 'LÍNGUA PORTUGUESA' : 'MATEMÁTICA'}
                          </h2>
                        </div>
                        <div className={`${componenteSimuladoEscola === 'Língua Portuguesa' ? 'bg-purple-50' : 'bg-blue-50'} rounded-lg p-6 max-w-md mx-auto`}>
                          <p className={`text-sm ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-800' : 'text-blue-800'} mb-2`}>
                            <span className="font-bold">{contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)}</span> questões
                          </p>
                          <p className={`text-xs ${componenteSimuladoEscola === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'}`}>
                            Questões {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) + 1} a {contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes) + contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* PÁGINA 5: QUESTÕES BLOCO 2 */}
                    {paginaSimuladoAtivaEscola === 5 && blocosSimuladoEscola[1].questoesSelecionadas.length > 0 && (
                      <div>
                        <div className="text-center border-b-2 border-blue-200 pb-3 mb-6">
                          <h3 className="font-bold text-blue-800">BLOCO 2 - {componenteSimuladoEscola === 'Língua Portuguesa' ? 'LÍNGUA PORTUGUESA' : 'MATEMÁTICA'}</h3>
                        </div>
                        <div className="space-y-4">
                          {blocosSimuladoEscola[1].questoesSelecionadas.map((qId, idx) => {
                            // Busca com comparação flexível de tipos (string vs number)
                            const q = questoes.find(quest => String(quest.id) === String(qId));
                            if (!q) return null;
                            return (
                              <div key={qId} className="mb-4 relative">
                                {/* Botão X para remover questão */}
                                <button
                                  onClick={() => {
                                    setBlocosSimuladoEscola(blocosSimuladoEscola.map(b => 
                                      b.id === 2 ? {...b, questoesSelecionadas: b.questoesSelecionadas.filter(id => String(id) !== String(qId))} : b
                                    ));
                                  }}
                                  className="absolute right-0 top-0 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                                {/* TÍTULO DA QUESTÃO - retângulo cinza igual ao PDF */}
                                <div className="mb-1">
                                  <span className="inline-block bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1">
                                    QUESTÃO {String(blocosSimuladoEscola[0].questoesSelecionadas.length + idx + 1).padStart(2, '0')} {getCodigoHabilidadeAtualizado(q) ? `(${getCodigoHabilidadeAtualizado(q)})` : ''}
                                  </span>
                                </div>
                                {/* Mostrar imagem se disponível */}
                                {q.imagem && (q.imagem.startsWith('http') || q.imagem.startsWith('data:')) ? (
                                  <div className="bg-white">
                                    <img 
                                      src={q.imagem} 
                                      alt={`Questão ${blocosSimuladoEscola[0].questoesSelecionadas.length + idx + 1}`} 
                                      className="w-full object-contain"
                                    />
                                  </div>
                                ) : (
                                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-400">
                                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm font-semibold">Questão {blocosSimuladoEscola[0].questoesSelecionadas.length + idx + 1}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Botão Gerar Simulado - Lógica igual ao professor */}
              {(blocosSimuladoEscola[0].questoesSelecionadas.length > 0 || blocosSimuladoEscola[1].questoesSelecionadas.length > 0 || pdfGerado) && (() => {
                const bloco1QuestoesReais = contarQuestoesReais(blocosSimuladoEscola[0].questoesSelecionadas, questoes);
                const bloco2QuestoesReais = contarQuestoesReais(blocosSimuladoEscola[1].questoesSelecionadas, questoes);
                const bloco1Completo = bloco1QuestoesReais >= questoesPorBlocoSAEB(filtrosEscola.serie);
                const bloco2Iniciado = blocosSimuladoEscola[1].questoesSelecionadas.length > 0;
                const bloco2Completo = bloco2QuestoesReais >= questoesPorBlocoSAEB(filtrosEscola.serie);
                
                // Pode gerar se: Bloco 1 completo E (Bloco 2 não iniciado OU Bloco 2 completo)
                const podeGerar = bloco1Completo && (!bloco2Iniciado || bloco2Completo);
                
                // Mensagem de status
                let mensagemStatus = '';
                let corStatus = '';
                if (!bloco1Completo) {
                  mensagemStatus = `Bloco 1: ${bloco1QuestoesReais}/${questoesPorBlocoSAEB(filtrosEscola.serie)} questões`;
                  corStatus = 'orange';
                } else if (bloco2Iniciado && !bloco2Completo) {
                  mensagemStatus = `Bloco 2: ${bloco2QuestoesReais}/${questoesPorBlocoSAEB(filtrosEscola.serie)} questões`;
                  corStatus = 'orange';
                } else if (bloco1Completo && !bloco2Iniciado) {
                  mensagemStatus = '✅ Bloco 1 completo! Deseja criar o Bloco 2 ou gerar apenas com o Bloco 1?';
                  corStatus = 'green';
                } else if (bloco1Completo && bloco2Completo) {
                  mensagemStatus = `✅ Simulado completo (${questoesPorBlocoSAEB(filtrosEscola.serie) * 2} questões)!`;
                  corStatus = 'green';
                }
                
                return (
                  <div className={`mt-3 md:mt-4 p-3 md:p-4 rounded-lg ${podeGerar ? 'bg-green-50 border-2 border-green-200' : 'bg-orange-50 border-2 border-orange-200'}`}>
                    {/* Status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {podeGerar ? (
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                        )}
                        <span className={`text-xs md:text-sm font-semibold ${podeGerar ? 'text-green-800' : 'text-orange-800'}`}>
                          {mensagemStatus}
                        </span>
                      </div>
                      <div className="text-[10px] md:text-xs text-gray-600">
                        {bloco1QuestoesReais + bloco2QuestoesReais} questões
                      </div>
                    </div>
                    
                    {/* Botão Gerar - só mostra se NÃO tem PDF gerado */}
                    {!pdfGerado && (
                      <button
                        onClick={() => {
                          if (podeGerar) {
                            // Abrir modal de geração de PDF (mesmo do professor)
                            setShowModalGerarPDF(true);
                          }
                        }}
                        disabled={!podeGerar}
                        className={`w-full py-2.5 md:py-3 rounded-lg font-bold text-xs md:text-sm flex items-center justify-center gap-2 transition ${
                          podeGerar 
                            ? 'text-white hover:opacity-90 shadow-lg' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        style={podeGerar ? {backgroundColor: '#f7941d'} : {}}
                      >
                        <Download className="w-4 h-4 md:w-5 md:h-5" />
                        {podeGerar 
                          ? (bloco2Completo ? 'Gerar Simulado PDF (2 Blocos)' : 'Gerar Simulado PDF (1 Bloco)')
                          : `Complete o bloco (${bloco1QuestoesReais + bloco2QuestoesReais}/${questoesPorBlocoSAEB(filtrosEscola.serie) * 2})`
                        }
                      </button>
                    )}
                    
                    {/* Botão Baixar PDF - aparece após gerar */}
                    {pdfGerado && (
                      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <p className="text-xs md:text-sm font-semibold text-green-800">Simulado Gerado!</p>
                        </div>
                        <button
                          onClick={() => {
                            // Abrir em nova aba (evita erro CORS do CloudLayer)
                            window.open(pdfGerado.url, '_blank');
                            
                            // Limpar estado após download
                            setPdfGerado(null);
                            console.log('✅ PDF aberto em nova aba e estado limpo');
                          }}
                          className="w-full py-2 md:py-2.5 rounded-lg font-bold text-xs md:text-sm flex items-center justify-center gap-2 text-white bg-green-600 hover:bg-green-700 transition"
                        >
                          <Download className="w-4 h-4" />
                          Baixar Simulado PDF
                        </button>
                        
                        {/* Botão para criar novo simulado */}
                        <button
                          onClick={() => {
                            setPdfGerado(null);
                          }}
                          className="w-full py-1.5 md:py-2 rounded-lg font-semibold text-[10px] md:text-xs flex items-center justify-center gap-1 text-gray-600 border border-gray-300 hover:bg-gray-50 transition"
                        >
                          <Plus className="w-3 h-3 md:w-4 md:h-4" />
                          Criar Novo Simulado
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - ESTATÍSTICAS */}
        {currentView === 'escola-estatisticas' && (
          <div className="w-full overflow-x-hidden">
            {/* Cards principais - responsivos */}
            {(() => {
              // v689d: Calcular estatísticas reais
              const totalCreditosUsados = historicoPDFs.reduce((acc, pdf) => acc + (pdf.creditosUsados || pdf.totalPaginas || 0), 0);
              const creditosProfessores = historicoPDFs.filter(p => p.origem === 'professor').reduce((acc, pdf) => acc + (pdf.creditosUsados || pdf.totalPaginas || 0), 0);
              const profAtivos = professoresEscola.filter(p => p.ativo).length;
              const mediaPorProf = profAtivos > 0 ? Math.round(creditosProfessores / profAtivos) : 0;
              // Créditos usados no mês atual
              const agora = new Date();
              const inicioMes = new Date(agora.getFullYear(), agora.getMonth(), 1).toISOString();
              const creditosMes = historicoPDFs
                .filter(pdf => pdf.dataGeracao && pdf.dataGeracao >= inicioMes)
                .reduce((acc, pdf) => acc + (pdf.creditosUsados || pdf.totalPaginas || 0), 0);
              
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6 mb-4 md:mb-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-3 md:p-6 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-1 md:mb-2">
                      <FileText className="w-5 h-5 md:w-8 md:h-8 opacity-80" />
                      <span className="text-[8px] md:text-xs bg-white bg-opacity-20 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Total</span>
                    </div>
                    <div className="text-xl md:text-4xl font-bold">{totalCreditosUsados}</div>
                    <div className="text-blue-100 text-[10px] md:text-sm">Créditos Gerados</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-3 md:p-6 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-1 md:mb-2">
                      <Users className="w-5 h-5 md:w-8 md:h-8 opacity-80" />
                      <span className="text-[8px] md:text-xs bg-white bg-opacity-20 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Ativos</span>
                    </div>
                    <div className="text-xl md:text-4xl font-bold">{profAtivos}</div>
                    <div className="text-green-100 text-[10px] md:text-sm">Professores</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-3 md:p-6 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-1 md:mb-2">
                      <PieChart className="w-5 h-5 md:w-8 md:h-8 opacity-80" />
                      <span className="text-[8px] md:text-xs bg-white bg-opacity-20 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Média</span>
                    </div>
                    <div className="text-xl md:text-4xl font-bold">{mediaPorProf}</div>
                    <div className="text-purple-100 text-[10px] md:text-sm">Créd/Professor</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-3 md:p-6 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-1 md:mb-2">
                      <CreditCard className="w-5 h-5 md:w-8 md:h-8 opacity-80" />
                      <span className="text-[8px] md:text-xs bg-white bg-opacity-20 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Mês</span>
                    </div>
                    <div className="text-xl md:text-4xl font-bold">{creditosMes}</div>
                    <div className="text-orange-100 text-[10px] md:text-sm">Créditos Usados</div>
                  </div>
                </div>
              );
            })()}

            {/* Ranking de Professores */}
            <div className="rounded-xl p-3 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="text-sm md:text-lg font-bold text-white mb-3 md:mb-4 flex items-center gap-2">
                <Award className="w-4 h-4 md:w-5 md:h-5 text-yellow-400" />
                Ranking de Créditos por Professor
              </h3>
              <div className="space-y-2 md:space-y-3">
                {(() => {
                  // v689d: Calcular paginasGeradas por professor a partir do histórico real
                  const creditosPorProfessor = {};
                  historicoPDFs.filter(pdf => pdf.origem === 'professor').forEach(pdf => {
                    const id = pdf.professorId || pdf.userId;
                    if (id) {
                      creditosPorProfessor[id] = (creditosPorProfessor[id] || 0) + (pdf.creditosUsados || pdf.totalPaginas || 0);
                    }
                  });
                  
                  const profRanking = professoresEscola
                    .filter(p => p.ativo)
                    .map(p => ({
                      ...p,
                      creditosReais: creditosPorProfessor[p.professorId] || 0
                    }))
                    .sort((a, b) => b.creditosReais - a.creditosReais);
                  
                  const maxCreditos = Math.max(...profRanking.map(p => p.creditosReais), 1);
                  
                  return profRanking.map((prof, idx) => {
                    const percentual = (prof.creditosReais / maxCreditos) * 100;
                    return (
                      <div key={prof.id} className="flex items-center gap-2 md:gap-4">
                        <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-white text-xs md:text-base ${idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-400' : 'bg-blue-400'}`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center mb-0.5 md:mb-1">
                            <span className="font-medium text-white text-xs md:text-base truncate">{prof.nome}</span>
                            <span className="font-bold text-gray-300 text-xs md:text-base ml-2">{prof.creditosReais}</span>
                          </div>
                          <div className="h-2 md:h-3 bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-400' : 'bg-blue-400'}`}
                              style={{width: `${percentual}%`}}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - RECARGAS */}
        {currentView === 'escola-recargas' && (
          <div className="w-full overflow-x-hidden box-border">
            {/* Saldo atual */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 md:p-8 rounded-xl md:rounded-2xl shadow-lg mb-3 md:mb-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-3">
                  <p className="text-blue-100 mb-0.5 md:mb-1 text-[10px] md:text-base">Saldo Atual</p>
                  <div className="text-2xl md:text-5xl font-bold">{currentUser?.creditos || 0}</div>
                  <p className="text-blue-200 text-[9px] md:text-sm mt-0.5 md:mt-2">1 crédito = 1 página PDF ou 1 correção</p>
                </div>
                <div className="w-12 h-12 md:w-24 md:h-24 bg-white bg-opacity-20 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 md:w-12 md:h-12" />
                </div>
              </div>
            </div>

            {/* Pacotes de créditos */}
            <h3 className="text-sm md:text-lg font-bold text-white mb-2 md:mb-4">Escolha um pacote</h3>
            
            {/* Cards empilhados no mobile */}
            {/* 🔧 v416: Debug - mostrar plano do usuário no console */}
            {console.log('👤 Plano do usuário atual:', currentUser?.plano)}
            <div className="flex flex-col gap-3 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-4 mb-4 md:mb-6">
              {recargas.filter(r => r.ativo).map((pacote) => {
                // 🔧 v415: Verificar se é o plano atual (comparação case-insensitive)
                const planoUsuario = (currentUser?.plano || '').toLowerCase().trim();
                const nomePacote = (pacote.nome || '').toLowerCase().trim();
                const isPlanoAtual = planoUsuario === nomePacote && planoUsuario !== 'gratuito' && planoUsuario !== '';
                
                console.log('🎯 Comparando plano:', planoUsuario, 'com pacote:', nomePacote, '=> isPlanoAtual:', isPlanoAtual);
                
                return (
                <div 
                  key={pacote.id}
                  className={`rounded-xl p-3 md:p-5 cursor-pointer w-full box-border ${
                    isPlanoAtual ? 'border-2 border-green-400 ring-2 ring-green-400 ring-opacity-50' : 'border-2 border-gray-600'
                  }`} 
                  style={{ backgroundColor: '#1e3a5f' }}
                  onClick={() => {
                    if (!isPlanoAtual) {
                      setRecargaSelecionada(pacote);
                      setEtapaCompra(1);
                      setFormaPagamento('');
                      setPagamentoProcessado(false); setShowModalCompraRecarga(true);
                    }
                  }}
                >
                  {/* Indicador de plano atual */}
                  {isPlanoAtual && (
                    <div className="text-center mb-2">
                      <span className="bg-green-500 text-white text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-full flex items-center justify-center gap-1">
                        <CheckCircle className="w-3 h-3" /> MINHA RECARGA ATUAL
                      </span>
                    </div>
                  )}
                  <h4 className="text-sm md:text-xl font-bold text-white text-center mb-1">{pacote.nome}</h4>
                  <div className="text-center mb-1 md:mb-3">
                    <span className="text-lg md:text-3xl font-bold" style={{ color: '#f7941d' }}>
                      R$ {pacote.preco.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                  <p className="text-gray-300 text-[10px] md:text-sm mb-2 text-center">{pacote.descricao}</p>
                  <div className="space-y-0.5 md:space-y-2 text-gray-300 text-[11px] md:text-sm mb-2 md:mb-4">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                      <span>{pacote.paginas} Créditos <span className="text-[9px] md:text-xs text-gray-400">(válidos por 1 ano)</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                      <span>{pacote.series === 1 ? '1 série' : pacote.series >= 5 ? 'Todas as séries' : `${pacote.series} séries`}</span>
                    </div>
                    {pacote.vantagens && pacote.vantagens.map((vantagem, idx) => (
                      <div key={idx} className="flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                        <span>{vantagem}</span>
                      </div>
                    ))}
                  </div>
                  {isPlanoAtual ? (
                    <div className="w-full py-1.5 md:py-2.5 rounded-lg font-semibold text-white text-xs md:text-base bg-green-600 text-center flex items-center justify-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Minha recarga atual
                    </div>
                  ) : (
                    <button 
                      className="w-full py-1.5 md:py-2.5 rounded-lg font-semibold text-white text-xs md:text-base bg-green-500 hover:bg-green-600"
                    >
                      Escolher plano
                    </button>
                  )}
                </div>
              );
              })}
            </div>

            {/* Histórico */}
            <div className="rounded-xl p-3 md:p-6 border-2 border-gray-600" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="font-bold text-white mb-3 md:mb-4 text-sm md:text-base">Histórico de Recargas</h3>
              <div className="rounded-xl overflow-x-auto">
                <table className="w-full min-w-[400px]">
                  <thead style={{ backgroundColor: '#0a1929' }}>
                    <tr>
                      <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Data</th>
                      <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Créditos</th>
                      <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Valor</th>
                      <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Pag.</th>
                      <th className="text-center p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {historicoRecargas.length > 0 ? (
                      historicoRecargas.map((recarga) => (
                        <tr key={recarga.id} className="hover:bg-gray-800">
                          <td className="p-2 md:p-3 text-gray-300 text-xs md:text-sm">
                            {recarga.createdAt?.toDate ? recarga.createdAt.toDate().toLocaleDateString('pt-BR') : '-'}
                          </td>
                          <td className="p-2 md:p-3 font-bold text-blue-400 text-xs md:text-sm">+{recarga.paginas}</td>
                          <td className="p-2 md:p-3 text-gray-300 text-xs md:text-sm">R$ {recarga.valor?.toFixed(2).replace('.', ',') || '-'}</td>
                          <td className="p-2 md:p-3 text-gray-300 text-xs md:text-sm">{recarga.formaPagamento === 'pix' ? 'PIX' : 'Cartão'}</td>
                          <td className="p-2 md:p-3 text-center"><span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-green-900 text-green-300 border border-green-500 rounded-full text-[10px] md:text-xs font-bold">OK</span></td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-4 text-center text-gray-400 text-sm">
                          Nenhuma recarga realizada ainda
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - SUPORTE */}
        {currentView === 'escola-suporte' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Card WhatsApp */}
            <div className="p-4 md:p-8 rounded-xl text-center border-2 border-green-500" style={{ backgroundColor: '#1e3a5f' }}>
              <Headphones className="w-12 h-12 md:w-16 md:h-16 text-green-400 mx-auto mb-3 md:mb-4" />
              <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-4 text-white">WhatsApp</h3>
              <p className="text-gray-300 text-xs md:text-sm mb-3 md:mb-4">Atendimento rápido</p>
              <a 
                href={`https://wa.me/${telefoneSuporte}?text=Olá! Sou da escola e preciso de suporte.`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-green-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg hover:bg-green-700 transition text-sm md:text-base w-full md:w-auto"
              >
                Abrir WhatsApp
              </a>
              <p className="text-[10px] md:text-xs text-gray-400 mt-3">Seg-Sex 8h-18h | Sáb 8h-12h</p>
            </div>

            {/* Card Vídeos Tutoriais */}
            <div className="p-4 md:p-8 rounded-xl border-2 border-red-500" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-white flex items-center gap-2">
                <Video className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                Vídeos Tutoriais
              </h3>
              <div className="space-y-2 md:space-y-3 max-h-60 md:max-h-80 overflow-y-auto">
                {videosTutoriais.filter(v => v.ativo && v.titulo).length > 0 ? (
                  videosTutoriais.filter(v => v.ativo && v.titulo).map((video) => (
                    <a 
                      key={video.id}
                      href={video.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`border border-gray-600 rounded-lg p-3 md:p-4 flex items-center gap-3 md:gap-4 hover:bg-gray-800 transition ${video.url ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                      onClick={(e) => !video.url && e.preventDefault()}
                    >
                      <Video className="w-8 h-8 md:w-10 md:h-10 text-red-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-200 text-sm md:text-base truncate">{video.titulo}</div>
                        {!video.url && <span className="text-[10px] md:text-xs text-gray-500">Link não configurado</span>}
                      </div>
                      {video.url && <Play className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />}
                    </a>
                  ))
                ) : (
                  <div className="text-center py-6 md:py-8 text-gray-400">
                    <Video className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm md:text-base">Nenhum vídeo disponível</p>
                    <p className="text-[10px] md:text-xs mt-1">Entre em contato com o suporte</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 🏫 PÁGINA ESCOLA - HISTÓRICO DE PDFs */}
        {currentView === 'escola-historico' && (
          <div className="rounded-xl p-3 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
            <h3 className="font-bold text-sm md:text-lg mb-3 md:mb-6 text-white">Histórico de Simulados</h3>
            
            {/* Filtros responsivos */}
            <div className="flex flex-col md:flex-row gap-2 md:gap-3 mb-3 md:mb-6">
              <select 
                className="px-3 md:px-4 py-2 border rounded-lg bg-white text-sm md:text-base flex-1"
                onChange={(e) => setFiltroHistorico({...filtroHistorico, componente: e.target.value})}
                value={filtroHistorico.componente || ''}
              >
                <option value="">Todos os Componentes</option>
                <option>Língua Portuguesa</option>
                <option>Matemática</option>
              </select>
              <select 
                className="px-3 md:px-4 py-2 border rounded-lg bg-white text-sm md:text-base flex-1"
                onChange={(e) => setFiltroHistorico({...filtroHistorico, serie: e.target.value})}
                value={filtroHistorico.serie || ''}
              >
                <option value="">Todas as Séries</option>
                <option>2º ano</option>
                <option>5º ano</option>
                <option>9º ano</option>
              </select>
              <button
                onClick={() => setBuscaHistoricoRealizada(true)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition text-sm md:text-base"
              >
                🔍 Buscar
              </button>
            </div>

            <div className="rounded-xl overflow-hidden">
              {!buscaHistoricoRealizada ? (
                <div className="text-center py-6 md:py-8 text-gray-400">
                  <FileText className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm md:text-base">Selecione os filtros e clique em Buscar.</p>
                </div>
              ) : carregandoHistorico ? (
                <div className="text-center py-6 md:py-8 text-gray-300">
                  <div className="w-6 h-6 md:w-8 md:h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <span className="text-sm">Carregando...</span>
                </div>
              ) : historicoPDFs.filter(pdf => pdf.tipo === 'simulado-escola' || pdf.tipo === 'simulado').length === 0 ? (
                <div className="text-center py-6 md:py-8 text-gray-400">
                  <FileText className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm md:text-base">Nenhum simulado gerado ainda.</p>
                  <p className="text-xs md:text-sm">Gere seu primeiro simulado!</p>
                </div>
              ) : (
                <>
                  {/* Desktop - Tabela */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ backgroundColor: '#0a1929' }}>
                        <tr>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Título</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Código</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Série</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Componente</th>
                          <th className="text-center py-3 px-4 text-blue-300 text-sm">Questões</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Data</th>
                          <th className="text-right py-3 px-4 text-blue-300 text-sm">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historicoPDFs
                          .filter(pdf => {
                            if (pdf.tipo !== 'simulado-escola' && pdf.tipo !== 'simulado') return false;
                            if (filtroHistorico.componente && pdf.componente !== filtroHistorico.componente) return false;
                            if (filtroHistorico.serie && pdf.serie !== filtroHistorico.serie) return false;
                            return true;
                          })
                          .map((pdf) => (
                          <tr key={pdf.id} className="border-b border-gray-700 hover:bg-gray-800">
                            <td className="py-3 px-4 text-white text-sm">
                              <div className="font-semibold">#{pdf.codigoSimulado || '---'} - {pdf.componente || 'Simulado'}</div>
                              <div className="text-xs text-gray-400">{pdf.serie || ''}</div>
                            </td>
                            <td className="py-3 px-4">
                              {pdf.codigoSimulado ? (
                                <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                  #{pdf.codigoSimulado}
                                </span>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-300">{pdf.serie || '-'}</td>
                            <td className="py-3 px-4 text-gray-300">{pdf.componente || '-'}</td>
                            <td className="py-3 px-4 text-center text-gray-300">{pdf.totalQuestoes || '-'}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">
                              {pdf.dataGeracao ? new Date(pdf.dataGeracao).toLocaleDateString('pt-BR') : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex justify-end gap-2">
                                <button 
                                  onClick={() => window.open(pdf.urlPdf, '_blank')}
                                  className="p-2 text-green-400 hover:bg-green-900 rounded"
                                  title="Baixar PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile - Cards */}
                  <div className="md:hidden space-y-2">
                    {historicoPDFs
                      .filter(pdf => {
                        if (pdf.tipo !== 'simulado-escola' && pdf.tipo !== 'simulado') return false;
                        if (filtroHistorico.componente && pdf.componente !== filtroHistorico.componente) return false;
                        if (filtroHistorico.serie && pdf.serie !== filtroHistorico.serie) return false;
                        return true;
                      })
                      .map((pdf) => (
                      <div key={pdf.id} className="bg-gray-800 rounded-lg p-2.5">
                        <div className="flex justify-between items-start mb-1.5">
                          <div className="flex-1 min-w-0 pr-2">
                            <p className="text-white text-xs font-semibold">#{pdf.codigoSimulado || '---'} - {pdf.componente || 'Simulado'}</p>
                            <p className="text-gray-400 text-[10px]">{pdf.serie || ''} • {pdf.dataGeracao ? new Date(pdf.dataGeracao).toLocaleDateString('pt-BR') : '-'}</p>
                          </div>
                          <button 
                            onClick={() => window.open(pdf.urlPdf, '_blank')}
                            className="p-1.5 bg-green-600 text-white rounded-lg flex-shrink-0"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                          <span className="bg-purple-900 text-purple-300 border border-purple-500 px-1.5 py-0.5 rounded-full text-[9px]">
                            Simulado
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {currentView === 'professor-turmas' && (
          <div>
            {/* Navegação entre views */}
            {viewDesempenho === 'lista' ? (
              <>
                {/* ⚠️ Aviso importante - responsivo */}
                <div className="mb-4 md:mb-6 p-3 md:p-4 rounded-xl flex items-start gap-2 md:gap-3" style={{ backgroundColor: '#1e3a5f', border: '2px solid #f7941d' }}>
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-4 h-4 md:w-6 md:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-orange-300 mb-1 text-sm md:text-base">⚠️ Importante</h4>
                    <p className="text-xs md:text-sm text-gray-300">
                      Para correção, crie a turma e cadastre os <strong className="text-white">alunos</strong> usando a seção <strong className="text-orange-300">"Gestão de Turmas"</strong> abaixo.
                    </p>
                  </div>
                </div>

                {/* CARDS DE ACESSO - v496: Ordem diferente desktop/mobile */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-6">
                  {/* Card 1 - Correção de Simulados - Aparece primeiro no mobile, terceiro no desktop */}
                  <div 
                    onClick={() => {
                      // v496: No desktop, não abre a correção (só funciona no mobile)
                      if (window.innerWidth >= 768) {
                        return; // Não faz nada no desktop
                      }
                      setViewDesempenho('correcao');
                    }}
                    className="rounded-xl p-4 md:p-6 hover:shadow-lg transition cursor-pointer border-2 border-orange-500 bg-white md:order-3"
                  >
                    <div className="flex items-center md:items-start gap-3 md:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <Camera className="w-6 h-6 md:w-8 md:h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base md:text-xl font-bold text-gray-800 mb-1 md:mb-2">Correção de Simulados</h3>
                        {/* v496: Texto diferente no mobile e desktop */}
                        <p className="text-xs text-gray-600 mb-2 md:hidden">Clique aqui para corrigir os Simulados SAEB!</p>
                        <p className="text-xs text-gray-500 mb-4 hidden md:block">Essa funcionalidade só está ativa pelo Smartphone! Acesse facilitaprof.com.br pelo seu Smartphone para corrigir Simulados SAEB.</p>
                        <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-orange-500 text-white rounded-full font-semibold">📷 Correção</span>
                      </div>
                      <Eye className="w-5 h-5 md:w-6 md:h-6 text-orange-500 hidden md:block" />
                    </div>
                  </div>

                  {/* Card 2 - Planilha de Resultados (renomeado) - Aparece segundo no mobile, primeiro no desktop */}
                  <div 
                    onClick={() => {
                      // v689d: Pré-definir série da primeira turma real
                      if (!configSimulado.serie && turmas.length > 0) {
                        const primeiraTurma = turmas[0];
                        setConfigSimulado(prev => ({...prev, serie: primeiraTurma.serie, turma: primeiraTurma.turma || '', turmaId: primeiraTurma.id, turmaNome: primeiraTurma.nome || `${primeiraTurma.serie} ${primeiraTurma.turma || ''}`}));
                      }
                      setViewDesempenho('planilha');
                    }}
                    className="rounded-xl p-4 md:p-6 hover:shadow-lg transition cursor-pointer border-2 border-green-500 bg-white md:order-1"
                  >
                    <div className="flex items-center md:items-start gap-3 md:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <FileText className="w-6 h-6 md:w-8 md:h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base md:text-xl font-bold text-gray-800 mb-1 md:mb-2">Planilha de Resultados</h3>
                        <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden md:block">Clique aqui para visualizar os resultados em tabela(s) detalhada(s)!</p>
                        <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-green-600 text-white rounded-full font-semibold">📊 Análise</span>
                      </div>
                      <Eye className="w-5 h-5 md:w-6 md:h-6 text-green-600 hidden md:block" />
                    </div>
                  </div>

                  {/* Card 3 - Gráficos e Padrões de Desempenho (renomeado) - Aparece terceiro no mobile, segundo no desktop */}
                  <div 
                    onClick={() => {
                      // v689d: Pré-definir série da primeira turma real
                      if (!configSimulado.serie && turmas.length > 0) {
                        const primeiraTurma = turmas[0];
                        setConfigSimulado(prev => ({...prev, serie: primeiraTurma.serie, turma: primeiraTurma.turma || '', turmaId: primeiraTurma.id, turmaNome: primeiraTurma.nome || `${primeiraTurma.serie} ${primeiraTurma.turma || ''}`}));
                      }
                      setViewDesempenho('grafico');
                    }}
                    className="rounded-xl p-4 md:p-6 hover:shadow-lg transition cursor-pointer border-2 border-blue-500 bg-white md:order-2"
                  >
                    <div className="flex items-center md:items-start gap-3 md:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base md:text-lg font-bold text-gray-800 mb-1 md:mb-2">Gráficos e Padrões de Desempenho</h3>
                        <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden md:block">Clique aqui para ver gráfico(s) de desempenho por Habilidades e Descritores!</p>
                        <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-blue-600 text-white rounded-full font-semibold">📈 Visualização</span>
                      </div>
                      <Eye className="w-5 h-5 md:w-6 md:h-6 text-blue-600 hidden md:block" />
                    </div>
                  </div>
                </div>

                {/* GESTÃO DE TURMAS - responsivo */}
                <div className="p-4 md:p-6 rounded-xl mb-4 md:mb-6" style={{ backgroundColor: '#1e3a5f' }}>
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                    <div>
                      <h3 className="font-bold text-base md:text-lg text-white">Gestão de Turmas</h3>
                      <p className="text-xs md:text-sm text-gray-300 mt-1">Gerencie suas turmas e alunos</p>
                    </div>
                    <button 
                      onClick={() => { setNovaTurma({ nome: '', serie: '', turma: '', turno: '' }); setShowModalTurma(true); }}
                      className="text-white px-4 md:px-6 py-2 md:py-3 rounded-lg flex items-center gap-2 hover:opacity-90 text-sm md:text-base w-full md:w-auto justify-center"
                      style={{backgroundColor: '#f7941d'}}
                    >
                      <Plus className="w-4 h-4 md:w-5 md:h-5" />
                      Nova Turma
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:gap-6">
                  {turmas.map(t => (
                    <div key={t.id} className="rounded-xl p-4 md:p-6 border-2 border-gray-600 hover:border-blue-400 transition" style={{ backgroundColor: '#1e3a5f' }}>
                      <div className="flex justify-between items-start cursor-pointer" onClick={() => setTurmasExpandidas({...turmasExpandidas, [t.id]: !turmasExpandidas[t.id]})}>
                        <div>
                          <div className="flex items-center gap-2 md:gap-3 mb-2">
                            <h4 className="font-bold text-base md:text-lg text-white">{t.nome}</h4>
                            <svg className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 transition-transform ${turmasExpandidas[t.id] ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                          <div className="flex gap-1 md:gap-2 flex-wrap">
                            <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-blue-900 text-blue-300 rounded-full border border-blue-500">{t.serie}</span>
                            <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 bg-purple-900 text-purple-300 rounded-full border border-purple-500">{t.turno}</span>
                          </div>
                          {/* 🔧 v412: Aviso para cadastrar alunos */}
                          {(!Array.isArray(t.alunos) || t.alunos.length === 0) && (
                            <p className="text-[10px] md:text-xs text-orange-400 mt-2 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Clique aqui para cadastrar alunos!
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-2xl md:text-3xl font-bold text-blue-400">{Array.isArray(t.alunos) ? t.alunos.length : 0}</div>
                          <div className="text-[10px] md:text-xs text-gray-400">alunos</div>
                        </div>
                      </div>
                      {turmasExpandidas[t.id] && (
                        <div className="border-t border-gray-600 pt-3 md:pt-4 mt-3 md:mt-4">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-xs md:text-sm font-semibold text-gray-300">Alunos</span>
                            <button onClick={(e) => { e.stopPropagation(); setTurmaEditando(t); setNovoAlunoNome(''); setShowModalAluno(true); }} className="text-blue-400 text-xs md:text-sm flex items-center gap-1">
                              <Plus className="w-3 h-3 md:w-4 md:h-4" /> Adicionar
                            </button>
                          </div>
                          {(!Array.isArray(t.alunos) || t.alunos.length === 0) ? (
                            <div className="text-center py-3 md:py-4 text-gray-400">
                              <Users className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-xs md:text-sm">Nenhum aluno</p>
                            </div>
                          ) : (
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {t.alunos.map((aluno, i) => (
                                <div key={i} className="flex justify-between items-center p-2 bg-gray-800 rounded hover:bg-gray-700">
                                  <span className="text-xs md:text-sm text-gray-200">{i+1}. {getNomeAluno(aluno)}</span>
                                  <div className="flex gap-1 md:gap-2">
                                    <button 
                                      type="button"
                                      onClick={async (e) => { 
                                        e.preventDefault();
                                        e.stopPropagation(); 
                                        const novoNome = prompt('Editar nome do aluno:', getNomeAluno(aluno)); 
                                        if(novoNome && novoNome.trim()) {
                                          try {
                                            if (firebaseReady && db) {
                                              const turmaAtual = turmasFirebase.find(turma => turma.id === t.id);
                                              const alunosAtuais = Array.isArray(turmaAtual?.alunos) ? turmaAtual.alunos : [];
                                              // v538: Manter formato original do aluno (objeto ou string)
                                              const novosAlunos = alunosAtuais.map((a, idx) => {
                                                if (idx !== i) return a;
                                                // Se o aluno original é objeto, atualiza o nome no objeto
                                                if (typeof a === 'object' && a !== null) {
                                                  return { ...a, nome: novoNome.trim() };
                                                }
                                                // Se é string, mantém como string
                                                return novoNome.trim();
                                              });
                                              await db.collection('turmas').doc(t.id).update({ alunos: novosAlunos });
                                              setTurmasFirebase(prev => prev.map(turma => turma.id === t.id ? {...turma, alunos: novosAlunos} : turma));
                                            }
                                          } catch (error) {
                                            console.error('Erro ao editar aluno:', error);
                                            alert('❌ Erro: ' + error.message);
                                          }
                                        }
                                      }} 
                                      className="text-blue-400 p-1.5 md:p-2 hover:bg-blue-900 rounded-lg transition"
                                      title="Editar aluno"
                                    >
                                      <Edit className="w-3 h-3 md:w-4 md:h-4" />
                                    </button>
                                    <button 
                                      type="button"
                                      onClick={async (e) => { 
                                        e.preventDefault();
                                        e.stopPropagation(); 
                                        if(window.confirm(`Remover ${getNomeAluno(aluno)}?`)) {
                                          try {
                                            if (firebaseReady && db) {
                                              const turmaAtual = turmasFirebase.find(turma => turma.id === t.id);
                                              const alunosAtuais = Array.isArray(turmaAtual?.alunos) ? turmaAtual.alunos : [];
                                              const novosAlunos = alunosAtuais.filter((_, idx) => idx !== i);
                                              await db.collection('turmas').doc(t.id).update({ alunos: novosAlunos });
                                              setTurmasFirebase(prev => prev.map(turma => turma.id === t.id ? {...turma, alunos: novosAlunos} : turma));
                                            }
                                          } catch (error) {
                                            console.error('Erro ao remover aluno:', error);
                                            alert('❌ Erro: ' + error.message);
                                          }
                                        }
                                      }} 
                                      className="text-red-400 p-1.5 md:p-2 hover:bg-red-900 rounded-lg transition"
                                      title="Excluir aluno"
                                    >
                                      <X className="w-3 h-3 md:w-4 md:h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t flex gap-2">
                            <button onClick={(e) => { e.stopPropagation(); setNovaTurma({nome: t.nome, serie: t.serie, turma: t.turma || '', turno: t.turno}); setTurmaEditando(t); setShowModalTurma(true); }} className="flex-1 py-2 border border-blue-600 text-blue-600 rounded-lg text-xs md:text-sm flex items-center justify-center gap-1"><Edit className="w-3 h-3 md:w-4 md:h-4" /> Editar</button>
                            <button onClick={async (e) => { 
                              e.stopPropagation(); 
                              if(window.confirm(`Excluir ${t.nome}?`)) {
                                try {
                                  if (firebaseReady && db) {
                                    await db.collection('turmas').doc(t.id).delete();
                                    setTurmasFirebase(prev => prev.filter(turma => turma.id !== t.id));
                                    alert('✅ Turma excluída com sucesso!');
                                  }
                                } catch (error) {
                                  console.error('Erro ao excluir turma:', error);
                                  alert('❌ Erro ao excluir: ' + error.message);
                                }
                              }
                            }} className="flex-1 py-2 border border-red-600 text-red-600 rounded-lg text-sm flex items-center justify-center gap-1"><Trash2 className="w-4 h-4" /> Excluir Turma</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : viewDesempenho === 'correcao' ? (
              /* 🆕 v475: TELA DE CORREÇÃO DE SIMULADOS - PROFESSOR */
              <div>
                {/* Header com botão voltar */}
                <div className="flex items-center gap-3 mb-4">
                  <button 
                    onClick={() => setViewDesempenho('lista')}
                    className="p-2 rounded-lg bg-gray-600 hover:bg-gray-500 transition"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-white">Correção de Simulados</h2>
                    <p className="text-xs text-gray-300 font-semibold">Selecione a turma e o simulado para corrigir</p>
                  </div>
                </div>

                {/* Filtros - v475: Turma + Simulado */}
                <div className="bg-white rounded-xl p-4 mb-4 border-2 border-orange-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Filtro Turma */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">👥 Turma</label>
                      <select
                        value={filtroCorrecaoTurma}
                        onChange={(e) => {
                          setFiltroCorrecaoTurma(e.target.value);
                          setFiltroCorrecaoSimulado('');
                          const turmaSel = turmas.find(t => String(t.id) === e.target.value);
                          setFiltroCorrecaoSerie(turmaSel?.serie || '');
                        }}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                      >
                        <option value="">Selecione a turma...</option>
                        {turmas.map(t => (
                          <option key={t.id} value={t.id}>
                            {formatarNomeTurma(t)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Filtro Simulado */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">📋 Simulado</label>
                      <select
                        value={filtroCorrecaoSimulado}
                        onChange={(e) => setFiltroCorrecaoSimulado(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                        disabled={!filtroCorrecaoTurma}
                      >
                        <option value="">Selecione o simulado...</option>
                        {simuladosFirebase
                          .filter(s => !filtroCorrecaoSerie || (s.serie || '').trim().toLowerCase() === (filtroCorrecaoSerie || '').trim().toLowerCase()).sort((a, b) => { const dataA = new Date(a.dataGeracao || a.dataCriacao || a.dataAplicacao || 0).getTime(); const dataB = new Date(b.dataGeracao || b.dataCriacao || b.dataAplicacao || 0).getTime(); return dataB - dataA; })
                          .map(s => (
                            <option key={s.id} value={s.codigo}>
                              #{formatarCodigoSimulado(s.codigo)} - {s.componente} • {s.serie}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Lista de Alunos para Correção */}
                {filtroCorrecaoSimulado && filtroCorrecaoTurma ? (
                  <div className="bg-white rounded-xl border-2 border-orange-200 overflow-hidden">
                    <div className="bg-orange-500 text-white px-4 py-3">
                      <h3 className="font-bold">Alunos - Simulado #{formatarCodigoSimulado(filtroCorrecaoSimulado)}</h3>
                      <p className="text-xs text-orange-100">Clique no nome do aluno para corrigir o cartão-resposta</p>
                    </div>
                    
                    <div className="divide-y">
                      {(() => {
                        const simuladoSelecionado = simuladosFirebase.find(s => String(s.codigo) === String(filtroCorrecaoSimulado));
                        const turmaDoSimulado = turmas.find(t => String(t.id) === String(filtroCorrecaoTurma));
                        const alunosDaTurma = turmaDoSimulado?.alunos || [];
                        
                        if (alunosDaTurma.length === 0) {
                          return (
                            <div className="p-8 text-center text-gray-500">
                              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>Nenhum aluno cadastrado nesta turma</p>
                              <p className="text-xs mt-1">Cadastre alunos em "Gestão de Turmas"</p>
                            </div>
                          );
                        }
                        
                        return alunosDaTurma.map((aluno, idx) => {
                          const respostasAluno = simuladoSelecionado?.respostas?.[aluno] || simuladoSelecionado?.respostas?.[idx];
                          const temRespostas = respostasAluno && Object.keys(respostasAluno).length > 0;
                          
                          return (
                            <div 
                              key={idx}
                              onClick={() => abrirModalOCR(idx, getNomeAluno(aluno), 'prof', simuladoSelecionado?.serie || turmaDoSimulado?.serie)}
                              className={`flex items-center justify-between px-4 py-3 hover:bg-orange-50 cursor-pointer transition ${temRespostas ? 'bg-green-50' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${temRespostas ? 'bg-green-500' : 'bg-gray-400'}`}>
                                  {idx + 1}
                                </div>
                                <span className="font-medium text-gray-800">{getNomeAluno(aluno)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {temRespostas ? (
                                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                                    ✓ Corrigido
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">
                                    Pendente
                                  </span>
                                )}
                                <Camera className="w-5 h-5 text-orange-500" />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-8 text-center border-2 border-dashed border-gray-300">
                    <Camera className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500 font-medium">Selecione a série e o simulado</p>
                    <p className="text-xs text-gray-400 mt-1">para visualizar os alunos e iniciar a correção</p>
                  </div>
                )}
              </div>
            ) : viewDesempenho === 'planilha' ? (
              /* VIEW PLANILHA */
              <div>
                {/* Aviso para rotacionar celular - só aparece em portrait mobile */}
                <div className="md:hidden fixed inset-0 bg-gray-900 bg-opacity-95 z-50 flex items-center justify-center portrait:flex landscape:hidden">
                  <div className="text-center p-6">
                    <div className="w-20 h-20 mx-auto mb-4 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
                      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Gire o Celular</h3>
                    <p className="text-gray-300 text-sm mb-4">Para melhor visualização da planilha, coloque o celular na horizontal</p>
                    <button 
                      onClick={() => setViewDesempenho('lista')}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm"
                    >
                      ← Voltar
                    </button>
                  </div>
                </div>

                {/* Botão Voltar - compacto no mobile */}
                <button onClick={() => setViewDesempenho('lista')} className="mb-1 md:mb-4 flex items-center gap-1 md:gap-2 text-orange-400 hover:text-orange-300 font-semibold text-xs md:text-base">
                  <ArrowLeft className="w-3 h-3 md:w-5 md:h-5" /> Voltar
                </button>
                
                {/* Container da planilha - tela cheia no mobile */}
                <div className="rounded-lg md:rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
                  {/* Cabeçalho - escondido no mobile landscape para maximizar espaço */}
                  <div className="hidden md:block p-3 md:p-6 border-b" style={{backgroundColor: '#1e3a5f'}}>
                    <div className="text-white">
                      <div className="flex justify-between items-start mb-2">
                        <h2 className="text-lg md:text-2xl font-bold">{cabecalhoConfig.nomeEscola}</h2>
                        <button onClick={() => setShowDropdownSimulado(true)} className="px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-bold flex items-center gap-1 hover:opacity-90 transition cursor-pointer" style={{backgroundColor: '#f7941d'}}>Simulado #{formatarCodigoSimulado(simuladoSelecionado?.codigo || simuladoSelecionado?.numero || numeroSimulado)} <ChevronDown className="w-3 h-3" /></button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 text-xs md:text-sm">
                        <div><span className="text-blue-200">Professor:</span> <span className="font-semibold ml-1">{cabecalhoConfig.professor}</span></div>
                        <div className="relative">
                          <span className="text-blue-200">Série:</span>
                          <button 
                            onClick={() => setShowDropdownTurma(!showDropdownTurma)}
                            className="font-semibold ml-1 hover:text-orange-300 cursor-pointer inline-flex items-center gap-1"
                          >
                            {configSimulado.turmaNome || `${configSimulado.serie} ${configSimulado.turma}`}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          {showDropdownTurma && (
                            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl z-50 min-w-[180px]">
                              {/* v497: Filtrar turmas compatíveis com a série do simulado selecionado */}
                              {turmas
                                .filter(t => {
                                  // Extrair série base do simulado (ex: "5º ano" de "5º ano A")
                                  const serieSimulado = simuladoSelecionado?.serie || configSimulado.serie || '';
                                  // Extrair apenas o número e "ano" (ex: "5º ano" de "5º ano A" ou "5º Ano B")
                                  const serieBaseSimulado = serieSimulado.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || serieSimulado.toLowerCase();
                                  const serieBaseTurma = t.serie?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || t.serie?.toLowerCase() || '';
                                  // Comparar séries base
                                  return serieBaseTurma === serieBaseSimulado || t.serie?.toLowerCase().includes(serieBaseSimulado);
                                })
                                .map(t => (
                                <button
                                  key={t.id}
                                  onClick={() => {
                                    // v498: Salvar ID da turma para identificação única
                                    setConfigSimulado(prev => ({...prev, serie: t.serie, turma: t.turma || '', turmaId: t.id, turmaNome: t.nome || `${t.serie} ${t.turma || ''}`}));
                                    setShowDropdownTurma(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700 ${configSimulado.turmaId === t.id ? 'bg-blue-100 font-bold' : ''}`}
                                >
                                  {t.nome || `${t.serie} ${t.turma || ''}`}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-blue-200">Data:</span>
                          <input 
                            type="date" 
                            value={configSimulado.dataAplicacao} 
                            onChange={(e) => setConfigSimulado(prev => ({...prev, dataAplicacao: e.target.value}))}
                            className="bg-transparent border-b border-blue-300 text-white font-semibold text-xs md:text-sm px-1 focus:outline-none focus:border-orange-400 max-w-[110px] md:max-w-none"
                          />
                        </div>
                        <div><span className="text-blue-200">Matriculados:</span> <span className="font-semibold ml-1">{turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)))?.alunos?.length || 0}</span></div>
                        <div><span className="text-blue-200">Presentes:</span> <span className="font-semibold ml-1">{(() => {
                          const turmaAtual = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
                          if (!turmaAtual?.alunos) return 0;
                          return turmaAtual.alunos.filter((_, idx) => {
                            for (let b = 0; b < 2; b++) {
                              const totalNoBloco = questoesPorBlocoSAEB(configSimulado.serie);
                              for (let q = 0; q < totalNoBloco; q++) {
                                if (respostasEditaveis[`prof_${idx}_${b}_${q}`]) return true;
                              }
                            }
                            return false;
                          }).length;
                        })()}</span></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mini cabeçalho mobile - v519: Adicionar seletor de turma */}
                  <div className="md:hidden p-1.5 border-b flex items-center justify-between gap-2" style={{backgroundColor: '#1e3a5f'}}>
                    <button 
                      onClick={() => setShowDropdownTurma(!showDropdownTurma)}
                      className="text-[10px] text-gray-300 flex items-center gap-1 px-2 py-1 rounded bg-white/10"
                    >
                      {configSimulado.turmaNome || `${configSimulado.serie} ${configSimulado.turma}`}
                      <ChevronDown className="w-2 h-2" />
                    </button>
                    <button onClick={() => setShowDropdownSimulado(true)} className="px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1" style={{backgroundColor: '#f7941d', color: 'white'}}>
                      #{simuladoSelecionado?.codigo || simuladoSelecionado?.numero || numeroSimulado} <ChevronDown className="w-2 h-2" />
                    </button>
                    {/* Dropdown de turma mobile - v519 */}
                    {showDropdownTurma && (
                      <div className="absolute top-12 left-2 bg-white rounded-lg shadow-xl z-50 min-w-[180px] max-h-60 overflow-y-auto">
                        {turmas
                          .filter(t => {
                            const serieSimulado = simuladoSelecionado?.serie || configSimulado.serie || '';
                            const serieBaseSimulado = serieSimulado.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || serieSimulado.toLowerCase();
                            const serieBaseTurma = t.serie?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || t.serie?.toLowerCase() || '';
                            return serieBaseTurma === serieBaseSimulado || t.serie?.toLowerCase().includes(serieBaseSimulado);
                          })
                          .map(t => (
                          <button
                            key={t.id}
                            onClick={() => {
                              setConfigSimulado(prev => ({...prev, serie: t.serie, turma: t.turma || '', turmaId: t.id, turmaNome: t.nome || `${t.serie} ${t.turma || ''}`}));
                              setShowDropdownTurma(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700 ${configSimulado.turmaId === t.id ? 'bg-blue-100 font-bold' : ''}`}
                          >
                            {t.nome || `${t.serie} ${t.turma || ''}`}
                          </button>
                        ))}
                        {turmas.filter(t => {
                          const serieSimulado = simuladoSelecionado?.serie || configSimulado.serie || '';
                          const serieBaseSimulado = serieSimulado.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || serieSimulado.toLowerCase();
                          const serieBaseTurma = t.serie?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || t.serie?.toLowerCase() || '';
                          return serieBaseTurma === serieBaseSimulado || t.serie?.toLowerCase().includes(serieBaseSimulado);
                        }).length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">Nenhuma turma compatível</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse" style={{tableLayout: "fixed"}}>
                      <thead>
                        {/* Linha 1: Cabeçalho dos Blocos */}
                        <tr>
                          <th rowSpan={2} className="p-2 text-center font-bold border-2 border-gray-400 sticky left-0 bg-gray-200 z-10 text-gray-700" style={{width: '195px', minWidth: '195px', maxWidth: '195px', verticalAlign: 'middle'}}>
                            <span style={{fontSize: '11px'}}>QUESTÕES</span>
                          </th>
                          <th colSpan={questoesPorBlocoSAEB(configSimulado.serie)} className={`p-1 text-center font-bold border-2 text-xs ${componenteSimulado === "Língua Portuguesa" ? "border-purple-500 bg-purple-300 text-purple-900" : "border-blue-500 bg-blue-300 text-blue-900"}`}>BLOCO 1 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</th>
                          <th colSpan={questoesPorBlocoSAEB(configSimulado.serie)} className={`p-1 text-center font-bold border-2 text-xs ${componenteSimulado === "Língua Portuguesa" ? "border-purple-500 bg-purple-300 text-purple-900" : "border-blue-500 bg-blue-300 text-blue-900"}`}>BLOCO 2 - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</th>
                          <th rowSpan={5} className="p-1 text-center font-bold border-2 border-green-500 bg-green-300 text-green-900 align-middle" style={{minWidth: '45px', maxWidth: '45px'}}>
                            <div className="text-xs font-bold flex items-center justify-center" style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', height: '100px', margin: '0 auto'}}>TOTAL DE ACERTOS</div>
                          </th>
                        </tr>
                        {/* Linha 2: Números das Questões (vertical, de cima para baixo) */}
                        <tr>
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`q1-${i}`} className="text-center font-bold text-xs bg-purple-200 text-purple-900 border border-purple-300" style={{minWidth: '20px', maxWidth: '20px', height: '90px', fontSize: '9px', verticalAlign: 'middle'}}>
                              <div style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', margin: '0 auto'}}>QUESTÃO {String(i + 1).padStart(2, '0')}</div>
                            </td>
                          ))}
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`q2-${i}`} className="text-center font-bold text-xs bg-blue-200 text-blue-900 border border-blue-300" style={{minWidth: '20px', maxWidth: '20px', height: '90px', fontSize: '9px', verticalAlign: 'middle'}}>
                              <div style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', margin: '0 auto'}}>QUESTÃO {String(i + questoesPorBlocoSAEB(configSimulado.serie) + 1).padStart(2, '0')}</div>
                            </td>
                          ))}
                        </tr>
                        {/* Linha 3: HABILIDADES/DESCRITORES (dados reais das questões selecionadas) */}
                        <tr className="bg-cyan-100">
                          <td className="p-2 font-bold border-2 border-cyan-400 sticky left-0 bg-cyan-200 z-10 text-cyan-900 text-center text-xs">HABILIDADES/<br/>DESCRITORES</td>
                          {(() => {
                            // 🔧 v475: Usar obterGabaritosSimulado para pegar descritores salvos
                            const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 1);
                            return (
                              <>
                                {gabaritosB1.map((g, i) => (
                                  <td key={`h1-${i}`} className="p-0 text-center font-mono bg-cyan-100 text-cyan-800 border border-cyan-300" style={{maxWidth: "22px", fontSize: "8px"}}>{g.habilidade || '-'}</td>
                                ))}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB1.length))].map((_, i) => (
                                  <td key={`h1e-${i}`} className="p-0 text-center bg-cyan-50 border border-cyan-200 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                          {(() => {
                            // 🔧 v475: Usar obterGabaritosSimulado para pegar descritores salvos
                            const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 2);
                            return (
                              <>
                                {gabaritosB2.map((g, i) => (
                                  <td key={`h2-${i}`} className="p-0 text-center font-mono bg-cyan-100 text-cyan-800 border border-cyan-300" style={{maxWidth: "22px", fontSize: "8px"}}>{g.habilidade || '-'}</td>
                                ))}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB2.length))].map((_, i) => (
                                  <td key={`h2e-${i}`} className="p-0 text-center bg-cyan-50 border border-cyan-200 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                        </tr>
                        {/* Linha 4: GABARITO (dados reais das questões selecionadas ou do simulado salvo) */}
                        <tr className="bg-yellow-200">
                          <td className="p-2 font-bold border-2 border-yellow-400 sticky left-0 bg-yellow-300 z-10 text-yellow-900 text-center text-sm">GABARITO</td>
                          {(() => {
                            // 🔧 v404: Usar função helper que prioriza gabaritos salvos no Firebase
                            const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 1);
                            return (
                              <>
                                {gabaritosB1.map((g, i) => {
                                  const numeroQuestao = String(i + 1).padStart(2, '0');
                                  return (
                                    <td key={`g1-${i}`} className={`p-0 text-center border border-yellow-300 relative cursor-pointer hover:brightness-95 ${g.foiEditado ? 'bg-orange-100' : 'bg-yellow-200'}`} style={{maxWidth: "22px", height: "26px"}}>
                                      <div className={`absolute inset-0 flex items-center justify-center font-bold ${g.foiEditado ? 'text-orange-900' : 'text-yellow-900'}`} style={{fontSize: "15px", pointerEvents: "none"}}>
                                        {g.gabarito || '-'}
                                      </div>
                                      <div className="absolute top-0.5 right-0.5 text-gray-500 pointer-events-none" style={{fontSize: "9px", lineHeight: "9px", opacity: 0.5}}>▼</div>
                                      <select
                                        value={g.gabarito || '-'}
                                        onChange={(e) => atualizarGabaritoSimulado(simuladoSelecionado?.id, numeroQuestao, e.target.value)}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        style={{appearance: "none", textAlign: "center"}}
                                        title={g.foiEditado ? `Editado (original: ${g.gabaritoOriginal})` : 'Clique para editar'}
                                      >
                                        <option value="-">-</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                      </select>
                                    </td>
                                  );
                                })}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB1.length))].map((_, i) => (
                                  <td key={`g1e-${i}`} className="p-0 text-center bg-yellow-100 border border-yellow-300 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                          {(() => {
                            // 🔧 v404: Usar função helper que prioriza gabaritos salvos no Firebase
                            const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 2);
                            return (
                              <>
                                {gabaritosB2.map((g, i) => {
                                  const numeroQuestao = String(i + questoesPorBlocoSAEB(configSimulado.serie) + 1).padStart(2, '0');
                                  return (
                                    <td key={`g2-${i}`} className={`p-0 text-center border border-yellow-300 relative cursor-pointer hover:brightness-95 ${g.foiEditado ? 'bg-orange-100' : 'bg-yellow-200'}`} style={{maxWidth: "22px", height: "26px"}}>
                                      <div className={`absolute inset-0 flex items-center justify-center font-bold ${g.foiEditado ? 'text-orange-900' : 'text-yellow-900'}`} style={{fontSize: "15px", pointerEvents: "none"}}>
                                        {g.gabarito || '-'}
                                      </div>
                                      <div className="absolute top-0.5 right-0.5 text-gray-500 pointer-events-none" style={{fontSize: "9px", lineHeight: "9px", opacity: 0.5}}>▼</div>
                                      <select
                                        value={g.gabarito || '-'}
                                        onChange={(e) => atualizarGabaritoSimulado(simuladoSelecionado?.id, numeroQuestao, e.target.value)}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        style={{appearance: "none", textAlign: "center"}}
                                        title={g.foiEditado ? `✏️ Editado (original: ${g.gabaritoOriginal})` : ''}
                                      >
                                        <option value="-">-</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                      </select>
                                    </td>
                                  );
                                })}
                                {[...Array(Math.max(0, questoesPorBlocoSAEB(configSimulado.serie) - gabaritosB2.length))].map((_, i) => (
                                  <td key={`g2e-${i}`} className="p-0 text-center bg-yellow-100 border border-yellow-300 text-gray-400" style={{maxWidth: "22px"}}>-</td>
                                ))}
                              </>
                            );
                          })()}
                        </tr>
                        {/* Linha 5: ALUNOS - PROFESSOR */}
                        <tr className="bg-gray-100">
                          <td className="p-2 font-bold border-2 border-gray-400 sticky left-0 bg-gray-300 z-10 text-gray-800 text-center text-sm">ALUNOS</td>
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`a1p-${i}`} className="p-1 bg-purple-50 border border-purple-100"></td>
                          ))}
                          {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => (
                            <td key={`a2p-${i}`} className="p-1 bg-blue-50 border border-blue-100"></td>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Alunos da turma selecionada - PROFESSOR - EDITÁVEL */}
                        {turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)))?.alunos.map((aluno, idx) => {
                          const opcoes = ['A', 'B', 'C', 'D', ''];
                          
                          // 🔧 v404: Usar função helper que prioriza gabaritos salvos no Firebase
                          const gabaritosExpandidosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 1);
                          const gabaritosExpandidosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 2);
                          
                          // Função para obter resposta editável (sem gerar aleatório)
                          const getRespostaEditavel = (alunoIdx, bloco, questaoIdx) => {
                            const chave = `prof_${alunoIdx}_${bloco}_${questaoIdx}`;
                            if (respostasEditaveis[chave] !== undefined) {
                              return respostasEditaveis[chave];
                            }
                            // Retorna vazio se não houver resposta cadastrada
                            return '';
                          };
                          
                          // Função para atualizar resposta
                          const atualizarResposta = (alunoIdx, bloco, questaoIdx, valor) => {
                            const chave = `prof_${alunoIdx}_${bloco}_${questaoIdx}`;
                            const valorUpper = valor.toUpperCase();
                            if (valorUpper === '' || ['A', 'B', 'C', 'D'].includes(valorUpper)) {
                              setRespostasEditaveis(prev => ({...prev, [chave]: valorUpper}));
                            }
                          };
                          
                          // Calcular acertos usando gabaritos expandidos
                          // 🔧 v406: Só conta acerto se o gabarito E a resposta NÃO estiverem vazios
                          let acertosTotal = 0;
                          const respostasB1 = gabaritosExpandidosB1.map((g, i) => {
                            const r = getRespostaEditavel(idx, 0, i);
                            if (g.gabarito && r === g.gabarito) acertosTotal++;
                            return r;
                          });
                          const respostasB2 = gabaritosExpandidosB2.map((g, i) => {
                            const r = getRespostaEditavel(idx, 1, i);
                            if (g.gabarito && r === g.gabarito) acertosTotal++;
                            return r;
                          });
                          
                          return (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td style={{width: "195px", maxWidth: "195px"}} className={`p-1 border-r-2 border-gray-400 sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <span className="text-xs font-medium truncate block">{idx + 1}. {getNomeAluno(aluno)}</span>
                              </td>
                              {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                const temQ = i < gabaritosExpandidosB1.length;
                                const r = respostasB1[i] || '';
                                const gab = gabaritosExpandidosB1[i]?.gabarito;
                                const ok = temQ && r === gab;
                                return (
                                  <td key={`r1p-${i}`} style={{width: "22px", maxWidth: "22px"}} className={`p-0 text-center border-r border-purple-100 ${!temQ ? 'bg-gray-100' : ''}`}>
                                    {temQ ? (
                                      <input
                                        type="text"
                                        value={r}
                                        onChange={(e) => atualizarResposta(idx, 0, i, e.target.value)}
                                        maxLength={1}
                                        className={`w-full h-full p-1 text-center font-mono text-xs border-0 focus:ring-2 focus:ring-blue-400 focus:outline-none ${ok ? 'bg-green-200 text-green-800 font-bold' : r === '' ? 'bg-gray-50 text-gray-400' : 'bg-red-100 text-red-600'}`}
                                        style={{width: '20px', maxWidth: '20px', padding: '2px'}}
                                      />
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </td>
                                );
                              })}
                              {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                const temQ = i < gabaritosExpandidosB2.length;
                                const r = respostasB2[i] || '';
                                const gab = gabaritosExpandidosB2[i]?.gabarito;
                                const ok = temQ && r === gab;
                                return (
                                  <td key={`r2p-${i}`} style={{width: "22px", maxWidth: "22px"}} className={`p-0 text-center border-r border-blue-100 ${!temQ ? 'bg-gray-100' : ''}`}>
                                    {temQ ? (
                                      <input
                                        type="text"
                                        value={r}
                                        onChange={(e) => atualizarResposta(idx, 1, i, e.target.value)}
                                        maxLength={1}
                                        className={`w-full h-full p-1 text-center font-mono text-xs border-0 focus:ring-2 focus:ring-blue-400 focus:outline-none ${ok ? 'bg-green-200 text-green-800 font-bold' : r === '' ? 'bg-gray-50 text-gray-400' : 'bg-red-100 text-red-600'}`}
                                        style={{width: '20px', maxWidth: '20px', padding: '2px'}}
                                      />
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="p-2 text-center font-bold bg-green-200 text-green-800 border-l-2 border-green-400">{acertosTotal}</td>
                            </tr>
                          );
                        })}
                        {/* Linha % ACERTOS - v494: Calcular porcentagem real por questão */}
                        <tr className="border-t-2 border-orange-500">
                          <td className="p-2 border-r-2 border-gray-400 sticky left-0 bg-orange-300 z-10 text-orange-900 text-center font-bold text-sm">% ACERTOS</td>
                          {(() => {
                            // Obter gabaritos do simulado selecionado
                            const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 1);
                            const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 2);
                            const turmaAtualPct = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
                            
                            // Calcular % acertos para Bloco 1
                            // v591: Contar respostas em branco e múltiplas marcações como ERRO
                            const pctB1 = gabaritosB1.map((g, qIdx) => {
                              if (!g.gabarito) return { temQ: false, pct: 0 };
                              let acertos = 0;
                              const totalAlunos = turmaAtualPct?.alunos?.length || 0;
                              
                              turmaAtualPct?.alunos?.forEach((_, alunoIdx) => {
                                const chave = `prof_${alunoIdx}_0_${qIdx}`;
                                const resp = respostasEditaveis[chave];
                                
                                // Só conta como acerto se marcou corretamente
                                if (resp === g.gabarito) {
                                  acertos++;
                                }
                              });
                              
                              return { temQ: true, pct: totalAlunos > 0 ? Math.round((acertos / totalAlunos) * 100) : 0 };
                            });
                            
                            // Calcular % acertos para Bloco 2
                            // v591: Contar respostas em branco e múltiplas marcações como ERRO
                            const pctB2 = gabaritosB2.map((g, qIdx) => {
                              if (!g.gabarito) return { temQ: false, pct: 0 };
                              let acertos = 0;
                              const totalAlunos = turmaAtualPct?.alunos?.length || 0;
                              
                              turmaAtualPct?.alunos?.forEach((_, alunoIdx) => {
                                const chave = `prof_${alunoIdx}_1_${qIdx}`;
                                const resp = respostasEditaveis[chave];
                                
                                // Só conta como acerto se marcou corretamente
                                if (resp === g.gabarito) {
                                  acertos++;
                                }
                              });
                              
                              return { temQ: true, pct: totalAlunos > 0 ? Math.round((acertos / totalAlunos) * 100) : 0 };
                            });
                            
                            return (
                              <>
                                {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                  const dados = pctB1[i] || { temQ: false, pct: 0 };
                                  return <td key={`pct1-${i}`} className={`p-1 text-center font-bold text-xs ${!dados.temQ ? 'bg-gray-100 text-gray-300' : dados.pct >= 70 ? 'text-green-800 bg-green-200' : dados.pct >= 50 ? 'text-yellow-800 bg-yellow-200' : 'text-red-800 bg-red-200'}`}>{dados.temQ ? `${dados.pct}%` : '-'}</td>;
                                })}
                                {[...Array(questoesPorBlocoSAEB(configSimulado.serie))].map((_, i) => {
                                  const dados = pctB2[i] || { temQ: false, pct: 0 };
                                  return <td key={`pct2-${i}`} className={`p-1 text-center font-bold text-xs ${!dados.temQ ? 'bg-gray-100 text-gray-300' : dados.pct >= 70 ? 'text-green-800 bg-green-200' : dados.pct >= 50 ? 'text-yellow-800 bg-yellow-200' : 'text-red-800 bg-red-200'}`}>{dados.temQ ? `${dados.pct}%` : '-'}</td>;
                                })}
                              </>
                            );
                          })()}
                          <td className="p-2 bg-orange-400 border-l-2 border-green-400"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {/* Botões de ação - compactos no mobile */}
                  <div className="p-2 md:p-4 border-t flex flex-wrap gap-1.5 md:gap-3 justify-between">
                    <div className="flex gap-1.5 md:gap-3">
                      <button onClick={limparPlanilha} className="px-2 md:px-6 py-1.5 md:py-2 border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50 rounded-lg font-semibold flex items-center gap-1 md:gap-2 transition text-[10px] md:text-sm"><Trash2 className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden md:inline">Limpar</span></button>
                      <button onClick={excluirSimulado} className="px-2 md:px-6 py-1.5 md:py-2 border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-lg font-semibold flex items-center gap-1 md:gap-2 transition text-[10px] md:text-sm"><Trash2 className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden md:inline">Excluir</span></button>
                    </div>
                    <div className="flex gap-1.5 md:gap-3">
                      <button className="px-2 md:px-6 py-1.5 md:py-2 border-2 rounded-lg font-semibold flex items-center gap-1 md:gap-2 text-[10px] md:text-sm" style={{borderColor: '#1e3a5f', color: '#1e3a5f'}}><Download className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden md:inline">Excel</span></button>
                      <button onClick={() => gerarPDFPlanilha('prof')} className="px-2 md:px-6 py-1.5 md:py-2 rounded-lg font-semibold flex items-center gap-1 md:gap-2 text-white text-[10px] md:text-sm" style={{backgroundColor: '#f7941d'}}><Download className="w-3 h-3 md:w-4 md:h-4" /> PDF</button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* VIEW GRÁFICO - PROFESSOR */
              <div>
                {/* Aviso para rotacionar celular - só aparece em portrait mobile */}
                <div className="md:hidden fixed inset-0 bg-gray-900 bg-opacity-95 z-50 flex items-center justify-center portrait:flex landscape:hidden">
                  <div className="text-center p-6">
                    <div className="w-20 h-20 mx-auto mb-4 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
                      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Gire o Celular</h3>
                    <p className="text-gray-300 text-sm mb-4">Para melhor visualização do gráfico, coloque o celular na horizontal</p>
                    <button 
                      onClick={() => setViewDesempenho('lista')}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm"
                    >
                      ← Voltar
                    </button>
                  </div>
                </div>

                <button onClick={() => setViewDesempenho('lista')} className="mb-3 md:mb-4 flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold text-sm md:text-base">
                  <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" /> Voltar
                </button>
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1e3a5f' }}>
                  {(() => {
                    // 🔧 v493: Calcular dados reais do simulado selecionado
                    const turmaAtual = turmas.find(t => configSimulado.turmaId ? t.id === configSimulado.turmaId : (t.serie === configSimulado.serie && (configSimulado.turma === "" || t.turma === configSimulado.turma)));
                    
                    // 🔧 v493: Usar gabaritos do simulado selecionado (não dos blocos locais)
                    const gabaritosB1 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 1);
                    const gabaritosB2 = obterGabaritosSimulado(simuladoSelecionado, blocosSimulado, questoes, 2);
                    
                    // Calcular total de questões baseado nos gabaritos válidos
                    const questoesB1 = gabaritosB1.filter(g => g.gabarito && g.gabarito !== '').length;
                    const questoesB2 = gabaritosB2.filter(g => g.gabarito && g.gabarito !== '').length;
                    const totalQuestoesReal = questoesB1 + questoesB2;
                    
                    // Calcular acertos usando dados reais da planilha
                    let totalAcertosGeral = 0;
                    let totalRespostasGeral = 0;
                    
                    const acertosPorHabilidade = {};
                    
                    // Inicializar contadores por habilidade usando gabaritos do simulado
                    gabaritosB1.forEach((g, i) => {
                      if (g.gabarito && g.habilidade) {
                        if (!acertosPorHabilidade[g.habilidade]) {
                          acertosPorHabilidade[g.habilidade] = { acertos: 0, total: 0 };
                        }
                      }
                    });
                    gabaritosB2.forEach((g, i) => {
                      if (g.gabarito && g.habilidade) {
                        if (!acertosPorHabilidade[g.habilidade]) {
                          acertosPorHabilidade[g.habilidade] = { acertos: 0, total: 0 };
                        }
                      }
                    });
                    
                    // Calcular acertos usando dados da planilha
                    turmaAtual?.alunos.forEach((aluno, alunoIdx) => {
                      // Bloco 1
                      gabaritosB1.forEach((g, qIdx) => {
                        if (g.gabarito) {
                          const chavePlanilha = `prof_${alunoIdx}_0_${qIdx}`;
                          const resposta = respostasEditaveis[chavePlanilha];
                          if (resposta && resposta !== '') {
                            const acertou = resposta === g.gabarito;
                            if (acertou) {
                              totalAcertosGeral++;
                              if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                                acertosPorHabilidade[g.habilidade].acertos++;
                              }
                            }
                            totalRespostasGeral++;
                            if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                              acertosPorHabilidade[g.habilidade].total++;
                            }
                          }
                        }
                      });
                      // Bloco 2
                      gabaritosB2.forEach((g, qIdx) => {
                        if (g.gabarito) {
                          const chavePlanilha = `prof_${alunoIdx}_1_${qIdx}`;
                          const resposta = respostasEditaveis[chavePlanilha];
                          if (resposta && resposta !== '') {
                            const acertou = resposta === g.gabarito;
                            if (acertou) {
                              totalAcertosGeral++;
                              if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                                acertosPorHabilidade[g.habilidade].acertos++;
                              }
                            }
                            totalRespostasGeral++;
                            if (g.habilidade && acertosPorHabilidade[g.habilidade]) {
                              acertosPorHabilidade[g.habilidade].total++;
                            }
                          }
                        }
                      });
                    });
                    
                    const percentualGeral = totalRespostasGeral > 0 ? (totalAcertosGeral / totalRespostasGeral) * 100 : 0;
                    
                    // Determinar padrão de desempenho (porcentagens corretas)
                    let padrao, padraoColor, padraoBg, padraoBorder, padraoDescricao;
                    if (percentualGeral >= 70) {
                      padrao = 'AVANÇADO';
                      padraoColor = 'text-sky-700';
                      padraoBg = 'bg-sky-100';
                      padraoBorder = 'border-sky-400';
                      padraoDescricao = 'Este padrão agrupa estudantes com desenvolvimento além do esperado para a sua etapa de escolaridade, os quais precisam de estímulos para continuar avançando no processo de aprendizagem.';
                    } else if (percentualGeral >= 40) {
                      padrao = 'ADEQUADO';
                      padraoColor = 'text-slate-600';
                      padraoBg = 'bg-slate-100';
                      padraoBorder = 'border-slate-400';
                      padraoDescricao = 'Este padrão reúne estudantes que consolidaram o desenvolvimento das habilidades e competências previstas para a etapa de escolaridade. Entretanto, ainda requerem ações para aprofundar a aprendizagem.';
                    } else if (percentualGeral >= 20) {
                      padrao = 'BÁSICO';
                      padraoColor = 'text-orange-600';
                      padraoBg = 'bg-orange-100';
                      padraoBorder = 'border-orange-300';
                      padraoDescricao = 'Este padrão agrupa estudantes que ainda não demonstram ter desenvolvido adequadamente as habilidades e competências essenciais para a sua etapa de escolaridade. Demandam atividades de reforço na aprendizagem.';
                    } else {
                      padrao = 'ABAIXO DO BÁSICO';
                      padraoColor = 'text-red-700';
                      padraoBg = 'bg-red-100';
                      padraoBorder = 'border-red-500';
                      padraoDescricao = 'Este padrão reúne estudantes com carência de aprendizagem para o desenvolvimento das habilidades e competências mínimas requeridas para a conclusão da etapa de escolaridade em que se encontram. São estudantes que necessitam de ações pedagógicas de recuperação.';
                    }
                    
                    // Calcular níveis separados para Português e Matemática
                    const calcularNivelPortugues5ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 125 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '125 a 150 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '150 a 175 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '175 a 200 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '200 a 225 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '225 a 250 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '250 a 275 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '275 a 300 pontos' };
                      if (pct <= 95) return { nivel: 9, faixa: '300 a 325 pontos' };
                      return { nivel: 10, faixa: 'Acima de 325 pontos' };
                    };
                    
                    const calcularNivelMatematica5ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 150 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '150 a 175 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '175 a 200 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '200 a 225 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '225 a 250 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '250 a 275 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '275 a 300 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '300 a 325 pontos' };
                      return { nivel: 9, faixa: 'Acima de 325 pontos' };
                    };
                    
                    const calcularNivelPortugues9ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 175 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '175 a 200 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '200 a 225 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '225 a 250 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '250 a 275 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '275 a 300 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '300 a 325 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '325 a 350 pontos' };
                      if (pct <= 95) return { nivel: 9, faixa: '350 a 375 pontos' };
                      return { nivel: 10, faixa: 'Acima de 375 pontos' };
                    };
                    
                    const calcularNivelMatematica9ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: 'Até 225 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '225 a 250 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '250 a 275 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '275 a 300 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '300 a 325 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '325 a 350 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '350 a 375 pontos' };
                      return { nivel: 8, faixa: 'Acima de 375 pontos' };
                    };
                    
                    const calcularNivelPortugues2ano = (pct) => {
                      if (pct <= 10) return { nivel: 1, faixa: '650 a 675 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '675 a 700 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '700 a 725 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '725 a 750 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '750 a 775 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '775 a 800 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '800 a 825 pontos' };
                      return { nivel: 8, faixa: 'Acima de 825 pontos' };
                    };
                    
                    const calcularNivelMatematica2ano = (pct) => {
                      if (pct <= 10) return { nivel: 'Abaixo de 1', faixa: 'Até 650 pontos' };
                      if (pct <= 20) return { nivel: 2, faixa: '675 a 700 pontos' };
                      if (pct <= 30) return { nivel: 3, faixa: '700 a 725 pontos' };
                      if (pct <= 40) return { nivel: 4, faixa: '725 a 750 pontos' };
                      if (pct <= 55) return { nivel: 5, faixa: '750 a 775 pontos' };
                      if (pct <= 70) return { nivel: 6, faixa: '775 a 800 pontos' };
                      if (pct <= 80) return { nivel: 7, faixa: '800 a 825 pontos' };
                      if (pct <= 90) return { nivel: 8, faixa: '825 a 850 pontos' };
                      return { nivel: 9, faixa: 'Acima de 850 pontos' };
                    };
                    
                    // Calcular percentual por bloco (Português = Bloco 1, Matemática = Bloco 2)
                    let acertosPort = 0, totalPort = 0, acertosMat = 0, totalMat = 0;
                    turmaAtual?.alunos.forEach((aluno, alunoIdx) => {
                      blocosSimulado[0].questoesSelecionadas.forEach((qId, qIdx) => {
                        const q = questoes.find(x => String(x.id) === String(qId));
                        if (q) {
                          const acertou = ((alunoIdx * 7 + qIdx * 3) % 10) > 4;
                          if (acertou) acertosPort++;
                          totalPort++;
                        }
                      });
                      blocosSimulado[1].questoesSelecionadas.forEach((qId, qIdx) => {
                        const q = questoes.find(x => String(x.id) === String(qId));
                        if (q) {
                          const acertou = ((alunoIdx * 5 + qIdx * 11) % 10) > 3;
                          if (acertou) acertosMat++;
                          totalMat++;
                        }
                      });
                    });
                    
                    const pctPort = totalPort > 0 ? (acertosPort / totalPort) * 100 : 0;
                    const pctMat = totalMat > 0 ? (acertosMat / totalMat) * 100 : 0;
                    
                    // Selecionar função de nível baseada na série
                    const serieAtual = configSimulado.serie;
                    let nivelPort, nivelMat;
                    if (serieAtual.includes('2')) {
                      nivelPort = calcularNivelPortugues2ano(pctPort);
                      nivelMat = calcularNivelMatematica2ano(pctMat);
                    } else if (serieAtual.includes('9')) {
                      nivelPort = calcularNivelPortugues9ano(pctPort);
                      nivelMat = calcularNivelMatematica9ano(pctMat);
                    } else {
                      nivelPort = calcularNivelPortugues5ano(pctPort);
                      nivelMat = calcularNivelMatematica5ano(pctMat);
                    }
                    
                    // Lista de habilidades para o gráfico
                    const habilidadesGrafico = Object.entries(acertosPorHabilidade).map(([hab, dados]) => ({
                      habilidade: hab,
                      percentual: dados.total > 0 ? Math.round((dados.acertos / dados.total) * 100) : 0
                    }));
                    
                    return (
                      <>
                        <div className="p-6 border-b text-center" style={{backgroundColor: '#1e3a5f'}}>
                          <div className="flex justify-center items-center gap-4 mb-2">
                            <h2 className="text-xl font-bold text-white">NÍVEIS E ESCALAS DE PROFICIÊNCIA NO SIMULADO SAEB</h2>
                            <button onClick={() => setShowDropdownSimulado(true)} className="px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 hover:opacity-90 transition cursor-pointer" style={{backgroundColor: '#f7941d'}}>Simulado #{formatarCodigoSimulado(simuladoSelecionado?.codigo || simuladoSelecionado?.numero || numeroSimulado)} <ChevronDown className="w-3 h-3" /></button>
                          </div>
                          <p className="text-blue-200">{configSimulado.turmaNome || `${configSimulado.serie} ${configSimulado.turma}`} - {componenteSimulado === "Língua Portuguesa" ? "LÍNGUA PORTUGUESA" : "MATEMÁTICA"}</p>
                        </div>
                        <div className="p-6 border-b">
                          <p className="text-center text-gray-300 mb-4">De acordo com os resultados neste simulado, a turma {configSimulado.turmaNome || `${configSimulado.serie} ${configSimulado.turma}`} está com:</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                            <div className={`${padraoBg} border-2 ${padraoBorder} rounded-xl p-5`}>
                              <div className={`text-xs ${padraoColor} mb-1 uppercase text-center`}>Padrão de Desempenho</div>
                              <div className={`text-2xl font-bold ${padraoColor} text-center mb-3`}>{padrao}</div>
                              <div className={`text-xs ${padraoColor} opacity-90 text-justify leading-relaxed`}>{padraoDescricao}</div>
                            </div>
                            <div className={`${componenteSimulado === 'Língua Portuguesa' ? 'bg-purple-50 border-purple-300' : 'bg-blue-50 border-blue-300'} border-2 rounded-xl p-5 text-center flex flex-col justify-center`}>
                              <div className={`text-xs ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'} mb-2 uppercase`}>Nível de Proficiência</div>
                              <div className={`text-4xl font-bold ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-700' : 'text-blue-700'}`}>
                                Nível {componenteSimulado === 'Língua Portuguesa' ? nivelPort.nivel : nivelMat.nivel}
                              </div>
                              <div className={`text-sm ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-600' : 'text-blue-600'} font-semibold mt-2`}>
                                {componenteSimulado === 'Língua Portuguesa' ? nivelPort.faixa : nivelMat.faixa}
                              </div>
                              <div className={`text-xs ${componenteSimulado === 'Língua Portuguesa' ? 'text-purple-400' : 'text-blue-400'} mt-2`}>
                                {configSimulado.serie} - {componenteSimulado === 'Língua Portuguesa' ? 'Língua Portuguesa' : 'Matemática'}
                              </div>
                            </div>
                            <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-5 text-center flex flex-col justify-center">
                              <div className="text-xs text-gray-600 mb-1 uppercase">Participantes</div>
                              <div className="text-2xl font-bold text-gray-700">{turmaAtual?.alunos.length || 0}</div>
                              <div className="text-xs text-gray-500 mt-1">alunos • {totalQuestoesReal} questões</div>
                            </div>
                          </div>
                        </div>
                        <div className="p-6">
                          <h3 className="text-center font-bold text-lg mb-6">PORCENTAGEM DE ACERTO POR HABILIDADES/DESCRITORES</h3>
                          {habilidadesGrafico.length === 0 ? (
                            <div className="text-center py-8 text-gray-400">
                              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                              <p>Selecione questões no simulado para ver o gráfico de desempenho</p>
                            </div>
                          ) : (
                            <>
                              <div className="overflow-x-auto">
                                <div className="flex items-end justify-center gap-2" style={{minWidth: `${habilidadesGrafico.length * 50}px`, height: '280px'}}>
                                  {habilidadesGrafico.map((item, i) => {
                                    const altura = (item.percentual / 100) * 200;
                                    const cor = item.percentual >= 70 ? '#22c55e' : item.percentual >= 40 ? '#3b82f6' : item.percentual >= 20 ? '#eab308' : '#ef4444';
                                    return (
                                      <div key={i} className="flex flex-col items-center" style={{width: '45px'}}>
                                        <div className="text-xs font-bold mb-1" style={{color: cor}}>{item.percentual}%</div>
                                        <div className="w-8 rounded-t transition-all" style={{height: `${Math.max(altura, 5)}px`, backgroundColor: cor}} />
                                        <div className="mt-2 text-xs font-mono text-gray-200 -rotate-45 origin-top-left whitespace-nowrap" style={{width: '60px'}}>{item.habilidade}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="flex justify-center gap-4 mt-10 pt-4 border-t flex-wrap">
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-green-500"></div><span className="text-xs">Avançado</span></div>
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-500"></div><span className="text-xs">Adequado</span></div>
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-yellow-500"></div><span className="text-xs">Básico</span></div>
                                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500"></div><span className="text-xs">Abaixo do Básico</span></div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="p-4 border-t flex gap-3 justify-end">
                          <button className="px-6 py-2 border-2 rounded-lg font-semibold flex items-center gap-2" style={{borderColor: '#1e3a5f', color: '#1e3a5f'}}><Download className="w-4 h-4" /> Imagem</button>
                          <button onClick={() => gerarPDFGrafico('prof')} className="px-6 py-2 rounded-lg font-semibold flex items-center gap-2 text-white" style={{backgroundColor: '#f7941d'}}><Download className="w-4 h-4" /> PDF</button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Modal Nova Turma */}
            {showModalTurma && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl p-6 w-full max-w-md">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold">{turmaEditando ? 'Editar' : 'Nova'} Turma</h3>
                    <button onClick={() => setShowModalTurma(false)} className="p-2 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nome da Turma</label>
                      <input type="text" className="w-full p-3 border rounded-lg" value={novaTurma.nome} onChange={(e) => setNovaTurma({...novaTurma, nome: e.target.value})} placeholder="Ex: 5º ano A" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-2">Série</label>
                        <select className="w-full p-3 border rounded-lg" value={novaTurma.serie} onChange={(e) => setNovaTurma({...novaTurma, serie: e.target.value})}>
                          <option value="">Selecione</option>
                          {seriesDisponiveis.filter(s => s.ativo).map(s => <option key={s.id}>{s.nome}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Turma</label>
                        <select className="w-full p-3 border rounded-lg" value={novaTurma.turma || ''} onChange={(e) => setNovaTurma({...novaTurma, turma: e.target.value})}>
                          <option value="">-</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                          <option value="E">E</option>
                          <option value="F">F</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Turno</label>
                      <select className="w-full p-3 border rounded-lg" value={novaTurma.turno} onChange={(e) => setNovaTurma({...novaTurma, turno: e.target.value})}>
                        <option value="">Selecione</option>
                        <option>Matutino</option>
                        <option>Vespertino</option>
                        <option>Noturno</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button onClick={async () => { 
                      try {
                        console.log('🔄 [TURMA] Iniciando salvamento...');
                        console.log('🔄 [TURMA] Firebase ready:', firebaseReady);
                        console.log('🔄 [TURMA] DB disponível:', !!db);
                        console.log('🔄 [TURMA] User UID:', currentUser?.uid);
                        
                        if (firebaseReady && db && currentUser?.uid) {
                          if(turmaEditando) {
                            // Editar turma existente
                            console.log('📝 [TURMA] Editando turma:', turmaEditando.id);
                            await db.collection('turmas').doc(String(turmaEditando.id)).update({
                              nome: String(novaTurma.nome || ''),
                              serie: String(novaTurma.serie || ''),
                              turma: String(novaTurma.turma || ''),
                              turno: String(novaTurma.turno || '')
                            });
                            console.log('✅ [TURMA] Turma atualizada no Firebase!');
                            setTurmasFirebase(prev => prev.map(t => t.id === turmaEditando.id ? {...t, ...novaTurma} : t));
                          } else {
                            // Criar nova turma
                            const novaTurmaData = {
                              nome: String(novaTurma.nome || ''),
                              serie: String(novaTurma.serie || ''),
                              turma: String(novaTurma.turma || ''),
                              turno: String(novaTurma.turno || ''),
                              alunos: [], 
                              userId: String(currentUser.uid),
                              dataCriacao: new Date().toISOString()
                            };
                            console.log('📝 [TURMA] Criando nova turma:', novaTurmaData);
                            const docRef = await db.collection('turmas').add(novaTurmaData);
                            console.log('✅ [TURMA] Turma criada no Firebase! ID:', docRef.id);
                            console.log('✅ [TURMA] userId salvo:', novaTurmaData.userId);
                            setTurmasFirebase(prev => [...prev, {id: docRef.id, ...novaTurmaData}]);
                          }
                          setShowModalTurma(false); 
                          setTurmaEditando(null);
                          alert('✅ Turma salva com sucesso!\n\n📌 ID do documento salvo no Firebase.\nVerifique o console para detalhes.');
                        } else {
                          console.error('❌ [TURMA] Firebase não disponível:', { firebaseReady, db: !!db, uid: currentUser?.uid });
                          alert('⚠️ Firebase não disponível ou usuário não autenticado');
                        }
                      } catch (error) {
                        console.error('❌ [TURMA] Erro ao salvar turma:', error);
                        alert('❌ Erro ao salvar: ' + error.message);
                      }
                    }} className="flex-1 py-3 rounded-lg font-semibold text-white" style={{backgroundColor: '#f7941d'}}>{turmaEditando ? 'Salvar' : 'Criar'}</button>
                    <button onClick={() => setShowModalTurma(false)} className="flex-1 py-3 border rounded-lg">Cancelar</button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal Adicionar Aluno */}
            {showModalAluno && turmaEditando && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl p-6 w-full max-w-md">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold">Adicionar Aluno - {turmaEditando.nome}</h3>
                    <button onClick={() => setShowModalAluno(false)} className="p-2 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome do Aluno</label>
                    <input type="text" className="w-full p-3 border rounded-lg" value={novoAlunoNome} onChange={(e) => setNovoAlunoNome(e.target.value)} placeholder="Nome completo" />
                  </div>
                  <button onClick={async () => { 
                    if(novoAlunoNome.trim()) { 
                      try {
                        console.log('🔄 [ALUNO] Iniciando adição de aluno...');
                        console.log('🔄 [ALUNO] Turma ID:', turmaEditando?.id);
                        console.log('🔄 [ALUNO] Nome do aluno:', novoAlunoNome.trim());
                        
                        if (firebaseReady && db && turmaEditando?.id) {
                          const turmaAtual = turmasFirebase.find(t => t.id === turmaEditando.id);
                          console.log('🔄 [ALUNO] Turma encontrada:', turmaAtual?.nome);
                          
                          // Garantir que alunos seja sempre um array de strings
                          const alunosAtuais = Array.isArray(turmaAtual?.alunos) ? turmaAtual.alunos : [];
                          console.log('🔄 [ALUNO] Alunos atuais:', alunosAtuais.length);
                          
                          const novosAlunos = [...alunosAtuais, String(novoAlunoNome.trim())];
                          console.log('🔄 [ALUNO] Novos alunos (total):', novosAlunos.length);
                          
                          await db.collection('turmas').doc(String(turmaEditando.id)).update({ alunos: novosAlunos });
                          console.log('✅ [ALUNO] Aluno salvo no Firebase! Turma:', turmaEditando.id);
                          
                          setTurmasFirebase(prev => prev.map(t => t.id === turmaEditando.id ? {...t, alunos: novosAlunos} : t));
                          setNovoAlunoNome('');
                          alert('✅ Aluno adicionado!\n\n📌 Salvo no Firebase.\nVerifique o console para detalhes.');
                        } else {
                          console.error('❌ [ALUNO] Erro:', { firebaseReady, db: !!db, turmaId: turmaEditando?.id });
                          alert('⚠️ Erro: turma não encontrada');
                        }
                      } catch (error) {
                        console.error('❌ [ALUNO] Erro ao adicionar aluno:', error);
                        alert('❌ Erro: ' + error.message);
                      }
                    }
                  }} className="w-full mt-6 py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2" style={{backgroundColor: '#f7941d'}}><Plus className="w-5 h-5" /> Adicionar</button>
                </div>
              </div>
            )}
          </div>
        )}

        {currentView === 'professor-historico' && (
          <div className="rounded-xl p-4 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
            <h3 className="font-bold text-base md:text-lg mb-4 md:mb-6 text-white">Histórico de Atividades</h3>
            
            <div className="flex flex-col md:flex-row gap-2 md:gap-3 mb-4 md:mb-6">
              <select 
                className="px-3 md:px-4 py-2 border rounded-lg bg-white text-sm md:text-base flex-1"
                onChange={(e) => setFiltroHistorico({...filtroHistorico, tipo: e.target.value})}
                value={filtroHistorico.tipo || ''}
              >
                <option value="">Todos os Tipos</option>
                <option value="questao">Atividade</option>
                <option value="avaliacao">Avaliação</option>
                <option value="simulado">Simulado SAEB</option>
                <option value="cartoes">Cartões</option>
                <option value="pronta">Pronta</option>
              </select>
              <select 
                className="px-3 md:px-4 py-2 border rounded-lg bg-white text-sm md:text-base flex-1"
                onChange={(e) => setFiltroHistorico({...filtroHistorico, componente: e.target.value})}
                value={filtroHistorico.componente || ''}
              >
                <option value="">Todos os Componentes</option>
                <option>Português</option>
                <option>Matemática</option>
              </select>
            </div>

            <div className="rounded-xl overflow-hidden">
              {carregandoHistorico ? (
                <div className="text-center py-6 md:py-8 text-gray-300">
                  <div className="w-6 h-6 md:w-8 md:h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <span className="text-sm">Carregando...</span>
                </div>
              ) : historicoPDFs.length === 0 ? (
                <div className="text-center py-6 md:py-8 text-gray-400">
                  <FileText className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm md:text-base">Nenhum PDF gerado ainda.</p>
                  <p className="text-xs md:text-sm">Gere sua primeira atividade!</p>
                </div>
              ) : (
                <>
                  {/* Desktop - Tabela */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ backgroundColor: '#0a1929' }}>
                        <tr>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Título</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Tipo</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Código</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Série</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Componente</th>
                          <th className="text-center py-3 px-4 text-blue-300 text-sm">Questões</th>
                          <th className="text-left py-3 px-4 text-blue-300 text-sm">Data</th>
                          <th className="text-right py-3 px-4 text-blue-300 text-sm">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historicoPDFs
                          .filter(pdf => {
                            if (filtroHistorico.tipo && pdf.tipo !== filtroHistorico.tipo) return false;
                            if (filtroHistorico.componente && pdf.componente !== filtroHistorico.componente) return false;
                            return true;
                          })
                          .map((pdf) => (
                          <tr key={pdf.id} className="border-b border-gray-700 hover:bg-gray-800">
                            <td className="py-3 px-4 text-white text-sm">{pdf.titulo?.substring(0, 35) || 'Sem título'}...</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs border ${
                                pdf.tipo === 'simulado' ? 'bg-purple-900 text-purple-300 border-purple-500' :
                                pdf.tipo === 'cartoes' ? 'bg-pink-900 text-pink-300 border-pink-500' :
                                pdf.tipo === 'avaliacao' ? 'bg-orange-900 text-orange-300 border-orange-500' :
                                'bg-blue-900 text-blue-300 border-blue-500'
                              }`}>
                                {pdf.tipo === 'questao' ? 'Atividade' : 
                                 pdf.tipo === 'avaliacao' ? 'Avaliação' : 
                                 pdf.tipo === 'simulado' ? 'Simulado' : 
                                 pdf.tipo === 'cartoes' ? 'Cartões' :
                                 pdf.tipo === 'pronta' ? 'Pronta' : pdf.tipo}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {pdf.codigoSimulado ? (
                                <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                  #{pdf.codigoSimulado}
                                </span>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-300">{pdf.serie || '-'}</td>
                            <td className="py-3 px-4 text-gray-300">{pdf.componente || '-'}</td>
                            <td className="py-3 px-4 text-center text-gray-300">{pdf.totalQuestoes || '-'}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">
                              {pdf.dataGeracao ? new Date(pdf.dataGeracao).toLocaleDateString('pt-BR') : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex justify-end gap-2">
                                <button 
                                  onClick={() => window.open(pdf.urlPdf, '_blank')}
                                  className="p-2 text-green-400 hover:bg-green-900 rounded"
                                  title="Baixar PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile - Cards */}
                  <div className="md:hidden space-y-3">
                    {historicoPDFs
                      .filter(pdf => {
                        if (filtroHistorico.tipo && pdf.tipo !== filtroHistorico.tipo) return false;
                        if (filtroHistorico.componente && pdf.componente !== filtroHistorico.componente) return false;
                        return true;
                      })
                      .map((pdf) => (
                      <div key={pdf.id} className="bg-gray-800 rounded-lg p-2.5">
                        <div className="flex justify-between items-start mb-1.5">
                          <div className="flex-1 min-w-0 pr-2">
                            <p className="text-white text-xs font-semibold truncate">{pdf.titulo?.substring(0, 28) || 'Sem título'}</p>
                            <p className="text-gray-400 text-[10px]">{pdf.dataGeracao ? new Date(pdf.dataGeracao).toLocaleDateString('pt-BR') : '-'}</p>
                          </div>
                          <button 
                            onClick={() => window.open(pdf.urlPdf, '_blank')}
                            className="p-1.5 bg-green-600 text-white rounded-lg flex-shrink-0"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] border ${
                            pdf.tipo === 'simulado' ? 'bg-purple-900 text-purple-300 border-purple-500' :
                            pdf.tipo === 'cartoes' ? 'bg-pink-900 text-pink-300 border-pink-500' :
                            pdf.tipo === 'avaliacao' ? 'bg-orange-900 text-orange-300 border-orange-500' :
                            'bg-blue-900 text-blue-300 border-blue-500'
                          }`}>
                            {pdf.tipo === 'questao' ? 'Atividade' : pdf.tipo === 'simulado' ? 'Simulado' : pdf.tipo}
                          </span>
                          {pdf.codigoSimulado && (
                            <span className="bg-orange-500 text-white px-1.5 py-0.5 rounded-full text-[9px] font-bold">
                              #{pdf.codigoSimulado}
                            </span>
                          )}
                          <span className="text-gray-400 text-[9px]">{pdf.serie || '-'} • {pdf.componente || '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {currentView === 'professor-recargas' && (
          <div className="w-full overflow-x-hidden box-border">
            {/* Saldo atual */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 md:p-8 rounded-xl md:rounded-2xl shadow-lg mb-3 md:mb-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-3">
                  <p className="text-blue-100 mb-0.5 md:mb-1 text-[10px] md:text-base">Saldo Atual</p>
                  <div className="text-2xl md:text-5xl font-bold">{currentUser?.creditos || 0}</div>
                  <p className="text-blue-200 text-[9px] md:text-sm mt-0.5 md:mt-2">1 crédito = 1 página PDF ou 1 correção</p>
                </div>
                <div className="w-12 h-12 md:w-24 md:h-24 bg-white bg-opacity-20 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 md:w-12 md:h-12" />
                </div>
              </div>
            </div>

            {/* Pacotes de créditos */}
            <h3 className="text-sm md:text-lg font-bold text-white mb-2 md:mb-4">Escolha um pacote</h3>
            
            {/* Cards empilhados no mobile */}
            <div className="flex flex-col gap-3 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-4 mb-4 md:mb-6">
              {recargas.filter(r => r.ativo).map((pacote) => {
                // 🔧 v415: Verificar se é o plano atual (comparação case-insensitive)
                const planoUsuario = (currentUser?.plano || '').toLowerCase().trim();
                const nomePacote = (pacote.nome || '').toLowerCase().trim();
                const isPlanoAtual = planoUsuario === nomePacote && planoUsuario !== 'gratuito' && planoUsuario !== '';
                
                return (
                <div 
                  key={pacote.id}
                  className={`rounded-xl p-3 md:p-5 cursor-pointer w-full box-border ${
                    isPlanoAtual ? 'border-2 border-green-400 ring-2 ring-green-400 ring-opacity-50' : 'border-2 border-gray-600'
                  }`} 
                  style={{ backgroundColor: '#1e3a5f' }}
                  onClick={() => {
                    if (!isPlanoAtual) {
                      setRecargaSelecionada(pacote);
                      setEtapaCompra(1);
                      setFormaPagamento('');
                      setPagamentoProcessado(false); setShowModalCompraRecarga(true);
                    }
                  }}
                >
                  {/* Indicador de plano atual */}
                  {isPlanoAtual && (
                    <div className="text-center mb-2">
                      <span className="bg-green-500 text-white text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-full flex items-center justify-center gap-1">
                        <CheckCircle className="w-3 h-3" /> MINHA RECARGA ATUAL
                      </span>
                    </div>
                  )}
                  
                  {/* Nome do plano */}
                  <h4 className="text-sm md:text-xl font-bold text-white text-center mb-1">{pacote.nome}</h4>
                  
                  {/* Preço */}
                  <div className="text-center mb-1 md:mb-3">
                    <span className="text-lg md:text-3xl font-bold" style={{ color: '#f7941d' }}>
                      R$ {pacote.preco.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                  
                  {/* Descrição */}
                  <p className="text-gray-300 text-[10px] md:text-sm mb-2 text-center">{pacote.descricao}</p>
                  
                  {/* Vantagens */}
                  <div className="space-y-0.5 md:space-y-2 text-gray-300 text-[11px] md:text-sm mb-2 md:mb-4">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                      <span>{pacote.paginas} Créditos <span className="text-[9px] md:text-xs text-gray-400">(válidos por 1 ano)</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                      <span>{pacote.series === 1 ? '1 série' : pacote.series >= 5 ? 'Todas as séries' : `${pacote.series} séries`}</span>
                    </div>
                    {pacote.vantagens && pacote.vantagens.map((vantagem, idx) => (
                      <div key={idx} className="flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                        <span>{vantagem}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Botão */}
                  {isPlanoAtual ? (
                    <div className="w-full py-1.5 md:py-2.5 rounded-lg font-semibold text-white text-xs md:text-base bg-green-600 text-center flex items-center justify-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Minha recarga atual
                    </div>
                  ) : (
                    <button 
                      className="w-full py-1.5 md:py-2.5 rounded-lg font-semibold text-white text-xs md:text-base bg-green-500 hover:bg-green-600"
                    >
                      Escolher plano
                    </button>
                  )}
                </div>
              );
              })}
            </div>

            {/* Histórico */}
            <div className="rounded-xl p-4 md:p-6" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="font-bold text-white mb-3 md:mb-4 text-sm md:text-base">Histórico de Recargas</h3>
              <div className="rounded-xl overflow-x-auto">
              <table className="w-full min-w-[400px]">
                <thead style={{ backgroundColor: '#0a1929' }}>
                  <tr>
                    <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Data</th>
                    <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Créditos</th>
                    <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Valor</th>
                    <th className="text-left p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Pag.</th>
                    <th className="text-center p-2 md:p-3 font-semibold text-blue-300 text-xs md:text-sm">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {historicoRecargas.length > 0 ? (
                    historicoRecargas.map((recarga) => (
                      <tr key={recarga.id} className="hover:bg-gray-800">
                        <td className="p-2 md:p-3 text-gray-300 text-xs md:text-sm">
                          {recarga.createdAt?.toDate ? recarga.createdAt.toDate().toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td className="p-2 md:p-3 font-bold text-blue-400 text-xs md:text-sm">+{recarga.paginas}</td>
                        <td className="p-2 md:p-3 text-gray-300 text-xs md:text-sm">R$ {recarga.valor?.toFixed(2).replace('.', ',') || '-'}</td>
                        <td className="p-2 md:p-3 text-gray-300 text-xs md:text-sm">{recarga.formaPagamento === 'pix' ? 'PIX' : 'Cartão'}</td>
                        <td className="p-2 md:p-3 text-center"><span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-green-900 text-green-300 border border-green-500 rounded-full text-[10px] md:text-xs font-bold">OK</span></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="p-4 text-center text-gray-400 text-sm">
                        Nenhuma recarga realizada ainda
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {currentView === 'professor-suporte' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* WhatsApp */}
            <div className="p-4 md:p-8 rounded-xl text-center border-2 border-green-500" style={{ backgroundColor: '#1e3a5f' }}>
              <Headphones className="w-12 h-12 md:w-16 md:h-16 text-green-400 mx-auto mb-3 md:mb-4" />
              <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-4 text-white">WhatsApp</h3>
              <p className="text-gray-300 text-xs md:text-sm mb-3 md:mb-4">Atendimento rápido</p>
              <a 
                href={`https://wa.me/${telefoneSuporte}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-green-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg hover:bg-green-700 transition text-sm md:text-base w-full md:w-auto"
              >
                Abrir WhatsApp
              </a>
              <p className="text-[10px] md:text-xs text-gray-400 mt-3">Seg-Sex 8h-18h | Sáb 8h-12h</p>
            </div>
            
            {/* Vídeos */}
            <div className="p-4 md:p-8 rounded-xl border-2 border-red-500" style={{ backgroundColor: '#1e3a5f' }}>
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-white flex items-center gap-2">
                <Video className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                Vídeos Tutoriais
              </h3>
              <div className="space-y-2 md:space-y-3 max-h-60 md:max-h-80 overflow-y-auto">
                {videosTutoriais.filter(v => v.ativo && v.titulo).length > 0 ? (
                  videosTutoriais.filter(v => v.ativo && v.titulo).map((video) => (
                    <a 
                      key={video.id}
                      href={video.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`border border-gray-600 rounded-lg p-3 md:p-4 flex items-center gap-3 md:gap-4 hover:bg-gray-800 transition ${video.url ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                      onClick={(e) => !video.url && e.preventDefault()}
                    >
                      <Video className="w-8 h-8 md:w-10 md:h-10 text-red-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-200 text-sm md:text-base truncate">{video.titulo}</div>
                        {!video.url && <span className="text-[10px] md:text-xs text-gray-500">Link não configurado</span>}
                      </div>
                      {video.url && <Play className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />}
                    </a>
                  ))
                ) : (
                  <div className="text-center py-6 md:py-8 text-gray-400">
                    <Video className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm md:text-base">Nenhum vídeo disponível</p>
                    <p className="text-[10px] md:text-xs mt-1">Entre em contato com o suporte</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal de Aviso - Limite de Página Atingido - z-[60] para ficar acima do modal de zoom */}
      {showModalLimitePagina && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-3 md:p-4">
          <div className="bg-white rounded-xl p-4 md:p-6 w-full max-w-lg">
            <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Essa questão não cabe na página atual!</h3>
                <p className="text-gray-600 text-sm mb-3 md:mb-4">
                  A página atual já está completa ou essa questão não cabe na página atual.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                  <p className="text-sm text-blue-800 font-semibold mb-1 md:mb-2">
                    Selecione uma questão menor ou crie uma nova página!
                  </p>
                  <p className="text-xs text-blue-700">
                    Caso escolha uma nova página, a questão selecionada será adicionada à ela.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 md:gap-3">
              <button 
                onClick={() => {
                  setShowModalLimitePagina(false);
                  setQuestaoParaAdicionar(null);
                }}
                className="flex-1 px-4 md:px-6 py-2.5 md:py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm md:text-base"
              >
                Cancelar
              </button>
              <button 
                onClick={confirmarNovaPagina}
                className="flex-1 px-4 md:px-6 py-2.5 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-1 md:gap-2 text-sm md:text-base"
              >
                <Plus className="w-4 h-4 md:w-5 md:h-5" />
                Criar Nova Página
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Aviso - Componente Curricular Diferente - z-[60] para ficar acima do modal de zoom */}
      {showModalComponenteDiferente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-3 md:p-4">
          <div className="bg-white rounded-xl p-4 md:p-6 w-full max-w-lg">
            <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <X className="w-5 h-5 md:w-6 md:h-6 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Componente Curricular Diferente</h3>
                <p className="text-gray-600 text-sm mb-3 md:mb-4">
                  Não é possível misturar questões de componentes curriculares diferentes em uma mesma {tipoGeracao === 'avaliacao' ? 'avaliação' : 'atividade'}.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                  <p className="text-sm text-blue-800 font-semibold mb-1 md:mb-2">
                    📚 Componente Atual: <span className="text-blue-900">{componenteBloqueado}</span>
                  </p>
                  <p className="text-xs text-blue-700">
                    Para adicionar questões de outro componente, você precisa primeiro remover todas as questões já selecionadas 
                    ou criar uma nova {tipoGeracao === 'avaliacao' ? 'avaliação' : 'atividade'}.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 md:gap-3">
              <button 
                onClick={() => setShowModalComponenteDiferente(false)}
                className="flex-1 px-4 md:px-6 py-2.5 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm md:text-base"
              >
                Entendi
              </button>
              <button 
                onClick={() => {
                  // Remove todas as questões e limpa os bloqueios
                  setPaginas([{ id: 1, questoes: [] }]);
                  setPaginaAtiva(1);
                  setComponenteBloqueado(null);
                  setHabilidadeBloqueada(null);
                  setShowModalComponenteDiferente(false);
                }}
                className="flex-1 px-6 py-3 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 font-semibold"
              >
                Limpar Tudo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Confirmação - Criar Segundo Bloco do Simulado */}
      {/* 🆕 Modal de Questões Vinculadas Excedentes */}
      {showModalQuestoesExcedentes && questaoExcedenteInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">⚠️ Questões Excedentes</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Este texto possui <span className="font-bold text-orange-600">{questaoExcedenteInfo.questoesDoTexto} questões vinculadas</span>, 
                  mas o bloco só tem espaço para <span className="font-bold text-blue-600">{questaoExcedenteInfo.espacoDisponivel} questão(ões)</span>.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-800">
                    <strong>Situação atual:</strong><br/>
                    • Questões no bloco: {questaoExcedenteInfo.questoesNoBloco}/{questaoExcedenteInfo.questoesNoBloco + questaoExcedenteInfo.espacoDisponivel}<br/>
                    • Questões do texto: {questaoExcedenteInfo.questoesDoTexto}<br/>
                    • Total se adicionar: {questaoExcedenteInfo.questoesNoBloco + questaoExcedenteInfo.questoesDoTexto} (máximo {questaoExcedenteInfo.questoesNoBloco + questaoExcedenteInfo.espacoDisponivel})
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-semibold mb-2">
                    O que deseja fazer?
                  </p>
                  <p className="text-xs text-blue-700">
                    Você pode usar apenas as primeiras {questaoExcedenteInfo.espacoDisponivel} questão(ões) deste texto para completar o bloco, 
                    ou escolher outro texto com menos questões.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => {
                  // Adicionar apenas até o limite
                  const questao = questaoExcedenteInfo.questao;
                  if (questaoExcedenteInfo.painel === 'prof') {
                    const novaLista = [...blocosSimulado.find(b => b.id === questaoExcedenteInfo.blocoId).questoesSelecionadas, questao.id];
                    setBlocosSimulado(blocosSimulado.map(b => 
                      b.id === questaoExcedenteInfo.blocoId 
                        ? {...b, questoesSelecionadas: novaLista}
                        : b
                    ));
                  } else {
                    const blocoIdx = blocosSimuladoEscola.findIndex(b => b.id === questaoExcedenteInfo.blocoId);
                    setBlocosSimuladoEscola(prev => prev.map((b, i) => 
                      i === blocoIdx 
                        ? { ...b, questoesSelecionadas: [...b.questoesSelecionadas, questao.id] }
                        : b
                    ));
                  }
                  setShowModalQuestoesExcedentes(false);
                  setQuestaoExcedenteInfo(null);
                }}
                className="flex-1 px-6 py-3 text-white rounded-lg font-semibold bg-orange-500 hover:bg-orange-600"
              >
                Usar até limite ({questaoExcedenteInfo.espacoDisponivel} questões)
              </button>
              <button 
                onClick={() => {
                  setShowModalQuestoesExcedentes(false);
                  setQuestaoExcedenteInfo(null);
                }}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Escolher outro texto
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalSegundoBloco && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Bloco 1 Completo!</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Você completou as {questoesPorBlocoSAEB(configSimulado.serie)} questões do <span className="font-bold text-purple-700">Bloco 1 - {componenteSimulado}</span>.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-semibold mb-2">
                    🔢 Deseja criar o Bloco 2 - {componenteSimulado}?
                  </p>
                  <p className="text-xs text-blue-700">
                    O Bloco 2 terá mais {questoesPorBlocoSAEB(configSimulado.serie)} questões de {componenteSimulado}, totalizando {questoesPorBlocoSAEB(configSimulado.serie) * 2} questões no simulado.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => {
                  setShowModalSegundoBloco(false);
                  // Muda para o bloco 2 e vai para a página do bloco 2
                  setBlocoAtivo(2);
                  setPaginaSimuladoAtiva(4);
                }}
                className="flex-1 px-6 py-3 text-white rounded-lg font-semibold" style={{backgroundColor: '#f7941d'}}
              >
                Sim, criar Bloco 2
              </button>
              <button 
                onClick={() => setShowModalSegundoBloco(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Não, apenas Bloco 1
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🏫 Modal de Segundo Bloco - ESCOLA */}
      {showModalSegundoBlocoEscola && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Bloco 1 Completo!</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Você completou as {questoesPorBlocoSAEB(filtrosEscola.serie)} questões do <span className="font-bold text-purple-700">Bloco 1 - {componenteSimuladoEscola}</span>.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-semibold mb-2">
                    🔢 Deseja criar o Bloco 2 - {componenteSimuladoEscola}?
                  </p>
                  <p className="text-xs text-blue-700">
                    O Bloco 2 terá mais {questoesPorBlocoSAEB(filtrosEscola.serie)} questões de {componenteSimuladoEscola}, totalizando {questoesPorBlocoSAEB(filtrosEscola.serie) * 2} questões no simulado.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => {
                  setShowModalSegundoBlocoEscola(false);
                  // Muda para o bloco 2 e vai para a página do bloco 2
                  setBlocoAtivoEscola(2);
                  setPaginaSimuladoAtivaEscola(4);
                }}
                className="flex-1 px-6 py-3 text-white rounded-lg font-semibold" style={{backgroundColor: '#f7941d'}}
              >
                Sim, criar Bloco 2
              </button>
              <button 
                onClick={() => setShowModalSegundoBlocoEscola(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Não, apenas Bloco 1
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Seleção de Alunos para Cartões Personalizados */}
      {showModalCartaoPersonalizado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <QrCode className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Gerar Cartões-Resposta</h3>
                <p className="text-gray-600 text-sm">
                  Selecione os alunos para gerar cartões-resposta personalizados com o nome de cada um.
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowModalCartaoPersonalizado(false);
                  setAlunosSelecionadosCartao([]);
                  setTurmaParaCartao(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Seleção de Turma */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Selecione a Turma:</label>
              <select 
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                value={turmaParaCartao || ''}
                onChange={(e) => {
                  setTurmaParaCartao(Number(e.target.value));
                  setAlunosSelecionadosCartao([]);
                }}
              >
                <option value="">Selecione uma turma</option>
                {turmas.map(turma => (
                  <option key={turma.id} value={turma.id}>{turma.nome} - {turma.turno}</option>
                ))}
              </select>
            </div>

            {/* Lista de Alunos */}
            {turmaParaCartao && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">
                    Alunos da turma: {turmas.find(t => t.id === turmaParaCartao)?.nome}
                  </label>
                  <button
                    onClick={() => {
                      const turma = turmas.find(t => t.id === turmaParaCartao);
                      if (turma) {
                        if (alunosSelecionadosCartao.length === turma.alunos.length) {
                          setAlunosSelecionadosCartao([]);
                        } else {
                          setAlunosSelecionadosCartao([...turma.alunos]);
                        }
                      }
                    }}
                    className="text-sm text-orange-600 hover:text-orange-700 font-semibold"
                  >
                    {alunosSelecionadosCartao.length === turmas.find(t => t.id === turmaParaCartao)?.alunos.length 
                      ? 'Desmarcar todos' 
                      : 'Selecionar todos'}
                  </button>
                </div>
                
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {turmas.find(t => t.id === turmaParaCartao)?.alunos.map((aluno, index) => (
                    <label key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={alunosSelecionadosCartao.includes(aluno)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAlunosSelecionadosCartao([...alunosSelecionadosCartao, aluno]);
                          } else {
                            setAlunosSelecionadosCartao(alunosSelecionadosCartao.filter(a => a !== aluno));
                          }
                        }}
                        className="w-5 h-5 rounded text-orange-600 focus:ring-orange-500"
                      />
                      <span className="flex-1 text-gray-700">{getNomeAluno(aluno)}</span>
                      {alunosSelecionadosCartao.includes(aluno) && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </label>
                  ))}
                </div>

                {alunosSelecionadosCartao.length > 0 && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <span className="font-bold">{alunosSelecionadosCartao.length}</span> aluno(s) selecionado(s) para cartões personalizados
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Opção de Cartão Genérico */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-semibold mb-1">Cartão Genérico</p>
                  <p className="text-xs text-blue-700">
                    Você também pode gerar um cartão genérico (sem nome do aluno) clicando em "Gerar Apenas Cartão Genérico".
                  </p>
                </div>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex gap-3">
              <button 
                onClick={() => {
                  console.log('🎯 Clicou em Gerar Cartões Personalizados');
                  // Gerar cartões personalizados
                  const turma = turmas.find(t => t.id === turmaParaCartao);
                  
                  // TODO: Integrar com geração real de PDF
                  window.alert('🚧 Funcionalidade em desenvolvimento!\n\nA geração de cartões personalizados será implementada em breve.\n\n📋 Alunos selecionados: ' + alunosSelecionadosCartao.length + '\n📝 Questões: ' + (blocosSimulado[0].questoesSelecionadas.length + blocosSimulado[1].questoesSelecionadas.length));
                  
                  // Fechar modal mas MANTER as questões selecionadas
                  setShowModalCartaoPersonalizado(false);
                  setAlunosSelecionadosCartao([]);
                  setTurmaParaCartao(null);
                }}
                disabled={alunosSelecionadosCartao.length === 0}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                  alunosSelecionadosCartao.length > 0 
                    ? 'text-white hover:opacity-90' 
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                style={alunosSelecionadosCartao.length > 0 ? {backgroundColor: '#f7941d'} : {}}
              >
                <Download className="w-5 h-5" />
                Gerar {alunosSelecionadosCartao.length} Cartões Personalizados
              </button>
              <button 
                onClick={() => {
                  console.log('🎯 Clicou em Gerar Cartão Genérico');
                  // TODO: Integrar com geração real de PDF
                  window.alert('🚧 Funcionalidade em desenvolvimento!\n\nA geração do Simulado SAEB será implementada em breve.\n\n📝 Questões selecionadas: ' + (blocosSimulado[0].questoesSelecionadas.length + blocosSimulado[1].questoesSelecionadas.length) + '\n\n💡 Suas questões foram mantidas. Você pode continuar editando.');
                  
                  // Fechar modal mas MANTER as questões selecionadas
                  setShowModalCartaoPersonalizado(false);
                  setAlunosSelecionadosCartao([]);
                  setTurmaParaCartao(null);
                }}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Gerar Apenas Cartão Genérico
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Edição/Criação de Recarga (Admin) */}
      {/* 🆕 Modal de Edição/Criação de Série (Admin) */}
      {showModalSerie && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {serieEditando ? 'Editar Série' : 'Nova Série'}
              </h3>
              <button onClick={() => setShowModalSerie(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nome da Série *</label>
                <input 
                  type="text" 
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Ex: 6º ano"
                  defaultValue={serieEditando?.nome || ''}
                  id="inputNomeSerie"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select 
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  defaultValue={serieEditando?.ativo ? 'ativo' : 'inativo'}
                  id="inputStatusSerie"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button 
                onClick={async () => {
                  const nome = document.getElementById('inputNomeSerie').value;
                  const status = document.getElementById('inputStatusSerie').value;
                  if (!nome.trim()) {
                    alert('⚠️ O nome da série é obrigatório!');
                    return;
                  }
                  
                  try {
                    if (firebaseReady && db) {
                      if (serieEditando) {
                        // Editar série existente
                        await db.collection('series').doc(serieEditando.id).update({ 
                          nome, 
                          ativo: status === 'ativo' 
                        });
                        setSeriesFirebase(prev => prev.map(s => 
                          s.id === serieEditando.id ? {...s, nome, ativo: status === 'ativo'} : s
                        ));
                      } else {
                        // Criar nova série
                        const novaSerie = { nome, ativo: status === 'ativo', dataCriacao: new Date() };
                        const docRef = await db.collection('series').add(novaSerie);
                        setSeriesFirebase(prev => [...prev, { id: docRef.id, ...novaSerie }]);
                      }
                      setShowModalSerie(false);
                      alert('✅ Série salva com sucesso!');
                    } else {
                      alert('⚠️ Firebase não disponível');
                    }
                  } catch (error) {
                    console.error('Erro ao salvar série:', error);
                    alert('❌ Erro ao salvar: ' + error.message);
                  }
                }}
                className="flex-1 px-6 py-3 rounded-lg font-semibold text-white hover:opacity-90"
                style={{backgroundColor: '#f7941d'}}
              >
                {serieEditando ? 'Salvar Alterações' : 'Criar Série'}
              </button>
              <button 
                onClick={() => setShowModalSerie(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Edição/Criação de Componente Curricular (Admin) */}
      {showModalComponente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {componenteEditando ? 'Editar Componente' : 'Novo Componente'}
              </h3>
              <button onClick={() => setShowModalComponente(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nome do Componente *</label>
                <input 
                  type="text" 
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Ex: Língua Inglesa"
                  defaultValue={componenteEditando?.nome || ''}
                  id="inputNomeComponente"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select 
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  defaultValue={componenteEditando?.ativo ? 'ativo' : 'inativo'}
                  id="inputStatusComponente"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button 
                onClick={async () => {
                  const nome = document.getElementById('inputNomeComponente').value;
                  const status = document.getElementById('inputStatusComponente').value;
                  if (!nome.trim()) {
                    alert('⚠️ O nome do componente é obrigatório!');
                    return;
                  }
                  
                  try {
                    if (firebaseReady && db) {
                      if (componenteEditando) {
                        // Editar componente existente
                        await db.collection('componentes').doc(componenteEditando.id).update({ 
                          nome, 
                          ativo: status === 'ativo' 
                        });
                        setComponentesFirebase(prev => prev.map(c => 
                          c.id === componenteEditando.id ? {...c, nome, ativo: status === 'ativo'} : c
                        ));
                      } else {
                        // Criar novo componente
                        const novoComp = { nome, ativo: status === 'ativo', dataCriacao: new Date() };
                        const docRef = await db.collection('componentes').add(novoComp);
                        setComponentesFirebase(prev => [...prev, { id: docRef.id, ...novoComp }]);
                      }
                      setShowModalComponente(false);
                      alert('✅ Componente salvo com sucesso!');
                    } else {
                      alert('⚠️ Firebase não disponível');
                    }
                  } catch (error) {
                    console.error('Erro ao salvar componente:', error);
                    alert('❌ Erro ao salvar: ' + error.message);
                  }
                }}
                className="flex-1 px-6 py-3 rounded-lg font-semibold text-white hover:opacity-90"
                style={{backgroundColor: '#f7941d'}}
              >
                {componenteEditando ? 'Salvar Alterações' : 'Criar Componente'}
              </button>
              <button 
                onClick={() => setShowModalComponente(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalRecarga && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-5 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {recargaEditando ? 'Editar Pacote' : 'Novo Pacote'}
              </h3>
              <button onClick={() => setShowModalRecarga(false)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Nome *</label>
                  <input 
                    type="text" 
                    id="recarga-nome"
                    className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="Ex: Premium"
                    defaultValue={recargaEditando?.nome || ''}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Valor (R$) *</label>
                  <input 
                    type="number" 
                    id="recarga-preco"
                    step="0.01"
                    className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="99,90"
                    defaultValue={recargaEditando?.preco || ''}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Descrição *</label>
                <textarea 
                  id="recarga-descricao"
                  className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 h-14 resize-none"
                  placeholder="Descreva os benefícios do pacote..."
                  defaultValue={recargaEditando?.descricao || ''}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Créditos *</label>
                  <input 
                    type="number" 
                    id="recarga-paginas"
                    className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="500"
                    defaultValue={recargaEditando?.paginas || ''}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Séries *</label>
                  <input 
                    type="number" 
                    id="recarga-series"
                    className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="1"
                    defaultValue={recargaEditando?.series || ''}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                  <select 
                    id="recarga-status"
                    className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                    defaultValue={recargaEditando?.ativo !== false ? 'ativo' : 'inativo'}
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                <input 
                  type="checkbox" 
                  id="recarga-popular"
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  defaultChecked={recargaEditando?.popular || false}
                />
                <label htmlFor="recarga-popular" className="text-xs font-medium text-gray-700">
                  Destacar como <strong>"MAIS POPULAR"</strong>
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Vantagens <span className="font-normal text-gray-500">(uma por linha)</span>
                </label>
                <textarea 
                  id="recarga-vantagens"
                  className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 h-16 resize-none"
                  placeholder="Suporte via WhatsApp&#10;Prioridade no atendimento"
                  defaultValue={recargaEditando?.vantagens?.join('\n') || ''}
                />
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white px-5 py-3 border-t flex gap-2">
              <button 
                onClick={() => {
                  const nome = document.getElementById('recarga-nome').value;
                  const descricao = document.getElementById('recarga-descricao').value;
                  const preco = parseFloat(document.getElementById('recarga-preco').value);
                  const paginas = parseInt(document.getElementById('recarga-paginas').value);
                  const series = parseInt(document.getElementById('recarga-series').value);
                  const ativo = document.getElementById('recarga-status').value === 'ativo';
                  const popular = document.getElementById('recarga-popular').checked;
                  const vantagensText = document.getElementById('recarga-vantagens').value;
                  const vantagens = vantagensText.split('\n').filter(v => v.trim() !== '');

                  if (!nome || !descricao || !preco || !paginas || !series) {
                    alert('Preencha todos os campos obrigatórios!');
                    return;
                  }

                  // Salvar no Firebase
                  const dadosRecarga = { nome, descricao, preco, paginas, series, ativo, popular, vantagens };
                  
                  if (recargaEditando) {
                    // Verificar se é um ID numérico (recarga local) ou string (já no Firebase)
                    const isIdLocal = typeof recargaEditando.id === 'number';
                    const docId = isIdLocal ? 'rec_' + recargaEditando.id : String(recargaEditando.id);
                    
                    if (firebaseReady && db) {
                      db.collection('recargas').doc(docId).set(dadosRecarga)
                        .then(() => {
                          console.log('✅ Recarga salva no Firebase:', docId);
                          alert('✅ Pacote salvo com sucesso!');
                        })
                        .catch(err => {
                          console.error('Erro ao salvar:', err);
                          alert('❌ Erro ao salvar. Tente novamente.');
                        });
                    }
                    // Atualizar localmente com o novo ID se era local
                    setRecargas(recargas.map(r => r.id === recargaEditando.id ? 
                      { ...r, id: isIdLocal ? docId : r.id, ...dadosRecarga } : r
                    ));
                  } else {
                    // Criar nova
                    const novoId = 'rec_' + Date.now();
                    if (firebaseReady && db) {
                      db.collection('recargas').doc(novoId).set(dadosRecarga)
                        .then(() => {
                          console.log('✅ Recarga criada no Firebase:', novoId);
                          alert('✅ Pacote criado com sucesso!');
                        })
                        .catch(err => {
                          console.error('Erro ao criar:', err);
                          alert('❌ Erro ao criar. Tente novamente.');
                        });
                    }
                    setRecargas([...recargas, { id: novoId, ...dadosRecarga }]);
                  }
                  setShowModalRecarga(false);
                }}
                className="flex-1 px-4 py-2 rounded-lg font-semibold text-white text-sm hover:opacity-90"
                style={{backgroundColor: '#f7941d'}}
              >
                {recargaEditando ? 'Salvar' : 'Criar'}
              </button>
              <button 
                onClick={() => setShowModalRecarga(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Cadastro/Edição de Membro da Equipe (Admin) */}
      {showModalEquipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {membroEditando ? 'Editar Membro' : 'Novo Membro'}
              </h3>
              <button onClick={() => setShowModalEquipe(false)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nome Completo *</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Nome do membro"
                  defaultValue={membroEditando?.nome || ''}
                  id="membroNome"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
                  <input 
                    type="email" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="email@exemplo.com"
                    defaultValue={membroEditando?.email || ''}
                    id="membroEmail"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Telefone *</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="(00) 00000-0000"
                    defaultValue={membroEditando?.telefone || ''}
                    id="membroTelefone"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Função *</label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg text-sm" 
                    defaultValue={membroEditando?.funcao || 'Comercial'} 
                    id="membroFuncao"
                  >
                    <option value="Comercial">📊 Comercial</option>
                    <option value="Suporte">🎧 Suporte</option>
                    <option value="Administrativo">⚙️ Administrativo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg text-sm" 
                    defaultValue={membroEditando?.status || 'ativo'} 
                    id="membroStatus"
                  >
                    <option value="ativo">✅ Ativo</option>
                    <option value="inativo">❌ Inativo</option>
                  </select>
                </div>
              </div>
              {/* Descrição da função */}
              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                <p className="font-semibold mb-1">Permissões por função:</p>
                <p><span className="text-blue-600 font-semibold">Comercial:</span> Cadastra novos clientes</p>
                <p><span className="text-purple-600 font-semibold">Suporte:</span> Edita dados de clientes (sem excluir)</p>
              </div>
            </div>
            <div className="border-t border-gray-200 px-5 py-3 flex gap-3">
              <button onClick={() => setShowModalEquipe(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
                Cancelar
              </button>
              <button 
                onClick={() => {
                  const nome = document.getElementById('membroNome').value;
                  const email = document.getElementById('membroEmail').value;
                  const telefone = document.getElementById('membroTelefone').value;
                  const funcao = document.getElementById('membroFuncao').value;
                  const status = document.getElementById('membroStatus').value;
                  
                  if (!nome || !email || !telefone) {
                    alert('⚠️ Preencha os campos obrigatórios!');
                    return;
                  }
                  
                  if (membroEditando) {
                    setEquipe(equipe.map(m => m.id === membroEditando.id ? {...m, nome, email, telefone, funcao, status} : m));
                    alert('✅ Membro atualizado!');
                  } else {
                    // Gerar token de convite
                    const token = Math.random().toString(36).substring(2, 15);
                    setEquipe([...equipe, {
                      id: Date.now(),
                      nome,
                      email,
                      telefone,
                      funcao,
                      status: 'pendente',
                      dataCadastro: new Date().toISOString().split('T')[0],
                      token
                    }]);
                    // Adicionar aos convites pendentes
                    setConvitesPendentes(prev => [...prev, {
                      id: Date.now(),
                      tipo: 'membro',
                      email,
                      nome,
                      funcao,
                      token,
                      dataEnvio: new Date().toISOString().split('T')[0]
                    }]);
                    alert(`✅ Membro cadastrado!\n\n📧 Convite será enviado para: ${email}\n\n🔗 Link de ativação (simulado):\nhttps://facilitaprof.com/ativar/${token}\n\nO membro receberá um email para confirmar o cadastro e criar sua senha de acesso.\n\n⏳ Status: Pendente (aguardando confirmação)`);
                  }
                  setShowModalEquipe(false);
                }}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
              >
                {membroEditando ? 'Salvar' : 'Cadastrar e Enviar Convite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Detalhes do Membro (Admin) */}
      {showModalDetalhesMembro && membroDetalhes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between rounded-t-xl">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Detalhes: {membroDetalhes.nome}</h3>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  membroDetalhes.funcao === 'Comercial' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {membroDetalhes.funcao}
                </span>
              </div>
              <button onClick={() => setShowModalDetalhesMembro(false)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5">
              {/* Filtro por mês */}
              <div className="flex items-center gap-3 mb-4">
                <label className="text-sm font-semibold text-gray-600">Filtrar por mês:</label>
                <select 
                  className="px-3 py-1.5 border rounded-lg text-sm"
                  value={filtroMesMembro}
                  onChange={(e) => setFiltroMesMembro(e.target.value)}
                >
                  <option value="">Todos os meses</option>
                  <option value="01">Janeiro</option>
                  <option value="02">Fevereiro</option>
                  <option value="03">Março</option>
                  <option value="04">Abril</option>
                  <option value="05">Maio</option>
                  <option value="06">Junho</option>
                  <option value="07">Julho</option>
                  <option value="08">Agosto</option>
                  <option value="09">Setembro</option>
                  <option value="10">Outubro</option>
                  <option value="11">Novembro</option>
                  <option value="12">Dezembro</option>
                </select>
              </div>

              {membroDetalhes.funcao === 'Comercial' ? (
                <>
                  {/* Estatísticas Comercial */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {clientes.filter(c => c.cadastradoPor === membroDetalhes.id && 
                          (!filtroMesMembro || c.dataCadastro.substring(5, 7) === filtroMesMembro)
                        ).length}
                      </div>
                      <div className="text-xs text-blue-700">Clientes Cadastrados</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {clientes.filter(c => c.cadastradoPor === membroDetalhes.id && (c.tipo === 'juridica' || c.tipo === 'escola') &&
                          (!filtroMesMembro || c.dataCadastro.substring(5, 7) === filtroMesMembro)
                        ).length}
                      </div>
                      <div className="text-xs text-green-700">Pessoa Jurídica</div>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {clientes.filter(c => c.cadastradoPor === membroDetalhes.id && (c.tipo === 'fisica' || c.tipo === 'professor') &&
                          (!filtroMesMembro || c.dataCadastro.substring(5, 7) === filtroMesMembro)
                        ).length}
                      </div>
                      <div className="text-xs text-orange-700">Pessoa Física</div>
                    </div>
                  </div>
                  {/* Lista de clientes cadastrados */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Cliente</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Tipo</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Data Cadastro</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Plano</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientes
                          .filter(c => c.cadastradoPor === membroDetalhes.id && 
                            (!filtroMesMembro || c.dataCadastro.substring(5, 7) === filtroMesMembro))
                          .map(c => (
                            <tr key={c.id} className="border-t hover:bg-gray-50">
                              <td className="py-2 px-3 font-semibold">{c.nome}</td>
                              <td className="py-2 px-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  (c.tipo === 'juridica' || c.tipo === 'escola') ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {(c.tipo === 'juridica' || c.tipo === 'escola') ? '🏫 PJ' : '👤 PF'}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-gray-600">{new Date(c.dataCadastro).toLocaleDateString('pt-BR')}</td>
                              <td className="py-2 px-3">{c.plano}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {clientes.filter(c => c.cadastradoPor === membroDetalhes.id && 
                      (!filtroMesMembro || c.dataCadastro.substring(5, 7) === filtroMesMembro)).length === 0 && (
                      <div className="text-center py-8 text-gray-500 text-sm">Nenhum cliente cadastrado neste período</div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Estatísticas Suporte */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-purple-50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {clientes.reduce((acc, c) => 
                          acc + (c.atendimentos?.filter(a => a.membroId === membroDetalhes.id &&
                            (!filtroMesMembro || a.data.substring(5, 7) === filtroMesMembro)
                          ).length || 0), 0)}
                      </div>
                      <div className="text-xs text-purple-700">Atendimentos Realizados</div>
                    </div>
                    <div className="bg-cyan-50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-cyan-600">
                        {new Set(clientes.filter(c => 
                          c.atendimentos?.some(a => a.membroId === membroDetalhes.id &&
                            (!filtroMesMembro || a.data.substring(5, 7) === filtroMesMembro))
                        ).map(c => c.id)).size}
                      </div>
                      <div className="text-xs text-cyan-700">Clientes Atendidos</div>
                    </div>
                  </div>
                  {/* Lista de atendimentos */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Cliente</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Data Atendimento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientes.flatMap(c => 
                          (c.atendimentos || [])
                            .filter(a => a.membroId === membroDetalhes.id &&
                              (!filtroMesMembro || a.data.substring(5, 7) === filtroMesMembro))
                            .map((a, idx) => ({cliente: c, atendimento: a, key: `${c.id}-${idx}`}))
                        ).map(item => (
                          <tr key={item.key} className="border-t hover:bg-gray-50">
                            <td className="py-2 px-3 font-semibold">{item.cliente.nome}</td>
                            <td className="py-2 px-3 text-gray-600">{new Date(item.atendimento.data).toLocaleDateString('pt-BR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {clientes.flatMap(c => 
                      (c.atendimentos || []).filter(a => a.membroId === membroDetalhes.id &&
                        (!filtroMesMembro || a.data.substring(5, 7) === filtroMesMembro))
                    ).length === 0 && (
                      <div className="text-center py-8 text-gray-500 text-sm">Nenhum atendimento neste período</div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="border-t border-gray-200 px-5 py-3">
              <button onClick={() => setShowModalDetalhesMembro(false)} className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Edição/Cadastro de Cliente (Admin) */}
      {showModalCliente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold text-gray-900">
                {clienteEditando ? 'Editar Cliente' : 'Novo Cliente'}
              </h3>
              <button onClick={() => setShowModalCliente(false)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Nome e Tipo em linha */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Nome *</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Nome do cliente"
                    defaultValue={clienteEditando?.nome || ''}
                    id="clienteNome"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo *</label>
                  <select className="w-full px-3 py-2 border rounded-lg text-sm" defaultValue={(clienteEditando?.tipo === 'escola' || clienteEditando?.tipo === 'juridica') ? 'juridica' : 'fisica'} id="clienteTipo">
                    <option value="fisica">👤 Pessoa Física</option>
                    <option value="juridica">🏫 Pessoa Jurídica</option>
                  </select>
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Email * {clienteEditando && <span className="text-gray-400 font-normal">(não editável)</span>}
                </label>
                {clienteEditando ? (
                  <div className="w-full px-3 py-2 border rounded-lg text-sm bg-gray-100 text-gray-600">
                    {clienteEditando?.email || 'Não informado'}
                  </div>
                ) : (
                  <input 
                    type="email" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="email@exemplo.com"
                    id="clienteEmail"
                  />
                )}
                {!clienteEditando && (
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Senha padrão: <strong>123456</strong> (o cliente poderá alterar após o primeiro login)
                  </p>
                )}
              </div>

              {/* Escola (para pessoa física) */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Escola Vinculada</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Nome da escola (opcional)"
                  defaultValue={clienteEditando?.escola || ''}
                  id="clienteEscola"
                />
              </div>

              {/* Telefone e Plano em linha */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Telefone *</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="(00) 00000-0000"
                    defaultValue={clienteEditando?.telefone || clienteEditando?.telefoneVerificado || ''}
                    id="clienteTelefone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Plano *</label>
                  <select className="w-full px-3 py-2 border rounded-lg text-sm" defaultValue={clienteEditando?.plano || 'Básico'} id="clientePlano">
                    <option value="Básico">Básico</option>
                    <option value="Profissional">Profissional</option>
                    <option value="Premium">Premium</option>
                  </select>
                </div>
              </div>

              {/* Créditos iniciais (apenas novo cadastro) */}
              {!clienteEditando && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Créditos Iniciais</label>
                  <input 
                    type="number" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="10"
                    defaultValue="10"
                    id="clienteCreditos"
                  />
                </div>
              )}

              {/* Séries Liberadas */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">
                  Séries Liberadas
                  <span className="ml-2 text-gray-400 font-normal">
                    ({clienteEditando ? clienteEditandoSeries.length : 0} selecionadas)
                  </span>
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {seriesDisponiveis.filter(s => s.ativo).map(serie => {
                    const selecionada = clienteEditando 
                      ? clienteEditandoSeries.includes(serie.nome)
                      : false;
                    return (
                      <button
                        key={serie.id}
                        onClick={() => {
                          if (clienteEditando) {
                            if (selecionada) {
                              setClienteEditandoSeries(clienteEditandoSeries.filter(s => s !== serie.nome));
                            } else {
                              setClienteEditandoSeries([...clienteEditandoSeries, serie.nome]);
                            }
                          }
                        }}
                        className={`p-2 border-2 rounded-lg text-center transition ${
                          selecionada
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-xs font-semibold">{serie.nome}</div>
                        {selecionada && <CheckCircle className="w-3 h-3 mx-auto mt-1 text-green-500" />}
                      </button>
                    );
                  })}
                </div>
                {clienteEditando && (
                  <div className="flex gap-2 mt-2">
                    <button 
                      onClick={() => setClienteEditandoSeries(seriesDisponiveis.filter(s => s.ativo).map(s => s.nome))}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Selecionar todas
                    </button>
                    <span className="text-gray-300">|</span>
                    <button 
                      onClick={() => setClienteEditandoSeries([])}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Remover todas
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex gap-3 rounded-b-xl">
              <button onClick={() => setShowModalCliente(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
                Cancelar
              </button>
              <button 
                onClick={async () => {
                  const nome = document.getElementById('clienteNome').value;
                  const tipo = document.getElementById('clienteTipo').value;
                  const telefone = document.getElementById('clienteTelefone').value;
                  const plano = document.getElementById('clientePlano').value;
                  const escola = document.getElementById('clienteEscola').value;
                  
                  if (!nome || !telefone) {
                    alert('⚠️ Preencha os campos obrigatórios!');
                    return;
                  }
                  
                  if (clienteEditando) {
                    // Atualizar cliente existente
                    const tipoFirestore = tipo === 'juridica' ? 'escola' : 'professor';
                    const clienteAtualizado = {
                      ...clienteEditando, 
                      nome, 
                      tipo: tipoFirestore, 
                      telefone, 
                      plano,
                      escola,
                      seriesLiberadas: clienteEditandoSeries
                    };
                    
                    setClientes(clientes.map(c => c.id === clienteEditando.id ? clienteAtualizado : c));
                    
                    // Atualizar no Firebase
                    if (db && clienteEditando.id) {
                      try {
                        await db.collection('users').doc(clienteEditando.id).update({
                          nome,
                          tipo: tipoFirestore,
                          telefone,
                          plano,
                          escola,
                          seriesLiberadas: clienteEditandoSeries
                        });
                        console.log('✅ Cliente atualizado no Firebase');
                      } catch (error) {
                        console.error('Erro ao atualizar no Firebase:', error);
                      }
                    }
                    
                    alert('✅ Cliente atualizado!');
                  } else {
                    // Novo cliente
                    const email = document.getElementById('clienteEmail')?.value;
                    if (!email) {
                      alert('⚠️ O email é obrigatório para novos clientes!');
                      return;
                    }
                    
                    const creditos = parseInt(document.getElementById('clienteCreditos').value) || 10;
                    const senhasPadrao = '123456';
                    
                    const novoCliente = {
                      id: Date.now().toString(),
                      nome,
                      email,
                      tipo,
                      telefone,
                      plano,
                      escola,
                      creditos,
                      dataCadastro: new Date().toISOString().split('T')[0],
                      seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'],
                      cadastradoPorAdmin: true
                    };
                    
                    // Criar no Firebase
                    if (db && auth) {
                      try {
                        // Criar usuário no Firebase Auth
                        const userCredential = await auth.createUserWithEmailAndPassword(email, senhasPadrao);
                        const uid = userCredential.user.uid;
                        
                        // Salvar dados no Firestore
                        await db.collection('users').doc(uid).set({
                          nome,
                          email,
                          tipo: tipo === 'juridica' ? 'escola' : 'professor',
                          telefone,
                          plano,
                          escola,
                          creditos,
                          dataCadastro: new Date().toISOString(),
                          seriesLiberadas: ['1º ano', '2º ano', '3º ano', '4º ano', '5º ano', '6º ano', '7º ano', '8º ano', '9º ano'],
                          cadastradoPorAdmin: true,
                          senhaTemporaria: true
                        });
                        
                        novoCliente.id = uid;
                        console.log('✅ Cliente criado no Firebase com ID:', uid);
                        alert('✅ Cliente cadastrado com sucesso!\n\n📧 Email: ' + email + '\n🔑 Senha temporária: 123456\n\n💡 Oriente o cliente a alterar a senha no primeiro acesso.');
                      } catch (error) {
                        console.error('Erro ao criar cliente no Firebase:', error);
                        if (error.code === 'auth/email-already-in-use') {
                          alert('⚠️ Este email já está cadastrado no sistema!');
                          return;
                        }
                        alert('⚠️ Erro ao criar cliente: ' + error.message);
                        return;
                      }
                    } else {
                      alert('✅ Cliente cadastrado localmente!\n\n⚠️ Firebase não configurado - cliente não foi salvo no banco de dados.');
                    }
                    
                    setClientes([...clientes, novoCliente]);
                  }
                  setShowModalCliente(false);
                }}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
              >
                {clienteEditando ? 'Salvar Alterações' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Gerenciamento de Créditos (Admin) - Compacto */}
      {showModalCreditos && clienteCreditos && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm">
            <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-900">Gerenciar Créditos</h3>
                <p className="text-xs text-gray-500">{clienteCreditos.nome}</p>
              </div>
              <button onClick={() => setShowModalCreditos(false)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <div className="bg-blue-50 rounded-lg p-3 mb-4 text-center">
                <div className="text-xs text-blue-600">Saldo Atual</div>
                <div className="text-2xl font-bold text-blue-700">{clienteCreditos.creditos.toLocaleString()}</div>
              </div>
              
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setOperacaoCreditos({...operacaoCreditos, tipo: 'adicionar'})}
                    className={`p-2 border-2 rounded-lg text-center transition ${
                      operacaoCreditos.tipo === 'adicionar' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <Plus className="w-4 h-4 mx-auto text-green-600" />
                    <span className="text-xs font-semibold">Adicionar</span>
                  </button>
                  <button
                    onClick={() => setOperacaoCreditos({...operacaoCreditos, tipo: 'retirar'})}
                    className={`p-2 border-2 rounded-lg text-center transition ${
                      operacaoCreditos.tipo === 'retirar' ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    }`}
                  >
                    <Minus className="w-4 h-4 mx-auto text-red-600" />
                    <span className="text-xs font-semibold">Retirar</span>
                  </button>
                </div>
                
                <input 
                  type="number" 
                  className="w-full px-3 py-2 border rounded-lg text-center font-bold"
                  placeholder="Quantidade"
                  min="0"
                  value={operacaoCreditos.quantidade || ''}
                  onChange={(e) => setOperacaoCreditos({...operacaoCreditos, quantidade: parseInt(e.target.value) || 0})}
                />
                
                {operacaoCreditos.quantidade > 0 && (
                  <div className={`p-2 rounded-lg text-center ${operacaoCreditos.tipo === 'adicionar' ? 'bg-green-50' : 'bg-red-50'}`}>
                    <span className="text-xs text-gray-600">Novo saldo: </span>
                    <span className={`font-bold ${operacaoCreditos.tipo === 'adicionar' ? 'text-green-600' : 'text-red-600'}`}>
                      {operacaoCreditos.tipo === 'adicionar' 
                        ? (clienteCreditos.creditos + operacaoCreditos.quantidade).toLocaleString()
                        : Math.max(0, clienteCreditos.creditos - operacaoCreditos.quantidade).toLocaleString()
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-gray-200 px-4 py-3 flex gap-2">
              <button onClick={() => setShowModalCreditos(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold">
                Cancelar
              </button>
              <button 
                onClick={async () => {
                  if (operacaoCreditos.quantidade <= 0) {
                    alert('⚠️ Informe uma quantidade válida!');
                    return;
                  }
                  const novoSaldo = operacaoCreditos.tipo === 'adicionar'
                    ? clienteCreditos.creditos + operacaoCreditos.quantidade
                    : Math.max(0, clienteCreditos.creditos - operacaoCreditos.quantidade);
                  
                  // Atualizar estado local
                  setClientes(clientes.map(c => c.id === clienteCreditos.id ? {...c, creditos: novoSaldo} : c));
                  
                  // 🔥 Salvar no Firebase
                  if (db && clienteCreditos.id) {
                    try {
                      await db.collection('users').doc(clienteCreditos.id).update({
                        creditos: novoSaldo
                      });
                      console.log('✅ Créditos atualizados no Firebase:', clienteCreditos.id, novoSaldo);
                    } catch (error) {
                      console.error('❌ Erro ao atualizar créditos no Firebase:', error);
                    }
                  }
                  
                  alert(`✅ ${operacaoCreditos.tipo === 'adicionar' ? 'Adicionados' : 'Retirados'} ${operacaoCreditos.quantidade} créditos!`);
                  setShowModalCreditos(false);
                }}
                disabled={operacaoCreditos.quantidade <= 0}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                  operacaoCreditos.quantidade > 0
                    ? operacaoCreditos.tipo === 'adicionar' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Upload de Questão (Admin) */}
      {showModalUploadQuestao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between rounded-t-xl">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Upload de Questão</h3>
                <p className="text-xs text-gray-500">Preencha os dados e faça o upload da imagem</p>
              </div>
              <button 
                onClick={() => setShowModalUploadQuestao(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5">
              {/* Módulo de Destino - linha única */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-2">Módulo de Destino *</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setUploadQuestao({...uploadQuestao, modulo: 'prontas', serie: '', componente: '', habilidade: ''})}
                    className={`p-2.5 border-2 rounded-lg flex items-center gap-2 transition ${
                      uploadQuestao.modulo === 'prontas' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-xl">📚</span>
                    <span className="text-sm font-semibold">Atividades Prontas</span>
                  </button>
                  <button
                    onClick={() => setUploadQuestao({...uploadQuestao, modulo: 'questao_avaliacao', serie: '', componente: '', habilidade: ''})}
                    className={`p-2.5 border-2 rounded-lg flex items-center gap-2 transition ${
                      uploadQuestao.modulo === 'questao_avaliacao' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-xl">📝</span>
                    <span className="text-sm font-semibold">Atividade / Avaliação</span>
                  </button>
                  <button
                    onClick={() => setUploadQuestao({...uploadQuestao, modulo: 'simulado', serie: '', componente: '', habilidade: ''})}
                    className={`p-2.5 border-2 rounded-lg flex items-center gap-2 transition ${
                      uploadQuestao.modulo === 'simulado' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-xl">🎯</span>
                    <span className="text-sm font-semibold">Simulado SAEB</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-4">
                {/* Coluna 1-3: Filtros */}
                <div className="col-span-2 space-y-3">
                  {/* Série e Componente em linha */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Série *</label>
                      <select 
                        className="w-full px-2 py-1.5 border rounded-lg text-sm"
                        value={uploadQuestao.serie}
                        onChange={(e) => setUploadQuestao({...uploadQuestao, serie: e.target.value, componente: '', habilidade: ''})}
                        disabled={!uploadQuestao.modulo}
                      >
                        <option value="">Selecione</option>
                        {uploadQuestao.modulo === 'simulado' ? (
                          <>
                            <option value="2º ano">2º ano</option>
                            <option value="5º ano">5º ano</option>
                            <option value="9º ano">9º ano</option>
                          </>
                        ) : (
                          seriesDisponiveis.filter(s => s.ativo).map(s => (
                            <option key={s.id} value={s.nome}>{s.nome}</option>
                          ))
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Componente *</label>
                      <select 
                        className="w-full px-2 py-1.5 border rounded-lg text-sm"
                        value={uploadQuestao.componente}
                        onChange={(e) => setUploadQuestao({...uploadQuestao, componente: e.target.value, habilidade: '', tipoQuestao: 'habilidade-unica'})}
                        disabled={!uploadQuestao.serie}
                      >
                        <option value="">Selecione</option>
                        {uploadQuestao.modulo === 'simulado' ? (
                          <>
                            <option value="Língua Portuguesa SAEB">Língua Portuguesa</option>
                            <option value="Matemática SAEB">Matemática</option>
                          </>
                        ) : (
                          componentesCurriculares.filter(c => c.ativo && !c.nome.includes('SAEB')).map(c => (
                            <option key={c.id} value={c.nome}>{c.nome}</option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Seletor de Tipo de Questão - apenas para Simulado SAEB + Língua Portuguesa */}
                  {uploadQuestao.modulo === 'simulado' && uploadQuestao.componente === 'Língua Portuguesa SAEB' && (
                    <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                      <label className="block text-xs font-semibold text-purple-700 mb-2">Tipo de Questão</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setUploadQuestao({...uploadQuestao, tipoQuestao: 'habilidade-unica', tamanho: ''})}
                          className={`flex-1 py-2 px-3 rounded-lg font-semibold text-xs transition flex items-center justify-center gap-1 ${
                            (!uploadQuestao.tipoQuestao || uploadQuestao.tipoQuestao === 'habilidade-unica')
                              ? 'bg-purple-500 text-white shadow-md'
                              : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-50'
                          }`}
                        >
                          <Target className="w-3 h-3" />
                          Habilidade Única
                        </button>
                        <button
                          onClick={() => {
                            setShowModalUploadQuestao(false);
                            setUploadTextoMultiplo({
                              serie: uploadQuestao.serie,
                              textoImagem: null,
                              questoesVinculadas: [
                                { id: 1, habilidade: '', gabarito: '', imagem: null },
                                { id: 2, habilidade: '', gabarito: '', imagem: null }
                              ]
                            });
                            setShowModalTextoMultiplo(true);
                          }}
                          className={`flex-1 py-2 px-3 rounded-lg font-semibold text-xs transition flex items-center justify-center gap-1 ${
                            uploadQuestao.tipoQuestao === 'texto-multiplas'
                              ? 'bg-orange-500 text-white shadow-md'
                              : 'bg-white text-orange-700 border border-orange-300 hover:bg-orange-50'
                          }`}
                        >
                          <Layers className="w-3 h-3" />
                          Texto c/ Habilidades Variadas
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Habilidade - só aparece se for Habilidade Única */}
                  {(!uploadQuestao.modulo || uploadQuestao.modulo !== 'simulado' || uploadQuestao.componente !== 'Língua Portuguesa SAEB' || !uploadQuestao.tipoQuestao || uploadQuestao.tipoQuestao === 'habilidade-unica') && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Habilidade/Conteúdo *</label>
                    <select 
                      className="w-full px-2 py-1.5 border rounded-lg text-sm"
                      value={uploadQuestao.habilidadeId || ''}
                      onChange={(e) => {
                        const habId = e.target.value;
                        const habSelecionada = habilidades.find(h => h.id === habId);
                        setUploadQuestao({
                          ...uploadQuestao, 
                          habilidadeId: habId,
                          habilidade: habSelecionada?.codigo || '',
                          conteudo: habSelecionada?.conteudo || ''
                        });
                      }}
                      disabled={!uploadQuestao.componente}
                    >
                      <option value="">Selecione a habilidade/conteúdo</option>
                      {habilidades
                        .filter(h => h.serie === uploadQuestao.serie && h.componente === uploadQuestao.componente)
                        .map(h => (
                          <option key={h.id} value={h.id}>
                            {h.codigo} - {h.conteudo}
                          </option>
                        ))}
                    </select>
                  </div>
                  )}

                  {/* Tamanho - só aparece se for Habilidade Única ou outros módulos */}
                  {(!uploadQuestao.modulo || uploadQuestao.modulo !== 'simulado' || uploadQuestao.componente !== 'Língua Portuguesa SAEB' || !uploadQuestao.tipoQuestao || uploadQuestao.tipoQuestao === 'habilidade-unica') && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Tamanho *</label>
                    <div className="grid gap-1.5 grid-cols-4">
                      {[
                        { value: 'pequena', label: 'P', desc: '5cm' },
                        { value: 'media', label: 'M', desc: '7cm' },
                        { value: 'grande', label: 'G', desc: '10.5cm' },
                        { value: 'inteira', label: 'I', desc: '22cm' }
                      ].map(t => (
                        <button
                          key={t.value}
                          onClick={() => setUploadQuestao({...uploadQuestao, tamanho: t.value})}
                          className={`p-1.5 border-2 rounded-lg text-center transition ${
                            uploadQuestao.tamanho === t.value 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-xs font-bold">{t.label}</div>
                          <div className="text-xs text-gray-500">{t.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  )}
                </div>

                {/* Coluna 4-5: Upload e Gabarito */}
                <div className="col-span-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Imagem da Questão *</label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-4 text-center transition cursor-pointer h-48 flex flex-col items-center justify-center ${
                      uploadQuestao.imagem ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    onClick={() => document.getElementById('inputUploadQuestao').click()}
                  >
                    <input 
                      type="file" 
                      id="inputUploadQuestao"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setUploadQuestao({...uploadQuestao, imagem: ev.target.result, imagemFile: file});
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    {uploadQuestao.imagem ? (
                      <div className="flex flex-col items-center">
                        <img src={uploadQuestao.imagem} alt="Preview" className="max-h-32 rounded-lg mb-2" />
                        <p className="text-xs text-green-600 font-semibold">✅ Imagem carregada</p>
                        <button onClick={(e) => { e.stopPropagation(); setUploadQuestao({...uploadQuestao, imagem: null, imagemFile: null}); }} className="text-xs text-red-500 hover:underline">Remover</button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Clique para upload</p>
                        <p className="text-xs text-gray-400">PNG, JPG ou WEBP</p>
                      </div>
                    )}
                  </div>

                  {/* Gabarito - abaixo do upload */}
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Gabarito *</label>
                    <div className="flex gap-2">
                      {['A', 'B', 'C', 'D'].map(letra => (
                        <button
                          key={letra}
                          onClick={() => setUploadQuestao({...uploadQuestao, gabarito: letra})}
                          className={`w-10 h-10 rounded-lg font-bold transition ${
                            uploadQuestao.gabarito === letra ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {letra}
                        </button>
                      ))}
                      {/* Botão para limpar gabarito */}
                      <button
                        onClick={() => setUploadQuestao({...uploadQuestao, gabarito: ''})}
                        className={`w-10 h-10 rounded-lg font-bold transition ${
                          !uploadQuestao.gabarito ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="Sem gabarito (questão aberta)"
                      >
                        -
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Deixe "-" para questões abertas (pintar, desenhar, escrever)</p>
                  </div>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 mt-5 pt-4 border-t">
                <button 
                  onClick={() => setShowModalUploadQuestao(false)}
                  className="flex-1 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button 
                  onClick={async () => {
                    // Gabarito NÃO é mais obrigatório
                    if (!uploadQuestao.modulo || !uploadQuestao.serie || !uploadQuestao.componente || !uploadQuestao.habilidade || !uploadQuestao.imagem) {
                      alert('⚠️ Preencha todos os campos obrigatórios!');
                      return;
                    }
                    
                    // Mostrar loading
                    const btn = document.getElementById('btnSalvarQuestao');
                    if (btn) {
                      btn.disabled = true;
                      btn.innerHTML = '⏳ Salvando...';
                    }
                    
                    try {
                      // Fazer upload da imagem para o Firebase Storage
                      let imagemUrl = uploadQuestao.imagem;
                      
                      if (uploadQuestao.imagemFile && firebaseReady && storage) {
                        const nomeArquivo = `questoes/${Date.now()}_${uploadQuestao.imagemFile.name}`;
                        const ref = storage.ref(nomeArquivo);
                        await ref.put(uploadQuestao.imagemFile);
                        imagemUrl = await ref.getDownloadURL();
                        console.log('✅ Imagem enviada para Storage:', imagemUrl);
                      }
                      
                      // Salvar questão no Firestore - gabarito pode ser vazio
                      const novaQuestao = {
                        modulo: uploadQuestao.modulo,
                        serie: uploadQuestao.serie,
                        componente: uploadQuestao.componente,
                        habilidade: uploadQuestao.habilidade,
                        habilidadeId: uploadQuestao.habilidadeId || '', // 🆕 ID único
                        conteudo: uploadQuestao.conteudo || '', // 🆕 Conteúdo
                        tamanho: uploadQuestao.tamanho || 'media',
                        gabarito: uploadQuestao.gabarito || '-', // "-" se não tiver gabarito
                        imagem: imagemUrl,
                        dataCriacao: new Date(),
                        ativo: true
                      };
                      
                      if (firebaseReady && db) {
                        const docRef = await db.collection('questoes').add(novaQuestao);
                        console.log('✅ Questão salva no Firestore:', docRef.id);
                        // v658: Invalidar cache após upload
                        _invalidarCache();
                        // v660: Forçar reload da lista
                        setReloadTrigger(prev => prev + 1);
                        alert('✅ Questão salva com sucesso no Firebase!');
                      } else {
                        console.log('⚠️ Firebase não disponível, questão salva apenas localmente');
                        alert('✅ Questão adicionada! (modo local)');
                      }
                      
                      setShowModalUploadQuestao(false);
                      setUploadQuestao({ modulo: '', serie: '', componente: '', habilidade: '', habilidadeId: '', conteudo: '', tamanho: 'media', gabarito: '', imagem: null, imagemFile: null });
                    } catch (error) {
                      console.error('❌ Erro ao salvar questão:', error);
                      alert('❌ Erro ao salvar questão: ' + error.message);
                    } finally {
                      if (btn) {
                        btn.disabled = false;
                        btn.innerHTML = '<svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>Salvar Questão';
                      }
                    }
                  }}
                  id="btnSalvarQuestao"
                  disabled={!uploadQuestao.modulo || !uploadQuestao.serie || !uploadQuestao.componente || !uploadQuestao.habilidadeId || !uploadQuestao.gabarito || !uploadQuestao.imagem}
                  className={`flex-1 py-2 rounded-lg font-semibold transition ${
                    uploadQuestao.modulo && uploadQuestao.serie && uploadQuestao.componente && uploadQuestao.habilidadeId && uploadQuestao.gabarito && uploadQuestao.imagem
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Salvar Questão
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de UPLOAD EM LOTE (até 20 questões) */}
      {showModalUploadLote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  Upload em Lote - Até 20 Questões
                </h3>
                <p className="text-green-100 text-sm">Selecione múltiplas imagens de uma vez</p>
              </div>
              <button 
                onClick={() => {
                  setShowModalUploadLote(false);
                  setUploadLote({ modulo: '', serie: '', componente: '', habilidade: '', tamanho: 'media', questoes: [] });
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Configurações comuns */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="font-semibold text-gray-700 mb-3">Configurações (aplicadas a todas as questões)</h4>
                <div className="grid grid-cols-5 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Módulo *</label>
                    <select 
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      value={uploadLote.modulo}
                      onChange={(e) => setUploadLote({...uploadLote, modulo: e.target.value, serie: '', componente: '', habilidade: ''})}
                    >
                      <option value="">Selecione</option>
                      <option value="prontas">📚 Atividades Prontas</option>
                      <option value="questao_avaliacao">📝 Atividade/Avaliação</option>
                      <option value="simulado">🎯 Simulado SAEB</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Série *</label>
                    <select 
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      value={uploadLote.serie}
                      onChange={(e) => setUploadLote({...uploadLote, serie: e.target.value, componente: '', habilidade: ''})}
                      disabled={!uploadLote.modulo}
                    >
                      <option value="">Selecione</option>
                      {uploadLote.modulo === 'simulado' ? (
                        <>
                          <option value="2º ano">2º ano</option>
                          <option value="5º ano">5º ano</option>
                          <option value="9º ano">9º ano</option>
                        </>
                      ) : (
                        seriesDisponiveis.filter(s => s.ativo).map(s => (
                          <option key={s.id} value={s.nome}>{s.nome}</option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Componente *</label>
                    <select 
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      value={uploadLote.componente}
                      onChange={(e) => setUploadLote({...uploadLote, componente: e.target.value, habilidade: ''})}
                      disabled={!uploadLote.serie}
                    >
                      <option value="">Selecione</option>
                      {uploadLote.modulo === 'simulado' ? (
                        <>
                          <option value="Língua Portuguesa SAEB">Língua Portuguesa</option>
                          <option value="Matemática SAEB">Matemática</option>
                        </>
                      ) : (
                        componentesCurriculares.filter(c => c.ativo && !c.nome.includes('SAEB')).map(c => (
                          <option key={c.id} value={c.nome}>{c.nome}</option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Habilidade/Conteúdo *</label>
                    <select 
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      value={uploadLote.habilidadeId || ''}
                      onChange={(e) => {
                        const habId = e.target.value;
                        const habSelecionada = habilidades.find(h => h.id === habId);
                        setUploadLote({
                          ...uploadLote, 
                          habilidadeId: habId,
                          habilidade: habSelecionada?.codigo || '',
                          conteudo: habSelecionada?.conteudo || ''
                        });
                      }}
                      disabled={!uploadLote.componente}
                    >
                      <option value="">Selecione</option>
                      {habilidades
                        .filter(h => h.serie === uploadLote.serie && h.componente === uploadLote.componente)
                        .map(h => (
                          <option key={h.id} value={h.id}>{h.codigo} - {h.conteudo}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Tamanho</label>
                    <select 
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      value={uploadLote.tamanho}
                      onChange={(e) => setUploadLote({...uploadLote, tamanho: e.target.value})}
                    >
                      <option value="pequena">Pequena (~6.5cm)</option>
                      <option value="media">Média (~8.5cm)</option>
                      <option value="grande">Grande (~12cm)</option>
                      <option value="inteira">Página Inteira (~22cm)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Área de Upload */}
              <div className="mb-4">
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${
                    uploadLote.questoes.length > 0 ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
                  }`}
                  onClick={() => document.getElementById('inputUploadLote').click()}
                >
                  <input 
                    type="file" 
                    id="inputUploadLote"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files).slice(0, 20); // Máximo 20
                      const novasQuestoes = files.map(file => ({
                        file,
                        preview: URL.createObjectURL(file),
                        gabarito: '' // Gabarito opcional
                      }));
                      setUploadLote({...uploadLote, questoes: novasQuestoes});
                    }}
                  />
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-lg text-gray-600 font-semibold">Clique para selecionar imagens</p>
                  <p className="text-sm text-gray-400">Selecione até 20 imagens (PNG, JPG ou WEBP)</p>
                </div>
              </div>

              {/* Preview das imagens selecionadas */}
              {uploadLote.questoes.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-700">
                      {uploadLote.questoes.length} {uploadLote.questoes.length === 1 ? 'imagem selecionada' : 'imagens selecionadas'}
                    </h4>
                    <button 
                      onClick={() => setUploadLote({...uploadLote, questoes: []})}
                      className="text-red-500 text-sm hover:underline"
                    >
                      Limpar todas
                    </button>
                  </div>
                  <div className="grid grid-cols-5 gap-3 max-h-64 overflow-y-auto">
                    {uploadLote.questoes.map((q, index) => (
                      <div key={index} className="border rounded-lg p-2 bg-gray-50">
                        <img src={q.preview} alt={`Questão ${index + 1}`} className="w-full h-24 object-cover rounded mb-2" />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Q{index + 1}</span>
                          <select
                            value={q.gabarito}
                            onChange={(e) => {
                              const novasQuestoes = [...uploadLote.questoes];
                              novasQuestoes[index].gabarito = e.target.value;
                              setUploadLote({...uploadLote, questoes: novasQuestoes});
                            }}
                            className="text-xs px-2 py-1 border rounded"
                          >
                            <option value="">-</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                            <option value="D">D</option>
                          </select>
                          <button 
                            onClick={() => {
                              const novasQuestoes = uploadLote.questoes.filter((_, i) => i !== index);
                              setUploadLote({...uploadLote, questoes: novasQuestoes});
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Barra de progresso */}
              {uploadLoteProgresso.salvando && (
                <div className="mt-4 bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-blue-700">Salvando questões...</span>
                    <span className="text-sm text-blue-600">{uploadLoteProgresso.atual} / {uploadLoteProgresso.total}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadLoteProgresso.atual / uploadLoteProgresso.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex gap-3 p-4 border-t bg-gray-50">
              <button 
                onClick={() => {
                  setShowModalUploadLote(false);
                  setUploadLote({ modulo: '', serie: '', componente: '', habilidade: '', tamanho: 'media', questoes: [] });
                }}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100"
                disabled={uploadLoteProgresso.salvando}
              >
                Cancelar
              </button>
              <button 
                onClick={async () => {
                  // Validação
                  if (!uploadLote.modulo || !uploadLote.serie || !uploadLote.componente || !uploadLote.habilidade) {
                    alert('⚠️ Preencha todos os campos obrigatórios!');
                    return;
                  }
                  if (uploadLote.questoes.length === 0) {
                    alert('⚠️ Selecione pelo menos uma imagem!');
                    return;
                  }

                  setUploadLoteProgresso({ atual: 0, total: uploadLote.questoes.length, salvando: true });

                  try {
                    const questoesSalvas = [];
                    
                    for (let i = 0; i < uploadLote.questoes.length; i++) {
                      const q = uploadLote.questoes[i];
                      setUploadLoteProgresso(prev => ({ ...prev, atual: i + 1 }));
                      
                      // Upload da imagem
                      let imagemUrl = q.preview;
                      if (firebaseReady && storage) {
                        const nomeArquivo = `questoes/${Date.now()}_${i}_${q.file.name}`;
                        const ref = storage.ref(nomeArquivo);
                        await ref.put(q.file);
                        imagemUrl = await ref.getDownloadURL();
                      }

                      // Salvar no Firestore
                      const novaQuestao = {
                        modulo: uploadLote.modulo,
                        serie: uploadLote.serie,
                        componente: uploadLote.componente,
                        habilidade: uploadLote.habilidade,
                        habilidadeId: uploadLote.habilidadeId || '', // 🆕 ID único
                        conteudo: uploadLote.conteudo || '', // 🆕 Conteúdo
                        tamanho: uploadLote.tamanho,
                        gabarito: q.gabarito || '-',
                        imagem: imagemUrl,
                        dataCriacao: new Date(),
                        ativo: true
                      };

                      if (firebaseReady && db) {
                        const docRef = await db.collection('questoes').add(novaQuestao);
                        questoesSalvas.push({ id: docRef.id, ...novaQuestao });
                      }
                    }

                    // Atualizar lista local
                    setQuestoesFirebase(prev => [...prev, ...questoesSalvas]);
                    
                    // v658: Invalidar cache após upload em lote
                    _invalidarCache();
                    
                    // v660: Forçar reload da lista
                    setReloadTrigger(prev => prev + 1);
                    
                    alert(`✅ ${uploadLote.questoes.length} questões salvas com sucesso!`);
                    setShowModalUploadLote(false);
                    setUploadLote({ modulo: '', serie: '', componente: '', habilidade: '', habilidadeId: '', conteudo: '', tamanho: 'media', questoes: [] });
                  } catch (error) {
                    console.error('Erro no upload em lote:', error);
                    alert('❌ Erro ao salvar: ' + error.message);
                  } finally {
                    setUploadLoteProgresso({ atual: 0, total: 0, salvando: false });
                  }
                }}
                disabled={uploadLote.questoes.length === 0 || !uploadLote.modulo || !uploadLote.serie || !uploadLote.componente || !uploadLote.habilidadeId || uploadLoteProgresso.salvando}
                className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                  uploadLote.questoes.length > 0 && uploadLote.modulo && uploadLote.serie && uploadLote.componente && uploadLote.habilidadeId && !uploadLoteProgresso.salvando
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Upload className="w-5 h-5" />
                {uploadLoteProgresso.salvando ? 'Salvando...' : `Salvar ${uploadLote.questoes.length} Questões`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Texto com Múltiplas Questões (Simulado SAEB - Língua Portuguesa) */}
      {showModalTextoMultiplo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 flex items-center justify-between rounded-t-xl">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Texto com Múltiplas Questões (máx. 3)
              </h3>
              <button 
                onClick={() => setShowModalTextoMultiplo(false)}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conteúdo com scroll */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Série */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Série *</label>
                <div className="flex gap-2">
                  {['2º ano', '5º ano', '9º ano'].map(serie => (
                    <button
                      key={serie}
                      onClick={() => setUploadTextoMultiplo({...uploadTextoMultiplo, serie})}
                      className={`px-6 py-2.5 border-2 rounded-lg font-semibold text-sm transition ${
                        uploadTextoMultiplo.serie === serie 
                          ? 'border-orange-500 bg-orange-50 text-orange-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {serie}
                    </button>
                  ))}
                </div>
              </div>

              {/* Upload do Texto Base */}
              <div className="mb-4 p-4 rounded-xl border-2 border-dashed border-orange-300 bg-orange-50">
                <h4 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Texto Base (imagem)
                </h4>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                    uploadTextoMultiplo.textoImagem 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-300 hover:border-orange-400 hover:bg-orange-100'
                  }`}
                  onClick={() => document.getElementById('inputTextoBase').click()}
                >
                  <input 
                    type="file" 
                    id="inputTextoBase"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setUploadTextoMultiplo({...uploadTextoMultiplo, textoImagem: ev.target.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {uploadTextoMultiplo.textoImagem ? (
                    <div className="flex items-center justify-center gap-4">
                      <img src={uploadTextoMultiplo.textoImagem} alt="Texto Base" className="max-h-32 rounded-lg" />
                      <div>
                        <p className="text-sm text-green-600 font-semibold">✅ Texto carregado</p>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setUploadTextoMultiplo({...uploadTextoMultiplo, textoImagem: null}); }}
                          className="text-sm text-red-500 hover:underline mt-1"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-10 h-10 text-gray-400" />
                      <p className="text-base text-gray-600 font-medium">Clique para upload do texto</p>
                      <p className="text-sm text-gray-400">PNG, JPG ou WEBP</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Questões Vinculadas */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2">
                    <List className="w-5 h-5 text-orange-500" />
                    Questões Vinculadas ({uploadTextoMultiplo.questoesVinculadas.length}/3)
                  </h4>
                  {uploadTextoMultiplo.questoesVinculadas.length < 3 && (
                    <button
                      onClick={() => {
                        const novaQuestao = { 
                          id: uploadTextoMultiplo.questoesVinculadas.length + 1, 
                          habilidade: '', 
                          habilidadeId: '',
                          gabarito: '', 
                          imagem: null 
                        };
                        setUploadTextoMultiplo({
                          ...uploadTextoMultiplo, 
                          questoesVinculadas: [...uploadTextoMultiplo.questoesVinculadas, novaQuestao]
                        });
                      }}
                      className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-semibold hover:bg-orange-600 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Adicionar
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {uploadTextoMultiplo.questoesVinculadas.map((questao, index) => (
                    <div key={questao.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-bold text-orange-600 flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm">
                            {index + 1}
                          </span>
                          Questão {index + 1}
                        </span>
                        {uploadTextoMultiplo.questoesVinculadas.length > 1 && (
                          <button
                            onClick={() => {
                              setUploadTextoMultiplo({
                                ...uploadTextoMultiplo,
                                questoesVinculadas: uploadTextoMultiplo.questoesVinculadas.filter((_, i) => i !== index)
                              });
                            }}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-4 gap-4 items-end">
                        {/* Habilidade */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-600 mb-1">Habilidade</label>
                          <select
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            value={questao.habilidadeId || ''}
                            onChange={(e) => {
                              const habId = e.target.value;
                              const habSelecionada = habilidades.find(h => h.id === habId);
                              const novasQuestoes = [...uploadTextoMultiplo.questoesVinculadas];
                              novasQuestoes[index].habilidade = habSelecionada?.codigo || '';
                              novasQuestoes[index].habilidadeId = habId;
                              setUploadTextoMultiplo({...uploadTextoMultiplo, questoesVinculadas: novasQuestoes});
                            }}
                          >
                            <option value="">Selecione</option>
                            {habilidades
                              .filter(h => h.serie === uploadTextoMultiplo.serie && h.componente === 'Língua Portuguesa SAEB')
                              .map(h => (
                                <option key={h.id} value={h.id}>{h.codigo}</option>
                              ))
                            }
                          </select>
                        </div>

                        {/* Gabarito */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-600 mb-1">Gabarito</label>
                          <div className="flex gap-1">
                            {['A', 'B', 'C', 'D'].map(letra => (
                              <button
                                key={letra}
                                onClick={() => {
                                  const novasQuestoes = [...uploadTextoMultiplo.questoesVinculadas];
                                  novasQuestoes[index].gabarito = letra;
                                  setUploadTextoMultiplo({...uploadTextoMultiplo, questoesVinculadas: novasQuestoes});
                                }}
                                className={`w-9 h-9 rounded-lg text-sm font-bold transition ${
                                  questao.gabarito === letra ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                              >
                                {letra}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Upload da Questão */}
                        <div className="col-span-2">
                          <label className="block text-sm font-semibold text-gray-600 mb-1">Imagem da Questão</label>
                          <div 
                            className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer h-16 flex items-center justify-center ${
                              questao.imagem ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-orange-400'
                            }`}
                            onClick={() => document.getElementById(`inputQuestao${index}`).click()}
                          >
                            <input 
                              type="file" 
                              id={`inputQuestao${index}`}
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (ev) => {
                                    const novasQuestoes = [...uploadTextoMultiplo.questoesVinculadas];
                                    novasQuestoes[index].imagem = ev.target.result;
                                    setUploadTextoMultiplo({...uploadTextoMultiplo, questoesVinculadas: novasQuestoes});
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                            {questao.imagem ? (
                              <div className="flex items-center gap-3">
                                <img src={questao.imagem} alt={`Q${index + 1}`} className="max-h-12 rounded" />
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                <span className="text-sm text-green-600 font-medium">Carregada</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Upload className="w-6 h-6 text-gray-400" />
                                <span className="text-sm text-gray-500">Clique para upload</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer com botões */}
            <div className="flex-shrink-0 flex gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button 
                onClick={() => setShowModalTextoMultiplo(false)}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button 
                onClick={async () => {
                  // Validação
                  if (!uploadTextoMultiplo.serie) {
                    alert('⚠️ Selecione a série!');
                    return;
                  }
                  if (!uploadTextoMultiplo.textoImagem) {
                    alert('⚠️ Faça o upload da imagem do texto base!');
                    return;
                  }
                  const questoesIncompletas = uploadTextoMultiplo.questoesVinculadas.some(q => !q.habilidadeId || !q.gabarito || !q.imagem);
                  if (questoesIncompletas) {
                    alert('⚠️ Preencha todos os campos de todas as questões vinculadas!');
                    return;
                  }
                  
                  // v552: Criar a nova questão do tipo texto-multiplas
                  const novaQuestao = {
                    id: questoes.length + 1,
                    serie: uploadTextoMultiplo.serie,
                    componente: 'Língua Portuguesa SAEB',
                    modulo: 'simulado',
                    tamanho: 'texto-multiplas',
                    tipo: 'texto-multiplas',
                    textoImagem: uploadTextoMultiplo.textoImagem,
                    questoesVinculadas: uploadTextoMultiplo.questoesVinculadas.map((q, i) => ({
                      id: i + 1,
                      habilidade: q.habilidade,
                      habilidadeId: q.habilidadeId,
                      gabarito: q.gabarito,
                      imagem: q.imagem
                    }))
                  };
                  
                  // Salvar no Firebase (se disponível)
                  if (firebaseReady && db) {
                    try {
                      const docRef = await db.collection('questoes').add({
                        ...novaQuestao,
                        dataCriacao: new Date(),
                        ativo: true
                      });
                      console.log('✅ Questão texto-multiplas salva:', docRef.id);
                      alert(`✅ Texto com ${uploadTextoMultiplo.questoesVinculadas.length} questões salvo no Firebase!`);
                    } catch (error) {
                      console.error('Erro ao salvar:', error);
                      alert('❌ Erro ao salvar: ' + error.message);
                      return;
                    }
                  } else {
                    console.log('Nova questão com texto (local):', novaQuestao);
                    alert(`✅ Texto com ${uploadTextoMultiplo.questoesVinculadas.length} questões adicionado! (modo local)`);
                  }
                  
                  setShowModalTextoMultiplo(false);
                  setUploadTextoMultiplo({
                    serie: '',
                    textoImagem: null,
                    questoesVinculadas: [
                      { id: 1, habilidade: '', habilidadeId: '', gabarito: '', imagem: null },
                      { id: 2, habilidade: '', habilidadeId: '', gabarito: '', imagem: null }
                    ]
                    });
                  }}
                  className="flex-1 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Salvar Texto com Questões
                </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Fluxo de Compra de Recarga (Professor/Escola) */}
      {showModalCompraRecarga && recargaSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between rounded-t-xl">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Comprar Recarga</h3>
                <p className="text-xs text-gray-500">Pacote {recargaSelecionada.nome} - R$ {recargaSelecionada.preco.toFixed(2).replace('.', ',')}</p>
              </div>
              <button onClick={() => {
                setShowModalCompraRecarga(false);
                setEtapaCompra(1);
                setFormaPagamento('');
                setSeriesSelecionadasCompra([]);
              }} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4">
              {/* ETAPA 1: Seleção de Séries (se não libera todas) */}
              {etapaCompra === 1 && recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length && (
                <div>
                  <h4 className="font-bold text-base mb-2">Escolha as séries</h4>
                  
                  {/* 🔧 v414: Destaque maior para a informação de séries */}
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg p-3 mb-4">
                    <p className="text-sm font-semibold text-center">
                      🎯 Pacote <span className="font-bold">{recargaSelecionada.nome}</span>: escolha {recargaSelecionada.series} {recargaSelecionada.series === 1 ? 'série' : 'séries'} a {recargaSelecionada.series === 1 ? 'ser liberada' : 'serem liberadas'}!
                    </p>
                  </div>
                  
                  <div className="space-y-1.5 mb-3">
                    {seriesDisponiveis.filter(s => s.ativo).map(serie => (
                      <button 
                        key={serie.id}
                        onClick={() => {
                          if (seriesSelecionadasCompra.includes(serie.nome)) {
                            setSeriesSelecionadasCompra(seriesSelecionadasCompra.filter(s => s !== serie.nome));
                          } else if (seriesSelecionadasCompra.length < recargaSelecionada.series) {
                            setSeriesSelecionadasCompra([...seriesSelecionadasCompra, serie.nome]);
                          }
                        }}
                        className={`w-full p-2 border-2 rounded-lg flex items-center justify-between transition text-sm ${
                          seriesSelecionadasCompra.includes(serie.nome) 
                            ? 'border-orange-500 bg-orange-50' 
                            : seriesSelecionadasCompra.length >= recargaSelecionada.series
                              ? 'border-gray-200 bg-gray-50 opacity-50'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium">{serie.nome}</span>
                        {seriesSelecionadasCompra.includes(serie.nome) && <CheckCircle className="w-4 h-4 text-orange-500" />}
                      </button>
                    ))}
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                    <p className="text-xs text-amber-700">
                      ⚠️ As séries selecionadas <strong>substituirão</strong> as anteriores.
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-600">Selecionadas:</span>
                    <span className="text-sm font-bold" style={{color: '#f7941d'}}>{seriesSelecionadasCompra.length} / {recargaSelecionada.series}</span>
                  </div>
                  
                  <button 
                    onClick={() => setEtapaCompra(2)}
                    disabled={seriesSelecionadasCompra.length !== recargaSelecionada.series}
                    className={`w-full py-3 rounded-lg font-semibold transition ${
                      seriesSelecionadasCompra.length === recargaSelecionada.series 
                        ? 'text-white hover:opacity-90' 
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                    style={seriesSelecionadasCompra.length === recargaSelecionada.series ? {backgroundColor: '#f7941d'} : {}}
                  >
                    Continuar
                  </button>
                </div>
              )}

              {/* ETAPA 1 ou 2: Forma de Pagamento */}
              {((etapaCompra === 1 && recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length) || 
                (etapaCompra === 2 && recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length)) && (
                <div>
                  <h4 className="font-bold text-lg mb-4">Escolha a forma de pagamento</h4>
                  
                  {recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-green-700 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        <span>Este pacote libera <strong>todas as séries</strong>!</span>
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <button 
                      onClick={() => setFormaPagamento('cartao')}
                      className={`w-full p-4 border-2 rounded-xl flex items-center gap-4 transition ${
                        formaPagamento === 'cartao' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-semibold">Cartão de Crédito</div>
                        <div className="text-sm text-gray-500">Visa, Mastercard, Elo</div>
                      </div>
                      {formaPagamento === 'cartao' && <CheckCircle className="w-6 h-6 text-orange-500" />}
                    </button>
                    
                    <button 
                      onClick={() => setFormaPagamento('pix')}
                      className={`w-full p-4 border-2 rounded-xl flex items-center gap-4 transition ${
                        formaPagamento === 'pix' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <QrCode className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-semibold">PIX</div>
                        <div className="text-sm text-gray-500">Pagamento instantâneo</div>
                      </div>
                      {formaPagamento === 'pix' && <CheckCircle className="w-6 h-6 text-orange-500" />}
                    </button>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    {recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length && (
                      <button 
                        onClick={() => setEtapaCompra(1)}
                        className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
                      >
                        Voltar
                      </button>
                    )}
                    <button 
                      onClick={() => setEtapaCompra(recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length ? 3 : 2)}
                      disabled={!formaPagamento}
                      className={`flex-1 py-3 rounded-lg font-semibold transition ${
                        formaPagamento ? 'text-white hover:opacity-90' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                      style={formaPagamento ? {backgroundColor: '#f7941d'} : {}}
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              )}

              {/* ETAPA 2 ou 3: Dados do Cartão */}
              {((etapaCompra === 2 && recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length) ||
                (etapaCompra === 3 && recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length)) && formaPagamento === 'cartao' && (
                <div>
                  <h4 className="font-bold text-lg mb-4">Dados do Cartão</h4>
                  <div className="space-y-4">
                    <input type="text" placeholder="Número do Cartão" className="w-full p-3 border rounded-lg" value={dadosCartao.numero} onChange={(e) => setDadosCartao({...dadosCartao, numero: e.target.value})} />
                    <input type="text" placeholder="Nome no Cartão" className="w-full p-3 border rounded-lg" value={dadosCartao.nome} onChange={(e) => setDadosCartao({...dadosCartao, nome: e.target.value})} />
                    <div className="grid grid-cols-2 gap-4">
                      <input type="text" placeholder="MM/AA" className="w-full p-3 border rounded-lg" value={dadosCartao.validade} onChange={(e) => setDadosCartao({...dadosCartao, validade: e.target.value})} />
                      <input type="text" placeholder="CVV" className="w-full p-3 border rounded-lg" value={dadosCartao.cvv} onChange={(e) => setDadosCartao({...dadosCartao, cvv: e.target.value})} />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button onClick={() => setEtapaCompra(recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length ? 2 : 1)} className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold">Voltar</button>
                    <button onClick={() => setEtapaCompra(recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length ? 5 : 4)} className="flex-1 py-3 rounded-lg font-semibold text-white" style={{backgroundColor: '#f7941d'}}>Pagar</button>
                  </div>
                </div>
              )}

              {/* ETAPA 2 ou 3: Dados do PIX - APENAS CPF */}
              {((etapaCompra === 2 && recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length) ||
                (etapaCompra === 3 && recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length)) && formaPagamento === 'pix' && (
                <div>
                  <h4 className="font-bold text-lg mb-2">Confirme seus dados</h4>
                  <p className="text-sm text-gray-500 mb-4">Informe o CPF do pagador para gerar o PIX</p>
                  
                  {erroPagamento && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {erroPagamento}
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CPF do Pagador *</label>
                      <input 
                        type="text" 
                        placeholder="000.000.000-00" 
                        className="w-full p-3 border rounded-lg text-lg" 
                        value={dadosPix.cpf} 
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                          const formatted = value
                            .replace(/(\d{3})(\d)/, '$1.$2')
                            .replace(/(\d{3})(\d)/, '$1.$2')
                            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                          setDadosPix({...dadosPix, cpf: formatted});
                        }}
                        maxLength={14}
                      />
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Dados da conta:</p>
                      <p className="text-sm font-medium">{currentUser?.nome}</p>
                      <p className="text-sm text-gray-600">{currentUser?.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button 
                      onClick={() => {
                        setEtapaCompra(recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length ? 2 : 1);
                        setErroPagamento('');
                      }} 
                      className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold"
                      disabled={processandoPagamento}
                    >
                      Voltar
                    </button>
                    <button 
                      onClick={criarPedidoPix}
                      disabled={processandoPagamento || dadosPix.cpf.replace(/\D/g, '').length !== 11}
                      className={`flex-1 py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 ${processandoPagamento || dadosPix.cpf.replace(/\D/g, '').length !== 11 ? 'opacity-70' : ''}`}
                      style={{backgroundColor: '#f7941d'}}
                    >
                      {processandoPagamento ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Gerando PIX...
                        </>
                      ) : (
                        'Gerar PIX'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* ETAPA 3 ou 4: QR Code PIX */}
              {((etapaCompra === 3 && recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length) ||
                (etapaCompra === 4 && recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length)) && formaPagamento === 'pix' && (
                <div className="text-center">
                  <h4 className="font-bold text-lg mb-4">Pague com PIX</h4>
                  
                  {erroPagamento && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-700 flex items-center gap-2 justify-center">
                        <AlertCircle className="w-4 h-4" />
                        {erroPagamento}
                      </p>
                    </div>
                  )}
                  
                  <div className="bg-gray-100 rounded-xl p-6 mb-4">
                    {pixData.qrCodeUrl ? (
                      <img src={pixData.qrCodeUrl} alt="QR Code PIX" className="w-48 h-48 mx-auto rounded-lg" />
                    ) : (
                      <div className="w-48 h-48 bg-white rounded-lg mx-auto flex items-center justify-center border-2 border-gray-300">
                        <QrCode className="w-28 h-28 text-gray-700" />
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <p className="text-lg font-bold text-green-700">R$ {recargaSelecionada.preco.toFixed(2).replace('.', ',')}</p>
                  </div>
                  
                  {pixData.copiaCola && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-2">Ou copie o código:</p>
                      <div className="bg-gray-100 rounded-lg p-2 flex items-center gap-2">
                        <input type="text" value={pixData.copiaCola} readOnly className="flex-1 bg-transparent text-xs text-gray-600 outline-none truncate" />
                        <button onClick={copiarCodigoPix} className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs font-semibold">Copiar</button>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800">⏱️ Código expira em <strong>1 hora</strong></p>
                    {pollingAtivo && (
                      <p className="text-xs text-green-600 mt-1 flex items-center justify-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        Aguardando confirmação do pagamento...
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button 
                      onClick={() => {
                        setEtapaCompra(recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length ? 3 : 2);
                        setErroPagamento('');
                        setPixData({ qrCode: '', qrCodeUrl: '', copiaCola: '' });
                      }} 
                      className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold"
                    >
                      Voltar
                    </button>
                    <button 
                      onClick={verificarStatusPix}
                      disabled={verificandoPix}
                      className={`flex-1 py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 ${verificandoPix ? 'opacity-70' : ''}`}
                      style={{backgroundColor: '#22c55e'}}
                    >
                      {verificandoPix ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Verificando...
                        </>
                      ) : (
                        'Já paguei'
                      )}
                    </button>
                  </div>
                  
                  {/* 🔧 v416: Botão para concluir manualmente quando der erro 500 */}
                  {mostrarBotaoConcluirManual && (
                    <button 
                      onClick={() => {
                        setShowModalCompraRecarga(false);
                        setEtapaCompra(1);
                        setFormaPagamento('');
                        setSeriesSelecionadasCompra([]);
                        setMostrarBotaoConcluirManual(false);
                        setErroPagamento('');
                        setPedidoAtual(null);
                        setPixData({ qrCode: '', qrCodeUrl: '', copiaCola: '' });
                      }}
                      className="w-full mt-3 py-3 rounded-lg font-semibold text-white"
                      style={{backgroundColor: '#f7941d'}}
                    >
                      ✅ Concluir e Fechar
                    </button>
                  )}
                </div>
              )}

              {/* ETAPA 4 ou 5: Confirmação */}
              {((etapaCompra === 4 && recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length) ||
                (etapaCompra === 5 && recargaSelecionada.series < seriesDisponiveis.filter(s => s.ativo).length)) && (
                <div className="text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Pagamento Confirmado!</h4>
                  <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Pacote:</span><span className="font-semibold">{recargaSelecionada.nome}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Créditos:</span><span className="font-semibold">+{recargaSelecionada.paginas.toLocaleString()}</span></div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Séries:</span>
                        <span className="font-semibold">{recargaSelecionada.series >= seriesDisponiveis.filter(s => s.ativo).length ? 'Todas' : seriesSelecionadasCompra.join(', ')}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t"><span className="text-gray-600">Total:</span><span className="font-bold" style={{color: '#f7941d'}}>R$ {recargaSelecionada.preco.toFixed(2).replace('.', ',')}</span></div>
                    </div>
                  </div>
                  <button 
                    onClick={async () => {
                      // 🔧 v422: Buscar dados ATUALIZADOS do Firebase (não somar localmente!)
                      if (firebaseReady && db && currentUser) {
                        try {
                          const userDoc = await db.collection('users').doc(currentUser.uid || currentUser.id).get();
                          if (userDoc.exists) {
                            const userData = userDoc.data();
                            setCurrentUser(prev => ({
                              ...prev,
                              creditos: userData.creditos || prev.creditos,
                              seriesLiberadas: userData.seriesLiberadas || prev.seriesLiberadas,
                              plano: userData.plano || prev.plano
                            }));
                          }
                        } catch (err) {
                          console.error('Erro ao buscar dados:', err);
                        }
                      }
                      
                      setShowModalCompraRecarga(false);
                      setEtapaCompra(1);
                      setFormaPagamento('');
                      setSeriesSelecionadasCompra([]);
                      setPagamentoProcessado(false);
                    }}
                    className="w-full py-3 rounded-lg font-semibold text-white"
                    style={{backgroundColor: '#f7941d'}}
                  >
                    Concluir
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Aviso - Habilidade Diferente (Atividades Prontas e Atividade por Questão) */}
      {showModalHabilidadeDiferente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Habilidade Diferente</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Neste tipo de atividade, todas as questões devem ser da mesma habilidade da BNCC.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-semibold mb-2">
                    📚 Habilidade Atual: <span className="text-blue-900">{habilidadeBloqueada}</span>
                  </p>
                  <p className="text-sm text-blue-800 mb-2">
                    {(filtros.habilidadeId && habilidades.find(h => h.id === filtros.habilidadeId) || habilidades.find(h => h.codigo === habilidadeBloqueada))?.conteudo}
                  </p>
                  <p className="text-xs text-blue-700">
                    Para adicionar questões de outra habilidade, você precisa primeiro remover todas as questões já selecionadas 
                    ou criar uma nova atividade.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => setShowModalHabilidadeDiferente(false)}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                Entendi
              </button>
              <button 
                onClick={() => {
                  // Remove todas as questões e limpa os bloqueios
                  setPaginas([{ id: 1, questoes: [] }]);
                  setPaginaAtiva(1);
                  setComponenteBloqueado(null);
                  setHabilidadeBloqueada(null);
                  setShowModalHabilidadeDiferente(false);
                }}
                className="flex-1 px-6 py-3 border-2 border-orange-600 text-orange-600 rounded-lg hover:bg-orange-50 font-semibold"
              >
                Limpar Tudo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📄 Modal de Geração de PDF - COMPACTO */}
      {showModalGerarPDF && (() => {
        // Detectar se é escola ou professor
        const isEscolaModal = currentView === 'escola-simulado';
        const blocosAtivos = isEscolaModal ? blocosSimuladoEscola : blocosSimulado;
        const componenteAtivo = isEscolaModal ? componenteSimuladoEscola : componenteSimulado;
        const filtrosAtivos = isEscolaModal ? filtrosEscola : filtros;
        
        // Calcular questões reais
        const bloco1QuestoesReaisModal = contarQuestoesReais(blocosAtivos[0].questoesSelecionadas, questoes);
        const bloco2QuestoesReaisModal = contarQuestoesReais(blocosAtivos[1].questoesSelecionadas, questoes);
        const totalQuestoesModal = bloco1QuestoesReaisModal + bloco2QuestoesReaisModal;
        
        // 🔧 v475: Calcular páginas baseado nos tamanhos reais das questões
        const paginasSimuladoCalculadas = calcularPaginasSimulado(blocosAtivos, questoes);
        
        // Para Atividade/Avaliação: calcular com cabeçalho na primeira página
        // v534: Não contar página de gabarito para plano Gold
        const todasQuestoesAtividade = paginas.flatMap(p => p.questoes);
        const incluirGabaritoNaContagem = incluirGabarito && !isPlanoGold;
        const paginasAtividadeCalculadas = calcularPaginasPorTamanho(todasQuestoesAtividade, questoes, true) + (incluirGabaritoNaContagem ? 1 : 0);
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Printer className="w-6 h-6" />
                <div>
                  <h3 className="text-lg font-bold">Gerar PDF</h3>
                  <p className="text-xs text-blue-100">
                    {tipoGeracao === 'pronta' ? 'Atividade Pronta' : 
                     tipoGeracao === 'questao' ? 'Atividade' : 
                     tipoGeracao === 'avaliacao' ? 'Avaliação' : 
                     isEscolaModal ? 'Simulado SAEB (Escola)' : 'Simulado SAEB'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowModalGerarPDF(false);
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-5">
              {/* Resumo */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 text-center mb-3">
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500 text-xs">Questões</p>
                    <p className="font-bold text-2xl text-blue-600">
                      {(tipoGeracao === 'simulado' || isEscolaModal)
                        ? totalQuestoesModal
                        : totalQuestoesSelecionadas}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500 text-xs">Créditos</p>
                    <p className="font-bold text-2xl text-green-600">
                      {(tipoGeracao === 'simulado' || isEscolaModal)
                        ? paginasSimuladoCalculadas + (tipoCartaoResposta === 'personalizado' && turmaSimuladoSelecionada?.alunos?.length ? turmaSimuladoSelecionada.alunos.length : 0)
                        : paginasAtividadeCalculadas}
                    </p>
                  </div>
                </div>
                
                <div className="text-xs text-gray-600 space-y-1">
                  <p>📚 <strong>{cabecalhoConfig.nomeEscola}</strong></p>
                  <p>👨‍🏫 Prof. {cabecalhoConfig.professor} • {isEscolaModal ? filtrosAtivos.serie : cabecalhoConfig.serie} - {cabecalhoConfig.turma}</p>
                  {(tipoGeracao === 'simulado' || isEscolaModal) && (
                    <p>📋 Componente: <strong>{componenteAtivo}</strong></p>
                  )}
                  {isEscolaModal && (
                    <p>🏫 Blocos: <strong>{bloco2QuestoesReaisModal > 0 ? '2' : '1'}</strong> ({bloco1QuestoesReaisModal}{bloco2QuestoesReaisModal > 0 ? ` + ${bloco2QuestoesReaisModal}` : ''} questões)</p>
                  )}
                </div>
              </div>

              {/* Opção de Gabarito - NÃO mostrar para Simulado (já tem cartão-resposta) e NÃO mostrar para plano Gold (v532) */}
              {tipoGeracao !== 'simulado' && !isEscolaModal && !isPlanoGold && (
                <label className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition mb-4">
                  <input 
                    type="checkbox" 
                    checked={incluirGabarito}
                    onChange={(e) => setIncluirGabarito(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600"
                  />
                  <div>
                    <span className="font-semibold text-gray-800 text-sm">Incluir Gabarito (quando houver)</span>
                    <p className="text-xs text-gray-500">Página extra com respostas (+1 crédito)</p>
                  </div>
                </label>
              )}
              
              {(tipoGeracao === 'simulado' || isEscolaModal) && (
                <>
                  {/* Seleção de Turma(s) com checkboxes - v497 */}
                  <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs font-semibold text-blue-800 mb-2">📚 Turma:</p>
                    <div className="max-h-32 overflow-y-auto">
                      {turmas
                        .filter(t => {
                          const serieAtual = isEscolaModal ? filtrosAtivos.serie : filtros.serie;
                          // Extrair série base (ex: "5º ano" de "5º ano A")
                          const serieBase = serieAtual?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || serieAtual?.toLowerCase() || '';
                          const serieBaseTurma = t.serie?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || t.serie?.toLowerCase() || '';
                          return serieBaseTurma === serieBase || t.serie?.toLowerCase().includes(serieBase);
                        })
                        .map(t => {
                          const isSelected = turmasSelecionadasSimulado.some(ts => ts.id === t.id);
                          return (
                            <label 
                              key={t.id} 
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-blue-100 ${isSelected ? 'bg-blue-100' : ''}`}
                            >
                              <input 
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setTurmasSelecionadasSimulado(prev => [...prev, t]);
                                    // Manter compatibilidade com turmaSimuladoSelecionada (usa a primeira)
                                    if (turmasSelecionadasSimulado.length === 0) {
                                      setTurmaSimuladoSelecionada(t);
                                    }
                                  } else {
                                    setTurmasSelecionadasSimulado(prev => prev.filter(ts => ts.id !== t.id));
                                    // Se removeu a turma principal, selecionar a próxima
                                    if (turmaSimuladoSelecionada?.id === t.id) {
                                      const restantes = turmasSelecionadasSimulado.filter(ts => ts.id !== t.id);
                                      setTurmaSimuladoSelecionada(restantes[0] || null);
                                    }
                                  }
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600"
                              />
                              <span className="text-sm text-gray-700">
                                {t.serie} {t.turma || ''} - {t.turno || ''} ({t.alunos?.length || 0} alunos)
                              </span>
                            </label>
                          );
                        })
                      }
                    </div>
                    {turmas.filter(t => {
                      const serieAtual = isEscolaModal ? filtrosAtivos.serie : filtros.serie;
                      const serieBase = serieAtual?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || serieAtual?.toLowerCase() || '';
                      const serieBaseTurma = t.serie?.match(/\d+[º°]?\s*ano/i)?.[0]?.toLowerCase() || t.serie?.toLowerCase() || '';
                      return serieBaseTurma === serieBase || t.serie?.toLowerCase().includes(serieBase);
                    }).length === 0 && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Nenhuma turma cadastrada para esta série. 
                        <button 
                          onClick={() => { setShowModalGerarPDF(false); setCurrentView('professor-turmas'); }}
                          className="underline ml-1"
                        >
                          Cadastrar
                        </button>
                      </p>
                    )}
                    {turmasSelecionadasSimulado.length > 0 && (
                      <p className="text-xs text-blue-600 mt-2 font-semibold">
                        ✓ {turmasSelecionadasSimulado.length} turma(s) selecionada(s) - {turmasSelecionadasSimulado.reduce((acc, t) => acc + (t.alunos?.length || 0), 0)} alunos
                      </p>
                    )}
                  </div>
                  
                  {/* Checkbox Cartões Personalizados */}
                  <label className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200 mb-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={tipoCartaoResposta === 'personalizado'}
                      onChange={(e) => setTipoCartaoResposta(e.target.checked ? 'personalizado' : 'generico')}
                      disabled={turmasSelecionadasSimulado.length === 0 || turmasSelecionadasSimulado.reduce((acc, t) => acc + (t.alunos?.length || 0), 0) === 0}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-purple-800 text-xs">Gerar Cartões Personalizados</span>
                      <p className="text-xs text-purple-600">
                        {turmasSelecionadasSimulado.length > 0 && turmasSelecionadasSimulado.reduce((acc, t) => acc + (t.alunos?.length || 0), 0) > 0
                          ? `Arquivo separado (+${turmasSelecionadasSimulado.reduce((acc, t) => acc + (t.alunos?.length || 0), 0)} créditos)`
                          : 'Selecione turma(s) com alunos'
                        }
                      </p>
                    </div>
                  </label>
                </>
              )}

              {/* Resumo de Créditos */}
              {(() => {
                // 🔧 v497: Usar cálculo real de páginas + cartões personalizados de múltiplas turmas
                const paginasBase = (tipoGeracao === 'simulado' || isEscolaModal) 
                  ? paginasSimuladoCalculadas 
                  : paginasAtividadeCalculadas;
                // Adicionar cartões personalizados se marcado - v497: soma alunos de todas as turmas
                const totalAlunosCartoes = turmasSelecionadasSimulado.reduce((acc, t) => acc + (t.alunos?.length || 0), 0);
                const cartoesExtras = (tipoGeracao === 'simulado' || isEscolaModal) && tipoCartaoResposta === 'personalizado' && totalAlunosCartoes > 0
                  ? totalAlunosCartoes
                  : 0;
                const creditosNecessarios = paginasBase + cartoesExtras;
                // v514: Usar creditosEfetivos para professor vinculado
                const saldoAtual = creditosEfetivos;
                const saldoSuficiente = currentUser?.tipo === 'admin' || isProfessorVinculado || saldoAtual >= creditosNecessarios;
                
                // v531: Omitir créditos para professor vinculado
                if (isProfessorVinculado) {
                  return (
                    <div className="p-3 rounded-lg border-2 bg-blue-50 border-blue-200">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🏫</span>
                        <div>
                          <p className="text-sm font-semibold text-blue-700">Professor Vinculado</p>
                          <p className="text-xs text-blue-600">Os créditos são gerenciados pela escola.</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div className={`p-3 rounded-lg border-2 ${saldoSuficiente ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-700">💳 Custo:</span>
                      <span className={`text-lg font-bold ${saldoSuficiente ? 'text-green-600' : 'text-red-600'}`}>
                        {creditosNecessarios} crédito{creditosNecessarios !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Seu saldo:</span>
                      <span className="font-semibold">{saldoAtual} créditos</span>
                    </div>
                    {cartoesExtras > 0 && (
                      <p className="text-xs text-purple-600 mt-1">
                        📋 Inclui {cartoesExtras} cartão(ões) personalizado(s)
                      </p>
                    )}
                    {!saldoSuficiente && (
                      <p className="text-xs text-red-600 mt-2 font-medium">
                        ⚠️ Saldo insuficiente! Faltam {creditosNecessarios - saldoAtual} crédito(s).
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Botões */}
            <div className="p-4 bg-gray-50 rounded-b-xl flex gap-3 justify-end border-t">
              <button
                onClick={() => setShowModalGerarPDF(false)}
                className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  // v703: Trava instantânea contra duplo-clique (ref, não state —
                  // não depende de re-render para ter efeito)
                  if (gerandoPDFRef.current) return;
                  gerandoPDFRef.current = true;

                  // v497: Validar turma(s) para Simulado
                  if ((tipoGeracao === 'simulado' || isEscolaModal) && turmasSelecionadasSimulado.length === 0) {
                    alert('⚠️ Selecione pelo menos uma turma para gerar o simulado!');
                    gerandoPDFRef.current = false;
                    return;
                  }
                  
                  // 🔧 v497: Usar cálculo real de páginas + cartões de múltiplas turmas
                  const totalAlunosCartoes = turmasSelecionadasSimulado.reduce((acc, t) => acc + (t.alunos?.length || 0), 0);
                  const cartoesExtras = (tipoGeracao === 'simulado' || isEscolaModal) && tipoCartaoResposta === 'personalizado' && totalAlunosCartoes > 0
                    ? totalAlunosCartoes
                    : 0;
                  const creditosNecessarios = ((tipoGeracao === 'simulado' || isEscolaModal) 
                    ? paginasSimuladoCalculadas 
                    : paginasAtividadeCalculadas) + cartoesExtras;
                  // v514: Usar creditosEfetivos para professor vinculado
                  const saldoAtual = creditosEfetivos;
                  
                  // v689d: TODOS verificam créditos (exceto admin)
                  if (currentUser?.tipo !== 'admin') {
                    if (saldoAtual < creditosNecessarios) {
                      if (isProfessorVinculado) {
                        alert('A escola não possui créditos suficientes para gerar este PDF.\n\nEntre em contato com a escola para solicitar mais créditos.');
                      } else {
                        alert('Saldo insuficiente!\n\nVocê precisa de ' + creditosNecessarios + ' crédito(s) mas possui apenas ' + saldoAtual + '.\n\nRecarregue seus créditos para continuar.');
                      }
                      gerandoPDFRef.current = false; // v703: liberar trava — não vai gerar PDF
                      return;
                    }
                  }
                  
                  // v531: Confirmar geração - omitir créditos para professor vinculado
                  let mensagemConfirm = '📄 Confirmar geração do PDF?\n\n' +
                    '• Páginas: ' + creditosNecessarios + '\n';
                  
                  if (isProfessorVinculado) {
                    mensagemConfirm += '\n🏫 Créditos gerenciados pela escola.\n\nDeseja continuar?';
                  } else if (currentUser?.tipo === 'admin') {
                    mensagemConfirm += '• Custo: 0 (admin)\n\nDeseja continuar?';
                  } else {
                    mensagemConfirm += '• Custo: ' + creditosNecessarios + ' crédito(s)\n' +
                      '• Saldo atual: ' + saldoAtual + ' créditos\n' +
                      '• Saldo após: ' + (saldoAtual - creditosNecessarios) + ' créditos\n\n' +
                      'Deseja continuar?';
                  }
                  
                  const confirmar = window.confirm(mensagemConfirm);
                  
                  if (!confirmar) { gerandoPDFRef.current = false; return; } // v703: liberar trava — usuário cancelou
                  
                  // v689d: Descontar créditos localmente
                  if (currentUser?.tipo !== 'admin') {
                    if (isProfessorVinculado) {
                      // Descontar da escola vinculada (local)
                      setEscolaVinculada(prev => prev ? ({
                        ...prev,
                        creditos: (prev.creditos || 0) - creditosNecessarios
                      }) : prev);
                    } else {
                      // Descontar do próprio usuário (local)
                      setCurrentUser(prev => ({
                        ...prev,
                        creditos: prev.creditos - creditosNecessarios
                      }));
                    }
                  }
                  
                  // 🔥 INTEGRAÇÃO CLOUDLAYER.IO via Cloud Function
                  const gerarHTMLParaPDF = (todasQuestoes) => {
                    // v689d: Usar array completo de questões (inclui extras buscadas do Firestore)
                    todasQuestoes = todasQuestoes || questoes;
                    // Determinar título do documento
                    let tituloDocumento = '';
                    let subtituloDocumento = '';
                    
                    // 🔧 v551: Função auxiliar para buscar habilidade correta pelo ID primeiro, depois por código
                    const buscarHabilidadeCorreta = (questao) => {
                      // 1. Prioridade máxima: filtros.habilidadeId (seleção exata do usuário no dropdown)
                      if (filtros.habilidadeId) {
                        const habPorFiltro = habilidades.find(h => h.id === filtros.habilidadeId);
                        if (habPorFiltro) return habPorFiltro;
                      }
                      // 2. habilidadeId da questão (ID único)
                      if (questao?.habilidadeId) {
                        const habPorId = habilidades.find(h => h.id === questao.habilidadeId);
                        if (habPorId) return habPorId;
                      }
                      // 3. Fallback: buscar por código (pode pegar a primeira duplicata)
                      if (questao?.habilidade) {
                        const codigoLimpo = questao.habilidade.replace(/[()]/g, '').trim();
                        const habPorCodigo = habilidades.find(h => h.codigo.replace(/[()]/g, '').trim() === codigoLimpo);
                        if (habPorCodigo) return habPorCodigo;
                      }
                      return null;
                    };
                    
                    // 🔧 v488: Buscar habilidade das questões selecionadas para o subtítulo
                    const todasQuestoesPDF = paginas.flatMap(p => p.questoes);
                    const primeiraQuestao = todasQuestoesPDF.length > 0 
                      ? todasQuestoes.find(q => String(q.id) === String(todasQuestoesPDF[0])) 
                      : null;
                    
                    if (tipoGeracao === 'pronta') {
                      // v566: Título de acordo com o Componente Curricular selecionado
                      tituloDocumento = `ATIVIDADE DE ${(componenteBloqueado || cabecalhoConfig.disciplina || 'MATEMÁTICA').toUpperCase()}`;
                      // Para atividade pronta, buscar habilidade da primeira questão
                      if (primeiraQuestao?.habilidade || primeiraQuestao?.habilidadeId) {
                        const codigoAtualizado = getCodigoHabilidadeAtualizado(primeiraQuestao);
                        const hab = buscarHabilidadeCorreta(primeiraQuestao);
                        
                        // Formatar código com parênteses se não tiver
                        const codigoFormatado = codigoAtualizado.startsWith('(') 
                          ? codigoAtualizado 
                          : `(${codigoAtualizado})`;
                          
                        if (hab && hab.conteudo) {
                          subtituloDocumento = `${codigoFormatado} - ${hab.conteudo}`;
                        } else {
                          subtituloDocumento = codigoFormatado;
                        }
                      }
                    } else if (tipoGeracao === 'questao') {
                      tituloDocumento = `ATIVIDADE DE ${(componenteBloqueado || cabecalhoConfig.disciplina || 'MATEMÁTICA').toUpperCase()}`;
                    } else if (tipoGeracao === 'avaliacao') {
                      tituloDocumento = `AVALIAÇÃO DE ${(componenteBloqueado || cabecalhoConfig.disciplina || 'MATEMÁTICA').toUpperCase()}`;
                    } else if (tipoGeracao === 'simulado') {
                      tituloDocumento = 'SIMULADO SAEB';
                    }
                    
                    // v552: Subtítulo com código e descrição das habilidades
                    // Descrição SEMPRE deve aparecer em Atividades Prontas e Atividades por Questão
                    if (!subtituloDocumento) {
                      const todasQuestoesIds = paginas.flatMap(p => p.questoes);
                      const questoesSelecionadas = todasQuestoesIds.map(qId => 
                        todasQuestoes.find(q => String(q.id) === String(qId))
                      ).filter(q => q);
                      
                      // Pegar habilidades únicas das questões
                      // v587: Usar getCodigoHabilidadeAtualizado para cada questão
                      const habilidadesUnicas = [...new Set(
                        questoesSelecionadas.map(q => getCodigoHabilidadeAtualizado(q)).filter(h => h)
                      )];
                      
                      if (habilidadesUnicas.length > 0) {
                        // Montar códigos formatados (adiciona parênteses se não tiver)
                        const codigosFormatados = habilidadesUnicas.map(habCodigo => {
                          return habCodigo.startsWith('(') ? habCodigo : `(${habCodigo})`;
                        }).join(' ');
                        
                        // Para Atividades Prontas e Por Questão: SEMPRE mostrar descrição
                        if (tipoGeracao === 'pronta' || tipoGeracao === 'questao') {
                          // v551: Buscar pela habilidade correta usando ID, não apenas código
                          const primeiraQuestaoComHab = questoesSelecionadas.find(q => q?.habilidadeId || q?.habilidade);
                          
                          if (primeiraQuestaoComHab) {
                            const hab = buscarHabilidadeCorreta(primeiraQuestaoComHab);
                            
                            if (hab && hab.conteudo) {
                              subtituloDocumento = `${codigosFormatados} - ${hab.conteudo}`;
                            } else {
                              subtituloDocumento = codigosFormatados;
                            }
                          }
                        } else {
                          // v566: Para Avaliação - omitir códigos das habilidades no subtítulo
                          // Para Simulado: só códigos (múltiplas habilidades)
                          if (tipoGeracao === 'avaliacao') {
                            subtituloDocumento = ''; // Não exibir códigos
                          } else {
                            subtituloDocumento = codigosFormatados;
                          }
                        }
                      }
                    }
                    
                    // Verificar se tem logomarca
                    // v654: Usar logoBase64 para evitar CORS
                    const temLogo = logoBase64 || cabecalhoConfig.logoUrl;
                    const logoUrl = logoBase64 || cabecalhoConfig.logoUrl || '';
                    
                    const styles = `
                      <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        html, body { height: auto; overflow: visible; }
                        body { font-family: Arial, sans-serif; font-size: 12px; padding: 15mm 15mm 8mm 15mm; } /* v699b: top 10mm -> 15mm a pedido do usuário */
                        @page { size: A4; margin: 0; }
                        @media print { 
                          .page-break { page-break-before: always; }
                        }
                        /* 🔧 v549: Margens laterais aumentadas de 10mm para 12mm */
                        .page-break + div {
                          padding-top: 8mm;
                        }
                        
                        /* Cabeçalho - v489: Fontes maiores mantendo limite de 5cm */
                        /* v699: Reescrito sem Flexbox/gap — o motor de renderização do
                           html2pdfrocket (baseado em wkhtmltopdf) não suporta essas
                           propriedades, causando perda total do espaçamento entre campos */
                        .header-container {
                          overflow: hidden; /* clearfix para o float do logo */
                          margin-bottom: 8px;
                        }
                        .header-logo {
                          float: left;
                          width: 55px;
                          height: 55px;
                          margin-right: 12px;
                        }
                        .header-logo img {
                          width: 100%;
                          height: 100%;
                        }
                        .header-info {
                          overflow: hidden; /* ocupa o espaço restante ao lado do float */
                        }
                        .school-name { 
                          font-size: 16px; 
                          font-weight: bold;
                          margin-bottom: 3px;
                        }
                        .header-row {
                          display: table;
                          width: 100%;
                          font-size: 14px;
                          margin-bottom: 3px;
                        }
                        .header-row .professor-field {
                          display: table-cell;
                          width: 55%;
                        }
                        .header-row .serie-field {
                          display: table-cell;
                          width: 25%;
                        }
                        .header-row .turma-field {
                          display: table-cell;
                          width: 20%;
                        }
                        .student-row {
                          display: table;
                          width: 100%;
                          font-size: 14px;
                          margin-bottom: 3px;
                        }
                        .student-row .aluno-field {
                          display: table-cell;
                          width: 80%;
                        }
                        .student-row .data-field {
                          display: table-cell;
                          width: 20%;
                        }
                        
                        /* Título centralizado - v489: Com subtítulo da habilidade */
                        .doc-title-container {
                          text-align: center;
                          margin-top: 8px;
                          margin-bottom: 12px;
                          padding-bottom: 4px;
                        }
                        .doc-title {
                          font-size: 15px;
                          font-weight: bold;
                          text-transform: uppercase;
                          margin-bottom: 2px;
                        }
                        .doc-subtitle {
                          font-size: 11px;
                          color: #333;
                          /* v549: Limitar a 1 linha */
                          white-space: nowrap;
                          overflow: hidden;
                          text-overflow: ellipsis;
                          max-width: 100%;
                        }
                        
                        /* Questão - v489: Padding 0.8cm */
                        .question { 
                          margin-bottom: 0.8cm;
                          page-break-inside: avoid;
                          break-inside: avoid;
                        }
                        /* Remover padding da última questão para evitar página extra */
                        .question:last-child {
                          margin-bottom: 0;
                        }
                        .question-header {
                          background: #e5e7eb;
                          color: #333;
                          padding: 0 12px;
                          font-weight: bold;
                          font-size: 13px;
                          display: inline-block; /* v699: era inline-flex — sem suporte no html2pdfrocket */
                          text-align: center;
                          page-break-after: avoid;
                          break-after: avoid;
                          height: 0.6cm;
                          line-height: 0.6cm; /* centraliza verticalmente sem precisar de flex */
                        }
                        /* Tamanhos de questão (conteúdo sem título e padding) */
                        .question-pequena .question-content { min-height: 5.0cm; }
                        .question-media .question-content { min-height: 7.0cm; }
                        .question-grande .question-content { min-height: 10.5cm; }
                        .question-inteira .question-content { min-height: 20.5cm; }
                        .question-content {
                          margin-top: 0;
                          padding-top: 0;
                          page-break-before: avoid;
                          break-before: avoid;
                        }
                        .question-content img {
                          max-width: 100%;
                          height: auto;
                          display: block;
                          page-break-inside: avoid;
                          break-inside: avoid;
                        }
                        
                        /* Gabarito */
                        .gabarito-title { 
                          font-size: 16px; 
                          font-weight: bold;
                          text-align: center;
                          margin: 20px 0;
                          text-transform: uppercase;
                        }
                        .gabarito-grid { 
                          text-align: center; /* v699: sem flex/gap — wrap natural com inline-block */
                        }
                        .gabarito-item { 
                          display: inline-block;
                          border: 2px solid #1e3a5f; 
                          border-radius: 5px; 
                          padding: 8px 12px; 
                          margin: 5px; /* v699: substitui o gap:10px do flex removido */
                          text-align: center;
                          min-width: 70px;
                        }
                        .gabarito-item .num { 
                          font-size: 9px; 
                          color: #666; 
                        }
                        .gabarito-item .answer { 
                          font-size: 18px; 
                          font-weight: bold; 
                          color: #16a34a; 
                        }
                      </style>
                    `;
                    
                    let conteudo = '';
                    const paginasComQuestoes = paginas.filter(p => p.questoes.length > 0);
                    
                    console.log('📄 GERANDO PDF:');
                    console.log('   Total de páginas no estado:', paginas.length);
                    console.log('   Páginas com questões:', paginasComQuestoes.length);
                    console.log('   Detalhes:', paginasComQuestoes.map(p => ({ id: p.id, questoes: p.questoes.length })));
                    
                    paginasComQuestoes.forEach((pagina, pageIndex) => {
                      console.log(`   Processando página ${pageIndex + 1}/${paginasComQuestoes.length} (id: ${pagina.id}, questões: ${pagina.questoes.length})`);
                      // Calcular questões anteriores baseado no array filtrado
                      const questoesAnteriores = paginasComQuestoes.slice(0, pageIndex).reduce((t, p) => t + p.questoes.length, 0);
                      
                      if (pageIndex > 0) {
                        conteudo += '<div class="page-break"></div>';
                        // v566: Em Atividades Prontas, repetir cabeçalho em todas as páginas
                        if (tipoGeracao === 'pronta') {
                          // Cabeçalho completo para páginas 2+
                          conteudo += '<div class="header-container">';
                          if (temLogo && logoUrl) {
                            conteudo += '<div class="header-logo"><img src="' + logoUrl + '" /></div>';
                          }
                          conteudo += '<div class="header-info">';
                          conteudo += '<div class="school-name">' + cabecalhoConfig.nomeEscola + '</div>';
                          conteudo += '<div class="header-row">';
                          conteudo += '<span class="professor-field">Professor(a): <strong>' + cabecalhoConfig.professor + '</strong></span>';
                          conteudo += '<span class="serie-field">Série: <strong>' + cabecalhoConfig.serie + '</strong></span>';
                          conteudo += '<span class="turma-field">Turma: ' + (cabecalhoConfig.turma ? ('<strong>' + cabecalhoConfig.turma + '</strong>') : '<span style="display:inline-block;border-bottom:1px solid #000;width:65px;">&nbsp;</span>') + '</span>';
                          conteudo += '</div>';
                          conteudo += '<div class="student-row">';
                          conteudo += '<span class="aluno-field">Aluno(a): _______________________________________________</span>';
                          conteudo += '<span class="data-field">Data: <span style="display:inline-block;border-bottom:1px solid #000;width:65px;">&nbsp;</span></span>';
                          conteudo += '</div>';
                          conteudo += '</div>'; // fecha header-info
                          conteudo += '</div>'; // fecha header-container
                          
                          // Título também nas páginas 2+
                          if (tituloDocumento) {
                            conteudo += '<div class="doc-title-container">';
                            conteudo += '<div class="doc-title">' + tituloDocumento + '</div>';
                            if (subtituloDocumento) {
                              conteudo += '<div class="doc-subtitle">' + subtituloDocumento + '</div>';
                            }
                            conteudo += '</div>';
                          }
                        } else {
                          // Adicionar espaço superior nas páginas 2+ (outros tipos)
                          conteudo += '<div style="padding-top: 10mm;"></div>';
                        }
                      }
                      
                      // Cabeçalho APENAS na primeira página
                      if (pageIndex === 0) {
                        conteudo += '<div class="header-container">';
                        if (temLogo && logoUrl) {
                          conteudo += '<div class="header-logo"><img src="' + logoUrl + '" /></div>';
                        }
                        conteudo += '<div class="header-info">';
                        // Linha 1: Nome da escola
                        conteudo += '<div class="school-name">' + cabecalhoConfig.nomeEscola + '</div>';
                        // Linha 2: Professor, Série, Turma
                        conteudo += '<div class="header-row">';
                        conteudo += '<span class="professor-field">Professor(a): <strong>' + cabecalhoConfig.professor + '</strong></span>';
                        conteudo += '<span class="serie-field">Série: <strong>' + cabecalhoConfig.serie + '</strong></span>';
                        conteudo += '<span class="turma-field">Turma: ' + (cabecalhoConfig.turma ? ('<strong>' + cabecalhoConfig.turma + '</strong>') : '<span style="display:inline-block;border-bottom:1px solid #000;width:65px;">&nbsp;</span>') + '</span>';
                        conteudo += '</div>';
                        // Linha 3: Aluno e Data (dentro do header-info)
                        conteudo += '<div class="student-row">';
                        conteudo += '<span class="aluno-field">Aluno(a): _______________________________________________</span>';
                        conteudo += '<span class="data-field">Data: <span style="display:inline-block;border-bottom:1px solid #000;width:65px;">&nbsp;</span></span>';
                        conteudo += '</div>';
                        conteudo += '</div>'; // fecha header-info
                        conteudo += '</div>'; // fecha header-container
                        
                        // Título centralizado - também só na primeira página
                        if (tituloDocumento) {
                          conteudo += '<div class="doc-title-container">';
                          conteudo += '<div class="doc-title">' + tituloDocumento + '</div>';
                          if (subtituloDocumento) {
                            conteudo += '<div class="doc-subtitle">' + subtituloDocumento + '</div>';
                          }
                          conteudo += '</div>';
                        }
                      }
                      
                      // Questões
                      pagina.questoes.forEach((qId, qIndex) => {
                        const q = todasQuestoes.find(x => String(x.id) === String(qId));
                        const numeroQuestao = String(questoesAnteriores + qIndex + 1).padStart(2, '0');
                        const tamanhoClasse = q?.tamanho ? 'question-' + q.tamanho : 'question-media';
                        
                        if (!q) {
                          conteudo += '<div class="question question-media">';
                          conteudo += '<div class="question-header">QUESTÃO ' + numeroQuestao + '</div>';
                          conteudo += '<div class="question-content"><p style="color:red;">Questão não encontrada</p></div>';
                          conteudo += '</div>';
                          return;
                        }
                        
                        // v475: NÃO adicionar código da habilidade ao header em Atividades (só no Simulado SAEB)
                        // A habilidade já aparece no subtítulo abaixo do título da atividade
                        
                        conteudo += '<div class="question ' + tamanhoClasse + '">';
                        // v566: Em Atividades Prontas, não exibir "QUESTÃO XX" pois já tem identificação própria
                        if (tipoGeracao !== 'pronta') {
                          conteudo += '<div class="question-header">QUESTÃO ' + numeroQuestao + '</div>';
                        }
                        conteudo += '<div class="question-content">';
                        
                        // Se a questão tem imagem
                        if (q.imagem) {
                          conteudo += '<img src="' + q.imagem + '" />';
                        }
                        
                        // Se tem campos de texto
                        if (q.enunciado) conteudo += '<p style="margin:8px 0;">' + q.enunciado + '</p>';
                        if (q.texto) conteudo += '<div style="background:#f5f5f5;padding:10px;margin:8px 0;border-left:3px solid #1e3a5f;">' + q.texto + '</div>';
                        if (q.pergunta) conteudo += '<p style="font-weight:600;margin:8px 0;">' + q.pergunta + '</p>';
                        
                        if (q.alternativas && q.alternativas.length > 0) {
                          conteudo += '<div style="margin-top:8px;">';
                          q.alternativas.forEach(alt => {
                            // v699: sem flex/gap — badge e texto alinhados via vertical-align + margin
                            conteudo += '<div style="padding:4px 0;">';
                            conteudo += '<span style="display:inline-block;vertical-align:middle;background:#1e3a5f;color:white;min-width:22px;width:22px;height:22px;border-radius:50%;text-align:center;line-height:22px;font-weight:bold;font-size:10px;">' + alt.charAt(0) + '</span>';
                            conteudo += '<span style="display:inline-block;vertical-align:middle;margin-left:8px;">' + alt.substring(3) + '</span>';
                            conteudo += '</div>';
                          });
                          conteudo += '</div>';
                        }
                        
                        conteudo += '</div>'; // fecha question-content
                        conteudo += '</div>'; // fecha question
                      });
                      
                      // Footer removido para ganhar espaço
                    });
                    
                    // Gabarito
                    // v532: Não incluir gabarito para plano Gold
                    if (incluirGabarito && !isPlanoGold) {
                      conteudo += '<div class="page-break"></div>';
                      conteudo += '<div class="school-name" style="margin-bottom:10px;">' + cabecalhoConfig.nomeEscola + '</div>';
                      conteudo += '<div class="gabarito-title">GABARITO</div>';
                      conteudo += '<div class="gabarito-grid">';
                      paginas.flatMap(p => p.questoes).forEach((qId, idx) => {
                        const q = todasQuestoes.find(x => String(x.id) === String(qId));
                        conteudo += '<div class="gabarito-item">';
                        conteudo += '<div class="num">QUESTÃO ' + String(idx + 1).padStart(2, '0') + '</div>';
                        conteudo += '<div class="answer">' + (q ? q.gabarito : '-') + '</div>';
                        conteudo += '</div>';
                      });
                      conteudo += '</div>';
                      // Footer do gabarito removido
                    }
                    
                    // 🔥 SIMULADO SAEB - Gera HTML específico com cartão-resposta
                    if (tipoGeracao === 'simulado' || isEscolaModal) {
                      conteudo = ''; // Limpar conteúdo anterior
                      
                      const totalQuestoesB1 = blocosAtivos[0].questoesSelecionadas.length;
                      const totalQuestoesB2 = blocosAtivos[1].questoesSelecionadas.length;
                      const totalQuestoesSimulado = totalQuestoesB1 + totalQuestoesB2;
                      // USAR O MESMO numeroSimulado do estado para consistência
                      const codigoSimuladoFinal = String(numeroSimulado).padStart(6, '0');
                      const serieSimulado = turmaSimuladoSelecionada?.serie || filtrosAtivos.serie || cabecalhoConfig.serie;
                      const turmaSimulado = turmaSimuladoSelecionada?.turma || cabecalhoConfig.turma;
                      const componenteTexto = componenteAtivo === "Português" || componenteAtivo === "Língua Portuguesa" ? "Língua Portuguesa" : "Matemática";
                      // v526: Mostrar série + turma mesmo para escola
                      const serieMostrar = serieSimulado + (turmaSimulado ? ' ' + turmaSimulado : '');
                      
                      // v526: Buscar nome do professor da turma selecionada
                      let professorDaTurma = cabecalhoConfig.professor || '';
                      if (isEscolaModal && turmaSimuladoSelecionada) {
                        // Se a turma tem professorId (turma de professor vinculado)
                        if (turmaSimuladoSelecionada.professorId) {
                          const profEncontrado = professoresEscola.find(p => 
                            p.professorId === turmaSimuladoSelecionada.professorId ||
                            p.professorUserId === turmaSimuladoSelecionada.professorId ||
                            p.id === turmaSimuladoSelecionada.professorId
                          );
                          if (profEncontrado?.nome) {
                            professorDaTurma = profEncontrado.nome;
                          }
                        }
                        // Se a turma tem userId (criada pelo professor)
                        else if (turmaSimuladoSelecionada.userId && turmaSimuladoSelecionada.userId !== currentUser?.uid) {
                          const profEncontrado = professoresEscola.find(p => 
                            p.professorId === turmaSimuladoSelecionada.userId ||
                            p.professorUserId === turmaSimuladoSelecionada.userId ||
                            p.id === turmaSimuladoSelecionada.userId
                          );
                          if (profEncontrado?.nome) {
                            professorDaTurma = profEncontrado.nome;
                          }
                        }
                      }
                      
                      // Função para gerar um cartão-resposta (similar à pré-visualização)
                      const gerarCartaoResposta = (nomeAluno = null) => {
                        let cartao = '<div style="background:white;padding:30px;min-height:100%;">';
                        
                        // Cabeçalho com título e número de referência
                        cartao += '<div style="text-align:center;padding-bottom:20px;margin-bottom:20px;">';
                        // v699: sem flex (incompatível com html2pdfrocket) — layout via display:table/table-cell
                        cartao += '<div style="display:table;width:100%;margin-bottom:10px;">';
                        cartao += '<div style="display:table-cell;width:33%;text-align:left;"></div>';
                        cartao += '<div style="display:table-cell;width:34%;text-align:center;"><h1 style="font-size:24px;font-weight:bold;margin:0;color:#1a1a1a;">SIMULADO SAEB</h1></div>';
                        cartao += '<div style="display:table-cell;width:33%;text-align:right;"><span style="background:#f7941d;color:white;padding:5px 12px;border-radius:20px;font-size:12px;font-weight:bold;">#' + codigoSimuladoFinal + '</span></div>';
                        cartao += '</div>';
                        cartao += '<p style="font-size:14px;color:#666;margin:10px 0 15px 0;">CARTÃO-RESPOSTA</p>';
                        // v526: Linha 1 - Escola e Série
                        // v699: sem flex/gap — centralização via text-align + margin
                        cartao += '<div style="text-align:center;font-size:12px;color:#555;margin-bottom:5px;">';
                        cartao += '<span style="display:inline-block;margin:0 20px;">Escola: <strong>' + cabecalhoConfig.nomeEscola + '</strong></span>';
                        cartao += '<span style="display:inline-block;margin:0 20px;">Série: <strong>' + serieMostrar + '</strong></span>';
                        cartao += '<span style="display:inline-block;margin:0 20px;">Componente: <strong>' + componenteTexto + '</strong></span>';
                        cartao += '</div>';
                        // v709: Linha única — Professor(a) [se houver] + Aluno(a) + Data juntos
                        // (antes Aluno/Data ficavam num bloco separado, abaixo do Professor)
                        // v699: sem flex/gap — display:table/table-cell (compatível com html2pdfrocket)
                        cartao += '<div style="display:table;width:100%;margin-top:8px;margin-bottom:45px;">';
                        if (professorDaTurma) {
                          cartao += '<div style="display:table-cell;width:27%;text-align:left;vertical-align:middle;font-size:12px;color:#555;white-space:nowrap;">Professor(a): <strong>' + professorDaTurma + '</strong></div>';
                        }
                        cartao += '<div style="display:table-cell;' + (professorDaTurma ? 'width:51%;' : 'width:78%;') + 'text-align:center;vertical-align:middle;font-size:14px;color:#555;white-space:nowrap;">';
                        if (nomeAluno) {
                          cartao += 'Aluno(a): <strong>' + nomeAluno + '</strong>';
                        } else {
                          cartao += '<span style="display:inline-block;vertical-align:middle;">Aluno(a):</span><span style="display:inline-block;vertical-align:middle;margin-left:10px;width:260px;border-bottom:2px solid #999;height:18px;"></span>';
                        }
                        cartao += '</div>';
                        cartao += '<div style="display:table-cell;width:22%;text-align:right;vertical-align:middle;font-size:14px;color:#555;white-space:nowrap;">';
                        cartao += '<span style="display:inline-block;vertical-align:middle;">Data:</span><span style="display:inline-block;vertical-align:middle;margin-left:10px;width:90px;border-bottom:2px solid #999;height:18px;"></span>';
                        cartao += '</div>';
                        cartao += '</div>';
                        cartao += '</div>'; // fecha cabeçalho (margin-bottom:20px já dá o respiro antes da grade)
                        
                        // Grade do cartão-resposta - TABELAS com fundo transparente
                        // v699: sem flex/gap — os blocos internos já usam inline-block + margin
                        // v719: wrapper agora é display:inline-block (não mais 100% da página) pra encolher
                        // exatamente no tamanho real dos 2 blocos — antes os quadrados pretos ficavam
                        // grudados na borda da PÁGINA inteira, não na borda da grade de bolinhas
                        cartao += '<div style="text-align:center;">';
                        cartao += '<div style="display:inline-block;position:relative;padding:0 38px;">';

                        // 🆕 v710: Marcadores pretos nos 4 cantos da área dos blocos de respostas —
                        // referência visual pro professor alinhar a câmera na hora da foto (a moldura
                        // do app deve ser encostada nesses 4 quadradinhos, não nas bordas da folha)
                        cartao += '<div style="position:absolute;top:0;left:0;width:14px;height:14px;background:#000;"></div>';
                        cartao += '<div style="position:absolute;top:0;right:0;width:14px;height:14px;background:#000;"></div>';
                        cartao += '<div style="position:absolute;bottom:0;left:0;width:14px;height:14px;background:#000;"></div>';
                        cartao += '<div style="position:absolute;bottom:0;right:0;width:14px;height:14px;background:#000;"></div>';
                        
                        // Bloco 1 - Layout otimizado para PDF
                        if (totalQuestoesB1 > 0) {
                          cartao += '<div style="text-align:center;display:inline-block;vertical-align:top;margin:0 20px;">';
                          // Cabeçalho do bloco
                          cartao += '<div style="background:#1e3a5f;color:white;padding:8px 20px;font-size:11px;font-weight:bold;text-align:center;margin-bottom:30px;white-space:nowrap;">BLOCO 1 - ' + componenteTexto.toUpperCase() + '</div>';
                          // Questões
                          for (let i = 0; i < totalQuestoesB1; i++) {
                            cartao += '<div style="margin-bottom:10px;white-space:nowrap;">';
                            cartao += '<span style="display:inline-block;font-size:11px;font-weight:bold;color:#333;width:20px;text-align:right;margin-right:10px;vertical-align:middle;">' + String(i + 1).padStart(2, '0') + '</span>';
                            ['A', 'B', 'C', 'D'].forEach(letra => {
                              cartao += '<span style="display:inline-block;width:26px;height:26px;border:2px solid #555;border-radius:50%;text-align:center;line-height:22px;font-size:11px;font-weight:bold;color:#555;margin:0 4px;vertical-align:middle;">' + letra + '</span>';
                            });
                            cartao += '</div>';
                          }
                          cartao += '</div>';
                        }
                        
                        // Bloco 2 - Layout otimizado para PDF
                        if (totalQuestoesB2 > 0) {
                          cartao += '<div style="text-align:center;display:inline-block;vertical-align:top;margin:0 20px;">';
                          // Cabeçalho do bloco
                          cartao += '<div style="background:#1e3a5f;color:white;padding:8px 20px;font-size:11px;font-weight:bold;text-align:center;margin-bottom:30px;white-space:nowrap;">BLOCO 2 - ' + componenteTexto.toUpperCase() + '</div>';
                          // Questões
                          for (let i = 0; i < totalQuestoesB2; i++) {
                            cartao += '<div style="margin-bottom:10px;white-space:nowrap;">';
                            cartao += '<span style="display:inline-block;font-size:11px;font-weight:bold;color:#333;width:20px;text-align:right;margin-right:10px;vertical-align:middle;">' + String(i + totalQuestoesB1 + 1) + '</span>';
                            ['A', 'B', 'C', 'D'].forEach(letra => {
                              cartao += '<span style="display:inline-block;width:26px;height:26px;border:2px solid #555;border-radius:50%;text-align:center;line-height:22px;font-size:11px;font-weight:bold;color:#555;margin:0 4px;vertical-align:middle;">' + letra + '</span>';
                            });
                            cartao += '</div>';
                          }
                          cartao += '</div>';
                        }
                        
                        cartao += '</div>'; // fecha wrapper inline-block dos blocos (encolhido, com os quadrados)
                        cartao += '</div>'; // fecha centralização
                        
                        cartao += '</div>'; // fecha página branca
                        return cartao;
                      };
                      
                      // Gerar cartões-resposta - SEMPRE genérico no PDF principal
                      // Cartões personalizados serão gerados em arquivo separado
                      conteudo += gerarCartaoResposta(null);
                      
                      // CAPA DO BLOCO 1 - centralizada verticalmente
                      if (totalQuestoesB1 > 0) {
                        conteudo += '<div class="page-break"></div>';
                        // v699: sem flex — centralização vertical via display:table/table-cell (compatível com html2pdfrocket)
                        conteudo += '<div style="display:table;width:100%;height:250mm;">';
                        conteudo += '<div style="display:table-cell;vertical-align:middle;text-align:center;">';
                        conteudo += '<h1 style="font-size:32px;font-weight:bold;margin-bottom:30px;">SIMULADO SAEB</h1>';
                        conteudo += '<div style="background:#1e3a5f;color:white;display:inline-block;padding:20px 50px;border-radius:10px;margin-bottom:30px;">';
                        conteudo += '<h2 style="font-size:28px;margin:0;">BLOCO 1</h2>';
                        conteudo += '</div>';
                        conteudo += '<p style="font-size:20px;color:#555;font-weight:500;">' + componenteTexto + '</p>';
                        conteudo += '<p style="font-size:16px;color:#888;margin-top:15px;">' + totalQuestoesB1 + ' questões</p>';
                        conteudo += '</div>'; // fecha table-cell
                        conteudo += '</div>'; // fecha table
                        
                        // PÁGINAS DE QUESTÕES DO BLOCO 1 (sem cabeçalho, sem identificação do bloco)
                        const questoesB1 = blocosAtivos[0].questoesSelecionadas;
                        
                        // 🔧 v475: Separar questões normais de texto-multiplas
                        const questoesNormaisB1 = questoesB1.filter(qId => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          return q?.tamanho !== 'texto-multiplas';
                        });
                        const questoesTextoB1 = questoesB1.filter(qId => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          return q?.tamanho === 'texto-multiplas';
                        });
                        
                        // Primeiro as questões normais (por tamanho - v475)
                        // Agrupar por página considerando tamanhos
                        let alturaUsadaB1 = 0;
                        let questoesPaginaAtualB1 = [];
                        const paginasQuestoesB1 = [];
                        const ALTURA_PAGINA_B1 = 27.0;
                        const TAMANHOS_B1 = { 'pequena': 6.5, 'media': 8.5, 'grande': 12.0, 'inteira': 22.0 };
                        
                        questoesNormaisB1.forEach((qId) => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          const tamanho = q?.tamanho || 'media';
                          const altura = TAMANHOS_B1[tamanho] || 8.5;
                          
                          if (alturaUsadaB1 + altura > ALTURA_PAGINA_B1 && questoesPaginaAtualB1.length > 0) {
                            paginasQuestoesB1.push([...questoesPaginaAtualB1]);
                            questoesPaginaAtualB1 = [];
                            alturaUsadaB1 = 0;
                          }
                          questoesPaginaAtualB1.push(qId);
                          alturaUsadaB1 += altura;
                        });
                        if (questoesPaginaAtualB1.length > 0) {
                          paginasQuestoesB1.push(questoesPaginaAtualB1);
                        }
                        
                        let numQuestaoB1 = 1;
                        
                        paginasQuestoesB1.forEach((questoesPagina, pag) => {
                          conteudo += '<div class="page-break"></div>';
                          conteudo += '<div style="padding-top:10mm;">';
                          
                          questoesPagina.forEach((qId) => {
                            const q = todasQuestoes.find(x => String(x.id) === String(qId));
                            const numQuestao = String(numQuestaoB1).padStart(2, '0');
                            const codigoHabilidade = getCodigoHabilidadeAtualizado(q); // v587: Usar código atualizado
                            const tamanhoClasse = q?.tamanho ? 'question-' + q.tamanho : 'question-media';
                            conteudo += '<div class="question ' + tamanhoClasse + '">';
                            conteudo += '<div class="question-header">QUESTÃO ' + numQuestao + (codigoHabilidade ? ' (' + codigoHabilidade + ')' : '') + '</div>';
                            conteudo += '<div class="question-content">';
                            if (q && q.imagem) {
                              conteudo += '<img src="' + q.imagem + '" />';
                            }
                            conteudo += '</div></div>';
                            numQuestaoB1++;
                          });
                          conteudo += '</div>';
                        });
                        
                        // v552: Questões texto-multiplas - renderizar texto base + questões vinculadas
                        // Texto base (14cm) + até 2 questões (6.3cm cada) cabem na mesma página (26.6cm)
                        // Se tiver 3ª questão, vai para próxima página
                        questoesTextoB1.forEach((qId) => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          if (!q) return;
                          
                          const questoesVinc = q.questoesVinculadas || [];
                          const numQuestaoInicial = numQuestaoB1;
                          
                          // Nova página para o texto base
                          conteudo += '<div class="page-break"></div>';
                          conteudo += '<div style="padding-top:10mm;">';
                          
                          // Texto base (14cm de altura)
                          conteudo += '<div style="height:14cm;margin-bottom:0.5cm;">';
                          if (q.textoImagem || q.imagem) {
                            conteudo += '<img src="' + (q.textoImagem || q.imagem) + '" style="max-width:100%;max-height:13.5cm;object-fit:contain;" />';
                          }
                          conteudo += '</div>';
                          
                          // Primeiras 2 questões vinculadas na mesma página (6.3cm cada)
                          const questoesPrimeiraPagina = questoesVinc.slice(0, 2);
                          questoesPrimeiraPagina.forEach((qv, idx) => {
                            const numQ = String(numQuestaoB1).padStart(2, '0');
                            const habCode = qv.habilidade || '';
                            conteudo += '<div class="question question-pequena" style="height:6.3cm;">';
                            conteudo += '<div class="question-header">QUESTÃO ' + numQ + (habCode ? ' (' + habCode + ')' : '') + '</div>';
                            conteudo += '<div class="question-content">';
                            if (qv.imagem) {
                              conteudo += '<img src="' + qv.imagem + '" style="max-width:100%;max-height:5.5cm;object-fit:contain;" />';
                            }
                            conteudo += '</div></div>';
                            numQuestaoB1++;
                          });
                          
                          conteudo += '</div>';
                          
                          // Se tiver 3ª questão, vai para próxima página
                          if (questoesVinc.length > 2) {
                            conteudo += '<div class="page-break"></div>';
                            conteudo += '<div style="padding-top:10mm;">';
                            
                            const questoesSegundaPagina = questoesVinc.slice(2);
                            questoesSegundaPagina.forEach((qv, idx) => {
                              const numQ = String(numQuestaoB1).padStart(2, '0');
                              const habCode = qv.habilidade || '';
                              conteudo += '<div class="question question-pequena" style="height:6.3cm;">';
                              conteudo += '<div class="question-header">QUESTÃO ' + numQ + (habCode ? ' (' + habCode + ')' : '') + '</div>';
                              conteudo += '<div class="question-content">';
                              if (qv.imagem) {
                                conteudo += '<img src="' + qv.imagem + '" style="max-width:100%;max-height:5.5cm;object-fit:contain;" />';
                              }
                              conteudo += '</div></div>';
                              numQuestaoB1++;
                            });
                            
                            conteudo += '</div>';
                          }
                        });
                      }
                      
                      // CAPA DO BLOCO 2 - centralizada verticalmente
                      if (totalQuestoesB2 > 0) {
                        conteudo += '<div class="page-break"></div>';
                        // v699: sem flex — centralização vertical via display:table/table-cell (compatível com html2pdfrocket)
                        conteudo += '<div style="display:table;width:100%;height:250mm;">';
                        conteudo += '<div style="display:table-cell;vertical-align:middle;text-align:center;">';
                        conteudo += '<h1 style="font-size:32px;font-weight:bold;margin-bottom:30px;">SIMULADO SAEB</h1>';
                        conteudo += '<div style="background:#1e3a5f;color:white;display:inline-block;padding:20px 50px;border-radius:10px;margin-bottom:30px;">';
                        conteudo += '<h2 style="font-size:28px;margin:0;">BLOCO 2</h2>';
                        conteudo += '</div>';
                        conteudo += '<p style="font-size:20px;color:#555;font-weight:500;">' + componenteTexto + '</p>';
                        conteudo += '<p style="font-size:16px;color:#888;margin-top:15px;">' + totalQuestoesB2 + ' questões</p>';
                        conteudo += '</div>'; // fecha table-cell
                        conteudo += '</div>'; // fecha table
                        
                        // PÁGINAS DE QUESTÕES DO BLOCO 2 (sem cabeçalho, sem identificação do bloco)
                        const questoesB2 = blocosAtivos[1].questoesSelecionadas;
                        
                        // 🔧 v475: Separar questões normais de texto-multiplas
                        const questoesNormaisB2 = questoesB2.filter(qId => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          return q?.tamanho !== 'texto-multiplas';
                        });
                        const questoesTextoB2 = questoesB2.filter(qId => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          return q?.tamanho === 'texto-multiplas';
                        });
                        
                        // Primeiro as questões normais (por tamanho - v475)
                        // Agrupar por página considerando tamanhos
                        let alturaUsadaB2 = 0;
                        let questoesPaginaAtualB2 = [];
                        const paginasQuestoesB2 = [];
                        const ALTURA_PAGINA_B2 = 27.0;
                        const TAMANHOS_B2 = { 'pequena': 6.5, 'media': 8.5, 'grande': 12.0, 'inteira': 22.0 };
                        
                        questoesNormaisB2.forEach((qId) => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          const tamanho = q?.tamanho || 'media';
                          const altura = TAMANHOS_B2[tamanho] || 8.5;
                          
                          if (alturaUsadaB2 + altura > ALTURA_PAGINA_B2 && questoesPaginaAtualB2.length > 0) {
                            paginasQuestoesB2.push([...questoesPaginaAtualB2]);
                            questoesPaginaAtualB2 = [];
                            alturaUsadaB2 = 0;
                          }
                          questoesPaginaAtualB2.push(qId);
                          alturaUsadaB2 += altura;
                        });
                        if (questoesPaginaAtualB2.length > 0) {
                          paginasQuestoesB2.push(questoesPaginaAtualB2);
                        }
                        
                        let numQuestaoB2 = totalQuestoesB1 + 1; // v713: Bloco 2 começa dinamicamente depois do Bloco 1 (12 no 2º/5º ano, 14 no 9º ano)
                        
                        paginasQuestoesB2.forEach((questoesPagina, pag) => {
                          conteudo += '<div class="page-break"></div>';
                          conteudo += '<div style="padding-top:10mm;">';
                          
                          questoesPagina.forEach((qId) => {
                            const q = todasQuestoes.find(x => String(x.id) === String(qId));
                            const numQuestao = String(numQuestaoB2).padStart(2, '0');
                            const codigoHabilidade = getCodigoHabilidadeAtualizado(q); // v587: Usar código atualizado
                            const tamanhoClasse = q?.tamanho ? 'question-' + q.tamanho : 'question-media';
                            conteudo += '<div class="question ' + tamanhoClasse + '">';
                            conteudo += '<div class="question-header">QUESTÃO ' + numQuestao + (codigoHabilidade ? ' (' + codigoHabilidade + ')' : '') + '</div>';
                            conteudo += '<div class="question-content">';
                            if (q && q.imagem) {
                              conteudo += '<img src="' + q.imagem + '" />';
                            }
                            conteudo += '</div></div>';
                            numQuestaoB2++;
                          });
                          conteudo += '</div>';
                        });
                        
                        // v552: Questões texto-multiplas do Bloco 2 - renderizar texto base + questões vinculadas
                        questoesTextoB2.forEach((qId) => {
                          const q = todasQuestoes.find(x => String(x.id) === String(qId));
                          if (!q) return;
                          
                          const questoesVinc = q.questoesVinculadas || [];
                          
                          // Nova página para o texto base
                          conteudo += '<div class="page-break"></div>';
                          conteudo += '<div style="padding-top:10mm;">';
                          
                          // Texto base (14cm de altura)
                          conteudo += '<div style="height:14cm;margin-bottom:0.5cm;">';
                          if (q.textoImagem || q.imagem) {
                            conteudo += '<img src="' + (q.textoImagem || q.imagem) + '" style="max-width:100%;max-height:13.5cm;object-fit:contain;" />';
                          }
                          conteudo += '</div>';
                          
                          // Primeiras 2 questões vinculadas na mesma página (6.3cm cada)
                          const questoesPrimeiraPagina = questoesVinc.slice(0, 2);
                          questoesPrimeiraPagina.forEach((qv, idx) => {
                            const numQ = String(numQuestaoB2).padStart(2, '0');
                            const habCode = qv.habilidade || '';
                            conteudo += '<div class="question question-pequena" style="height:6.3cm;">';
                            conteudo += '<div class="question-header">QUESTÃO ' + numQ + (habCode ? ' (' + habCode + ')' : '') + '</div>';
                            conteudo += '<div class="question-content">';
                            if (qv.imagem) {
                              conteudo += '<img src="' + qv.imagem + '" style="max-width:100%;max-height:5.5cm;object-fit:contain;" />';
                            }
                            conteudo += '</div></div>';
                            numQuestaoB2++;
                          });
                          
                          conteudo += '</div>';
                          
                          // Se tiver 3ª questão, vai para próxima página
                          if (questoesVinc.length > 2) {
                            conteudo += '<div class="page-break"></div>';
                            conteudo += '<div style="padding-top:10mm;">';
                            
                            const questoesSegundaPagina = questoesVinc.slice(2);
                            questoesSegundaPagina.forEach((qv, idx) => {
                              const numQ = String(numQuestaoB2).padStart(2, '0');
                              const habCode = qv.habilidade || '';
                              conteudo += '<div class="question question-pequena" style="height:6.3cm;">';
                              conteudo += '<div class="question-header">QUESTÃO ' + numQ + (habCode ? ' (' + habCode + ')' : '') + '</div>';
                              conteudo += '<div class="question-content">';
                              if (qv.imagem) {
                                conteudo += '<img src="' + qv.imagem + '" style="max-width:100%;max-height:5.5cm;object-fit:contain;" />';
                              }
                              conteudo += '</div></div>';
                              numQuestaoB2++;
                            });
                            
                            conteudo += '</div>';
                          }
                        });
                      }
                    }
                    
                    return '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>FacilitaPROF</title>' + styles + '</head><body>' + conteudo + '</body></html>';
                  };
                  
                  // v689d: Buscar questões faltantes e gerar HTML dentro do bloco async
                  let questoesCompletas = [...questoes];
                  
                  // Gerar nome do arquivo baseado no tipo de documento
                  let nomeArquivo = '';
                  // Código do simulado consistente
                  const codigoSimuladoFinal = String(numeroSimulado).padStart(6, '0');
                  
                  if (tipoGeracao === 'simulado' || isEscolaModal) {
                    // SIMULADO SAEB + SÉRIE + TURMA + COMPONENTE + NÚMERO DE REFERÊNCIA
                    const serie = turmaSimuladoSelecionada?.serie || filtrosAtivos.serie || '';
                    const turma = turmaSimuladoSelecionada?.turma || '';
                    const componente = componenteAtivo || '';
                    nomeArquivo = `SIMULADO_SAEB_${serie}${turma}_${componente}_${codigoSimuladoFinal}`.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
                  } else if (tipoGeracao === 'questao') {
                    // ATIVIDADE DE MATEMÁTICA (EF03MA01) - descrição
                    const componente = componenteBloqueado || cabecalhoConfig.disciplina || 'MATEMATICA';
                    // v551: Buscar habilidade pelo ID do filtro (prioridade) ou pelo código
                    const hab = (filtros.habilidadeId && habilidades.find(h => h.id === filtros.habilidadeId)) 
                      || habilidades.find(h => h.codigo === habilidadeBloqueada);
                    const habilidadeTexto = habilidadeBloqueada || '';
                    const descricao = hab?.conteudo || '';
                    nomeArquivo = `ATIVIDADE_DE_${componente}_${habilidadeTexto}_${descricao}`.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').substring(0, 100);
                  } else if (tipoGeracao === 'avaliacao') {
                    const componente = componenteBloqueado || cabecalhoConfig.disciplina || 'AVALIACAO';
                    nomeArquivo = `AVALIACAO_DE_${componente}`.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
                  } else if (tipoGeracao === 'pronta') {
                    nomeArquivo = 'ATIVIDADE_PRONTA';
                  } else {
                    nomeArquivo = 'FacilitaPROF_Documento';
                  }
                  // Adicionar data
                  nomeArquivo += '_' + new Date().toISOString().split('T')[0];
                  console.log('📄 Nome do arquivo:', nomeArquivo);
                  
                  // Mostrar loading (mas NÃO fechar o modal ainda)
                  setGerandoPDF(true);
                  
                  // Chamar API CloudLayer.io diretamente
                  (async () => {
                    try {
                      // v689d: Buscar questões faltantes do Firestore antes de gerar HTML
                      const todosIdsQuestoes = paginas.flatMap(p => p.questoes);
                      const idsFaltantes = [...new Set(todosIdsQuestoes.filter(id => !questoes.find(q => String(q.id) === String(id))))];
                      if (idsFaltantes.length > 0 && db) {
                        console.log('🔍 Buscando ' + idsFaltantes.length + ' questões faltantes do Firestore...');
                        for (const id of idsFaltantes) {
                          try {
                            const doc = await db.collection('questoes').doc(String(id)).get();
                            if (doc.exists) {
                              questoesCompletas.push({ id: doc.id, ...doc.data() });
                              console.log('✅ Questão ' + id + ' recuperada');
                            }
                          } catch (e) { console.warn('⚠️ Erro ao buscar questão ' + id + ':', e.message); }
                        }
                      }
                      
                      // Gerar HTML com todas as questões
                      const htmlParaPDF = gerarHTMLParaPDF(questoesCompletas);
                      console.log('📄 HTML gerado para PDF:', htmlParaPDF.length, 'caracteres');
                      
                      console.log('🚀 Enviando para geração de PDF (via Cloud Function segura)...');
                      
                      // v698: API Key NUNCA fica no frontend — a Cloud Function chama
                      // o html2pdfrocket.com no servidor e devolve só a URL do PDF pronto.
                      
                      // v701: DESATIVADO — a conversão para base64 inflava o HTML para vários MB,
                      // causando erro 413 "Request Entity Too Large" no html2pdfrocket em Simulados
                      // grandes (limite de ~6MB do provedor). Confirmado que NÃO é necessária:
                      // atividades comuns (sem essa conversão) já renderizam imagens do Firebase
                      // Storage perfeitamente via URL direta. Mantido como `false &&` para reverter
                      // facilmente caso troquemos de provedor de PDF no futuro.
                      let htmlFinal = htmlParaPDF;
                      if (false && (tipoGeracao === 'simulado' || isEscolaModal)) {
                        const imgUrls = [...new Set(
                          (htmlFinal.match(/src="(https:\/\/firebasestorage\.googleapis\.com[^"]+)"/g) || [])
                            .map(m => m.slice(5, -1))
                        )];
                        if (imgUrls.length > 0) {
                          console.log('🖼️ [SIMULADO] Convertendo ' + imgUrls.length + ' imagens para base64...');
                          for (const url of imgUrls) {
                            try {
                              const img = await new Promise((res, rej) => {
                                const i = document.createElement('img');
                                i.crossOrigin = 'anonymous';
                                i.onload = () => res(i);
                                i.onerror = rej;
                                i.src = url;
                              });
                              const MAX_W = 1200;
                              let w = img.naturalWidth, h = img.naturalHeight;
                              if (w > MAX_W) { h = Math.round(h * MAX_W / w); w = MAX_W; }
                              const canvas = document.createElement('canvas');
                              canvas.width = w; canvas.height = h;
                              canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                              const b64 = canvas.toDataURL('image/jpeg', 0.95);
                              if (b64) htmlFinal = htmlFinal.split(url).join(b64);
                            } catch (e) { console.warn('⚠️ Imagem não convertida:', e.message); }
                          }
                          console.log('✅ Imagens convertidas! HTML: ' + (htmlFinal.length / 1024 / 1024).toFixed(1) + ' MB');
                        }
                      }
                      
                      // v706: Apenas informativo agora — o pdfEndpoint (navegador real,
                      // cobrança por conversão) não tem os limites de 30s/6MB do
                      // html2pdfrocket, então isso é só um log de diagnóstico.
                      const tamanhoMB = htmlFinal.length / 1024 / 1024;
                      if (tamanhoMB > 10) {
                        console.log(`ℹ️ HTML grande (${tamanhoMB.toFixed(1)} MB) — gerando via pdfEndpoint.`);
                      }
                      
                      const response = await fetch('https://us-central1-facilitaprof-c2d1a.cloudfunctions.net/gerarPDFViaPdfEndpoint', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          html: htmlFinal,
                          filename: nomeArquivo,
                          options: {
                            pageSize: 'A4',
                            landscape: false,
                            marginTop: 0,
                            marginBottom: 0,
                            marginLeft: 0,
                            marginRight: 0
                            // v706: removida a flag `batch` (era específica do html2pdfrocket,
                            // que tinha limite de 30s/6MB no modo síncrono). O pdfEndpoint usa
                            // navegador real e cobra por conversão, não por tamanho — sem essa
                            // necessidade.
                          }
                        })
                      });
                      
                      console.log('📡 Status da resposta:', response.status);
                      
                      if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        console.error('❌ Erro na geração do PDF:', errorData);
                        throw new Error(errorData.error || 'Erro ao gerar PDF: ' + response.status);
                      }
                      
                      const resultado = await response.json();
                      console.log('✅ Resposta da Cloud Function:', JSON.stringify(resultado));
                      
                      const pdfUrl = resultado.url || null;
                      
                      if (pdfUrl) {
                        // Sucesso com URL direta!
                        setShowModalGerarPDF(false);
                        
                        // Salvar URL e nome do PDF no estado para o botão "Baixar PDF"
                        setPdfGerado({
                          url: pdfUrl,
                          nome: nomeArquivo + '.pdf'
                        });
                        
                        // Calcular total de questões (diferente para Simulado)
                        const totalQuestoes = (tipoGeracao === 'simulado' || isEscolaModal)
                          ? (blocosAtivos[0].questoesSelecionadas.length + blocosAtivos[1].questoesSelecionadas.length)
                          : totalQuestoesSelecionadas;
                        
                        // Usar código do simulado consistente (já definido antes)
                        // codigoSimuladoFinal já existe no escopo
                        
                        // Gerar gabarito do simulado
                        // 🔧 v403: Corrigido para tratar questões texto-multiplas corretamente
                        let gabaritoSimulado = null;
                        if (tipoGeracao === 'simulado' || isEscolaModal) {
                          gabaritoSimulado = {};
                          let numeroQuestao = 1;
                          
                          // Bloco 1
                          blocosAtivos[0].questoesSelecionadas.forEach((qId) => {
                            const q = questoes.find(x => String(x.id) === String(qId));
                            if (q) {
                              // Se for texto-multiplas, pegar gabarito das questões vinculadas
                              if (q.tamanho === 'texto-multiplas' && q.questoesVinculadas) {
                                q.questoesVinculadas.forEach((qv) => {
                                  gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = qv.gabarito || '';
                                  numeroQuestao++;
                                });
                              } else {
                                gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = q.gabarito || '';
                                numeroQuestao++;
                              }
                            }
                          });
                          
                          // Bloco 2 (continua a numeração do bloco 1)
                          const inicioBloco2 = numeroQuestao;
                          blocosAtivos[1].questoesSelecionadas.forEach((qId) => {
                            const q = questoes.find(x => String(x.id) === String(qId));
                            if (q) {
                              // Se for texto-multiplas, pegar gabarito das questões vinculadas
                              if (q.tamanho === 'texto-multiplas' && q.questoesVinculadas) {
                                q.questoesVinculadas.forEach((qv) => {
                                  gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = qv.gabarito || '';
                                  numeroQuestao++;
                                });
                              } else {
                                gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = q.gabarito || '';
                                numeroQuestao++;
                              }
                            }
                          });
                          
                          console.log('📋 Gabarito do Simulado:', gabaritoSimulado);
                        }
                        
                        // 🔥 Salvar no histórico do Firebase
                        if (db && currentUser?.uid) {
                          try {
                            const historicoData = {
                              userId: currentUser.uid,
                              titulo: nomeArquivo,
                              nomeArquivo: nomeArquivo + '.pdf',
                              urlPdf: pdfUrl,
                              tipo: isEscolaModal ? 'simulado-escola' : tipoGeracao,
                              componente: (tipoGeracao === 'simulado' || isEscolaModal) ? componenteAtivo : (componenteBloqueado || cabecalhoConfig.disciplina || ''),
                              serie: isEscolaModal ? filtrosAtivos.serie : (cabecalhoConfig.serie || ''),
                              habilidade: habilidadeBloqueada || '',
                              totalQuestoes: totalQuestoes,
                              totalPaginas: creditosNecessarios, // 🔧 v475: Usar páginas calculadas por tamanho
                              dataGeracao: new Date().toISOString(),
                              creditosUsados: currentUser?.tipo === 'admin' ? 0 : creditosNecessarios,
                              // Dados específicos do Simulado - USAR codigoSimuladoFinal consistente
                              codigoSimulado: (tipoGeracao === 'simulado' || isEscolaModal) ? codigoSimuladoFinal : null,
                              gabarito: gabaritoSimulado
                            };
                            
                            const docRef = await db.collection('historico_pdfs').add(historicoData);
                            console.log('✅ PDF salvo no histórico:', docRef.id);
                            
                            // Atualizar estado local do histórico
                            setHistoricoPDFs(prev => [{ id: docRef.id, ...historicoData }, ...prev]);
                            
                            // Se for Simulado, criar registro na planilha de correção
                            if (tipoGeracao === 'simulado' || isEscolaModal) {
                              // 🔧 v475: Extrair descritores das questões selecionadas
                              const descritoresSimulado = {};
                              const blocosAtivos = isEscolaModal ? blocosSimuladoEscola : blocosSimulado;
                              
                              // Bloco 1
                              const questoesB1 = blocosAtivos[0]?.questoesSelecionadas || [];
                              questoesB1.forEach((qId, idx) => {
                                const questao = questoes.find(q => String(q.id) === String(qId));
                                const chave = String(idx + 1).padStart(2, '0');
                                descritoresSimulado[chave] = questao?.habilidade || questao?.codigo || '-';
                              });
                              
                              // Bloco 2
                              const questoesB2 = blocosAtivos[1]?.questoesSelecionadas || [];
                              questoesB2.forEach((qId, idx) => {
                                const questao = questoes.find(q => String(q.id) === String(qId));
                                const chave = String(idx + questoesB1.length + 1).padStart(2, '0');
                                descritoresSimulado[chave] = questao?.habilidade || questao?.codigo || '-';
                              });
                              
                              const simuladoData = {
                                pdfRef: docRef.id,
                                codigo: codigoSimuladoFinal,
                                titulo: nomeArquivo,
                                componente: componenteAtivo,
                                serie: isEscolaModal ? filtrosAtivos.serie : (cabecalhoConfig.serie || ''),
                                turma: turmaSimuladoSelecionada?.turma || '',
                                turmaId: turmaSimuladoSelecionada?.id || '', // v693: salvar turmaId para busca precisa na planilha
                                totalQuestoes: totalQuestoes,
                                gabarito: gabaritoSimulado,
                                descritores: descritoresSimulado, // 🔧 v475: Salvar descritores
                                dataGeracao: new Date().toISOString(),
                                userId: currentUser.uid,
                                respostas: {} // Para guardar respostas dos alunos depois
                              };
                              const simDocRef = await db.collection('simulados').add(simuladoData);
                              // Adicionar à lista local para aparecer no dropdown
                              setSimuladosFirebase(prev => [...prev, { id: simDocRef.id, ...simuladoData }]);
                              console.log('✅ Simulado registrado para correção:', simDocRef.id);
                            }
                          } catch (histError) {
                            console.error('⚠️ Erro ao salvar no histórico:', histError);
                          }
                          
                          // 🔧 v519: Incrementar paginasGeradas no registro do professor vinculado
                          if (currentUser?.tipo === 'professor' && currentUser?.escolaId && db) {
                            try {
                              // Buscar o registro do professor na coleção professoresEscola
                              const profSnapshot = await db.collection('professoresEscola')
                                .where('professorId', '==', currentUser.uid)
                                .where('escolaId', '==', currentUser.escolaId)
                                .limit(1)
                                .get();
                              
                              if (!profSnapshot.empty) {
                                const profDoc = profSnapshot.docs[0];
                                const paginasAtuais = profDoc.data().paginasGeradas || 0;
                                await db.collection('professoresEscola').doc(profDoc.id).update({
                                  paginasGeradas: paginasAtuais + creditosNecessarios
                                });
                                console.log('📊 v519: Páginas geradas atualizadas:', paginasAtuais, '->', paginasAtuais + creditosNecessarios);
                              }
                            } catch (pagesError) {
                              console.error('⚠️ Erro ao atualizar páginas geradas:', pagesError);
                            }
                          }
                          
                          // 🔧 v475: Créditos já foram descontados no botão de gerar
                          // Apenas atualizar no Firebase
                          if (currentUser?.tipo !== 'admin') {
                            const novoSaldo = currentUser.creditos - creditosNecessarios;
                            await db.collection('users').doc(currentUser.uid).update({
                              creditos: novoSaldo
                            });
                            console.log('💳 Créditos salvos no Firebase:', creditosNecessarios, '- Novo saldo:', novoSaldo);
                          }
                        }
                        
                        // NÃO limpar questões automaticamente - deixar usuário baixar o PDF primeiro
                        // O usuário clica em "Limpar e criar novo simulado" quando quiser
                        
                        // 🆕 LIMPAR LAYOUT ANTES do alert (para garantir que execute)
                        // 🔧 v406: NÃO limpar pdfGerado aqui - só limpar quando clicar em "Baixar"
                        if (tipoGeracao === 'simulado' || isEscolaModal) {
                          // Limpar blocos corretos (escola ou professor) mas MANTER pdfGerado
                          if (isEscolaModal) {
                            setBlocosSimuladoEscola([
                              { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                              { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                            ]);
                            setComponenteSimuladoEscola('');
                            setBlocoAtivoEscola(1);
                            setPaginaSimuladoAtivaEscola(0);
                          } else {
                            setBlocosSimulado([
                              { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                              { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                            ]);
                            setComponenteSimulado('');
                          }
                        } else {
                          setPaginas([{ id: 1, questoes: [] }]);
                          setPaginaAtiva(1);
                          setComponenteBloqueado(null);
                          setHabilidadeBloqueada(null);
                          setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                        }
                        console.log('✅ Layout limpo após geração do PDF');
                        
                        alert('✅ PDF gerado com sucesso!\n\n📄 ' + nomeArquivo + '.pdf\n\n💡 Clique no botão "Baixar PDF" para salvar o arquivo.\n\n📚 O PDF também foi salvo no seu Histórico.');
                        
                      } else if (resultado.asset || resultado.data?.asset || resultado.base64) {
                        // Resposta com base64
                        const base64Data = resultado.asset || resultado.data?.asset || resultado.base64;
                        setShowModalGerarPDF(false);
                        
                        // Criar URL blob para o botão Baixar PDF
                        const byteCharacters = atob(base64Data);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: 'application/pdf' });
                        const blobUrl = URL.createObjectURL(blob);
                        
                        // Salvar para o botão Baixar PDF
                        setPdfGerado({
                          url: blobUrl,
                          nome: nomeArquivo + '.pdf'
                        });
                        
                        // Calcular total de questões
                        const totalQuestoes = (tipoGeracao === 'simulado' || isEscolaModal)
                          ? (blocosAtivos[0].questoesSelecionadas.length + blocosAtivos[1].questoesSelecionadas.length)
                          : totalQuestoesSelecionadas;
                        
                        // Usar código do simulado consistente (já definido antes)
                        // codigoSimuladoFinal já existe no escopo
                        // 🔧 v403: Corrigido para tratar questões texto-multiplas corretamente
                        let gabaritoSimulado = null;
                        if (tipoGeracao === 'simulado' || isEscolaModal) {
                          gabaritoSimulado = {};
                          let numeroQuestao = 1;
                          
                          // Bloco 1
                          blocosAtivos[0].questoesSelecionadas.forEach((qId) => {
                            const q = questoes.find(x => String(x.id) === String(qId));
                            if (q) {
                              if (q.tamanho === 'texto-multiplas' && q.questoesVinculadas) {
                                q.questoesVinculadas.forEach((qv) => {
                                  gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = qv.gabarito || '';
                                  numeroQuestao++;
                                });
                              } else {
                                gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = q.gabarito || '';
                                numeroQuestao++;
                              }
                            }
                          });
                          
                          // Bloco 2
                          blocosAtivos[1].questoesSelecionadas.forEach((qId) => {
                            const q = questoes.find(x => String(x.id) === String(qId));
                            if (q) {
                              if (q.tamanho === 'texto-multiplas' && q.questoesVinculadas) {
                                q.questoesVinculadas.forEach((qv) => {
                                  gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = qv.gabarito || '';
                                  numeroQuestao++;
                                });
                              } else {
                                gabaritoSimulado[String(numeroQuestao).padStart(2, '0')] = q.gabarito || '';
                                numeroQuestao++;
                              }
                            }
                          });
                          
                          console.log('📋 Gabarito do Simulado (fallback):', gabaritoSimulado);
                        }
                        
                        // 🔥 Salvar no histórico do Firebase
                        if (db && currentUser?.uid) {
                          try {
                            const historicoData = {
                              userId: currentUser.uid,
                              titulo: nomeArquivo,
                              nomeArquivo: nomeArquivo + '.pdf',
                              urlPdf: 'base64', // Indicar que foi base64
                              tipo: isEscolaModal ? 'simulado-escola' : tipoGeracao,
                              componente: (tipoGeracao === 'simulado' || isEscolaModal) ? componenteAtivo : (componenteBloqueado || cabecalhoConfig.disciplina || ''),
                              serie: isEscolaModal ? filtrosAtivos.serie : (cabecalhoConfig.serie || ''),
                              habilidade: habilidadeBloqueada || '',
                              totalQuestoes: totalQuestoes,
                              totalPaginas: creditosNecessarios, // 🔧 v475: Usar páginas calculadas por tamanho
                              dataGeracao: new Date().toISOString(),
                              creditosUsados: currentUser?.tipo === 'admin' ? 0 : creditosNecessarios,
                              codigoSimulado: (tipoGeracao === 'simulado' || isEscolaModal) ? codigoSimuladoFinal : null,
                              gabarito: gabaritoSimulado
                            };
                            
                            const docRef = await db.collection('historico_pdfs').add(historicoData);
                            setHistoricoPDFs(prev => [{ id: docRef.id, ...historicoData }, ...prev]);
                            
                            // Se for Simulado, criar registro na planilha de correção
                            if (tipoGeracao === 'simulado' || isEscolaModal) {
                              // 🔧 v475: Extrair descritores das questões selecionadas
                              const descritoresSimulado = {};
                              const blocosAtivos = isEscolaModal ? blocosSimuladoEscola : blocosSimulado;
                              
                              const questoesB1 = blocosAtivos[0]?.questoesSelecionadas || [];
                              questoesB1.forEach((qId, idx) => {
                                const questao = questoes.find(q => String(q.id) === String(qId));
                                const chave = String(idx + 1).padStart(2, '0');
                                descritoresSimulado[chave] = questao?.habilidade || questao?.codigo || '-';
                              });
                              
                              const questoesB2 = blocosAtivos[1]?.questoesSelecionadas || [];
                              questoesB2.forEach((qId, idx) => {
                                const questao = questoes.find(q => String(q.id) === String(qId));
                                const chave = String(idx + questoesB1.length + 1).padStart(2, '0');
                                descritoresSimulado[chave] = questao?.habilidade || questao?.codigo || '-';
                              });
                              
                              const simuladoData = {
                                pdfRef: docRef.id,
                                codigo: codigoSimuladoFinal,
                                titulo: nomeArquivo,
                                componente: componenteAtivo,
                                serie: isEscolaModal ? filtrosAtivos.serie : (cabecalhoConfig.serie || ''),
                                turma: turmaSimuladoSelecionada?.turma || '',
                                turmaId: turmaSimuladoSelecionada?.id || '', // v693: salvar turmaId para busca precisa na planilha
                                totalQuestoes: totalQuestoes,
                                gabarito: gabaritoSimulado,
                                descritores: descritoresSimulado, // 🔧 v475: Salvar descritores
                                dataGeracao: new Date().toISOString(),
                                userId: currentUser.uid,
                                respostas: {}
                              };
                              const simDocRef = await db.collection('simulados').add(simuladoData);
                              // Adicionar à lista local para aparecer no dropdown
                              setSimuladosFirebase(prev => [...prev, { id: simDocRef.id, ...simuladoData }]);
                            }
                          } catch (histError) {
                            console.error('⚠️ Erro ao salvar no histórico:', histError);
                          }
                          
                          // 🔧 v519: Incrementar paginasGeradas para professor vinculado
                          if (currentUser?.tipo === 'professor' && currentUser?.escolaId && db) {
                            try {
                              const profSnapshot = await db.collection('professoresEscola')
                                .where('professorId', '==', currentUser.uid)
                                .where('escolaId', '==', currentUser.escolaId)
                                .limit(1)
                                .get();
                              
                              if (!profSnapshot.empty) {
                                const profDoc = profSnapshot.docs[0];
                                const paginasAtuais = profDoc.data().paginasGeradas || 0;
                                await db.collection('professoresEscola').doc(profDoc.id).update({
                                  paginasGeradas: paginasAtuais + creditosNecessarios
                                });
                                console.log('📊 v519: Páginas geradas atualizadas:', paginasAtuais + creditosNecessarios);
                              }
                            } catch (e) { console.error('⚠️ Erro ao atualizar páginas:', e); }
                          }
                          
                          // v689d: Descontar créditos - da escola se vinculado
                          if (currentUser?.tipo !== 'admin') {
                            const idDesconto = (currentUser?.tipo === 'professor' && currentUser?.escolaId) 
                              ? currentUser.escolaId 
                              : currentUser.uid;
                            const docDesconto = await db.collection('users').doc(idDesconto).get();
                            const creditosAtuais = docDesconto.exists ? (docDesconto.data().creditos || 0) : 0;
                            await db.collection('users').doc(idDesconto).update({
                              creditos: Math.max(0, creditosAtuais - creditosNecessarios)
                            });
                          }
                        }
                        
                        // NÃO limpar questões automaticamente - deixar usuário baixar o PDF primeiro
                        
                        // 🆕 LIMPAR LAYOUT ANTES do alert (para garantir que execute)
                        if (tipoGeracao === 'simulado' || isEscolaModal) {
                          if (isEscolaModal) {
                            setBlocosSimuladoEscola([
                              { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                              { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                            ]);
                            setComponenteSimuladoEscola('');
                            setBlocoAtivoEscola(1);
                            setPaginaSimuladoAtivaEscola(0);
                          } else {
                            setBlocosSimulado([
                              { id: 1, nome: 'Bloco 1', questoesSelecionadas: [] },
                              { id: 2, nome: 'Bloco 2', questoesSelecionadas: [] }
                            ]);
                            setComponenteSimulado('');
                          }
                        } else {
                          setPaginas([{ id: 1, questoes: [] }]);
                          setPaginaAtiva(1);
                          setComponenteBloqueado(null);
                          setHabilidadeBloqueada(null);
                          setFiltros({ serie: '', componente: '', habilidade: '', tamanho: '' });
                        }
                        console.log('✅ Layout limpo após geração do PDF (base64)');
                        
                        alert('✅ PDF gerado com sucesso!\n\n📄 ' + nomeArquivo + '.pdf\n\n💡 Clique no botão "Baixar PDF" para salvar o arquivo.\n\n📚 O PDF também foi salvo no seu Histórico.');
                        
                      } else {
                        console.error('❌ Resposta inesperada da API:', resultado);
                        throw new Error('Resposta inesperada da API de geração de PDF. Tente novamente ou contate o suporte.');
                      }
                    } catch (error) {
                      console.error('❌ Erro ao gerar PDF:', error);
                      
                      // Reembolsar créditos
                      setCurrentUser({
                        ...currentUser,
                        creditos: currentUser.creditos + creditosNecessarios
                      });
                      
                      alert('❌ Erro ao gerar PDF!\n\n' + error.message + '\n\n💡 Seus créditos foram reembolsados.\n\n⚠️ Suas questões foram mantidas. Tente novamente ou cancele.');
                    } finally {
                      setGerandoPDF(false);
                      gerandoPDFRef.current = false; // v703: libera a trava ao final (sucesso ou erro)
                    }
                  })();
                }}
                disabled={((tipoGeracao === 'simulado' || isEscolaModal)
                  ? (blocosAtivos[0].questoesSelecionadas.length === 0 && blocosAtivos[1].questoesSelecionadas.length === 0) || !turmaSimuladoSelecionada
                  : totalQuestoesSelecionadas === 0) || gerandoPDF}
                className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:shadow-lg transition font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {gerandoPDF ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Gerando...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    {(tipoGeracao === 'simulado' || isEscolaModal) && !turmaSimuladoSelecionada ? 'Selecione uma turma' : 'Gerar PDF'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Modal de Visualização da Questão em Zoom */}
      {questaoEmVisualizacao && (() => {
        // Detectar se está no painel da escola
        const isEscola = currentView === 'escola-simulado';
        
        // v574: Determinar o módulo correto baseado no contexto
        // Prioridade: módulo da questão > tipoGeracao > isEscola
        let moduloFiltro = questaoEmVisualizacao.modulo;
        if (!moduloFiltro) {
          // Se a questão não tem módulo definido, usar o contexto
          if (isEscola || tipoGeracao === 'simulado') {
            moduloFiltro = 'simulado';
          } else if (tipoGeracao === 'pronta') {
            moduloFiltro = 'prontas';
          } else if (tipoGeracao === 'questao' || tipoGeracao === 'avaliacao') {
            moduloFiltro = 'questao_avaliacao';
          }
        }
        
        // Detectar se é simulado SAEB
        const isSimuladoSAEB = isEscola || tipoGeracao === 'simulado' || moduloFiltro === 'simulado';
        
        // Buscar questões filtradas do mesmo tamanho - usar filtros corretos
        const tamanhoAtual = questaoEmVisualizacao.tamanho;
        const filtrosAtivos = isEscola ? filtrosEscola : filtros;
        const componenteAtivo = isEscola ? componenteSimuladoEscola : (tipoGeracao === 'simulado' ? componenteSimulado : filtros.componente);
        
        // Para Simulado SAEB, incluir variantes do componente (com e sem SAEB)
        const componenteBase = questaoEmVisualizacao.componente?.replace(' SAEB', '') || componenteAtivo || 'Matemática';
        const componentesFiltro = isSimuladoSAEB
          ? [componenteBase, `${componenteBase} SAEB`]
          : [filtrosAtivos.componente].filter(Boolean);
        
        // v579: Filtrar questões - MÓDULO PRIMEIRO para garantir separação
        const questoesFiltradas = questoes.filter(q => {
          // PRIMEIRO: Filtro por módulo - CRÍTICO para separar bancos
          if (moduloFiltro && q.modulo !== moduloFiltro) return false;
          
          // Filtro por tamanho
          if (q.tamanho !== tamanhoAtual) return false;
          
          // Filtro por série
          if (filtrosAtivos.serie && q.serie !== filtrosAtivos.serie) return false;
          
          // Filtro por componente (para simulado usa componentesFiltro)
          if (isSimuladoSAEB) {
            if (!componentesFiltro.some(c => c === q.componente)) return false;
          } else {
            if (filtrosAtivos.componente && q.componente !== filtrosAtivos.componente) return false;
          }
          
          // v579: Filtro por habilidade - diferente para escola vs professor
          // No modo ESCOLA usa "habilidade" (descritor como "D1")
          // No modo PROFESSOR usa "habilidadeId"
          if (isEscola && filtrosAtivos.habilidade && q.habilidade !== filtrosAtivos.habilidade) return false;
          if (!isEscola && filtrosAtivos.habilidadeId && q.habilidadeId !== filtrosAtivos.habilidadeId) return false;
          
          return true;
        });
        
        const indiceAtual = questoesFiltradas.findIndex(q => String(q.id) === String(questaoEmVisualizacao.id));
        const totalQuestoes = questoesFiltradas.length;
        
        // Verificar se questão já está selecionada - verificar nos estados corretos
        const blocosAtivos = isEscola ? blocosSimuladoEscola : blocosSimulado;
        const jaAdicionada = paginas.some(p => p.questoes.some(qId => String(qId) === String(questaoEmVisualizacao.id))) ||
                            blocosAtivos.some(b => b.questoesSelecionadas.some(qId => String(qId) === String(questaoEmVisualizacao.id)));
        
        // Funções de navegação
        const irParaAnterior = () => {
          if (indiceAtual > 0) {
            setQuestaoEmVisualizacao(questoesFiltradas[indiceAtual - 1]);
          }
        };
        
        const irParaProxima = () => {
          if (indiceAtual < totalQuestoes - 1) {
            setQuestaoEmVisualizacao(questoesFiltradas[indiceAtual + 1]);
          }
        };
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 md:p-4">
          {/* 🆕 Altura dinâmica: grande +15% (86vh), inteira +30% (97vh) */}
          {/* v566: Largura menor para Atividades Prontas (max-w-2xl ao invés de max-w-3xl) */}
          <div className={`bg-white rounded-xl w-full ${tipoGeracao === 'pronta' ? 'max-w-2xl' : 'max-w-3xl'} flex flex-col ${
            questaoEmVisualizacao.tamanho === 'inteira' ? 'max-h-[97vh]' :
            questaoEmVisualizacao.tamanho === 'grande' ? 'max-h-[86vh]' :
            'max-h-[75vh]'
          }`}>
            {/* Header do Modal - fixo */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 p-2 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                <div className={`w-2 h-2 rounded-full ${
                  questaoEmVisualizacao.tamanho === 'pequena' ? 'bg-green-500' :
                  questaoEmVisualizacao.tamanho === 'media' ? 'bg-blue-500' :
                  questaoEmVisualizacao.tamanho === 'grande' ? 'bg-orange-500' :
                  questaoEmVisualizacao.tamanho === 'texto-multiplas' ? 'bg-pink-500' :
                  'bg-purple-500'
                }`}></div>
                <h3 className="text-xs md:text-sm font-bold text-gray-900">
                  {questaoEmVisualizacao.tamanho === 'texto-multiplas' 
                    ? `Texto com ${questaoEmVisualizacao.questoesVinculadas?.length || 0} Questões` 
                    : `Questão ${indiceAtual >= 0 ? indiceAtual + 1 : 1}/${totalQuestoes > 0 ? totalQuestoes : 1}`
                  }
                </h3>
                <span className={`text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full font-semibold ${
                  questaoEmVisualizacao.tamanho === 'pequena' ? 'bg-green-100 text-green-700' :
                  questaoEmVisualizacao.tamanho === 'media' ? 'bg-blue-100 text-blue-700' :
                  questaoEmVisualizacao.tamanho === 'grande' ? 'bg-orange-100 text-orange-700' :
                  questaoEmVisualizacao.tamanho === 'texto-multiplas' ? 'bg-pink-100 text-pink-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {questaoEmVisualizacao.tamanho === 'texto-multiplas' ? 'Texto + Questões' : questaoEmVisualizacao.tamanho}
                </span>
                {/* v532: Omitir gabarito para plano Gold */}
                {!isPlanoGold && (
                  questaoEmVisualizacao.tamanho === 'texto-multiplas' ? (
                    <span className="text-[10px] md:text-xs bg-orange-100 text-orange-700 px-1.5 md:px-2 py-0.5 rounded font-semibold">
                      Gab: {questaoEmVisualizacao.questoesVinculadas?.map(qv => qv.gabarito).join(', ')}
                    </span>
                  ) : (
                    <span className="text-[10px] md:text-xs bg-green-100 text-green-700 px-1.5 md:px-2 py-0.5 rounded font-semibold">
                      Gab: {questaoEmVisualizacao.gabarito}
                    </span>
                  )
                )}
              </div>
              <button 
                onClick={() => setQuestaoEmVisualizacao(null)}
                className="p-1 md:p-1.5 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
              </button>
            </div>

            {/* Conteúdo do Modal com Setas de Navegação - com scroll */}
            <div className="flex-1 overflow-y-auto p-1 md:p-4 relative flex items-center">
              {/* Seta Esquerda - menor no mobile para ganhar espaço */}
              {totalQuestoes > 1 && (
                <button
                  onClick={irParaAnterior}
                  disabled={indiceAtual <= 0}
                  className={`flex-shrink-0 w-5 h-5 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center transition-all ${
                    indiceAtual > 0 
                      ? 'bg-orange-500 hover:bg-orange-600 text-white hover:scale-110' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-3 h-3 md:w-6 md:h-6" />
                </button>
              )}

              {/* Área de Visualização da Questão em Zoom com PROTEÇÃO - largura total */}
              {/* v569: Padding menor para questão inteira */}
              <ProtectedImageArea className="flex-1 border border-gray-200 rounded-lg bg-white mx-0.5 md:mx-2">
                <div className={questaoEmVisualizacao.tamanho === 'inteira' ? 'p-1 md:p-2' : 'p-1.5 md:p-4'}>
                  {/* Conteúdo específico para tipo texto-multiplas */}
                  {questaoEmVisualizacao.tamanho === 'texto-multiplas' ? (
                    <div className="space-y-3">
                      {/* Texto Base */}
                      <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Layers className="w-4 h-4 text-orange-600" />
                          <span className="text-sm font-bold text-orange-700">TEXTO BASE</span>
                        </div>
                        {questaoEmVisualizacao.textoBase ? (
                          <p className="text-xs text-gray-700 whitespace-pre-line max-h-24 overflow-y-auto">{questaoEmVisualizacao.textoBase}</p>
                        ) : questaoEmVisualizacao.textoImagem ? (
                          <img src={questaoEmVisualizacao.textoImagem} alt="Texto Base" className="max-w-full max-h-32 object-contain rounded-lg" />
                        ) : (
                          <div className="bg-gray-100 h-20 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                            [Imagem do texto base]
                          </div>
                        )}
                      </div>
                      
                      {/* Questões Vinculadas */}
                      <div>
                        <h4 className="text-xs font-bold text-pink-600 mb-2 flex items-center gap-1">
                          <List className="w-3 h-3" />
                          {questaoEmVisualizacao.questoesVinculadas?.length} QUESTÕES VINCULADAS:
                        </h4>
                        <div className="space-y-2">
                          {questaoEmVisualizacao.questoesVinculadas?.map((qv, idx) => (
                            <div key={idx} className="bg-pink-50 border border-pink-200 rounded-lg p-2">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-bold text-pink-700">
                                  Q{idx + 1} - {qv.habilidade}
                                </span>
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold">
                                  Gab: {qv.gabarito}
                                </span>
                              </div>
                              {qv.enunciado && (
                                <p className="text-xs text-gray-700 line-clamp-2">{qv.enunciado}</p>
                              )}
                              {qv.imagem && (
                                <img src={qv.imagem} alt={`Questão ${idx + 1}`} className="max-w-full max-h-24 object-contain rounded border border-gray-200 mt-1" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                  {/* Conteúdo da Questão normal */}
                  {questaoEmVisualizacao.enunciado && (
                    <p className="text-sm font-semibold text-gray-700 mb-2">{questaoEmVisualizacao.enunciado}</p>
                  )}
                  
                  {questaoEmVisualizacao.texto && (
                    <div className="bg-gray-50 border-l-3 border-blue-500 p-3 mb-3 text-sm italic whitespace-pre-line max-h-32 overflow-y-auto">
                      {questaoEmVisualizacao.texto}
                    </div>
                  )}
                  
                  {questaoEmVisualizacao.temImagem && (
                    <div className="my-3 flex justify-center">
                      <ImagemQuestao tipo={questaoEmVisualizacao.tipoImagem} />
                    </div>
                  )}
                  
                  {questaoEmVisualizacao.pergunta && (
                    <p className="text-sm font-medium text-gray-800 mb-3">{questaoEmVisualizacao.pergunta}</p>
                  )}
                  
                  {questaoEmVisualizacao.alternativas && (
                    <div className="space-y-1.5">
                      {questaoEmVisualizacao.alternativas.map((alt, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 rounded bg-gray-50 hover:bg-gray-100 transition">
                          <span className="w-6 h-6 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {alt.charAt(0)}
                          </span>
                          <span className="text-sm">{alt.substring(3)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* 🔥 Questões com imagem do Firebase - v557: com loading */}
                  {/* v569: Questão inteira ocupa toda largura, altura ajustada */}
                  {questaoEmVisualizacao.imagem && (questaoEmVisualizacao.imagem.startsWith('http') || questaoEmVisualizacao.imagem.startsWith('data:')) && (
                    <div className="flex items-center justify-center py-1">
                      <ImagemComLoading 
                        src={questaoEmVisualizacao.imagem} 
                        alt={`Questão`} 
                        className={`object-contain rounded-lg shadow-md ${questaoEmVisualizacao.tamanho === 'inteira' ? 'w-full' : 'max-w-full'}`}
                        style={{ maxHeight: questaoEmVisualizacao.tamanho === 'inteira' ? '78vh' : '450px' }}
                        tamanho={questaoEmVisualizacao.tamanho}
                      />
                    </div>
                  )}
                  
                  {/* Fallback */}
                  {!questaoEmVisualizacao.enunciado && !questaoEmVisualizacao.texto && !questaoEmVisualizacao.temImagem && !questaoEmVisualizacao.imagem && (
                    <div className="flex items-center justify-center py-6 text-gray-400">
                      <div className="text-center">
                        <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{questaoEmVisualizacao.serie} • {questaoEmVisualizacao.componente}</p>
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </div>
              </ProtectedImageArea>

              {/* Seta Direita - menor no mobile para ganhar espaço */}
              {totalQuestoes > 1 && (
                <button
                  onClick={irParaProxima}
                  disabled={indiceAtual >= totalQuestoes - 1}
                  className={`flex-shrink-0 w-5 h-5 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center transition-all ${
                    indiceAtual < totalQuestoes - 1 
                      ? 'bg-orange-500 hover:bg-orange-600 text-white hover:scale-110' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <ChevronRight className="w-3 h-3 md:w-6 md:h-6" />
                </button>
              )}
            </div>

            {/* Botões de Ação - fixo no rodapé - menores no mobile */}
            <div className="flex-shrink-0 p-2 md:p-3 bg-gray-50 border-t flex justify-center gap-2 rounded-b-xl">
              <button 
                onClick={() => setQuestaoEmVisualizacao(null)}
                className="px-3 md:px-4 py-1.5 md:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-bold text-xs md:text-sm transition whitespace-nowrap"
              >
                FECHAR
              </button>
              <button 
                onClick={() => {
                  if (!jaAdicionada) {
                    // Detectar se está no painel da escola
                    if (isEscola) {
                      // Adicionar no simulado da ESCOLA
                      const blocoAtualEsc = blocosSimuladoEscola.find(b => b.id === blocoAtivoEscola);
                      const questoesReaisEsc = contarQuestoesReais(blocoAtualEsc?.questoesSelecionadas || [], questoes);
                      if (blocoAtualEsc && questoesReaisEsc < questoesPorBlocoSAEB(filtrosEscola.serie)) {
                        const novaLista = [...blocoAtualEsc.questoesSelecionadas, questaoEmVisualizacao.id];
                        setBlocosSimuladoEscola(blocosSimuladoEscola.map(b => 
                          b.id === blocoAtivoEscola 
                            ? { ...b, questoesSelecionadas: novaLista }
                            : b
                        ));
                        // Verificar se bloco 1 ficou completo
                        const novoTotal = contarQuestoesReais(novaLista, questoes);
                        if (blocoAtivoEscola === 1 && novoTotal >= questoesPorBlocoSAEB(filtrosEscola.serie)) {
                          setQuestaoEmVisualizacao(null); // Fechar modal de zoom
                          setShowModalSegundoBlocoEscola(true);
                        } else {
                          const questaoAtual = questaoEmVisualizacao;
                          setTimeout(() => {
                            setQuestaoEmVisualizacao({...questaoAtual});
                          }, 50);
                        }
                      } else if (questoesReaisEsc >= questoesPorBlocoSAEB(filtrosEscola.serie)) {
                        alert(`Este bloco já possui ${questoesPorBlocoSAEB(filtrosEscola.serie)} questões (máximo permitido).`);
                      }
                    } else if (tipoGeracao === 'simulado') {
                      // Adicionar no simulado do PROFESSOR
                      const blocoAtual = blocosSimulado.find(b => b.id === blocoAtivo);
                      const questoesReaisProf = contarQuestoesReais(blocoAtual?.questoesSelecionadas || [], questoes);
                      if (blocoAtual && questoesReaisProf < questoesPorBlocoSAEB(configSimulado.serie)) {
                        setBlocosSimulado(blocosSimulado.map(b => 
                          b.id === blocoAtivo 
                            ? { ...b, questoesSelecionadas: [...b.questoesSelecionadas, questaoEmVisualizacao.id] }
                            : b
                        ));
                        const questaoAtual = questaoEmVisualizacao;
                        setTimeout(() => {
                          setQuestaoEmVisualizacao({...questaoAtual});
                        }, 50);
                      } else if (questoesReaisProf >= questoesPorBlocoSAEB(configSimulado.serie)) {
                        alert(`Este bloco já possui ${questoesPorBlocoSAEB(filtrosEscola.serie)} questões (máximo permitido).`);
                      }
                    } else {
                      adicionarQuestao(questaoEmVisualizacao.id);
                      const questaoAtual = questaoEmVisualizacao;
                      setTimeout(() => {
                        setQuestaoEmVisualizacao({...questaoAtual});
                      }, 50);
                    }
                  }
                }}
                disabled={jaAdicionada}
                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-bold text-xs md:text-sm flex items-center justify-center transition-all whitespace-nowrap ${
                  jaAdicionada 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-green-600 hover:to-green-700'
                }`}
              >
                {jaAdicionada 
                  ? 'JÁ ADICIONADA' 
                  : (questaoEmVisualizacao.tamanho === 'texto-multiplas' ? 'QUERO ESTE TEXTO' : 'QUERO ESSA QUESTÃO')
                }
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* 🔍 MODAL DE ZOOM - ADMIN (com navegação e nome do arquivo) */}
      {questaoAdminVisualizacao && (() => {
        // 🔧 v401: Filtrar questões usando os mesmos filtros da tela do admin (CORRIGIDO)
        // Usa habilidadeId para filtrar corretamente
        const questoesFiltradas = questoes.filter(q => 
          (!filtroQuestoesAdmin.modulo || q.modulo === filtroQuestoesAdmin.modulo) &&
          (!filtroQuestoesAdmin.serie || q.serie === filtroQuestoesAdmin.serie) &&
          (!filtroQuestoesAdmin.componente || q.componente === filtroQuestoesAdmin.componente) &&
          (!filtroQuestoesAdmin.habilidadeId || q.habilidadeId === filtroQuestoesAdmin.habilidadeId)
        );
        
        // Extrair nome do arquivo da URL da imagem ou usar ID
        const extrairNomeArquivo = (questao) => {
          if (questao.nomeArquivo) return questao.nomeArquivo;
          if (questao.imagem) {
            // Tentar extrair nome do arquivo da URL
            const url = questao.imagem;
            const match = url.match(/questoes%2F([^?]+)/);
            if (match) {
              return decodeURIComponent(match[1]).replace(/\.[^.]+$/, ''); // Remove extensão
            }
          }
          return `Questão ${questao.id}`;
        };
        
        // 🔢 v402: Extrair as duas últimas casas decimais do nome do arquivo
        // Ex: "1767140033202_5_25" -> 25
        const extrairUltimasCasas = (questao) => {
          const nome = extrairNomeArquivo(questao);
          const partes = nome.split('_');
          if (partes.length >= 1) {
            const ultimaParte = partes[partes.length - 1];
            const numero = parseInt(ultimaParte, 10);
            if (!isNaN(numero)) return numero;
          }
          return 9999; // Fallback para questões sem padrão
        };
        
        // 🔢 v402: Mapear tamanho para ordem numérica (menor tamanho = menor número)
        const ordemTamanho = { 'pequena': 1, 'media': 2, 'grande': 3, 'inteira': 4, 'texto-multiplas': 5 };
        
        // 🔢 v402: Ordenar questões por últimas casas (crescente) e depois por tamanho (crescente)
        const questoesOrdenadas = [...questoesFiltradas].sort((a, b) => {
          const casasA = extrairUltimasCasas(a);
          const casasB = extrairUltimasCasas(b);
          
          // Primeiro ordena pelas últimas casas decimais
          if (casasA !== casasB) return casasA - casasB;
          
          // Em caso de empate, ordena por tamanho (menor primeiro)
          const tamanhoA = ordemTamanho[a.tamanho] || 99;
          const tamanhoB = ordemTamanho[b.tamanho] || 99;
          return tamanhoA - tamanhoB;
        });
        
        console.log('🔍 Modal Admin - Filtros:', filtroQuestoesAdmin);
        console.log('🔍 Modal Admin - Questões filtradas e ordenadas:', questoesOrdenadas.length);
        
        const indiceAtual = questoesOrdenadas.findIndex(q => String(q.id) === String(questaoAdminVisualizacao.id));
        const totalQuestoes = questoesOrdenadas.length;
        
        const nomeArquivo = extrairNomeArquivo(questaoAdminVisualizacao);
        
        // Funções de navegação (usando questoesOrdenadas)
        const irParaAnterior = () => {
          if (indiceAtual > 0) {
            setQuestaoAdminVisualizacao(questoesOrdenadas[indiceAtual - 1]);
          }
        };
        
        const irParaProxima = () => {
          if (indiceAtual < totalQuestoes - 1) {
            setQuestaoAdminVisualizacao(questoesOrdenadas[indiceAtual + 1]);
          }
        };
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
              {/* Header do Modal */}
              <div className="flex-shrink-0 bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4 flex items-center justify-between rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-white bg-opacity-20 px-3 py-1 rounded-lg">
                    <span className="text-sm font-bold">{nomeArquivo}</span>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${
                      questaoAdminVisualizacao.modulo === 'prontas' ? 'bg-green-500' :
                      questaoAdminVisualizacao.modulo === 'simulado' ? 'bg-orange-500' :
                      'bg-purple-500'
                    }`}>
                      {questaoAdminVisualizacao.modulo === 'prontas' ? 'Prontas' : 
                       questaoAdminVisualizacao.modulo === 'simulado' ? 'Simulado' : 'Ativ/Aval'}
                    </span>
                    <span className="bg-blue-500 px-2 py-1 rounded">{questaoAdminVisualizacao.serie}</span>
                    <span className="bg-gray-500 px-2 py-1 rounded">{questaoAdminVisualizacao.habilidade}</span>
                  </div>
                </div>
                <button 
                  onClick={() => setQuestaoAdminVisualizacao(null)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Área da Imagem com Navegação */}
              <div className="flex-1 overflow-hidden flex items-center justify-center p-4 bg-gray-100 relative">
                {/* Botão Anterior */}
                {totalQuestoes > 1 && indiceAtual > 0 && (
                  <button
                    onClick={irParaAnterior}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}
                
                {/* Imagem da Questão - v557: com loading */}
                <div className="max-w-full max-h-full overflow-auto">
                  {questaoAdminVisualizacao.imagem && (questaoAdminVisualizacao.imagem.startsWith('http') || questaoAdminVisualizacao.imagem.startsWith('data:')) ? (
                    <ImagemComLoading 
                      src={questaoAdminVisualizacao.imagem} 
                      alt={nomeArquivo}
                      className="max-w-full object-contain rounded-lg shadow-md"
                      style={{ maxHeight: 'calc(90vh - 200px)' }}
                      tamanho={questaoAdminVisualizacao.tamanho}
                    />
                  ) : questaoAdminVisualizacao.tamanho === 'texto-multiplas' ? (
                    <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                      <Layers className="w-16 h-16 text-orange-500 mx-auto mb-3" />
                      <p className="text-lg font-bold text-orange-600">Texto com {questaoAdminVisualizacao.questoesVinculadas?.length || 0} Questões</p>
                      <p className="text-sm text-gray-500 mt-2">Gabaritos: {questaoAdminVisualizacao.questoesVinculadas?.map(qv => qv.gabarito).join(', ')}</p>
                    </div>
                  ) : (
                    <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">Imagem não disponível</p>
                    </div>
                  )}
                </div>
                
                {/* Botão Próximo */}
                {totalQuestoes > 1 && indiceAtual < totalQuestoes - 1 && (
                  <button
                    onClick={irParaProxima}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}
              </div>
              
              {/* Footer com Contador e Info */}
              <div className="flex-shrink-0 bg-white border-t p-4 rounded-b-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-sm">
                      <span className="text-gray-500">Componente:</span>
                      <span className="font-semibold text-gray-800 ml-1">{questaoAdminVisualizacao.componente}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Tamanho:</span>
                      <span className={`font-semibold ml-1 ${
                        questaoAdminVisualizacao.tamanho === 'pequena' ? 'text-green-600' :
                        questaoAdminVisualizacao.tamanho === 'media' ? 'text-blue-600' :
                        questaoAdminVisualizacao.tamanho === 'grande' ? 'text-orange-600' :
                        'text-purple-600'
                      }`}>
                        {questaoAdminVisualizacao.tamanho}
                      </span>
                    </div>
                    {/* Campo de Gabarito Editável */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Gabarito:</span>
                      {questaoAdminVisualizacao.tamanho === 'texto-multiplas' ? (
                        <span className="font-bold text-orange-600">
                          {questaoAdminVisualizacao.questoesVinculadas?.map(qv => qv.gabarito).join(', ')}
                        </span>
                      ) : questaoAdminVisualizacao.modulo === 'simulado' ? (
                        <select
                          value={questaoAdminVisualizacao.gabarito || '-'}
                          onChange={(e) => {
                            atualizarGabaritoQuestao(questaoAdminVisualizacao.id, e.target.value);
                            setQuestaoAdminVisualizacao({...questaoAdminVisualizacao, gabarito: e.target.value});
                          }}
                          className="text-sm font-bold text-green-600 bg-green-50 border-2 border-green-300 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-green-100 focus:ring-2 focus:ring-green-400"
                        >
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                          <option value="-">-</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={questaoAdminVisualizacao.gabarito || ''}
                          onChange={(e) => {
                            atualizarGabaritoQuestao(questaoAdminVisualizacao.id, e.target.value);
                            setQuestaoAdminVisualizacao({...questaoAdminVisualizacao, gabarito: e.target.value});
                          }}
                          placeholder="A, B, C, D ou texto..."
                          className="text-sm font-bold text-green-600 bg-green-50 border-2 border-green-300 rounded-lg px-3 py-1.5 w-32 focus:ring-2 focus:ring-green-400 focus:outline-none hover:bg-green-100"
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Contador de Questões */}
                  <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-bold text-lg">
                    {indiceAtual >= 0 ? indiceAtual + 1 : 1} de {totalQuestoes}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 📋 DROPDOWN GLOBAL DE HISTÓRICO DE SIMULADOS */}
      {showDropdownSimulado && (
        <div className="fixed inset-0 z-50" onClick={() => setShowDropdownSimulado(false)}>
          <div 
            className="fixed top-20 right-10 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <h4 className="font-bold text-sm flex items-center gap-2">📋 Histórico de Simulados</h4>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {simuladosFirebase.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">
                  <div className="text-4xl mb-2">📄</div>
                  Nenhum simulado salvo ainda.<br/>
                  <span className="text-xs">Gere um simulado para começar.</span>
                </div>
              ) : (
                [...simuladosFirebase].sort((a, b) => {
                  // v714: Ordenar por dataGeracao (timestamp real) — dataCriacao/dataAplicacao
                  // são strings inconsistentes em simulados antigos e não ordenavam direito
                  const dataA = new Date(a.dataGeracao || a.dataCriacao || a.dataAplicacao || 0).getTime();
                  const dataB = new Date(b.dataGeracao || b.dataCriacao || b.dataAplicacao || 0).getTime();
                  return dataB - dataA; // Decrescente (mais recente primeiro)
                }).map(sim => (
                  <button
                    key={sim.id}
                    onClick={() => selecionarSimulado(sim)}
                    className={`w-full p-3 text-left hover:bg-blue-50 transition border-b border-gray-100 ${
                      simuladoSelecionado?.id === sim.id ? 'bg-blue-100 border-l-4 border-l-orange-500' : ''
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-bold text-gray-800 text-sm">
                          #{formatarCodigoSimulado(sim.codigo || sim.numero)} - {sim.componente}
                        </div>
                        <div className="text-xs text-gray-500">{sim.serie}</div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {sim.dataGeracao ? new Date(sim.dataGeracao).toLocaleDateString('pt-BR') : sim.dataAplicacao}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="p-3 bg-gray-50 border-t">
              <button
                onClick={() => {
                  setSimuladoSelecionado(null);
                  setRespostasEditaveis({});
                  setShowDropdownSimulado(false);
                }}
                className="w-full py-2.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
              >
                + Criar Novo Simulado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📷 MODAL DE OCR - LEITURA DE CARTÃO-RESPOSTA - v482 SALVAMENTO */}
      {showModalOCR && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header compacto */}
            <div className="p-3 border-b flex items-center justify-between flex-shrink-0" style={{backgroundColor: '#1e3a5f'}}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                  <Camera className="w-4 h-4 text-white" />
                </div>
                <div className="text-white">
                  <h2 className="text-sm font-bold leading-tight">Correção de Cartão</h2>
                  <p className="text-xs text-blue-200 truncate max-w-[180px]">{alunoOCR?.nome}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => { pararCameraGuiada(); setShowCameraGuiada(false); setShowModalOCR(false); }}
                  className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Conteúdo - scroll vertical */}
            <div className="flex-1 overflow-y-auto p-3">
              
              {/* SEM IMAGEM - Botões de Captura empilhados */}
              {!imagemOCR && (
                <div className="space-y-3">
                  <button
                    onClick={abrirCameraGuiada}
                    className="w-full flex items-center gap-4 p-4 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50 hover:bg-blue-100 transition"
                  >
                    <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Camera className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-left">
                      <span className="text-base font-bold text-blue-700 block">Tirar Foto</span>
                      <span className="text-xs text-blue-500">Com moldura de enquadramento</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      if (inputFileOCR.current) {
                        inputFileOCR.current.value = '';
                        inputFileOCR.current.click();
                      }
                    }}
                    className="w-full flex items-center gap-4 p-4 border-2 border-dashed border-purple-300 rounded-xl bg-purple-50 hover:bg-purple-100 transition"
                  >
                    <div className="w-14 h-14 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Upload className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-left">
                      <span className="text-base font-bold text-purple-700 block">Escolher da Galeria</span>
                      <span className="text-xs text-purple-500">Selecionar imagem existente</span>
                    </div>
                  </button>
                  
                  <div className="text-center py-4">
                    <p className="text-xs text-gray-400">Fotografe o cartão-resposta do aluno</p>
                  </div>
                </div>
              )}
              
              {/* COM IMAGEM - Layout: Imagem | Bloco1 | Bloco2 */}
              {imagemOCR && (
                <div className="space-y-2">
                  {/* Layout principal */}
                  <div className="flex gap-2">
                    {/* Coluna 1 - Imagem capturada */}
                    <div className="w-1/3 flex-shrink-0 border-2 border-green-400 rounded-xl p-1.5 bg-green-50">
                      <p className="text-[9px] font-bold text-green-700 mb-1 text-center">📷 Captura</p>
                      <img 
                        src={imagemOCR} 
                        alt="Cartão-resposta" 
                        className="w-full h-auto max-h-[40vh] rounded-lg object-contain"
                      />
                      <button
                        onClick={() => {
                          setImagemOCR(null);
                          setResultadoOCR(null);
                        }}
                        className="w-full mt-1.5 py-1 border border-gray-300 rounded-lg text-gray-600 text-[9px] font-medium hover:bg-white"
                      >
                        📷 Nova Foto
                      </button>
                    </div>
                    
                    {/* Coluna 2 e 3 - Respostas em 2 colunas */}
                    <div className="flex-1 flex flex-col min-w-0">
                      {/* Antes de processar */}
                      {!resultadoOCR && (
                        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 p-3">
                          <Eye className="w-8 h-8 text-gray-300 mb-2" />
                          <p className="text-[10px] text-gray-500 text-center mb-2">Processar imagem</p>
                          <button
                            onClick={processarOCR}
                            disabled={processandoOCR}
                            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {processandoOCR ? (
                              <>
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Lendo...
                              </>
                            ) : (
                              <>
                                <Eye className="w-3 h-3" />
                                Processar
                              </>
                            )}
                          </button>
                        </div>
                      )}
                      
                      {/* Após processar - Blocos lado a lado */}
                      {resultadoOCR && (
                        <div className="flex flex-col flex-1">
                          {/* Mensagem informativa */}
                          <div className="bg-green-100 border border-green-300 rounded-lg p-2 mb-2">
                            <p className="text-[10px] text-green-700 text-center leading-tight">
                              ✅ Aqui estão as respostas captadas! Caso perceba alguma divergência, você pode alterar a resposta final!
                            </p>
                          </div>
                          
                          {/* Blocos lado a lado */}
                          <div className="flex gap-1.5 flex-1">
                            {/* BLOCO 1 */}
                            <div className="flex-1 bg-purple-50 border-2 border-purple-200 rounded-xl p-1.5 flex flex-col">
                              <p className="text-[9px] font-bold text-purple-700 mb-1 text-center bg-purple-200 rounded py-0.5">BLOCO 1</p>
                              <div className="flex-1 flex flex-col justify-between">
                                {resultadoOCR.respostas.bloco1.map((r, i) => (
                                  <div key={i} className="flex items-center gap-1 bg-white rounded px-1.5 py-[3px]">
                                    <span className="text-[10px] font-bold text-purple-700 w-5">{String(i+1).padStart(2,'0')}</span>
                                    <select
                                      value={r}
                                      onChange={(e) => {
                                        const novasRespostas = {...resultadoOCR};
                                        novasRespostas.respostas.bloco1[i] = e.target.value;
                                        setResultadoOCR(novasRespostas);
                                      }}
                                      className={`flex-1 h-6 rounded text-center text-xs font-mono font-bold cursor-pointer border-2 ${
                                        r === 'X' ? 'bg-yellow-200 text-yellow-700 border-yellow-400' : 
                                        r === '-' ? 'bg-gray-200 text-gray-500 border-gray-300' :
                                        'bg-purple-100 text-purple-700 border-purple-300'
                                      }`}
                                    >
                                      <option value="A">A</option>
                                      <option value="B">B</option>
                                      <option value="C">C</option>
                                      <option value="D">D</option>
                                      <option value="X">X</option>
                                      <option value="-">-</option>
                                    </select>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* BLOCO 2 - v475: Só mostra se realmente tiver dados */}
                            {resultadoOCR.respostas.bloco2?.length > 0 && (
                              <div className="flex-1 bg-blue-50 border-2 border-blue-200 rounded-xl p-1.5 flex flex-col">
                                <p className="text-[9px] font-bold text-blue-700 mb-1 text-center bg-blue-200 rounded py-0.5">BLOCO 2</p>
                                <div className="flex-1 flex flex-col justify-between">
                                  {resultadoOCR.respostas.bloco2.map((r, i) => (
                                    <div key={i} className="flex items-center gap-1 bg-white rounded px-1.5 py-[3px]">
                                      <span className="text-[10px] font-bold text-blue-700 w-5">{String(i + (resultadoOCR.respostas.bloco1?.length || 11) + 1).padStart(2,'0')}</span>
                                      <select
                                        value={r}
                                        onChange={(e) => {
                                          const novasRespostas = {...resultadoOCR};
                                          novasRespostas.respostas.bloco2[i] = e.target.value;
                                          setResultadoOCR(novasRespostas);
                                        }}
                                        className={`flex-1 h-6 rounded text-center text-xs font-mono font-bold cursor-pointer border-2 ${
                                          r === 'X' ? 'bg-yellow-200 text-yellow-700 border-yellow-400' : 
                                          r === '-' ? 'bg-gray-200 text-gray-500 border-gray-300' :
                                          'bg-blue-100 text-blue-700 border-blue-300'
                                        }`}
                                      >
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                        <option value="X">X</option>
                                        <option value="-">-</option>
                                      </select>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer com botão Confirmar */}
            {resultadoOCR && (
              <div className="p-3 border-t bg-gray-50 flex-shrink-0">
                <button
                  onClick={async () => {
                    // v690: Verificar saldo usando creditosEfetivos (considera escola se professor vinculado)
                    if (currentUser?.tipo !== 'admin') {
                      if (creditosEfetivos < 1) {
                        alert('❌ Saldo insuficiente! Você precisa de 1 crédito para confirmar a leitura do cartão-resposta.');
                        return;
                      }

                      // v690: Descontar 1 crédito — da escola se professor vinculado, do próprio usuário caso contrário
                      if (db) {
                        try {
                          if (isProfessorVinculado && escolaVinculada?.id) {
                            // Professor vinculado: descontar da escola
                            const escolaRef = db.collection('users').doc(escolaVinculada.id);
                            const novoSaldo = (escolaVinculada.creditos || 0) - 1;
                            await escolaRef.update({ creditos: novoSaldo });
                            // Atualizar estado local da escola vinculada
                            setEscolaVinculada(prev => ({ ...prev, creditos: novoSaldo }));
                            console.log('💳 1 crédito descontado da escola. Novo saldo escola:', novoSaldo);
                          } else if (currentUser?.uid) {
                            // Usuário comum: descontar do próprio
                            const userRef = db.collection('users').doc(currentUser.uid);
                            const novoSaldo = (currentUser.creditos || 0) - 1;
                            await userRef.update({ creditos: novoSaldo });
                            setCurrentUser(prev => ({ ...prev, creditos: novoSaldo }));
                            console.log('💳 1 crédito descontado. Novo saldo:', novoSaldo);
                          }
                        } catch (creditoError) {
                          console.error('❌ Erro ao descontar crédito:', creditoError);
                          alert('❌ Erro ao processar crédito. Tente novamente.');
                          return;
                        }
                      }
                    }

                    // Abrir modal de resultados
                    setShowModalResultadoOCR(true);
                  }}
                  className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-base flex items-center justify-center gap-2 shadow-lg"
                >
                  <CheckCircle className="w-5 h-5" />
                  Confirmar Leitura
                </button>
              </div>
            )}
            
            {/* Inputs ocultos para câmera e galeria */}
            <input 
              ref={inputCameraOCR} 
              type="file" 
              accept="image/*" 
              capture="environment" 
              onChange={handleImagemOCR} 
              className="hidden" 
            />
            <input 
              ref={inputFileOCR} 
              type="file" 
              accept="image/*" 
              onChange={handleImagemOCR} 
              className="hidden" 
            />
          </div>
        </div>
      )}

      {/* 🆕 v709: Câmera guiada com moldura de enquadramento (getUserMedia) */}
      {showCameraGuiada && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[60]">
          <video
            ref={videoRefOCR}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />
          <canvas ref={canvasRefOCR} className="hidden" />

          {/* Moldura de enquadramento - v718: VERTICAL e GRANDE, definitivo — pra foto de ~10-15cm de
              distância (a que o usuário confirmou repetidamente que funciona melhor). Sem aspect-ratio
              fixo em formato paisagem: a moldura ocupa quase a tela toda na vertical, acompanhando
              como o celular é naturalmente segurado. NÃO trocar para paisagem/aspect landscape de novo. */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-3" style={{ paddingTop: '150px', paddingBottom: '220px' }}>
            <div ref={guiaFrameRefOCR} className="w-full h-full border-4 border-white border-opacity-90 rounded-2xl relative" style={{ boxShadow: '0 0 0 2000px rgba(0,0,0,0.45)' }}>
              {/* Cantos reforçados estilo "scanner de documentos" */}
              <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-orange-400 rounded-tl-xl"></div>
              <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-orange-400 rounded-tr-xl"></div>
              <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-orange-400 rounded-bl-xl"></div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-orange-400 rounded-br-xl"></div>
              {/* Linha central pontilhada sugerindo a divisão Bloco 1 / Bloco 2 */}
              <div className="absolute top-4 bottom-4 left-1/2 border-l-2 border-dashed border-white border-opacity-50"></div>
            </div>
          </div>

          {/* Mensagem de orientação */}
          <div className="absolute top-0 left-0 right-0 p-4 pt-safe">
            <div className="bg-black bg-opacity-60 rounded-xl px-4 py-3 mx-2">
              <p className="text-white text-sm font-semibold text-center leading-snug">
                📐 Alinhe os blocos de respostas (Bloco 1 e Bloco 2) dentro da moldura
              </p>
              <p className="text-white text-opacity-80 text-xs text-center mt-1 leading-snug">
                Não precisa incluir o cabeçalho (escola/professor/aluno) — mantenha o celular paralelo à folha e evite sombras sobre as bolinhas
              </p>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-center gap-8 pb-safe">
            <button
              onClick={fecharCameraGuiada}
              className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white"
            >
              <X className="w-6 h-6" />
            </button>
            <button
              onClick={capturarFotoGuiada}
              className="w-16 h-16 bg-white rounded-full border-4 border-orange-400 flex items-center justify-center active:scale-95 transition"
              aria-label="Capturar foto"
            >
              <div className="w-12 h-12 bg-orange-500 rounded-full"></div>
            </button>
            <button
              onClick={() => {
                fecharCameraGuiada();
                if (inputCameraOCR.current) {
                  inputCameraOCR.current.value = '';
                  inputCameraOCR.current.click();
                }
              }}
              className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white"
              title="Usar câmera nativa do celular"
            >
              <Camera className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* 🔄 Overlay de Loading - Geração de PDF */}
      {gerandoPDF && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl p-8 max-w-sm mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
              <FileText className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Gerando PDF...</h3>
            <p className="text-gray-500 text-sm mb-2">
              Aguarde enquanto seu documento está sendo processado
            </p>
            <p className="text-amber-600 text-xs mb-4">
              ⚠️ Para simulados grandes pode levar mais tempo. Mantenha esta aba aberta e a tela ligada até concluir.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-blue-600">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
            </div>
          </div>
        </div>
      )}

      {/* v593: Modal de Resultados OCR com Destaque e Total de Acertos */}
      {showModalResultadoOCR && resultadoOCR && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-2">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-3 border-b flex items-center justify-between flex-shrink-0" style={{backgroundColor: '#1e3a5f'}}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div className="text-white">
                  <h2 className="text-sm font-bold leading-tight">Resultado da Leitura</h2>
                  <p className="text-xs text-blue-200 truncate max-w-[180px]">{alunoOCR?.nome}</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowModalResultadoOCR(false);
                  setShowModalOCR(false);
                  setImagemOCR(null);
                  setResultadoOCR(null);
                }}
                className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-2">
                {/* Total de Acertos */}
                {(() => {
                  const resultado = calcularAcertos();
                  
                  // Se não tem gabarito, mostrar aviso
                  if (!resultado.simulado || !resultado.simulado.gabarito) {
                    return (
                      <div className="bg-yellow-100 border border-yellow-300 rounded-xl p-4 text-center">
                        <p className="text-sm font-semibold text-yellow-800">⚠️ Gabarito não encontrado</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          Não é possível calcular os acertos sem o gabarito do simulado.
                        </p>
                      </div>
                    );
                  }
                  
                  // v720: total de questões dinâmico (22 pro 2º/5º ano, 26 pro 9º ano) —
                  // antes vinha fixo em 22, então um simulado de 9º ano mostrava "23/22"
                  const totalQuestoesPreview = resultado.detalhes.length || 22;
                  return (
                    <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white text-center shadow-lg">
                      <p className="text-sm font-semibold mb-1">✅ TOTAL DE ACERTOS</p>
                      <p className="text-4xl font-bold">{resultado.total}/{totalQuestoesPreview}</p>
                      <p className="text-xs mt-1 opacity-90">
                        {Math.round((resultado.total / totalQuestoesPreview) * 100)}% de aproveitamento
                      </p>
                    </div>
                  );
                })()}

                {/* Mensagem informativa */}
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-2">
                  <p className="text-[10px] text-blue-700 text-center leading-tight">
                    📊 Confira o resultado da leitura antes de confirmar e salvar!
                  </p>
                </div>
                
                {/* Blocos lado a lado */}
                <div className="flex gap-2">
                  {/* BLOCO 1 */}
                  <div className="flex-1 bg-purple-50 border-2 border-purple-200 rounded-xl p-2">
                    <p className="text-[10px] font-bold text-purple-700 mb-1.5 text-center bg-purple-200 rounded py-1">BLOCO 1</p>
                    <div className="space-y-1">
                      {resultadoOCR.respostas.bloco1.map((r, i) => {
                        const numQuestao = String(i + 1).padStart(2, '0');
                        const resultado = calcularAcertos();
                        const gabarito = resultado.simulado?.gabarito?.[numQuestao];
                        const acertou = r === gabarito && r !== '-' && r !== 'X';
                        const errou = r !== gabarito && r !== '-' && r !== 'X' && gabarito;
                        
                        return (
                          <div key={i} className="flex items-center gap-1.5 bg-white rounded px-2 py-1">
                            <span className="text-xs font-bold text-purple-700 w-6">{numQuestao}</span>
                            <div
                              className={`flex-1 h-7 rounded text-center text-sm font-mono font-bold border-2 flex items-center justify-center ${
                                acertou ? 'bg-green-200 text-green-800 border-green-500' :
                                errou ? 'bg-red-200 text-red-800 border-red-500' :
                                r === 'X' ? 'bg-yellow-200 text-yellow-700 border-yellow-400' : 
                                r === '-' ? 'bg-gray-200 text-gray-500 border-gray-300' :
                                'bg-purple-100 text-purple-700 border-purple-300'
                              }`}
                            >
                              {r}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* BLOCO 2 */}
                  {resultadoOCR.respostas.bloco2?.length > 0 && (
                    <div className="flex-1 bg-blue-50 border-2 border-blue-200 rounded-xl p-2">
                      <p className="text-[10px] font-bold text-blue-700 mb-1.5 text-center bg-blue-200 rounded py-1">BLOCO 2</p>
                      <div className="space-y-1">
                        {resultadoOCR.respostas.bloco2.map((r, i) => {
                          const numQuestao = String(i + (resultadoOCR.respostas.bloco1?.length || 11) + 1).padStart(2, '0');
                          const resultado = calcularAcertos();
                          const gabarito = resultado.simulado?.gabarito?.[numQuestao];
                          const acertou = r === gabarito && r !== '-' && r !== 'X';
                          const errou = r !== gabarito && r !== '-' && r !== 'X' && gabarito;
                          
                          return (
                            <div key={i} className="flex items-center gap-1.5 bg-white rounded px-2 py-1">
                              <span className="text-xs font-bold text-blue-700 w-6">{numQuestao}</span>
                              <div
                                className={`flex-1 h-7 rounded text-center text-sm font-mono font-bold border-2 flex items-center justify-center ${
                                  acertou ? 'bg-green-200 text-green-800 border-green-500' :
                                  errou ? 'bg-red-200 text-red-800 border-red-500' :
                                  r === 'X' ? 'bg-yellow-200 text-yellow-700 border-yellow-400' : 
                                  r === '-' ? 'bg-gray-200 text-gray-500 border-gray-300' :
                                  'bg-blue-100 text-blue-700 border-blue-300'
                                }`}
                              >
                                {r}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer com botões */}
            <div className="p-3 border-t bg-gray-50 flex-shrink-0">
              <button
                onClick={() => {
                  // Confirmar e salvar respostas
                  if (alunoOCR && resultadoOCR) {
                    const respostasParaSalvar = {};
                    resultadoOCR.respostas.bloco1.forEach((r, i) => {
                      respostasParaSalvar[String(i + 1).padStart(2, '0')] = r;
                    });
                    if (resultadoOCR.respostas.bloco2) {
                      resultadoOCR.respostas.bloco2.forEach((r, i) => {
                        respostasParaSalvar[String(i + (resultadoOCR.respostas.bloco1?.length || 11) + 1).padStart(2, '0')] = r;
                      });
                    }
                    
                    // Atualizar respostas editáveis localmente
                    setRespostasEditaveis(prev => ({
                      ...prev,
                      [alunoOCR.index]: respostasParaSalvar
                    }));
                    
                    // Buscar simulado correto
                    let simuladoParaSalvar = null;
                    
                    if (filtroCorrecaoSimulado) {
                      simuladoParaSalvar = simuladosFirebase.find(s => 
                        String(s.codigo) === String(filtroCorrecaoSimulado)
                      );
                    } else if (simuladoSelecionado) {
                      simuladoParaSalvar = simuladoSelecionado;
                    }
                    
                    // Salvar no Firebase
                    if (simuladoParaSalvar && simuladoParaSalvar.id && db) {
                      const simuladoRef = db.collection('simulados').doc(simuladoParaSalvar.id);
                      const alunoNome = alunoOCR.nome;
                      const turmaCorrecao = turmas.find(t => String(t.id) === String(filtroCorrecaoTurma));
                      const turmaNome = turmaCorrecao ? `${turmaCorrecao.serie} ${turmaCorrecao.turma}` : '';
                      
                      simuladoRef.update({
                        [`respostas.${alunoNome}`]: respostasParaSalvar,
                        [`turmasCorrigidas.${turmaCorrecao?.id || 'default'}`]: turmaNome,
                        dataUltimaCorrecao: new Date().toISOString()
                      }).then(() => {
                        setSimuladosFirebase(prev => prev.map(s => 
                          s.id === simuladoParaSalvar.id 
                            ? {...s, respostas: {...(s.respostas || {}), [alunoNome]: respostasParaSalvar}}
                            : s
                        ));
                        alert('✅ Respostas salvas com sucesso!');
                        setShowModalResultadoOCR(false);
                        setShowModalOCR(false);
                        setImagemOCR(null);
                        setResultadoOCR(null);
                      }).catch(err => {
                        alert('❌ Erro ao salvar: ' + err.message);
                      });
                    } else {
                      alert('⚠️ Simulado não encontrado. Verifique se selecionou o simulado corretamente.');
                    }
                  }
                }}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-base flex items-center justify-center gap-2 shadow-lg"
              >
                <CheckCircle className="w-5 h-5" />
                Confirmar e Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔧 v427: Modal de Recuperação de Senha - Global */}
      {showModalRecuperarSenha && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-4 flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Recuperar Senha
              </h3>
              <button onClick={() => { setShowModalRecuperarSenha(false); setEmailRecuperacao(''); }} className="text-white hover:bg-orange-700 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <p className="text-sm text-gray-600 text-center">
                  Digite seu email cadastrado. Você receberá um link para redefinir sua senha.
                </p>
                <input 
                  type="email" 
                  placeholder="Seu email" 
                  className="w-full p-3 border rounded-lg focus:outline-none focus:border-orange-500" 
                  value={emailRecuperacao} 
                  onChange={(e) => setEmailRecuperacao(e.target.value)} 
                />
                <button 
                  onClick={async () => {
                    if (!emailRecuperacao) { 
                      alert('⚠️ Digite seu email!'); 
                      return; 
                    }
                    
                    setLoginLoading(true);
                    try {
                      const resultado = await recuperarSenhaFirebase(emailRecuperacao);
                      
                      if (resultado.success) {
                        alert('✅ Email de recuperação enviado!\n\nVerifique sua caixa de entrada (e spam) e clique no link para redefinir sua senha.');
                        setShowModalRecuperarSenha(false);
                        setEmailRecuperacao('');
                      } else {
                        alert('❌ ' + resultado.error);
                      }
                    } catch (error) {
                      alert('❌ Erro ao enviar email. Tente novamente.');
                    } finally {
                      setLoginLoading(false);
                    }
                  }} 
                  disabled={loginLoading}
                  className="w-full py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50"
                >
                  {loginLoading ? 'Enviando...' : 'Enviar Email de Recuperação'}
                </button>
                <button 
                  onClick={() => { setShowModalRecuperarSenha(false); setLandingMostrarLogin(true); setCurrentView('landing'); }} 
                  className="w-full py-2 text-gray-500 hover:text-orange-500 text-sm"
                >
                  ← Voltar para Login
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
